// This file is auto-generated by @hey-api/openapi-ts

export type ClientOptions = {
  baseURL: string;
};

export const PricingPlanComponentCapability = {
  SSO: 'sso',
  NDA: 'nda',
  PAST_PARTICIPANT: 'pastParticipant',
  PARTICIPANT_DATABASE: 'participantDatabase',
} as const;

export type PricingPlanComponentCapability =
  (typeof PricingPlanComponentCapability)[keyof typeof PricingPlanComponentCapability];

export const NewTeamRequestStatus = {
  PENDING: 'pending',
  APPROVED: 'approved',
  DENIED: 'denied',
  CANCELLED: 'cancelled',
} as const;

export type NewTeamRequestStatus =
  (typeof NewTeamRequestStatus)[keyof typeof NewTeamRequestStatus];

export type NewTeamRequest = {
  id: string;
  createdAt: string;
  teamName: string;
  description: string;
  imageUrl?: string;
  userName: string;
  userId: string;
  status: NewTeamRequestStatus;
  updatedBy?: string;
  updatedAt?: string;
};

export type IdentityProvider = {
  id: string;
  name: string;
  enabled: boolean;
  default: boolean;
};

export type ClaimedDomain = {
  id: string;
  createdAt: string;
  domain: string;
  verified: boolean;
  updatedAt?: string;
};

export type SsoProvider = {
  id: string;
  createdAt: string;
  name: string;
  ssoUrl: string;
  certificate: string;
  enabled: boolean;
  issuer?: string;
  updatedAt?: string;
};

export type ObjectId = {
  [key: string]: unknown;
};

export type Features = {
  inviteRespondents: boolean;
  outlookCalendar: boolean;
  googleCalendar: boolean;
  ssoEnabled: boolean;
  hasReferral: boolean;
  increasedTargetLimit: boolean;
  increasedMaxTips: boolean;
  participantDatabase: boolean;
  exposeEmailsInUnmodProjects: boolean;
  noCreditReservation: boolean;
  /**
   * Price B2B recruits with the pricing multiplier. Stamped on the
   * project at publish; on by default for new orgs. Absent / false = every
   * study prices at 1.00x.
   */
  pricingMultiplier: boolean;
  /**
   * When true, every project this org publishes is routed to manual review
   * (status PENDING) before going live — all channels and users, including
   * API/externalResearcher publishes, regardless of user trust level.
   */
  forceProjectReview: boolean;
  /**
   * The organization has opted out of AI features. When true the researcher
   * app hides every AI entry point (AI Moderator creation, prompt/brief
   * creation, the editor assistant and AI edits, AI study checks, AI media
   * notes, insights chat). Absent / false = AI available as today. This is an
   * organization-level setting, not a `feature_flags` document, so it is
   * written through the admin organization features editor. Deliberately
   * optional: existing organization documents do not carry it.
   */
  'ai-opted-out'?: boolean;
};

export type OrganizationApiCredentials = {
  id: string;
};

export const OrganizationRoles = {
  OWNER: 'owner',
  ADMIN: 'admin',
  MEMBER: 'member',
} as const;

export type OrganizationRoles =
  (typeof OrganizationRoles)[keyof typeof OrganizationRoles];

export type OrganizationMember = {
  userId: string;
  roles: Array<OrganizationRoles>;
  createdAt: string;
  trustLevel?: number;
  firstName?: string;
  lastName?: string;
  picture?: string;
};

export type Balance = {
  credit: number;
  incentive: number;
};

export type Plan = {
  id: string;
  name: string;
  price: number;
  maxUsers?: number;
};

export type LocationCoordinate = {
  longitude: number;
  latitude: number;
};

export type Location = {
  id: string;
  googleId: string;
  region: string;
  city: string;
  state: string;
  country: string;
  zipcode?: string;
  address1: string;
  formattedAddress: string;
  location?: LocationCoordinate;
};

export type UserRmsToken = {
  token: string;
  organizationId: string;
  applicationUid: string;
  id?: string;
};

export type UserRms = {
  id: string | null;
  tokens: Array<UserRmsToken>;
};

export type UserCustomerScheduler = {
  uid: string;
  token: string;
};

export type UserScheduler = {
  id: string;
  token: string;
};

export type UserMessaging = {
  id: string;
  token: string;
};

export type UserInviter = {
  projectId: string;
  organizationId: string;
  teamId: string;
  inviteCode: string;
  userId?: string;
  type?: string;
  createdAt?: string;
};

export type NotificationSettings = {
  newMessage: boolean;
  booking: boolean;
  cancellation: boolean;
  firstQualified: boolean;
  autocomplete: boolean;
};

export const TeamRoles = {
  OWNER: 'owner',
  ADMIN: 'admin',
  MEMBER: 'member',
} as const;

export type TeamRoles = (typeof TeamRoles)[keyof typeof TeamRoles];

export type UserTeam = {
  teamId: string;
  organizationId: string;
  roles: Array<TeamRoles>;
};

export type UserOrganization = {
  organizationId: string;
  roles: Array<OrganizationRoles>;
  createdAt: string;
};

export const ReferralType = { PROJECT: 'project', SIGNUP: 'signup' } as const;

export type ReferralType = (typeof ReferralType)[keyof typeof ReferralType];

export type Referrer = {
  userId: string;
  projectId?: string | null;
  type: ReferralType;
  createdAt: string;
};

export const ProjectRejectionType = {
  INVALID_SCREENER: 'INVALID_SCREENER',
  INAPPROPRIATE_SCREENER: 'INAPPROPRIATE_SCREENER',
  AMBIGUOUS_PROJECT: 'AMBIGUOUS_PROJECT',
  INFORMATION_CONFLICT: 'INFORMATION_CONFLICT',
  SILENT_REJECT: 'SILENT_REJECT',
  OTHER: 'OTHER',
} as const;

export type ProjectRejectionType =
  (typeof ProjectRejectionType)[keyof typeof ProjectRejectionType];

export type ProjectRejectionReason = {
  rejectionType: ProjectRejectionType;
  title: string;
  context?: string;
};

export const KeyQualifier = {
  HOUSEHOLD_INCOME: 'householdIncome',
  PROFESSIONAL_INDUSTRIES: 'professionalIndustries',
  COMPANY_SIZE: 'companySize',
  TOPICS: 'topics',
  DESIRED_SKILLS: 'desiredSkills',
  RELATIONSHIP_STATUS: 'relationshipStatus',
  LANGUAGES: 'languages',
  NUMBER_OF_CHILDREN: 'numberOfChildren',
  NUMBER_OF_CHILDREN_LIVE_AT_HOME: 'numberOfChildrenLiveAtHome',
  CHILDREN_BIRTH_YEARS: 'childrenBirthYears',
  LIVING_SITUATION: 'livingSituation',
  PETS: 'pets',
  HOME_OWNER: 'homeOwner',
  BUSINESS_OWNER: 'businessOwner',
  INCOME: 'income',
  DISABILITY_TYPE: 'disabilityType',
  NATIONALITY: 'nationality',
  MILITARY_SERVICE_STATUS: 'militaryServiceStatus',
  EMPLOYMENT_STATUSES: 'employmentStatuses',
  CLOUD_PLATFORMS: 'cloudPlatforms',
  CODING_AI_TOOLS: 'codingAITools',
  DESIGN_PROTOTYPING_TOOLS: 'designPrototypingTools',
  PRODUCTIVITY_SOFTWARE: 'productivitySoftware',
  STREAMING_SERVICES: 'streamingServices',
  FOOD_DELIVERY_SERVICES: 'foodDeliveryServices',
  ONLINE_BANKING_TOOLS: 'onlineBankingTools',
  ONLINE_LEARNING_PLATFORMS: 'onlineLearningPlatforms',
  ONLINE_MARKETPLACE_PLATFORMS: 'onlineMarketplacePlatforms',
  ONLINE_TRAVEL_BOOKING: 'onlineTravelBooking',
  SMART_HOME_DEVICES: 'smartHomeDevices',
  SOCIAL_NETWORK_PLATFORMS: 'socialNetworkPlatforms',
  VEHICLE_BRANDS: 'vehicleBrands',
  HEALTH_WELLNESS_APPS: 'healthWellnessApps',
  ASSISTIVE_TECHNOLOGIES: 'assistiveTechnologies',
  ONLINE_SHOPPING_FREQUENCY: 'onlineShoppingFrequency',
} as const;

export type KeyQualifier = (typeof KeyQualifier)[keyof typeof KeyQualifier];

export const ProjectMetadataFilters = {
  EDUCATION: 'education',
  ETHNICITY: 'ethnicity',
  AGE_GROUPS: 'ageGroups',
  COUNTRIES: 'countries',
  JOB_FUNCTIONS: 'jobFunctions',
  TECHNICAL: 'technical',
  CITIES: 'cities',
  TRUSTED_COMPANY: 'trustedCompany',
  GENDERS: 'genders',
  SENIORITY: 'seniority',
  HOUSEHOLD_INCOME: 'householdIncome',
  PROFESSIONAL_INDUSTRIES: 'professionalIndustries',
  PROFESSIONAL_JOB_TITLES: 'professionalJobTitles',
  COMPANY_SIZE: 'companySize',
  TOPICS: 'topics',
  DESIRED_SKILLS: 'desiredSkills',
  RELATIONSHIP_STATUS: 'relationshipStatus',
  LANGUAGES: 'languages',
  NUMBER_OF_CHILDREN: 'numberOfChildren',
  NUMBER_OF_CHILDREN_LIVE_AT_HOME: 'numberOfChildrenLiveAtHome',
  CHILDREN_BIRTH_YEARS: 'childrenBirthYears',
  LIVING_SITUATION: 'livingSituation',
  PETS: 'pets',
  HOME_OWNER: 'homeOwner',
  BUSINESS_OWNER: 'businessOwner',
  INCOME: 'income',
  DISABILITY_TYPE: 'disabilityType',
  NATIONALITY: 'nationality',
  MILITARY_SERVICE_STATUS: 'militaryServiceStatus',
  EMPLOYMENT_STATUSES: 'employmentStatuses',
  CLOUD_PLATFORMS: 'cloudPlatforms',
  CODING_AI_TOOLS: 'codingAITools',
  DESIGN_PROTOTYPING_TOOLS: 'designPrototypingTools',
  PRODUCTIVITY_SOFTWARE: 'productivitySoftware',
  STREAMING_SERVICES: 'streamingServices',
  FOOD_DELIVERY_SERVICES: 'foodDeliveryServices',
  ONLINE_BANKING_TOOLS: 'onlineBankingTools',
  ONLINE_LEARNING_PLATFORMS: 'onlineLearningPlatforms',
  ONLINE_MARKETPLACE_PLATFORMS: 'onlineMarketplacePlatforms',
  ONLINE_TRAVEL_BOOKING: 'onlineTravelBooking',
  SMART_HOME_DEVICES: 'smartHomeDevices',
  SOCIAL_NETWORK_PLATFORMS: 'socialNetworkPlatforms',
  VEHICLE_BRANDS: 'vehicleBrands',
  HEALTH_WELLNESS_APPS: 'healthWellnessApps',
  ASSISTIVE_TECHNOLOGIES: 'assistiveTechnologies',
  ONLINE_SHOPPING_FREQUENCY: 'onlineShoppingFrequency',
} as const;

export type ProjectMetadataFilters =
  (typeof ProjectMetadataFilters)[keyof typeof ProjectMetadataFilters];

export type ProjectMetadata = {
  filters?: Array<ProjectMetadataFilters>;
  signals?: Array<KeyQualifier>;
  /**
   * PX-456 open audience: when the researcher first picked the audience type
   * in the Target audience drawer. Absent = not picked; every V3 draft is
   * seeded with B2B, so `talkToAudience` alone cannot tell. Set only while
   * absent by `ResearcherProjectsService.updateDraft()`, for organizations on
   * `allow-optional-topics-and-industries` only.
   */
  audienceTypeSelectedAt?: string;
};

export type AudienceSizeEstimate = {
  /**
   * Last login within 90 days + all "must" targeting applied
   */
  minStrict?: number;
  /**
   * Last login within 90 days + standard matching "must"/"should" mix targeting applied
   */
  minMatching?: number;
  /**
   * Last login within 180 days + all "must" targeting applied
   */
  maxStrict?: number;
  /**
   * Last login within 180 days + standard matching "must"/"should" mix targeting applied
   */
  maxMatching?: number;
  createdAt?: string;
};

export type MetricsFeasibility = {
  audienceSizeEstimate?: AudienceSizeEstimate;
};

export type MetricsParticipants = {
  matched?: number;
  emailsDelivered?: number;
  qualified?: number;
  payRate?: number;
  avgTimeFromInviteToPaid?: number;
  viewCount?: number;
  clickCount?: number;
};

export const MatchingProjectStatus = {
  CREATED: 'CREATED',
  PROCESSING: 'PROCESSING',
  RECRUITING: 'RECRUITING',
  COMPLETED: 'COMPLETED',
  SKIPPED: 'SKIPPED',
  PAUSED: 'PAUSED',
} as const;

export type MatchingProjectStatus =
  (typeof MatchingProjectStatus)[keyof typeof MatchingProjectStatus];

export type MetricsRecruitment = {
  emailsSent?: number;
  target?: number;
  status?: MatchingProjectStatus;
};

export type MetricsScreenerQuestion = {
  total?: number;
  avgAnswered?: number;
  avgAnsweredQualified?: number;
  avgAnsweredDisqualified?: number;
  avgAnsweredIncomplete?: number;
};

export type MetricsScreenerStatus = {
  draft?: number;
  pending?: number;
  /**
   * External-screener flow: still in the partner's screener (draft-equivalent).
   */
  externalScreenerInProgress?: number;
  invited?: number;
  scheduled?: number;
  attended?: number;
  noShow?: number;
  paid?: number;
  cancelled?: number;
  invalid?: number;
  rejected?: number;
};

export type MetricsScreeners = {
  complete?: number;
  incomplete?: number;
  qualifiedScreeners?: number;
  status?: MetricsScreenerStatus;
  questions?: MetricsScreenerQuestion;
  taskCompleted?: number;
  notVisible?: number;
  qualifiedVisible?: number;
};

export type MetricsMilestones = {
  createToPublish?: number;
};

export type ProjectMetrics = {
  milestones?: MetricsMilestones;
  screeners?: MetricsScreeners;
  recruitment?: MetricsRecruitment;
  participants?: MetricsParticipants;
  feasibility?: MetricsFeasibility;
};

export type ProjectAutomationInvite = {
  /**
   * Delay in minutes
   */
  delay: number;
};

export type ProjectAutomationComplete = {
  /**
   * Delay in minutes
   */
  delay: number;
};

export type ProjectAutomationPay = {
  /**
   * Delay in minutes
   */
  delay: number;
};

export type ProjectAutomations = {
  pay?: ProjectAutomationPay;
  complete?: ProjectAutomationComplete;
  invite?: ProjectAutomationInvite;
};

export type ExternalResearcher = {
  researcherId: string;
  /**
   * Display name used when messaging participants. Full name is not required —
   * participants only ever see the first token (PX-214), even if a full name
   * is stored here.
   */
  researcherName?: string;
  teamId?: string;
  companyName?: string;
  companyId?: string;
  /**
   * Link to book a meeting with the researcher.
   */
  bookingUrl?: string;
};

export const TaskStatus = { PENDING: 'pending', COMPLETE: 'complete' } as const;

export type TaskStatus = (typeof TaskStatus)[keyof typeof TaskStatus];

export type TaskDeadline = {
  endsAt?: string;
  timezone?: string;
};

export type TaskParameters = {
  projectId?: string;
  participantId?: string;
  screenerResponseId?: string;
};

export const TaskType = {
  LOOKBACK: 'lookback',
  SURVEYMONKEY: 'surveymonkey',
  CUSTOM_URL: 'custom_url',
} as const;

export type TaskType = (typeof TaskType)[keyof typeof TaskType];

export type Task = {
  id?: string;
  type: TaskType;
  surveyId?: string;
  surveyName?: string;
  url?: string;
  parameters?: TaskParameters;
  projectCompletionId?: string;
  deadline?: TaskDeadline;
  status?: TaskStatus;
  context?: {
    [key: string]: unknown;
  };
  instructions?: string;
};

export type TopicLegacy = {
  id: ObjectId;
  name: string;
  createdAt: {
    [key: string]: unknown;
  };
  updatedAt: {
    [key: string]: unknown;
  };
  customName: string;
};

export type SkillLegacy = {
  id: ObjectId;
  name: string;
  slug: string;
  validated: boolean;
  type: string;
  createdAt: {
    [key: string]: unknown;
  };
  count: number;
};

export type ProjectParticipationFilteringLegacy = {
  since: {
    [key: string]: unknown;
  };
  months: number;
};

export type ProjectFilteringLegacy = {
  participation: ProjectParticipationFilteringLegacy;
};

export type ProjectUserLegacy = {
  id: ObjectId;
  firstName: string;
  lastName: string;
  email: string;
};

export type SimpleSocOccupancy = {
  id: string;
  code?: string;
};

export type JobTitleLegacy = {
  id: ObjectId;
  name: string;
  slug: string;
  socCode: SimpleSocOccupancy;
  profileCount: number;
  createdAt: {
    [key: string]: unknown;
  };
  emsiId: string;
  emsiName: string;
  emsiConfidence: number;
  mappingNames: Array<string>;
  isCanonical: boolean;
  alternativeSocCodes: Array<SimpleSocOccupancy>;
};

export const ProjectCountryCode = {
  AF: 'AF',
  AX: 'AX',
  AL: 'AL',
  DZ: 'DZ',
  AS: 'AS',
  AD: 'AD',
  AO: 'AO',
  AI: 'AI',
  AQ: 'AQ',
  AG: 'AG',
  AR: 'AR',
  AM: 'AM',
  AW: 'AW',
  AU: 'AU',
  AT: 'AT',
  AZ: 'AZ',
  BS: 'BS',
  BH: 'BH',
  BD: 'BD',
  BB: 'BB',
  BY: 'BY',
  BE: 'BE',
  BZ: 'BZ',
  BJ: 'BJ',
  BM: 'BM',
  BT: 'BT',
  BO: 'BO',
  BA: 'BA',
  BW: 'BW',
  BV: 'BV',
  BR: 'BR',
  VG: 'VG',
  IO: 'IO',
  BN: 'BN',
  BG: 'BG',
  BF: 'BF',
  BI: 'BI',
  CV: 'CV',
  KH: 'KH',
  CM: 'CM',
  CA: 'CA',
  KY: 'KY',
  CF: 'CF',
  TD: 'TD',
  CL: 'CL',
  CN: 'CN',
  CX: 'CX',
  CC: 'CC',
  CO: 'CO',
  KM: 'KM',
  CG: 'CG',
  CK: 'CK',
  CR: 'CR',
  CI: 'CI',
  HR: 'HR',
  CW: 'CW',
  CY: 'CY',
  CZ: 'CZ',
  CD: 'CD',
  DK: 'DK',
  DJ: 'DJ',
  DM: 'DM',
  DO: 'DO',
  EC: 'EC',
  EG: 'EG',
  SV: 'SV',
  GQ: 'GQ',
  ER: 'ER',
  EE: 'EE',
  SZ: 'SZ',
  ET: 'ET',
  FK: 'FK',
  FO: 'FO',
  FJ: 'FJ',
  FI: 'FI',
  FR: 'FR',
  GF: 'GF',
  PF: 'PF',
  TF: 'TF',
  GA: 'GA',
  GM: 'GM',
  GE: 'GE',
  DE: 'DE',
  GH: 'GH',
  GI: 'GI',
  GR: 'GR',
  GL: 'GL',
  GD: 'GD',
  GP: 'GP',
  GU: 'GU',
  GT: 'GT',
  GG: 'GG',
  GN: 'GN',
  GW: 'GW',
  GY: 'GY',
  HT: 'HT',
  HM: 'HM',
  VA: 'VA',
  HN: 'HN',
  HK: 'HK',
  HU: 'HU',
  IS: 'IS',
  IN: 'IN',
  ID: 'ID',
  IQ: 'IQ',
  IE: 'IE',
  IM: 'IM',
  IL: 'IL',
  IT: 'IT',
  JM: 'JM',
  JP: 'JP',
  JE: 'JE',
  JO: 'JO',
  KZ: 'KZ',
  KE: 'KE',
  KI: 'KI',
  KW: 'KW',
  KG: 'KG',
  LA: 'LA',
  LV: 'LV',
  LB: 'LB',
  LS: 'LS',
  LR: 'LR',
  LY: 'LY',
  LI: 'LI',
  LT: 'LT',
  LU: 'LU',
  MO: 'MO',
  MG: 'MG',
  MW: 'MW',
  MY: 'MY',
  MV: 'MV',
  ML: 'ML',
  MT: 'MT',
  MH: 'MH',
  MQ: 'MQ',
  MR: 'MR',
  MU: 'MU',
  YT: 'YT',
  MX: 'MX',
  FM: 'FM',
  MD: 'MD',
  MC: 'MC',
  MN: 'MN',
  ME: 'ME',
  MS: 'MS',
  MA: 'MA',
  MZ: 'MZ',
  NA: 'NA',
  NR: 'NR',
  NP: 'NP',
  NL: 'NL',
  NC: 'NC',
  NZ: 'NZ',
  NI: 'NI',
  NE: 'NE',
  NG: 'NG',
  NU: 'NU',
  NF: 'NF',
  MP: 'MP',
  MK: 'MK',
  NO: 'NO',
  OM: 'OM',
  PK: 'PK',
  PW: 'PW',
  PS: 'PS',
  PA: 'PA',
  PG: 'PG',
  PY: 'PY',
  PE: 'PE',
  PH: 'PH',
  PN: 'PN',
  PL: 'PL',
  PT: 'PT',
  PR: 'PR',
  QA: 'QA',
  RE: 'RE',
  RO: 'RO',
  RW: 'RW',
  SH: 'SH',
  KN: 'KN',
  LC: 'LC',
  PM: 'PM',
  VC: 'VC',
  WS: 'WS',
  SM: 'SM',
  ST: 'ST',
  SA: 'SA',
  SN: 'SN',
  RS: 'RS',
  SC: 'SC',
  SL: 'SL',
  SG: 'SG',
  SK: 'SK',
  SI: 'SI',
  SB: 'SB',
  SO: 'SO',
  ZA: 'ZA',
  GS: 'GS',
  KR: 'KR',
  ES: 'ES',
  LK: 'LK',
  SD: 'SD',
  SR: 'SR',
  SJ: 'SJ',
  SE: 'SE',
  CH: 'CH',
  TJ: 'TJ',
  TZ: 'TZ',
  TH: 'TH',
  TL: 'TL',
  TG: 'TG',
  TK: 'TK',
  TO: 'TO',
  TT: 'TT',
  TN: 'TN',
  TR: 'TR',
  TM: 'TM',
  TC: 'TC',
  TV: 'TV',
  UG: 'UG',
  UA: 'UA',
  AE: 'AE',
  GB: 'GB',
  US: 'US',
  UM: 'UM',
  UY: 'UY',
  VI: 'VI',
  UZ: 'UZ',
  VU: 'VU',
  VE: 'VE',
  VN: 'VN',
  WF: 'WF',
  EH: 'EH',
  YE: 'YE',
  ZM: 'ZM',
  ZW: 'ZW',
  ALL: 'all',
} as const;

export type ProjectCountryCode =
  (typeof ProjectCountryCode)[keyof typeof ProjectCountryCode];

export type ProjectCitiesLegacy = {
  location: Location;
};

export type ProjectLocationInPersonLegacy = {
  id: string;
  address1: string;
  zipcode: string;
  city: string;
  region: string;
  state: string;
  country: string;
  formattedAddress: string;
  latitude: number;
  longitude: number;
};

export const KindOfResearch = {
  1: 1,
  2: 2,
  3: 3,
  4: 4,
  5: 5,
  6: 6,
  8: 8,
  9: 9,
  10: 10,
  11: 11,
  12: 12,
} as const;

export type KindOfResearch =
  (typeof KindOfResearch)[keyof typeof KindOfResearch];

export type UpdateDefaultCreditCardDto = {
  token: string;
};

export type UpdateCreditCardDto = {
  billingAddress?: CreditCardBillingAddressDto;
  /**
   * Format: MM/YYYY
   */
  expirationDate?: string;
  token: string;
  deviceData?: string;
  cvv?: string;
};

export type CreditCardBillingAddressDto = {
  streetAddress?: string;
  postalCode?: string;
};

export type CreateCreditCardDto = {
  paymentMethodNonce: string;
  cardholderName: string;
  billingAddress: CreditCardBillingAddressDto;
  deviceData: string;
};

export type ClientTokenDto = {
  clientToken?: string;
};

export type UpdateRoundtableVerificationDto = {
  id?: string;
  createdAt?: string;
  surveyResponseId?: string;
  screenerQuestionAnswer?: Array<RoundtableVerificationScreenerQuestions>;
  response?: RoundtableVerificationResponse;
  updatedAt?: string;
};

export type RoundtableVerificationResponse = {
  error: boolean;
  flagged: boolean;
  num_checks_failed: number;
  response_groups: {
    [key: string]: unknown;
  };
  checks: {
    [key: string]: unknown;
  };
  effort_ratings: {
    [key: string]: unknown;
  };
  model: string;
  problem?: string;
};

export const RoundtableVerificationFlag = {
  AUTOMATED_TEST_GIBBERISH: 'Automated test: Gibberish',
  AUTOMATED_TEST_OFF_TOPIC: 'Automated test: Off-topic',
  LOW_EFFORT: 'Low-effort',
  AUTOMATED_TEST_GPT: 'Automated test: GPT',
  CROSS_DUPLICATE_RESPONSE: 'Cross-duplicate response',
  SELF_DUPLICATE_RESPONSE: 'Self-duplicate response',
  AUTOMATED_TEST_PROFANE: 'Automated test: Profane',
  RESPONSE_PASTED: 'Response pasted',
  PROGRAMMATIC_ENTRY: 'Programmatic entry',
  UNNATURAL_TYPING: 'Unnatural typing',
  GPT_PASTE_ARTIFACTS: 'GPT paste artifacts',
} as const;

export type RoundtableVerificationFlag =
  (typeof RoundtableVerificationFlag)[keyof typeof RoundtableVerificationFlag];

export const QuestionType = {
  1: 1,
  2: 2,
  3: 3,
  4: 4,
  5: 5,
  6: 6,
  7: 7,
  8: 8,
  9: 9,
  10: 10,
  11: 11,
} as const;

export type QuestionType = (typeof QuestionType)[keyof typeof QuestionType];

export type RoundtableVerificationScreenerQuestions = {
  questionId: string;
  questionType: QuestionType;
  questionText: string;
  answerText: string;
  checks?: Array<RoundtableVerificationFlag>;
};

export type RoundtableVerification = {
  id: string;
  createdAt: string;
  userId: string;
  profileId: string;
  projectId: string;
  teamId: string;
  organizationId: string;
  surveyResponseId: string;
  screenerQuestionAnswer?: Array<RoundtableVerificationScreenerQuestions>;
  response?: RoundtableVerificationResponse;
  updatedAt?: string;
};

export type CreateRoundtableVerificationDto = {
  id: string;
  createdAt: string;
  surveyResponseId: string;
  screenerQuestionAnswer?: Array<RoundtableVerificationScreenerQuestions>;
  response?: RoundtableVerificationResponse;
  updatedAt?: string;
};

export type ClearbitRiskHistory = {
  id: string;
  createdAt: string;
  userId: string;
  profileId?: string;
  riskData?: ClearbitRiskData;
  riskError?: {
    [key: string]: unknown;
  };
  ip?: string;
  updatedAt?: string;
};

export const ClearbitRiskDataRiskLevel = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
} as const;

export type ClearbitRiskDataRiskLevel =
  (typeof ClearbitRiskDataRiskLevel)[keyof typeof ClearbitRiskDataRiskLevel];

export type ClearbitRiskDataRisk = {
  level: ClearbitRiskDataRiskLevel;
  score: number;
  reasons: Array<string>;
};

export type ClearbitRiskDataIp = {
  proxy?: boolean;
  geoMatch?: boolean;
  blacklisted?: boolean;
  rateLimited?: boolean;
};

export type ClearbitRiskDataAddress = {
  geoMatch?: boolean;
};

export type ClearbitRiskDataEmail = {
  valid?: boolean;
  socialMatch?: boolean;
  companyMatch?: boolean;
  nameMatch?: boolean;
  disposable?: boolean;
  freeProvider?: boolean;
  blacklisted?: boolean;
};

export type ClearbitRiskData = {
  id: string;
  live: boolean;
  fingerprint: boolean;
  email: ClearbitRiskDataEmail;
  address: ClearbitRiskDataAddress;
  ip: ClearbitRiskDataIp;
  risk: ClearbitRiskDataRisk;
};

export type CreateClearbitRiskHistoryDto = {
  userId: string;
  profileId?: string;
  riskData?: ClearbitRiskData;
  riskError?: {
    [key: string]: unknown;
  };
  ip?: string;
};

export type UsersSession = {
  id: string;
  createdAt: string;
  userId: string;
  clientOrigin: UserSessionClientOrigin;
  actionType?: UsersSessionActionType;
  updatedAt?: string;
};

export const UsersSessionActionType = {
  SCREENER_SUBMIT: 'screenerSubmit',
  LOGIN: 'login',
  SSO_LOGIN: 'ssoLogin',
} as const;

export type UsersSessionActionType =
  (typeof UsersSessionActionType)[keyof typeof UsersSessionActionType];

export type UserAgentCpu = {
  architecture?: string;
};

export type UserAgentDevice = {
  vendor?: string;
  model?: string;
  type?: string;
};

export type UserAgentOs = {
  name?: string;
  version?: string;
};

export type UserAgentEngine = {
  name?: string;
  version?: string;
};

export type UserAgentBrowser = {
  name?: string;
  version?: string;
  major?: string;
};

export type UserSessionUserAgent = {
  ua: string;
  browser?: UserAgentBrowser;
  engine?: UserAgentEngine;
  os?: UserAgentOs;
  device?: UserAgentDevice;
  cpu?: UserAgentCpu;
};

export type UserSessionClientOrigin = {
  ip: string;
  fingerprint: string;
  userAgent?: UserSessionUserAgent;
};

export type CreateUserSessionDto = {
  userId: string;
  clientOrigin: UserSessionClientOrigin;
  actionType?: UsersSessionActionType;
};

export type EmailEvent = {
  id: string;
  createdAt: string;
  userId?: string;
  teamId?: string;
  organizationId?: string;
  projectId?: string;
  type?: NotificationType;
  templateName: string;
  status: EmailEventStatus;
  updatedAt?: string;
};

export const EmailEventStatus = { SENT: 'SENT', SKIPPED: 'SKIPPED' } as const;

export type EmailEventStatus =
  (typeof EmailEventStatus)[keyof typeof EmailEventStatus];

export const NotificationType = {
  RESPONDENT_BOOKING_REMINDER: 'respondent-booking-reminder',
  RESPONDENT_BOOKING_REMINDER_EXTERNAL: 'respondent-booking-reminder-external',
  RESPONDENT_INSTANT_MATCHER: 'respondent-instant-matcher',
  RESPONDENT_INSTANT_MATCHER_PERSONALIZED:
    'respondent-instant-matcher-personalized',
  RESPONDENT_INSTANT_MATCHER_REFERRER: 'respondent-instant-matcher-referrer',
  RESPONDENT_INSTANT_MATCHER_RECONTACT: 'respondent-instant-matcher-recontact',
  RESPONDENT_INVITATION_REMINDER: 'respondent-invitation-reminder',
  RESPONDENT_TASK_COMPLETION_REMINDER: 'respondent-task-completion-reminder',
  RESPONDENT_INVITATION_REMINDER_EXTERNAL:
    'respondent-invitation-reminder-external',
  RESEARCHER_PROJECT_PUBLISH_REMINDER: 'researcher-project-publish-reminder',
  RESEARCHER_PROJECT_EXPIRED_NOTIFICATION:
    'researcher-project-expired-notification',
} as const;

export type NotificationType =
  (typeof NotificationType)[keyof typeof NotificationType];

export type CreateEmailEventDto = {
  userId?: string;
  teamId?: string;
  organizationId?: string;
  projectId?: string;
  type?: NotificationType;
  templateName: string;
  status: EmailEventStatus;
};

export type WebhookReceieved = {
  id: string;
  createdAt: string;
  /**
   * The organizationId to which the webhook belongs.
   */
  organizationId?: string;
  /**
   * The teamId to which the webhook belongs.
   */
  teamId?: string;
  webhookId?: string;
  /**
   * The URL the webhook payload was sent to.
   */
  url: string;
  body?: WebhookBody;
  updatedAt?: string;
};

export type WebhookUpdatedField = {
  name: string;
  oldValue?: string;
  newValue: string;
};

export const ProjectType = { API: 'API', MARKETPLACE: 'MARKETPLACE' } as const;

export type ProjectType = (typeof ProjectType)[keyof typeof ProjectType];

export const WebhookResourceType = {
  SCREENER_RESPONSES: 'screener-responses',
  PROJECTS: 'projects',
} as const;

export type WebhookResourceType =
  (typeof WebhookResourceType)[keyof typeof WebhookResourceType];

export type WebhookResource = {
  id: string;
  parentId?: string;
  projectId?: string;
  type: WebhookResourceType;
  projectType?: ProjectType;
  updatedFields?: Array<WebhookUpdatedField>;
};

export type WebhookPayload = {
  resource: WebhookResource;
};

export const WebhookEventType = {
  PROJECTS_UPDATED: 'PROJECTS.UPDATED',
  SCREENER_RESPONSES_CREATED: 'SCREENER_RESPONSES.CREATED',
  SCREENER_RESPONSES_UPDATED: 'SCREENER_RESPONSES.UPDATED',
  MESSAGES_CREATED: 'MESSAGES.CREATED',
  CONVERSATIONS_CREATED: 'CONVERSATIONS.CREATED',
  QUOTAS_SEGMENT_FILLED: 'QUOTAS.SEGMENT_FILLED',
  QUOTAS_FILLED: 'QUOTAS.FILLED',
} as const;

export type WebhookEventType =
  (typeof WebhookEventType)[keyof typeof WebhookEventType];

export type WebhookBody = {
  event: WebhookEventType;
  uuid: string;
  created: string;
  payload: WebhookPayload;
};

export type CreateWebhookReceivedDto = {
  /**
   * The organizationId to which the webhook belongs.
   */
  organizationId?: string;
  /**
   * The teamId to which the webhook belongs.
   */
  teamId?: string;
  webhookId?: string;
  /**
   * The URL the webhook payload was sent to.
   */
  url: string;
  body?: WebhookBody;
};

export type SimulateWebhookDto = {
  event: WebhookEventType;
  screenerResponseUpdatedType?: WebhookScreenerResponseUpdatedType;
};

export const WebhookScreenerResponseUpdatedType = {
  STATUS_PAID: 'STATUS.PAID',
  STATUS_CANCELLED: 'STATUS.CANCELLED',
  REJECTED_TRUE: 'REJECTED.TRUE',
  REJECTED_FALSE: 'REJECTED.FALSE',
} as const;

export type WebhookScreenerResponseUpdatedType =
  (typeof WebhookScreenerResponseUpdatedType)[keyof typeof WebhookScreenerResponseUpdatedType];

export type WebhookEventTypesDto = {
  eventTypes: Array<WebhookEventType>;
};

export type WebhookDto = {
  id: string;
  /**
   * The organizationId to which the webhook belongs.
   */
  organizationId: string;
  /**
   * The teamId to which the webhook belongs.
   */
  teamId?: string;
  /**
   * The URL to send the webhook payload to.
   */
  url: string;
  /**
   * The secret key to be used to sign the webhook payload.
   */
  privateKey: string;
};

export type CreateWebhookDto = {
  /**
   * The URL to send the webhook payload to.
   */
  url: string;
};

export type PlaidLinkTokenResponseDto = {
  link_token?: string;
  expiration?: string;
  request_id?: string;
  hosted_link_url?: string;
  linkedin_identity_verification?: boolean;
  persona_inquiry?: PersonaInquirySessionDto;
};

export type PersonaInquirySessionDto = {
  inquiry_id: string;
  session_token: string;
  /**
   * Persona environment (`env_...`) the inquiry lives in. The SDK defaults
   * to the first *production* environment, so this pins sandbox inquiries
   * to sandbox. Absent only when not configured.
   */
  environment_id?: string;
};

export type TwilioVerifyCodeDto = {
  phone: string;
  countryCode?: string;
};

export type TwilioCreateVerificationDto = {
  phone: string;
  countryCode?: string;
};

export type VerifyCodeDto = {
  phone: string;
  countryCode?: string;
};

export type CreateVerificationDto = {
  phone: string;
  countryCode?: string;
};

export type SetTestProfileRiskSignalsDto = {
  score?: number;
  /**
   * true stores a valid US mobile lookup; false removes the lookup.
   */
  hasTwilioPhoneData?: boolean;
};

export type UpdateTestUserDto = {
  trustLevel?: TrustLevel;
  isActive?: boolean;
  isEmailVerified?: boolean;
};

export const TrustLevel = {
  0: 0,
  50: 50,
  95: 95,
  100: 100,
} as const;

export type TrustLevel = (typeof TrustLevel)[keyof typeof TrustLevel];

export type AddFeatureFlagDenyOrganizationDto = {
  name: string;
  organizationId: string;
};

export type AddFeatureFlagAllowOrganizationDto = {
  name: string;
  organizationId: string;
};

export type BatchInviteTeamRespondentsResponseDto = {
  /**
   * Every invitation written, whichever outcome followed it.
   */
  invitations: Array<Invitation>;
  /**
   * The participants nothing was written for. Kept alongside `outcomes` for
   * clients that only count; `outcomes` says why each was skipped.
   */
  skipped: Array<InviteRespondentDto>;
  /**
   * One entry per requested participant, in request order.
   */
  outcomes: Array<BatchInviteOutcomeDto>;
};

export const ScreenerResponseStatus = {
  DRAFT: 'DRAFT',
  PENDING: 'PENDING',
  EXTERNAL_SCREENER_IN_PROGRESS: 'EXTERNAL_SCREENER_IN_PROGRESS',
  INVITED: 'INVITED',
  SCHEDULED: 'SCHEDULED',
  ATTENDED: 'ATTENDED',
  NOSHOW: 'NOSHOW',
  INVALIDRESPONSE: 'INVALIDRESPONSE',
  REJECTEDQUALITY: 'REJECTEDQUALITY',
  PAID: 'PAID',
  CANCELLED: 'CANCELLED',
} as const;

export type ScreenerResponseStatus =
  (typeof ScreenerResponseStatus)[keyof typeof ScreenerResponseStatus];

export const WaivedAdmissionHoldReason = {
  QUOTA_FULL: 'QUOTA_FULL',
  HELD_FOR_REVIEW: 'HELD_FOR_REVIEW',
  NOT_INVITABLE: 'NOT_INVITABLE',
} as const;

export type WaivedAdmissionHoldReason =
  (typeof WaivedAdmissionHoldReason)[keyof typeof WaivedAdmissionHoldReason];

export const BatchInviteOutcome = {
  ADMITTED: 'ADMITTED',
  INVITED_TO_APPLY: 'INVITED_TO_APPLY',
  AWAITING_RESEARCHER: 'AWAITING_RESEARCHER',
  SKIPPED_ALREADY_RESPONDED: 'SKIPPED_ALREADY_RESPONDED',
  SKIPPED_ALREADY_INVITED: 'SKIPPED_ALREADY_INVITED',
  SKIPPED_PROJECT_LINK_REQUIRED: 'SKIPPED_PROJECT_LINK_REQUIRED',
  SKIPPED_PROJECT_CLOSED: 'SKIPPED_PROJECT_CLOSED',
  SKIPPED_PROFILE_DISABLED: 'SKIPPED_PROFILE_DISABLED',
  SKIPPED_ERROR: 'SKIPPED_ERROR',
} as const;

export type BatchInviteOutcome =
  (typeof BatchInviteOutcome)[keyof typeof BatchInviteOutcome];

export type BatchInviteOutcomeDto = {
  profileId: string;
  /**
   * First name and surname initial, for the researcher's summary. Absent when the profile could not be read.
   */
  name?: string;
  outcome: BatchInviteOutcome;
  holdReason?: WaivedAdmissionHoldReason;
  existingStatus?: ScreenerResponseStatus;
  /**
   * With `SKIPPED_ALREADY_RESPONDED`: whether that response qualified.
   */
  existingQualified?: boolean;
};

export type InviteRespondentDto = {
  profileId: string;
  skipScreenerQuestions: boolean;
  /**
   * Follow-up provenance: the project this participant is being shared from.
   * Per-participant because a program roster mixes people from different
   * source projects. Validated to belong to the same team as the target.
   */
  sourceProjectId?: string;
};

export type InvitationProject = {
  id: string;
  name: string;
  inviteCode: string;
  incentive: number;
};

export type InvitationProfile = {
  id: string;
  firstName: string;
  lastName: string;
};

export const RecruitmentSource = {
  MARKETPLACE: 'marketplace',
  PARTICIPANT_DATABASE: 'participantDatabase',
  EXTERNAL_PARTICIPANTS: 'externalParticipants',
} as const;

export type RecruitmentSource =
  (typeof RecruitmentSource)[keyof typeof RecruitmentSource];

export type Invitation = {
  id: string;
  createdAt: string;
  profileId: string;
  userId: string;
  organizationId: string;
  teamId: string;
  projectId: string;
  senderId: string;
  message: string;
  eventUid?: string;
  remoteCommunicationDetails?: string;
  skipScreenerQuestions: boolean;
  recruitmentSource?: RecruitmentSource;
  /**
   * Provenance for cross-project (follow-up) invitations: the project this
   * participant was shared FROM, always a project of the same team. Absent on
   * ordinary panel or same-project invitations, which is what makes the
   * "shared with A / new from panel" reporting possible — see
   * `../../programs/programs.md`. Unrelated to
   * `ProfileProjectMatch.sourceProjectId` (matching subsystem).
   */
  sourceProjectId?: string;
  /**
   * The messaging conversation attached to this invitee for this project,
   * mirroring `ScreenerResponse.conversationUid`. Written by the conversations
   * service on create so a person with no screener response yet (asked, not
   * heard back) still resolves to their existing thread — the messaging
   * create endpoint does not dedupe, so without this the program roster's
   * Message tab would fork a parallel conversation on every reopen.
   */
  conversationUid?: string;
  profile: InvitationProfile;
  project: InvitationProject;
  updatedAt?: string;
};

export type BatchInviteTeamRespondentsDto = {
  projectId: string;
  participants: Array<InviteRespondentDto>;
  message: string;
};

export type TeamRespondentsPaginatedDto = {
  /**
   * The total number of results possible, which is only provided in the response when `includeCount` is passed as `true` within a request.
   */
  totalResults?: number;
  page: number;
  pageSize: number;
  results: Array<TeamRespondentDto>;
};

export const ReviewVersions = { V1: 'V1', V2: 'V2' } as const;

export type ReviewVersions =
  (typeof ReviewVersions)[keyof typeof ReviewVersions];

export type ReviewV2 = {
  id: string;
  createdAt: string;
  surveyResponseId: string;
  teamId: string;
  researcherId: string;
  text: string;
  __type: ReviewVersions;
  questions: {
    [key: string]: unknown;
  };
  updatedAt?: string;
};

export const ProjectVisibility = {
  MARKETPLACE: 'marketplace',
  RMS: 'rms',
} as const;

export type ProjectVisibility =
  (typeof ProjectVisibility)[keyof typeof ProjectVisibility];

export const ParticipantAudienceType = { 1: 1, 2: 2 } as const;

export type ParticipantAudienceType =
  (typeof ParticipantAudienceType)[keyof typeof ParticipantAudienceType];

export type PricingTier = {
  price: number;
  code: string;
};

export type ScreenerResponseProject = {
  id: string;
  /**
   * Character Limit: min 3 and max 255
   */
  name: string;
  userId: string;
  pricingTier?: PricingTier;
  /**
   * Number Limit: min 0 and max 5000
   */
  incentive: number;
  organizationId: string;
  teamId: string;
  numQuestions?: number;
  talkToAudience?: ParticipantAudienceType;
  visibility?: ProjectVisibility;
  automations?: ProjectAutomations;
  referralBonus?: number | null;
  /**
   * Audit record for the external-screener flow (PX-376): the project's
   * `externalScreenerLink` as it stood when this snapshot was last rebuilt by
   * the reducer, so a response shows which partner URL the project pointed
   * at when the participant applied — including `null`/absent for a
   * participant who applied before a partner configured the link
   * post-publish. Like the rest of this snapshot it refreshes on every
   * reducer pass (`loadProject`); the URL the participant was actually
   * handed off with is stamped once, on first handoff, in
   * `ScreenerResponse.externalScreener.url` and never rewritten.
   */
  externalScreenerLink?: string | null;
};

export const ScreenerResponseCode = {
  DRAFT: 'DRAFT',
  MANUAL_REVIEW_REJECTED: 'MANUAL_REVIEW_REJECTED',
  REJECTED_IP: 'REJECTED_IP',
  REJECTED_VPN: 'REJECTED_VPN',
  REJECTED_WORK_DOMAIN: 'REJECTED_WORK_DOMAIN',
  REJECTED_PROFILE: 'REJECTED_PROFILE',
  REJECTED_PROFILE_TWILIO: 'REJECTED_PROFILE_TWILIO',
  REJECTED_PROFILE_RISK_LEVEL: 'REJECTED_PROFILE_RISK_LEVEL',
  MARKETPLACE_PAID: 'MARKETPLACE_PAID',
  FLAGGED: 'FLAGGED',
  DATA_AUTOMATED: 'DATA_AUTOMATED',
  OK: 'OK',
  PENDING_PROFILE: 'PENDING_PROFILE',
  CONVERTED_TO_RESEARCHER: 'CONVERTED_TO_RESEARCHER',
  QUOTA_FULL: 'QUOTA_FULL',
} as const;

export type ScreenerResponseCode =
  (typeof ScreenerResponseCode)[keyof typeof ScreenerResponseCode];

export const ScreenerResponseVisibilityStatus = {
  DRAFT: 'DRAFT',
  DISMISSED: 'DISMISSED',
  VISIBLE: 'VISIBLE',
  ONHOLD: 'ONHOLD',
} as const;

export type ScreenerResponseVisibilityStatus =
  (typeof ScreenerResponseVisibilityStatus)[keyof typeof ScreenerResponseVisibilityStatus];

export const ScreenerResponseVisibilityName = {
  DISMISSED_PROFILE_REJECTED: 'DISMISSED: Profile Rejected',
  DISMISSED_MANUAL_REVIEW_REJECTED: 'DISMISSED: Manual Review Rejected',
  MARKETPLACE_PAID: 'MARKETPLACE_PAID',
  DISMISSED_FLAGGED: 'DISMISSED: Flagged',
  DISMISSED_AUTO_REJECTED: 'DISMISSED: Auto Rejected',
  VISIBLE: 'VISIBLE',
  ONHOLD_PROFILE_INCOMPLETE: 'ONHOLD: Profile Incomplete',
  DRAFT: 'DRAFT',
  DISMISSED_CONVERTED_TO_RESEARCHER: 'DISMISSED: Converted to Researcher',
  DISMISSED_QUOTA_FULL: 'DISMISSED: Quota Full',
} as const;

export type ScreenerResponseVisibilityName =
  (typeof ScreenerResponseVisibilityName)[keyof typeof ScreenerResponseVisibilityName];

export type ScreenerResponseVisibility = {
  name: ScreenerResponseVisibilityName;
  status: ScreenerResponseVisibilityStatus;
  code: ScreenerResponseCode;
};

export type SimpleTeamProfileScreenerDto = {
  id: string;
  createdAt: string;
  projectId: string;
  userId: string;
  qualified: boolean;
  percentage: number;
  status: ScreenerResponseStatus;
  isMatch: boolean;
  visibility: ScreenerResponseVisibility;
  organizationFlag: boolean;
  statusUpdatedAt?: string | null;
  project?: ScreenerResponseProject;
  review?: ReviewV2;
  updatedAt?: string;
};

export const Roles = {
  SUPERADMIN: 'superadmin',
  SERVICE: 'service',
  ADMIN: 'admin',
  RESEARCHER: 'researcher',
  RESPONDENT: 'respondent',
  PAYMENTMANAGER: 'paymentmanager',
  USERMANAGER: 'usermanager',
  PROMOTION_MANAGER: 'promotionManager',
  ADMIN_PRICING: 'adminPricing',
  LEADS_ADMIN: 'leadsAdmin',
  DEVELOPER: 'developer',
  EXTERNAL_PARTICIPANT: 'externalParticipant',
} as const;

export type Roles = (typeof Roles)[keyof typeof Roles];

export type SimpleUser = {
  id: string;
  createdAt: string;
  email: string;
  firstName: string;
  lastName: string;
  isActive: boolean;
  referrerUserId?: string | null;
  timezone: string;
  phone?: string | null;
  picture?: string | null;
  roles: Array<Roles>;
  /**
   * Researcher Only
   */
  pendingReview?: boolean;
  /**
   * Researcher Only
   */
  approvedBy?: string | null;
  company?: string | null;
  isEmailVerified: boolean;
  lastLoginAt: string;
  uri?: string;
};

export type ProfileTrustedCompany = {
  companyId: string;
  name: string;
  domain: string;
  createdAt: string;
};

export type SimpleTopic = {
  id: string;
  name: string;
};

export type Skill = {
  id: string;
  createdAt: string;
  name: string;
  slug: string;
  validated: boolean;
  isCanonical?: boolean;
  type: string;
  count?: number;
  updatedAt?: string;
};

export type SocialLink = {
  url?: string | null;
  createdAt?: string | null;
  /**
   * Stable provider-side member id (e.g. LinkedIn identityMe `id`). Used to dedupe verifications across profiles.
   */
  id?: string | null;
};

export type ProfileSocialLinks = {
  linkedin?: SocialLink;
  facebook?: SocialLink;
  twitter?: SocialLink;
  instagram?: SocialLink;
  github?: SocialLink;
};

export type ProfileSocial = {
  linkedin: {
    [key: string]: unknown;
  } | null;
  facebook: {
    [key: string]: unknown;
  } | null;
};

export type ProfileLocationLoc = {
  type: string;
  coordinates: Array<number>;
};

export type ProfileLocationCityLocation = {
  latitude: number;
  longitude: number;
};

export const CountryCode = {
  AF: 'AF',
  AX: 'AX',
  AL: 'AL',
  DZ: 'DZ',
  AS: 'AS',
  AD: 'AD',
  AO: 'AO',
  AI: 'AI',
  AQ: 'AQ',
  AG: 'AG',
  AR: 'AR',
  AM: 'AM',
  AW: 'AW',
  AU: 'AU',
  AT: 'AT',
  AZ: 'AZ',
  BS: 'BS',
  BH: 'BH',
  BD: 'BD',
  BB: 'BB',
  BY: 'BY',
  BE: 'BE',
  BZ: 'BZ',
  BJ: 'BJ',
  BM: 'BM',
  BT: 'BT',
  BO: 'BO',
  BA: 'BA',
  BW: 'BW',
  BV: 'BV',
  BR: 'BR',
  VG: 'VG',
  IO: 'IO',
  BN: 'BN',
  BG: 'BG',
  BF: 'BF',
  BI: 'BI',
  CV: 'CV',
  KH: 'KH',
  CM: 'CM',
  CA: 'CA',
  KY: 'KY',
  CF: 'CF',
  TD: 'TD',
  CL: 'CL',
  CN: 'CN',
  CX: 'CX',
  CC: 'CC',
  CO: 'CO',
  KM: 'KM',
  CG: 'CG',
  CK: 'CK',
  CR: 'CR',
  HR: 'HR',
  CU: 'CU',
  CW: 'CW',
  CY: 'CY',
  CZ: 'CZ',
  CI: 'CI',
  CD: 'CD',
  DK: 'DK',
  DJ: 'DJ',
  DM: 'DM',
  DO: 'DO',
  EC: 'EC',
  EG: 'EG',
  SV: 'SV',
  GQ: 'GQ',
  ER: 'ER',
  EE: 'EE',
  SZ: 'SZ',
  ET: 'ET',
  FK: 'FK',
  FO: 'FO',
  FJ: 'FJ',
  FI: 'FI',
  FR: 'FR',
  GF: 'GF',
  PF: 'PF',
  TF: 'TF',
  GA: 'GA',
  GM: 'GM',
  GE: 'GE',
  DE: 'DE',
  GH: 'GH',
  GI: 'GI',
  GR: 'GR',
  GL: 'GL',
  GD: 'GD',
  GP: 'GP',
  GU: 'GU',
  GT: 'GT',
  GG: 'GG',
  GN: 'GN',
  GW: 'GW',
  GY: 'GY',
  HT: 'HT',
  HM: 'HM',
  VA: 'VA',
  HN: 'HN',
  HK: 'HK',
  HU: 'HU',
  IS: 'IS',
  IN: 'IN',
  ID: 'ID',
  IR: 'IR',
  IQ: 'IQ',
  IE: 'IE',
  IM: 'IM',
  IL: 'IL',
  IT: 'IT',
  JM: 'JM',
  JP: 'JP',
  JE: 'JE',
  JO: 'JO',
  KZ: 'KZ',
  KE: 'KE',
  KI: 'KI',
  KW: 'KW',
  KG: 'KG',
  LA: 'LA',
  LV: 'LV',
  LB: 'LB',
  LS: 'LS',
  LR: 'LR',
  LY: 'LY',
  LI: 'LI',
  LT: 'LT',
  LU: 'LU',
  MO: 'MO',
  MG: 'MG',
  MW: 'MW',
  MY: 'MY',
  MV: 'MV',
  ML: 'ML',
  MT: 'MT',
  MH: 'MH',
  MQ: 'MQ',
  MR: 'MR',
  MU: 'MU',
  YT: 'YT',
  MX: 'MX',
  FM: 'FM',
  MD: 'MD',
  MC: 'MC',
  MN: 'MN',
  ME: 'ME',
  MS: 'MS',
  MA: 'MA',
  MZ: 'MZ',
  NA: 'NA',
  NR: 'NR',
  NP: 'NP',
  NL: 'NL',
  NC: 'NC',
  NZ: 'NZ',
  NI: 'NI',
  NE: 'NE',
  NG: 'NG',
  NU: 'NU',
  NF: 'NF',
  KP: 'KP',
  MK: 'MK',
  MP: 'MP',
  NO: 'NO',
  OM: 'OM',
  PK: 'PK',
  PW: 'PW',
  PS: 'PS',
  PA: 'PA',
  PG: 'PG',
  PY: 'PY',
  PE: 'PE',
  PH: 'PH',
  PN: 'PN',
  PL: 'PL',
  PT: 'PT',
  PR: 'PR',
  QA: 'QA',
  RO: 'RO',
  RW: 'RW',
  RE: 'RE',
  SH: 'SH',
  KN: 'KN',
  LC: 'LC',
  PM: 'PM',
  VC: 'VC',
  WS: 'WS',
  SM: 'SM',
  ST: 'ST',
  SA: 'SA',
  SN: 'SN',
  RS: 'RS',
  SC: 'SC',
  SL: 'SL',
  SG: 'SG',
  SK: 'SK',
  SI: 'SI',
  SB: 'SB',
  SO: 'SO',
  ZA: 'ZA',
  GS: 'GS',
  KR: 'KR',
  ES: 'ES',
  LK: 'LK',
  SD: 'SD',
  SR: 'SR',
  SJ: 'SJ',
  SE: 'SE',
  CH: 'CH',
  TJ: 'TJ',
  TZ: 'TZ',
  TW: 'TW',
  TH: 'TH',
  TL: 'TL',
  TG: 'TG',
  TK: 'TK',
  TO: 'TO',
  TT: 'TT',
  TN: 'TN',
  TM: 'TM',
  TC: 'TC',
  TV: 'TV',
  TR: 'TR',
  VI: 'VI',
  UG: 'UG',
  UA: 'UA',
  AE: 'AE',
  GB: 'GB',
  UM: 'UM',
  US: 'US',
  UY: 'UY',
  UZ: 'UZ',
  VU: 'VU',
  VE: 'VE',
  VN: 'VN',
  WF: 'WF',
  EH: 'EH',
  YE: 'YE',
  ZM: 'ZM',
  ZW: 'ZW',
  ALL: 'all',
} as const;

export type CountryCode = (typeof CountryCode)[keyof typeof CountryCode];

export type ProfileLocationCity = {
  id: string;
  country: CountryCode;
  city: string;
  state?: string | null;
  formattedAddress: string;
  administrative_area_level_1?: string;
  postal_code?: string;
  locality?: string;
  location: ProfileLocationCityLocation;
  zip?: string | null;
};

export type ProfileLocation = {
  city: ProfileLocationCity;
  zipcode?: string | null;
  state?: string | null;
  loc: ProfileLocationLoc;
};

export const Income = {
  CONTRACT: 'contract',
  PAYROLL: 'payroll',
  OTHER: 'other',
} as const;

export type Income = (typeof Income)[keyof typeof Income];

export const JobFunction = {
  ACCOUNTING_AUDITING: 'accountingAuditing',
  ADMINISTRATIVE: 'administrative',
  ADVERTISING: 'advertising',
  ANALYST: 'analyst',
  ART_CREATIVE: 'artCreative',
  BUSINESS_DEVELOPMENT: 'businessDevelopment',
  CONSULTING: 'consulting',
  CUSTOMER_SERVICE: 'customerService',
  DISTRIBUTION: 'distribution',
  DESIGN: 'design',
  EDUCATION: 'education',
  FINANCE: 'finance',
  GENERAL_BUSINESS: 'generalBusiness',
  HEALTHCARE_PROVIDER: 'healthcareProvider',
  HUMAN_RESOURCES: 'humanResources',
  INFORMATION_TECHNOLOGY: 'informationTechnology',
  LEGAL: 'legal',
  MANAGEMENT: 'management',
  MANUFACTURING: 'manufacturing',
  MARKETING: 'marketing',
  OTHER: 'other',
  PUBLIC_RELATIONS: 'publicRelations',
  PURCHASING: 'purchasing',
  PRODUCT_MANAGEMENT: 'productManagement',
  PROJECT_MANAGEMENT: 'projectManagement',
  PRODUCTION: 'production',
  QUALITY_ASSURANCE: 'qualityAssurance',
  RESEARCH: 'research',
  SALES: 'sales',
  SCIENCE: 'science',
  STRATEGY_PLANNING: 'strategyPlanning',
  SUPPLY_CHAIN: 'supplyChain',
  TRAINING: 'training',
  WRITING_EDITING: 'writingEditing',
} as const;

export type JobFunction = (typeof JobFunction)[keyof typeof JobFunction];

export type JobTitle = {
  id: string;
  createdAt: string;
  name: string;
  slug: string;
  socCode?: SimpleSocOccupancy;
  profileCount?: number;
  emsiId?: string;
  emsiName?: string;
  emsiConfidence?: number;
  isCanonical?: boolean;
  mappingNames?: Array<string>;
  alternativeSocCodes?: Array<SimpleSocOccupancy>;
  seniority?: Array<SeniorityLevel>;
  jobFunctions?: Array<JobFunction>;
  siblingGroupId?: number;
  familyGroupId?: number;
  groupingVersion?: number;
  updatedAt?: string;
};

export const SeniorityLevel = {
  UNPAID: 'unpaid',
  STUDENT: 'student',
  TRAINING: 'training',
  ENTRYLEVEL: 'entrylevel',
  SENIOR: 'senior',
  MANAGER: 'manager',
  DIRECTOR: 'director',
  CHIEFOFFICER: 'chiefofficer',
  PARTNER: 'partner',
  VICEPRESIDENT: 'vicepresident',
} as const;

export type SeniorityLevel =
  (typeof SeniorityLevel)[keyof typeof SeniorityLevel];

export const EmploymentStatus = {
  OWNER: 'owner',
  EMPLOYEDFULLTIME: 'employedfulltime',
  EMPLOYEDPARTTIME: 'employedparttime',
  UNEMPLOYEDLOOKINGFORWORK: 'unemployedlookingforwork',
  UNEMPLOYED: 'unemployed',
  RETIRED: 'retired',
  DISABLED: 'disabled',
  HOMEMAKER: 'homemaker',
  FULLTIMESTUDENT: 'fulltimestudent',
  PARTTIMESTUDENT: 'parttimestudent',
} as const;

export type EmploymentStatus =
  (typeof EmploymentStatus)[keyof typeof EmploymentStatus];

export const CompanySize = {
  SELFEMPLOYED: 'selfemployed',
  '1_10': '1-10',
  '11_50': '11-50',
  '51_200': '51-200',
  '201_500': '201-500',
  '501_1000': '501-1000',
  '1001_5000': '1001-5000',
  '5001_10000': '5001-10000',
  10000: '10000+',
  ALL: 'all',
} as const;

export type CompanySize = (typeof CompanySize)[keyof typeof CompanySize];

export type ProfileEmployment = {
  email?: string | null;
  emailDomain?: string | null;
  isEmailVerified?: boolean;
  isLinkedInVerified?: boolean;
  company?: string | null;
  companySize?: CompanySize;
  businessOwner?: boolean;
  employmentStatus: EmploymentStatus;
  jobTitle?: string | null;
  jobTitleId?: string | null;
  jobTitleCanonicalId?: string | null;
  jobSiblingGroupId?: number | null;
  jobFamilyGroupId?: number | null;
  jobGroupingVersion?: number | null;
  seniorityLevel?: SeniorityLevel;
  normalizedJobTitle?: JobTitle | null;
  /**
   * Token for the legacy click-a-link work email verification.
   *
   *
   * (`verificationCodeHash` below). Still issued and honoured so links already
   * sitting in inboxes and the legacy Vue participant portal keep working;
   * remove once that portal no longer drives work email verification.
   *
   * @deprecated
   */
  verificationToken?: string;
  industry?: Array<string> | null;
  roleDescription?: string | null;
  relatedJobTitles?: Array<string> | null;
  /**
   * The company ID that is associated to the profile employment via an association
   * between the given company name and the given work email domain.
   * The ID itself may refer to a record in the `company`, `fullcontactcompany`,
   * or `clearbitcompany` collections.
   * If an association between the company name and work email domain does not exist,
   * then we set the field to `null` in the client.
   * If an association has _not yet_ been made, we leave this field `undefined`.
   */
  internalOrExternalCompanyId?: string | null;
  jobFunctions?: Array<JobFunction>;
  income?: Income;
  smallBusinessOwner?: boolean;
  updatedAt?: string | null;
  lastTrustedCompanyCheck?: string | null;
};

export const HouseholdIncome = {
  '<30000': '<30000',
  '30000_39999': '30000-39999',
  '40000_59999': '40000-59999',
  '60000_79999': '60000-79999',
  '80000_99999': '80000-99999',
  '100000_149999': '100000-149999',
  '150000_199999': '150000-199999',
  200000: '200000+',
  ALL: 'all',
  ALL2: 'all',
} as const;

export type HouseholdIncome =
  (typeof HouseholdIncome)[keyof typeof HouseholdIncome];

export const EducationLevel = {
  SOMEHIGHSCHOOL: 'somehighschool',
  HIGHSCHOOLGRADUATE: 'highschoolgraduate',
  SOMECOLLEGENODEGREE: 'somecollegenodegree',
  TRADETECHNICALVOCATIONALTRAINING: 'tradetechnicalvocationaltraining',
  ASSOCIATEDEGREE: 'associatedegree',
  BACHELORDEGREE: 'bachelordegree',
  MASTERDEGREE: 'masterdegree',
  DOCTORATEDEGREE: 'doctoratedegree',
  N_A: 'N/A',
} as const;

export type EducationLevel =
  (typeof EducationLevel)[keyof typeof EducationLevel];

export const Ethnicities = {
  AMERICANINDIANALASKANNATIVE: 'americanindianalaskannative',
  HISPANICLATINO: 'hispaniclatino',
  BLACKORAFRICANAMERICAN: 'blackorafricanamerican',
  ASIANPACIFICISLANDER: 'asianpacificislander',
  WHITECAUCASIAN: 'whitecaucasian',
  MIDDLEEASTERN: 'middleeastern',
  MULTIPLEETHNICITY: 'multipleethnicity',
  PREFERNOTDISCLOSE: 'prefernotdisclose',
  N_A: 'N/A',
} as const;

export type Ethnicities = (typeof Ethnicities)[keyof typeof Ethnicities];

export const Gender = {
  MALE: 'male',
  FEMALE: 'female',
  OTHER: 'other',
  NONBINARY: 'nonbinary',
  TRANSMALE: 'transmale',
  TRANSFEMALE: 'transfemale',
  PREFERNOTTOANSWER: 'prefernottoanswer',
  N_A: 'N/A',
} as const;

export type Gender = (typeof Gender)[keyof typeof Gender];

export type ProfileDemographics = {
  gender: Gender;
  ethnicity: Ethnicities;
  educationLevel: EducationLevel;
  dateOfBirth: string;
  birthDate?: string;
  householdIncome?: HouseholdIncome;
  /**
   * profile snapshots such as surveyresponses.profile.demographics. Known to
   * be unreliable. Use dateOfBirth directly instead.
   *
   * @deprecated
   */
  age?: string;
};

export const RespondentStatus = {
  APPROVED: 'APPROVED',
  VERIFIED: 'VERIFIED',
  PENDING: 'PENDING',
  REJECTED: 'REJECTED',
} as const;

export type RespondentStatus =
  (typeof RespondentStatus)[keyof typeof RespondentStatus];

export type TeamRespondentProfileDto = {
  id: string;
  createdAt: string;
  userId: string;
  firstName: string;
  lastName: string;
  status: RespondentStatus;
  mobileType?: string | null;
  isPhoneVerified?: boolean;
  verifiedPhone?: string;
  email?: string;
  paypal?: string | null;
  demographics?: ProfileDemographics;
  employment?: ProfileEmployment | null;
  picture?: string | null;
  location?: ProfileLocation;
  social?: ProfileSocial;
  socialLinks?: ProfileSocialLinks | null;
  skills: Array<Skill>;
  topics?: Array<SimpleTopic>;
  /**
   * Must stay serialized on ProfileDto: the profile-risk-change-respond-queue
   * lambda reads the profile over GET v4/profiles/:profileId and feeds it to
   * computeOverallProfileRisk (@common/utils), where a null trustedCompany
   * marks the profile risk component 'questioned'. An `@Exclude()` here made
   * the DTO report null for every profile, so trusted-company verification
   * could never clear that flag (profile.dto.unit.spec.ts guards this).
   */
  trustedCompany?: ProfileTrustedCompany | null;
  user?: SimpleUser;
  fullName?: string;
  updatedAt?: string;
};

export type ScreenerResponseProfileStatsReviews = {
  total: number;
  organization: number;
  team: number;
  average: number;
  averageOrganization: number;
  averageTeam: number;
};

export type ScreenerResponseProfileStatsTotals = {
  taken: number;
  organization: number;
  team: number;
  qualifiedPercent: number;
};

export type ScreenerResponseProfileStatsBucketResponse = {
  id: string;
  organizationId: string;
  teamId: string;
};

export type ScreenerResponseProfileStatsBucket = {
  status: ScreenerResponseStatus;
  averageIncentive: number;
  lastDate: string;
  percentage: number;
  responses: Array<ScreenerResponseProfileStatsBucketResponse>;
};

export type ScreenerResponseProfileStats = {
  id: string;
  buckets: Array<ScreenerResponseProfileStatsBucket>;
  totals?: ScreenerResponseProfileStatsTotals;
  reviews?: ScreenerResponseProfileStatsReviews;
};

export type TagSimpleDto = {
  id: string;
  name: string;
  color: string;
};

export type TeamRespondentFollower = {
  userId: string;
  createdAt: string;
};

export const ParticipantSource = {
  MARKETPLACE: 'marketplace',
  RECRUITED: 'recruited',
  INVITED: 'invited',
} as const;

export type ParticipantSource =
  (typeof ParticipantSource)[keyof typeof ParticipantSource];

export type TeamRespondentList = {
  id: string;
  createdAt: string;
};

export type TeamRespondentDto = {
  id: string;
  createdAt: string;
  organizationId: string;
  profileId: string;
  teamId: string;
  userId: string;
  lists?: Array<TeamRespondentList>;
  lastPaidAt?: string;
  reviewCount?: number;
  source?: ParticipantSource;
  followers?: Array<TeamRespondentFollower>;
  tags?: Array<TagSimpleDto>;
  stats?: ScreenerResponseProfileStats;
  profile?: TeamRespondentProfileDto;
  surveys?: Array<SimpleTeamProfileScreenerDto>;
  updatedAt?: string;
};

export const SortBy = {
  MOSTRECENT: 'mostrecent',
  LEASTRECENT: 'leastrecent',
  NAME: 'name',
} as const;

export type SortBy = (typeof SortBy)[keyof typeof SortBy];

export type UpdateCalendarConnectionDto = {
  conflictsCalendarId: string;
  bookingsCalendarId: string;
};

export type CalendarConnectionDto = {
  /**
   * `unavailable` means a provider is connected but its calendars could not be
   * read (expired grant, scheduler outage). Reconnecting usually fixes it.
   */
  status: 'disconnected' | 'connected' | 'unavailable';
  /**
   * Only Google is offered; Outlook is disabled (CS-96).
   */
  provider?: 'google';
  accountEmail?: string;
  calendars: Array<ConnectedCalendarDto>;
};

export type ConnectedCalendarDto = {
  /**
   * The calendar's id at the provider (Google calendar id).
   */
  id: string;
  name: string;
  /**
   * Busy times on this calendar block bookings.
   */
  checksConflicts: boolean;
  /**
   * New bookings are added to this calendar.
   */
  receivesBookings: boolean;
};

export type UpdateSchedulerDto = {
  kind?: 'single' | 'group';
  isActive?: boolean;
  projectId?: string;
  name?: string;
  duration?: number;
  maxBookings?: number;
  maxBookingsPerSlot?: number;
  timezone?: string;
  color?: string;
  fromDate?: string;
  endDate?: string;
  sync?: boolean;
  conflicts?: boolean;
};

export type SchedulerEventListDto = {
  items: Array<SchedulerEventDto>;
};

export type SchedulerEventInvitationDto = {
  count: number;
};

export type SchedulerTeamDto = {
  uid?: string | null;
  name?: string | null;
};

export type EventOwnerDto = {
  uid?: string;
  firstName?: string;
  lastName?: string;
  foreignId?: string;
};

export type SchedulerCalendarDto = {
  name?: string;
  provider?: string;
  enabled?: boolean;
  push?: boolean;
};

export type SchedulerSettingDto = {
  uid: string | null;
  eventUid?: string;
};

export type AvailabilityIntervalDto = {
  start?: string;
  end?: string;
};

export type SchedulerAvailabilityDto = {
  uid: string | null;
  eventUid?: string;
  day?: number | null;
  date?: string | null;
  step?: number;
  maxBookings?: number | null;
  intervals?: Array<AvailabilityIntervalDto>;
  mode?: string;
  enabled?: boolean;
  prettyDay?: string;
};

export type SchedulerEventDto = {
  uid: string;
  name?: string;
  description?: string | null;
  location?: string | null;
  timezone?: string;
  duration?: number;
  kind?: string;
  color?: string;
  isActive?: boolean;
  sync?: boolean;
  conflicts?: boolean;
  maxBookings?: number;
  maxBookingsPerSlot?: number;
  maxBookingsPerDay?: number | null;
  minimumSchedulingNotice?: number;
  bufferBeforeEvent?: number;
  bufferAfterEvent?: number;
  showLocation?: boolean;
  teamEventStyle?: string | null;
  forever?: boolean;
  rollingDays?: number | null;
  fromDate?: string;
  endDate?: string;
  createdAt?: string;
  updatedAt?: string;
  availabilities?: Array<SchedulerAvailabilityDto>;
  settings?: Array<SchedulerSettingDto>;
  calendars?: Array<SchedulerCalendarDto>;
  owner?: EventOwnerDto;
  team?: SchedulerTeamDto | null;
  bookingsCount?: number;
  invitation?: SchedulerEventInvitationDto;
};

export type FindEventsDto = {
  projectId: string;
};

export type CreateSchedulerDto = {
  kind: 'single' | 'group';
  projectId: string;
  name: string;
  duration: number;
  maxBookings: number;
  maxBookingsPerSlot: number;
  timezone: string;
  color: string;
  fromDate: string;
  endDate: string;
  sync?: boolean;
  conflicts?: boolean;
};

export type ReferralsByReferrerPaginatedDto = {
  /**
   * The total number of results possible, which is only provided in the response when `includeCount` is passed as `true` within a request.
   */
  totalResults?: number;
  page: number;
  pageSize: number;
  results: Array<ReferralResultDto>;
  /**
   * Lifetime bonus received by the referrer, only provided when `includeLifetimeBonusReceived` is true
   */
  lifetimeBonusReceived?: number;
};

export type ReferredUserDto = {
  /**
   * Full name of the referred user
   */
  fullName: string;
  /**
   * Formatted signup date (e.g. "Jan 1, 2024")
   */
  signupDate: string;
  /**
   * Bonus amount in dollars, if applicable
   */
  bonusAmount?: number;
};

export type ReferralInfoDto = {
  /**
   * The type of referral
   */
  referralType: 'project' | 'signup';
  /**
   * The current active status of the referral
   */
  activeStatus: string;
  /**
   * All possible states for this referral type and scenario
   */
  possibleStates: Array<string>;
};

export type ReferralResultDto = {
  referral: ReferralInfoDto;
  user: ReferredUserDto;
};

export type StudyCheckInteractionsDto = {
  events: Array<StudyCheckInteractionDto>;
};

export const StudyCheckSurface = {
  DRAWER: 'drawer',
  INLINE: 'inline',
} as const;

export type StudyCheckSurface =
  (typeof StudyCheckSurface)[keyof typeof StudyCheckSurface];

export const StudyCheckSeverity = {
  BLOCKER: 'blocker',
  WARNING: 'warning',
  SUGGESTION: 'suggestion',
} as const;

export type StudyCheckSeverity =
  (typeof StudyCheckSeverity)[keyof typeof StudyCheckSeverity];

export const StudyCheckInteractionAction = {
  SHOWN: 'shown',
  OPENED: 'opened',
  IGNORED: 'ignored',
  FIX_WITH_AI: 'fix-with-ai',
  GO_TO: 'go-to',
  REMOVE_QUESTION: 'remove-question',
  RESOLVED: 'resolved',
  REVIEWED: 'reviewed',
} as const;

export type StudyCheckInteractionAction =
  (typeof StudyCheckInteractionAction)[keyof typeof StudyCheckInteractionAction];

export type StudyCheckInteractionDto = {
  action: StudyCheckInteractionAction;
  /**
   * What the check is about: an AI finding kind, a screener rule, or a setup
   * step. Free-form because blockers are defined in the browser, but limited to
   * slug characters so it can't carry question text.
   */
  kind?: string;
  severity?: StudyCheckSeverity;
  surface?: StudyCheckSurface;
  /**
   * An id, never text: the uid alphabet only.
   */
  questionUid?: string;
  /**
   * For `opened` and `reviewed`: what the editor was showing at the time.
   */
  blockerCount?: number;
  warningCount?: number;
  suggestionCount?: number;
};

export type StudyCheckFeedbackDto = {
  checkId: string;
  action: StudyCheckFeedbackAction;
  questionUid?: string;
  kind?: StudyCheckFindingKind;
};

export const StudyCheckFindingKind = {
  CONTACT_DETAILS: 'contact-details',
  EXACT_BIRTH_DATE: 'exact-birth-date',
  SENSITIVE_IDENTIFIER: 'sensitive-identifier',
  ANSWER_MISMATCH: 'answer-mismatch',
  INVERTED_RULES: 'inverted-rules',
  CHOICE_QUALITY: 'choice-quality',
  NDA_AGREEMENT: 'nda-agreement',
  REVIEW: 'review',
  TARGETING_REPLACEMENT: 'targeting-replacement',
} as const;

export type StudyCheckFindingKind =
  (typeof StudyCheckFindingKind)[keyof typeof StudyCheckFindingKind];

export const StudyCheckFeedbackAction = {
  IGNORED: 'ignored',
  ACTED_ON: 'acted-on',
  EDITED: 'edited',
} as const;

export type StudyCheckFeedbackAction =
  (typeof StudyCheckFeedbackAction)[keyof typeof StudyCheckFeedbackAction];

export type StudyChecksResultDto = {
  findings: Array<StudyCheckFindingDto>;
  /**
   * The whole check's Braintrust row. Feedback should name a finding's own
   * `checkId`; this is the fallback when a finding has none. Absent when
   * tracing is off in this environment, or for a screener with no questions.
   */
  checkId?: string;
};

export type StudyCheckFindingDto = {
  /**
   * Keyed by question uid rather than position, so a finding stays on the
   * right question when the researcher reorders the screener.
   */
  questionUid: string;
  kind: StudyCheckFindingKind;
  /**
   * For targeting replacements: the targeting field id, matching the web
   * targeting drawer's ids so the client can label it in the user's language.
   */
  fieldId?: string;
  /**
   * The field lives under the drawer's "Advanced targeting" section.
   */
  isAdvancedField?: boolean;
  /**
   * Identifies this finding for feedback (its own Braintrust row). Absent
   * when tracing is off in this environment, and on results cached before
   * findings had their own rows; send the result's `checkId` then.
   */
  checkId?: string;
};

export type AddFollowerDto = {
  userId: string;
};

export type ReExtractInterviewResponseResponseDto = {
  force: boolean;
  /**
   * Echoes the response id for client-side confirmation.
   */
  responseId: string;
};

export type ReExtractInterviewResponseDto = {
  /**
   * When true, the existing `interview_insights` doc for the response is
   * deleted before re-enqueueing so the executor re-runs every feature
   * from scratch. When false, only the status flips to PENDING and the
   * per-feature freshness check skips features already DONE at the
   * current pipeline version.
   */
  force: boolean;
  /**
   * Extract at this feature schema version instead of the pin. Only extraction
   * runs: build the version's report with `insights-report/regenerate` once
   * the interviews have landed.
   */
  featuresSchemaVersion?: number;
};

export type UpdateInsightsInclusionResponseDto = {
  /**
   * Number of interview responses whose inclusion flag actually changed.
   */
  updatedCount: number;
};

export type UpdateInsightsInclusionDto = {
  /**
   * Participant user ids to include back into insights generation.
   */
  include?: Array<string>;
  /**
   * Participant user ids to exclude from insights generation.
   */
  exclude?: Array<string>;
};

export type UpdateInterviewContentTranslationDto = {
  sections?: Array<TranslatedInterviewSection>;
  sectionsSourceHash?: string;
  welcomeScreen?: TranslatedWelcomeScreen;
  welcomeSourceHash?: string;
  closingMessage?: TranslatedClosingMessage;
  closingSourceHash?: string;
};

export type TranslatedClosingMessage = {
  closingMessage?: string;
};

export type TranslatedWelcomeScreen = {
  title?: string;
  message?: string;
};

export type TranslatedInterviewConcept = {
  uid: string;
  title?: string;
  description?: string;
  media?: TranslatedInterviewQuestionMedia;
};

export type TranslatedInterviewQuestionMedia = {
  label?: string;
  aiContext?: string;
};

export type TranslatedInterviewOption = {
  uid: string;
  text: string;
};

export type TranslatedInterviewQuestion = {
  uid: string;
  text: string;
  continueButtonText?: string;
  /**
   * TASK only — the translated "I'm stuck" hint.
   */
  hint?: string;
  options?: Array<TranslatedInterviewOption>;
  media?: TranslatedInterviewQuestionMedia;
};

export type TranslatedInterviewSection = {
  uid: string;
  name: string;
  questions?: Array<TranslatedInterviewQuestion>;
  /**
   * Absent on every section without concept testing. `defaultValue:
   * undefined` suppresses Mongoose's empty-array default so "no concepts" and
   * "concepts that produced no translation" both read back as absent rather
   * than as a stored `[]`.
   */
  concepts?: Array<TranslatedInterviewConcept>;
  /**
   * The section's stimulus strings. Section-level, unlike every other entry
   * here, because the stimulus is: one attachment shown alongside all of the
   * section's questions.
   *
   * Both halves matter for a non-English study. `label` is the caption the
   * participant reads; `aiContext` is researcher-only and is what stops the
   * moderator reasoning about the prototype in English and code-switching
   * mid-probe — the same reason a question's media `aiContext` is translated.
   */
  stimulus?: TranslatedInterviewQuestionMedia;
};

export type InterviewContentTranslationForResearcherDto = {
  language: string;
  sections?: Array<TranslatedInterviewSection>;
  sectionsSourceHash?: string;
  /**
   * Set while the sections block is a manual researcher edit.
   */
  sectionsEditedAt?: string;
  welcomeScreen?: TranslatedWelcomeScreen;
  welcomeSourceHash?: string;
  welcomeEditedAt?: string;
  closingMessage?: TranslatedClosingMessage;
  closingSourceHash?: string;
  closingEditedAt?: string;
};

export type GeneratePreviewFollowUpResponseDto = {
  /**
   * Whether a follow-up question was generated
   */
  hasFollowUp: boolean;
  kind?: InterviewTurnKind;
  /**
   * The AI-generated follow-up or retake question
   */
  followUpQuestion?: string;
};

export const InterviewTurnKind = { PROBE: 'probe', RETAKE: 'retake' } as const;

export type InterviewTurnKind =
  (typeof InterviewTurnKind)[keyof typeof InterviewTurnKind];

export type GeneratePreviewFollowUpDto = {
  /**
   * The question UID to generate a follow-up for
   */
  questionUid: string;
  /**
   * Latest transcript/typed answer to the scripted question (empty triggers a retake)
   */
  mainAnswer: string;
  /**
   * Answered probe exchanges on this question, oldest first. A retake re-record overwrites the answer of the turn it retook rather than appending.
   */
  exchanges: Array<PreviewFollowUpExchangeDto>;
  /**
   * Retakes already asked for the current turn
   */
  retakeCount: number;
  /**
   * Probe questions asked on earlier questions in this preview run — dedup context so the model does not repeat them
   */
  priorProbes?: Array<string>;
  /**
   * How the task went, for a TASK question. Ignored on every other type.
   */
  task?: PreviewTaskOutcomeDto;
};

export const TaskOutcome = {
  COMPLETED: 'completed',
  GAVE_UP: 'gave_up',
} as const;

export type TaskOutcome = (typeof TaskOutcome)[keyof typeof TaskOutcome];

export type PreviewTaskOutcomeDto = {
  outcome: TaskOutcome;
  /**
   * The 1-5 ease rating, when the task asked for one
   */
  rating?: number;
  /**
   * Whether the participant pressed "I'm stuck"
   */
  hintShown: boolean;
};

export type PreviewFollowUpExchangeDto = {
  /**
   * The AI probe question that was asked
   */
  aiQuestion: string;
  /**
   * The researcher's answer to the probe (may be empty for a silent recording)
   */
  participantAnswer: string;
};

export type GenerateInsightsReportResponseDto = {
  force: boolean;
  /**
   * Echoes the project id for client-side confirmation.
   */
  projectId: string;
};

export type GenerateInsightsReportDto = {
  /**
   * When true, regenerate even if the report's structural inputs are unchanged
   * (bypasses the regeneration debounce). The threshold gate still applies. When false,
   * the report Lambda's gate decides whether a regeneration is warranted.
   */
  force: boolean;
  /**
   * The feature schema version to rebuild: the pin, which is the default, or
   * one of the experimental versions the project is also extracted at.
   */
  featuresSchemaVersion?: number;
};

export type InsightsReportStatementSearchDto = {
  /**
   * Every matching statement the study still holds, by id.
   */
  statementIds: Array<number>;
};

export type InsightsReportStatementsDto = {
  /**
   * Whether the study has an insights report to show.
   */
  found: boolean;
  /**
   * The drawing of the statement list these positions come from, which is the `runId` on the report they stand behind.
   */
  runId: string | null;
  statements: Array<InsightsReportStatementPositionsDto>;
};

export type InsightsReportQuoteDto = {
  /**
   * The participant's words, as the transcript has them.
   */
  text: string;
  /**
   * The interview question the words were spoken under.
   */
  questionUid: string;
  /**
   * The follow-up the words were spoken in, and null for the answer itself. What the page seeks the interview video by.
   */
  followUpUid: string | null;
};

export type InsightsReportStanceDto = {
  /**
   * This participant's interview response id.
   */
  responseId: string;
  /**
   * How the report names this participant.
   */
  label: string;
  stance: 'supports' | 'contradicts';
  /**
   * Why the position was recorded, in the reading call's own words.
   */
  reason: string;
  /**
   * What the participant made their position depend on, and empty where they qualified it with nothing.
   */
  condition: string;
  quotes: Array<InsightsReportQuoteDto>;
};

export type InsightsReportStatementPositionsDto = {
  /**
   * The id the report prints this statement under.
   */
  statementId: number;
  /**
   * The people who hold this statement, one row each, counting a person once however many statements merged into it they took a position on.
   */
  holds: Array<InsightsReportStanceDto>;
  /**
   * The people who deny this statement, on the same terms as `holds`. Somebody recorded on both sides appears in both.
   */
  denies: Array<InsightsReportStanceDto>;
};

export type InsightsReportChartEvaluationDto = {
  result: StudyChartResultDto;
};

export type StudyChartBaseDto = {
  total: number;
  /**
   * The smallest segment base, or `total`.
   */
  min: number;
  /**
   * The largest segment base, or `total`.
   */
  max: number;
};

export type StudyChartQuoteDto = {
  /**
   * The participant's words, as the transcript has them.
   */
  text: string;
  /**
   * The interview question the words were spoken under.
   */
  questionUid: string;
  /**
   * The follow-up the words were spoken in, and null for the answer itself.
   */
  followUpUid: string | null;
};

export type StudyChartMemberDto = {
  /**
   * This person's interview response id.
   */
  responseId: string;
  /**
   * Their words behind this bar, where the source has any.
   */
  quotes: Array<StudyChartQuoteDto>;
};

export type StudyChartCellDto = {
  value: string;
  /**
   * The segment, and null where the chart is not broken down.
   */
  segment: string | null;
  count: number;
  base: number;
  /**
   * `count / base`, 0 where the base is 0.
   */
  share: number;
  members: Array<StudyChartMemberDto>;
};

export type StudyChartSegmentBaseDto = {
  segment: string;
  base: number;
};

export const StudyChartSplit = {
  NONE: 'none',
  SEGMENT: 'segment',
  SERIES: 'series',
} as const;

export type StudyChartSplit =
  (typeof StudyChartSplit)[keyof typeof StudyChartSplit];

export type StudyChartResultDto = {
  /**
   * The bars, in order.
   */
  values: Array<string>;
  split: StudyChartSplit;
  /**
   * The groups in order (segments largest first), and null where `split` is none.
   */
  segments: Array<StudyChartSegmentBaseDto> | null;
  /**
   * One per value (per segment), zero cells included.
   */
  cells: Array<StudyChartCellDto>;
  base: StudyChartBaseDto;
  /**
   * Somebody falls under more than one bar.
   */
  multiValued: boolean;
  /**
   * The smallest base is under `smallBaseUnder`.
   */
  smallBase: boolean;
  /**
   * The base under which a chart is flagged as small.
   */
  smallBaseUnder: number;
};

export type EvaluateInsightsReportChartDto = {
  chartId: string;
  breakdown: string | null;
  version?: number | null;
};

export type InsightsReportDto = {
  /**
   * The sectioned report, and null where the study has none yet or is served `legacy`.
   */
  report: InsightsReportBodyDto | null;
  /**
   * The themes report of a study pinned to a version from before the sectioned report. It carries its own lifecycle `status`.
   */
  legacy?: LegacyInsightsReportDto;
};

export type ConceptExposureDto = {
  conceptCount: number;
  /**
   * Concepts each participant was shown; equals `conceptCount` unless sampled.
   */
  sampleSize: number;
  sampled: boolean;
  /**
   * False means everyone saw the authored order.
   */
  randomizedOrder: boolean;
};

export type LegacyInsightsReportConceptDto = {
  conceptUid: string;
  /**
   * Participant-facing title, resolved from the live script rather than stored
   * with the report, so a renamed concept is never stale here.
   */
  label: string;
  /**
   * Carried over verbatim from the Concepts tab's write-up.
   */
  summary: string;
  citations: Array<ParticipantRefDto>;
};

export type ParticipantRefDto = {
  participantUserId: string;
  name: string;
  picture?: string;
  /**
   * Job title from the screener profile, when known (for quote attribution).
   */
  jobTitle?: string;
  /**
   * Employer from the screener profile, when known (for quote attribution).
   */
  company?: string;
  /**
   * Seniority level (`SeniorityLevel` value) from the screener profile, when known.
   */
  seniority?: string;
};

export type LegacyInsightsReportConceptSectionDto = {
  blockId: string;
  /**
   * → the script's concept section, for a deep link to the Concepts tab.
   */
  sectionUid: string;
  heading: string;
  /**
   * What separated the concepts, and what was true of all of them.
   */
  overview: Array<LegacyInsightsReportTextBlockDto>;
  /**
   * In authored order.
   */
  concepts: Array<LegacyInsightsReportConceptDto>;
  pullQuotes: Array<InsightsReportPullQuoteDto>;
  /**
   * Read from the current script, not the stored report. The UI uses
   * `randomizedOrder` to decide whether to show the fixed-order caveat.
   */
  exposure: ConceptExposureDto;
};

export type InsightsReportPullQuoteDto = {
  quote: string;
  participant: ParticipantRefDto;
  questionUid: string;
  /**
   * Set when the quote came from an AI follow-up exchange.
   */
  followUpUid?: string | null;
  /**
   * The single best exemplar for its theme (the Tier-2 selection's rank-1 pick).
   */
  isRepresentative?: boolean;
  /**
   * A purposeful, explicitly-labelled outlier / counterpoint (RTA deviant case).
   */
  isDeviantCase?: boolean;
  /**
   * Why the Tier-2 selection step picked this quote, in one line. Absent when
   * selection never ran, and on surfaces that do not serve it.
   */
  selectionReason?: string;
  /**
   * 1-based exemplar rank from that same step; 1 is the best.
   */
  selectionRank?: number;
};

export type LegacyInsightsReportTextBlockDto = {
  text: string;
  /**
   * Participants whose responses the engine cited as supporting this block.
   */
  citations: Array<ParticipantRefDto>;
};

export type LegacyInsightsReportSectionDto = {
  /**
   * Stable id per meta-theme (for incremental rendering / deep-links).
   */
  blockId: string;
  /**
   * → the `studyThemes` meta-theme this section develops.
   */
  studyThemeId: string;
  heading: string;
  centralConcept: string;
  /**
   * Distinct participants this meta-theme draws on (the prevalence count shown
   * on the theme map). From the `studyThemes` synthesis, not the prose.
   */
  participantCount: number;
  body: Array<LegacyInsightsReportTextBlockDto>;
  /**
   * Top 1–2 exemplar quotes for this meta-theme (the engine's curated narrative picks).
   */
  pullQuotes: Array<InsightsReportPullQuoteDto>;
  /**
   * The full evidence pool for this meta-theme — every Tier-2 `themeTaxonomy`
   * ref across its supporting question-themes, deduped, representative first.
   * Backs the "View more quotes" expander. Overlaps `pullQuotes` but is a
   * distinct pool (a curated pull quote may not be a taxonomy ref); the client
   * dedupes the two on display, and the selection flags live here.
   */
  quotes: Array<InsightsReportPullQuoteDto>;
};

export const InterviewDeliveryMode = {
  TEXT: 'text',
  VIDEO: 'video',
  AUDIO: 'audio',
} as const;

export type InterviewDeliveryMode =
  (typeof InterviewDeliveryMode)[keyof typeof InterviewDeliveryMode];

export type LegacyInsightsReportStudyMetaDto = {
  /**
   * Researcher-authored study description (the "why").
   */
  objective?: string;
  deliveryMode?: InterviewDeliveryMode;
  /**
   * Target interview length, in minutes.
   */
  durationMinutes?: number;
  /**
   * When the study went live (ISO 8601).
   */
  fieldedAt?: string;
  /**
   * When the study closed (ISO 8601), if it has.
   */
  closedAt?: string;
};

export type LegacyInsightsReportDocumentDto = {
  /**
   * The single interpretive thesis of the study.
   */
  headline: string;
  subhead?: string;
  /**
   * Study provenance for the hero (objective, method, fielding window).
   */
  studyMeta: LegacyInsightsReportStudyMetaDto;
  executiveSummary: string;
  studyGoals: Array<string>;
  keyFindings: Array<LegacyInsightsReportTextBlockDto>;
  sections: Array<LegacyInsightsReportSectionDto>;
  /**
   * Present only on a concept study, and only once a report has been generated
   * since concept testing shipped. Renders after `sections`.
   */
  conceptSections?: Array<LegacyInsightsReportConceptSectionDto>;
};

export const InsightsReportStatus = {
  PENDING_THRESHOLD: 'PENDING_THRESHOLD',
  CONSOLIDATING: 'CONSOLIDATING',
  GENERATING: 'GENERATING',
  READY: 'READY',
  STALE: 'STALE',
  FAILED: 'FAILED',
} as const;

export type InsightsReportStatus =
  (typeof InsightsReportStatus)[keyof typeof InsightsReportStatus];

export type LegacyInsightsReportDto = {
  status: InsightsReportStatus;
  /**
   * The aggregate feature-extraction schema version this project is pinned to;
   * the frontend switches its rendering on this (every new version is breaking).
   */
  featuresSchemaVersion: number;
  /**
   * Distinct analyzed responses the current report was generated from, less any
   * that are no longer insights-eligible (whose evidence this response also
   * filters out) — so it always matches the attributions actually rendered.
   */
  generatedFromResponseCount: number;
  /**
   * True when the stored report predates a cohort change and a rebuild is
   * outstanding: either it still cited participants who are no longer
   * insights-eligible (manually excluded, rejected, disqualified, dismissed —
   * their quotes and citations are filtered out of `report`, but the narrative
   * was *written* from them), or a rebuild was requested that no completed
   * rebuild has covered yet (MongoDB rebuild revision — the re-inclusion
   * new-interview case, which filtering can't see). Drives the pending-rebuild
   * banner.
   */
  rebuildPending?: boolean;
  /**
   * Terminal failure message from the last generation run, present when
   * `status` is `FAILED`. Surfaced for support/debugging — not for the
   * researcher-facing report UI.
   */
  error?: string;
  /**
   * When the last generation run produced the current value. Surfaced for
   * support/debugging (e.g. "is this report stale?").
   */
  generatedAt?: string;
  /**
   * Wall-clock duration of the last generation run, in milliseconds.
   */
  generationDurationMs?: number;
  /**
   * LLM model id that produced the current value (provenance).
   */
  model?: string;
  report?: LegacyInsightsReportDocumentDto;
};

export type InsightsReportStatementSummaryDto = {
  /**
   * The id the report prints this statement under.
   */
  statementId: number;
  /**
   * The position the study takes, in one sentence.
   */
  text: string;
  /**
   * The interview response ids of the people who hold this statement, each once however many statements merged into it they took a position on.
   */
  holds: Array<string>;
  /**
   * The interview response ids of the people who deny this statement, on the same terms as `holds`.
   */
  denies: Array<string>;
  /**
   * The goals this answers, by their position in the report body's `goals`.
   */
  goalIndexes: Array<number>;
  /**
   * The interview questions whose answers this was drawn from. Empty where nobody was asked about it.
   */
  questionUids: Array<string>;
  /**
   * Where the statement stops holding, in the participants' own terms.
   */
  boundary: string;
};

export type InsightsReportParticipantDto = {
  /**
   * This person's interview response id.
   */
  responseId: string;
  /**
   * This person's user id.
   */
  participantUserId: string;
  /**
   * What the report prints beside them: 1-based, in the order the run read the interviews.
   */
  number: number;
  /**
   * First name and surname initial, or the participant's id where the study has no profile for them.
   */
  name: string;
  picture?: string;
  jobTitle?: string;
  company?: string;
  seniority?: string;
};

export type StudySegmentDto = {
  /**
   * `profile:seniority`, or `question:<source>:<questionUid>`.
   */
  id: string;
  label: string;
};

export type InsightsReportGoalDto = {
  /**
   * The goal, in the researcher's own words.
   */
  text: string;
  /**
   * What became of this goal. `answered` when at least half the study spoke to it, `thin` when fewer did, and `unanswered` when nobody did.
   */
  verdict: 'answered' | 'thin' | 'unanswered';
  /**
   * How many people hold a statement filed under this goal, each counted once. The same population the verdict is read from.
   */
  addressedBy: number;
};

export type InsightsReportChartBlockDto = {
  kind: 'chart';
  /**
   * Names the chart to the evaluate endpoint.
   */
  chartId: string;
  title: string;
  comment: string;
  /**
   * What is counted, in plain words.
   */
  description: string;
  /**
   * The segment it was placed split by, or null.
   */
  breakdown: string | null;
  /**
   * A chart that cannot be evaluated, or has too few people or nobody on a bar, is left out of the body.
   */
  result: StudyChartResultDto;
};

export type InsightsReportInsightDto = {
  /**
   * The insight's id, as the report's ordered list holds it.
   */
  blockId: string;
  /**
   * What the report prints beside this insight: its 1-based place in the reading order.
   */
  number: number;
  heading: string;
  standfirst: string;
  /**
   * The insight's blocks in reading order: paragraphs, quotes, charts, and groups of two columns. Only the chart is typed here.
   */
  body: Array<
    | InsightsReportChartBlockDto
    | {
        [key: string]: unknown;
      }
  >;
  /**
   * The one thing the insight is about, in the naming call's words.
   */
  centralConcept: string;
  /**
   * The statements filed under this insight, by id.
   */
  statementIds: Array<number>;
  /**
   * How much this insight matters, 1 being most, which is not where it sits in the report.
   */
  rank: number;
  /**
   * The goals this insight answers, by position in the report body's `goals`.
   */
  goalIndexes: Array<number>;
};

export type InsightsReportParticipantsSectionDto = {
  kind: 'participants';
};

export type InsightsReportStatementsSectionDto = {
  kind: 'statements';
};

export type InsightsReportMethodSectionDto = {
  kind: 'method';
  /**
   * How many interviews the report was read from.
   */
  interviewed: number;
  /**
   * The screener answers every participant gave: what the study let people in on, which is the door rather than a distribution.
   */
  screenedFor: Array<string>;
};

export type InsightsReportMoreInsightsSectionDto = {
  kind: 'moreInsights';
  /**
   * The insights listed here, in reading order, by id on the report's `insights`.
   */
  insightIds: Array<string>;
};

export type InsightsReportGoalsSectionDto = {
  kind: 'goals';
};

export type InsightsReportOutlierDto = {
  /**
   * The id the report prints this statement under.
   */
  statementId: number;
  /**
   * How many people took the other side.
   */
  against: number;
  /**
   * The person's own position, with why it was recorded and their words.
   */
  stance: InsightsReportStanceDto;
};

export type InsightsReportOutliersSectionDto = {
  kind: 'outliers';
  /**
   * The largest majority first.
   */
  outliers: Array<InsightsReportOutlierDto>;
};

export type InsightsReportDivisionVoiceDto = {
  /**
   * The participant's words, as the transcript has them.
   */
  text: string;
  /**
   * The interview question the words were spoken under.
   */
  questionUid: string;
  /**
   * The follow-up the words were spoken in, and null for the answer itself. What the page seeks the interview video by.
   */
  followUpUid: string | null;
  /**
   * The speaker's interview response id.
   */
  responseId: string;
};

export type InsightsReportDivisionSideDto = {
  /**
   * The id the report prints this statement under.
   */
  statementId: number;
  side: 'holds' | 'denies';
  /**
   * The people on this side, by interview response id.
   */
  responseIds: Array<string>;
  /**
   * One person on this side in their own words, and null where nobody on it was quoted.
   */
  voice: InsightsReportDivisionVoiceDto | null;
};

export type InsightsReportDivisionDto = {
  /**
   * `split` is one statement some hold and others deny; `pair` is two statements nobody holds both of.
   */
  kind: 'split' | 'pair';
  /**
   * What the two sides disagree about: the pairing call's words, or a split's own statement.
   */
  subject: string;
  sides: Array<InsightsReportDivisionSideDto>;
};

export type InsightsReportDivisionsSectionDto = {
  kind: 'divisions';
  /**
   * Counted from the stances as they stand, the best-attended first.
   */
  divisions: Array<InsightsReportDivisionDto>;
};

export type InsightsReportInsightsSectionDto = {
  kind: 'insights';
  /**
   * The insights drawn here, in reading order, by id on the report's `insights`.
   */
  insightIds: Array<string>;
};

export type InsightsReportLeansOnDto = {
  /**
   * The insight the sentence belongs to.
   */
  blockId: string;
  /**
   * The sentence as the insight reads.
   */
  text: string;
};

export type InsightsReportRecommendationDto = {
  /**
   * The action, as one sentence.
   */
  action: string;
  /**
   * Why the study points to it, and where it stops holding.
   */
  why: string;
  /**
   * Where each count in `why` sits.
   */
  whyCounts: Array<InsightsReportProseCountDto>;
  /**
   * The statements the recommendation rests on, by id.
   */
  statementIds: Array<number>;
  /**
   * The insight sentences the prose leans on.
   */
  leansOn: Array<InsightsReportLeansOnDto>;
};

export type InsightsReportProseCountDto = {
  /**
   * Where the count starts in the prose.
   */
  at: number;
  /**
   * How many characters the count runs.
   */
  length: number;
  /**
   * The statements the count counts, by id.
   */
  statementIds: Array<number>;
};

export type InsightsReportRecommendationsSectionDto = {
  kind: 'recommendations';
  recommendations: Array<InsightsReportRecommendationDto>;
};

export type InsightsReportCoverSectionDto = {
  kind: 'cover';
  headline: string;
  executiveSummary: string;
  /**
   * Where each count in `executiveSummary` sits.
   */
  executiveSummaryCounts: Array<InsightsReportProseCountDto>;
  /**
   * The short labels printed beside the summary, one per paragraph.
   */
  summaryLabels: Array<string>;
};

export type InsightsReportBodyDto = {
  /**
   * What the page keys this report by: the study and the drawing of the statement list it was written from.
   */
  reportId: string;
  /**
   * The drawing of the statement list this report was written from, as a string. The statements endpoint answers with the same value, so a report and the statements behind it can be told to belong together.
   */
  runId: string;
  /**
   * When the report was written.
   */
  createdAt: string;
  /**
   * When the study went live, ISO 8601. Read from the project on every request rather than stored on the report, so it cannot go stale. Absent where the project was never published.
   */
  fieldedAt?: string;
  /**
   * The report in reading order, one entry per section, each drawn by its `kind`. A kind the page does not know is skipped.
   */
  sections: Array<
    | ({
        kind: 'cover';
      } & InsightsReportCoverSectionDto)
    | ({
        kind: 'recommendations';
      } & InsightsReportRecommendationsSectionDto)
    | ({
        kind: 'insights';
      } & InsightsReportInsightsSectionDto)
    | ({
        kind: 'divisions';
      } & InsightsReportDivisionsSectionDto)
    | ({
        kind: 'outliers';
      } & InsightsReportOutliersSectionDto)
    | ({
        kind: 'goals';
      } & InsightsReportGoalsSectionDto)
    | ({
        kind: 'moreInsights';
      } & InsightsReportMoreInsightsSectionDto)
    | ({
        kind: 'method';
      } & InsightsReportMethodSectionDto)
    | ({
        kind: 'statements';
      } & InsightsReportStatementsSectionDto)
    | ({
        kind: 'participants';
      } & InsightsReportParticipantsSectionDto)
  >;
  /**
   * Every insight the report carries, in reading order, with its wording and its finding joined on. The sections name them by `blockId`.
   */
  insights: Array<InsightsReportInsightDto>;
  /**
   * What the study set out to learn, one goal a line: the researcher's own paragraph, split. Insights and statements name them by position.
   */
  goals: Array<InsightsReportGoalDto>;
  /**
   * What any chart in the report can be split by.
   */
  segments: Array<StudySegmentDto>;
  /**
   * The people the report was written from, one row per interview.
   */
  participants: Array<InsightsReportParticipantDto>;
  /**
   * Every statement the study still holds, lowest id first, with who holds and denies it.
   */
  statements: Array<InsightsReportStatementSummaryDto>;
};

export type FeaturesSchemaVersionsDto = {
  /**
   * Every version the feature-extraction pipeline has, ascending. Insights can be read and built at any of them.
   */
  versions: Array<number>;
};

export type QuoteClipsDto = {
  /**
   * The aggregate feature-extraction schema version this project is pinned to.
   * The frontend switches its rendering on this; every new version is breaking.
   */
  featuresSchemaVersion: number;
  /**
   * Questions in script order, only those with at least one selected quote.
   * Empty until Tier-2 consolidation + exemplar selection have produced a
   * shortlist for at least one question.
   */
  questions: Array<QuoteClipQuestionGroupDto>;
};

export const QuoteClipStatus = {
  PENDING: 'pending',
  READY: 'ready',
  SKIPPED: 'skipped',
  FAILED: 'failed',
} as const;

export type QuoteClipStatus =
  (typeof QuoteClipStatus)[keyof typeof QuoteClipStatus];

export type QuoteClipDto = {
  participant: ParticipantRefDto;
  /**
   * The quote text exactly as stored on the taxonomy ref.
   */
  quote: string;
  /**
   * Turn identity, for deep-linking into the interview drawer.
   */
  questionUid: string;
  /**
   * Set when the quote came from an AI follow-up exchange.
   */
  followUpUid?: string | null;
  /**
   * 1-based exemplar rank from the Tier-2 selection step; 1 is the best.
   */
  selectionRank?: number;
  /**
   * The single best exemplar for its theme (the selection's rank-1 pick).
   */
  isRepresentative?: boolean;
  /**
   * A purposeful, explicitly-labelled outlier / counterpoint (RTA deviant case).
   */
  isDeviantCase?: boolean;
  /**
   * Why the Tier-2 selection step picked this quote, in one line.
   */
  selectionReason?: string;
  /**
   * S3 key of the cut artifact, present when `status` is READY. Signed via the
   * standard secure-files signed-URL path, like any other interview media key.
   */
  clipBucketKey?: string;
  /**
   * MIME type of the cut artifact (e.g. video/mp4, audio/mp4).
   */
  mimeType?: string;
  /**
   * Clip window start, ms relative to the source recording.
   */
  startMs?: number;
  /**
   * Clip window end, ms relative to the source recording.
   */
  endMs?: number;
  status: QuoteClipStatus;
  /**
   * Why the clip is SKIPPED/FAILED, e.g. 'sidecar-missing', 'low-match-score'.
   */
  statusReason?: string;
};

export type QuoteClipThemeGroupDto = {
  /**
   * Stable id for the consolidated theme, reused across consolidation runs.
   */
  canonicalThemeId: string;
  tagSlug: string;
  title: string;
  /**
   * Ordered for display: representative pick first, then by selection rank,
   * deviant cases per their rank; one clip per participant per theme.
   */
  clips: Array<QuoteClipDto>;
};

export type QuoteClipQuestionGroupDto = {
  questionUid: string;
  questionText: string;
  sectionUid: string;
  sectionName: string;
  /**
   * Concept testing. Present on rows produced by expanding a concept section,
   * where `questionUid` is the derived `${baseQuestionUid}_c_${conceptUid}`.
   * `conceptInternalName` is the researcher's own label — this is a
   * researcher-only view, so it is the right thing to display.
   */
  conceptUid?: string;
  conceptInternalName?: string;
  /**
   * The concept's participant-facing title, when the researcher gave it one.
   */
  conceptTitle?: string;
  themes: Array<QuoteClipThemeGroupDto>;
};

export type ConceptReportDto = {
  status: InsightsReportStatus;
  featuresSchemaVersion: number;
  /**
   * One per concept section; empty for a study with no concepts.
   */
  sections: Array<ConceptReportSectionDto>;
  /**
   * Stored write-ups predate the current cohort; a rebuild is outstanding.
   */
  rebuildPending?: boolean;
  error?: string;
  generatedAt?: string;
  model?: string;
};

export const ConceptWriteUpSkipReason = {
  TOO_MANY_CONCEPTS: 'tooManyConcepts',
  NO_ANALYZED_ANSWERS: 'noAnalyzedAnswers',
  GENERATION_FAILED: 'generationFailed',
} as const;

export type ConceptWriteUpSkipReason =
  (typeof ConceptWriteUpSkipReason)[keyof typeof ConceptWriteUpSkipReason];

export type ConceptHiddenThemeDto = {
  canonicalThemeId: string;
  tagSlug: string;
  title: string;
};

export type ConceptAnsweredCountDto = {
  conceptUid: string;
  /**
   * Analyzed participants who answered this concept's copy of the question —
   * the denominator every count on the row is read against, and the same rule
   * the Details tab's `answeredCount` uses.
   */
  answeredCount: number;
};

export type ConceptOptionCountDto = {
  conceptUid: string;
  /**
   * The comparable plain count. MULTIPLE_CHOICE: participants who selected this
   * option. RANKING: participants who put it FIRST (top box) — a count with an
   * explicit denominator, which is the only ranking statistic that stays
   * comparable when concepts differ in how many people ranked at all.
   */
  count: number;
  /**
   * `count / answeredCount * 100` for this concept, 1 dp.
   */
  percentage: number;
  /**
   * RANKING only: mean position (1 = best) over the participants who ranked
   * this option for this concept. Secondary to `count` and never comparable on
   * its own — an option two people loved outranks one nine people placed
   * mid-table — so it must be shown with `rankedCount` beside it.
   */
  meanRank?: number;
  /**
   * RANKING only: participants who placed this option at any position.
   */
  rankedCount?: number;
};

export type ConceptOptionDto = {
  optionUid: string;
  /**
   * As authored. Option text is never interpolated per concept, so the row
   * label is the same under every column.
   */
  optionText: string;
  /**
   * The synthetic free-text bucket; its texts live on the Details tab.
   */
  isOther?: boolean;
  /**
   * Selected uids no longer in the question's option list.
   */
  isRemoved?: boolean;
  /**
   * One entry per concept, in the order participants saw them. Zeros kept.
   */
  counts: Array<ConceptOptionCountDto>;
};

export type ConceptThemeCountDto = {
  conceptUid: string;
  /**
   * Distinct eligible participants who raised it. Zero is meaningful.
   */
  participantCount: number;
  /**
   * Of `participantCount`, those who raised it in their own answer rather than
   * under a follow-up — "five raised it, three without being asked" is a much
   * stronger claim than "five raised it". Deduped per participant, so
   * `volunteeredCount + probedCount === participantCount` always.
   */
  volunteeredCount: number;
  /**
   * The remainder: participants whose every mention came from a probe.
   */
  probedCount: number;
  /**
   * Top 1–2 typical quotes for THIS concept, so no count is shown without a
   * voice behind it, and a shared theme never speaks for every concept through
   * one unattributed quote.
   */
  quotes: Array<InsightsReportPullQuoteDto>;
  /**
   * The deliberately-flagged outlier for this concept, when there is one. Kept
   * out of `quotes` so it can be labelled as the counterpoint: in concept
   * testing the participant who went against the grain is often the finding,
   * but shown unlabelled they read as typical.
   */
  counterpoint?: InsightsReportPullQuoteDto;
};

export const ConceptThemeKind = {
  SHARED: 'shared',
  SPECIFIC: 'specific',
  MIXED: 'mixed',
} as const;

export type ConceptThemeKind =
  (typeof ConceptThemeKind)[keyof typeof ConceptThemeKind];

export type ConceptThemeDto = {
  /**
   * Shared across the concepts — Tier 2 mints one id per theme per question.
   */
  canonicalThemeId: string;
  title: string;
  description: string;
  kind: ConceptThemeKind;
  /**
   * One entry per concept, in the order participants saw them. Zeros kept.
   */
  counts: Array<ConceptThemeCountDto>;
};

export const InterviewQuestionType = {
  OPEN_ENDED: 'OPEN_ENDED',
  MULTIPLE_CHOICE: 'MULTIPLE_CHOICE',
  RANKING: 'RANKING',
  STATEMENT: 'STATEMENT',
  TASK: 'TASK',
} as const;

export type InterviewQuestionType =
  (typeof InterviewQuestionType)[keyof typeof InterviewQuestionType];

export type ConceptQuestionDto = {
  baseQuestionUid: string;
  /**
   * The question as authored, placeholders left as written. Each concept's
   * copy has its own title substituted in, so none of them can stand for the
   * question that was asked about all of them.
   */
  questionText: string;
  questionType: InterviewQuestionType;
  /**
   * In the shared stored order, the same under every concept.
   */
  themes: Array<ConceptThemeDto>;
  /**
   * The closed-ended comparison: one row per option in authored order, one
   * count per concept. Present for MULTIPLE_CHOICE and RANKING only, and never
   * empty when present.
   */
  options?: Array<ConceptOptionDto>;
  /**
   * MULTIPLE_CHOICE only: participants could pick more than one option.
   */
  multiSelect?: boolean;
  /**
   * One per concept, in the order participants saw them.
   */
  answeredByConcept: Array<ConceptAnsweredCountDto>;
  /**
   * Themes left out of `themes` because no eligible participant is left behind
   * them under any concept. The Details tab reads the same taxonomies without
   * the eligibility filter and keeps them, so disclosing the difference is what
   * stops the researcher finding two theme sets for one question.
   */
  hiddenThemes?: Array<ConceptHiddenThemeDto>;
  /**
   * Themes the clustering model says it merged into the ones shown. Labels
   * only: the model reports them, we did not measure them, so there are
   * deliberately no counts to go with them.
   */
  foldedThemeLabels?: Array<string>;
  /**
   * Themes that were fully built and then cut at the per-question cap, highest
   * support first. CAPPED — a sample, not a census. Use `omittedThemeCount` for
   * the number, never this array's length.
   */
  omittedThemeTitles?: Array<string>;
  /**
   * How many themes were cut at the cap in total.
   */
  omittedThemeCount?: number;
};

export type ConceptPanelDto = {
  conceptUid: string;
  /**
   * Participant-facing title.
   */
  title: string;
  /**
   * Researcher-only label, shown beside the title because they think in it.
   */
  internalName?: string;
  /**
   * Signed URL for the concept's first image, absent when there is none.
   */
  imageUrl?: string;
  /**
   * Signed URLs for every concept image, in display order. `imageUrl` is its
   * first entry, kept for older clients.
   */
  imageUrls?: Array<string>;
  description?: string;
  /**
   * Distinct eligible participants behind this concept — who reached it at all,
   * falling back to who said something codeable about it when reach cannot be
   * established. The denominator its theme counts are read against.
   */
  participantCount: number;
  /**
   * Participants who answered at least one of this concept's questions,
   * including ones whose interview is still unfinished. Wider than
   * `participantCount` on purpose: it is the denominator for `abandonedCount`,
   * which an analysis-only cohort could never contain.
   */
  startedCount: number;
  /**
   * Of `startedCount`, participants who never finished the interview and whose
   * last recorded answer was to one of THIS concept's questions — i.e. this is
   * where they stopped.
   *
   * Measures WHICH CONCEPT they quit during, never which position it was shown
   * in. Under a fixed order the two coincide; under a random order they don't,
   * and this field only answers the first.
   *
   * At most one concept is credited per unfinished interview, and none at all
   * when the participant's last answer was outside the concept block.
   */
  abandonedCount: number;
  /**
   * The generated write-up. Absent until the report has been generated.
   */
  summary?: string;
  /**
   * Participants the write-up draws on.
   */
  citations: Array<ParticipantRefDto>;
};

export type ConceptReportSectionDto = {
  sectionUid: string;
  sectionName: string;
  /**
   * In authored order.
   */
  concepts: Array<ConceptPanelDto>;
  /**
   * How the concepts were shown; drives the order caveats on the tab.
   */
  exposure: ConceptExposureDto;
  questions: Array<ConceptQuestionDto>;
  /**
   * Total themes hidden across this section's questions (the roll-up of
   * `ConceptQuestionDto.hiddenThemes`), so the section can state the loss in
   * one line. Absent when nothing is hidden.
   */
  hiddenThemeCount?: number;
  /**
   * Back-compat alias for `writeUpSkippedReason === TOO_MANY_CONCEPTS`, the only
   * thing this flag has ever meant and the only case worded "too many concepts
   * to write up". Deliberately NOT set for the other reasons: that sentence
   * would be untrue over a section merely waiting for its first analysed
   * answer. Branch on `writeUpSkippedReason` in new code.
   */
  writeUpSkipped?: boolean;
  writeUpSkippedReason?: ConceptWriteUpSkipReason;
};

export type QuestionSummariesDto = {
  /**
   * The aggregate feature-extraction schema version this project is pinned to.
   * The frontend switches its rendering on this; since every new version is
   * breaking, a new value is one the frontend must explicitly handle.
   */
  featuresSchemaVersion: number;
  /**
   * Responses with feature extraction COMPLETED — the cohort all summaries are computed over.
   */
  analyzedResponseCount: number;
  /**
   * Interviews with completion status COMPLETED (denominator for "N of M analyzed" messaging).
   */
  completedInterviewCount: number;
  /**
   * Completed interviews still being analyzed (extraction PENDING/PROCESSING). Drives polling.
   */
  processingResponseCount: number;
  /**
   * Open-ended questions whose theme taxonomy is still consolidating.
   * Non-zero ⇒ the displayed themes for those questions may be incomplete;
   * drives a "still consolidating" banner + polling, like processingResponseCount.
   */
  consolidatingQuestionCount: number;
  /**
   * True when the stored taxonomies predate a cohort change and a rebuild is
   * outstanding: either at least one question's taxonomy still cited a
   * participant who is no longer insights-eligible (their evidence is filtered
   * out of this response, but the themes were clustered *with* them), or a
   * rebuild was requested that no completed rebuild has covered yet
   * (MongoDB rebuild revision — the re-inclusion / new-interview case, which
   * filtering can't see). Drives the pending-rebuild banner.
   */
  rebuildPending?: boolean;
  questions: Array<QuestionSummaryDto>;
};

export type TaskQuestionSummaryDto = {
  completedCount: number;
  gaveUpCount: number;
  /**
   * Participants who pressed "I'm stuck" and saw the hint.
   */
  hintShownCount: number;
  /**
   * `completedCount / answeredCount * 100`, rounded to 1 dp.
   */
  completionRate: number;
  averageRating?: number;
  /**
   * Ratings the average is over.
   */
  ratedCount: number;
  medianDurationMs?: number;
};

export const OpenEndedTaxonomyStatus = {
  READY: 'READY',
  CONSOLIDATING: 'CONSOLIDATING',
  NONE: 'NONE',
} as const;

export type OpenEndedTaxonomyStatus =
  (typeof OpenEndedTaxonomyStatus)[keyof typeof OpenEndedTaxonomyStatus];

export type ThemeQuoteSummaryDto = {
  quote: string;
  participant: ParticipantRefDto;
  /**
   * Set when the quote came from an AI follow-up exchange.
   */
  followUpUid?: string | null;
};

export type ThemeSummaryDto = {
  tagSlug: string;
  /**
   * Stable id for the consolidated theme, reused across consolidation runs so
   * deep-links/selection survive re-clustering.
   */
  canonicalThemeId?: string;
  title: string;
  description: string;
  /**
   * Distinct participants whose responses contributed this theme.
   */
  mentionCount: number;
  /**
   * All distinct contributing participants (the `mentionCount` cohort), not just those quoted.
   */
  participants: Array<ParticipantRefDto>;
  quotes: Array<ThemeQuoteSummaryDto>;
};

export type OpenEndedQuestionSummaryDto = {
  /**
   * Themes for this question, ordered by mentionCount desc.
   */
  themes: Array<ThemeSummaryDto>;
  taxonomyStatus: OpenEndedTaxonomyStatus;
  /**
   * True when refs citing participants who are no longer insights-eligible
   * (manually excluded, rejected, disqualified, dismissed) were filtered out of
   * this question's stored taxonomy. The themes above are already clean, but
   * their clustering and wording still reflect the removed participant until the
   * queued rebuild lands — it rolls up into `QuestionSummariesDto.rebuildPending`.
   */
  hasFilteredEvidence?: boolean;
};

export type RankingOptionSummaryDto = {
  optionUid: string;
  optionText: string;
  /**
   * Mean rank over responses that ranked this option (1 = best). 0 if never ranked.
   */
  meanRank: number;
  /**
   * Responses that ranked this option at any position (mean-rank denominator).
   */
  rankedCount: number;
  /**
   * `positionCounts[i]` = responses that ranked this option (i+1)-th. Heatmap row.
   */
  positionCounts: Array<number>;
};

export type RankingQuestionSummaryDto = {
  /**
   * Rank positions present, e.g. `[1,2,3,4]`. Heatmap columns.
   */
  positions: Array<number>;
  /**
   * Ordered by `meanRank` ascending (best first); never-ranked options last.
   */
  options: Array<RankingOptionSummaryDto>;
};

export type ChoiceOptionSummaryDto = {
  optionUid: string;
  optionText: string;
  /**
   * Analyzed responses that selected this option.
   */
  count: number;
  /**
   * `count / answeredCount * 100`, rounded to 1 dp.
   */
  percentage: number;
  participants: Array<ParticipantRefDto>;
  /**
   * True for the synthetic "Other" free-text bucket.
   */
  isOther?: boolean;
  /**
   * Free-text entries, present only on the "Other" bucket.
   */
  otherTexts?: Array<string>;
};

export type ChoiceQuestionSummaryDto = {
  /**
   * When true, participants could pick more than one option — totals can exceed 100%.
   */
  multiSelect: boolean;
  /**
   * Sum of all option counts (can exceed `answeredCount` for multi-select).
   */
  totalSelections: number;
  options: Array<ChoiceOptionSummaryDto>;
};

export type QuestionSummaryDto = {
  questionUid: string;
  questionText: string;
  questionType: InterviewQuestionType;
  sectionUid: string;
  sectionName: string;
  /**
   * Analyzed responses that answered this question (the per-question denominator).
   */
  answeredCount: number;
  /**
   * Concept testing. Present only on rows produced by expanding a concept
   * section, where `questionUid` above is the derived
   * `${baseQuestionUid}_c_${conceptUid}` and there is one row per
   * (question × concept). `conceptInternalName` is the researcher's internal
   * label — this is a researcher-only view, so it is the right thing to
   * display; it is never served to participants.
   */
  baseQuestionUid?: string;
  conceptUid?: string;
  conceptInternalName?: string;
  /**
   * The concept's participant-facing title, when the researcher gave it one.
   * Shown as the sub-heading, with `conceptInternalName` beside it.
   */
  conceptTitle?: string;
  /**
   * The authored question text with its `{concept title}`
   * `{concept description}` placeholders left as written — `questionText` above
   * has this concept's own copy substituted in, so the rows of one concept
   * group can differ. This is the wording they have in common, and it is what
   * the group heading shows.
   */
  baseQuestionText?: string;
  /**
   * Signed URL for the concept's first image, so the researcher can see what
   * was actually shown. Absent when the concept has no image or signing failed
   * — the row renders without it rather than failing the request.
   */
  conceptImageUrl?: string;
  choice?: ChoiceQuestionSummaryDto;
  ranking?: RankingQuestionSummaryDto;
  themes?: OpenEndedQuestionSummaryDto;
  /**
   * TASK only. A task also carries `themes` from its think-aloud.
   */
  task?: TaskQuestionSummaryDto;
};

export type GetInterviewResponsesPageForResearcherDto = {
  results: Array<GetInterviewResponsesForResearcherDto>;
  /**
   * Total responses matching the query filters across all pages (after the
   * `extractedOnly` filter when set).
   */
  totalResults: number;
  metrics: InterviewResponsesMetricsDto;
};

export type InterviewResponsesMetricsDto = {
  /**
   * Interviews with status COMPLETED.
   */
  completedCount: number;
  /**
   * Interviews with status COMPLETED or IN_PROGRESS.
   */
  startedCount: number;
  /**
   * Average per-interview total recorded media duration in ms (sum of
   * `mediaDurationMs` across answers and follow-ups), across completed
   * interviews that recorded any media. Omitted when none have media.
   */
  avgCompletionMs?: number;
  /**
   * Average extracted quality rating (1–5) across completed interviews that
   * have a rating. Omitted when none have been rated yet.
   */
  avgQualityRating?: number;
};

export type InterviewConceptForResearcherDto = {
  uid: string;
  /**
   * Researcher-only internal label.
   */
  internalName: string;
  /**
   * Participant-facing heading; fallback when internalName is blank.
   */
  title?: string;
  order: number;
};

export const TranscriptStatus = {
  AVAILABLE: 'available',
  FAILED: 'failed',
} as const;

export type TranscriptStatus =
  (typeof TranscriptStatus)[keyof typeof TranscriptStatus];

export const MediaStatus = {
  UPLOADING: 'uploading',
  UPLOADED: 'uploaded',
  FAILED: 'failed',
} as const;

export type MediaStatus = (typeof MediaStatus)[keyof typeof MediaStatus];

export type FollowUpExchangeForResearcherDto = {
  /**
   * Stable id of the exchange (matches insights-report quote followUpUid). Absent on legacy exchanges.
   */
  uid?: string;
  aiQuestion: string;
  /**
   * Study-language translation of aiQuestion (multi-language interviews).
   */
  translatedAiQuestion?: string;
  participantAnswer?: string;
  /**
   * Study-language translation of participantAnswer.
   */
  translatedParticipantAnswer?: string;
  askedAt: string;
  answeredAt?: string;
  mediaBucketKey?: string;
  mediaMimeType?: string;
  mediaDurationMs?: number;
  mediaStatus?: MediaStatus;
  transcriptStatus?: TranscriptStatus;
  screenBucketKey?: string;
};

export type InterviewAnswerForResearcherDto = {
  textAnswer?: string;
  /**
   * Study-language translation of textAnswer (multi-language interviews).
   */
  translatedTextAnswer?: string;
  selectedOptions?: Array<string>;
  rankedOptions?: Array<string>;
  acknowledged?: boolean;
  otherText?: string;
  /**
   * Study-language translation of otherText.
   */
  translatedOtherText?: string;
  answeredAt: string;
  followUps?: Array<FollowUpExchangeForResearcherDto>;
  mediaBucketKey?: string;
  screenBucketKey?: string;
  mediaMimeType?: string;
  mediaDurationMs?: number;
  mediaStatus?: MediaStatus;
  transcriptStatus?: TranscriptStatus;
  taskOutcome?: TaskOutcome;
  taskRating?: number;
  taskStartedAt?: string;
  taskHintShown?: boolean;
};

export type InterviewQuestionWithAnswerDto = {
  uid: string;
  questionType: InterviewQuestionType;
  text: string;
  answer?: InterviewAnswerForResearcherDto | null;
  /**
   * Concept testing. Present only on derived questions
   * (`${baseQuestionUid}_c_${conceptUid}`) from a concept section.
   */
  baseQuestionUid?: string;
  conceptUid?: string;
  /**
   * Researcher-only concept label for chips / column headers.
   */
  conceptInternalName?: string;
  /**
   * Participant-facing concept heading; dropdown fallback.
   */
  conceptTitle?: string;
};

export type InterviewSectionWithAnswersDto = {
  uid: string;
  name: string;
  questions: Array<InterviewQuestionWithAnswerDto>;
  /**
   * Concepts attached to this section, in authored order. Present only when
   * the section has concept testing enabled — drives the drawer concept
   * dropdown and Results concept lozenges.
   */
  concepts?: Array<InterviewConceptForResearcherDto>;
};

export const FeatureExtractionStatus = {
  PENDING: 'PENDING',
  PROCESSING: 'PROCESSING',
  COMPLETED: 'COMPLETED',
  PARTIAL: 'PARTIAL',
  FAILED: 'FAILED',
} as const;

export type FeatureExtractionStatus =
  (typeof FeatureExtractionStatus)[keyof typeof FeatureExtractionStatus];

export const InterviewCompletionStatus = {
  NOT_STARTED: 'NOT_STARTED',
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED',
} as const;

export type InterviewCompletionStatus =
  (typeof InterviewCompletionStatus)[keyof typeof InterviewCompletionStatus];

export const InsightsExclusionReason = {
  MANUAL: 'MANUAL',
  REJECTED: 'REJECTED',
  NOT_A_FIT: 'NOT_A_FIT',
  DISMISSED: 'DISMISSED',
} as const;

export type InsightsExclusionReason =
  (typeof InsightsExclusionReason)[keyof typeof InsightsExclusionReason];

export type GetInterviewResponsesForResearcherDto = {
  participantUserId: string;
  participantName: string;
  participantEmail?: string;
  participantPicture?: string;
  /**
   * True when this response does not factor into insights generation, for
   * any reason (manual researcher exclusion or terminal screener outcome).
   * Computed with the same predicate as consolidation, so it can't disagree
   * with what the generated report contains.
   */
  isExcludedFromInsights?: boolean;
  insightsExclusionReason?: InsightsExclusionReason;
  interviewStatus: InterviewCompletionStatus;
  startedAt: string;
  completedAt?: string;
  /**
   * Language the participant responded in, when it differs from the study
   * language. Answers/probes carry `translated*` study-language counterparts
   * once ingestion translation has run.
   */
  responseLanguage?: string;
  featureExtractionStatus?: FeatureExtractionStatus;
  /**
   * When the feature-extraction pipeline last started (dev/debugging).
   */
  featureExtractionStartedAt?: string;
  /**
   * When the feature-extraction pipeline last completed (dev/debugging).
   */
  featureExtractionCompletedAt?: string;
  /**
   * Last feature-extraction error message, if the pipeline failed (dev).
   */
  featureExtractionError?: string;
  /**
   * One-line, evocative summary of the interview (extracted feature).
   */
  tagline?: string;
  /**
   * 3-5 bullet summary of the interview (extracted feature).
   */
  summaryBullets?: Array<string>;
  /**
   * 1-5 whole-star interview-quality rating (extracted feature).
   */
  qualityRating?: number;
  sections: Array<InterviewSectionWithAnswersDto>;
};

export type GeneratedProjectMediaContextDto = {
  context: string;
  sourceHash: string;
  /**
   * ISO timestamp of the generation.
   */
  generatedAt: string;
};

export type GenerateProjectMediaContextRequestDto = {
  type?: MediaType;
  /**
   * IMAGE drafts — bucket keys in display order, minted by the upload/import
   * endpoints.
   */
  imageKeys?: Array<string>;
};

export const MediaType = {
  LINK: 'LINK',
  IMAGE: 'IMAGE',
  PDF: 'PDF',
} as const;

export type MediaType = (typeof MediaType)[keyof typeof MediaType];

export type ProjectMediaEmbedCheckDto = {
  embeddable: boolean;
  reason: EmbedCheckReason;
  /**
   * The URL to store and frame instead of the one supplied, when a known host
   * wants a different form embedded (a Figma share link becomes its embed
   * link). Absent means keep the URL as pasted.
   */
  embedUrl?: string;
};

export const EmbedCheckReason = {
  KNOWN_HOST: 'known-host',
  HEADERS: 'headers',
  UNKNOWN: 'unknown',
} as const;

export type EmbedCheckReason =
  (typeof EmbedCheckReason)[keyof typeof EmbedCheckReason];

export type CheckProjectMediaEmbedDto = {
  url: string;
};

export type ImportProjectMediaUrlDto = {
  url: string;
};

export type UploadedProjectMediaDto = {
  imageKey: string;
  signedUrl: string;
  filename: string;
};

export type UpdatePublishedProjectDetailsDto = {
  name?: string;
  internalName?: string;
  description?: string;
  studyGoals?: string;
  duration?: number;
  incentive?: number;
  incentiveUpdateType?: ProjectIncentiveUpdateType;
  welcomeScreen?: WelcomeScreenDto;
  /**
   * The marketplace target. Hard cap only; the kind/org-specific limit is
   * enforced in the service via getParticipantTargetLimit. 0 is accepted here
   * because a project with stored `recruitmentTargets` may be invite-only;
   * the service refuses it for a project without them
   * (`resolveRecruitmentTargetWrite`). See `projects/recruitment-targets.md`.
   */
  numberOfParticipants?: number;
  taskDeadline?: UpdatePublishedTaskDeadlineDto;
  /**
   * Auto-invite switch. `true` sets `automations.invite` to a zero-delay
   * config (what the launch flow writes); `false` unsets it. Kept as a boolean
   * rather than exposing the automations sub-document so the researcher UI
   * cannot rewrite unrelated automations (e.g. auto-pay) by accident.
   */
  autoInvite?: boolean;
  /**
   * Auto-pay switch. `true` sets `automations.pay` to the standard 48-hour
   * delay (what the launch flow writes); `false` unsets it and also clears the
   * `pay.autopayOn` already scheduled on this project's ATTENDED responses,
   * since the autopay cron selects on that field alone and never re-reads the
   * project. Boolean for the same reason as `autoInvite`.
   */
  autoPay?: boolean;
  /**
   * Task URL for a custom-URL study (`remoteCommunicationDetails`).
   * Write-once: it may only be filled in when the project has none, never
   * changed. Participants who were already invited hold a snapshot of this
   * value, so a rewrite would leave them pointing at a dead link; setting it
   * for the first time cannot strand anyone, because nobody was ever sent one.
   */
  taskUrl?: string;
  /**
   * Per-channel participant targets. Accepted only for organizations on
   * `granular-recruitment-targets`. The marketplace channel goes through the
   * same quota re-target and credit re-reservation as `numberOfParticipants`.
   * See `projects/recruitment-targets.md`.
   */
  recruitmentTargets?: RecruitmentTargets;
  /**
   * Campaign tags; `[]` clears them. Accepted only for organizations on
   * `granular-recruitment-targets`. See `projects/campaigns.md`.
   */
  campaigns?: Array<string>;
};

export type RecruitmentTargets = {
  /**
   * Respondent sources the participants: matching and the participant feed.
   */
  marketplace: number;
  /**
   * The researcher invites participants already on Respondent: participant
   * database, past participants, admin bulk invites, campaigns.
   */
  participantDatabase: number;
  /**
   * The researcher brings participants from outside Respondent, through the
   * project's external invite link (`Project.externalInvites`), which admits
   * at most this many. See `features/external-participants`.
   */
  externalParticipants: number;
};

export type UpdatePublishedTaskDeadlineDto = {
  endsAt: string;
  timezone?: string;
};

export type WelcomeScreenDto = {
  title?: string;
  message?: string;
};

export const ProjectIncentiveUpdateType = {
  ALL_RESPONSES: 'ALL_RESPONSES',
  NEW_RESPONSES_ONLY: 'NEW_RESPONSES_ONLY',
} as const;

export type ProjectIncentiveUpdateType =
  (typeof ProjectIncentiveUpdateType)[keyof typeof ProjectIncentiveUpdateType];

export type UpdateResearcherDraftDto = {
  talkToAudience?: ParticipantAudienceType;
  kindOfResearch?: KindOfResearch;
  researchConductType?: ResearchConductType;
  duration?: number;
  incentive?: number;
  /**
   * The marketplace channel. Zero recruits nobody from the marketplace,
   * which the service accepts only while the recruitment targets (stored, or
   * written by this save's external invites) still add up to one; the
   * inherited minimum of one would refuse it before that rule runs.
   */
  numberOfParticipants?: number;
  description?: string;
  studyGoals?: string;
  professionalJobTitles?: Array<string>;
  professionalIndustries?: Array<string>;
  /**
   * @deprecated Use professionalIndustries instead.
   *
   * @deprecated
   */
  professionalIndustry?: Array<string>;
  desiredSkills?: Array<string>;
  seniority?: Array<SeniorityLevel>;
  jobFunctions?: Array<JobFunction>;
  companySize?: Array<CompanySize>;
  businessOwner?: boolean;
  topics?: Array<string>;
  ageGroups?: Array<AgeGroup>;
  minAge?: number | null;
  maxAge?: number | null;
  education?: Array<EducationLevel>;
  ethnicity?: Array<Ethnicities>;
  genders?: Gender;
  householdIncome?: Array<HouseholdIncome>;
  income?: Income;
  country?: ProjectCountryCode;
  countries?: Array<ProjectCountryCode>;
  cities?: Array<ProjectCities>;
  locationRespondentInPerson?: ProjectLocationInPerson;
  visibility?: ProjectVisibility;
  remoteCommunicationDetails?: string | null;
  specialInstructions?: string | null;
  exposeParticipantEmails?: boolean;
  ndaType?: NdaType;
  ndaLink?: string;
  ndaFileUrl?: string;
  externalResearcher?: ExternalResearcher;
  task?: Task | null;
  automations?: ProjectAutomations;
  filtering?: ProjectFiltering;
  keyQualifiers?: Array<KeyQualifier>;
  hideCompanyName?: boolean;
  companyName?: string;
  interviewDeliveryMode?: InterviewDeliveryMode;
  studyLanguage?: StudyLanguage;
  responseLanguages?: Array<StudyLanguage> | null;
  advancedTargeting?: UpdateAdvancedTargeting;
  name?: string;
  internalName?: string;
  screenerQuestions?: Array<UpdateDraftScreenerQuestionDto>;
  /**
   * Optimistic-concurrency guard for `screenerQuestions` (ONCALL-2139).
   * Send the `screenerVersion` the project was hydrated with; the save is
   * rejected with 409 when another session has since replaced the screener.
   * Only meaningful together with `screenerQuestions`; omit to keep the
   * legacy last-write-wins behavior.
   */
  screenerVersion?: number;
  welcomeScreen?: WelcomeScreenDto;
  rejectionScreen?: RejectionScreenDto;
  closingMessage?: ClosingMessageDto;
  interviewSections?: Array<InterviewSectionDto>;
  /**
   * PX-456 open audience: the researcher picked the audience type in the
   * Target audience drawer. Recorded once as `metadata.audienceTypeSelectedAt`
   * for organizations on `allow-optional-topics-and-industries`; ignored
   * otherwise. Only the drawer sends it.
   */
  audienceTypeSelected?: boolean;
  /**
   * Per-channel participant targets. Accepted only for organizations on
   * `granular-recruitment-targets`; `numberOfParticipants` is written as the
   * marketplace channel so every existing reader keeps working. See
   * `projects/recruitment-targets.md`.
   */
  recruitmentTargets?: RecruitmentTargets;
  /**
   * Campaign tags; `[]` clears them. Accepted only for organizations on
   * `granular-recruitment-targets`. See `projects/campaigns.md`.
   */
  campaigns?: Array<string>;
  /**
   * The researcher's own participants, through the project's external invite
   * link: an object turns the source on (or updates it), `null` turns it off.
   * Only for organizations on `external-participant-invites`, and only for
   * the study types the link admits to (AI-moderated and unmoderated).
   */
  externalInvites?: UpdateDraftExternalInvitesDto | null;
};

export type UpdateDraftExternalInvitesDto = {
  /**
   * How many of the researcher's own participants the study wants, and the
   * most the link admits. Stored as `recruitmentTargets.externalParticipants`
   * (the entity caps every channel at 1000), not on `externalInvites`.
   */
  target: number;
  /**
   * Admit straight in without asking the screener questions.
   */
  skipScreener?: boolean;
  /**
   * The researcher pays the incentive off the platform.
   */
  incentivePaidExternally?: boolean;
};

export type InterviewConceptDto = {
  /**
   * Charset-constrained, unlike a question uid: a concept uid becomes part of
   * the derived question uid (`${baseQuestionUid}_c_${conceptUid}`) that the
   * participant's recording S3 key is built from, and the upload endpoint
   * rejects anything outside this charset
   * (`interview-media/dto/initiate-upload.dto.ts`). Catching it here fails the
   * draft save instead of the participant's first recording.
   */
  uid: string;
  /**
   * Researcher-only internal label. Never served to participants.
   */
  internalName: string;
  title?: string;
  description?: string;
  order: number;
  /**
   * Same explicit-clear contract as a question's media, and the same shape
   * constraint (PDF and URL are exclusive; images may sit alongside either).
   */
  media?: InterviewQuestionMediaDto | null;
};

export type InterviewQuestionMediaDto = {
  type: MediaType;
  /**
   * Required for LINK (via the shape constraint). LINK only.
   */
  url?: string;
  /**
   * LINK only — whether the participant pane embeds the page or offers only
   * the open-in-new-tab control. Absent means embed. Meaningless (and
   * rejected) on IMAGE / PDF, which are always rendered inline.
   */
  embedEnabled?: boolean;
  /**
   * Images in display order. IMAGE only — a media that shows images shows
   * nothing else. Produced by the media upload/import endpoints; clients
   * never mint these themselves.
   */
  imageKeys?: Array<string>;
  /**
   * PDF bucket key. PDF only — exclusive with both url and imageKeys.
   * Produced by the media upload endpoint; clients never mint this.
   */
  pdfKey?: string;
  label?: string;
  aiContext?: string;
  aiContextGeneratedAt?: string;
  aiContextSourceHash?: string;
};

export type InterviewConceptBlockDto = {
  concepts: Array<InterviewConceptDto>;
  /**
   * Concepts each participant sees; absent means all.
   */
  sampleSize?: number;
  /**
   * Absent means authored order.
   */
  randomizeOrder?: boolean;
};

export const FollowUpType = {
  NONE: 'NONE',
  IF_SHORT_ANSWER: 'IF_SHORT_ANSWER',
  ONE_FOLLOWUP: 'ONE_FOLLOWUP',
  TWO_THREE_FOLLOWUPS: 'TWO_THREE_FOLLOWUPS',
} as const;

export type FollowUpType = (typeof FollowUpType)[keyof typeof FollowUpType];

export type InterviewFollowUpConfigDto = {
  type: FollowUpType;
  guidelines?: string;
};

export type InterviewQuestionOptionDto = {
  uid: string;
  text: string;
  order: number;
};

export type InterviewQuestionDto = {
  uid: string;
  questionType: InterviewQuestionType;
  text: string;
  order: number;
  options?: Array<InterviewQuestionOptionDto>;
  multiSelect?: boolean;
  showOtherOption?: boolean;
  randomizeOrder?: boolean;
  continueButtonText?: string;
  followUpConfig?: InterviewFollowUpConfigDto;
  preferredInputType?: string;
  /**
   * Explicit-clear contract: omit to leave the stored media untouched (this
   * is what protects attachments from AI Create chat edits, which never author
   * media), send `null` to remove it, send an object to replace it. See
   * `mergeMediaIntoSections` in the draft-save path.
   */
  media?: InterviewQuestionMediaDto | null;
  /**
   * Record the participant's screen while they answer this question.
   * Audio/video interviews only — text interviews have no recorder to pair
   * the screen take with.
   *
   * Independent of `media`: a usability task worth recording does not have to
   * be something Respondent hosts ("find the last receipt in your own inbox").
   */
  recordScreen?: boolean;
  /**
   * Hide the section's stimulus while this question is asked (the
   * participant client hides but never unmounts the pane). Rides the same
   * merge rails as `recordScreen`: researcher-authored, absent from the AI
   * tool schemas, restored by uid on AI script rewrites.
   */
  hideStimulus?: boolean;
  /**
   * TASK only — shown and read aloud when the participant presses "I'm
   * stuck". Rides the same merge rails as `recordScreen` (researcher-authored,
   * absent from the AI tool schemas), so an AI rewrite cannot drop it.
   *
   * Same explicit-clear contract as `media`: omitted keeps the stored hint,
   * `null` deletes it. Without the `null` an emptied hint field would be
   * indistinguishable from an untouched one, and the merge would restore it.
   */
  hint?: string | null;
  /**
   * TASK only — ask a 1–5 ease rating after "done" (1 = very hard, 5 = very easy). Absent means true.
   */
  askRating?: boolean;
  /**
   * TASK only — let the moderator ask one transcript-aware question after the
   * task. Absent means true.
   */
  askDebrief?: boolean;
};

export type InterviewSectionDto = {
  uid: string;
  name: string;
  order: number;
  questions?: Array<InterviewQuestionDto>;
  /**
   * Same explicit-clear contract as a question's media: omit to leave the
   * stored block untouched (this is what protects concepts from AI Create
   * chat edits, which never author concepts), send `null` to remove concept
   * testing from the section, send an object to replace it. See
   * `mergeProjectMedia`.
   */
  conceptBlock?: InterviewConceptBlockDto | null;
  /**
   * Stimulus — one attachment shown alongside every question in the
   * section (kept mounted across questions on the participant side). Same
   * shape and single-kind rule as a question's media, and the same
   * explicit-clear contract: omit to leave the stored value untouched (AI
   * Create edits never author it), send `null` to remove it, send an object
   * to replace it. See `mergeProjectMedia`.
   */
  stimulus?: InterviewQuestionMediaDto | null;
};

export type ClosingMessageDto = {
  closingMessage?: string;
  redirectOnComplete?: boolean;
  redirectUrl: string;
};

export type RejectionScreenDto = {
  closingMessage?: string;
  redirectOnComplete?: boolean;
  redirectUrl: string;
};

export type UpdateDraftScreenerQuestionAnswerDto = {
  createdAt?: string;
  uid: string;
  /**
   * Required  unless `isOther`, then text will be set to 'Other'
   */
  text?: string;
  /**
   * CHECKBOX: {
   * id: 3,
   * answerValues: [
   * { id: 1, text: 'May Select' },
   * { id: 2, text: 'Must Select' },
   * { id: 3, text: 'Disqualify' },
   * ],
   * },
   * RADIO: {
   * id: 1,
   * answerValues: [
   * { id: 1, text: 'Qualify' },
   * { id: 2, text: 'Disqualify' },
   * ],
   * },
   */
  answerValue: number;
  /**
   * Skip logic for radio questions. Uid of question to skip to.
   */
  goToQuestionUid?: string;
  /**
   * Is 'Other' answer with comment field
   */
  isOther?: boolean;
  id?: string;
  updatedAt?: string;
};

export const MatrixSelectionMode = {
  SINGLE: 'single',
  MULTI: 'multi',
} as const;

export type MatrixSelectionMode =
  (typeof MatrixSelectionMode)[keyof typeof MatrixSelectionMode];

export const CheckboxMatchType = { ALL: 'all', ANY: 'any' } as const;

export type CheckboxMatchType =
  (typeof CheckboxMatchType)[keyof typeof CheckboxMatchType];

export const MatrixMatchType = { ALL: 'all', ANY: 'any' } as const;

export type MatrixMatchType =
  (typeof MatrixMatchType)[keyof typeof MatrixMatchType];

export type ScreenerQuestionMatrixRow = {
  uid: string;
  text: string;
  qualifyingAnswerUids?: Array<string>;
  /**
   * Multi-mode only. Columns the participant must select for the row to qualify.
   */
  mustSelectAnswerUids?: Array<string>;
  /**
   * Multi-mode only. Columns that disqualify the row when selected.
   */
  disqualifyAnswerUids?: Array<string>;
};

export const MultipleAnswersBoolType = { AND: 'AND', OR: 'OR' } as const;

export type MultipleAnswersBoolType =
  (typeof MultipleAnswersBoolType)[keyof typeof MultipleAnswersBoolType];

export const MultipleAnswersBoolTypeId = { 1: 1, 2: 2 } as const;

export type MultipleAnswersBoolTypeId =
  (typeof MultipleAnswersBoolTypeId)[keyof typeof MultipleAnswersBoolTypeId];

export type SkipLogicMultipleAnswersBoolType = {
  id: MultipleAnswersBoolTypeId;
  text: MultipleAnswersBoolType;
};

export type SkipLogicMultipleAnswer = {
  answer: string;
  logicType: ScreenerQuestionSkipLogicType;
  /**
   * Set only for MATRIX questions — the matrix row this criterion applies to.
   * `answer` holds the column uid.
   */
  rowUid?: string;
};

export const SkipLogicType = {
  SELECTED: 'Selected',
  NOT_SELECTED: 'Not Selected',
} as const;

export type SkipLogicType = (typeof SkipLogicType)[keyof typeof SkipLogicType];

export const SkipLogicTypeId = { 1: 1, 2: 2 } as const;

export type SkipLogicTypeId =
  (typeof SkipLogicTypeId)[keyof typeof SkipLogicTypeId];

export type ScreenerQuestionSkipLogicType = {
  id: SkipLogicTypeId;
  text: SkipLogicType;
};

export type ScreenerQuestionSkipLogic = {
  answerUid: string;
  goToQuestionUid?: string;
  /**
   * Set only for MATRIX questions — the matrix row this criterion applies to.
   * `answerUid` holds the column uid.
   */
  rowUid?: string;
  skipLogicType?: ScreenerQuestionSkipLogicType;
  multipleAnswers?: Array<SkipLogicMultipleAnswer>;
  multipleAnswersBoolType?: SkipLogicMultipleAnswersBoolType;
};

export type UpdateDraftScreenerQuestionDto = {
  createdAt?: string;
  questionType?: QuestionType;
  uid?: string;
  text: string;
  isRequired?: boolean;
  skipLogic?: boolean;
  includeOtherOption?: boolean;
  isEnd?: boolean;
  logic?: ScreenerQuestionSkipLogic;
  minValue?: number;
  maxValue?: number;
  step?: number;
  showStep?: boolean;
  randomizeOrder?: boolean;
  matrixRows?: Array<ScreenerQuestionMatrixRow>;
  matrixMatchType?: MatrixMatchType;
  matchType?: CheckboxMatchType;
  matrixSelectionMode?: MatrixSelectionMode;
  /**
   * Warnings found during validation on create, update & publish.
   */
  warnings?: Array<string>;
  id?: string;
  answers?: Array<UpdateDraftScreenerQuestionAnswerDto>;
  updatedAt?: string;
};

export const VehicleBrand = {
  FORD: 'ford',
  CHEVROLET: 'chevrolet',
  DODGE: 'dodge',
  RAM: 'ram',
  JEEP: 'jeep',
  GMC: 'gmc',
  CADILLAC: 'cadillac',
  TESLA: 'tesla',
  RIVIAN: 'rivian',
  LUCID: 'lucid',
  TOYOTA: 'toyota',
  HONDA: 'honda',
  NISSAN: 'nissan',
  MAZDA: 'mazda',
  SUBARU: 'subaru',
  MITSUBISHI: 'mitsubishi',
  LEXUS: 'lexus',
  ACURA: 'acura',
  INFINITI: 'infiniti',
  HYUNDAI: 'hyundai',
  KIA: 'kia',
  GENESIS: 'genesis',
  BMW: 'bmw',
  MERCEDESBENZ: 'mercedesbenz',
  AUDI: 'audi',
  VOLKSWAGEN: 'volkswagen',
  PORSCHE: 'porsche',
  VOLVO: 'volvo',
  MINI: 'mini',
  LANDROVER: 'landrover',
  JAGUAR: 'jaguar',
  FIAT: 'fiat',
  ALFAROMEO: 'alfaromeo',
  PEUGEOT: 'peugeot',
  RENAULT: 'renault',
  FERRARI: 'ferrari',
  LAMBORGHINI: 'lamborghini',
  MASERATI: 'maserati',
  BENTLEY: 'bentley',
  ROLLSROYCE: 'rollsroyce',
  OTHER: 'other',
} as const;

export type VehicleBrand = (typeof VehicleBrand)[keyof typeof VehicleBrand];

export const StreamingService = {
  NETFLIX: 'netflix',
  AMAZONPRIMEVIDEO: 'amazonprimevideo',
  DISNEYPLUS: 'disneyplus',
  HULU: 'hulu',
  HBOMAX: 'hbomax',
  APPLETVPLUS: 'appletvplus',
  PARAMOUNTPLUS: 'paramountplus',
  PEACOCK: 'peacock',
  YOUTUBETV: 'youtubetv',
  YOUTUBEPREMIUM: 'youtubepremium',
  CRUNCHYROLL: 'crunchyroll',
  DISCOVERYPLUS: 'discoveryplus',
  SHOWTIME: 'showtime',
  STARZ: 'starz',
  SPOTIFY: 'spotify',
  APPLEMUSIC: 'applemusic',
  YOUTUBEMUSIC: 'youtubemusic',
  AMAZONMUSIC: 'amazonmusic',
  TIDAL: 'tidal',
  PANDORA: 'pandora',
  SOUNDCLOUD: 'soundcloud',
  SLINGTV: 'slingtv',
  FUBOTV: 'fubotv',
  OTHER: 'other',
} as const;

export type StreamingService =
  (typeof StreamingService)[keyof typeof StreamingService];

export const SocialNetworkPlatform = {
  FACEBOOK: 'facebook',
  INSTAGRAM: 'instagram',
  TWITTER: 'twitter',
  TIKTOK: 'tiktok',
  LINKEDIN: 'linkedin',
  SNAPCHAT: 'snapchat',
  PINTEREST: 'pinterest',
  REDDIT: 'reddit',
  YOUTUBE: 'youtube',
  WHATSAPP: 'whatsapp',
  TELEGRAM: 'telegram',
  DISCORD: 'discord',
  TWITCH: 'twitch',
  MASTODON: 'mastodon',
  THREADS: 'threads',
  BLUESKY: 'bluesky',
  WECHAT: 'wechat',
  WEIBO: 'weibo',
  CLUBHOUSE: 'clubhouse',
  NEXTDOOR: 'nextdoor',
  OTHER: 'other',
} as const;

export type SocialNetworkPlatform =
  (typeof SocialNetworkPlatform)[keyof typeof SocialNetworkPlatform];

export const SmartHomeDevice = {
  AMAZONECHO: 'amazonecho',
  GOOGLENEST: 'googlenest',
  APPLEHOMEPOD: 'applehomepod',
  ECHOSHOW: 'echoshow',
  NESTHUB: 'nesthub',
  NESTTHERMOSTAT: 'nestthermostat',
  ECOBEE: 'ecobee',
  HONEYWELLHOME: 'honeywellhome',
  PHILIPSHUE: 'philipshue',
  LIFX: 'lifx',
  NANOLEAF: 'nanoleaf',
  RING: 'ring',
  ARLO: 'arlo',
  NESTCAM: 'nestcam',
  BLINK: 'blink',
  AUGUSTLOCK: 'augustlock',
  TPLINK: 'tplink',
  WEMO: 'wemo',
  KASA: 'kasa',
  ROOMBA: 'roomba',
  ROBOROCK: 'roborock',
  EUFY: 'eufy',
  SMARTREFRIGERATOR: 'smartrefrigerator',
  SMARTOVEN: 'smartoven',
  SMARTWASHER: 'smartwasher',
  OTHER: 'other',
} as const;

export type SmartHomeDevice =
  (typeof SmartHomeDevice)[keyof typeof SmartHomeDevice];

export const ProductivitySoftware = {
  MICROSOFTOFFICE: 'microsoftoffice',
  MICROSOFT365: 'microsoft365',
  GOOGLEWORKSPACE: 'googleworkspace',
  APPLEIWORK: 'appleiwork',
  NOTION: 'notion',
  EVERNOTE: 'evernote',
  TRELLO: 'trello',
  ASANA: 'asana',
  MONDAYDOTCOM: 'mondaydotcom',
  SLACK: 'slack',
  MICROSOFTTEAMS: 'microsoftteams',
  ZOOM: 'zoom',
  AIRTABLE: 'airtable',
  CLICKUP: 'clickup',
  BASECAMP: 'basecamp',
  JIRA: 'jira',
  CONFLUENCE: 'confluence',
  DROPBOX: 'dropbox',
  GOOGLEDRIVE: 'googledrive',
  OTHER: 'other',
} as const;

export type ProductivitySoftware =
  (typeof ProductivitySoftware)[keyof typeof ProductivitySoftware];

export const OnlineTravelBooking = {
  EXPEDIA: 'expedia',
  BOOKINGDOTCOM: 'bookingdotcom',
  AIRBNB: 'airbnb',
  VRBO: 'vrbo',
  KAYAK: 'kayak',
  PRICELINE: 'priceline',
  HOTELSDOTCOM: 'hotelsdotcom',
  TRIPADVISOR: 'tripadvisor',
  ORBITZ: 'orbitz',
  TRAVELOCITY: 'travelocity',
  HOTWIRE: 'hotwire',
  SKYSCANNER: 'skyscanner',
  AGODA: 'agoda',
  TRIPDOTCOM: 'tripdotcom',
  HOPPER: 'hopper',
  GOOGLEFLIGHTS: 'googleflights',
  OTHER: 'other',
} as const;

export type OnlineTravelBooking =
  (typeof OnlineTravelBooking)[keyof typeof OnlineTravelBooking];

export const OnlineMarketplacePlatform = {
  AMAZON: 'amazon',
  EBAY: 'ebay',
  ETSY: 'etsy',
  WALMART: 'walmart',
  TARGET: 'target',
  BESTBUY: 'bestbuy',
  ALIBABA: 'alibaba',
  ALIEXPRESS: 'aliexpress',
  SHOPIFY: 'shopify',
  WAYFAIR: 'wayfair',
  MERCARI: 'mercari',
  POSHMARK: 'poshmark',
  DEPOP: 'depop',
  FACEBOOKMARKETPLACE: 'facebookmarketplace',
  CRAIGSLIST: 'craigslist',
  OFFERUP: 'offerup',
  LETGO: 'letgo',
  OTHER: 'other',
} as const;

export type OnlineMarketplacePlatform =
  (typeof OnlineMarketplacePlatform)[keyof typeof OnlineMarketplacePlatform];

export const OnlineLearningPlatform = {
  COURSERA: 'coursera',
  UDEMY: 'udemy',
  LINKEDINLEARNING: 'linkedinlearning',
  SKILLSHARE: 'skillshare',
  EDX: 'edx',
  KHANACADEMY: 'khanacademy',
  PLURALSIGHT: 'pluralsight',
  MASTERCLASS: 'masterclass',
  UDACITY: 'udacity',
  DATACAMP: 'datacamp',
  CODECADEMY: 'codecademy',
  TREEHOUSE: 'treehouse',
  BRILLIANT: 'brilliant',
  DUOLINGO: 'duolingo',
  ROSETTASTONE: 'rosettastone',
  BABBEL: 'babbel',
  GOOGLESKILLSHOP: 'googleskillshop',
  OTHER: 'other',
} as const;

export type OnlineLearningPlatform =
  (typeof OnlineLearningPlatform)[keyof typeof OnlineLearningPlatform];

export const OnlineBankingTool = {
  CHIME: 'chime',
  VARO: 'varo',
  CURRENT: 'current',
  REVOLUT: 'revolut',
  N26: 'n26',
  MONZO: 'monzo',
  MINT: 'mint',
  YNAB: 'ynab',
  PERSONALCAPITAL: 'personalcapital',
  QUICKEN: 'quicken',
  PAYPAL: 'paypal',
  VENMO: 'venmo',
  CASHAPP: 'cashapp',
  ZELLE: 'zelle',
  APPLEPAY: 'applepay',
  GOOGLEPAY: 'googlepay',
  ROBINHOOD: 'robinhood',
  WEBULL: 'webull',
  ACORNS: 'acorns',
  STASH: 'stash',
  BETTERMENT: 'betterment',
  WEALTHFRONT: 'wealthfront',
  COINBASE: 'coinbase',
  BINANCE: 'binance',
  KRAKEN: 'kraken',
  OTHER: 'other',
} as const;

export type OnlineBankingTool =
  (typeof OnlineBankingTool)[keyof typeof OnlineBankingTool];

export const HealthWellnessApp = {
  APPLEFITNESS: 'applefitness',
  FITBIT: 'fitbit',
  STRAVA: 'strava',
  PELOTON: 'peloton',
  NIKETRAININGCLUB: 'niketrainingclub',
  MYFITNESSPAL: 'myfitnesspal',
  LOSEIT: 'loseit',
  HEADSPACE: 'headspace',
  CALM: 'calm',
  INSIGHTTIMER: 'insighttimer',
  TENPERCENTHAPPIER: 'tenpercenthappier',
  BETTERHELP: 'betterhelp',
  TALKSPACE: 'talkspace',
  SLEEPCYCLE: 'sleepcycle',
  SLUMBER: 'slumber',
  APPLEHEALTH: 'applehealth',
  GOOGLEFIT: 'googlefit',
  SAMSUNGHEALTH: 'samsunghealth',
  NOOM: 'noom',
  WEIGHTWATCHERS: 'weightwatchers',
  CRONOMETER: 'cronometer',
  OTHER: 'other',
} as const;

export type HealthWellnessApp =
  (typeof HealthWellnessApp)[keyof typeof HealthWellnessApp];

export const FoodDeliveryService = {
  UBEREATS: 'ubereats',
  DOORDASH: 'doordash',
  GRUBHUB: 'grubhub',
  POSTMATES: 'postmates',
  SEAMLESS: 'seamless',
  INSTACART: 'instacart',
  DELIVEROO: 'deliveroo',
  JUSTEAT: 'justeat',
  SKIPTHEDISHES: 'skipthedishes',
  MENULOG: 'menulog',
  FOODPANDA: 'foodpanda',
  ZOMATO: 'zomato',
  SWIGGY: 'swiggy',
  RAPPI: 'rappi',
  OTHER: 'other',
} as const;

export type FoodDeliveryService =
  (typeof FoodDeliveryService)[keyof typeof FoodDeliveryService];

export const DesignPrototypingTool = {
  FIGMA: 'figma',
  ADOBEXD: 'adobexd',
  SKETCH: 'sketch',
  INVISION: 'invision',
  FRAMER: 'framer',
  AXURE: 'axure',
  BALSAMIQ: 'balsamiq',
  MARVEL: 'marvel',
  PROTOIO: 'protoio',
  ADOBEPHOTOSHOP: 'adobephotoshop',
  ADOBEILLUSTRATOR: 'adobeillustrator',
  CANVA: 'canva',
  AFFINITYDESIGNER: 'affinitydesigner',
  PRINCIPLE: 'principle',
  ORIGAMI: 'origami',
  OTHER: 'other',
} as const;

export type DesignPrototypingTool =
  (typeof DesignPrototypingTool)[keyof typeof DesignPrototypingTool];

export const CodingAiTool = {
  CLAUDECODE: 'claudecode',
  CURSOR: 'cursor',
  WINDSURF: 'windsurf',
  GITHUBCOPILOT: 'githubcopilot',
  CHATGPT: 'chatgpt',
  GEMINI: 'gemini',
  COPILOTCHAT: 'copilotchat',
  OTHER: 'other',
} as const;

export type CodingAiTool = (typeof CodingAiTool)[keyof typeof CodingAiTool];

export const CloudPlatform = {
  AWS: 'aws',
  MICROSOFTAZURE: 'microsoftazure',
  GOOGLECLOUD: 'googlecloud',
  IBMCLOUD: 'ibmcloud',
  ORACLECLOUD: 'oraclecloud',
  ALIBABACLOUD: 'alibabacloud',
  DIGITALOCEAN: 'digitalocean',
  HEROKU: 'heroku',
  VERCEL: 'vercel',
  NETLIFY: 'netlify',
  CLOUDFLARE: 'cloudflare',
  LINODE: 'linode',
  OTHER: 'other',
} as const;

export type CloudPlatform = (typeof CloudPlatform)[keyof typeof CloudPlatform];

export const AssistiveTechnology = {
  JAWS: 'jaws',
  NVDA: 'nvda',
  VOICEOVER: 'voiceover',
  TALKBACK: 'talkback',
  NARRATOR: 'narrator',
  ZOOMTEXT: 'zoomtext',
  MAGNIFIER: 'magnifier',
  DRAGONNATURALLYSPEAKING: 'dragonnaturallyspeaking',
  VOICECONTROL: 'voicecontrol',
  VOICEACCESS: 'voiceaccess',
  SWITCHCONTROL: 'switchcontrol',
  EYETRACKING: 'eyetracking',
  HEADMOUSE: 'headmouse',
  SIPANDPUFF: 'sipandpuff',
  ONSCREENKEYBOARD: 'onscreenkeyboard',
  TRACKBALL: 'trackball',
  ADAPTIVEKEYBOARD: 'adaptivekeyboard',
  LIVECAPTION: 'livecaption',
  CLOSEDCAPTIONS: 'closedcaptions',
  NATURALREADER: 'naturalreader',
  READANDWRITE: 'readandwrite',
  KURZWEIL: 'kurzweil',
  BRAILLEDISPLAY: 'brailledisplay',
  HEARINGAID: 'hearingaid',
  NONE: 'none',
  OTHER: 'other',
} as const;

export type AssistiveTechnology =
  (typeof AssistiveTechnology)[keyof typeof AssistiveTechnology];

export const ComputerOs = {
  MAC_OS: 'macOS',
  WINDOWS: 'windows',
  LINUX: 'linux',
  OTHER: 'other',
  NO_COMPUTER: 'noComputer',
} as const;

export type ComputerOs = (typeof ComputerOs)[keyof typeof ComputerOs];

export const TabletOs = {
  AMAZON_FIRE: 'amazonFire',
  ANDROID: 'android',
  IPAD_OS: 'ipadOS',
  WINDOWS: 'windows',
  OTHER: 'other',
  NO_TABLET: 'noTablet',
} as const;

export type TabletOs = (typeof TabletOs)[keyof typeof TabletOs];

export const SmartphoneOs = {
  ANDROID: 'android',
  IOS: 'ios',
  WINDOWS: 'windows',
  OTHER: 'other',
  NO_SMARTPHONE: 'noSmartphone',
} as const;

export type SmartphoneOs = (typeof SmartphoneOs)[keyof typeof SmartphoneOs];

export const Browser = {
  CHROME: 'chrome',
  EDGE: 'edge',
  FIREFOX: 'firefox',
  INTERNET_EXPLORER: 'internetExplorer',
  SAFARI: 'safari',
  OTHER: 'other',
} as const;

export type Browser = (typeof Browser)[keyof typeof Browser];

export const ProgrammingLanguage = {
  JAVA_SCRIPT: 'javaScript',
  PYTHON: 'python',
  JAVA: 'java',
  'C#': 'c#',
  C: 'c++',
  RUBY: 'ruby',
  PHP: 'php',
  SWIFT: 'swift',
  KOTLIN: 'kotlin',
  GOLANG: 'golang',
  R: 'r',
  TYPE_SCRIPT: 'typeScript',
  SQL: 'sql',
  OTHER: 'other',
} as const;

export type ProgrammingLanguage =
  (typeof ProgrammingLanguage)[keyof typeof ProgrammingLanguage];

export const Nationality = {
  AMERICAN: 'american',
  CANADIAN: 'canadian',
  MEXICAN: 'mexican',
  BRITISH: 'british',
  FRENCH: 'french',
  GERMAN: 'german',
  ITALIAN: 'italian',
  SPANISH: 'spanish',
  DUTCH: 'dutch',
  SWEDISH: 'swedish',
  NORWEGIAN: 'norwegian',
  DANISH: 'danish',
  FINNISH: 'finnish',
  POLISH: 'polish',
  IRISH: 'irish',
  PORTUGUESE: 'portuguese',
  GREEK: 'greek',
  SWISS: 'swiss',
  AUSTRIAN: 'austrian',
  BELGIAN: 'belgian',
  CZECH: 'czech',
  ROMANIAN: 'romanian',
  HUNGARIAN: 'hungarian',
  CHINESE: 'chinese',
  INDIAN: 'indian',
  JAPANESE: 'japanese',
  KOREAN: 'korean',
  FILIPINO: 'filipino',
  VIETNAMESE: 'vietnamese',
  THAI: 'thai',
  MALAYSIAN: 'malaysian',
  SINGAPOREAN: 'singaporean',
  INDONESIAN: 'indonesian',
  PAKISTANI: 'pakistani',
  BANGLADESHI: 'bangladeshi',
  AUSTRALIAN: 'australian',
  NEWZEALANDER: 'newzealander',
  BRAZILIAN: 'brazilian',
  ARGENTINIAN: 'argentinian',
  COLOMBIAN: 'colombian',
  CHILEAN: 'chilean',
  PERUVIAN: 'peruvian',
  VENEZUELAN: 'venezuelan',
  SOUTHAFRICAN: 'southafrican',
  NIGERIAN: 'nigerian',
  EGYPTIAN: 'egyptian',
  KENYAN: 'kenyan',
  ISRAELI: 'israeli',
  TURKISH: 'turkish',
  SAUDIARABIAN: 'saudiarabian',
  OTHER: 'other',
} as const;

export type Nationality = (typeof Nationality)[keyof typeof Nationality];

export const DisabilityType = {
  PHYSICAL: 'physical',
  SENSORY: 'sensory',
  COGNITIVEORLEARNING: 'cognitiveorlearning',
  MENTALHEALTH: 'mentalhealth',
  CHRONICILLNESS: 'chronicillness',
  OTHER: 'other',
  NO: 'no',
  PREFERNOTTOSAY: 'prefernottosay',
} as const;

export type DisabilityType =
  (typeof DisabilityType)[keyof typeof DisabilityType];

export const Pet = {
  NO_PETS: 'noPets',
  DOG: 'dog',
  CAT: 'cat',
  BIRD: 'bird',
  FISH: 'fish',
  REPTILE: 'reptile',
  AMPHIBIAN: 'amphibian',
  OTHER: 'other',
} as const;

export type Pet = (typeof Pet)[keyof typeof Pet];

export const ParticipantLanguage = {
  EN: 'en',
  ES: 'es',
  ZH: 'zh',
  HI: 'hi',
  AR: 'ar',
  BN: 'bn',
  RU: 'ru',
  PT: 'pt',
  JA: 'ja',
  DE: 'de',
  FR: 'fr',
  IT: 'it',
  KO: 'ko',
  TR: 'tr',
  PL: 'pl',
  NL: 'nl',
  VI: 'vi',
  SV: 'sv',
  FA: 'fa',
  EL: 'el',
  DA: 'da',
  FI: 'fi',
  NO: 'no',
  CS: 'cs',
  TH: 'th',
  RO: 'ro',
  HE: 'he',
  ID: 'id',
  HU: 'hu',
  UK: 'uk',
  OTHER: 'other',
} as const;

export type ParticipantLanguage =
  (typeof ParticipantLanguage)[keyof typeof ParticipantLanguage];

export type UpdateAdvancedTargeting = {
  languages?: Array<ParticipantLanguage>;
  childrenBirthYears?: Array<string>;
  pets?: Array<Pet>;
  disabilityType?: Array<DisabilityType>;
  nationality?: Array<Nationality>;
  programmingLanguages?: Array<ProgrammingLanguage>;
  browsers?: Array<Browser>;
  smartphoneOS?: Array<SmartphoneOs>;
  tabletOS?: Array<TabletOs>;
  computerOS?: Array<ComputerOs>;
  assistiveTechnologies?: Array<AssistiveTechnology>;
  cloudPlatforms?: Array<CloudPlatform>;
  codingAITools?: Array<CodingAiTool>;
  designPrototypingTools?: Array<DesignPrototypingTool>;
  foodDeliveryServices?: Array<FoodDeliveryService>;
  healthWellnessApps?: Array<HealthWellnessApp>;
  onlineBankingTools?: Array<OnlineBankingTool>;
  onlineLearningPlatforms?: Array<OnlineLearningPlatform>;
  onlineMarketplacePlatforms?: Array<OnlineMarketplacePlatform>;
  onlineTravelBooking?: Array<OnlineTravelBooking>;
  productivitySoftware?: Array<ProductivitySoftware>;
  smartHomeDevices?: Array<SmartHomeDevice>;
  socialNetworkPlatforms?: Array<SocialNetworkPlatform>;
  streamingServices?: Array<StreamingService>;
  vehicleBrands?: Array<VehicleBrand>;
  employmentStatuses?: Array<EmploymentStatus>;
  relationshipStatus?:
    | 'single'
    | 'inRelationship'
    | 'married'
    | 'divorced'
    | 'widowed'
    | 'separated'
    | 'domesticPartnershipCivilUnion'
    | 'itsComplicated'
    | 'preferNotToSay'
    | 'other'
    | null;
  livingSituation?:
    | 'alone'
    | 'roommates'
    | 'significantOther'
    | 'spouse'
    | 'parents'
    | 'other'
    | null;
  areaType?: 'urban' | 'suburban' | 'rural' | null;
  militaryServiceStatus?: 'neverserved' | 'currentlyserving' | 'veteran' | null;
  workSetting?: 'hybrid' | 'inPerson' | 'remote' | null;
  onlineShoppingFrequency?:
    | 'severaltimesweek'
    | 'aboutonceweek'
    | 'fewtimesmonth'
    | 'rarely'
    | 'never'
    | null;
  homeOwner?: boolean | null;
  webcam?: boolean | null;
  mobilePhoneWithInternet?: boolean | null;
  headsetWithMicrophone?: boolean | null;
  numberOfChildren?: number | null;
  numberOfChildrenLiveAtHome?: number | null;
};

export const StudyLanguage = {
  EN: 'en',
  AR: 'ar',
  BE: 'be',
  BG: 'bg',
  BN: 'bn',
  BS: 'bs',
  CA: 'ca',
  CS: 'cs',
  DA: 'da',
  DE: 'de',
  EL: 'el',
  ES: 'es',
  ET: 'et',
  FA: 'fa',
  FI: 'fi',
  FR: 'fr',
  GU: 'gu',
  HE: 'he',
  HI: 'hi',
  HR: 'hr',
  HU: 'hu',
  ID: 'id',
  IT: 'it',
  JA: 'ja',
  KN: 'kn',
  KO: 'ko',
  LT: 'lt',
  LV: 'lv',
  MK: 'mk',
  MR: 'mr',
  MS: 'ms',
  NL: 'nl',
  NO: 'no',
  PL: 'pl',
  PT: 'pt',
  RO: 'ro',
  RU: 'ru',
  SK: 'sk',
  SL: 'sl',
  SR: 'sr',
  SV: 'sv',
  TA: 'ta',
  TE: 'te',
  TH: 'th',
  TL: 'tl',
  TR: 'tr',
  UK: 'uk',
  UR: 'ur',
  VI: 'vi',
  ZH: 'zh',
  ZH_HK: 'zh-HK',
  ZH_TW: 'zh-TW',
} as const;

export type StudyLanguage = (typeof StudyLanguage)[keyof typeof StudyLanguage];

export type ProjectParticipationFiltering = {
  since?: string;
  months?: number;
};

export type ProjectFiltering = {
  participation?: ProjectParticipationFiltering;
};

export const NdaType = {
  NONE: 'none',
  FILE: 'file',
  LINK: 'link',
} as const;

export type NdaType = (typeof NdaType)[keyof typeof NdaType];

export type ProjectLocationInPerson = {
  id?: string;
  address1: string;
  zipcode?: string;
  city: string;
  region?: string;
  state?: string;
  country: ProjectCountryCode;
  formattedAddress: string;
  latitude: number;
  longitude: number;
};

export type ProjectCities = {
  location: Location;
};

export const AgeGroup = {
  '18_20': '18-20',
  '21_29': '21-29',
  '30_39': '30-39',
  '40_54': '40-54',
  55: '55+',
  N_A: 'N/A',
} as const;

export type AgeGroup = (typeof AgeGroup)[keyof typeof AgeGroup];

export const ResearchConductType = { 1: 1, 2: 2 } as const;

export type ResearchConductType =
  (typeof ResearchConductType)[keyof typeof ResearchConductType];

export type CreateResearcherProjectDto = {
  name: string;
  internalName?: string;
  description?: string;
  screenerQuestions?: Array<ScreenerQuestion>;
  teamId: string;
};

export type ScreenerQuestionAnswer = {
  id: string;
  createdAt: string;
  uid: string;
  /**
   * Required  unless `isOther`, then text will be set to 'Other'
   */
  text?: string;
  /**
   * CHECKBOX: {
   * id: 3,
   * answerValues: [
   * { id: 1, text: 'May Select' },
   * { id: 2, text: 'Must Select' },
   * { id: 3, text: 'Disqualify' },
   * ],
   * },
   * RADIO: {
   * id: 1,
   * answerValues: [
   * { id: 1, text: 'Qualify' },
   * { id: 2, text: 'Disqualify' },
   * ],
   * },
   */
  answerValue: number;
  /**
   * Skip logic for radio questions. Uid of question to skip to.
   */
  goToQuestionUid?: string;
  /**
   * Is 'Other' answer with comment field
   */
  isOther?: boolean;
  updatedAt?: string;
};

export type ScreenerQuestion = {
  id: string;
  createdAt: string;
  questionType?: QuestionType;
  uid?: string;
  text: string;
  answers?: Array<ScreenerQuestionAnswer>;
  isRequired?: boolean;
  skipLogic?: boolean;
  includeOtherOption?: boolean;
  isEnd?: boolean;
  logic?: ScreenerQuestionSkipLogic;
  minValue?: number;
  maxValue?: number;
  step?: number;
  showStep?: boolean;
  randomizeOrder?: boolean;
  matrixRows?: Array<ScreenerQuestionMatrixRow>;
  matrixMatchType?: MatrixMatchType;
  matchType?: CheckboxMatchType;
  matrixSelectionMode?: MatrixSelectionMode;
  /**
   * Warnings found during validation on create, update & publish.
   */
  warnings?: Array<string>;
  updatedAt?: string;
};

export type RecruitmentStatsDto = {
  totals: RecruitmentStatsTotalsDto;
  questions: Array<RecruitmentQuestionStatsDto>;
  /**
   * Every platform that ever sent people; empty unless the project is still recruiting.
   */
  externalRecruitment: Array<ExternalRecruitmentCampaignDto>;
};

export const ExternalRecruitmentPlatform = {
  META: 'meta',
  LINKEDIN: 'linkedin',
  REDDIT: 'reddit',
} as const;

export type ExternalRecruitmentPlatform =
  (typeof ExternalRecruitmentPlatform)[keyof typeof ExternalRecruitmentPlatform];

export type ExternalRecruitmentCampaignDto = {
  platform: ExternalRecruitmentPlatform;
  /**
   * When the newest ad-attributed response from this platform was created.
   */
  lastResponseAt: string;
};

export const RecruitmentOptionRule = {
  QUALIFY: 'qualify',
  MAY_SELECT: 'may_select',
  MUST_SELECT: 'must_select',
  DISQUALIFY: 'disqualify',
} as const;

export type RecruitmentOptionRule =
  (typeof RecruitmentOptionRule)[keyof typeof RecruitmentOptionRule];

export type RecruitmentQuestionOptionStatsDto = {
  uid: string;
  text: string;
  rule: RecruitmentOptionRule;
  /**
   * Non-skipped answers to the question that selected this option.
   */
  count: number;
};

export type RecruitmentQuestionStatsDto = {
  uid: string;
  text: string;
  /**
   * Zero-based position in the screener's display order (end questions excluded).
   */
  order: number;
  questionType?: QuestionType;
  /**
   * Responses in which the participant was shown this question, whether they
   * answered or chose to skip it. Questions skip-logic routed past are not
   * counted.
   */
  reached: number;
  /**
   * `reached` minus `skipped`.
   */
  answered: number;
  /**
   * Responses whose participant was shown the question and skipped it.
   */
  skipped: number;
  /**
   * Draft-equivalent responses whose last shown question is this one.
   */
  abandoned: number;
  /**
   * Non-skipped answers whose per-question verdict was qualify.
   */
  qualifiedCount: number;
  /**
   * Non-skipped answers whose per-question verdict was disqualify.
   */
  disqualifiedCount: number;
  /**
   * Of `disqualifiedCount`, the answers that chose no Disqualify option, so
   * failed by leaving out a Must Select one. Zero unless the question is a
   * checkbox with Must Select options. Judged by the rules stored when each
   * response was scored, while `options[].rule` is the current screener, so
   * the two can disagree after the screener is edited.
   */
  missedMustSelectCount: number;
  mustSelectMatchType?: CheckboxMatchType;
  /**
   * Per-option split; only populated for radio and checkbox questions.
   */
  options: Array<RecruitmentQuestionOptionStatsDto>;
};

export type RecruitmentStatsTotalsDto = {
  /**
   * Every response for the project, drafts included.
   */
  started: number;
  /**
   * Responses past the draft-equivalent statuses (DRAFT, EXTERNAL_SCREENER_IN_PROGRESS).
   */
  completed: number;
  /**
   * Of those, the ones with a qualifying verdict (`qualified: true`).
   */
  qualified: number;
  /**
   * Of the completed, the ones admitted without the screener questions: a
   * waived invitation, or the researcher's own participants through a link
   * that skips the screener. Zero when nobody did.
   */
  screenerSkipped?: number;
};

export type ProjectIncentiveRecommendationDto = {
  /**
   * False when no recommendation could be computed for this study.
   */
  enabled: boolean;
  point?: number | null;
  rangeLow?: number | null;
  rangeHigh?: number | null;
};

export type ProjectPricingMultiplierPreviewDto = {
  /**
   * False when the org does not have the pricing multiplier feature.
   */
  enabled: boolean;
  multiplier: number;
  tier?: string | null;
  strategy?: string | null;
  /**
   * The pre-snap average across job families (the multiplier is this average
   * snapped to the nearest tier). Null when the study did not price on titles.
   */
  studyAverage?: number | null;
  /**
   * How many job families entered the average. Null off the titles path.
   */
  familiesCount?: number | null;
  titles: Array<ProjectPricingMultiplierPreviewTitleDto>;
  /**
   * Strategy C route derivation strings for no-title studies. Display
   * fallback only, for projects stamped before routeDetails existed — render
   * routeDetails when it is non-empty.
   */
  routes: Array<string>;
  /**
   * Strategy C routes in display-ready parts for no-title studies.
   */
  routeDetails: Array<ProjectPricingMultiplierPreviewRouteDto>;
  notes: Array<string>;
  /**
   * "How to lower your multiplier" — counterfactuals computed by re-running
   * the engine without one criterion (the dearest title, the declared
   * seniority). Only populated for drafts, where the price can still move.
   */
  hints: Array<string>;
};

export type ProjectPricingMultiplierPreviewRouteDto = {
  /**
   * 'seniority' | 'function' | 'functionManagement'
   */
  kind: string;
  /**
   * The raw targeting enum value (a seniority or job function code).
   */
  key: string;
  occupationName: string;
  multiplier: number;
  tier: string;
};

export type ProjectPricingMultiplierPreviewTitleDto = {
  title: string;
  multiplier: number;
  tier: string;
  occupationName?: string | null;
};

export const SortDirection = { ASC: 'asc', DESC: 'desc' } as const;

export type SortDirection = (typeof SortDirection)[keyof typeof SortDirection];

export const ProjectSortField = { CREATED_AT: 'createdAt' } as const;

export type ProjectSortField =
  (typeof ProjectSortField)[keyof typeof ProjectSortField];

export type ChatRequestDto = {
  messages: Array<ChatMessageDto>;
  /**
   * The study type (kindOfResearch) if already selected
   */
  kindOfResearch?: number;
  /**
   * The selected study language code. Used to generate participant-facing AI interview content.
   */
  studyLanguage?:
    | 'en'
    | 'ar'
    | 'be'
    | 'bg'
    | 'bn'
    | 'bs'
    | 'ca'
    | 'cs'
    | 'da'
    | 'de'
    | 'el'
    | 'es'
    | 'et'
    | 'fa'
    | 'fi'
    | 'fr'
    | 'gu'
    | 'he'
    | 'hi'
    | 'hr'
    | 'hu'
    | 'id'
    | 'it'
    | 'ja'
    | 'kn'
    | 'ko'
    | 'lt'
    | 'lv'
    | 'mk'
    | 'mr'
    | 'ms'
    | 'nl'
    | 'no'
    | 'pl'
    | 'pt'
    | 'ro'
    | 'ru'
    | 'sk'
    | 'sl'
    | 'sr'
    | 'sv'
    | 'ta'
    | 'te'
    | 'th'
    | 'tl'
    | 'tr'
    | 'uk'
    | 'ur'
    | 'vi'
    | 'zh'
    | 'zh-HK'
    | 'zh-TW';
  /**
   * The ID of the project. Omitted for new projects
   */
  projectId?: string;
  /**
   * When true, the latest user message is an uploaded brief and the model should generate the entire project in one turn (no clarifying questions). Set only on the first message of a brief-upload session.
   */
  briefUpload?: boolean;
  /**
   * When true, the researcher clicked "Fill in the rest for me" and the model should fill only empty fields in the remaining checklist items without overwriting existing values.
   */
  fillRest?: boolean;
  /**
   * Plain-text summary of the current draft (title, targeting, screener questions, interview script). Sent on every turn so the model edits what is on the canvas rather than what it remembers generating.
   */
  projectState?: string;
  /**
   * Checklist steps the draft has not completed yet, in step order. Drives which generation instructions the system prompt carries this turn.
   */
  remainingSteps?: Array<string>;
  /**
   * When true, the canvas shows the Participant sources section (the organization is on the external-participant-invites flag and the study kind admits the invite link). The model is then offered updateParticipantSources and asks about the source once when the brief does not settle it. Only changes what the model may propose; the draft save enforces the flag.
   */
  participantSources?: boolean;
};

export type ChatMessageDto = {
  role: 'user' | 'assistant';
  content: string;
};

export type InterviewCaptionsResponseDto = {
  /**
   * The captions' language — a language code, or 'original' for the recording's own language.
   */
  language: string;
  source: CaptionSource;
  cues: Array<CaptionCueDto>;
};

export type CaptionCueDto = {
  startMs: number;
  endMs: number;
  text: string;
};

export const CaptionSource = {
  WORDS: 'words',
  SIDECAR: 'sidecar',
  FALLBACK: 'fallback',
  NONE: 'none',
} as const;

export type CaptionSource = (typeof CaptionSource)[keyof typeof CaptionSource];

export type TranscriptionTokenResponseDto = {
  token: string;
  expiresAt: string;
};

export type UploadTranscriptWordsDto = {
  /**
   * The media `bucketKey` returned by `initiate`. The sidecar is written to
   * `<bucketKey>.words.json`, so the extraction pipeline can derive it from the
   * answer's stored `mediaBucketKey` without a separate pointer.
   */
  bucketKey: string;
  words: Array<TranscriptWordDto>;
};

export type TranscriptWordDto = {
  w: string;
  s: number;
  e: number;
};

export type AbortUploadDto = {
  uploadId: string;
  bucketKey: string;
};

export type CompleteUploadResponseDto = {
  bucketKey: string;
};

export type CompleteUploadDto = {
  uploadId: string;
  bucketKey: string;
  parts: Array<CompletedPartDto>;
};

export type CompletedPartDto = {
  partNumber: number;
  etag: string;
};

export type UploadPartDto = {
  uploadId: string;
  bucketKey: string;
  partNumber: number;
  /**
   * Base64-encoded binary data for this part
   */
  data: string;
};

export type InitiateUploadResponseDto = {
  uploadId: string;
  bucketKey: string;
};

export type InitiateUploadDto = {
  interviewResponseId: string;
  questionUid: string;
  mimeType: 'video/webm' | 'video/mp4' | 'audio/webm' | 'audio/mp4';
};

export type NotifyResearcherDto = {
  message?: string;
};

export type CancelBookingDto = {
  reason: ParticipantCancellationReason;
  message?: string;
};

export const ParticipantCancellationReason = {
  TIME: 'time',
  FINAL: 'final',
} as const;

export type ParticipantCancellationReason =
  (typeof ParticipantCancellationReason)[keyof typeof ParticipantCancellationReason];

export type RescheduleSessionDto = {
  start: string;
  timezone: string;
  /**
   * A note to the researcher; the reason, when rescheduling.
   */
  message?: string;
  /**
   * The phone the legacy page asked for at booking. Falls back to the
   * profile's phone; stored on the booking only. Empty is allowed, and
   * falls back too, as the legacy endpoint accepted it.
   */
  phone?: string;
  originalBookingUid: string;
};

export type BookSessionResponseDto = {
  uid: string;
  /**
   * ISO datetimes with the offset of `timezone`.
   */
  start: string;
  end: string;
  timezone: string;
  /**
   * The researcher the participant is "confirmed with".
   */
  hostFirstName?: string;
  /**
   * Effective joining instructions or address after the booking commits.
   */
  location?: string;
};

export type BookSessionDto = {
  start: string;
  timezone: string;
  /**
   * A note to the researcher; the reason, when rescheduling.
   */
  message?: string;
  /**
   * The phone the legacy page asked for at booking. Falls back to the
   * profile's phone; stored on the booking only. Empty is allowed, and
   * falls back too, as the legacy endpoint accepted it.
   */
  phone?: string;
};

export type FindBookingTimesResponseDto = {
  today: string;
  timezone: string;
  days: Array<BookingTimeDayDto>;
  weeks: Array<BookingTimeWeekDto>;
  /**
   * Set to 0 when the calendar has reached its total booking limit; the
   * page then shows "the maximum number of appointments has been reached".
   */
  available?: number;
};

export type BookingTimeWeekDto = {
  label: string;
  /**
   * How many of `days` belong to this week, in order.
   */
  length: number;
};

export type BookingTimeSlotDto = {
  /**
   * ISO datetime of the slot start, offset in the requested timezone.
   */
  start: string;
  meridiem: 'am' | 'pm';
  /**
   * Places left in the slot (group sessions).
   */
  available: number;
};

export type BookingTimeDayDto = {
  /**
   * `YYYY-MM-DD` in the requested timezone.
   */
  date: string;
  status: 'available' | 'unavailable';
  spots: Array<BookingTimeSlotDto>;
};

export type FindBookingTimesDto = {
  /**
   * First day of the window, `YYYY-MM-DD` in `timezone`.
   */
  date: string;
  timezone: string;
  /**
   * Days in the window; the legacy page always asked for 7.
   */
  length?: number;
};

export type ParticipantBookingDto = {
  screenerResponseId: string;
  status: ScreenerResponseStatus;
  blockedReason?: ParticipantBookingBlockedReason;
  event?: ParticipantBookingEventDto;
  booking?: ParticipantBookingSlotDto;
  project: ParticipantBookingProjectDto;
  /**
   * Whether a SCHEDULED booking may still be moved: more than
   * `rescheduleLeadHours` before the start, or the researcher overrode the
   * limit for this participant.
   */
  rescheduleAllowed: boolean;
  /**
   * 4 for remote sessions, 24 for in-person.
   */
  rescheduleLeadHours: number;
  /**
   * Cancelling closer than this to the start shows the late-cancellation
   * warning: 12 for remote sessions, 24 for in-person.
   */
  lateCancelHours: number;
  /**
   * The invitation's special instructions, shown on the confirm step.
   */
  invitationMessage?: string | null;
};

export type ParticipantBookingProjectDto = {
  name: string;
  researchConductType?: ResearchConductType;
  /**
   * Meeting link for remote sessions, as the invitation or project has it.
   */
  remoteCommunicationDetails?: string | null;
  /**
   * One-line address for in-person sessions.
   */
  inPersonAddress?: string | null;
};

export type ParticipantBookingSlotDto = {
  uid: string;
  start: string;
  end: string;
  /**
   * The timezone the participant booked in.
   */
  timezone: string;
};

export type ParticipantBookingEventDto = {
  uid: string;
  timezone: string;
  /**
   * Session length in minutes.
   */
  duration: number;
  description?: string | null;
  fromDate?: string;
  endDate?: string;
  rollingDays?: number | null;
  /**
   * More than one means a group session.
   */
  maxBookingsPerSlot: number;
  /**
   * The researcher's first name, shown as "You are confirmed with …".
   */
  hostFirstName?: string;
  /**
   * The researcher's user id, the Zoom host when the invite uses Zoom.
   */
  hostUserId?: string;
};

export const ParticipantBookingBlockedReason = {
  NOT_INVITED: 'NOT_INVITED',
  NO_CALENDAR: 'NO_CALENDAR',
  CALENDAR_INACTIVE: 'CALENDAR_INACTIVE',
} as const;

export type ParticipantBookingBlockedReason =
  (typeof ParticipantBookingBlockedReason)[keyof typeof ParticipantBookingBlockedReason];

export const PastScreenerResponseStatus = {
  PAID: 'PAID',
  CANCELLED: 'CANCELLED',
  NOSHOW: 'NOSHOW',
  REJECTEDQUALITY: 'REJECTEDQUALITY',
  INVALIDRESPONSE: 'INVALIDRESPONSE',
  CLOSED: 'CLOSED',
  NOT_A_FIT: 'NOT_A_FIT',
} as const;

export type PastScreenerResponseStatus =
  (typeof PastScreenerResponseStatus)[keyof typeof PastScreenerResponseStatus];

export type MessagingProjectDto = {
  /**
   * Id of the project.
   */
  projectId: string;
  /**
   * Project name.
   */
  name: string;
  /**
   * Whether the project has been closed.
   */
  closed: boolean;
  /**
   * User id of the researcher who owns the project — the counterpart participant for a conversation created from this project.
   */
  researcherUserId: string;
  /**
   * Id of the participant's most recent qualifying survey response on the project.
   */
  surveyResponseId: string;
  /**
   * Uid of the existing conversation linked to the survey response, when one has already been started.
   */
  conversationUid?: string;
  /**
   * First name of the external researcher running the project, when the project is run through the public API. Present means the project is externally run.
   */
  externalResearcherName?: string;
};

export type ActiveSurveyResponsesPaginatedDto = {
  /**
   * The total number of results possible, which is only provided in the response when `includeCount` is passed as `true` within a request.
   */
  totalResults?: number;
  page: number;
  pageSize: number;
  results?: Array<ActiveSurveyResultDto>;
};

export const RespondentPayoutStatus = {
  PENDING: 'PENDING',
  PROCESSING: 'PROCESSING',
  PAID: 'PAID',
  FAILED: 'FAILED',
  CANCELLED: 'CANCELLED',
  TEMP_CANCELLED: 'TEMP_CANCELLED',
  VOIDED: 'VOIDED',
  HOLD: 'HOLD',
} as const;

export type RespondentPayoutStatus =
  (typeof RespondentPayoutStatus)[keyof typeof RespondentPayoutStatus];

export type PayoutResultDto = {
  payoutStatus?: RespondentPayoutStatus;
  rewardId?: string;
};

export const ScreenerResponseOutcome = {
  QUALIFIED: 'qualified',
  NOT_QUALIFIED: 'notQualified',
} as const;

export type ScreenerResponseOutcome =
  (typeof ScreenerResponseOutcome)[keyof typeof ScreenerResponseOutcome];

export type ActiveSurveyResultDto = {
  projectId: string;
  timeMinutesRequired: number;
  publishedAt?: string;
  researchConductType: ResearchConductType;
  kindOfResearch: KindOfResearch;
  respondentRemuneration: number;
  name: string;
  description: string;
  status: ScreenerResponseStatus;
  outcome?: ScreenerResponseOutcome;
  payout?: PayoutResultDto;
  scheduledAt?: string;
  appliedAt?: string;
  bookingUrl: string;
  cancelBookingUrl: string;
  rescheduleUrl: string;
  contactUrl: string;
  ndaUrl?: string;
  closed: boolean;
  isClosedToNewApplications: boolean;
  /**
   * The campaign the project belongs to, when it is a campaign project (see
   * `projects/campaigns.md`). The dashboard banner reads it off a DRAFT row to
   * nudge an invitee who started but did not finish applying.
   */
  campaign?: string;
  /**
   * The researcher's note from the invitation this response was admitted or
   * invited under, when there was one. Lets the campaign banner keep showing
   * supply's wording after a waived-screener admission has turned the pending
   * invitation into an INVITED row.
   */
  invitationMessage?: string;
  /**
   * The study is finished (closed, or recruitment complete). The server
   * refuses to start a task on it, so the card must withhold its start action
   * — `closed` alone misses the expiry path, which marks a project RECRUITED
   * without ever setting `closed`.
   */
  isStudyOver: boolean;
  /**
   * PX-440. The start endpoint would refuse this participant's first start
   * because the project has delivered its target (organization opted in).
   * False for anyone who already started — they may still return — and
   * whenever there is no start to refuse. The card disables its Start button
   * off this rather than letting the click fail.
   */
  isTargetReached: boolean;
  /**
   * Taking part requires sharing a screen (an AI-moderated interview with at
   * least one screen-recorded question). Screen capture does not exist on iOS,
   * so the card withholds its "Go to interview" action on a device that cannot
   * honour it rather than sending the participant to a dead end.
   */
  screenRecordingEnabled?: boolean;
  /**
   * The participant has started the task (unmoderated or AI-moderated).
   */
  projectStarted: boolean;
  /**
   * First start. Absent on responses started before it was recorded.
   */
  projectStartedAt?: string;
  /**
   * The external task is complete. The response stays INVITED until
   * autocomplete or the researcher marks it attended, so this is the only
   * signal that the participant is done and awaiting confirmation.
   */
  isTaskComplete: boolean;
  /**
   * Best effort: can be absent even when `isTaskComplete` is true.
   */
  taskCompletedAt?: string;
};

export type GenerateFollowUpResponseDto = {
  /**
   * Whether a follow-up question was generated
   */
  hasFollowUp: boolean;
  /**
   * The AI-generated follow-up question
   */
  followUpQuestion?: string;
  /**
   * Uid of the follow-up exchange — pass as `questionId` when calling the TTS endpoint to fetch this turn's audio. Present only when a follow-up question is being returned.
   */
  followUpQuestionUid?: string;
  kind?: InterviewTurnKind;
};

export type GenerateFollowUpDto = {
  /**
   * The question UID to generate a follow-up for
   */
  questionUid: string;
  /**
   * The attempt this request was minted under, as reported by `GET /interview`.
   * Optional so a client bundle that predates the field keeps working; when
   * present the server rejects follow-up work belonging to a superseded
   * attempt.
   */
  attemptNumber?: number;
  /**
   * Participant's answer to the previous follow-up question (for multi-round follow-ups)
   */
  followUpAnswer?: string;
  followUpMediaBucketKey?: string;
  followUpScreenBucketKey?: string;
  followUpMediaMimeType?: string;
  followUpMediaDurationMs?: number;
  followUpMediaStatus?: MediaStatus;
  followUpTranscriptStatus?: TranscriptStatus;
};

export type CompleteInterviewResponseDto = {
  status: ScreenerResponseStatus;
  interviewStatus: InterviewCompletionStatus;
  closingMessage?: ClosingMessageForParticipantDto;
  respondentRemunerationFormatted?: string;
};

export type ClosingMessageForParticipantDto = {
  closingMessage?: string;
  redirectOnComplete?: boolean;
  redirectUrl?: string;
};

export type CompleteInterviewRequestDto = {
  /**
   * The attempt this request was minted under, as reported by
   * `GET /interview`. When present the server refuses to complete an
   * interview that has since been restarted, so a tab left open across a
   * re-invite cannot mark the fresh retake as finished.
   */
  attemptNumber?: number;
};

export type SaveInterviewResponseResponseDto = {
  interviewStatus: InterviewCompletionStatus;
  answeredCount: number;
  totalQuestions: number;
};

export type SaveInterviewResponseDto = {
  responses: Array<InterviewResponseAnswerDto>;
  /**
   * The attempt this request was minted under, as reported by `GET /interview`.
   * Optional so a client bundle that predates the field keeps working; when
   * present the server rejects answers belonging to a superseded attempt
   * instead of merging them into the active retake.
   */
  attemptNumber?: number;
};

export type FollowUpExchangeDto = {
  uid?: string;
  aiQuestion: string;
  participantAnswer?: string;
  mediaBucketKey?: string;
  screenBucketKey?: string;
  mediaMimeType?: string;
  mediaDurationMs?: number;
  mediaStatus?: MediaStatus;
  transcriptStatus?: TranscriptStatus;
};

export type InterviewResponseAnswerDto = {
  questionUid: string;
  sectionUid: string;
  questionType: InterviewQuestionType;
  textAnswer?: string;
  selectedOptionUids?: Array<string>;
  rankedOptionUids?: Array<string>;
  acknowledged?: boolean;
  otherText?: string;
  followUps?: Array<FollowUpExchangeDto>;
  mediaBucketKey?: string;
  screenBucketKey?: string;
  mediaMimeType?: string;
  mediaDurationMs?: number;
  mediaStatus?: MediaStatus;
  transcriptStatus?: TranscriptStatus;
  taskOutcome?: TaskOutcome;
  /**
   * 1–5 ease the participant gave after "done" (1 = very hard, 5 = very easy).
   */
  taskRating?: number;
  /**
   * When the participant pressed "start task"; duration is derived from it.
   */
  taskStartedAt?: string;
  /**
   * The researcher's hint was shown (the participant pressed "I'm stuck").
   */
  taskHintShown?: boolean;
};

export type SetResponseLanguageDto = {
  language: StudyLanguage;
  /**
   * The attempt this request was minted under, as reported by `GET /interview`.
   * Optional so a client bundle that predates the field keeps working; when
   * present the server rejects the write if the interview has since been
   * restarted.
   */
  attemptNumber?: number;
};

export type InterviewQuestionMediaUrlsDto = {
  imageUrls?: Array<string>;
  pdfUrl?: string;
  /**
   * Concept turns show the concept's media as well as the question's.
   */
  conceptImageUrls?: Array<string>;
  conceptPdfUrl?: string;
  /**
   * The question's section's stimulus, re-signed alongside the
   * question's own media. The client adopts these only when the stimulus pane
   * has not rendered yet — swapping the `src` of a mounted iframe would throw
   * away exactly the state pinning exists to keep.
   */
  stimulusImageUrls?: Array<string>;
  stimulusPdfUrl?: string;
};

export type GetInterviewResponseDto = {
  welcomeScreen?: WelcomeScreenForParticipantDto;
  sections: Array<InterviewSectionForParticipantDto>;
  closingMessage?: ClosingMessageForParticipantDto;
  existingResponses?: Array<InterviewResponseAnswerDto>;
  interviewStatus: InterviewCompletionStatus;
  deliveryMode?: InterviewDeliveryMode;
  /**
   * POC (usability-testing screen recording): the participant is asked to
   * share their screen; a parallel screen recording is captured per answer.
   */
  screenRecordingEnabled?: boolean;
  /**
   * The client renders screen-recorded questions in the recording panel
   * layout (collapsible question panel, floating recording dock). Only ever
   * true alongside `screenRecordingEnabled`; derived from the project
   * organization's rollout.
   */
  recordingPanelEnabled?: boolean;
  /**
   * Which attempt of this interview the client is looking at. A re-invite
   * after a quality rejection archives the previous attempt and increments
   * this on the same response, so the client must echo it back on every
   * mutation — that is what lets the server reject writes from a tab left
   * open across the restart. Legacy responses with no stored value report 1.
   */
  attemptNumber: number;
  studyLanguage: string;
  /**
   * The language the participant chose to respond in (absent until picked).
   * When set, it wins over `studyLanguage` for STT/TTS and follow-ups.
   */
  responseLanguage?: string;
  /**
   * Languages the participant may choose to respond in. Always contains at
   * least the study language; the picker is only shown when there is more
   * than one entry.
   */
  availableResponseLanguages: Array<string>;
  interviewResponseId?: string;
  respondentRemunerationFormatted?: string;
  resume?: InterviewResumeStateDto;
};

export type InterviewResumeFollowUpDto = {
  questionUid: string;
  exchanges: Array<FollowUpExchangeDto>;
  currentAiQuestion: string;
  currentFollowUpQuestionUid?: string;
  currentTurnKind: InterviewTurnKind;
};

export const InterviewResumePhase = {
  WELCOME: 'welcome',
  QUESTIONS: 'questions',
  CLOSING: 'closing',
} as const;

export type InterviewResumePhase =
  (typeof InterviewResumePhase)[keyof typeof InterviewResumePhase];

export type InterviewResumeStateDto = {
  phase: InterviewResumePhase;
  questionIndex: number;
  followUp?: InterviewResumeFollowUpDto;
};

export type InterviewQuestionMediaForParticipantDto = {
  type: MediaType;
  label?: string;
  /**
   * LINK only — opened in a new tab.
   */
  linkUrl?: string;
  /**
   * LINK only — false when the page must not be framed (it refuses embedding,
   * or the researcher switched the pane off), so the client offers only the
   * open-in-new-tab control. Absent means embed.
   */
  embedEnabled?: boolean;
  /**
   * IMAGE only — freshly-signed, short-lived GET URL for the first image.
   * Never the bucket key.
   *
   * rendering single-image media.
   *
   * @deprecated
   */
  imageUrl?: string;
  /**
   * IMAGE only — freshly-signed, short-lived GET URLs in display order.
   * Never the bucket keys.
   */
  imageUrls?: Array<string>;
  /**
   * PDF only — freshly-signed, short-lived GET URL for the attached PDF.
   * Never the bucket key.
   */
  pdfUrl?: string;
};

export type InterviewFollowUpConfigForParticipantDto = {
  type: FollowUpType;
};

export type InterviewQuestionOptionForParticipantDto = {
  uid: string;
  text: string;
  order: number;
};

export type InterviewQuestionForParticipantDto = {
  uid: string;
  questionType: InterviewQuestionType;
  text: string;
  order: number;
  options?: Array<InterviewQuestionOptionForParticipantDto>;
  multiSelect?: boolean;
  showOtherOption?: boolean;
  randomizeOrder?: boolean;
  continueButtonText?: string;
  followUpConfig?: InterviewFollowUpConfigForParticipantDto;
  preferredInputType?: string;
  /**
   * The participant's screen is recorded while answering this question (and
   * its follow-ups). Sits beside `media` rather than inside it: a question
   * can ask the participant to do something on their own machine, with no
   * attachment for the media serializer to hang the flag off.
   */
  recordScreen?: boolean;
  /**
   * Hide the section's stimulus while this question is asked. The
   * client hides — but must not unmount — the stimulus pane, so an embedded
   * page keeps its state for the next question that shows it. Absent means
   * "show".
   */
  hideStimulus?: boolean;
  /**
   * TASK only — shown and read aloud when the participant presses "I'm stuck".
   */
  hint?: string;
  /**
   * TASK only — ask a 1–5 ease rating after "done" (1 = very hard, 5 = very easy). Absent means true.
   */
  askRating?: boolean;
  /**
   * TASK only — one moderator question about the task afterwards. Absent means true.
   */
  askDebrief?: boolean;
  media?: InterviewQuestionMediaForParticipantDto;
  /**
   * Concept testing. Set on every question produced by expanding a concept
   * section. `uid` above is the DERIVED uid
   * (`${baseQuestionUid}_c_${conceptUid}`) — the answer's identity everywhere.
   * `media` is the question's own attachment; `conceptMedia` is the concept's.
   * Title/description are not served to participants.
   */
  conceptUid?: string;
  conceptMedia?: InterviewQuestionMediaForParticipantDto;
};

export type InterviewSectionForParticipantDto = {
  uid: string;
  name: string;
  order: number;
  /**
   * Already EXPANDED on a concept-testing section: every question, once per
   * concept THIS participant is shown, in the order they are shown, each
   * carrying its concept's media as `conceptMedia`.
   */
  questions: Array<InterviewQuestionForParticipantDto>;
  /**
   * Stimulus — one attachment shown alongside every question in this
   * section. The client renders it in a pane that stays MOUNTED for the whole
   * section (an embedded page keeps its state between questions) and hides it
   * on questions carrying `hideStimulus`. Same participant-safe shape as
   * question media: signed URLs only, never `aiContext` or bucket keys.
   */
  stimulus?: InterviewQuestionMediaForParticipantDto;
};

export type WelcomeScreenForParticipantDto = {
  title?: string;
  message?: string;
};

export type ApplicationSignalsDto = {
  /**
   * Whether the participant has applied to at least one project. Counts every screener response including DRAFT — a B2B applicant held for ID verification stays DRAFT until it resolves
   */
  hasAnyApplication: boolean;
  /**
   * Whether at least one application is on a B2B project
   */
  hasB2bApplication: boolean;
  /**
   * How many distinct projects the participant has applied to. Onboarding offers the connect-LinkedIn step only to first-time applicants, i.e. a count of exactly 1 alongside hasB2bApplication
   */
  applicationCount: number;
  /**
   * Whether any application passed screener qualification, counting qualified-pending-manual-review — a first public application always parks there until a reviewer approves it, and from the participant's side it qualified. Only ever revealed to the participant about their own committed applications, and only as a positive: the client copy for everything else stays one neutral message, never confirming a negative
   */
  hasQualifiedApplication: boolean;
};

export type HideProjectResponseDto = {
  /**
   * Operation success status
   */
  success: boolean;
  /**
   * Message describing the result
   */
  message?: string;
};

export type StartExternalScreenerResponseDto = {
  externalScreenerLink: string;
};

export type StartExternalScreenerDto = {
  /**
   * Invite ID if participant was invited directly.
   */
  inviteId?: string;
  /**
   * Project share-link invite code. The server validates it against the
   * project before storing it as invitation provenance.
   */
  inviteCode?: string;
};

export type StartUnmoderatedProjectResponseDto = {
  surveyUrl: string;
};

export type WorkEmailAvailabilityDto = {
  available: boolean;
  reason?: WorkEmailUnavailableReason;
};

export const WorkEmailUnavailableReason = {
  IN_USE: 'inUse',
  FORBIDDEN_DOMAIN: 'forbiddenDomain',
} as const;

export type WorkEmailUnavailableReason =
  (typeof WorkEmailUnavailableReason)[keyof typeof WorkEmailUnavailableReason];

export type CheckWorkEmailAvailabilityDto = {
  email: string;
};

export type PublicApplicationResponseDto = {
  userId: string;
  email: string;
  status: ScreenerResponseStatus;
  nextStep?: PostSubmissionStep;
};

export const PostSubmissionStep = {
  INVITED: 'invited',
  CHECK_EMAIL: 'checkEmail',
  EXTERNAL_SCREENER: 'externalScreener',
} as const;

export type PostSubmissionStep =
  (typeof PostSubmissionStep)[keyof typeof PostSubmissionStep];

export type PublicApplicationDto = {
  /**
   * Screener question responses.
   * Each response contains the screener question's MongoDB _id (hex string),
   * answer(s), and question metadata.
   */
  responses: Array<
    | SaveResponseAnswerRadio
    | SaveResponseAnswerCheckbox
    | SaveResponseAnswerText
    | SaveResponseAnswerSkipped
    | SaveResponseAnswerFile
    | SaveResponseAnswerRank
    | SaveResponseAnswerMatrix
  >;
  /**
   * Total time spent on the screener in seconds.
   */
  totalTime: number;
  /**
   * NDA agreement flag.
   * Required to be true for final submission if project has NDA.
   */
  agree?: boolean;
  /**
   * Invite ID if participant was invited directly.
   */
  inviteId?: string;
  /**
   * Project share-link invite code. The server validates it against the
   * project before storing it as invitation provenance.
   */
  inviteCode?: string;
  initialUtm?: InitialUtm;
  /**
   * Surface, sort and card position the participant clicked through from.
   * Only honoured on the first save; later values are ignored server-side.
   */
  dashboardPlacement?: DashboardPlacement;
  firstName: string;
  lastName: string;
  email: string;
  termsAccepted: boolean;
  /**
   * The six digits emailed by the send endpoint. Consumed here — proving
   * ownership inside the same call that creates the account is what lets it
   * start life email-verified.
   */
  verificationCode: string;
  /**
   * Buffered profile answers, already merged into one nested update.
   * Written before the screener save, because the save scores the
   * application against the profile as it writes.
   */
  profileUpdate?: UpdateProfileDto;
  /**
   * IANA timezone detected on the client.
   */
  timezone?: string;
  /**
   * Ad click identifiers and landing context, parsed out of the
   * `rio_attribution` cookie by the apps/web BFF exactly as on the two
   * ordinary signup routes. Carried here because this endpoint is a third
   * account-creation door, and one paid ads land on: the accounts it creates
   * would otherwise be the only unattributed ones.
   */
  adAttribution?: AdAttributionInputDto;
  /**
   * The banner decision this browser recorded. Persisted onto the profile the
   * signup creates, exactly as on the participant signup route, and read here
   * to resolve the ad-consent grants both of this request's conversions answer
   * to. Absent reads as denied.
   */
  cookieSettings?: ProfileCookieSettings;
};

export type AdAttributionInputDto = {
  fbclid?: string;
  fbc?: string;
  fbp?: string;
  rdtCid?: string;
  rdtUuid?: string;
  gclid?: string;
  wbraid?: string;
  liFatId?: string;
  landingUrl?: string;
  referrer?: string;
};

export type ProfileCookieSettings = {
  performanceAnalyticsCookie?: boolean;
  /**
   * Functional + personalization storage.
   */
  functionalCookie?: boolean;
  /**
   * Ad storage + ad user data.
   */
  adMeasurementCookie?: boolean;
  /**
   * Ad personalization.
   */
  adPersonalizationCookie?: boolean;
  /**
   * Consent model version the choice was made under. Absent means v1 (the
   * analytics-only model), which triggers a one-time re-prompt.
   */
  consentVersion?: number;
};

export type ProfileNotificationSettings = {
  dailyDigest: boolean;
  targetedResearchInvitation: boolean;
};

export type UpdateProfileSocialDto = {
  linkedin?: SocialLink;
  facebook?: SocialLink;
  twitter?: SocialLink;
  instagram?: SocialLink;
  github?: SocialLink;
};

export const WorkSetting = {
  HYBRID: 'hybrid',
  IN_PERSON: 'inPerson',
  REMOTE: 'remote',
} as const;

export type WorkSetting = (typeof WorkSetting)[keyof typeof WorkSetting];

export const MilitaryServiceStatus = {
  NEVERSERVED: 'neverserved',
  CURRENTLYSERVING: 'currentlyserving',
  VETERAN: 'veteran',
} as const;

export type MilitaryServiceStatus =
  (typeof MilitaryServiceStatus)[keyof typeof MilitaryServiceStatus];

export type UpdateProfileProfessionalDto = {
  militaryServiceStatus?: MilitaryServiceStatus;
  workSetting?: WorkSetting;
};

export const OnlineShoppingFrequency = {
  SEVERALTIMESWEEK: 'severaltimesweek',
  ABOUTONCEWEEK: 'aboutonceweek',
  FEWTIMESMONTH: 'fewtimesmonth',
  RARELY: 'rarely',
  NEVER: 'never',
} as const;

export type OnlineShoppingFrequency =
  (typeof OnlineShoppingFrequency)[keyof typeof OnlineShoppingFrequency];

export type UpdateProfileShoppingDto = {
  onlineShoppingFrequency?: OnlineShoppingFrequency;
};

export type UpdateProfileProductsServicesDto = {
  cloudPlatforms?: Array<CloudPlatform>;
  codingAITools?: Array<CodingAiTool>;
  designPrototypingTools?: Array<DesignPrototypingTool>;
  foodDeliveryServices?: Array<FoodDeliveryService>;
  healthWellnessApps?: Array<HealthWellnessApp>;
  onlineBankingTools?: Array<OnlineBankingTool>;
  onlineLearningPlatforms?: Array<OnlineLearningPlatform>;
  onlineMarketplacePlatforms?: Array<OnlineMarketplacePlatform>;
  onlineTravelBooking?: Array<OnlineTravelBooking>;
  productivitySoftware?: Array<ProductivitySoftware>;
  smartHomeDevices?: Array<SmartHomeDevice>;
  socialNetworkPlatforms?: Array<SocialNetworkPlatform>;
  streamingServices?: Array<StreamingService>;
  vehicleBrands?: Array<VehicleBrand>;
};

export type UpdateProfileTechnicalDto = {
  webcam?: boolean;
  mobilePhoneWithInternet?: boolean;
  headsetWithMicrophone?: boolean;
  programmingLanguages?: Array<ProgrammingLanguage>;
  browsers?: Array<Browser>;
  smartphoneOS?: Array<SmartphoneOs>;
  tabletOS?: Array<TabletOs>;
  computerOS?: Array<ComputerOs>;
  assistiveTechnologies?: Array<AssistiveTechnology>;
};

export const AreaType = {
  URBAN: 'urban',
  SUBURBAN: 'suburban',
  RURAL: 'rural',
} as const;

export type AreaType = (typeof AreaType)[keyof typeof AreaType];

export const LivingSituation = {
  ALONE: 'alone',
  ROOMMATES: 'roommates',
  SIGNIFICANT_OTHER: 'significantOther',
  SPOUSE: 'spouse',
  PARENTS: 'parents',
  OTHER: 'other',
} as const;

export type LivingSituation =
  (typeof LivingSituation)[keyof typeof LivingSituation];

export const RelationshipStatus = {
  SINGLE: 'single',
  IN_RELATIONSHIP: 'inRelationship',
  MARRIED: 'married',
  DIVORCED: 'divorced',
  WIDOWED: 'widowed',
  SEPARATED: 'separated',
  DOMESTIC_PARTNERSHIP_CIVIL_UNION: 'domesticPartnershipCivilUnion',
  ITS_COMPLICATED: 'itsComplicated',
  PREFER_NOT_TO_SAY: 'preferNotToSay',
  OTHER: 'other',
} as const;

export type RelationshipStatus =
  (typeof RelationshipStatus)[keyof typeof RelationshipStatus];

export type UpdateProfilePersonalDto = {
  relationshipStatus?: RelationshipStatus;
  languages?: Array<ParticipantLanguage>;
  numberOfChildren?: number;
  numberOfChildrenLiveAtHome?: number;
  childrenBirthYears?: Array<string>;
  livingSituation?: LivingSituation;
  pets?: Array<Pet>;
  homeOwner?: boolean;
  disabilityType?: Array<DisabilityType>;
  nationality?: Array<Nationality>;
  areaType?: AreaType;
};

export type UpdateProfileEmploymentDto = {
  email?: string | null;
  emailDomain?: string | null;
  company?: string | null;
  companySize?: CompanySize;
  businessOwner?: boolean;
  employmentStatus?: EmploymentStatus;
  jobTitle?: string | null;
  seniorityLevel?: SeniorityLevel;
  industry?: Array<string> | null;
  roleDescription?: string | null;
  jobFunctions?: Array<JobFunction>;
  income?: Income;
  smallBusinessOwner?: boolean;
  linkedinUrl?: string | null;
};

export type UpdateProfileDemographicsDto = {
  gender?: Gender;
  ethnicity?: Ethnicities;
  educationLevel?: EducationLevel;
  dateOfBirth?: string;
  householdIncome?: HouseholdIncome;
};

export type UpdateProfileAboutDto = {
  firstName?: string;
  lastName?: string;
  phone?: string;
  email?: string;
  emailDomain?: string;
  paypal?: string | null;
  picture?: string | null;
  location?: ProfileLocation;
  videoIntroUrl?: string | null;
};

export type UpdateProfileDto = {
  about?: UpdateProfileAboutDto;
  demographics?: UpdateProfileDemographicsDto;
  employment?: UpdateProfileEmploymentDto;
  personal?: UpdateProfilePersonalDto;
  technical?: UpdateProfileTechnicalDto;
  productsServices?: UpdateProfileProductsServicesDto;
  shopping?: UpdateProfileShoppingDto;
  professional?: UpdateProfileProfessionalDto;
  skills?: Array<string>;
  socialLinks?: UpdateProfileSocialDto;
  notificationSettings?: ProfileNotificationSettings;
  cookieSettings?: ProfileCookieSettings;
  topics?: Array<string>;
};

export const DashboardExperimentArm = {
  CONTROL: 'control',
  TREATMENT: 'treatment',
} as const;

export type DashboardExperimentArm =
  (typeof DashboardExperimentArm)[keyof typeof DashboardExperimentArm];

export const RespondentDashboardSort = {
  V4_SCORE: 'v4Score',
  PUBLISHED_AT: 'publishedAt',
  RESPONDENT_REMUNERATION: 'respondentRemuneration',
  TIME_MINUTES_REQUIRED: 'timeMinutesRequired',
} as const;

export type RespondentDashboardSort =
  (typeof RespondentDashboardSort)[keyof typeof RespondentDashboardSort];

export const DashboardPlacementSource = {
  DASHBOARD: 'dashboard',
  POST_SCREENER_OUTCOME: 'postScreenerOutcome',
  DASHBOARD_TOP_PICKS: 'dashboardTopPicks',
} as const;

export type DashboardPlacementSource =
  (typeof DashboardPlacementSource)[keyof typeof DashboardPlacementSource];

export type DashboardPlacement = {
  source: DashboardPlacementSource;
  sort: RespondentDashboardSort;
  index: number;
  experimentArm?: DashboardExperimentArm;
  /**
   * v4Score > 0 for this participant on the card at click time.
   */
  matched?: boolean;
  /**
   * The project had boostProjectFeed set at click time, on any surface.
   */
  boosted?: boolean;
};

export type InitialUtm = {
  source?: string | null;
  medium?: string | null;
  campaign?: string | null;
  term?: string | null;
  content?: string | null;
  advertId?: string | null;
  utmId?: string | null;
};

export type SaveResponseMatrixSelection = {
  rowUid: string;
  rowText: string;
  answerUid: string;
  answerText: string;
};

export type SaveResponseAnswerMatrix = {
  questionId: string;
  questionText: string;
  order?: number;
  questionType: QuestionType;
  qualify?: boolean;
  selections: Array<SaveResponseMatrixSelection>;
};

export type SaveResponseRankItem = {
  uid: string;
  text: string;
};

export type SaveResponseAnswerRank = {
  questionId: string;
  questionText: string;
  order?: number;
  questionType: QuestionType;
  qualify?: boolean;
  ranking: Array<SaveResponseRankItem>;
};

export type SaveResponseFileItem = {
  key: string;
  name: string;
  size: number;
  mime:
    | 'image/jpeg'
    | 'image/png'
    | 'image/gif'
    | 'image/webp'
    | 'image/heic'
    | 'application/pdf'
    | 'application/msword'
    | 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    | 'application/vnd.ms-powerpoint'
    | 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
    | 'application/vnd.ms-excel'
    | 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    | 'text/plain'
    | 'video/mp4'
    | 'video/webm'
    | 'video/quicktime'
    | 'video/x-m4v'
    | 'audio/mpeg'
    | 'audio/mp4'
    | 'audio/x-m4a';
  uploadedAt?: string;
};

export type SaveResponseAnswerFile = {
  questionId: string;
  questionText: string;
  order?: number;
  questionType: QuestionType;
  qualify?: boolean;
  files: Array<SaveResponseFileItem>;
  note?: string;
};

export type SaveResponseAnswerSkipped = {
  questionId: string;
  questionText: string;
  order?: number;
  questionType: QuestionType;
  qualify?: boolean;
  skipped: boolean;
  skippedBy?: 'user' | 'skipLogic';
};

export type SaveResponseAnswerText = {
  questionId: string;
  questionText: string;
  order?: number;
  questionType: QuestionType;
  qualify?: boolean;
  answers: string;
};

export type SaveResponseAnswerCheckbox = {
  questionId: string;
  questionText: string;
  order?: number;
  questionType: QuestionType;
  qualify?: boolean;
  answers: Array<SaveAnswerValueDto>;
};

export type SaveAnswerValueDto = {
  id: string;
  uid: string;
  text: string;
  goToQuestionUid?: string | null;
  isOther?: boolean;
  other?: string;
  checked?: boolean;
};

export type SaveResponseAnswerRadio = {
  questionId: string;
  questionText: string;
  order?: number;
  questionType: QuestionType;
  qualify?: boolean;
  answers: SaveAnswerValueDto;
};

export type MarkPlaidCompleteResponseDto = {
  /**
   * Operation success status
   */
  success: boolean;
};

export type RecalculateScreenerStatusResponseDto = {
  status?: ScreenerResponseStatus;
};

export type B2bCompletionResponseDto = {
  /**
   * Questions for the B2B employment fields the project targets
   */
  questions: Array<B2bCompletionQuestionDto>;
};

export const LookupGroup = {
  AGE_GROUP: 'ageGroup',
  COMPANY_SIZE: 'companySize',
  COUNTRY: 'country',
  EDUCATION_LEVEL: 'educationLevel',
  EMPLOYMENT_STATUS: 'employmentStatus',
  ETHNICITY: 'ethnicity',
  GENDER: 'gender',
  HOUSEHOLD_INCOME: 'householdIncome',
  INDUSTRIES: 'industries',
  JOB_TITLES: 'jobTitles',
  KEY_QUALIFIER: 'keyQualifier',
  SENIORITY_LEVEL: 'seniorityLevel',
  SKILLS: 'skills',
  TOPICS: 'topics',
  JOB_FUNCTION: 'jobFunction',
  INCOME: 'income',
  PROGRAMMING_LANGUAGE: 'programmingLanguage',
  BROWSER: 'browser',
  SMARTPHONE_OS: 'smartphoneOS',
  TABLET_OS: 'tabletOS',
  COMPUTER_OS: 'computerOS',
  RELATIONSHIP_STATUS: 'relationshipStatus',
  LANGUAGE: 'language',
  LIVING_SITUATION: 'livingSituation',
  PET: 'pet',
  SCREENER_RESPONSE_STATUS: 'screenerResponseStatus',
} as const;

export type LookupGroup = (typeof LookupGroup)[keyof typeof LookupGroup];

export type ProfileCompletionQuestionOptionDto = {
  /**
   * The value to submit
   */
  value: string;
  /**
   * The display label
   */
  label: string;
};

export const ProfileCompletionQuestionType = {
  1: 1,
  3: 3,
  4: 4,
  5: 5,
  10: 10,
  11: 11,
} as const;

export type ProfileCompletionQuestionType =
  (typeof ProfileCompletionQuestionType)[keyof typeof ProfileCompletionQuestionType];

export const B2bAttributeKey = {
  EMAIL: 'email',
  JOB_TITLE: 'jobTitle',
  COMPANY: 'company',
  JOB_FUNCTIONS: 'jobFunctions',
  SENIORITY_LEVEL: 'seniorityLevel',
  INDUSTRY: 'industry',
  COMPANY_SIZE: 'companySize',
} as const;

export type B2bAttributeKey =
  (typeof B2bAttributeKey)[keyof typeof B2bAttributeKey];

export type B2bCompletionQuestionDto = {
  attributeKey: B2bAttributeKey;
  /**
   * The question text to display
   */
  questionText: string;
  questionType: ProfileCompletionQuestionType;
  /**
   * Options for enum-based questions. Absent for the `industry` question (a LOOKUP_MULTI_SELECT whose choices are searched from `lookupGroup`) and for `jobTitle` (free text, normalized server-side on write).
   */
  options?: Array<ProfileCompletionQuestionOptionDto>;
  lookupGroup?: LookupGroup;
};

export type ScreenerNavigationResponseDto = {
  /**
   * `true` when every reachable question has a response. `nextQuestionUid` is
   * meaningless when this is `true`.
   */
  isScreenerComplete: boolean;
  /**
   * UID of the question the participant should answer next, computed
   * server-side against the answers supplied. `null` when
   * `isScreenerComplete` is `true`.
   */
  nextQuestionUid: string | null;
  /**
   * `true` when `nextQuestionUid` points to the final reachable question under
   * default forward progression — used to label the primary action button
   * "Submit" vs "Continue". Position-only, deliberately not skip-aware: a
   * skip-aware answer would leak qualification logic through the label.
   */
  isNextQuestionLast: boolean;
};

export type ScreenerNavigationDto = {
  /**
   * Every response the participant has given so far, not just the latest one —
   * the endpoint holds no state between calls, so the client sends the whole
   * set each time and the server re-derives the path from scratch.
   */
  responses: Array<
    | SaveResponseAnswerRadio
    | SaveResponseAnswerCheckbox
    | SaveResponseAnswerText
    | SaveResponseAnswerSkipped
    | SaveResponseAnswerFile
    | SaveResponseAnswerRank
    | SaveResponseAnswerMatrix
  >;
};

export type ScreenerQuestionsResponseDto = {
  questions?: Array<ScreenerQuestionForParticipantDto>;
  nda?: NdaInfoDto;
  /**
   * `true` when every reachable question has a response — mirrors
   * `SaveScreenerResponseResponseDto.isScreenerComplete`. This is the
   * canonical "stop showing questions" signal; `nextQuestionUid` is
   * meaningless when this is `true`.
   */
  isScreenerComplete: boolean;
  /**
   * UID of the question the participant should answer next, computed
   * server-side against any existing draft responses. `null` when
   * `isScreenerComplete` is `true`.
   */
  nextQuestionUid: string | null;
  /**
   * `true` when `nextQuestionUid` points to the final reachable question
   * under default forward progression — used by the client to label the
   * primary action button as "Submit" vs "Continue" on first render.
   * Updated on each save via `SaveScreenerResponseResponseDto`.
   */
  isNextQuestionLast: boolean;
};

export type NdaInfoDto = {
  requiresNda?: boolean;
  ndaType?: NdaType;
  ndaUrl?: string | null;
};

export type ScreenerMatrixRowForParticipantDto = {
  uid: string;
  text: string;
};

export type ScreenerAnswerForParticipantDto = {
  id: string;
  uid: string;
  text?: string;
  isOther?: boolean;
};

export type ScreenerQuestionForParticipantDto = {
  id: string;
  uid: string;
  questionType: QuestionType;
  text?: string;
  answers?: Array<ScreenerAnswerForParticipantDto>;
  isRequired?: boolean;
  includeOtherOption?: boolean;
  minValue?: number;
  maxValue?: number;
  step?: number;
  showStep?: boolean;
  randomizeOrder?: boolean;
  matrixRows?: Array<ScreenerMatrixRowForParticipantDto>;
  matrixSelectionMode?: MatrixSelectionMode;
};

export type ProjectDetailsResponseDto = {
  project: PublicProjectDto;
  projectStatus: ProjectStatus;
  isProjectClosed: boolean;
  /**
   * `true` when the project is closed or no longer recruiting. Invite
   * code/id holders are exempt, matching `viewProject`.
   */
  isClosedToNewApplications: boolean;
  /**
   * Number of screener questions on the project. Unlike `viewProject` this
   * does not add the participant's missing profile attributes — there is no
   * profile to compare against. Clients building a public progress indicator
   * should add the count from the project profile-questions endpoint.
   */
  screenerQuestionsLength: number;
};

export const ProjectStatus = {
  PENDING: 'PENDING',
  DRAFT: 'DRAFT',
  PAID: 'PAID',
  ARCHIVED: 'ARCHIVED',
  DELETED: 'DELETED',
  APPROVED: 'APPROVED',
  PARTIALLYPAID: 'PARTIALLYPAID',
} as const;

export type ProjectStatus = (typeof ProjectStatus)[keyof typeof ProjectStatus];

export type LocationDto = {
  country?: string;
  address1?: string;
  formattedAddress?: string;
  state?: string;
  city?: string;
  zipcode?: string;
  latitude?: number;
  longitude?: number;
};

export type PublicProjectDto = {
  _id?: string;
  name?: string;
  description?: string;
  slug?: string;
  respondentRemuneration?: number;
  respondentRemunerationFormatted?: string;
  isRemote?: boolean;
  countryFormatted?: string;
  studyLengthMinutes?: number;
  hasLocation?: boolean;
  /**
   * Whether this project accepts logged-out applications via the public
   * apply page. Set manually in the database, absent means off. Safe to
   * expose: it says a page exists, not anything about the project's
   * screener or targeting.
   */
  publicApplicationsEnabled?: boolean;
  location?: LocationDto;
  kindOfResearchFormatted?: string;
  radius?: number;
  companyName?: string;
  kindOfResearch?: number;
  researchConductType?: string;
  talkToAudience?: string;
  country?: string;
  timeHoursRequired?: number;
  timeMinutesRequired?: number;
  incentive?: number;
  oversubscribed?: boolean;
  closed?: boolean;
  recruitingStatus?: string;
  organizationId?: string;
  referralBonus?: number;
  hideCompanyName?: boolean;
  specialInstructions?: string;
  exposeParticipantEmails?: boolean;
  /**
   * Taking part requires sharing a screen (an AI-moderated interview with at
   * least one screen-recorded question). Exposed so the participant can be
   * told the study is desktop-only *before* starting it — iOS has no
   * `getDisplayMedia` at all, so on a phone the interview cannot be completed.
   * Safe to expose: it describes the device requirement, not the script.
   */
  screenRecordingEnabled?: boolean;
};

export type ProfileCompletionResponseDto = {
  /**
   * List of questions for missing profile attributes
   */
  questions: Array<ProfileCompletionQuestionDto>;
};

export type ProfileCompletionQuestionDto = {
  /**
   * The attribute key from the registry
   */
  attributeKey:
    | 'gender'
    | 'ethnicity'
    | 'householdIncome'
    | 'educationLevel'
    | 'dateOfBirth'
    | 'relationshipStatus'
    | 'languages'
    | 'numberOfChildren'
    | 'numberOfChildrenLiveAtHome'
    | 'childrenBirthYears'
    | 'livingSituation'
    | 'pets'
    | 'homeOwner'
    | 'disabilityType'
    | 'nationality'
    | 'areaType'
    | 'militaryServiceStatus'
    | 'employmentStatuses'
    | 'industry'
    | 'companySize'
    | 'seniorityLevel'
    | 'workSetting'
    | 'jobFunctions'
    | 'cloudPlatforms'
    | 'codingAITools'
    | 'designPrototypingTools'
    | 'foodDeliveryServices'
    | 'healthWellnessApps'
    | 'onlineBankingTools'
    | 'onlineLearningPlatforms'
    | 'onlineMarketplacePlatforms'
    | 'onlineTravelBooking'
    | 'productivitySoftware'
    | 'smartHomeDevices'
    | 'socialNetworkPlatforms'
    | 'streamingServices'
    | 'vehicleBrands'
    | 'onlineShoppingFrequency'
    | 'webcam'
    | 'mobilePhoneWithInternet'
    | 'headsetWithMicrophone'
    | 'programmingLanguages'
    | 'browsers'
    | 'smartphoneOS'
    | 'tabletOS'
    | 'computerOS'
    | 'assistiveTechnologies';
  /**
   * The question text to display
   */
  questionText: string;
  questionType: ProfileCompletionQuestionType;
  /**
   * Options for enum-based questions. Absent for LOOKUP_MULTI_SELECT questions, whose choices are searched from `lookupGroup`.
   */
  options?: Array<ProfileCompletionQuestionOptionDto>;
  lookupGroup?: LookupGroup;
};

export type ViewProjectResponseDto = {
  success?: boolean;
  message?: string;
  statusCode?: number;
  response?: ViewProjectResponseDataDto;
  errors: Array<string>;
  redirectCode?: RedirectCode;
  notification?: NotificationDto;
};

export const NotificationUiType = {
  INFO: 'info',
  DANGER: 'danger',
  ERROR: 'error',
} as const;

export type NotificationUiType =
  (typeof NotificationUiType)[keyof typeof NotificationUiType];

export type NotificationDto = {
  show?: boolean;
  type?: NotificationUiType;
};

export const RedirectCode = {
  BROWSE_PROJECTS: 'BROWSE_PROJECTS',
  COMPLETE_PROFILE: 'COMPLETE_PROFILE',
  COMPLETE_EMPLOYMENT: 'COMPLETE_EMPLOYMENT',
} as const;

export type RedirectCode = (typeof RedirectCode)[keyof typeof RedirectCode];

export const ProjectDisqualificationReason = {
  MUST_COMPLETE_PROFILE: 'mustCompleteProfile',
  WORK_EMAIL_REQUIRED: 'workEmailRequired',
  WORK_EMAIL_NEEDS_REVERIFICATION: 'workEmailNeedsReverification',
  TRUSTED_COMPANY_CHECK_PENDING: 'trustedCompanyCheckPending',
  INCORRECT_DEMOGRAPHICS: 'incorrectDemographics',
  PAST_PARTICIPATION: 'pastParticipation',
  PROJECT_MATCH_GRACE_PERIOD: 'projectMatchGracePeriod',
  QUOTA_SEGMENT_FULL: 'quotaSegmentFull',
} as const;

export type ProjectDisqualificationReason =
  (typeof ProjectDisqualificationReason)[keyof typeof ProjectDisqualificationReason];

export const PrequalificationResult = {
  QUALIFIED: 'qualified',
  DISQUALIFIED: 'disqualified',
  INCOMPLETE: 'incomplete',
} as const;

export type PrequalificationResult =
  (typeof PrequalificationResult)[keyof typeof PrequalificationResult];

export type PrequalificationResultDto = {
  result?: PrequalificationResult;
  disqualificationReason?: ProjectDisqualificationReason;
  message?: string;
  redirectCode?: RedirectCode;
  notification?: NotificationDto;
  requiredProfileCompletionQuestions: Array<ProfileCompletionQuestionDto>;
};

export type SanitizedProjectDto = {
  _id?: string;
  name?: string;
  description?: string;
  slug?: string;
  respondentRemuneration?: number;
  respondentRemunerationFormatted?: string;
  isRemote?: boolean;
  countryFormatted?: string;
  studyLengthMinutes?: number;
  hasLocation?: boolean;
  /**
   * Whether this project accepts logged-out applications via the public
   * apply page. Set manually in the database, absent means off. Safe to
   * expose: it says a page exists, not anything about the project's
   * screener or targeting.
   */
  publicApplicationsEnabled?: boolean;
  location?: LocationDto;
  kindOfResearchFormatted?: string;
  radius?: number;
  companyName?: string;
  kindOfResearch?: number;
  researchConductType?: string;
  talkToAudience?: string;
  country?: string;
  timeHoursRequired?: number;
  timeMinutesRequired?: number;
  incentive?: number;
  oversubscribed?: boolean;
  closed?: boolean;
  recruitingStatus?: string;
  organizationId?: string;
  referralBonus?: number;
  hideCompanyName?: boolean;
  specialInstructions?: string;
  exposeParticipantEmails?: boolean;
  /**
   * Taking part requires sharing a screen (an AI-moderated interview with at
   * least one screen-recorded question). Exposed so the participant can be
   * told the study is desktop-only *before* starting it — iOS has no
   * `getDisplayMedia` at all, so on a phone the interview cannot be completed.
   * Safe to expose: it describes the device requirement, not the script.
   */
  screenRecordingEnabled?: boolean;
  prequalifies?: PrequalificationResultDto;
};

export type ScreenerCycleDto = {
  /**
   * Number of screeners taken in current cycle
   */
  screenerCount: number;
  /**
   * Max allowed per cycle (5 or 10)
   */
  limit: number;
  /**
   * ISO date when cycle resets
   */
  cycleExpiryDate?: string | null;
};

export type ExternalProjectDto = {
  researcherId: string;
  bookingUrl?: string;
  /**
   * Link to cancel a meeting. If not set, bookingLink will be used.
   */
  meetingCancelLink?: string;
  bookedDate?: string;
  /**
   * IANA timezone. Example: 'America/New_York'
   */
  timezone?: string;
};

export type InvitationDto = {
  id?: string;
  externalBookingUrl?: string;
  message?: string | null;
};

export type ViewProjectResponseDataDto = {
  profileEmail: string;
  referralCode?: string | null;
  referralLink?: string;
  referralLinkPretty?: string;
  isTaskComplete: boolean;
  /**
   * Best effort: can be absent even when `isTaskComplete` is true.
   */
  taskCompletedAt?: string;
  referralFee?: number;
  inviteCode?: string | null;
  inviteId?: string | null;
  invitation?: InvitationDto;
  externalProject?: ExternalProjectDto;
  messengerLink?: string;
  projectCompletionId?: string;
  contactUrl: string;
  screenerResponseStatus?: ScreenerResponseStatus;
  payoutStatus: RespondentPayoutStatus;
  projectStatus?: ProjectStatus;
  scheduledAt?: string;
  bookingUrl: string;
  meetingJoinTarget?: string;
  isProjectClosed: boolean;
  isClosedToNewApplications: boolean;
  /**
   * The study is finished (closed, or recruitment complete). Unlike
   * `isClosedToNewApplications` this is not waived for invitees — it blocks
   * starting the task as well as applying, so the UI must withdraw the start
   * button rather than let the click fail server-side.
   */
  isStudyOver: boolean;
  /**
   * PX-440. The start endpoint would refuse this participant's first start
   * because the project has delivered its target (organization opted in).
   * Per participant, not per project: false for anyone who already started,
   * who may still return to the task, and false whenever there is no start to
   * refuse (not invited, moderated study, anonymous view). The UI disables
   * the Start button off this rather than letting the click fail.
   */
  isTargetReached: boolean;
  screenerCycle?: ScreenerCycleDto | null;
  cancelBookingUrl: string;
  rescheduleUrl: string;
  id?: string;
  userId?: string | null;
  privateUser?: boolean;
  project?: SanitizedProjectDto;
  showReferral?: boolean;
  /**
   * Questions the participant will actually be asked. Zero when the caller's
   * invitation waives the screener (`skipScreenerQuestions`), even if the
   * project has questions — the apply page will show one Apply step.
   */
  screenerQuestionsLength?: number;
  /**
   * The caller holds a skip-the-screener invitation to this project, resolved
   * from the invitation bound to them (never from the raw `inviteId` query).
   * Lets the details page say what the apply page will do before they click.
   */
  skipScreenerQuestions?: boolean;
  externalQuestionsLength?: number;
  surveyResponseId?: string | null;
  projectStarted: boolean;
  /**
   * First start. Absent on responses started before it was recorded.
   */
  projectStartedAt?: string;
  requiresManualCompletion: boolean;
  showDoubleScreeningAlert?: boolean;
  isMatched: boolean;
  /**
   * Matched to the project, or invited to it (participant database or direct link).
   */
  isExemptFromScreenerLimit: boolean;
};

export type ChangePasswordDto = {
  currentPassword: string;
  newPassword: string;
};

export type VerifyEmploymentEmailCodeDto = {
  /**
   * The 6 digit code emailed to the work email address.
   */
  code: string;
};

export type ParticipantProfileWithLocksDto = {
  profile: ParticipantProfileDto;
  lockedFields: {
    firstName?: {
      unlocksAt?: string;
    };
    lastName?: {
      unlocksAt?: string;
    };
    'employment.email'?: {
      unlocksAt?: string;
    };
    'employment.emailDomain'?: {
      unlocksAt?: string;
    };
    'employment.isEmailVerified'?: {
      unlocksAt?: string;
    };
    'employment.company'?: {
      unlocksAt?: string;
    };
    'employment.companySize'?: {
      unlocksAt?: string;
    };
    'employment.employmentStatus'?: {
      unlocksAt?: string;
    };
    'employment.jobTitle'?: {
      unlocksAt?: string;
    };
    'employment.jobTitleId'?: {
      unlocksAt?: string;
    };
    'employment.seniorityLevel'?: {
      unlocksAt?: string;
    };
    'employment.industry'?: {
      unlocksAt?: string;
    };
    'employment.roleDescription'?: {
      unlocksAt?: string;
    };
    'employment.jobFunctions'?: {
      unlocksAt?: string;
    };
    'employment.income'?: {
      unlocksAt?: string;
    };
    'employment.businessOwner'?: {
      unlocksAt?: string;
    };
    'employment.smallBusinessOwner'?: {
      unlocksAt?: string;
    };
    'employment.internalOrExternalCompanyId'?: {
      unlocksAt?: string;
    };
    'demographics.gender'?: {
      unlocksAt?: string;
    };
    'demographics.ethnicity'?: {
      unlocksAt?: string;
    };
    'demographics.educationLevel'?: {
      unlocksAt?: string;
    };
    'demographics.dateOfBirth'?: {
      unlocksAt?: string;
    };
    'demographics.birthDate'?: {
      unlocksAt?: string;
    };
    'demographics.age'?: {
      unlocksAt?: string;
    };
    'demographics.householdIncome'?: {
      unlocksAt?: string;
    };
    'personal.relationshipStatus'?: {
      unlocksAt?: string;
    };
    'personal.languages'?: {
      unlocksAt?: string;
    };
    'personal.numberOfChildren'?: {
      unlocksAt?: string;
    };
    'personal.nationality'?: {
      unlocksAt?: string;
    };
    'personal.numberOfChildrenLiveAtHome'?: {
      unlocksAt?: string;
    };
    'personal.childrenBirthYears'?: {
      unlocksAt?: string;
    };
    'personal.livingSituation'?: {
      unlocksAt?: string;
    };
    'personal.pets'?: {
      unlocksAt?: string;
    };
    'personal.homeOwner'?: {
      unlocksAt?: string;
    };
    'personal.areaType'?: {
      unlocksAt?: string;
    };
    'technical.webcam'?: {
      unlocksAt?: string;
    };
    'technical.mobilePhoneWithInternet'?: {
      unlocksAt?: string;
    };
    'technical.headsetWithMicrophone'?: {
      unlocksAt?: string;
    };
    'technical.programmingLanguages'?: {
      unlocksAt?: string;
    };
    'technical.browsers'?: {
      unlocksAt?: string;
    };
    'technical.smartphoneOS'?: {
      unlocksAt?: string;
    };
    'technical.assistiveTechnologies'?: {
      unlocksAt?: string;
    };
    'technical.tabletOS'?: {
      unlocksAt?: string;
    };
    'technical.computerOS'?: {
      unlocksAt?: string;
    };
    'location.city'?: {
      unlocksAt?: string;
    };
    'location.state'?: {
      unlocksAt?: string;
    };
    'location.zipcode'?: {
      unlocksAt?: string;
    };
    'socialLinks.linkedin'?: {
      unlocksAt?: string;
    };
    'socialLinks.facebook'?: {
      unlocksAt?: string;
    };
    'socialLinks.twitter'?: {
      unlocksAt?: string;
    };
    'socialLinks.instagram'?: {
      unlocksAt?: string;
    };
    'socialLinks.github'?: {
      unlocksAt?: string;
    };
    'productsServices.streamingServices'?: {
      unlocksAt?: string;
    };
    'productsServices.socialNetworkPlatforms'?: {
      unlocksAt?: string;
    };
    'productsServices.onlineMarketplacePlatforms'?: {
      unlocksAt?: string;
    };
    'productsServices.foodDeliveryServices'?: {
      unlocksAt?: string;
    };
    'productsServices.healthWellnessApps'?: {
      unlocksAt?: string;
    };
    'productsServices.onlineBankingTools'?: {
      unlocksAt?: string;
    };
    'productsServices.onlineTravelBooking'?: {
      unlocksAt?: string;
    };
    'productsServices.onlineLearningPlatforms'?: {
      unlocksAt?: string;
    };
    'productsServices.productivitySoftware'?: {
      unlocksAt?: string;
    };
    'productsServices.smartHomeDevices'?: {
      unlocksAt?: string;
    };
    'productsServices.vehicleBrands'?: {
      unlocksAt?: string;
    };
    'productsServices.cloudPlatforms'?: {
      unlocksAt?: string;
    };
    'productsServices.codingAITools'?: {
      unlocksAt?: string;
    };
    'productsServices.designPrototypingTools'?: {
      unlocksAt?: string;
    };
    'shopping.onlineShoppingFrequency'?: {
      unlocksAt?: string;
    };
    'professional.militaryServiceStatus'?: {
      unlocksAt?: string;
    };
    'professional.workSetting'?: {
      unlocksAt?: string;
    };
    videoIntroUrl?: {
      unlocksAt?: string;
    };
    skills?: {
      unlocksAt?: string;
    };
  };
};

export const LeadStatus = {
  PENDING: 'PENDING',
  VERIFIED: 'VERIFIED',
  REJECTED: 'REJECTED',
  DECLINED: 'DECLINED',
} as const;

export type LeadStatus = (typeof LeadStatus)[keyof typeof LeadStatus];

export type ProfileEmploymentDto = {
  email?: string | null;
  emailDomain?: string | null;
  isEmailVerified?: boolean;
  isLinkedInVerified?: boolean;
  company?: string | null;
  companySize?: CompanySize;
  businessOwner?: boolean;
  employmentStatus: EmploymentStatus;
  jobTitle?: string | null;
  jobTitleId?: string | null;
  seniorityLevel?: SeniorityLevel;
  normalizedJobTitle?: JobTitle | null;
  industry?: Array<string> | null;
  roleDescription?: string | null;
  relatedJobTitles?: Array<string> | null;
  /**
   * The company ID that is associated to the profile employment via an association
   * between the given company name and the given work email domain.
   * The ID itself may refer to a record in the `company`, `fullcontactcompany`,
   * or `clearbitcompany` collections.
   * If an association between the company name and work email domain does not exist,
   * then we set the field to `null` in the client.
   * If an association has _not yet_ been made, we leave this field `undefined`.
   */
  internalOrExternalCompanyId?: string | null;
  jobFunctions?: Array<JobFunction>;
  income?: Income;
  smallBusinessOwner?: boolean;
  updatedAt?: string | null;
  lastTrustedCompanyCheck?: string | null;
};

export const ManualReviewAuditStatus = {
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
} as const;

export type ManualReviewAuditStatus =
  (typeof ManualReviewAuditStatus)[keyof typeof ManualReviewAuditStatus];

export type ProfilePlaidRiskItem = {
  lastChecked: string;
  level: ProfileRiskLevel;
  templateId?: string;
};

export const ProfileRiskLevel = {
  REJECTED: 'rejected',
  APPROVED: 'approved',
  QUESTIONED: 'questioned',
} as const;

export type ProfileRiskLevel =
  (typeof ProfileRiskLevel)[keyof typeof ProfileRiskLevel];

export type ProfileRiskItem = {
  lastChecked: string;
  level: ProfileRiskLevel;
};

export type ProfileRisk = {
  riskLevel?: ProfileRiskLevel;
  profile?: ProfileRiskItem;
  clearbit?: ProfileRiskItem;
  plaid?: ProfilePlaidRiskItem;
  roundtable?: ProfileRiskItem;
  country?: ProfileRiskItem;
  manualReviewAudit?: ManualReviewAuditStatus;
};

export type ProfessionalInfo = {
  militaryServiceStatus?: MilitaryServiceStatus;
  workSetting?: WorkSetting;
};

export type ShoppingInfo = {
  onlineShoppingFrequency?: OnlineShoppingFrequency;
};

export type ProductsServicesInfo = {
  cloudPlatforms?: Array<CloudPlatform>;
  codingAITools?: Array<CodingAiTool>;
  designPrototypingTools?: Array<DesignPrototypingTool>;
  foodDeliveryServices?: Array<FoodDeliveryService>;
  healthWellnessApps?: Array<HealthWellnessApp>;
  onlineBankingTools?: Array<OnlineBankingTool>;
  onlineLearningPlatforms?: Array<OnlineLearningPlatform>;
  onlineMarketplacePlatforms?: Array<OnlineMarketplacePlatform>;
  onlineTravelBooking?: Array<OnlineTravelBooking>;
  productivitySoftware?: Array<ProductivitySoftware>;
  smartHomeDevices?: Array<SmartHomeDevice>;
  socialNetworkPlatforms?: Array<SocialNetworkPlatform>;
  streamingServices?: Array<StreamingService>;
  vehicleBrands?: Array<VehicleBrand>;
};

export type Technical = {
  webcam?: boolean;
  mobilePhoneWithInternet?: boolean;
  headsetWithMicrophone?: boolean;
  programmingLanguages?: Array<ProgrammingLanguage>;
  browsers?: Array<Browser>;
  smartphoneOS?: Array<SmartphoneOs>;
  tabletOS?: Array<TabletOs>;
  computerOS?: Array<ComputerOs>;
  assistiveTechnologies?: Array<AssistiveTechnology>;
};

export type PersonalInfo = {
  relationshipStatus?: RelationshipStatus;
  languages?: Array<ParticipantLanguage>;
  numberOfChildren?: number;
  numberOfChildrenLiveAtHome?: number;
  childrenBirthYears?: Array<string>;
  livingSituation?: LivingSituation;
  pets?: Array<Pet>;
  homeOwner?: boolean;
  disabilityType?: Array<DisabilityType>;
  nationality?: Array<Nationality>;
  areaType?: AreaType;
};

export type ProfileTwilioLineTypeIntelligence = {
  carrierName?: string | null;
  errorCode?: string | null;
  mobileCountryCode?: string | null;
  mobileNetworkCode?: string | null;
  type?: string | null;
};

export type ProfileTwilioPhoneData = {
  countryCode?: string | null;
  phoneNumber?: string | null;
  valid?: boolean;
  lineTypeIntelligence?: ProfileTwilioLineTypeIntelligence;
};

export type ParticipantProfileDto = {
  id: string;
  createdAt: string;
  userId: string;
  firstName: string;
  lastName: string;
  status: RespondentStatus;
  linkedinUrl?: string | null;
  mobileType?: string | null;
  phone?: string;
  isPhoneVerified?: boolean;
  verifiedPhone?: string;
  phoneVerificationCode?: string | null;
  twilioPhoneData?: ProfileTwilioPhoneData;
  email?: string;
  emailDomain?: string;
  paypal?: string | null;
  demographics?: ProfileDemographics;
  picture?: string | null;
  location?: ProfileLocation;
  social?: ProfileSocial;
  socialLinks?: ProfileSocialLinks | null;
  notificationSettings?: ProfileNotificationSettings;
  cookieSettings?: ProfileCookieSettings;
  score?: number;
  skills: Array<Skill>;
  videoIntroUrl?: string | null;
  topics?: Array<SimpleTopic>;
  /**
   * Must stay serialized on ProfileDto: the profile-risk-change-respond-queue
   * lambda reads the profile over GET v4/profiles/:profileId and feeds it to
   * computeOverallProfileRisk (@common/utils), where a null trustedCompany
   * marks the profile risk component 'questioned'. An `@Exclude()` here made
   * the DTO report null for every profile, so trusted-company verification
   * could never clear that flag (profile.dto.unit.spec.ts guards this).
   */
  trustedCompany?: ProfileTrustedCompany | null;
  /**
   * For public api partners only
   */
  externalOrganizationId?: string;
  personal?: PersonalInfo;
  technical?: Technical;
  productsServices?: ProductsServicesInfo;
  shopping?: ShoppingInfo;
  professional?: ProfessionalInfo;
  risk?: ProfileRisk;
  gdprDeletedAt?: string;
  lastLoginAt?: string;
  lastScreenerSubmittedAt?: string;
  employment?: ProfileEmploymentDto;
  /**
   * Whether the work email domain matches the company name.
   */
  workEmailMatchesCompany: boolean;
  /**
   * Whether the work email needs to be re-verified (e.g. after 90 days).
   */
  workEmailNeedsReverification: boolean;
  /**
   * Whether a work email verification message has been sent and is still pending (verification token present).
   */
  workVerificationEmailSent: boolean;
  leadStatus?: LeadStatus;
  updatedAt?: string;
};

export type UpdatePricingPurchaseOrderDto = {
  organizationId?: string;
  teamId?: string;
  price?: number;
  poNumber?: string;
  name?: string;
  description?: string;
  state?: PricingPurchaseOrderState;
  components?: Array<PricingPurchaseOrderComponent>;
};

export const PricingPurchaseOrderComponentType = {
  PLAN: 'plan',
  CREDIT: 'credit',
  INCENTIVE: 'incentive',
} as const;

export type PricingPurchaseOrderComponentType =
  (typeof PricingPurchaseOrderComponentType)[keyof typeof PricingPurchaseOrderComponentType];

export type PricingPurchaseOrderComponent = {
  id: string;
  type: PricingPurchaseOrderComponentType;
  charge: number;
  planId?: string;
  teamId?: string;
  teamPlanId?: string;
  amount?: number;
};

export const PricingPurchaseOrderState = {
  DRAFT: 'draft',
  PENDING: 'pending',
  PAID: 'paid',
  CANCELLED: 'cancelled',
} as const;

export type PricingPurchaseOrderState =
  (typeof PricingPurchaseOrderState)[keyof typeof PricingPurchaseOrderState];

export type PricingPurchaseOrdersPaginatedDto = {
  /**
   * The total number of results possible, which is only provided in the response when `includeCount` is passed as `true` within a request.
   */
  totalResults?: number;
  page: number;
  pageSize: number;
  results: Array<PricingPurchaseOrder>;
};

export type PricingPurchaseOrder = {
  id: string;
  createdAt: string;
  organizationId: string;
  teamId?: string;
  price: number;
  poNumber: string;
  name: string;
  description: string;
  state: PricingPurchaseOrderState;
  components: Array<PricingPurchaseOrderComponent>;
  updatedAt?: string;
};

export type GetPricingPurchaseOrdersPaginatedDto = {
  page?: number;
  pageSize?: number;
  includeCount?: boolean;
  filters?: PricingPurchaseOrderFilters;
};

export type PricingPurchaseOrderFilters = {
  organizationId?: string;
  teamId?: string;
  poNumber?: string;
  name?: string;
  state?: PricingPurchaseOrderState;
};

export type CreatePricingPurchaseOrderDto = {
  organizationId: string;
  teamId?: string;
  price: number;
  poNumber: string;
  name: string;
  description: string;
  state: PricingPurchaseOrderState;
  components: Array<PricingPurchaseOrderComponent>;
};

export type RedeemPricingPromotionResultDto = {
  /**
   * Display name of the redeemed promotion.
   */
  promotionName: string;
  /**
   * Credits granted. On pricingVersion 3 this is an amount in cents (5000 = $50); on pricingVersion 2 it is a count of session units (30 = 1 B2C session).
   */
  amountGranted: number;
  /**
   * Pricing version of the redeeming team's active plan.
   */
  pricingVersion: 2 | 3;
};

export type RedeemPricingPromotionDto = {
  code: string;
};

export type UpdatePricingPromotionDto = {
  /**
   * Internal label, e.g. "Christmas 2026".
   */
  name?: string;
  /**
   * Stored uppercase; uniqueness enforced by the code_1 index.
   */
  code?: string;
  /**
   * Deactivation replaces deletion; there is no DELETE endpoint.
   */
  active?: boolean;
  /**
   * Maximum total redemptions across all organizations.
   */
  quantity?: number;
  /**
   * Credits granted to a v3 team (v3 credits are cents: 5000 = $50).
   */
  amountV3?: number;
  /**
   * Credits granted to a v2 team (session raw-units: B2C session = 30).
   */
  amountV2?: number;
  /**
   * Null clears the description.
   */
  description?: string | null;
  /**
   * Null clears the expiry, leaving a code that never expires.
   */
  expiresAt?: string | null;
  /**
   * Null clears the cap, making per-organization redemptions unlimited.
   */
  organizationCap?: number | null;
  /**
   * Null clears the override, restoring the 2-month credit expiry default.
   */
  creditExpiryMonths?: number | null;
};

export type PricingPromotionsPaginatedDto = {
  /**
   * The total number of results possible, which is only provided in the response when `includeCount` is passed as `true` within a request.
   */
  totalResults?: number;
  page: number;
  pageSize: number;
  results: Array<PricingPromotion>;
};

export type PricingPromotionRedemption = {
  userId: string;
  teamId: string;
  organizationId: string;
  teamPlanId: string;
  /**
   * 2 or 3: which pricing model the team was on when it redeemed.
   */
  pricingVersion: number;
  /**
   * Credits granted (denominated per pricingVersion).
   */
  amount: number;
  /**
   * Set after the grant succeeds; links to the pricing_credits lot.
   */
  creditId?: string;
  redeemedAt: string;
};

export type PricingPromotion = {
  id: string;
  createdAt: string;
  /**
   * Internal label, e.g. "Christmas 2026".
   */
  name: string;
  description?: string;
  /**
   * Stored uppercase; uniqueness enforced by the code_1 index.
   */
  code: string;
  /**
   * Deactivation replaces deletion; there is no DELETE endpoint.
   */
  active: boolean;
  /**
   * Absent means the code never expires.
   */
  expiresAt?: string;
  /**
   * Maximum total redemptions across all organizations.
   */
  quantity: number;
  /**
   * Maximum redemptions per organization. Absent means unlimited; the
   * per-user once-only rule is the only fixed limit.
   */
  organizationCap?: number;
  /**
   * How many months the granted credits live after redemption. Absent means
   * createForSupport's default (2 months).
   */
  creditExpiryMonths?: number;
  /**
   * Credits granted to a v3 team (v3 credits are cents: 5000 = $50).
   */
  amountV3: number;
  /**
   * Credits granted to a v2 team (session raw-units: B2C session = 30).
   */
  amountV2: number;
  /**
   * Denormalized redemptions[] length, maintained by the atomic claim so the
   * quantity guard can live inside a single findOneAndUpdate filter.
   */
  redemptionCount: number;
  redemptions: Array<PricingPromotionRedemption>;
  updatedAt?: string;
};

export type GetPricingPromotionsPaginatedDto = {
  page?: number;
  pageSize?: number;
  includeCount?: boolean;
  filters?: PricingPromotionFilters;
};

export type PricingPromotionFilters = {
  name?: string;
  code?: string;
  active?: boolean;
};

export type CreatePricingPromotionDto = {
  /**
   * Internal label, e.g. "Christmas 2026".
   */
  name: string;
  description?: string;
  /**
   * Stored uppercase; uniqueness enforced by the code_1 index.
   */
  code: string;
  /**
   * Deactivation replaces deletion; there is no DELETE endpoint.
   */
  active: boolean;
  /**
   * Absent means the code never expires.
   */
  expiresAt?: string;
  /**
   * Maximum total redemptions across all organizations.
   */
  quantity: number;
  /**
   * Maximum redemptions per organization. Absent means unlimited; the
   * per-user once-only rule is the only fixed limit.
   */
  organizationCap?: number;
  /**
   * How many months the granted credits live after redemption. Absent means
   * createForSupport's default (2 months).
   */
  creditExpiryMonths?: number;
  /**
   * Credits granted to a v3 team (v3 credits are cents: 5000 = $50).
   */
  amountV3: number;
  /**
   * Credits granted to a v2 team (session raw-units: B2C session = 30).
   */
  amountV2: number;
};

export type PricingCreditsAuditPaginatedDto = {
  /**
   * The total number of results possible, which is only provided in the response when `includeCount` is passed as `true` within a request.
   */
  totalResults?: number;
  page: number;
  pageSize: number;
  results: Array<PricingCreditAudit>;
};

export type CreditAuditSurveyResponse = {
  surveyResponseId: string;
  participantUserId: string;
};

export const PricingCreditAuditAction = {
  RESERVED: 'reserved',
  CONSUMED: 'consumed',
  CREATED: 'created',
  ADDED: 'added',
  RELEASED: 'released',
  TRANSFERED: 'transfered',
  EXTENDED: 'extended',
  EXPIRED: 'expired',
  CANCELLED: 'cancelled',
  RESTORED: 'restored',
  REFUNDED: 'refunded',
} as const;

export type PricingCreditAuditAction =
  (typeof PricingCreditAuditAction)[keyof typeof PricingCreditAuditAction];

export const PricingCreditType = {
  CREDIT: 'credit',
  INCENTIVE: 'incentive',
} as const;

export type PricingCreditType =
  (typeof PricingCreditType)[keyof typeof PricingCreditType];

export type PricingCreditAudit = {
  id: string;
  createdAt: string;
  creditId: string;
  type: PricingCreditType;
  action: PricingCreditAuditAction;
  actionId?: string;
  /**
   * this is the unit cost in USD cents of each credit, if type is incentive then it should be 1
   */
  unitCost?: number;
  userId?: string;
  teamId: string;
  organizationId: string;
  projectId?: string;
  masterTransactionId?: string;
  note?: string;
  surveyResponse?: CreditAuditSurveyResponse;
  amount: number;
  fromReservation?: boolean;
  updatedAt?: string;
};

export type GetPricingCreditsAuditPaginatedDto = {
  page?: number;
  pageSize?: number;
  includeCount?: boolean;
  filters?: PricingCreditAuditFilters;
};

export type PricingCreditAuditFilters = {
  teamId?: string;
  organizationId?: string;
  userId?: string;
  projectId?: string;
  creditId?: string;
  masterTransactionId?: string;
  action?: PricingCreditAuditAction;
  createdAt?: string;
};

export type PrepayResponseDto = {
  creditCharged: number;
  incentiveCharged: number;
  totalCharged: number;
  masterTransactionId?: string;
  newBalanceCredits: number;
  newBalanceIncentives: number;
};

export type PrepayProjectDto = {
  creditCardToken?: string;
  note?: string;
};

export type PayoutCancelDto = {
  note?: string;
};

export type ProcessAutopayResponseDto = {
  processed: number;
  remaining: number;
};

export type ProcessAutopayDto = {
  numberToProcess?: number;
};

export type PayoutProjectDto = {
  participants: Array<PayoutParticipant>;
  creditCardToken?: string;
};

export type PayoutParticipant = {
  surveyResponseId: string;
  payoutCount?: number;
  tip?: number;
};

export type CreatePaygPricingCreditDto = {
  /**
   * Optional note from system or CS
   */
  note?: string;
  type: PricingCreditType;
  projectId?: string;
  creditCardToken?: string;
  amount: number;
  unitCost?: number;
  noCharge?: boolean;
  customExpiry?: number;
  idempotencyKey?: string;
};

export type PayoutSummaryDto = {
  creditReservedBalance: number;
  creditAvailableBalance: number;
  /**
   * @deprecated
   */
  creditToBePaid: number;
  incentiveReservedBalance: number;
  incentiveAvailableBalance: number;
  /**
   * @deprecated
   */
  incentiveToBePaid: number;
  creditTotalCost: number;
  incentiveTotalCost: number;
  merchantFee: number;
  participantType: ParticipantType;
  kindOfResearch: KindOfResearch;
};

export const ParticipantType = { B2C: 'b2c', B2B: 'b2b' } as const;

export type ParticipantType =
  (typeof ParticipantType)[keyof typeof ParticipantType];

export type GetPayoutSummaryDto = {
  participants: Array<PayoutParticipant>;
};

export type LegacyProjectPaymentsPaginatedDto = {
  /**
   * The total number of results possible, which is only provided in the response when `includeCount` is passed as `true` within a request.
   */
  totalResults?: number;
  page: number;
  pageSize: number;
  results: Array<LegacyProjectPaymentDto>;
};

export type LegacyProjectPaymentDto = {
  id: string;
  organizationId: string;
  amount: number;
  transactionFee: number;
  createdAt: string;
};

export type PastPaymentsPaginatedDto = {
  /**
   * The total number of results possible, which is only provided in the response when `includeCount` is passed as `true` within a request.
   */
  totalResults?: number;
  page: number;
  pageSize: number;
  /**
   * True when the project has at least one downloadable receipt — i.e. at
   * least one past payment backed by a pricing charge (PAYG). Projects funded
   * purely from plan/credits have no pricing charges and so no receipt.
   */
  hasReceipt: boolean;
  results: Array<PastPaymentDto>;
};

export type PastPaymentDto = {
  respondentPayoutId: string;
  /**
   * The pricing charge that paid this payment — links to the receipt PDF.
   */
  pricingChargeId?: string;
  surveyResponseId: string;
  userId: string;
  firstName: string;
  lastInitial: string;
  avatarUrl?: string;
  incentive: number;
  tip: number;
  fee: number;
  total: number;
  datePaid: string;
  /**
   * The 1-based index of this payment for the participant on this project.
   */
  payoutCount: number;
  status: RespondentPayoutStatus;
};

export type ParticipantsToPayPaginatedDto = {
  /**
   * The total number of results possible, which is only provided in the response when `includeCount` is passed as `true` within a request.
   */
  totalResults?: number;
  page: number;
  pageSize: number;
  results: Array<ParticipantToPayDto>;
};

export type ParticipantToPayDto = {
  surveyResponseId: string;
  userId: string;
  firstName: string;
  lastInitial: string;
  avatarUrl?: string;
  recruitmentCost: number;
  incentiveCost: number;
  maxTip: number;
  payoutCount: number;
  /**
   * The research team pays this participant's incentive directly (an
   * external invite link set to pay off the platform). Paying them out here
   * charges the recruitment fee only: `incentiveCost` and `maxTip` are 0.
   */
  incentivePaidExternally: boolean;
};

export type ProjectPaymentsOverviewDto = {
  /**
   * Sum of completed RespondentPayout totalIncentive + tip for this project, in cents.
   */
  paidToParticipants: number;
  /**
   * Sum of tip portion within `paidToParticipants`, in cents.
   */
  paidTipsTotal: number;
  /**
   * Number of completed RespondentPayouts for this project.
   */
  paidPaymentCount: number;
  /**
   * Total outstanding payout still to be made for eligible participants, in cents.
   */
  pendingPayout: number;
  /**
   * Number of eligible participants not yet paid.
   */
  pendingPayoutCount: number;
  /**
   * Total on-hold credits for this project (recruitment + incentive reservations), in cents.
   */
  onHoldTotal: number;
  /**
   * Recruitment credits reserved against this project, in cents.
   */
  onHoldRecruitment: number;
  /**
   * Incentive credits reserved against this project, in cents.
   */
  onHoldIncentive: number;
  /**
   * Available team-level recruitment credit pool (sum of all unreserved CREDIT), in cents.
   */
  recruitmentBalance: number;
  /**
   * Available team-level incentive credit pool (sum of all unreserved INCENTIVE), in cents.
   */
  incentiveBalance: number;
  /**
   * Percentage applied to card payments (e.g. 3 = 3%).
   */
  processingFeePercent: number;
  /**
   * Default payment method for the team, or null if none exists.
   */
  defaultPaymentMethod?: CreditCardDto;
  /**
   * Numeric account trust level; UI hides Pay button when < 90.
   */
  accountTrustLevel: number;
  /**
   * Whether multipay UI (payoutCount filter, max-5 enforcement) should be shown.
   */
  enableMultipay: boolean;
  /**
   * Default cap on tip as a fraction of incentive (0..1).
   */
  maxTipPercentageOfIncentive: number;
  /**
   * True when the team still has legacy V2 pricing payments to surface.
   */
  hasLegacyPricing: boolean;
  kindOfResearch: KindOfResearch;
};

export type CreditCardDto = {
  token?: string;
  cardType?: string;
  /**
   * @deprecated
   */
  imageUrl?: string;
  maskedNumber?: string;
  last4?: string;
  cardholderName?: string;
  /**
   * @deprecated
   */
  fullName?: string;
  billingAddress?: CreditCardBillingAddressDto;
  /**
   * @deprecated
   */
  streetAddress?: string;
  /**
   * @deprecated
   */
  postalCode?: string;
  /**
   * Format: MM/YYYY
   */
  expirationDate?: string;
  /**
   * Format: MM
   */
  expirationMonth?: string;
  /**
   * Format: YYYY
   */
  expirationYear?: string;
  /**
   * @deprecated
   */
  isDefault?: boolean;
  default?: boolean;
  /**
   * @deprecated
   */
  isExpired?: boolean;
  expired?: boolean;
};

export type ManagePausedPlanSummaryDto = {
  numberPaused?: number;
};

export type ManageTeamPlanRenewalsSummaryDto = {
  numberExpirations?: number;
  numberExpirationFailures?: number;
  numberRenewalReminders?: number;
  numberRenewalReminderFailures?: number;
  numberRenewals?: number;
  numberRenewalFailures?: number;
};

export type ManagePricingTeamBalanceDto = {
  details: ManagePricingTeamPlanAuditDto;
  balance: ManagePricingTeamBalanceTransactionDto;
};

export const ManageTeamBalanceReason = {
  NEW_PURCHASE: 'NEW_PURCHASE',
  NO_PAYMENT: 'NO_PAYMENT',
  CUSTOMER_SERVICE: 'CUSTOMER_SERVICE',
  EXCESSIVE_INCENTIVE: 'EXCESSIVE_INCENTIVE',
  DISTRIBUTION_REQUEST: 'DISTRIBUTION_REQUEST',
  TEAM_CONSOLIDATION: 'TEAM_CONSOLIDATION',
} as const;

export type ManageTeamBalanceReason =
  (typeof ManageTeamBalanceReason)[keyof typeof ManageTeamBalanceReason];

export const ManageTeamBalanceAction = {
  ADD_RECRUITING: 'ADD_RECRUITING',
  ADD_INCENTIVE: 'ADD_INCENTIVE',
  SUBTRACT_RECRUITING: 'SUBTRACT_RECRUITING',
  SUBTRACT_INCENTIVE: 'SUBTRACT_INCENTIVE',
  INTERNAL_TRANSFER: 'INTERNAL_TRANSFER',
  EXTERNAL_TRANSFER_RECRUITING: 'EXTERNAL_TRANSFER_RECRUITING',
  EXTERNAL_TRANSFER_INCENTIVE: 'EXTERNAL_TRANSFER_INCENTIVE',
} as const;

export type ManageTeamBalanceAction =
  (typeof ManageTeamBalanceAction)[keyof typeof ManageTeamBalanceAction];

export type ManagePricingTeamBalanceTransactionDto = {
  action: ManageTeamBalanceAction;
  reason: ManageTeamBalanceReason;
  invoiceId?: string;
  teamId?: ObjectId;
  amount?: number;
  customExpiryInMonths?: number;
};

export type ManagePricingTeamPlanAuditDto = {
  requesterId: string;
  adminId: string;
  comment: string;
};

export type ManagePricingTeamCreateCustomPlanDto = {
  details: ManagePricingTeamPlanAuditDto;
  agreement: ManagePricingPlanAgreementDto;
  rateCard: PricingTeamPlanRateCardDto;
  autoRenew: boolean;
};

export type MethodologyRate = {
  survey: number;
  unmoderatedStudy: number;
  diaryStudy: number;
  focusGroup: number;
  oneOnOne: number;
  inRespondentHome: number;
  inRespondentOffice: number;
  aiModeratedInterview: number;
};

export type RateCardMatrix = {
  b2c: MethodologyRate;
  b2b: MethodologyRate;
};

export type PricingTeamPlanRateCardDto = {
  baseDiscount?: number;
  customRates: boolean;
  matrix?: RateCardMatrix;
};

export type ManagePricingPlanAgreementDto = {
  commitment: number;
  expiresAt?: string;
  baseDiscount?: number;
};

export type BillingHistoryPaginatedDto = {
  /**
   * The total number of results possible, which is only provided in the response when `includeCount` is passed as `true` within a request.
   */
  totalResults?: number;
  page: number;
  pageSize: number;
  results: Array<AugmentedPricingCharge>;
};

export const MasterTransactionType = {
  EXPIRE: 'expire',
  PAYOUT: 'payout',
  PLAN: 'plan',
  PAUSE_PLAN: 'pausePlan',
  UNPAUSE_PLAN: 'unpausePlan',
  PAYG: 'payg',
  TRANSFER_IN: 'transferIn',
  TRANSFER_OUT: 'transferOut',
  RESERVE: 'reserve',
  RELEASE: 'release',
  PROMOTION: 'promotion',
  SUPPORT: 'support',
  CANCEL_CREDIT: 'cancelCredit',
  RESTORE_CREDIT: 'restoreCredit',
  CANCEL_PAYOUT: 'cancelPayout',
  EXTEND_CREDIT: 'extendCredit',
  CREDITCARD: 'creditcard',
  ACCOUNTBALANCE: 'accountbalance',
  BALANCETRANSFER: 'balancetransfer',
} as const;

export type MasterTransactionType =
  (typeof MasterTransactionType)[keyof typeof MasterTransactionType];

export const PricingChargeDetailType = {
  PLAN: 'plan',
  CREDIT: 'credit',
  INCENTIVE: 'incentive',
  PAUSE_PLAN: 'pausePlan',
  MERCHANT_FEE: 'merchantFee',
} as const;

export type PricingChargeDetailType =
  (typeof PricingChargeDetailType)[keyof typeof PricingChargeDetailType];

export type PricingChargeDetail = {
  type: PricingChargeDetailType;
  charge: number;
  amount?: number;
  creditRate?: number;
};

export const PricingChargeStatus = {
  SUCCESS: 'success',
  FAILURE: 'failure',
  PREPAY: 'prepay',
} as const;

export type PricingChargeStatus =
  (typeof PricingChargeStatus)[keyof typeof PricingChargeStatus];

export const PricingChargeType = {
  PLAN: 'plan',
  PAYG: 'payg',
  PAUSE_PLAN: 'pausePlan',
} as const;

export type PricingChargeType =
  (typeof PricingChargeType)[keyof typeof PricingChargeType];

export const DisputeStatus = {
  OPEN: 'Open',
  DISPUTED: 'Disputed',
  WON: 'Won',
  LOST: 'Lost',
  ACCEPTED: 'Accepted',
  EXPIRED: 'Expired',
} as const;

export type DisputeStatus = (typeof DisputeStatus)[keyof typeof DisputeStatus];

export type PaymentDisputeDetails = {
  status: DisputeStatus;
  disputeId: string;
  receivedDate: string;
  lastUpdated: string;
  transactionDate: string;
  amountDisputed: number;
  amountWon: number;
  transactionAmount: number;
  reason: string;
};

export const PaymentStatus = {
  CREATED: 'created',
  PAID: 'paid',
  FAILED: 'failed',
  VOIDED: 'voided',
  REFUNDED: 'refunded',
  DISPUTED: 'disputed',
} as const;

export type PaymentStatus = (typeof PaymentStatus)[keyof typeof PaymentStatus];

export type Payment = {
  /**
   * @deprecated
   */
  transactionFee?: number;
  status?: PaymentStatus;
  disputeDetails?: PaymentDisputeDetails;
  transactionId?: string;
};

export type AugmentedPricingCharge = {
  id: string;
  createdAt: string;
  teamPlanId: string;
  organizationId: string;
  teamId: string;
  masterTransactionId: string;
  referenceId: string;
  payment: Payment;
  type: PricingChargeType;
  status: PricingChargeStatus;
  details: Array<PricingChargeDetail>;
  description?: string;
  masterTransactionType?: MasterTransactionType;
  failureDetails?: string;
  charge: number;
  updatedAt?: string;
};

export type BalancesPaginatedDto = {
  /**
   * The total number of results possible, which is only provided in the response when `includeCount` is passed as `true` within a request.
   */
  totalResults?: number;
  page: number;
  pageSize: number;
  results: Array<AugmentedMasterTransaction>;
};

export type TermsAcceptanceRecord = {
  version: string;
  acceptedAt: string;
};

export type TermsAcceptance = {
  participant?: TermsAcceptanceRecord;
  researcher?: TermsAcceptanceRecord;
};

export type User = {
  id: string;
  createdAt: string;
  email: string;
  emailDomain?: string;
  firstName: string;
  lastName: string;
  isActive: boolean;
  gdprDeletedAt?: string;
  referralCode?: string | null;
  referrerUserId?: string | null;
  referrer?: Referrer;
  timezone: string;
  phone?: string | null;
  picture?: string | null;
  gender?: Gender;
  roles: Array<Roles>;
  organizations: Array<UserOrganization>;
  teams: Array<UserTeam>;
  notificationSettings?: NotificationSettings;
  inviter?: UserInviter;
  messaging?: UserMessaging | null;
  scheduler?: UserScheduler | null;
  customerScheduler?: UserCustomerScheduler | null;
  /**
   * Respondent Only
   */
  isPrivate: boolean;
  consentAt?: string;
  /**
   * Researcher Only
   */
  signupIntent?: string | null;
  source?: string | null;
  /**
   * Researcher Only
   */
  pendingReview?: boolean;
  /**
   * Researcher Only
   */
  approvedBy?: string | null;
  /**
   * Researcher Only
   */
  trustLevel?: number;
  company?: string | null;
  companySize?: string | null;
  jobTitle?: string | null;
  industry?: string | null;
  rms?: UserRms;
  /**
   * SSO identifier from the IdP, if SSO is enabled
   */
  ssoId?: string;
  isEmailVerified: boolean;
  lastLoginAt: string;
  termsAcceptance?: TermsAcceptance;
  /**
   * Researcher Only
   */
  ssoEnabled?: boolean;
  providers: Array<{
    [key: string]: unknown;
  }>;
  experiments?: {
    [key: string]: unknown;
  } | null;
  readNotifications?: {
    [key: string]: unknown;
  };
  contactMetadata?: {
    [key: string]: unknown;
  };
  updatedAt?: string;
};

export type RespondentInRespondentTransaction = {
  /**
   * Absent for an external participant: no name is collected from them.
   */
  firstName?: string;
  /**
   * Absent for an external participant: no name is collected from them.
   */
  lastName?: string;
  userId: ObjectId;
  /**
   * Absent for an invite-link participant, who has no Profile; `userId` is
   * then the only reference to the payee.
   */
  profileId?: ObjectId;
};

export type UserInProject = {
  id: ObjectId;
  firstName: string;
  lastName: string;
};

export type ProjectInRespondentTransaction = {
  id: string;
  userId: string;
  name: string;
  internalName?: string;
  kindOfResearch?: KindOfResearch;
  talkToAudience?: ParticipantAudienceType;
  user?: UserInProject;
};

export type CheckPoint = {
  creditBalance: number;
  creditReserved: number;
  creditCostBalance: number;
  creditCostReserved: number;
  incentiveBalance: number;
  incentiveReserved: number;
};

export type AugmentedMasterTransaction = {
  id: string;
  createdAt: string;
  organizationId: string;
  teamId: string;
  userId?: string;
  projectId?: string;
  targetMasterTransactionId?: string;
  type: MasterTransactionType;
  creditType?: PricingCreditType;
  /**
   * Not included in legacy master transaction
   */
  checkPoint?: CheckPoint;
  note?: string;
  participantType?: ParticipantType;
  creditParticipantType?: ParticipantType;
  pricingCreditType?: PricingCreditType;
  credit?: number;
  creditParticipant?: number;
  creditBalance?: number;
  creditBalanceParticipant?: number;
  incentiveBalance?: number;
  description?: string;
  project?: ProjectInRespondentTransaction;
  respondent?: RespondentInRespondentTransaction;
  tip?: number;
  total?: number;
  creditCost?: number;
  pricingCreditAmount?: number;
  pricingCreditCost?: number;
  pricingCreditUnitCost?: number;
  user?: User;
  incentive?: number;
  targetTransferToTeamName?: string;
  targetTransferFromTeamName?: string;
  projectUrl?: string;
  amount: number;
  updatedAt?: string;
};

export type PricingCreditsExpirySummaryPaginatedDto = {
  /**
   * The total number of results possible, which is only provided in the response when `includeCount` is passed as `true` within a request.
   */
  totalResults?: number;
  page: number;
  pageSize: number;
  results: Array<PricingCreditExpiringDto>;
};

export type PricingCreditTransfer = {
  id: string;
  createdAt: string;
  creditId: string;
  teamId: string;
  organizationId: string;
  /**
   * if incentive then its in USD cents
   */
  amount: number;
  transferredAt: string;
  updatedAt?: string;
};

export type PricingCreditConsumer = {
  id: string;
  createdAt: string;
  projectId: string;
  surveyResponseIds: Array<string>;
  /**
   * if incentive then its in USD cents
   */
  amount: number;
  updatedAt?: string;
};

export type PricingCreditReservation = {
  id: string;
  createdAt: string;
  projectId: string;
  /**
   * if incentive then its in USD cents
   */
  amount: number;
  updatedAt?: string;
};

export const PricingCreditIssuedType = {
  PLAN: 'plan',
  PAYG: 'payg',
  SUPPORT: 'support',
  PROMOTION: 'promotion',
  TRANSFER: 'transfer',
} as const;

export type PricingCreditIssuedType =
  (typeof PricingCreditIssuedType)[keyof typeof PricingCreditIssuedType];

export type PricingCreditExpiringDto = {
  id: string;
  createdAt: string;
  organizationId: string;
  teamId: string;
  teamPlanId: string;
  masterTransactionId: string;
  chargeId?: string;
  /**
   * Optional note from system or CS
   */
  note?: string;
  type: PricingCreditType;
  /**
   * if incentive then its in USD cents
   */
  amount: number;
  /**
   * cost in USD cents for all the credits, if type is incentive then it should be the same as amount
   */
  cost: number;
  /**
   * this is the unit cost in USD cents of each credit, if type is incentive then it should be 1
   */
  unitCost: number;
  /**
   * starts as amount and should eventually go to 0
   */
  available: number;
  reserved: number;
  /**
   * can be null for incentive
   */
  expiresAt?: string;
  /**
   * can be null for incentive
   */
  expired?: number;
  expiryReminderSent?: boolean;
  reservedExpiryReminderSent?: boolean;
  /**
   * amount of credits cancelled
   */
  cancelled?: number;
  issued: PricingCreditIssuedType;
  issuedAt: string;
  /**
   * only set if credits provided by a promo
   */
  promotionId?: string;
  /**
   * for filtering convenience, controlled by plan
   */
  paused: boolean;
  reservations: Array<PricingCreditReservation>;
  consumers: Array<PricingCreditConsumer>;
  transfers: Array<PricingCreditTransfer>;
  pausedDays?: number;
  updatedAt?: string;
  renewalExtension: boolean;
};

export type RestorePricingCreditDto = {
  amount: number;
  note: string;
  monthsToExtend: number;
};

export type ExtendPricingCreditDto = {
  monthsToExtend: number;
  note: string;
};

export type CancelPricingCreditDto = {
  amount: number;
  note: string;
};

export type AtlasListOfTriggersDto = {
  triggerIds: Array<string>;
};

export type AtlasListOfTriggersOptionalDto = {
  triggerIds?: Array<string>;
};

export type RunRepairActionResponseDto = {
  results: {
    [key: string]: unknown;
  };
};

export type RunRepairActiontDto = {
  action: string;
  config: {
    [key: string]: unknown;
  };
};

export type PrequalifiesForProjectResultsDto = {
  result?: PrequalificationResult;
  disqualificationReason?: ProjectDisqualificationReason;
};

export const KindOfResearchStudy = {
  AI_INTERVIEW: 'ai-interview',
  VIDEO_CALL: 'video-call',
  VIDEO_CALL_GROUP: 'video-call-group',
  IN_PERSON: 'in-person',
  IN_PERSON_GROUP: 'in-person-group',
  ONLINE_TASK: 'online-task',
  SURVEY: 'survey',
  DIARY_STUDY: 'diary-study',
  VISIT_HOME: 'visit-home',
  VISIT_OFFICE: 'visit-office',
} as const;

export type KindOfResearchStudy =
  (typeof KindOfResearchStudy)[keyof typeof KindOfResearchStudy];

export type UpdateManyRespondentPayoutsDto = {
  status?: RespondentPayoutStatus;
  metadata?: PaypalPayoutMetadata;
  paypalMetadata?: PaypalPayoutMetadata;
  tremendousMetadata?: TremendousPayoutMetadata;
  respondentPayoutIds: Array<string>;
};

export const TremendousPayoutMetadataStatus = {
  CANCELED: 'CANCELED',
  EXECUTED: 'EXECUTED',
  FAILED: 'FAILED',
  CART: 'CART',
  PENDING_APPROVAL: 'PENDING APPROVAL',
  PENDING_INTERNAL_PAYMENT_APPROVAL: 'PENDING INTERNAL PAYMENT APPROVAL',
} as const;

export type TremendousPayoutMetadataStatus =
  (typeof TremendousPayoutMetadataStatus)[keyof typeof TremendousPayoutMetadataStatus];

export const TremendousPayoutMetadataPaymentMethod = {
  EMAIL: 'EMAIL',
  LINK: 'LINK',
  PHONE: 'PHONE',
} as const;

export type TremendousPayoutMetadataPaymentMethod =
  (typeof TremendousPayoutMetadataPaymentMethod)[keyof typeof TremendousPayoutMetadataPaymentMethod];

export type TremendousPayoutMetadata = {
  payoutItemId?: string;
  orderId?: string;
  rewardId?: string;
  retryCount?: number;
  paymentProcessedAt?: string;
  paymentMethod?: TremendousPayoutMetadataPaymentMethod;
  recipientEmail?: string;
  subtotal?: number;
  total?: number;
  fees?: number;
  discount?: number;
  orderStatus?: TremendousPayoutMetadataStatus;
};

export type PaypalPayoutLink = {
  href: string;
  rel: string;
  method: string;
  encType?: string;
};

export type PaypalPayoutErrorDetails = {
  field?: string;
  value?: string;
  location?: string;
  issue?: string;
  description?: string;
};

export type PaypalPayoutError = {
  message: string;
  name?: string;
  debug_id?: string;
  information_link?: string;
  details?: Array<PaypalPayoutErrorDetails>;
  links?: Array<PaypalPayoutLink>;
};

export type PaypalPayoutRecipientName = {
  prefix?: string;
  given_name?: string;
  surname?: string;
  middle_name?: string;
  suffix?: string;
  full_name?: string;
};

export type PaypalPayoutAmount = {
  currency: string;
  value: number;
};

export type PaypalPayoutItem = {
  note: string;
  receiver: string;
  sender_item_id: string;
  recipient_type: string;
  amount: PaypalPayoutAmount;
  recipient_wallet: string;
  purpose: string;
  recipient_name?: PaypalPayoutRecipientName;
};

export const RespondentPayoutMetadataStatus = {
  SUCCESS: 'SUCCESS',
  UNCLAIMED: 'UNCLAIMED',
  FAILED: 'FAILED',
  RETURNED: 'RETURNED',
  ONHOLD: 'ONHOLD',
  VOIDED: 'VOIDED',
  N_A: 'N/A',
  REFUNDED: 'REFUNDED',
} as const;

export type RespondentPayoutMetadataStatus =
  (typeof RespondentPayoutMetadataStatus)[keyof typeof RespondentPayoutMetadataStatus];

export type PaypalPayoutMetadata = {
  payoutItemId?: string;
  payoutItemFee?: number;
  payoutBatchId?: string;
  transactionId?: string;
  transactionStatus?: RespondentPayoutMetadataStatus;
  paymentBatchReferenceId?: string;
  timeProcessed?: string;
  batchId?: string;
  item?: PaypalPayoutItem;
  errors?: PaypalPayoutError;
};

export type UpdateRespondentPayoutsDto = {
  status?: RespondentPayoutStatus;
  metadata?: PaypalPayoutMetadata;
  paypalMetadata?: PaypalPayoutMetadata;
  tremendousMetadata?: TremendousPayoutMetadata;
};

export type UpdateManyPaymentBatchDto = {
  status?: PaymentBatchStatus;
  retriesCount?: number;
  retryPayPalCount?: number;
  completedAt?: string;
  payoutBatchId?: string;
  response?: {
    [key: string]: unknown;
  };
  processingAt?: string;
  payout?: PaymentBatchPayout;
  paymentBatchIds: Array<string>;
};

export const PaymentBatchPayoutStatus = {
  MANUAL: 'MANUAL',
  COMPLETED: 'COMPLETED',
  PENDING: 'PENDING',
  PROCESSING: 'PROCESSING',
  REJECTED: 'REJECTED',
  ERROR: 'ERROR',
} as const;

export type PaymentBatchPayoutStatus =
  (typeof PaymentBatchPayoutStatus)[keyof typeof PaymentBatchPayoutStatus];

export type PaymentBatchPayout = {
  status: PaymentBatchPayoutStatus;
  fees?: number;
  currency?: string;
  payload?: {
    [key: string]: unknown;
  };
};

export const PaymentBatchStatus = {
  PENDING: 'PENDING',
  PROCESSING: 'PROCESSING',
  COMPLETED: 'COMPLETED',
  ERROR: 'ERROR',
  FAILED: 'FAILED',
} as const;

export type PaymentBatchStatus =
  (typeof PaymentBatchStatus)[keyof typeof PaymentBatchStatus];

export type UpdatePaymentBatchDto = {
  status?: PaymentBatchStatus;
  retriesCount?: number;
  retryPayPalCount?: number;
  completedAt?: string;
  payoutBatchId?: string;
  response?: {
    [key: string]: unknown;
  };
  processingAt?: string;
  payout?: PaymentBatchPayout;
};

export type PendingRespondentPayoutsDto = {
  origin: PaymentBatchOrigin;
  paymentProcessingSystem: PaymentProcessingSystem;
  /**
   * Payment cutoff date for which to get pending payments for project participation.
   * Not valid for referral payments.
   */
  paymentCutoffDate: string;
  maxPayouts?: number;
};

export const PaymentProcessingSystem = {
  PAYPAL: 'PAYPAL',
  TREMENDOUS: 'TREMENDOUS',
} as const;

export type PaymentProcessingSystem =
  (typeof PaymentProcessingSystem)[keyof typeof PaymentProcessingSystem];

export const PaymentBatchOrigin = {
  PARTICIPATION: 'participation',
  REFERRAL: 'referral',
} as const;

export type PaymentBatchOrigin =
  (typeof PaymentBatchOrigin)[keyof typeof PaymentBatchOrigin];

export type CreateBulkPaymentBatchesDto = {
  origin: PaymentBatchOrigin;
  referralType?: ReferralType;
  /**
   * Payment cutoff date for which to create payment batches for project participation.
   * Not valid for referral payments.
   */
  paymentCutoffDate?: string;
};

export type PaymentBatch = {
  id: string;
  createdAt: string;
  status: PaymentBatchStatus;
  origin: PaymentBatchOrigin;
  referralType?: ReferralType;
  referenceId: string;
  surveyResponseId?: string;
  type: PaymentBatchType;
  retriesCount?: number;
  retryPayPalCount?: number;
  completedAt?: string;
  payoutBatchId?: string;
  response?: {
    [key: string]: unknown;
  };
  version?: number;
  totalAmount: number;
  totalCommission: number;
  processingAt?: string;
  processAt?: string;
  payout?: PaymentBatchPayout;
  note?: string;
  items: Array<PaymentBatchItem>;
  updatedAt?: string;
};

export type PaymentBatchItemProject = {
  id: string;
  name: string;
  referralBonus?: number;
};

export type PaymentBatchItemUser = {
  firstName: string;
  lastName: string;
  userId?: string;
};

export type PaymentBatchItem = {
  id: string;
  userId: string;
  respondentPayoutId?: string;
  amount: number;
  email: string;
  paypal?: string | null;
  user: PaymentBatchItemUser;
  referree?: PaymentBatchItemUser;
  surveyResponseId: string;
  project: PaymentBatchItemProject;
};

export const PaymentBatchType = {
  SINGLE: 'single',
  MULTIPLE: 'multiple',
} as const;

export type PaymentBatchType =
  (typeof PaymentBatchType)[keyof typeof PaymentBatchType];

export type UpdateGdprDeletionDto = {
  status?: GdprDeletionStatus;
  deletedAt?: string;
  updatedAt?: string;
};

export const GdprDeletionStatus = {
  REQUEST: 'request',
  COMPLETE: 'complete',
  ERROR: 'error',
  CANCELLED: 'cancelled',
} as const;

export type GdprDeletionStatus =
  (typeof GdprDeletionStatus)[keyof typeof GdprDeletionStatus];

export type CreateGdprDeletionDto = {
  requestUserId?: string;
  userId: string;
  isResearcher?: boolean;
  status?: GdprDeletionStatus;
  deletedAt?: string;
  updatedAt?: string;
};

export type GdprDeletionsPaginatedDto = {
  /**
   * The total number of results possible, which is only provided in the response when `includeCount` is passed as `true` within a request.
   */
  totalResults?: number;
  page: number;
  pageSize: number;
  results: Array<GdprDeletion>;
};

export type GdprDeletion = {
  id: string;
  createdAt: string;
  requestUserId?: string;
  userId: string;
  isResearcher?: boolean;
  status?: GdprDeletionStatus;
  deletedAt?: string;
  updatedAt?: string;
};

export type AddForbiddenWorkDomainDto = {
  domain: string;
  matchType: ForbiddenWorkDomainMatchType;
  reason?: string;
};

export const ForbiddenWorkDomainMatchType = {
  EXACT: 'exact',
  SELF_AND_SUBDOMAINS: 'self_and_subdomains',
  TLD: 'tld',
} as const;

export type ForbiddenWorkDomainMatchType =
  (typeof ForbiddenWorkDomainMatchType)[keyof typeof ForbiddenWorkDomainMatchType];

export type UpdateFlaggedRespondentDto = {
  surveyResponseId?: string;
  profileId?: string;
  organizationId?: string;
  teamId?: string;
  reasons?: Array<FlaggedReason>;
  message?: string;
  projectId?: string;
  userId?: string;
};

export const FlagReasonDescription = {
  PARTICIPANT_INTENTIONALLY_GAVE_FALSE_OR_MISLEADING_INFORMATION:
    'Participant intentionally gave false or misleading information',
  INAPPROPRIATE_LANGUAGE_OR_BEHAVIOR: 'Inappropriate language or behavior',
  ATTENDED_THE_PROJECT_LATE_OR_NOT_AT_ALL:
    'Attended the project late or not at all',
  PROJECT_TASK_WAS_NOT_COMPLETED: 'Project task was not completed',
  OTHER: 'Other',
} as const;

export type FlagReasonDescription =
  (typeof FlagReasonDescription)[keyof typeof FlagReasonDescription];

export const FlagReason = {
  MISREPRESENTED: 'MISREPRESENTED',
  INAPPROPRIATE: 'INAPPROPRIATE',
  INVALIDRESPONSE: 'INVALIDRESPONSE',
  NOSHOW: 'NOSHOW',
  OTHER: 'OTHER',
} as const;

export type FlagReason = (typeof FlagReason)[keyof typeof FlagReason];

export type FlaggedReason = {
  code: FlagReason;
  text: FlagReasonDescription;
};

export type CreateFlaggedRespondentDto = {
  surveyResponseId: string;
  profileId: string;
  organizationId: string;
  teamId: string;
  reasons: Array<FlaggedReason>;
  message: string;
  projectId: string;
  userId: string;
};

export type FlaggedRespondent = {
  id: string;
  createdAt: string;
  surveyResponseId: string;
  profileId: string;
  organizationId: string;
  teamId: string;
  reasons: Array<FlaggedReason>;
  message: string;
  projectId: string;
  userId: string;
  updatedAt?: string;
};

export type FeedbacksPaginatedDto = {
  /**
   * The total number of results possible, which is only provided in the response when `includeCount` is passed as `true` within a request.
   */
  totalResults?: number;
  page: number;
  pageSize: number;
  results: Array<FeedbackDto>;
};

export const FeedbackContext = {
  AI_MOD_PARTICIPANT_COMPLETION: 'ai_mod_participant_completion',
  RESEARCHER_PROJECT_PUBLISH: 'researcher_project_publish',
  RESEARCHER_PARTICIPANT_LIMIT_REQUEST: 'researcher_participant_limit_request',
} as const;

export type FeedbackContext =
  (typeof FeedbackContext)[keyof typeof FeedbackContext];

export type FeedbackDto = {
  id: string;
  userId: string;
  context: FeedbackContext;
  projectId?: string;
  interviewResponseId?: string;
  rating?: number;
  requestedParticipants?: number;
  themes: Array<string>;
  freeText?: string;
  /**
   * Email of the user who left the feedback. Only populated on the admin list endpoint.
   */
  userEmail?: string;
  createdAt: string;
  updatedAt: string;
};

export type UpdateFeedbackDto = {
  rating?: number;
  themes?: Array<string>;
  freeText?: string;
};

export type CreateFeedbackDto = {
  context: FeedbackContext;
  projectId?: string;
  interviewResponseId?: string;
  rating?: number;
  requestedParticipants?: number;
  themes?: Array<string>;
  freeText?: string;
};

export type EmailQueueNewMessageAvailableDto = {
  organizationId: string;
  teamId: string;
  projectId: string;
  receiverUserId: string;
  senderUserId: string;
  message: string;
};

export type QueueEmailForSendingDto = {
  template: string;
  to: Array<string>;
  bcc?: Array<string>;
  subject: string;
  projectId: string;
  locals: {
    [key: string]: unknown;
  };
  customMetadata?: {
    [key: string]: unknown;
  };
};

export type CspReportDto = {
  'csp-report': CspReport;
  type?: string;
  url?: string;
};

export type CspReport = {
  /**
   * URL of the resource that violated the policy.
   */
  url?: string;
  /**
   * URL of document violation occurred
   */
  'document-uri'?: string;
  /**
   * URI of resource that was blocked from loading
   */
  'blocked-uri'?: string;
  /**
   * HTTP status code of the resouce that violated the
   * policy, or 0 if the resource was blocked before fetching
   */
  'status-code'?: number;
  /**
   * Directive effective in enforcing the CSP policy, which
   * might be different from the violated-directive when
   * multiple directives apply
   */
  'effective-directive'?: string;
  /**
   * Specific directive of the CSP policy that was violated
   */
  'violated-directive'?: string;
  /**
   * File in which the violation occured, useful for script or
   * style violations
   */
  'source-file'?: string;
  'line-number'?: number;
  'column-number'?: number;
  /**
   * Indicates whether the violation report is sent in "enforce"
   * or "report-only" mode
   */
  disposition?: string;
  /**
   * Entire original CSP policy as specified in the
   * Content-Security-Policy HTTP header
   */
  'original-policy'?: string;
  /**
   * @deprecated
   */
  referrer?: {
    [key: string]: unknown;
  };
  /**
   * Sample of the inline script, style, or other resource that
   * caused the violation
   */
  'script-sample'?: {
    [key: string]: unknown;
  };
};

export type AtlasAppSyncResult = {
  functions?: FunctionsSyncResult;
  triggers?: TriggersSyncResult;
  searchIndexes?: SearchIndexesSyncResult;
  mongoIndexes?: MongoIndexesSyncResult;
};

export type MongoIndexSkipped = {
  definition: MongoIndexDefinition;
  type: string;
  error: string;
};

export type IndexCollation = {
  alternate: string;
  backwards: boolean;
  caseFirst: string;
  caseLevel: boolean;
  locale: string;
  maxVariable: string;
  normalization: boolean;
  numericOrdering: boolean;
  strength: number;
};

export type IndexOptions = {
  '2dsphereIndexVersion'?: number;
  background?: boolean;
  bits?: number;
  bucketSize?: number;
  default_language?: string;
  expireAfterSeconds?: number;
  hidden?: boolean;
  language_override?: string;
  max?: number;
  min?: number;
  name?: string;
  partialFilterExpression?: {
    [key: string]: unknown;
  };
  sparse?: boolean;
  storageEngine?: {
    [key: string]: unknown;
  };
  textIndexVersion?: number;
  unique?: boolean;
  weights?: {
    [key: string]: unknown;
  };
};

export type MongoIndexDefinition = {
  name: string;
  keys: {
    [key: string]: unknown;
  };
  options?: IndexOptions;
  collection: string;
  collation?: IndexCollation;
};

export type MongoIndexesSyncResult = {
  mongoIndexesNotDefinedInCode: Array<MongoIndexDefinition>;
  mongoIndexesUpdated: Array<MongoIndexDefinition>;
  mongoIndexesCreated: Array<MongoIndexDefinition>;
  mongoIndexesDeleted: Array<MongoIndexDefinition>;
  mongoIndexesSkipped: Array<MongoIndexSkipped>;
};

export type SearchIndexSkipped = {
  definition: AtlasSearchIndexDefinition;
  type: string;
  error: string;
};

export type Synonym = {
  name: string;
  source: {
    collection?: string;
  };
  analyzer: Analyzer;
};

export type TokenFilter = {
  type:
    | 'asciiFolding'
    | 'daitchMokotoffSoundex'
    | 'edgeGram'
    | 'icuFolding'
    | 'icuNormalizer'
    | 'length'
    | 'lowercase'
    | 'nGram'
    | 'regex'
    | 'reverse'
    | 'shingle'
    | 'snowballStemming'
    | 'stopword'
    | 'trim';
};

export type Tokenizer = {
  type:
    | 'edgeGram'
    | 'keyword'
    | 'nGram'
    | 'regexCaptureGroup'
    | 'regexSplit'
    | 'standard'
    | 'uaxUrlEmail'
    | 'whitespace';
  maxTokenLength?: number;
  minGram?: number;
  maxGram?: number;
};

export type CharFilter = {
  type: 'htmlStrip' | 'icuNormalize' | 'mapping' | 'persian';
};

export type Analyzer = {
  name: string;
  charFilters?: Array<CharFilter>;
  tokenizer: Tokenizer;
  tokenFilters?: Array<TokenFilter>;
};

export type Mapping = {
  dynamic: boolean;
  fields?: {
    [key: string]: unknown;
  };
};

export type AtlasSearchIndexDefinition = {
  collectionName: string;
  name: string;
  mappings: Mapping;
  analyzer?: {
    [key: string]: unknown;
  };
  analyzers?: Array<Analyzer>;
  searchAnalyzer?: {
    [key: string]: unknown;
  };
  synonyms?: Array<Synonym>;
};

export type SearchIndexesSyncResult = {
  searchIndexesNotDefinedInCode: Array<AtlasSearchIndexDefinition>;
  searchIndexesUpdated: Array<AtlasSearchIndexDefinition>;
  searchIndexesCreated: Array<AtlasSearchIndexDefinition>;
  searchIndexesDeleted: Array<AtlasSearchIndexDefinition>;
  searchIndexesSkipped: Array<SearchIndexSkipped>;
};

export type TriggerSkipped = {
  definition: TriggerDefinition;
  type: string;
  error: string;
};

export type ErrorHandlerConfigDefiniton = {
  enabled: boolean;
  functionName: string;
};

export type ErrorHandlerDefinition = {
  config: ErrorHandlerConfigDefiniton;
};

export type TriggerDefinition = {
  config: {
    [key: string]: unknown;
  };
  error_handler?: ErrorHandlerDefinition;
  name: string;
  type: 'DATABASE' | 'AUTHENTICATION' | 'SCHEDULED';
  disabled?: boolean;
  function_name?: string;
  event_processors?: {
    [key: string]: unknown;
  };
};

export type TriggersSyncResult = {
  triggersNotDefinedInCode: Array<TriggerDefinition>;
  /**
   * Will return trigger as defined in atlas before it was updated.
   */
  triggersUpdated: Array<TriggerDefinition>;
  triggersCreated: Array<TriggerDefinition>;
  triggersDeleted: Array<TriggerDefinition>;
  triggersSkipped: Array<TriggerSkipped>;
};

export type FunctionSkipped = {
  definition: FunctionDefinition;
  type: string;
  error: string;
};

export type FunctionDefinition = {
  name: string;
  can_evaluate?: {
    [key: string]: unknown;
  };
  private: boolean;
  source: string;
  run_as_system?: boolean;
};

export type FunctionsSyncResult = {
  functionsNotDefinedInCode: Array<FunctionDefinition>;
  functionsUpdated: Array<FunctionDefinition>;
  functionsCreated: Array<FunctionDefinition>;
  functionsDeleted: Array<FunctionDefinition>;
  functionsSkipped: Array<FunctionSkipped>;
};

export type SyncAtlasAppDto = {
  /**
   * The assets to sync.
   */
  assets?: Array<AtlasAppSyncAssets>;
  /**
   * If true, the sync will be committed. If false, the sync will be a dry run.
   */
  commit?: boolean;
  /**
   * If true, the sync will delete any assets found in atlas that are not in the codebase definitions.
   * Currently only applies to mongo indexes.
   */
  deleteAssetsNotDefinedInCode?: boolean;
  /**
   * If true, the sync will stop after the first error.
   */
  stopAfterFirstError?: boolean;
  /**
   * If true, the sync will stop after the first change. (If commit = false, this will stop after what would be the first change)
   */
  stopAfterFirstChange?: boolean;
};

export const AtlasAppSyncAssets = {
  FUNCTIONS: 'functions',
  TRIGGERS: 'triggers',
  SEARCH_INDEXES: 'search-indexes',
  MONGO_INDEXES: 'mongo-indexes',
} as const;

export type AtlasAppSyncAssets =
  (typeof AtlasAppSyncAssets)[keyof typeof AtlasAppSyncAssets];

export type UpdateLeadDto = {
  email?: string;
  firstName?: string;
  lastName?: string;
  jobTitle?: string | null;
  jobTitleId?: string | null;
  company?: string | null;
  linkedinUrl?: string | null;
  phone?: string | null;
  sourceId?: string | null;
  campaignId?: string | null;
};

export type ConsumeLeadDto = {
  email: string;
  employmentStatus?: EmploymentStatus;
  jobTitle?: string;
  company?: string;
  /**
   * verification flow. Kept so clients that still send it are not rejected.
   *
   * @deprecated
   */
  linkedinUrl?: string;
};

export type ValidatedLeadDto = {
  jobTitle?: string;
  jobTitleId?: string;
  company?: string;
  linkedinUrl?: string;
  phone?: string;
};

export type ValidateLeadDto = {
  email: string;
};

export type UtmTrackedResponsesPaginatedDto = {
  /**
   * The total number of results possible, which is only provided in the response when `includeCount` is passed as `true` within a request.
   */
  totalResults?: number;
  page: number;
  pageSize: number;
  results: Array<UtmTrackedResponseDto>;
};

export type UtmTrackedResponseDto = {
  screenerResponseId: string;
  projectId: string;
  userId: string;
  status: ScreenerResponseStatus;
  qualified?: boolean;
  visibility: ScreenerResponseVisibility;
  createdAt: string;
  updatedAt: string;
  initialUtm: InitialUtm;
};

export type UtmTrackedUsersPaginatedDto = {
  /**
   * The total number of results possible, which is only provided in the response when `includeCount` is passed as `true` within a request.
   */
  totalResults?: number;
  page: number;
  pageSize: number;
  results: Array<UtmTrackedUserDto>;
};

export type UtmTrackedUserDto = {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  signedUpAt: string;
  updatedAt: string;
  initialUtm: InitialUtm;
};

export type LeadsPaginatedDto = {
  /**
   * The total number of results possible, which is only provided in the response when `includeCount` is passed as `true` within a request.
   */
  totalResults?: number;
  page: number;
  pageSize: number;
  results: Array<Lead>;
};

export type Lead = {
  id: string;
  createdAt: string;
  leadId: string;
  status: LeadStatus;
  email: string;
  firstName: string;
  lastName: string;
  jobTitle?: string;
  jobTitleId?: string;
  company?: string;
  linkedinUrl?: string;
  phone?: string;
  sourceId?: string;
  campaignId?: string;
  updatedAt?: string;
};

export type CreateLeadsBulkDto = {
  leads: Array<CreateLeadDto>;
};

export type CreateLeadDto = {
  leadId?: string;
  email: string;
  firstName: string;
  lastName: string;
  jobTitle?: string;
  jobTitleId?: string;
  company?: string;
  linkedinUrl?: string;
  phone?: string;
  sourceId?: string;
  campaignId?: string;
};

export type SecurityCheckAttemptResponseDto = {
  securityCheckAttempts: number;
};

export type RejectDomainProfilesResponseDto = {
  rejectedProfilesCount: number;
  /**
   * Pending screener responses hidden from researcher lists by this call.
   */
  dismissedResponsesCount: number;
};

export type MissingDomainToProcessDto = {
  domain: string;
  websiteCheckedAt?: string;
};

export type MissingDomainReviewDto = {
  missingCompanyDomain: MissingCompanyDomain;
  /**
   * Carries clearbitCompanyData; unset when no company record exists yet — a
   * domain held for review has none on purpose (see
   * missingCompanyDomain.verification.clearbit for Clearbit's snapshot).
   */
  company?: Company;
  /**
   * Newest first, capped server-side.
   */
  profiles?: Array<MissingDomainReviewProfileDto>;
};

export type MissingDomainReviewProfileDto = {
  profileId: string;
  userId?: string;
  firstName?: string;
  lastName?: string;
  status?: RespondentStatus;
  createdAt?: string;
  employment?: ProfileEmployment;
};

export type Company = {
  id: string;
  createdAt: string;
  name: string;
  domain: string;
  domainAliases?: Array<string>;
  trusted?: boolean;
  description?: string;
  phone?: string;
  logo?: string;
  size?: number;
  tags?: Array<string>;
  techTags?: Array<string>;
  industries?: Array<string>;
  annualRevenue?: number;
  clearbitCompanyData?: {
    [key: string]: unknown;
  };
  clearbitUpdatedAt?: string;
  updatedAt?: string;
};

export const VerificationAnswer = {
  GREEN: 'GREEN',
  AMBER: 'AMBER',
  RED: 'RED',
  GRAY: 'GRAY',
} as const;

export type VerificationAnswer =
  (typeof VerificationAnswer)[keyof typeof VerificationAnswer];

export const WebsiteCheckVerdict = {
  PASS: 'PASS',
  FAIL: 'FAIL',
  INCONCLUSIVE: 'INCONCLUSIVE',
} as const;

export type WebsiteCheckVerdict =
  (typeof WebsiteCheckVerdict)[keyof typeof WebsiteCheckVerdict];

export type MissingCompanyDomainWebsiteVerification = {
  verdict: WebsiteCheckVerdict;
  exists: VerificationAnswer;
  legitimate: VerificationAnswer;
  peopleWorkThere: VerificationAnswer;
  /**
   * Legitimacy red flags raised, as the labels the admin screen shows.
   */
  redFlags: Array<string>;
  /**
   * `websiteCheck.checkedAt` of the check this was assessed from; unset when never checked.
   */
  checkedAt?: string;
  checkVersion?: number;
};

export const ClearbitLookupStatus = {
  SUCCESS: 'SUCCESS',
  NOTFOUND: 'NOTFOUND',
  INVALID: 'INVALID',
} as const;

export type ClearbitLookupStatus =
  (typeof ClearbitLookupStatus)[keyof typeof ClearbitLookupStatus];

export const ClearbitVerdict = { PASS: 'PASS', FAIL: 'FAIL' } as const;

export type ClearbitVerdict =
  (typeof ClearbitVerdict)[keyof typeof ClearbitVerdict];

export type MissingCompanyDomainClearbitVerification = {
  verdict: ClearbitVerdict;
  status: ClearbitLookupStatus;
  /**
   * Clearbit's company name, when it found one.
   */
  companyName?: string;
  /**
   * Clearbit's full payload, kept here (not on a Company record) when the
   * domain is held for review: an existing Company auto-trusts later signups,
   * so it is only created once the domain is approved — from this snapshot.
   */
  companyData?: {
    [key: string]: unknown;
  };
};

export const VerificationDecision = {
  AUTO_APPROVED: 'AUTO_APPROVED',
  HELD_FOR_REVIEW: 'HELD_FOR_REVIEW',
  RESCUED_BY_WEBSITE: 'RESCUED_BY_WEBSITE',
  AUTO_REJECTED: 'AUTO_REJECTED',
  DEFAULT: 'DEFAULT',
} as const;

export type VerificationDecision =
  (typeof VerificationDecision)[keyof typeof VerificationDecision];

export type MissingCompanyDomainVerification = {
  /**
   * Server-set.
   */
  checkedAt: string;
  /**
   * Whether the decision below set the domain's status. False = shadow mode:
   * the flag was off, so the pre-existing Clearbit-only status was applied
   * and the decision is recorded for comparison only.
   */
  gatingApplied: boolean;
  decision: VerificationDecision;
  /**
   * Why a Clearbit pass was held for review; empty for every other decision.
   */
  blockers: Array<string>;
  clearbit: MissingCompanyDomainClearbitVerification;
  website: MissingCompanyDomainWebsiteVerification;
};

export type WebsiteCheckJsonLdOrganization = {
  /**
   * The node's schema.org @type value(s), verbatim.
   */
  types: Array<string>;
  name?: string;
  url?: string;
  /**
   * Declared social/profile links — corroboration for signals.socialLinks.
   */
  sameAs?: Array<string>;
};

export const ContactEmailSource = {
  HOMEPAGE: 'HOMEPAGE',
  CONTACT_PAGE: 'CONTACT_PAGE',
} as const;

export type ContactEmailSource =
  (typeof ContactEmailSource)[keyof typeof ContactEmailSource];

export const ContactEmailClassification = {
  SAME_DOMAIN: 'SAME_DOMAIN',
  FREEMAIL: 'FREEMAIL',
  OTHER: 'OTHER',
} as const;

export type ContactEmailClassification =
  (typeof ContactEmailClassification)[keyof typeof ContactEmailClassification];

export type WebsiteCheckContactEmail = {
  address: string;
  classification: ContactEmailClassification;
};

export type WebsiteCheckContact = {
  emails?: Array<WebsiteCheckContactEmail>;
  source?: ContactEmailSource;
  /**
   * The contact-ish page that was fetched (whether or not it had emails).
   */
  contactPageUrl?: string;
};

export const WebsiteCheckLlmClassification = {
  ACTIVE_COMPANY_SITE: 'ACTIVE_COMPANY_SITE',
  PARKED: 'PARKED',
  UNDER_CONSTRUCTION: 'UNDER_CONSTRUCTION',
  PLACEHOLDER_TEMPLATE: 'PLACEHOLDER_TEMPLATE',
  OTHER: 'OTHER',
} as const;

export type WebsiteCheckLlmClassification =
  (typeof WebsiteCheckLlmClassification)[keyof typeof WebsiteCheckLlmClassification];

export type WebsiteCheckLlmJudgement = {
  classification: WebsiteCheckLlmClassification;
  /**
   * Model-reported confidence, 0..1.
   */
  confidence: number;
  /**
   * Short verbatim quotes from the page supporting the classification.
   */
  evidence?: Array<string>;
  /**
   * Which model produced the judgement.
   */
  model?: string;
};

export type WebsiteCheckLinkStats = {
  checked: number;
  broken: number;
  /**
   * Bot-shield answers (403/429/503) — neither working nor broken.
   */
  unknown: number;
  brokenUrls?: Array<string>;
};

export const SocialPlatform = {
  LINKEDIN: 'LINKEDIN',
  FACEBOOK: 'FACEBOOK',
  TWITTER: 'TWITTER',
  INSTAGRAM: 'INSTAGRAM',
  YOUTUBE: 'YOUTUBE',
  TIKTOK: 'TIKTOK',
} as const;

export type SocialPlatform =
  (typeof SocialPlatform)[keyof typeof SocialPlatform];

export type WebsiteCheckSocialLink = {
  platform: SocialPlatform;
  urls: Array<string>;
};

export type WebsiteCheckSignals = {
  title?: string;
  /**
   * Verbatim <meta name="generator"> content — the most honest builder tell.
   */
  generatorMeta?: string;
  siteBuilders?: Array<string>;
  /**
   * Which placeholder phrases hit, not just whether any did.
   */
  placeholderPhrases?: Array<string>;
  underConstructionPhrases?: Array<string>;
  parkedPhrases?: Array<string>;
  /**
   * How much text the page actually shows — stubs are one short screen.
   */
  visibleTextChars: number;
  /**
   * The <title> is just the domain itself — a default-page tell.
   */
  titleIsDomain: boolean;
  looksLikeStub: boolean;
  /**
   * Near-empty HTML rendered by JS (SPA): the other signals are incomplete.
   * The rate of these decides whether a rendering API is warranted.
   */
  isJsRenderedShell: boolean;
  socialLinks?: Array<WebsiteCheckSocialLink>;
  /**
   * Social links that are template filler rather than presence: a platform's
   * bare root, share/intent/login URLs, an unfilled profile prefix, or social
   * icons pointing back at the site itself. Having no social links at all is
   * fine; having social icons that were never wired up is a façade tell.
   */
  placeholderSocialLinks?: Array<string>;
  links: WebsiteCheckLinkStats;
  /**
   * Only present when the page was ambiguous enough to ask the LLM judge.
   */
  llmJudgement?: WebsiteCheckLlmJudgement;
};

export const WebsiteCheckOutcome = {
  FOUND: 'FOUND',
  HTTP_ERROR: 'HTTP_ERROR',
  NOT_FOUND: 'NOT_FOUND',
} as const;

export type WebsiteCheckOutcome =
  (typeof WebsiteCheckOutcome)[keyof typeof WebsiteCheckOutcome];

export type MissingCompanyDomainWebsiteCheck = {
  /**
   * Server-set; doubles as the re-check TTL key for the cron.
   */
  checkedAt: string;
  /**
   * WEBSITE_CHECK_VERSION of the library that produced these signals; bumped
   * when signal semantics change, so the data segments cleanly.
   */
  checkVersion: number;
  /**
   * Cron time-budget telemetry.
   */
  durationMs: number;
  outcome: WebsiteCheckOutcome;
  /**
   * Final post-redirect URL (FOUND/HTTP_ERROR only).
   */
  url?: string;
  httpStatus?: number;
  /**
   * First attempt's error, NOT_FOUND only.
   */
  unreachableReason?: string;
  /**
   * FOUND only.
   */
  signals?: WebsiteCheckSignals;
  /**
   * FOUND only.
   */
  contact?: WebsiteCheckContact;
  /**
   * FOUND only. The site's own JSON-LD organization declaration(s).
   */
  jsonLdOrganizations?: Array<WebsiteCheckJsonLdOrganization>;
};

export type MissingCompanyDomainHistory = {
  oldStatus?: MissingCompanyDomainsStatus;
  newStatus: MissingCompanyDomainsStatus;
  reason?: string;
  date: string;
  updatedBy?: string;
};

export const MissingCompanyDomainsStatus = {
  PENDING: 'PENDING',
  FORBIDDEN: 'FORBIDDEN',
  RESOLVED: 'RESOLVED',
  NOTFOUND: 'NOTFOUND',
} as const;

export type MissingCompanyDomainsStatus =
  (typeof MissingCompanyDomainsStatus)[keyof typeof MissingCompanyDomainsStatus];

export type MissingCompanyDomain = {
  id: string;
  createdAt: string;
  domain: string;
  status: MissingCompanyDomainsStatus;
  count: number;
  lastClearbitCheckedAt?: string;
  securityCheckAttempts?: number;
  lastSecurityCheckedAt?: string;
  history?: Array<MissingCompanyDomainHistory>;
  /**
   * Latest website-check flag data; overwritten on every check.
   */
  websiteCheck?: MissingCompanyDomainWebsiteCheck;
  /**
   * Latest automated dual verification (Clearbit × website check) and the
   * decision it reached; overwritten on every run. Always recorded, applied
   * to the status only behind the dual-company-verification flag.
   */
  verification?: MissingCompanyDomainVerification;
  updatedAt?: string;
};

export type MissingCompanyDomainsPaginatedDto = {
  /**
   * The total number of results possible, which is only provided in the response when `includeCount` is passed as `true` within a request.
   */
  totalResults?: number;
  page: number;
  pageSize: number;
  results: Array<MissingCompanyDomain>;
};

export type UpdateWebsiteCheckDto = {
  checkVersion: number;
  durationMs: number;
  outcome: WebsiteCheckOutcome;
  url?: string;
  httpStatus?: number;
  unreachableReason?: string;
  signals?: WebsiteCheckSignals;
  contact?: WebsiteCheckContact;
  jsonLdOrganizations?: Array<WebsiteCheckJsonLdOrganization>;
};

export type BulkUpdateMissingDomainStatusResponseDto = {
  updatedDomains: Array<string>;
  /**
   * Requested domains with no tracked record; skipped rather than failing the batch.
   */
  notFoundDomains: Array<string>;
};

export type BulkUpdateMissingDomainStatusDto = {
  /**
   * Domain names selected in the review queue to update.
   */
  domainNames: Array<string>;
  status: MissingCompanyDomainsStatus;
  reason: string;
};

export type VerificationResultDto = {
  status: MissingCompanyDomainsStatus;
  decision: VerificationDecision;
  blockers: Array<string>;
  gatingApplied: boolean;
  clearbitVerdict: ClearbitVerdict;
  websiteVerdict: WebsiteCheckVerdict;
};

export type ApplyVerificationDto = {
  clearbitStatus: ClearbitLookupStatus;
  /**
   * Clearbit's company payload; SUCCESS only.
   */
  clearbitCompanyData?: {
    [key: string]: unknown;
  };
};

export type ProfileExternalResearcherUserIdDto = {
  userId: string;
};

export type RejectProfilesResponseDto = {
  /**
   * True when no writes were made (preview).
   */
  dryRun: boolean;
  /**
   * Whether pending screener responses were also hidden (false = skipped).
   */
  hideResponses: boolean;
  requested: number;
  /**
   * Profiles newly set to REJECTED (found and not already rejected).
   */
  rejected: number;
  alreadyRejected: number;
  notFound: number;
  totalResponsesHidden: number;
  results: Array<RejectProfileResultDto>;
};

export type RejectProfileResultDto = {
  profileId: string;
  /**
   * Whether a profile with this id was found.
   */
  found: boolean;
  /**
   * The profile status before this operation.
   */
  prevStatus?: string | null;
  /**
   * True if the profile was already REJECTED (no status change).
   */
  alreadyRejected: boolean;
  /**
   * Pending screener responses hidden (on execute) or that would be hidden (on dry-run).
   */
  responsesHidden: number;
};

export type RejectProfilesDto = {
  /**
   * Profile IDs to reject (shadow-ban). Each is set to status=REJECTED.
   */
  profileIds: Array<string>;
  /**
   * Audit note stored with each rejection (for action_audit_log).
   */
  note: string;
  /**
   * When true (the default), preview only — no writes are made.
   */
  dryRun?: boolean;
  /**
   * When true (the default), also hide the participants' pending screener responses. Set false to skip that sweep — e.g. when the responses were already dismissed by a domain ban.
   */
  hideResponses?: boolean;
};

export type RecheckRiskHiddenResponsesResponseDto = {
  /**
   * True when no writes were made (preview).
   */
  dryRun: boolean;
  profiles: Array<RiskRecheckedProfileDto>;
};

export const RiskRecheckOutcome = {
  RESTORED: 'restored',
  HELD_FOR_REVIEW: 'held-for-review',
  REHIDDEN: 'rehidden',
  REOPENED: 'reopened',
  NEEDS_STEP: 'needs-step',
  STILL_RISKY: 'still-risky',
  CHANGED: 'changed',
  FAILED: 'failed',
} as const;

export type RiskRecheckOutcome =
  (typeof RiskRecheckOutcome)[keyof typeof RiskRecheckOutcome];

export type RiskRecheckedResponseDto = {
  screenerResponseId: string;
  projectId: string;
  /**
   * Whether the study is still recruiting.
   */
  projectRecruiting: boolean;
  outcome: RiskRecheckOutcome;
  /**
   * Whether the response qualified, as re-derived.
   */
  qualified: boolean;
};

export const RiskRecheckSkipReason = {
  NOT_FOUND: 'not-found',
  NOT_APPROVED: 'not-approved',
} as const;

export type RiskRecheckSkipReason =
  (typeof RiskRecheckSkipReason)[keyof typeof RiskRecheckSkipReason];

export type RiskRecheckedProfileDto = {
  profileId: string;
  skipped?: RiskRecheckSkipReason;
  responses: Array<RiskRecheckedResponseDto>;
};

export type RecheckRiskHiddenResponsesDto = {
  /**
   * Profiles whose responses hidden for risk should be re-checked. Profiles whose overall risk level is not approved are skipped.
   */
  profileIds: Array<string>;
  /**
   * Only re-check responses created on or after this time.
   */
  createdSince?: string;
  /**
   * When true (the default), preview only: no writes are made.
   */
  dryRun?: boolean;
};

export type UpdateRiskDto = {
  source?: ProfileRiskSource;
  level?: ProfileRiskLevel;
  lastChecked?: string;
  templateId?: string;
  risk?: ProfileRisk;
};

export const ProfileRiskSource = {
  CLEARBIT: 'clearbit',
  PLAID: 'plaid',
  ROUNDTABLE: 'roundtable',
} as const;

export type ProfileRiskSource =
  (typeof ProfileRiskSource)[keyof typeof ProfileRiskSource];

export type UpdateTrustedCompanyDto = {
  companyId?: string;
  domain: string;
};

export type ProfileDto = {
  id: string;
  createdAt: string;
  userId: string;
  firstName: string;
  lastName: string;
  status: RespondentStatus;
  linkedinUrl?: string | null;
  mobileType?: string | null;
  phone?: string;
  isPhoneVerified?: boolean;
  verifiedPhone?: string;
  phoneVerificationCode?: string | null;
  twilioPhoneData?: ProfileTwilioPhoneData;
  email?: string;
  emailDomain?: string;
  paypal?: string | null;
  demographics?: ProfileDemographics;
  employment?: ProfileEmployment | null;
  picture?: string | null;
  location?: ProfileLocation;
  social?: ProfileSocial;
  socialLinks?: ProfileSocialLinks | null;
  notificationSettings?: ProfileNotificationSettings;
  cookieSettings?: ProfileCookieSettings;
  score?: number;
  skills: Array<Skill>;
  videoIntroUrl?: string | null;
  topics?: Array<SimpleTopic>;
  /**
   * Must stay serialized on ProfileDto: the profile-risk-change-respond-queue
   * lambda reads the profile over GET v4/profiles/:profileId and feeds it to
   * computeOverallProfileRisk (@common/utils), where a null trustedCompany
   * marks the profile risk component 'questioned'. An `@Exclude()` here made
   * the DTO report null for every profile, so trusted-company verification
   * could never clear that flag (profile.dto.unit.spec.ts guards this).
   */
  trustedCompany?: ProfileTrustedCompany | null;
  /**
   * For public api partners only
   */
  externalOrganizationId?: string;
  personal?: PersonalInfo;
  technical?: Technical;
  productsServices?: ProductsServicesInfo;
  shopping?: ShoppingInfo;
  professional?: ProfessionalInfo;
  risk?: ProfileRisk;
  gdprDeletedAt?: string;
  lastLoginAt?: string;
  codeOfConductAcceptedAt?: string;
  lastScreenerSubmittedAt?: string;
  leadStatus?: LeadStatus;
  updatedAt?: string;
};

export type CreateProfileDto = {
  userId: string;
  linkedinUrl?: string | null;
  isPhoneVerified?: boolean;
  verifiedPhone?: string;
  phoneVerificationCode?: string | null;
  paypal?: string | null;
  demographics?: ProfileDemographics;
  picture?: string | null;
  location?: ProfileLocation;
  social?: ProfileSocial;
  socialLinks?: ProfileSocialLinks | null;
  notificationSettings?: ProfileNotificationSettings;
  cookieSettings?: ProfileCookieSettings;
  score?: number;
  /**
   * For public api partners only
   */
  externalOrganizationId?: string;
  leadId?: string;
  skills?: Array<string>;
  employment?: CreateProfileEmploymentDto;
};

export type CreateProfileEmploymentDto = {
  email?: string | null;
  isEmailVerified?: boolean;
  company?: string | null;
  companySize?: CompanySize;
  businessOwner?: boolean;
  employmentStatus: EmploymentStatus;
  jobTitle?: string | null;
  seniorityLevel?: SeniorityLevel;
  /**
   * Token for the legacy click-a-link work email verification.
   *
   *
   * (`verificationCodeHash` below). Still issued and honoured so links already
   * sitting in inboxes and the legacy Vue participant portal keep working;
   * remove once that portal no longer drives work email verification.
   *
   * @deprecated
   */
  verificationToken?: string;
  industry?: Array<string> | null;
  roleDescription?: string | null;
  /**
   * The company ID that is associated to the profile employment via an association
   * between the given company name and the given work email domain.
   * The ID itself may refer to a record in the `company`, `fullcontactcompany`,
   * or `clearbitcompany` collections.
   * If an association between the company name and work email domain does not exist,
   * then we set the field to `null` in the client.
   * If an association has _not yet_ been made, we leave this field `undefined`.
   */
  internalOrExternalCompanyId?: string | null;
  jobFunctions?: Array<JobFunction>;
  income?: Income;
};

export type VerifyEmailResponseDto = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  roles: Array<Roles>;
  isEmailVerified: boolean;
};

export type VerifyEmailDto = {
  userId: string;
  token: string;
};

export type InitiatePasswordResetDto = {
  email: string;
};

export type ResearcherSignupResponseDto = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  roles: Array<Roles>;
};

export type ResearcherSignupDto = {
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  termsAccepted: boolean;
  referrerUserId?: string;
  initialUtm?: InitialUtm;
  /**
   * Ad click identifiers and landing context, parsed server-side from the
   * `rio_attribution` cookie (and Meta's own `_fbp`/`_fbc` cookies) by the
   * apps/web BFF and forwarded here. Persisted to `user_ad_attributions` by
   * `AuthService.signupResearcher`, not stored on the user document itself.
   */
  adAttribution?: AdAttributionInputDto;
  /**
   * IANA timezone detected on the client (e.g. `Intl.DateTimeFormat()
   * .resolvedOptions().timeZone`). Falls back to {@link DefaultTimezone}
   * server-side when omitted.
   */
  timezone?: string;
  /**
   * The banner decision this browser recorded, read for one purpose: resolving
   * the two ad-consent grants the `signup_completed` conversion answers to.
   *
   * Not persisted, unlike the participant door's copy — researchers have no
   * Profile entity to hold it. Absent reads as denied, which means a researcher
   * signup that arrives without it reports no conversion at all.
   */
  cookieSettings?: ProfileCookieSettings;
};

export type SendPublicApplyVerificationDto = {
  email: string;
  projectId: string;
};

export type ParticipantSignupResponseDto = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  roles: Array<Roles>;
};

export type ParticipantSignupDto = {
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  termsAccepted: boolean;
  referrerUserId?: string;
  referrerProjectId?: string;
  leadId?: string;
  initialUtm?: InitialUtm;
  cookieSettings?: ProfileCookieSettings;
  /**
   * Ad click identifiers and landing context, parsed server-side from the
   * `rio_attribution` cookie (and Meta's own `_fbp`/`_fbc` cookies) by the
   * apps/web BFF and forwarded here. Persisted to `user_ad_attributions` by
   * `AuthService.signupParticipant`, not stored on the user document itself.
   */
  adAttribution?: AdAttributionInputDto;
  /**
   * IANA timezone detected on the client (e.g. `Intl.DateTimeFormat()
   * .resolvedOptions().timeZone`). Falls back to {@link DefaultTimezone}
   * server-side when omitted.
   */
  timezone?: string;
};

export type SendInsightsChatMessageDto = {
  /**
   * The feature schema version to read the project's insights at: its pin,
   * which is the default, or one of the experimental versions it is also
   * extracted at.
   */
  featuresSchemaVersion?: number;
  /**
   * Thread to continue. Omitted on the first message: the server creates the thread and streams its id in a data-thread part.
   */
  threadId?: string;
  /**
   * Client-generated id of the user message.
   */
  messageId: string;
  text: string;
};

export type InsightsChatChartEvaluationDto = {
  result: StudyChartResultDto;
  people: Array<InsightsChatChartPersonDto>;
  dataVersion: string;
  evaluatedAt: string;
};

export type InsightsChatChartPersonDto = {
  /**
   * This person's interview response id.
   */
  responseId: string;
  participantUserId: string;
  /**
   * Their participant number, as the chat and the Results tab number them.
   */
  number: number;
  name: string;
  picture?: string;
  jobTitle?: string;
  company?: string;
  seniority?: string;
};

export type EvaluateInsightsChatChartDto = {
  chartId: string;
  breakdown: string | null;
  /**
   * The feature schema version to read the project's insights at: its pin,
   * which is the default, or one of the experimental versions it is also
   * extracted at.
   */
  featuresSchemaVersion?: number;
};

export type RenameInsightsChatThreadDto = {
  title: string;
};

export type InsightsChatThreadDto = {
  id: string;
  title: string;
  scopeType: InsightsChatScopeType;
  scopeId: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  messages: Array<InsightsChatMessage>;
  /**
   * The study data a chart would be evaluated against now, set only on a thread with charts: a chart whose `dataVersion` differs was drawn from older data. Null where the study has no study data.
   */
  studyDataVersion?: string | null;
};

export type InsightsChatMessagePart = {
  type: string;
  text?: string;
  id?: string;
  data?: {
    [key: string]: unknown;
  };
};

export type InsightsChatMessage = {
  id: string;
  role: string;
  parts: Array<InsightsChatMessagePart>;
  createdAt: string;
};

export const InsightsChatScopeType = { PROJECT: 'project' } as const;

export type InsightsChatScopeType =
  (typeof InsightsChatScopeType)[keyof typeof InsightsChatScopeType];

export type InsightsChatThreadSummaryDto = {
  id: string;
  title: string;
  scopeType: InsightsChatScopeType;
  scopeId: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
};

export type AiChatThread = {
  id: string;
  createdAt: string;
  projectId: string;
  researcherId: string;
  messages?: Array<ChatMessage>;
  currentStep?: string;
  updatedAt?: string;
};

export type ChatMessagePart = {
  type: string;
  text?: string;
};

export type ChatMessage = {
  id: string;
  role: string;
  parts?: Array<ChatMessagePart>;
};

export type SaveChatThreadDto = {
  /**
   * UIMessage array — stored as opaque JSON for chat restore
   */
  messages: Array<{
    [key: string]: unknown;
  }>;
  currentStep?: string;
};

export type FindChatThreadDto = {
  messages?: Array<ChatMessage>;
  currentStep?: string;
  updatedAt?: string;
  hasProjectThread: boolean;
};

export type PlaidRiskVerificationsMetricsResponseDto = {
  granularity: FraudMetricsGranularity;
  from: string;
  to: string;
  buckets: Array<PlaidRiskVerificationsMetricsBucketDto>;
};

export type PlaidRiskVerificationsMetricsBucketDto = {
  periodStart: string;
  documentTemplateCount: number;
  defaultTemplateCount: number;
};

export const FraudMetricsGranularity = { WEEK: 'week', DAY: 'day' } as const;

export type FraudMetricsGranularity =
  (typeof FraudMetricsGranularity)[keyof typeof FraudMetricsGranularity];

export type FraudMetricsTimeSeriesResponseDto = {
  granularity: FraudMetricsGranularity;
  from: string;
  to: string;
  buckets: Array<FraudMetricsCountBucketDto>;
};

export type FraudMetricsCountBucketDto = {
  periodStart: string;
  count: number;
};

export type ReportedUsersMetricsResponseDto = {
  granularity: FraudMetricsGranularity;
  from: string;
  to: string;
  buckets: Array<ReportedUsersMetricsBucketDto>;
};

export type ReportedUsersMetricsBucketDto = {
  periodStart: string;
  reportCount: number;
  uniqueUserCount: number;
};

export type ProjectIncentiveAdjustmentsDto = {
  responses: Array<AdjustableResponseDto>;
  history: Array<IncentiveAdjustmentRecordDto>;
  /**
   * The organization's usual tip cap as a fraction of the incentive (0.25, or
   * 2 with `increasedMaxTips`). Admin tips are not capped; the UI warns when
   * a tip exceeds this.
   */
  maxTipPercentageOfIncentive: number;
};

export type IncentiveAdjustmentChangeDto = {
  surveyResponseId: string;
  participantName: string;
  oldIncentive: number | null;
  /**
   * New effective incentive, USD dollars, tip included.
   */
  newIncentive: number;
  /**
   * The part of `newIncentive` that was entered as a tip, when any.
   */
  tip?: number;
};

export type IncentiveAdjustmentRecordDto = {
  id: string;
  createdAt: string;
  actorUserId: string | null;
  actorName: string;
  reason: string;
  changes: Array<IncentiveAdjustmentChangeDto>;
};

export type AdjustableResponseDto = {
  surveyResponseId: string;
  userId: string;
  firstName: string;
  lastInitial: string;
  attendedAt: string | null;
  /**
   * Project incentive snapshot on the response, USD dollars.
   */
  projectIncentive: number;
  /**
   * `customIncentive ?? project.incentive`, USD dollars.
   */
  currentIncentive: number;
  editable: boolean;
  blockedReason?: string;
};

export type AdjustProjectIncentivesDto = {
  reason: string;
  adjustments: Array<IncentiveAdjustmentItemDto>;
};

export type IncentiveAdjustmentItemDto = {
  surveyResponseId: string;
  /**
   * Absolute incentive in USD dollars, at most two decimals
   */
  incentive: number;
  /**
   * Optional tip in USD dollars, at most two decimals; added to the incentive
   */
  tip?: number;
};

export type UpdateAdminUserDto = {
  firstName?: string;
  lastName?: string;
  /**
   * Empty string clears the field.
   */
  company?: string;
  /**
   * Empty string clears the field.
   */
  phone?: string;
  isActive?: boolean;
  /**
   * Replaces the user's roles wholesale (the legacy form posted the full
   * desired set). Admin-tier roles are guarded in the service.
   */
  roles?: Array<Roles>;
};

export type ConvertParticipantToResearcherResponseDto = {
  userId: string;
  previousRoles: Array<string>;
  newRoles: Array<string>;
  /**
   * Number of PENDING screener responses hidden by applying the CONVERTED_TO_RESEARCHER visibility preset (status DISMISSED)
   */
  hiddenScreenerResponseCount: number;
  profileDeleted: boolean;
  schedulerUserCreated: boolean;
};

export type ConvertParticipantToResearcherDto = {
  /**
   * The participant user ID. Provide this or `email`.
   */
  userId?: string;
  /**
   * The participant account email, matched case-insensitively. Provide this or `userId`.
   */
  email?: string;
  /**
   * Freeform note recorded on the action audit log for this conversion (e.g. a GEAR ticket or other reference).
   */
  reason: string;
};

export type PinFeaturesSchemaVersionDto = {
  featuresSchemaVersion: number;
};

export type ProjectInsightsStatusDto = {
  featuresSchemaVersion: number;
  /**
   * Whether the version has a project insights document at all.
   */
  found: boolean;
  /**
   * A rebuild has been asked for and no run has covered it yet.
   */
  rebuildOutstanding: boolean;
  features: Array<ProjectInsightsFeatureStatusDto>;
};

export type ProjectInsightsFeatureStatusDto = {
  featureId: string;
  questionUid?: string;
  status: InsightsReportStatus;
  generatedAt?: string;
  error?: string;
};

export type AiModProjectStatsDto = {
  projectId: string;
  /**
   * Interviews ever started: in progress or completed.
   */
  startedCount: number;
  completedCount: number;
  /**
   * When the project's first completed interview finished.
   */
  firstCompletedAt?: string;
  /**
   * Qualified, non-draft screener responses whose visibility code is OK.
   */
  qualifiedVisible: number;
};

export type GetAiModProjectStatsDto = {
  projectIds: Array<string>;
};

export type UpdateAdminParticipantDto = {
  status?: RespondentStatus;
  firstName?: string;
  lastName?: string;
  demographics?: UpdateAdminParticipantDemographicsDto;
};

export type UpdateAdminParticipantDemographicsDto = {
  gender?: Gender;
  /**
   * Date of birth as YYYY-MM-DD. Must not be in the future.
   */
  dateOfBirth?: string;
  ethnicity?: Ethnicities;
  educationLevel?: EducationLevel;
  householdIncome?: HouseholdIncome;
};

export type UpdateAdminParticipantResponseDto = {
  profileId: string;
  status: RespondentStatus;
  firstName: string;
  lastName: string;
  demographics: AdminParticipantDemographicsDto | null;
};

export type AdminParticipantDemographicsDto = {
  gender?: Gender;
  dateOfBirth?: string;
  ethnicity?: Ethnicities;
  educationLevel?: EducationLevel;
  householdIncome?: HouseholdIncome;
};

export type AdminParticipantsPaginatedDto = {
  /**
   * The total number of results possible, which is only provided in the response when `includeCount` is passed as `true` within a request.
   */
  totalResults?: number;
  page: number;
  pageSize: number;
  results: Array<AdminParticipantRowDto>;
  /**
   * True when `totalResults` is a lower bound rather than an exact count: the
   * match set exceeds the 10,000-result window, so the count stopped there.
   * Only present when `includeCount` was requested.
   */
  totalResultsIsLowerBound?: boolean;
};

export type AdminParticipantAccountDto = {
  email?: string;
  isEmailVerified: boolean;
  socialLogins: Array<string>;
};

export type AdminParticipantSocialLinksDto = {
  linkedin?: string;
  facebook?: string;
  twitter?: string;
  instagram?: string;
};

export type AdminParticipantEmploymentDto = {
  company?: string;
  jobTitle?: string;
  employmentStatus?: EmploymentStatus;
  seniorityLevel?: SeniorityLevel;
  companySize?: CompanySize;
  industry: Array<string>;
  /**
   * Work email (`employment.email`), distinct from the account email.
   */
  email?: string;
  isEmailVerified: boolean;
};

export type AdminParticipantRowDto = {
  id: string;
  userId?: string;
  firstName?: string;
  lastName?: string;
  /**
   * Profile contact email; the account email lives on `account`.
   */
  email?: string;
  phone?: string;
  picture?: string;
  status?: RespondentStatus;
  createdAt?: string;
  updatedAt?: string;
  /**
   * Flattened for display: `location.city.formattedAddress` when the city is a
   * structured document, else the older `city state zipcode` strings.
   */
  location?: string;
  demographics?: AdminParticipantDemographicsDto;
  employment?: AdminParticipantEmploymentDto;
  socialLinks?: AdminParticipantSocialLinksDto;
  /**
   * Absent when the profile has no matching user document.
   */
  account?: AdminParticipantAccountDto;
};

export type AdminParticipantHistoryDto = {
  stats: AdminParticipantHistoryStatsDto;
  /**
   * Latest 25 by `createdAt` desc.
   */
  responses: Array<AdminParticipantHistoryRowDto>;
};

export type AdminParticipantHistoryReviewDto = {
  text: string | null;
  /**
   * Mean of this review's `questions[].rating`; null for a v1 review.
   */
  rating: number | null;
};

export type AdminParticipantHistoryRowDto = {
  responseId: string;
  projectId: string;
  /**
   * Live project name, falling back to the response snapshot.
   */
  projectName: string | null;
  responseStatus: ScreenerResponseStatus;
  /**
   * Profile status as snapshotted on the response when it was taken, or null
   * when the response carries no profile snapshot (`ScreenerResponse.profile`
   * is optional).
   *
   * `ApiPropertyEnum` rather than a raw `@ApiProperty`: a bare
   * `nullable: true` next to an enum `$ref` is dropped in OpenAPI 3.0, so the
   * generated client would type this as non-nullable and make the UI's
   * `?? '—'` fallback look like dead code.
   */
  profileStatus: 'APPROVED' | 'VERIFIED' | 'PENDING' | 'REJECTED' | null;
  flagged: boolean;
  flaggedMessage: string | null;
  review: AdminParticipantHistoryReviewDto | null;
  paid: boolean;
  bookingTimezone: string | null;
  createdAt: string;
};

export type AdminParticipantHistoryRatesDto = {
  paid: number;
  flagged: number;
  qualified: number;
  rejected: number;
};

export type AdminParticipantHistoryCountsDto = {
  paid: number;
  invited: number;
  scheduled: number;
  cancelled: number;
  pending: number;
  /**
   * Responses taken while the PROFILE SNAPSHOT was REJECTED — not rejected
   * responses. Legacy semantics, kept; the UI labels it accordingly.
   */
  rejected: number;
  flagged: number;
  qualified: number;
  reviews: number;
};

export type AdminParticipantHistoryStatsDto = {
  /**
   * EVERY screener response for this user, with no status filter — DRAFT and
   * EXTERNAL_SCREENER_IN_PROGRESS included.
   *
   * This is deliberate legacy parity (user ruling, 2026-09-08), not an
   * oversight, and it has a consequence worth knowing before you change it:
   * `rates` all divide by this number, so a participant with many abandoned
   * drafts reads as low-qualified and low-paid. `admin/projects` takes the
   * other view and filters `status: { $ne: DRAFT }`, so the two admin surfaces
   * disagree on what "taken" means. The drawer states the definition in a
   * tooltip on the tile and on every rate derived from it rather than leaving
   * a reader to infer it.
   */
  taken: number;
  /**
   * Sum of the snapshot `project.incentive` over PAID responses.
   */
  earned: number;
  counts: AdminParticipantHistoryCountsDto;
  rates: AdminParticipantHistoryRatesDto;
  /**
   * Mean 1–5 rating across the participant's reviews, rounded to two
   * decimals, or null when no review carries ratings.
   *
   * This replaces legacy's `rates.stars`, which averaged `reviews.stars` — a
   * field no writer has ever produced, so it always rendered 0. Ratings come
   * from v2 reviews (`questions[].rating`), the only review version still
   * written; v1 reviews (`score`) count in `counts.reviews` only.
   */
  averageRating: number | null;
};

export type AdminRespondentPayoutsListDto = {
  /**
   * The total number of results possible, which is only provided in the response when `includeCount` is passed as `true` within a request.
   */
  totalResults?: number;
  page: number;
  pageSize: number;
  results: Array<AdminRespondentPayoutRowDto>;
};

export type AdminRespondentPayoutCancellationDto = {
  /**
   * Absent on legacy records.
   */
  userId?: string;
  userEmail?: string;
  note?: string;
  createdAt?: string;
};

export type AdminRespondentPayoutParticipantDto = {
  userId: string;
  /**
   * Absent for an external participant, who has no Profile.
   */
  profileId?: string;
  firstName: string;
  lastName: string;
  email: string;
};

export const RespondentPayoutCancelBlocker = {
  NOT_PENDING: 'NOT_PENDING',
  NOT_LATEST_ON_RESPONSE: 'NOT_LATEST_ON_RESPONSE',
  RESPONSE_MISSING: 'RESPONSE_MISSING',
} as const;

export type RespondentPayoutCancelBlocker =
  (typeof RespondentPayoutCancelBlocker)[keyof typeof RespondentPayoutCancelBlocker];

export type AdminRespondentPayoutRowDto = {
  id: string;
  status: RespondentPayoutStatus;
  /**
   * The cancel endpoint would accept this row: pending, and the newest on its response.
   */
  canCancel: boolean;
  cancelBlockedBy?: RespondentPayoutCancelBlocker;
  incentive: number;
  tip: number;
  fee: number;
  totalIncentive: number;
  paymentProcessingSystem: string;
  createdAt: string;
  surveyResponseId: string;
  projectId: string;
  projectName: string;
  participant: AdminRespondentPayoutParticipantDto;
  /**
   * Only present once the payout has been cancelled.
   */
  cancellation?: AdminRespondentPayoutCancellationDto;
};

export type OrganizationMergeResponseDto = {
  /**
   * Id of the queued merge request; execution happens asynchronously.
   */
  mergeId: string;
};

export type CreateOrganizationMergeDto = {
  /**
   * Member userIds of the child organization to leave behind (not moved to the parent).
   */
  excludedMemberIds?: Array<string>;
  /**
   * Team ids of the child organization to leave behind (not moved to the parent).
   */
  excludedTeamIds?: Array<string>;
};

export type UpdateAdminOrganizationDto = {
  trustLevel?: TrustLevel;
  features?: UpdateOrganizationFeaturesDto;
};

export type UpdateOrganizationFeaturesDto = {
  inviteRespondents?: boolean;
  outlookCalendar?: boolean;
  ssoEnabled?: boolean;
  hasReferral?: boolean;
  increasedTargetLimit?: boolean;
  increasedMaxTips?: boolean;
  participantDatabase?: boolean;
  exposeEmailsInUnmodProjects?: boolean;
  noCreditReservation?: boolean;
  pricingMultiplier?: boolean;
  forceProjectReview?: boolean;
  /**
   * Organization-level AI opt-out; see `Features['ai-opted-out']`.
   */
  'ai-opted-out'?: boolean;
};

export type AdminOrganizationsPaginatedDto = {
  /**
   * The total number of results possible, which is only provided in the response when `includeCount` is passed as `true` within a request.
   */
  totalResults?: number;
  page: number;
  pageSize: number;
  results: Array<AdminOrganizationRowDto>;
};

export type AdminOrganizationRowDto = {
  id: string;
  name: string;
  /**
   * Only in `groupByName` mode: every organization id sharing this row's
   * name (`id` is the first of them). Absent on plain rows.
   */
  organizationIds?: Array<string>;
  trustLevel?: number;
  city?: string;
  schedulerId?: string;
  usersCount: number;
  createdAt?: string;
  updatedAt?: string;
  isDeleted: boolean;
};

export type AdminOrganizationActiveUsersDto = {
  /**
   * Current organization members, active or not.
   */
  memberCount: number;
  users: Array<AdminOrganizationActiveUserDto>;
};

export type AdminOrganizationActiveUserTeamDto = {
  /**
   * Old team documents can lack a name; empty string then.
   */
  name: string;
  isDeleted: boolean;
};

export type AdminOrganizationActiveUserDto = {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  isActive: boolean;
  /**
   * The user's teams in this organization, sorted by name.
   */
  teams: Array<AdminOrganizationActiveUserTeamDto>;
  /**
   * Login sessions recorded inside the window. Sessions dedupe on
   * (user, IP, device), so this counts new IP/device combinations rather
   * than every sign-in.
   */
  sessionCount: number;
  /**
   * Latest login signal inside the window.
   */
  lastActiveAt: string;
};

export type IncentiveResponseDto = {
  range: {
    /**
     * Minimum suggested incentive, in US cents
     */
    min?: number;
    /**
     * Maximum suggested incentive. In US cents
     */
    max?: number;
  };
  /**
   * A short summary of why the given range was suggested.
   */
  summary: string;
  /**
   * Factors that contributed to the suggested range.
   */
  factors: Array<{
    /**
     * A factor that contributed to the suggested range.
     */
    factor?: string;
    /**
     * A rating from -5 to +5 of how much the factor impacted the suggested range.
     */
    impact?: number;
  }>;
};

export type IncentiveRequestB2cDto = {
  /**
   * The type of research being conducted
   */
  methodology:
    | 'oneOnOne'
    | 'focusGroup'
    | 'unmoderatedStudy'
    | 'survey'
    | 'diaryStudy'
    | 'inRespondentHome'
    | 'inRespondentOffice'
    | 'aiModeratedInterview';
  /**
   * Time required for a participant to complete the study, in minutes
   */
  timeRequired: number;
  /**
   * The location of the research, in person or remote. 1="in person", 2="remote"
   */
  researchConductType: 1 | 2;
  /**
   * The country code of the country in which the research is being conducted
   */
  countryCode:
    | 'AF'
    | 'AX'
    | 'AL'
    | 'DZ'
    | 'AS'
    | 'AD'
    | 'AO'
    | 'AI'
    | 'AQ'
    | 'AG'
    | 'AR'
    | 'AM'
    | 'AW'
    | 'AU'
    | 'AT'
    | 'AZ'
    | 'BS'
    | 'BH'
    | 'BD'
    | 'BB'
    | 'BY'
    | 'BE'
    | 'BZ'
    | 'BJ'
    | 'BM'
    | 'BT'
    | 'BO'
    | 'BA'
    | 'BW'
    | 'BV'
    | 'BR'
    | 'VG'
    | 'IO'
    | 'BN'
    | 'BG'
    | 'BF'
    | 'BI'
    | 'CV'
    | 'KH'
    | 'CM'
    | 'CA'
    | 'KY'
    | 'CF'
    | 'TD'
    | 'CL'
    | 'CN'
    | 'CX'
    | 'CC'
    | 'CO'
    | 'KM'
    | 'CG'
    | 'CK'
    | 'CR'
    | 'HR'
    | 'CU'
    | 'CW'
    | 'CY'
    | 'CZ'
    | 'CI'
    | 'CD'
    | 'DK'
    | 'DJ'
    | 'DM'
    | 'DO'
    | 'EC'
    | 'EG'
    | 'SV'
    | 'GQ'
    | 'ER'
    | 'EE'
    | 'SZ'
    | 'ET'
    | 'FK'
    | 'FO'
    | 'FJ'
    | 'FI'
    | 'FR'
    | 'GF'
    | 'PF'
    | 'TF'
    | 'GA'
    | 'GM'
    | 'GE'
    | 'DE'
    | 'GH'
    | 'GI'
    | 'GR'
    | 'GL'
    | 'GD'
    | 'GP'
    | 'GU'
    | 'GT'
    | 'GG'
    | 'GN'
    | 'GW'
    | 'GY'
    | 'HT'
    | 'HM'
    | 'VA'
    | 'HN'
    | 'HK'
    | 'HU'
    | 'IS'
    | 'IN'
    | 'ID'
    | 'IR'
    | 'IQ'
    | 'IE'
    | 'IM'
    | 'IL'
    | 'IT'
    | 'JM'
    | 'JP'
    | 'JE'
    | 'JO'
    | 'KZ'
    | 'KE'
    | 'KI'
    | 'KW'
    | 'KG'
    | 'LA'
    | 'LV'
    | 'LB'
    | 'LS'
    | 'LR'
    | 'LY'
    | 'LI'
    | 'LT'
    | 'LU'
    | 'MO'
    | 'MG'
    | 'MW'
    | 'MY'
    | 'MV'
    | 'ML'
    | 'MT'
    | 'MH'
    | 'MQ'
    | 'MR'
    | 'MU'
    | 'YT'
    | 'MX'
    | 'FM'
    | 'MD'
    | 'MC'
    | 'MN'
    | 'ME'
    | 'MS'
    | 'MA'
    | 'MZ'
    | 'NA'
    | 'NR'
    | 'NP'
    | 'NL'
    | 'NC'
    | 'NZ'
    | 'NI'
    | 'NE'
    | 'NG'
    | 'NU'
    | 'NF'
    | 'KP'
    | 'MK'
    | 'MP'
    | 'NO'
    | 'OM'
    | 'PK'
    | 'PW'
    | 'PS'
    | 'PA'
    | 'PG'
    | 'PY'
    | 'PE'
    | 'PH'
    | 'PN'
    | 'PL'
    | 'PT'
    | 'PR'
    | 'QA'
    | 'RO'
    | 'RW'
    | 'RE'
    | 'SH'
    | 'KN'
    | 'LC'
    | 'PM'
    | 'VC'
    | 'WS'
    | 'SM'
    | 'ST'
    | 'SA'
    | 'SN'
    | 'RS'
    | 'SC'
    | 'SL'
    | 'SG'
    | 'SK'
    | 'SI'
    | 'SB'
    | 'SO'
    | 'ZA'
    | 'GS'
    | 'KR'
    | 'ES'
    | 'LK'
    | 'SD'
    | 'SR'
    | 'SJ'
    | 'SE'
    | 'CH'
    | 'TJ'
    | 'TZ'
    | 'TW'
    | 'TH'
    | 'TL'
    | 'TG'
    | 'TK'
    | 'TO'
    | 'TT'
    | 'TN'
    | 'TM'
    | 'TC'
    | 'TV'
    | 'TR'
    | 'VI'
    | 'UG'
    | 'UA'
    | 'AE'
    | 'GB'
    | 'UM'
    | 'US'
    | 'UY'
    | 'UZ'
    | 'VU'
    | 'VE'
    | 'VN'
    | 'WF'
    | 'EH'
    | 'YE'
    | 'ZM'
    | 'ZW'
    | 'all';
  participantAudienceType: B2cParticipantAudienceType;
  /**
   * Likelihood of participants in the country qualifying for the study, 1-100
   */
  incidenceRate: number;
};

export const B2cParticipantAudienceType = { 2: 2 } as const;

export type B2cParticipantAudienceType =
  (typeof B2cParticipantAudienceType)[keyof typeof B2cParticipantAudienceType];

export type IncentiveRequestB2bDto = {
  /**
   * The type of research being conducted
   */
  methodology:
    | 'oneOnOne'
    | 'focusGroup'
    | 'unmoderatedStudy'
    | 'survey'
    | 'diaryStudy'
    | 'inRespondentHome'
    | 'inRespondentOffice'
    | 'aiModeratedInterview';
  /**
   * Time required for a participant to complete the study, in minutes
   */
  timeRequired: number;
  /**
   * The location of the research, in person or remote. 1="in person", 2="remote"
   */
  researchConductType: 1 | 2;
  /**
   * The country code of the country in which the research is being conducted
   */
  countryCode:
    | 'AF'
    | 'AX'
    | 'AL'
    | 'DZ'
    | 'AS'
    | 'AD'
    | 'AO'
    | 'AI'
    | 'AQ'
    | 'AG'
    | 'AR'
    | 'AM'
    | 'AW'
    | 'AU'
    | 'AT'
    | 'AZ'
    | 'BS'
    | 'BH'
    | 'BD'
    | 'BB'
    | 'BY'
    | 'BE'
    | 'BZ'
    | 'BJ'
    | 'BM'
    | 'BT'
    | 'BO'
    | 'BA'
    | 'BW'
    | 'BV'
    | 'BR'
    | 'VG'
    | 'IO'
    | 'BN'
    | 'BG'
    | 'BF'
    | 'BI'
    | 'CV'
    | 'KH'
    | 'CM'
    | 'CA'
    | 'KY'
    | 'CF'
    | 'TD'
    | 'CL'
    | 'CN'
    | 'CX'
    | 'CC'
    | 'CO'
    | 'KM'
    | 'CG'
    | 'CK'
    | 'CR'
    | 'HR'
    | 'CU'
    | 'CW'
    | 'CY'
    | 'CZ'
    | 'CI'
    | 'CD'
    | 'DK'
    | 'DJ'
    | 'DM'
    | 'DO'
    | 'EC'
    | 'EG'
    | 'SV'
    | 'GQ'
    | 'ER'
    | 'EE'
    | 'SZ'
    | 'ET'
    | 'FK'
    | 'FO'
    | 'FJ'
    | 'FI'
    | 'FR'
    | 'GF'
    | 'PF'
    | 'TF'
    | 'GA'
    | 'GM'
    | 'GE'
    | 'DE'
    | 'GH'
    | 'GI'
    | 'GR'
    | 'GL'
    | 'GD'
    | 'GP'
    | 'GU'
    | 'GT'
    | 'GG'
    | 'GN'
    | 'GW'
    | 'GY'
    | 'HT'
    | 'HM'
    | 'VA'
    | 'HN'
    | 'HK'
    | 'HU'
    | 'IS'
    | 'IN'
    | 'ID'
    | 'IR'
    | 'IQ'
    | 'IE'
    | 'IM'
    | 'IL'
    | 'IT'
    | 'JM'
    | 'JP'
    | 'JE'
    | 'JO'
    | 'KZ'
    | 'KE'
    | 'KI'
    | 'KW'
    | 'KG'
    | 'LA'
    | 'LV'
    | 'LB'
    | 'LS'
    | 'LR'
    | 'LY'
    | 'LI'
    | 'LT'
    | 'LU'
    | 'MO'
    | 'MG'
    | 'MW'
    | 'MY'
    | 'MV'
    | 'ML'
    | 'MT'
    | 'MH'
    | 'MQ'
    | 'MR'
    | 'MU'
    | 'YT'
    | 'MX'
    | 'FM'
    | 'MD'
    | 'MC'
    | 'MN'
    | 'ME'
    | 'MS'
    | 'MA'
    | 'MZ'
    | 'NA'
    | 'NR'
    | 'NP'
    | 'NL'
    | 'NC'
    | 'NZ'
    | 'NI'
    | 'NE'
    | 'NG'
    | 'NU'
    | 'NF'
    | 'KP'
    | 'MK'
    | 'MP'
    | 'NO'
    | 'OM'
    | 'PK'
    | 'PW'
    | 'PS'
    | 'PA'
    | 'PG'
    | 'PY'
    | 'PE'
    | 'PH'
    | 'PN'
    | 'PL'
    | 'PT'
    | 'PR'
    | 'QA'
    | 'RO'
    | 'RW'
    | 'RE'
    | 'SH'
    | 'KN'
    | 'LC'
    | 'PM'
    | 'VC'
    | 'WS'
    | 'SM'
    | 'ST'
    | 'SA'
    | 'SN'
    | 'RS'
    | 'SC'
    | 'SL'
    | 'SG'
    | 'SK'
    | 'SI'
    | 'SB'
    | 'SO'
    | 'ZA'
    | 'GS'
    | 'KR'
    | 'ES'
    | 'LK'
    | 'SD'
    | 'SR'
    | 'SJ'
    | 'SE'
    | 'CH'
    | 'TJ'
    | 'TZ'
    | 'TW'
    | 'TH'
    | 'TL'
    | 'TG'
    | 'TK'
    | 'TO'
    | 'TT'
    | 'TN'
    | 'TM'
    | 'TC'
    | 'TV'
    | 'TR'
    | 'VI'
    | 'UG'
    | 'UA'
    | 'AE'
    | 'GB'
    | 'UM'
    | 'US'
    | 'UY'
    | 'UZ'
    | 'VU'
    | 'VE'
    | 'VN'
    | 'WF'
    | 'EH'
    | 'YE'
    | 'ZM'
    | 'ZW'
    | 'all';
  participantAudienceType: B2bParticipantAudienceType;
  /**
   * The job function of the participants being sought.
   */
  jobFunction:
    | 'accountingAuditing'
    | 'administrative'
    | 'advertising'
    | 'analyst'
    | 'artCreative'
    | 'businessDevelopment'
    | 'consulting'
    | 'customerService'
    | 'distribution'
    | 'design'
    | 'education'
    | 'finance'
    | 'generalBusiness'
    | 'healthcareProvider'
    | 'humanResources'
    | 'informationTechnology'
    | 'legal'
    | 'management'
    | 'manufacturing'
    | 'marketing'
    | 'other'
    | 'publicRelations'
    | 'purchasing'
    | 'productManagement'
    | 'projectManagement'
    | 'production'
    | 'qualityAssurance'
    | 'research'
    | 'sales'
    | 'science'
    | 'strategyPlanning'
    | 'supplyChain'
    | 'training'
    | 'writingEditing';
  /**
   * The seniority level of the participants being sought.
   */
  seniorityLevel:
    | 'unpaid'
    | 'student'
    | 'training'
    | 'entrylevel'
    | 'senior'
    | 'manager'
    | 'director'
    | 'chiefofficer'
    | 'partner'
    | 'vicepresident';
};

export const B2bParticipantAudienceType = { 1: 1 } as const;

export type B2bParticipantAudienceType =
  (typeof B2bParticipantAudienceType)[keyof typeof B2bParticipantAudienceType];

export type ProjectMetric = {
  id: string;
  createdAt: string;
  projectId: string;
  type: ProjectMetricType;
  items: Array<ProjectMetricItem>;
  updatedAt?: string;
};

export type ProjectMetricItem = {
  createdAt: string;
  count: number;
};

export const ProjectMetricType = { VIEWS: 'views', CLICKS: 'clicks' } as const;

export type ProjectMetricType =
  (typeof ProjectMetricType)[keyof typeof ProjectMetricType];

export type UpsertDailyMetricsDto = {
  projectId: string;
  event: Array<UpsertProjectMetricsItems>;
  from: string;
  to: string;
};

export type UpsertProjectMetricsItems = {
  type: ProjectMetricType;
  count: number;
};

export type SaveScreenerResponseResponseDto = {
  id: string;
  status: ScreenerResponseStatus;
  responsesCount: number;
  /**
   * UID of the next question the participant should answer based on
   * skip-logic evaluation against the persisted responses. Populated when
   * `isScreenerComplete` is `false`; `null` when `true`.
   */
  nextQuestionUid: string | null;
  /**
   * `true` when every reachable question has a response — i.e. the
   * participant has finished the screener and the client should submit
   * rather than navigate. Mirrors the engine's `isScreenerComplete`.
   */
  isScreenerComplete: boolean;
  /**
   * `true` when the question identified by `nextQuestionUid` is the last
   * one under default forward progression — used by the client to label
   * the next question's primary action button as "Submit" vs "Continue".
   * `false` when `isScreenerComplete` is true (no next question exists)
   * or when the next question is not the final reachable one.
   */
  isNextQuestionLast: boolean;
  nextStep?: PostSubmissionStep;
};

export type SaveScreenerResponseDto = {
  /**
   * Screener question responses.
   * Each response contains the screener question's MongoDB _id (hex string),
   * answer(s), and question metadata.
   */
  responses: Array<
    | SaveResponseAnswerRadio
    | SaveResponseAnswerCheckbox
    | SaveResponseAnswerText
    | SaveResponseAnswerSkipped
    | SaveResponseAnswerFile
    | SaveResponseAnswerRank
    | SaveResponseAnswerMatrix
  >;
  /**
   * Total time spent on the screener in seconds.
   */
  totalTime: number;
  /**
   * NDA agreement flag.
   * Required to be true for final submission if project has NDA.
   */
  agree?: boolean;
  /**
   * Invite ID if participant was invited directly.
   */
  inviteId?: string;
  /**
   * Project share-link invite code. The server validates it against the
   * project before storing it as invitation provenance.
   */
  inviteCode?: string;
  initialUtm?: InitialUtm;
  /**
   * Surface, sort and card position the participant clicked through from.
   * Only honoured on the first save; later values are ignored server-side.
   */
  dashboardPlacement?: DashboardPlacement;
};

export type GetScreenerResponseDto = {
  questions?: Array<ScreenerQuestionForParticipantDto>;
  nda?: NdaInfoDto;
  /**
   * `true` when every reachable question has a response — mirrors
   * `SaveScreenerResponseResponseDto.isScreenerComplete`. This is the
   * canonical "stop showing questions" signal; `nextQuestionUid` is
   * meaningless when this is `true`.
   */
  isScreenerComplete: boolean;
  /**
   * UID of the question the participant should answer next, computed
   * server-side against any existing draft responses. `null` when
   * `isScreenerComplete` is `true`.
   */
  nextQuestionUid: string | null;
  /**
   * `true` when `nextQuestionUid` points to the final reachable question
   * under default forward progression — used by the client to label the
   * primary action button as "Submit" vs "Continue" on first render.
   * Updated on each save via `SaveScreenerResponseResponseDto`.
   */
  isNextQuestionLast: boolean;
  existingResponse?: ExistingResponseDto;
  /**
   * `true` when this participant holds an invitation to the project that
   * waives the screener (a researcher's follow-up invite with "skip the
   * screener" on). The questions are still returned, but the client should
   * offer a single accept action instead of walking them; the submission is
   * qualified by the invitation, not by its answers. Resolved from the
   * invitation bound to this user and project — never from the raw query.
   */
  skipScreenerQuestions: boolean;
};

export type ScreenerResponseOutstandingStepsDto = {
  linkedin: boolean;
  nda: boolean;
  phone: boolean;
  plaid: boolean;
};

export type ExistingResponseDto = {
  id?: string;
  status?: ScreenerResponseStatus;
  answeredQuestionIds?: Array<string>;
  outstandingSteps?: ScreenerResponseOutstandingStepsDto;
  /**
   * External-screener flow: the partner's screener URL (with the
   * `respondent_*` correlation params appended) while the response is
   * EXTERNAL_SCREENER_IN_PROGRESS — so a returning participant can be offered
   * the way back into the partner's screener. Null/absent otherwise.
   */
  externalScreenerLink?: string | null;
  outcome?: ScreenerResponseOutcome;
  /**
   * The filed application is waiting only on the participant's ID
   * verification. Present (true) instead of `outcome` while the hold lasts,
   * and only for orgs on at least one post-screener flag; the client keeps the
   * neutral screen and says so.
   */
  idVerificationPending?: boolean;
  /**
   * The participant has already opened the project's task link (unmoderated
   * studies), so the start button reads "Go to project". Only the external
   * screener sets it; the participant app reads it off the project view.
   */
  projectStarted?: boolean;
};

export type ExternalInviteApplicationResponseDto = {
  userId: string;
  externalParticipantId: string;
  projectId: string;
  screenerResponseId: string;
  /**
   * The response's status: INVITED when admitted, DRAFT while the screener
   * is still to be answered, PENDING when filed without an admission.
   */
  status: string;
  admitted: boolean;
  studyType: ExternalStudyType;
};

export const ExternalStudyType = {
  AI_MODERATED: 'aiModerated',
  UNMODERATED: 'unmoderated',
} as const;

export type ExternalStudyType =
  (typeof ExternalStudyType)[keyof typeof ExternalStudyType];

export type ExternalInviteApplicationDto = {
  contactEmail: string;
  /**
   * The code emailed to `contactEmail`; single-use, expires in 15 minutes.
   */
  verificationCode: string;
  privacyAccepted: boolean;
  /**
   * The per-person id the researcher put on the link, if any.
   */
  uid?: string;
  /**
   * The researcher's own query parameters on the link, other than `uid`,
   * as the web tier captured them from the URL the person first arrived on.
   * Stored on the response as `externalLinkParams` and handed back to the
   * researcher unchanged; see `external-link-params.ts` for the limits.
   */
  linkParams?: {
    [key: string]: string;
  };
  timezone?: string;
};

export type SendExternalInviteVerificationDto = {
  contactEmail: string;
};

export type ExternalInviteValidationDto = {
  projectId: string;
  projectName: string;
  /**
   * The incentive in dollars, as shown to marketplace applicants.
   */
  incentive: number;
  durationMinutes: number;
  studyType: ExternalStudyType;
  skipScreener: boolean;
  /**
   * The researcher pays the incentive directly, not through Respondent.
   */
  incentivePaidExternally: boolean;
  /**
   * The project asks participants to agree to an NDA and this link does not
   * skip the screener, so the agreement is an outstanding step after
   * applying. Always false when `skipScreener` is on.
   */
  requiresNda: boolean;
  state: ExternalInviteState;
};

export const ExternalInviteState = {
  OPEN: 'OPEN',
  FULL: 'FULL',
  UID_USED: 'UID_USED',
} as const;

export type ExternalInviteState =
  (typeof ExternalInviteState)[keyof typeof ExternalInviteState];

export type Topic = {
  id: string;
  createdAt: string;
  name: string;
  customName?: string;
  updatedAt?: string;
};

export type SimpleTopicsPaginatedDto = {
  /**
   * The total number of results possible, which is only provided in the response when `includeCount` is passed as `true` within a request.
   */
  totalResults?: number;
  page: number;
  pageSize: number;
  results: Array<SimpleTopic>;
};

export type SimpleSkillsPaginatedDto = {
  /**
   * The total number of results possible, which is only provided in the response when `includeCount` is passed as `true` within a request.
   */
  totalResults?: number;
  page: number;
  pageSize: number;
  results: Array<SimpleSkill>;
};

export type SimpleSkill = {
  id: string;
  name: string;
  slug: string;
  validated: boolean;
  isCanonical?: boolean;
  type: string;
};

export type LookupsExpandedDto = {
  ageGroup?: Array<LookupExpandedDto>;
  companySize?: Array<LookupExpandedDto>;
  country?: Array<LookupExpandedDto>;
  educationLevel?: Array<LookupExpandedDto>;
  employmentStatus?: Array<LookupExpandedDto>;
  ethnicity?: Array<LookupExpandedDto>;
  gender?: Array<LookupExpandedDto>;
  householdIncome?: Array<LookupExpandedDto>;
  industries?: Array<LookupExpandedDto>;
  jobTitles?: Array<LookupExpandedDto>;
  skills?: Array<LookupExpandedDto>;
  topics?: Array<LookupExpandedDto>;
  seniorityLevel?: Array<LookupExpandedDto>;
  jobFunction?: Array<LookupExpandedDto>;
  income?: Array<LookupExpandedDto>;
  programmingLanguage?: Array<LookupExpandedDto>;
  browser?: Array<LookupExpandedDto>;
  smartphoneOS?: Array<LookupExpandedDto>;
  tabletOS?: Array<LookupExpandedDto>;
  computerOS?: Array<LookupExpandedDto>;
  relationshipStatus?: Array<LookupExpandedDto>;
  language?: Array<LookupExpandedDto>;
  livingSituation?: Array<LookupExpandedDto>;
  pet?: Array<LookupExpandedDto>;
  keyQualifier?: Array<LookupExpandedDto>;
  screenerResponseStatus?: Array<LookupExpandedDto>;
};

export const Context = { PROJECT: 'project', PROFILE: 'profile' } as const;

export type Context = (typeof Context)[keyof typeof Context];

export const Language = { EN: 'en' } as const;

export type Language = (typeof Language)[keyof typeof Language];

export type LookupExpandedDto = {
  code: string;
  value: string;
  weight?: number;
  disabled?: boolean;
  description: string;
  lang?: Language;
  contexts?: Array<Context>;
};

export type LookupsMinimalDto = {
  ageGroup?: Array<LookupMinimalDto>;
  companySize?: Array<LookupMinimalDto>;
  country?: Array<LookupMinimalDto>;
  educationLevel?: Array<LookupMinimalDto>;
  employmentStatus?: Array<LookupMinimalDto>;
  ethnicity?: Array<LookupMinimalDto>;
  gender?: Array<LookupMinimalDto>;
  householdIncome?: Array<LookupMinimalDto>;
  industries?: Array<LookupMinimalDto>;
  jobTitles?: Array<LookupMinimalDto>;
  skills?: Array<LookupMinimalDto>;
  topics?: Array<LookupMinimalDto>;
  seniorityLevel?: Array<LookupMinimalDto>;
  jobFunction?: Array<LookupMinimalDto>;
  income?: Array<LookupMinimalDto>;
  programmingLanguage?: Array<LookupMinimalDto>;
  browser?: Array<LookupMinimalDto>;
  smartphoneOS?: Array<LookupMinimalDto>;
  tabletOS?: Array<LookupMinimalDto>;
  computerOS?: Array<LookupMinimalDto>;
  relationshipStatus?: Array<LookupMinimalDto>;
  language?: Array<LookupMinimalDto>;
  livingSituation?: Array<LookupMinimalDto>;
  pet?: Array<LookupMinimalDto>;
  keyQualifier?: Array<LookupMinimalDto>;
  screenerResponseStatus?: Array<LookupMinimalDto>;
};

export type LookupMinimalDto = {
  code: string;
  value: string;
  weight?: number;
  disabled?: boolean;
};

export type ScreenerQuestionProjectGroup = {
  projectId: string;
  /**
   * Display name for the project (`name`, falling back to `internalName`).
   */
  projectName: string;
  questions: Array<ScreenerQuestion>;
};

export type UpsertMatrixScreenerQuestionDto = {
  text: string;
  isRequired?: boolean;
  logic?: ScreenerQuestionSkipLogic;
  questionType: MatrixQuestionType;
  /**
   * The row items.
   */
  matrixRows: Array<CreateMatrixScreenerQuestionRowDto>;
  matrixMatchType?: MatrixMatchType;
  matrixSelectionMode?: MatrixSelectionMode;
  /**
   * Whether to randomize row display order for each participant. Defaults to
   * `false` when omitted — existing matrices must not start shuffling.
   */
  randomizeOrder?: boolean;
  /**
   * Paired with `logic`: send `true` whenever `logic` is set and omit it (or
   * send `false`) whenever it is not. Setting one without the other is
   * rejected — the field is optional, but not independently so.
   */
  skipLogic?: boolean;
  id?: string;
  uid?: string;
  answers: Array<UpsertMatrixScreenerQuestionAnswerDto>;
  isEnd?: boolean;
  warnings?: Array<string>;
  createdAt?: string;
};

export type UpsertMatrixScreenerQuestionAnswerDto = {
  /**
   * Required  unless `isOther`, then text will be set to 'Other'
   */
  text?: string;
  uid: string;
  /**
   * Unused for MATRIX (qualification is per-row); persisted to satisfy the shared answer schema.
   */
  answerValue?: number;
  id?: string;
  createdAt?: string;
};

export type CreateMatrixScreenerQuestionRowDto = {
  uid: string;
  text: string;
  qualifyingAnswerUids?: Array<string>;
  /**
   * Multi-mode only. Columns the participant must select for the row to qualify.
   */
  mustSelectAnswerUids?: Array<string>;
  /**
   * Multi-mode only. Columns that disqualify the row when selected.
   */
  disqualifyAnswerUids?: Array<string>;
};

export const MatrixQuestionType = { 9: 9 } as const;

export type MatrixQuestionType =
  (typeof MatrixQuestionType)[keyof typeof MatrixQuestionType];

export type UpdateMatrixScreenerQuestionDto = {
  text?: string;
  isRequired?: boolean;
  logic?: ScreenerQuestionSkipLogic;
  questionType?: MatrixQuestionType;
  /**
   * The shared column scale.
   */
  answers?: Array<CreateMatrixScreenerQuestionAnswerDto>;
  /**
   * The row items.
   */
  matrixRows?: Array<CreateMatrixScreenerQuestionRowDto>;
  matrixMatchType?: MatrixMatchType;
  matrixSelectionMode?: MatrixSelectionMode;
  /**
   * Whether to randomize row display order for each participant. Defaults to
   * `false` when omitted — existing matrices must not start shuffling.
   */
  randomizeOrder?: boolean;
  /**
   * Paired with `logic`: send `true` whenever `logic` is set and omit it (or
   * send `false`) whenever it is not. Setting one without the other is
   * rejected — the field is optional, but not independently so.
   */
  skipLogic?: boolean;
};

export type CreateMatrixScreenerQuestionAnswerDto = {
  /**
   * Required  unless `isOther`, then text will be set to 'Other'
   */
  text?: string;
  uid: string;
  /**
   * Unused for MATRIX (qualification is per-row); persisted to satisfy the shared answer schema.
   */
  answerValue?: number;
};

export type CreateMatrixScreenerQuestionDto = {
  text: string;
  isRequired?: boolean;
  logic?: ScreenerQuestionSkipLogic;
  questionType: MatrixQuestionType;
  /**
   * The shared column scale.
   */
  answers: Array<CreateMatrixScreenerQuestionAnswerDto>;
  /**
   * The row items.
   */
  matrixRows: Array<CreateMatrixScreenerQuestionRowDto>;
  matrixMatchType?: MatrixMatchType;
  matrixSelectionMode?: MatrixSelectionMode;
  /**
   * Whether to randomize row display order for each participant. Defaults to
   * `false` when omitted — existing matrices must not start shuffling.
   */
  randomizeOrder?: boolean;
  /**
   * Paired with `logic`: send `true` whenever `logic` is set and omit it (or
   * send `false`) whenever it is not. Setting one without the other is
   * rejected — the field is optional, but not independently so.
   */
  skipLogic?: boolean;
};

export type UpsertRankScreenerQuestionDto = {
  text: string;
  isRequired?: boolean;
  questionType: RankQuestionType;
  /**
   * Whether to randomize option display order for each participant.
   * Defaults to `true` when omitted — the create flow applies the default
   * before validation, so the participant view always sees an explicit value.
   */
  randomizeOrder?: boolean;
  id?: string;
  uid?: string;
  answers: Array<UpsertRankScreenerQuestionAnswerDto>;
  isEnd?: boolean;
  warnings?: Array<string>;
  createdAt?: string;
};

export type UpsertRankScreenerQuestionAnswerDto = {
  /**
   * Required  unless `isOther`, then text will be set to 'Other'
   */
  text?: string;
  /**
   * Unused for RANK (a valid ranking always qualifies); persisted to satisfy the shared answer schema.
   */
  answerValue?: number;
  id?: string;
  uid?: string;
  createdAt?: string;
};

export const RankQuestionType = { 8: 8 } as const;

export type RankQuestionType =
  (typeof RankQuestionType)[keyof typeof RankQuestionType];

export type UpdateRankScreenerQuestionDto = {
  text?: string;
  isRequired?: boolean;
  questionType?: RankQuestionType;
  answers?: Array<CreateRankScreenerQuestionAnswerDto>;
  /**
   * Whether to randomize option display order for each participant.
   * Defaults to `true` when omitted — the create flow applies the default
   * before validation, so the participant view always sees an explicit value.
   */
  randomizeOrder?: boolean;
};

export type CreateRankScreenerQuestionAnswerDto = {
  /**
   * Required  unless `isOther`, then text will be set to 'Other'
   */
  text?: string;
  /**
   * Unused for RANK (a valid ranking always qualifies); persisted to satisfy the shared answer schema.
   */
  answerValue?: number;
};

export type CreateRankScreenerQuestionDto = {
  text: string;
  isRequired?: boolean;
  questionType: RankQuestionType;
  answers: Array<CreateRankScreenerQuestionAnswerDto>;
  /**
   * Whether to randomize option display order for each participant.
   * Defaults to `true` when omitted — the create flow applies the default
   * before validation, so the participant view always sees an explicit value.
   */
  randomizeOrder?: boolean;
};

export type UpsertFileUploadScreenerQuestionDto = {
  text: string;
  isRequired?: boolean;
  questionType: FileUploadQuestionType;
  id?: string;
  uid?: string;
  isEnd?: boolean;
  warnings?: Array<string>;
  createdAt?: string;
};

export const FileUploadQuestionType = { 7: 7 } as const;

export type FileUploadQuestionType =
  (typeof FileUploadQuestionType)[keyof typeof FileUploadQuestionType];

export type UpsertSliderScaleScreenerQuestionDto = {
  text: string;
  isRequired?: boolean;
  showStep?: boolean;
  questionType: SliderScaleQuestionType;
  minValue?: number;
  maxValue?: number;
  step?: number;
  id?: string;
  uid?: string;
  isEnd?: boolean;
  warnings?: Array<string>;
  createdAt?: string;
};

export const SliderScaleQuestionType = { 6: 6 } as const;

export type SliderScaleQuestionType =
  (typeof SliderScaleQuestionType)[keyof typeof SliderScaleQuestionType];

export type UpsertTextboxScreenerQuestionDto = {
  text: string;
  isRequired?: boolean;
  questionType: TextboxQuestionType;
  id?: string;
  uid?: string;
  isEnd?: boolean;
  warnings?: Array<string>;
  createdAt?: string;
};

export const TextboxQuestionType = {
  4: 4,
  2: 2,
  5: 5,
} as const;

export type TextboxQuestionType =
  (typeof TextboxQuestionType)[keyof typeof TextboxQuestionType];

export type UpsertCheckboxScreenerQuestionDto = {
  text: string;
  isRequired?: boolean;
  includeOtherOption?: boolean;
  logic?: ScreenerQuestionSkipLogic;
  questionType: CheckboxQuestionType;
  matchType?: CheckboxMatchType;
  /**
   * Paired with `logic`: send `true` whenever `logic` is set and omit it (or
   * send `false`) whenever it is not. Setting one without the other is
   * rejected — the field is optional, but not independently so.
   */
  skipLogic?: boolean;
  id?: string;
  uid?: string;
  answers: Array<UpsertCheckboxScreenerQuestionAnswerDto>;
  isEnd?: boolean;
  warnings?: Array<string>;
  createdAt?: string;
};

export const CheckboxAnswerValue = {
  1: 1,
  2: 2,
  3: 3,
} as const;

export type CheckboxAnswerValue =
  (typeof CheckboxAnswerValue)[keyof typeof CheckboxAnswerValue];

export type UpsertCheckboxScreenerQuestionAnswerDto = {
  /**
   * Required  unless `isOther`, then text will be set to 'Other'
   */
  text?: string;
  /**
   * Is 'Other' answer with comment field
   */
  isOther?: boolean;
  answerValue: CheckboxAnswerValue;
  id?: string;
  uid?: string;
  createdAt?: string;
};

export const CheckboxQuestionType = { 3: 3 } as const;

export type CheckboxQuestionType =
  (typeof CheckboxQuestionType)[keyof typeof CheckboxQuestionType];

export type UpsertRadioScreenerQuestionDto = {
  text: string;
  isRequired?: boolean;
  includeOtherOption: boolean;
  questionType: RadioQuestionType;
  /**
   * Send `true` only when at least one answer carries `goToQuestionUid`; a
   * `true` with no such answer is rejected. The reverse is tolerated — answers
   * may declare destinations while this stays unset.
   */
  skipLogic?: boolean;
  id?: string;
  uid?: string;
  answers: Array<UpsertRadioScreenerQuestionAnswerDto>;
  isEnd?: boolean;
  warnings?: Array<string>;
  createdAt?: string;
};

export const RadioAnswerValue = { 1: 1, 2: 2 } as const;

export type RadioAnswerValue =
  (typeof RadioAnswerValue)[keyof typeof RadioAnswerValue];

export type UpsertRadioScreenerQuestionAnswerDto = {
  /**
   * Required  unless `isOther`, then text will be set to 'Other'
   */
  text?: string;
  /**
   * Skip logic for radio questions. Uid of question to skip to.
   */
  goToQuestionUid?: string;
  /**
   * Is 'Other' answer with comment field
   */
  isOther?: boolean;
  answerValue: RadioAnswerValue;
  id?: string;
  uid?: string;
  createdAt?: string;
};

export const RadioQuestionType = { 1: 1 } as const;

export type RadioQuestionType =
  (typeof RadioQuestionType)[keyof typeof RadioQuestionType];

export type UpdateFileUploadScreenerQuestionDto = {
  text?: string;
  isRequired?: boolean;
  questionType?: FileUploadQuestionType;
};

export type UpdateSliderScaleScreenerQuestionDto = {
  text?: string;
  isRequired?: boolean;
  showStep?: boolean;
  questionType?: SliderScaleQuestionType;
  minValue?: number;
  maxValue?: number;
  step?: number;
};

export type UpdateTextboxScreenerQuestionDto = {
  text?: string;
  isRequired?: boolean;
  questionType?: TextboxQuestionType;
};

export type UpdateCheckboxScreenerQuestionDto = {
  text?: string;
  isRequired?: boolean;
  includeOtherOption?: boolean;
  logic?: ScreenerQuestionSkipLogic;
  questionType?: CheckboxQuestionType;
  answers?: Array<CreateCheckboxScreenerQuestionAnswerDto>;
  matchType?: CheckboxMatchType;
  /**
   * Paired with `logic`: send `true` whenever `logic` is set and omit it (or
   * send `false`) whenever it is not. Setting one without the other is
   * rejected — the field is optional, but not independently so.
   */
  skipLogic?: boolean;
};

export type CreateCheckboxScreenerQuestionAnswerDto = {
  /**
   * Required  unless `isOther`, then text will be set to 'Other'
   */
  text?: string;
  /**
   * Is 'Other' answer with comment field
   */
  isOther?: boolean;
  answerValue: CheckboxAnswerValue;
};

export type UpdateRadioScreenerQuestionDto = {
  text?: string;
  isRequired?: boolean;
  includeOtherOption?: boolean;
  questionType?: RadioQuestionType;
  answers?: Array<CreateRadioScreenerQuestionAnswerDto>;
  /**
   * Send `true` only when at least one answer carries `goToQuestionUid`; a
   * `true` with no such answer is rejected. The reverse is tolerated — answers
   * may declare destinations while this stays unset.
   */
  skipLogic?: boolean;
};

export type CreateRadioScreenerQuestionAnswerDto = {
  /**
   * Required  unless `isOther`, then text will be set to 'Other'
   */
  text?: string;
  /**
   * Skip logic for radio questions. Uid of question to skip to.
   */
  goToQuestionUid?: string;
  /**
   * Is 'Other' answer with comment field
   */
  isOther?: boolean;
  answerValue: RadioAnswerValue;
};

export type CreateFileUploadScreenerQuestionDto = {
  text: string;
  isRequired?: boolean;
  questionType: FileUploadQuestionType;
};

export type CreateSliderScaleScreenerQuestionDto = {
  text: string;
  isRequired?: boolean;
  showStep?: boolean;
  questionType: SliderScaleQuestionType;
  minValue?: number;
  maxValue?: number;
  step?: number;
};

export type CreateTextboxScreenerQuestionDto = {
  text: string;
  isRequired?: boolean;
  questionType: TextboxQuestionType;
};

export type CreateCheckboxScreenerQuestionDto = {
  text: string;
  isRequired?: boolean;
  includeOtherOption?: boolean;
  logic?: ScreenerQuestionSkipLogic;
  questionType: CheckboxQuestionType;
  answers: Array<CreateCheckboxScreenerQuestionAnswerDto>;
  matchType?: CheckboxMatchType;
  /**
   * Paired with `logic`: send `true` whenever `logic` is set and omit it (or
   * send `false`) whenever it is not. Setting one without the other is
   * rejected — the field is optional, but not independently so.
   */
  skipLogic?: boolean;
};

export type CreateRadioScreenerQuestionDto = {
  text: string;
  isRequired?: boolean;
  includeOtherOption: boolean;
  questionType: RadioQuestionType;
  answers: Array<CreateRadioScreenerQuestionAnswerDto>;
  /**
   * Send `true` only when at least one answer carries `goToQuestionUid`; a
   * `true` with no such answer is rejected. The reverse is tolerated — answers
   * may declare destinations while this stays unset.
   */
  skipLogic?: boolean;
};

export type AddProgramProjectDto = {
  projectId: string;
};

export type UpdateProgramDto = {
  name?: string;
  description?: string;
};

export type ProgramParticipantsDto = {
  /**
   * Distinct profiles across the program's projects. Not the sum of the
   * projects' participant counts — someone in two projects counts once here,
   * which is the whole point of the view.
   */
  uniqueParticipants: number;
  sessionsCompleted: number;
  participants: Array<ProgramParticipantDto>;
};

export type ProgramParticipantJourneyEntryDto = {
  projectId: string;
  projectName: string;
  kindOfResearch?: KindOfResearch;
  researchConductType?: ResearchConductType;
  status?: ScreenerResponseStatus;
  /**
   * True when the researcher invited this person to this project and no screener
   * response exists yet — i.e. "asked, not heard back". This is the state the
   * mocks render as a pale "· invited" chip.
   */
  invitationOnly: boolean;
  /**
   * When this person entered this project — the screener response's or
   * invitation's `createdAt`. Lets the client order the journey and pick the
   * **most recent** project as follow-up-invite provenance
   * (`Invitation.sourceProjectId`). Absent only for legacy documents that
   * predate timestamps.
   */
  enteredAt?: string;
  /**
   * The messaging conversation already attached to this person for this
   * project, when one exists — from the screener response, or from the
   * invitation for invitation-only rows (the conversations service writes the
   * uid to both on create). Surfaced so the drawer's Message tab reuses the
   * existing thread instead of creating a duplicate conversation — the
   * messaging create endpoint does not dedupe. Absent when nobody has
   * messaged this person on this project yet.
   */
  conversationUid?: string;
};

export type ProgramParticipantProfileDto = {
  demographics?: ProfileDemographics;
  employment?: ProfileEmployment;
  location?: ProfileLocation;
  technical?: Technical;
  productsServices?: ProductsServicesInfo;
  shopping?: ShoppingInfo;
};

export type ProgramParticipantDto = {
  /**
   * Participants are identified by **profile**, never by user account. Screener
   * responses are keyed on `userId` internally, so this is the collapse point:
   * one person appearing in three projects is one row.
   */
  profileId: string;
  /**
   * The live profile's user account, for the drawer's Message tab — messaging
   * is keyed on users, not profiles. Absent when the profile has been deleted
   * since the response/invitation, in which case messaging is impossible and
   * the client hides the tab.
   */
  userId?: string;
  firstName: string;
  /**
   * Surname initial only, as on every researcher-facing participant list.
   */
  lastName: string;
  location?: string;
  age?: string;
  /**
   * Identity for the roster row and the detail drawer, read from the **live**
   * profile rather than the response/invitation snapshot — so every row is
   * equally rich (invitation-only snapshots carry just a name) and shows who the
   * person is now. All derived server-side; no raw risk data crosses the wire.
   */
  picture?: string;
  jobTitle?: string;
  company?: string;
  /**
   * True when the profile passed advanced (Plaid) identity verification.
   */
  isIdVerified: boolean;
  /**
   * The profile's LinkedIn URL, set only when it exists **and** the profile's
   * employment is LinkedIn-verified — the same gate the project participants
   * table uses, so the client just renders it when present.
   */
  linkedinUrl?: string;
  /**
   * Self-reported profile sections for the drawer's Profile tab. Absent when
   * the live profile no longer exists (the snapshot name above still renders).
   */
  profile?: ProgramParticipantProfileDto;
  /**
   * Projects in this program where this person got as far as attending or being
   * paid.
   *
   * This is a definition local to programs: there is no canonical "completed"
   * grouping in `@common/utils` alongside `SLOT_RELEASING_STATUSES` and
   * `REJECTED_LIFECYCLE_STATUSES`. If one is ever added, reconcile with it
   * rather than keeping two.
   */
  sessionsCompleted: number;
  journey: Array<ProgramParticipantJourneyEntryDto>;
};

export type ProgramDto = {
  id: string;
  createdAt: string;
  organizationId: string;
  /**
   * A program belongs to exactly one team, never to an organization and never to
   * several teams.
   *
   * Not for simplicity's sake: the participant data is already team-scoped
   * (`team_respondents` is keyed on team), so a program spanning two teams would
   * present a participant list that is the union of two separate pools, and
   * sharing a participant into a new project would let one team recruit people
   * another team found. That is a data-sharing decision, and it should not
   * arrive as a side effect of a grouping feature.
   */
  teamId: string;
  name: string;
  description?: string;
  status: ProgramStatus;
  createdBy: string;
  updatedAt?: string;
};

export const ProgramStatus = {
  ACTIVE: 'ACTIVE',
  ARCHIVED: 'ARCHIVED',
} as const;

export type ProgramStatus = (typeof ProgramStatus)[keyof typeof ProgramStatus];

export type CreateProgramDto = {
  name: string;
  description?: string;
};

export type UpdateVisibilityDto = {
  name: ScreenerResponseVisibilityName;
  status: ScreenerResponseVisibilityStatus;
  code: ScreenerResponseCode;
};

export type ProjectResponsesPaginatedDto = {
  total_results?: number;
  page: number;
  results: Array<ProjectResponse>;
};

export type SurveyResponseProfileStats = {
  id: ObjectId;
  buckets: Array<string>;
  totals: {
    [key: string]: unknown;
  };
  reviews: {
    [key: string]: unknown;
  };
};

export type TeamProfile = {
  id: ObjectId;
  profileId: ObjectId;
  userId: ObjectId;
  organizationId: ObjectId;
  teamId: ObjectId;
  source: string;
  followers: Array<string>;
  tags: Array<string>;
  lists: Array<string>;
  surveys: Array<string>;
  reviews: Array<string>;
  stats: SurveyResponseProfileStats;
  lastPaidAt: {
    [key: string]: unknown;
  };
  createdAt: {
    [key: string]: unknown;
  };
  updatedAt: {
    [key: string]: unknown;
  };
  profile: ProfileLegacy;
};

export type ProfileLegacy = {
  [key: string]: unknown;
};

export type ExternalProject = {
  researcherId: string;
  bookingUrl?: string;
  /**
   * Link to cancel a meeting. If not set, bookingLink will be used.
   */
  meetingCancelLink?: string;
  bookedDate?: string;
  /**
   * IANA timezone. Example: 'America/New_York'
   */
  timezone?: string;
};

export type SurveyResponseInvite = {
  [key: string]: unknown;
};

export type SurveyResponseComplete = {
  autocompleteOn: string;
};

export type SurveyResponsePay = {
  [key: string]: unknown;
};

export type SurveyResponseReferrer = {
  [key: string]: unknown;
};

export type ProjectStatusHistoryLegacy = {
  [key: string]: unknown;
};

export type ProjectIncentiveRecordLegacy = {
  newIncentive: number;
  oldIncentive: number;
  id: ObjectId;
  createdAt: {
    [key: string]: unknown;
  };
};

export type ProjectFollowerLegacy = {
  userId: ObjectId;
  email: string;
  firstName: string;
  lastName: string;
  picture?: string;
};

export const AudienceTypes = { 1: 1, 2: 2 } as const;

export type AudienceTypes = (typeof AudienceTypes)[keyof typeof AudienceTypes];

export type PricingTierLegacy = {
  price: number;
  code: string;
};

export type SkipLogicMultipleAnswersBoolTypeLegacy = {
  id: number;
  text: string;
};

export type SkipLogicMultipleAnswersLegacy = {
  answer: string;
  logicType: ScreenerQuestionSkipLogicTypeLegacy;
};

export type ScreenerQuestionSkipLogicTypeLegacy = {
  id: number;
  text: string;
};

export type ScreenerQuestionSkipLogicLegacy = {
  answerUid: string;
  goToQuestionUid: string;
  skipLogicType: ScreenerQuestionSkipLogicTypeLegacy;
  multipleAnswers: Array<SkipLogicMultipleAnswersLegacy>;
  multipleAnswersBoolType: Array<SkipLogicMultipleAnswersBoolTypeLegacy>;
};

export type ScreenerQuestionAnswerLegacy = {
  id: ObjectId;
  uid: string;
  text: string;
  answerValue: number;
  goToQuestionUid: string;
  isOther: boolean;
  other: string;
};

export type ScreenerQuestionAnswerArrayLegacy = {
  answers: Array<ScreenerQuestionAnswerLegacy>;
};

export type ScreenerQuestionLegacy = {
  id: ObjectId;
  questionType: number;
  uid: string;
  text: string;
  answers: ScreenerQuestionAnswerArrayLegacy;
  isRequired: boolean;
  skipLogic: boolean;
  includeOtherOption: boolean;
  isEnd: boolean;
  logic: ScreenerQuestionSkipLogicLegacy;
  minValue: number;
  maxValue: number;
  step: number;
  order: number;
};

export type ScreenerQuestionArrayLegacy = {
  _questions: Array<ScreenerQuestionLegacy>;
};

export const ProjectRecruitingStatus = {
  RECRUITING: 'RECRUITING',
  RECRUITED: 'RECRUITED',
  PAUSED: 'PAUSED',
} as const;

export type ProjectRecruitingStatus =
  (typeof ProjectRecruitingStatus)[keyof typeof ProjectRecruitingStatus];

export const ProjectStatusLegacy = {
  DRAFT: 'DRAFT',
  DELETED: 'DELETED',
  APPROVED: 'APPROVED',
  PARTIALLYPAID: 'PARTIALLYPAID',
  PAID: 'PAID',
  ARCHIVED: 'ARCHIVED',
  PENDING: 'PENDING',
} as const;

export type ProjectStatusLegacy =
  (typeof ProjectStatusLegacy)[keyof typeof ProjectStatusLegacy];

export type ProjectLegacy = {
  id: ObjectId;
  name: string;
  status: ProjectStatusLegacy;
  closedMessage: string;
  closedAt: {
    [key: string]: unknown;
  };
  closedByUserId: ObjectId;
  paymentType: string;
  slug: string;
  inviteCode: string;
  kindOfResearch: number;
  internalName: string;
  hideCompanyName: boolean;
  ndaType: string;
  organizationId: ObjectId;
  teamId: ObjectId;
  userId: ObjectId;
  createdAt: {
    [key: string]: unknown;
  };
  updatedAt: {
    [key: string]: unknown;
  };
  publishedAt: {
    [key: string]: unknown;
  };
  expireAt: {
    [key: string]: unknown;
  };
  recruitingStatus?: ProjectRecruitingStatus;
  screenerQuestions: ScreenerQuestionArrayLegacy;
  ageGroups?: Array<AgeGroup>;
  education: Array<string>;
  ethnicity?: Array<Ethnicities>;
  pricingTier: PricingTierLegacy;
  isQuantitativeStudy: boolean;
  exposeParticipantEmails: boolean;
  talkToAudience?: AudienceTypes;
  ndaFileUrl: string;
  ndaLink: string;
  inviteSurveyLink: string;
  locationRespondentInPerson: ProjectLocationInPersonLegacy;
  followers: Array<ProjectFollowerLegacy>;
  incentiveHistory: Array<ProjectIncentiveRecordLegacy>;
  companySize?: Array<CompanySize>;
  description: string;
  companyName: string;
  calendars: Array<string>;
  visibility?: ProjectVisibility;
  campaigns: Array<string>;
  incentive: number;
  timeMinutesRequired: number;
  cities: Array<ProjectCitiesLegacy>;
  exportCSVUrl: string;
  country?: ProjectCountryCode;
  countries?: Array<ProjectCountryCode>;
  professionalIndustry: Array<string>;
  professionalJobTitles: Array<JobTitleLegacy>;
  researchConductType: number;
  numberOfParticipants: number;
  genders: string;
  history: Array<ProjectStatusHistoryLegacy>;
  closed: boolean;
  user: ProjectUserLegacy;
  filtering: ProjectFilteringLegacy;
  remoteCommunicationDetails: string;
  specialInstructions: string;
  referralBonus: number;
  saved: boolean;
  hiddenForRespondent: boolean;
  desiredSkills: Array<SkillLegacy>;
  topics: Array<TopicLegacy>;
  score: number;
  topicMatched: string;
  householdIncome?: Array<HouseholdIncome>;
  autopaused: boolean;
  autopauseWarn: boolean;
  task: Task;
  externalResearcher: ExternalResearcher;
  automations?: ProjectAutomations;
  metrics?: ProjectMetrics;
  metadata?: ProjectMetadata;
  rejectionReason?: ProjectRejectionReason;
};

export type SurveyResponseInviter = {
  [key: string]: unknown;
};

export type SurveyResponseInvitation = {
  [key: string]: unknown;
};

export const SurveyResponseStatus = {
  ATTENDED: 'ATTENDED',
  CANCELLED: 'CANCELLED',
  INVITED: 'INVITED',
  INVALIDRESPONSE: 'INVALIDRESPONSE',
  NOSHOW: 'NOSHOW',
  PAID: 'PAID',
  PENDING: 'PENDING',
  SCHEDULED: 'SCHEDULED',
} as const;

export type SurveyResponseStatus =
  (typeof SurveyResponseStatus)[keyof typeof SurveyResponseStatus];

export type ProjectResponse = {
  id: ObjectId;
  projectId: ObjectId;
  qualified: boolean;
  qualifiedPendingIdVerification?: boolean;
  qualifiedPendingManualReview?: boolean;
  manualReviewAudit?: {
    [key: string]: unknown;
  };
  percentage: number;
  ndaUrl?: string;
  userId: ObjectId;
  status: SurveyResponseStatus;
  favorite: boolean;
  hidden: boolean;
  isMatch: boolean;
  responses: Array<string>;
  totalTime: number;
  agree: boolean;
  clientOrigin: {
    [key: string]: unknown;
  };
  isNew: boolean;
  matching: {
    [key: string]: unknown;
  };
  emailEvents: {
    [key: string]: unknown;
  };
  visibility: {
    [key: string]: unknown;
  };
  history: Array<string>;
  invitation: SurveyResponseInvitation;
  inviteSentAt: string;
  booking: {
    [key: string]: unknown;
  };
  organizationFlag: boolean;
  inviteId: string;
  inviteCode: string;
  invite: {
    [key: string]: unknown;
  };
  inviter: SurveyResponseInviter;
  createdAt: string;
  updatedAt: string;
  scheduledAt: string;
  statusUpdatedAt: string;
  project: ProjectLegacy;
  profile: ProfileLegacy;
  referrer?: SurveyResponseReferrer;
  review: ReviewV2;
  payouts: Array<string>;
  conversationUid: string;
  rescheduleOverriden: boolean;
  projectStarted: boolean;
  task?: Task;
  customIncentive?: number;
  pay?: SurveyResponsePay;
  complete?: SurveyResponseComplete;
  autoInvite?: SurveyResponseInvite;
  externalProject?: ExternalProject;
  teamProfile: TeamProfile;
  stats: SurveyResponseProfileStats;
  teamPastParticipant: number;
};

export type SurveyResponseFilters = {
  limit: number;
  sort: string;
  reviewed?: boolean;
  /**
   * TODO update frontend to use the new endpoint
   *
   * @deprecated
   */
  projectName?: string;
  hidden?: boolean;
  projectId?: string;
  query?: string;
  currentPage?: number;
  skip?: number;
  minPercentage?: number;
  maxPercentage?: number;
  status?: string;
  qualified?: boolean;
  socialLinks?: string;
  skillsFilter?: string;
  lastPaidAt?: string;
  favorite?: boolean;
  /**
   * request (meaning "include rejected rows", the default). Ignored.
   *
   * @deprecated
   */
  rejected?: boolean;
  /**
   * When true, only responses the researcher manually rejected (via the
   * "Not a fit" flow) — i.e. rows with a `disqualification` record. Mirrors
   * the paginated list's `rejected` param so CSV exports match the on-screen
   * Rejected view.
   */
  rejectedOnly?: boolean;
  visibilityStatus?: string;
  includeTeamProfile?: boolean;
  includeStats?: boolean;
};

export type RespondentTransactionsSearchResultDto = {
  /**
   * The total number of results possible, which is only provided in the response when `includeCount` is passed as `true` within a request.
   */
  totalResults?: number;
  page: number;
  pageSize: number;
  results: Array<RespondentTransaction>;
  summary?: SummaryInRespondentTransaction;
};

export type SummaryInRespondentTransaction = {
  totalCount?: number;
  total?: number;
  totalFee?: number;
  totalIncentive?: number;
  totalTip?: number;
};

export type MasterTransactionCompact = {
  id: string;
  createdAt: string;
  userId?: string;
  type: MasterTransactionType;
  note?: string;
  updatedAt?: string;
};

export const RespondentTransactionStatus = {
  CREATED: 'CREATED',
  CANCELLED: 'CANCELLED',
} as const;

export type RespondentTransactionStatus =
  (typeof RespondentTransactionStatus)[keyof typeof RespondentTransactionStatus];

export const MethodologyType = {
  ONE_ON_ONE: 'oneOnOne',
  FOCUS_GROUP: 'focusGroup',
  UNMODERATED_STUDY: 'unmoderatedStudy',
  SURVEY: 'survey',
  DIARY_STUDY: 'diaryStudy',
  IN_RESPONDENT_HOME: 'inRespondentHome',
  IN_RESPONDENT_OFFICE: 'inRespondentOffice',
  AI_MODERATED_INTERVIEW: 'aiModeratedInterview',
} as const;

export type MethodologyType =
  (typeof MethodologyType)[keyof typeof MethodologyType];

export type RespondentTransaction = {
  id: string;
  createdAt: string;
  projectId: string;
  organizationId: string;
  teamId: string;
  surveyResponseId: string;
  masterTransactionId: string;
  incentive?: number;
  fee?: number;
  tip?: number;
  totalIncentive?: number;
  total?: number;
  credit?: number;
  creditCost?: number;
  creditBalance?: number;
  incentiveBalance?: number;
  participantType?: ParticipantType;
  methodologyType?: MethodologyType;
  respondent: RespondentInRespondentTransaction;
  project: ProjectInRespondentTransaction;
  status?: RespondentTransactionStatus;
  masterTransactions?: Array<MasterTransactionCompact>;
  updatedAt?: string;
};

export type GetRespondentTransactionsPaginatedDto = {
  page?: number;
  pageSize?: number;
  includeCount?: boolean;
  filters?: RespondentTransactionsFilters;
};

export type RespondentTransactionsFilters = {
  organizationId?: string;
  teamId: string;
  projectId?: string;
  userId?: string;
  search?: string;
  fromDate?: string;
  toDate?: string;
  /**
   * Only contain legacy transactions, otherwise will
   * contain both new and legacy pricing transactions.
   */
  legacy?: boolean;
};

export type BrainTreeWebhookDto = {
  bt_signature: string;
  bt_payload: string;
};

export type PricingChargesPaginatedDto = {
  /**
   * The total number of results possible, which is only provided in the response when `includeCount` is passed as `true` within a request.
   */
  totalResults?: number;
  page: number;
  pageSize: number;
  results: Array<PricingCharge>;
};

export type PricingCharge = {
  id: string;
  createdAt: string;
  teamPlanId: string;
  organizationId: string;
  teamId: string;
  masterTransactionId: string;
  referenceId: string;
  payment: Payment;
  type: PricingChargeType;
  status: PricingChargeStatus;
  details: Array<PricingChargeDetail>;
  failureDetails?: string;
  charge: number;
  updatedAt?: string;
};

export type GetPricingChargesPaginatedDto = {
  page?: number;
  pageSize?: number;
  includeCount?: boolean;
  filters?: PricingChargeFilters;
};

export type PricingChargeFilters = {
  type?: PricingChargeType;
  status?: PricingChargeStatus;
  projectId?: string;
  fromDate?: string;
  toDate?: string;
};

export type CheckV2PricingDataResultDto = {
  isV3?: boolean;
  existingCredits: MigrationCreditsSummaryDto;
};

export type MigrationCreditsSummaryDto = {
  creditAvailable: number;
  creditReserved: number;
  incentiveAvailable: number;
  incentiveReserved: number;
};

export type MigratePricingV2ToV3ResultDto = {
  isV3: boolean;
  newTeamPlanId: string;
  balanceBefore: MigrationCreditsSummaryDto;
  cancelledCredits: MigrationCreditsSummaryDto;
  creditsReAdded: MigrationCreditsReAddedDto;
  rateCardSet: boolean;
  steps: Array<MigrationStepResultDto>;
  message: string;
};

export type MigrationStepResultDto = {
  step: string;
  ok: boolean;
  error?: string;
};

export type MigrationCreditsReAddedDto = {
  recruiting: number;
  incentive: number;
};

export type MigratePricingV2ToV3Dto = {
  teamId: string;
  rateCard?: PricingTeamPlanRateCardDto;
  balances?: MigratePricingV2ToV3BalancesDto;
  comment?: string;
};

export type MigratePricingV2ToV3BalancesDto = {
  incentive?: number;
  recruiting?: number;
};

export type PricingTeamsUpgradeResponseDto = {
  results: Array<string>;
};

export type PricingTeamsUpgradeDto = {
  teamIds: Array<string>;
};

export type PricingTeamUpgradeDto = {
  sessionType: string;
  sessionCount: number;
  sessionRate: number;
  incentiveAmount: number;
  expiresIn: number;
  planName?: string;
  description?: string;
  teamCapabilities?: Array<PricingPlanComponentCapability>;
  orgCapabilities?: Array<PricingPlanComponentCapability>;
  customPlan?: boolean;
};

export type PricingTeamTransferToCustomPlanDto = {
  sessionType: ParticipantType;
  /**
   * Sessions purchased; each is 50 (b2b) or 30 (b2c) raw credits.
   */
  sessionCount: number;
  /**
   * Price per session in cents ($0..$100).
   */
  sessionRate: number;
  /**
   * Incentive credit issued after the plan is applied, in cents ($0..$100,000).
   */
  incentiveAmount: number;
  /**
   * Plan term in months.
   */
  expiresIn: number;
  planName: string;
  description: string;
  teamCapabilities?: Array<PricingPlanComponentCapability>;
  orgCapabilities?: Array<PricingPlanComponentCapability>;
};

export type PricingTeamPlanSummaryDto = {
  organizationId: string;
  teamId: string;
  plan: PricingTeamPlanPartialForSummaryDto | null;
  checkPoint: CheckPointEnhanced | null;
};

export type CheckPointEnhanced = {
  creditBalance: number;
  creditReserved: number;
  creditCostBalance: number;
  creditCostReserved: number;
  incentiveBalance: number;
  incentiveReserved: number;
  incentiveAvailable: number;
  creditAvailable: number;
  creditCostAvailable: number;
};

export type PricingTeamPlanAgreementForSummaryDto = {
  id: string;
  createdAt: string;
  commitment: number;
  expiresAt?: string;
  baseDiscount?: number;
  commitmentOwed: number;
  updatedAt?: string;
};

export type RateCard = {
  id: string;
  createdAt: string;
  organizationId: string;
  teamId: string;
  pricingTeamPlanId: string;
  baseDiscount?: number;
  customRates: boolean;
  matrix: RateCardMatrix;
  active: boolean;
  updatedAt?: string;
};

export const MarketingPricingPlanType = {
  PAYG: 'payg',
  BASIC: 'basic',
  ADVANCED: 'advanced',
  PAYG_V3: 'payg_v3',
  BASIC_V3: 'basic_v3',
  ADVANCED_V3: 'advanced_v3',
  CUSTOM: 'custom',
  CUSTOM_V3: 'custom_v3',
} as const;

export type MarketingPricingPlanType =
  (typeof MarketingPricingPlanType)[keyof typeof MarketingPricingPlanType];

export type PricingTeamPlanPartialForSummaryDto = {
  autoRenew: boolean;
  startsAt: string;
  endsAt?: string;
  pausedUntil?: string;
  pausedAt?: string;
  pausedDays?: number;
  pricingVersion?: number;
  teamPlanId: string;
  name: string;
  tags: Array<string>;
  participantType: ParticipantType;
  planType: MarketingPricingPlanType;
  renewalCost: number;
  planCredits: number;
  creditRate: number;
  teamCapabilities: PricingPlanComponentCapability;
  orgCapabilities: PricingPlanComponentCapability;
  rateCard: RateCard;
  agreement: PricingTeamPlanAgreementForSummaryDto;
};

export type PausePricingTeamPlanDto = {
  creditCardToken?: string;
};

export type UpdatePricingTeamPlanCapabilitiesDto = {
  details: PricingTeamPlanAuditCondensedDto;
  capabilities?: Array<CapabilityComponent>;
};

export const PricingPlanComponentCapabilityLevel = {
  TEAM: 'team',
  ORGANIZATION: 'organization',
} as const;

export type PricingPlanComponentCapabilityLevel =
  (typeof PricingPlanComponentCapabilityLevel)[keyof typeof PricingPlanComponentCapabilityLevel];

export const CapabilityComponentType = { CAPABILITY: 'capability' } as const;

export type CapabilityComponentType =
  (typeof CapabilityComponentType)[keyof typeof CapabilityComponentType];

export type CapabilityComponent = {
  type: CapabilityComponentType;
  capability: PricingPlanComponentCapability;
  level: PricingPlanComponentCapabilityLevel;
  rate?: number;
};

export type PricingTeamPlanAuditCondensedDto = {
  requesterId: string;
  adminId: string;
  comment: string;
};

export type UpdatePricingTeamPlanDto = {
  autoRenew?: boolean;
  active?: boolean;
  pausedUntil?: string;
  pausedAt?: string;
  pausedDays?: number;
  notPauseable?: boolean;
};

export type CreatePricingTeamPlanDto = {
  planId: string;
  creditCardToken?: string;
  noCharge?: boolean;
  rateCard?: CreateRateCardCondensedDto;
};

export type CreateRateCardCondensedDto = {
  baseDiscount?: number;
  customRates: boolean;
  matrix?: RateCardMatrix;
};

export type PricingTeamPlansPaginatedDto = {
  /**
   * The total number of results possible, which is only provided in the response when `includeCount` is passed as `true` within a request.
   */
  totalResults?: number;
  page: number;
  pageSize: number;
  results: Array<PricingTeamPlan>;
};

export const ManageTeamPlanRenewalEmailType = {
  PLAN_RENEWAL_REMINDER_3_MONTH: 'PLAN_RENEWAL_REMINDER_3_MONTH',
  PLAN_RENEWAL_REMINDER_1_MONTH: 'PLAN_RENEWAL_REMINDER_1_MONTH',
  PLAN_RENEWAL_REMINDER_1_WEEK: 'PLAN_RENEWAL_REMINDER_1_WEEK',
  PLAN_RENEWAL_REMINDER_2_DAY: 'PLAN_RENEWAL_REMINDER_2_DAY',
  PLAN_RENEWAL_ATTEMPT_1: 'PLAN_RENEWAL_ATTEMPT_1',
  PLAN_RENEWAL_ATTEMPT_2: 'PLAN_RENEWAL_ATTEMPT_2',
  PLAN_RENEWED: 'PLAN_RENEWED',
  PLAN_EXPIRED: 'PLAN_EXPIRED',
} as const;

export type ManageTeamPlanRenewalEmailType =
  (typeof ManageTeamPlanRenewalEmailType)[keyof typeof ManageTeamPlanRenewalEmailType];

export type PricingTeamPlanNotification = {
  type: ManageTeamPlanRenewalEmailType;
  sentAt: string;
};

export type PricingPlanAgreement = {
  id: string;
  createdAt: string;
  commitment: number;
  expiresAt?: string;
  baseDiscount?: number;
  updatedAt?: string;
};

export const CreditRateComponentType = { CREDIT_RATE: 'creditRate' } as const;

export type CreditRateComponentType =
  (typeof CreditRateComponentType)[keyof typeof CreditRateComponentType];

export type CreditRateComponent = {
  type: CreditRateComponentType;
  rate: number;
};

export const CreditComponentType = { CREDIT: 'credit' } as const;

export type CreditComponentType =
  (typeof CreditComponentType)[keyof typeof CreditComponentType];

export type CreditComponent = {
  type: CreditComponentType;
  /**
   * (amount of credits) * (type of credits)
   * type of credits: (30 credit == B2C, 50 credit == B2B)
   */
  creditAmount: number;
};

export type PricingTeamPlan = {
  id: string;
  createdAt: string;
  organizationId: string;
  teamId: string;
  planId: string;
  masterTransactionId: string;
  components: Array<
    CreditComponent | CapabilityComponent | CreditRateComponent
  >;
  agreement: PricingPlanAgreement;
  autoRenew: boolean;
  active: boolean;
  startsAt: string;
  endsAt?: string;
  pausedUntil?: string;
  pausedAt?: string;
  pausedDays?: number;
  notPauseable?: boolean;
  pricingVersion?: number;
  notifications?: Array<PricingTeamPlanNotification>;
  updatedAt?: string;
};

export type GetPricingTeamPlansPaginatedDto = {
  page?: number;
  pageSize?: number;
  includeCount?: boolean;
  filters?: PricingTeamPlanFilters;
};

export type PricingTeamPlanFilters = {
  active?: boolean;
  autoRenew?: boolean;
  pauseExpiring?: boolean;
  notPauseable?: boolean;
};

export type ReleasePricingCreditDto = {
  amount: number;
  note: string;
};

export type ProjectReservationsSummaryDto = {
  credits: number;
  incentives: number;
  participantType: ParticipantType;
  kindOfResearch: KindOfResearch;
};

export type ProjectRequirementsDto = {
  creditToBeReserved: number;
  creditToBePaidLater: number;
  creditTotal: number;
  incentiveToBeReserved: number;
  incentiveToBePaidLater: number;
  incentiveTotal: number;
  creditCardRequired: boolean;
  creditCardOnFile: boolean;
  defaultCreditCard?: CreditCardDto;
  creditCardExpired?: boolean;
  participantType: ParticipantType;
  prepayment?: PrepaymentRequirementsDto;
  /**
   * Present when the project's external invite link is on.
   */
  externalParticipants?: ExternalParticipantsRequirementsDto;
  /**
   * True when the organization is on the `publish-without-payment-commitment`
   * flag: the project can be published with any balance and no card, nothing
   * is charged, and `creditCardRequired` is false. Lets the UIs explain why
   * no payment is being asked for. Absent/false otherwise.
   */
  publishWithoutPaymentCommitment?: boolean;
};

export type ExternalParticipantsRequirementsDto = {
  /**
   * How many may complete the study (`recruitmentTargets.externalParticipants`).
   */
  count: number;
  /**
   * The flat fee per completed participant, in cents.
   */
  recruitmentFeeCents: number;
  /**
   * The researcher pays the incentive themselves, off the platform.
   */
  incentivePaidExternally: boolean;
};

export type PrepaymentRequirementsDto = {
  creditToBeReserved: number;
  incentiveToBeReserved: number;
  creditToBePaidLater: number;
  incentiveToBePaidLater: number;
  creditCardRequired: boolean;
  availableBalanceCredits: number;
  availableBalanceIncentives: number;
  shortfallCredits: number;
  shortfallIncentives: number;
  minimumDepositCredits: number;
  minimumDepositIncentives: number;
  canPublishWithoutPayment: boolean;
  requiresPayment: boolean;
  recruitmentHoldRequiredCredits: number;
  recruitmentHoldRequiredIncentives: number;
  coveredByRecruitmentBalanceCredits: number;
  coveredByRecruitmentBalanceIncentives: number;
};

export type ReservePricingCreditDto = {
  type: PricingCreditType;
  /**
   * if incentive then its in USD cents
   */
  amount: number;
  projectId: string;
};

export type TransferPricingCreditDto = {
  /**
   * Optional note from system or CS
   */
  note?: string;
  type: PricingCreditType;
  /**
   * if incentive then its in USD cents
   */
  amount: number;
  toTeamId?: string;
  toOrganizationId?: string;
  toType?: PricingCreditType;
};

export type PricingCreditsPaginatedDto = {
  /**
   * The total number of results possible, which is only provided in the response when `includeCount` is passed as `true` within a request.
   */
  totalResults?: number;
  page: number;
  pageSize: number;
  results: Array<PricingCredit>;
};

export type PricingCredit = {
  id: string;
  createdAt: string;
  organizationId: string;
  teamId: string;
  teamPlanId: string;
  masterTransactionId: string;
  chargeId?: string;
  /**
   * Optional note from system or CS
   */
  note?: string;
  type: PricingCreditType;
  /**
   * if incentive then its in USD cents
   */
  amount: number;
  /**
   * cost in USD cents for all the credits, if type is incentive then it should be the same as amount
   */
  cost: number;
  /**
   * this is the unit cost in USD cents of each credit, if type is incentive then it should be 1
   */
  unitCost: number;
  /**
   * starts as amount and should eventually go to 0
   */
  available: number;
  reserved: number;
  /**
   * can be null for incentive
   */
  expiresAt?: string;
  /**
   * can be null for incentive
   */
  expired?: number;
  expiryReminderSent?: boolean;
  reservedExpiryReminderSent?: boolean;
  /**
   * amount of credits cancelled
   */
  cancelled?: number;
  issued: PricingCreditIssuedType;
  issuedAt: string;
  /**
   * only set if credits provided by a promo
   */
  promotionId?: string;
  /**
   * for filtering convenience, controlled by plan
   */
  paused: boolean;
  reservations: Array<PricingCreditReservation>;
  consumers: Array<PricingCreditConsumer>;
  transfers: Array<PricingCreditTransfer>;
  pausedDays?: number;
  updatedAt?: string;
};

export type GetPricingCreditsPaginatedDto = {
  page?: number;
  pageSize?: number;
  includeCount?: boolean;
  filters?: PricingCreditFilters;
  sort?: PricingCreditSort;
  sortOrder?: NumericSortOrder;
};

export const NumericSortOrder = { 1: 1, '-1': -1 } as const;

export type NumericSortOrder =
  (typeof NumericSortOrder)[keyof typeof NumericSortOrder];

export const PricingCreditSort = {
  EXPIRES_AT: 'expiresAt',
  CREATED_AT: 'createdAt',
} as const;

export type PricingCreditSort =
  (typeof PricingCreditSort)[keyof typeof PricingCreditSort];

export type PricingCreditFilters = {
  type?: PricingCreditType;
  available?: number;
  hasAvailable?: boolean;
  allConsumed?: boolean;
  paused?: boolean;
  expiresAt?: string;
};

export type UpsertOrganizationRateCardDto = {
  matrix: RateCardMatrix;
  applyToNewTeams: boolean;
  comment: string;
};

export type OrganizationRateCardStateDto = {
  rateCard?: OrganizationRateCard;
  defaultMatrix: RateCardMatrix;
  isOrganizationOnPricingV3: boolean;
};

export type OrganizationRateCard = {
  id: string;
  createdAt: string;
  organizationId: string;
  matrix: RateCardMatrix;
  applyToNewTeams: boolean;
  active: boolean;
  createdByAdminId: string;
  comment: string;
  updatedAt?: string;
};

export type UpdateBalanceManagementSettingsDto = {
  notifications?: NotificationSettingsDto;
  autoTopUp?: AutoTopUpSettingsDto;
};

export type AutoTopUpConfigDto = {
  enabled: boolean;
  threshold?: number;
  amount?: number;
};

export type AutoTopUpSettingsDto = {
  credit?: AutoTopUpConfigDto;
  incentive?: AutoTopUpConfigDto;
};

export type NotificationConfigDto = {
  enabled: boolean;
  threshold?: number;
};

export type NotificationSettingsDto = {
  credit?: NotificationConfigDto;
  incentive?: NotificationConfigDto;
};

export type BalanceManagementSettingsResponseDto = {
  notifications?: NotificationSettingsResponseDto;
  autoTopUp?: AutoTopUpSettingsResponseDto;
};

export type AutoTopUpConfigResponseDto = {
  enabled: boolean;
  threshold?: number;
  amount?: number;
};

export type AutoTopUpSettingsResponseDto = {
  credit?: AutoTopUpConfigResponseDto;
  incentive?: AutoTopUpConfigResponseDto;
};

export type NotificationConfigResponseDto = {
  enabled: boolean;
  threshold?: number;
};

export type NotificationSettingsResponseDto = {
  credit?: NotificationConfigResponseDto;
  incentive?: NotificationConfigResponseDto;
};

export type TeamMembersDto = {
  userId: string;
  roles: Array<TeamRole>;
  firstName: string;
  lastName: string;
};

export const TeamRole = {
  OWNER: 'owner',
  ADMIN: 'admin',
  MEMBER: 'member',
} as const;

export type TeamRole = (typeof TeamRole)[keyof typeof TeamRole];

export type UpdateJoinRequestDto = {
  status: UpdateJoinRequestStatus;
};

export const UpdateJoinRequestStatus = {
  APPROVED: 'approved',
  DENIED: 'denied',
} as const;

export type UpdateJoinRequestStatus =
  (typeof UpdateJoinRequestStatus)[keyof typeof UpdateJoinRequestStatus];

export type TeamSanitizedDto = {
  id: string;
  createdAt: string;
  organizationId: string;
  name: string;
  slug: string;
  imageUrl?: string;
  description: string;
  isPrivate: boolean;
  isActive: boolean;
  isDeleted: boolean;
  members: Array<TeamMember>;
  integrations?: Array<Integration>;
  customerId?: string;
  newPricing?: boolean;
  hasLegacyPricing?: boolean;
  noCreditReservation?: boolean;
  balanceManagementSettings?: BalanceManagementSettings;
  updatedAt?: string;
};

export type AutoTopUpConfig = {
  enabled?: boolean;
  threshold?: number;
  amount?: number;
};

export type AutoTopUpSettings = {
  credit?: AutoTopUpConfig;
  incentive?: AutoTopUpConfig;
};

export type NotificationConfig = {
  enabled?: boolean;
  threshold?: number;
  lastAlertSentAt?: string;
  lastAlertBalance?: number;
};

export type BalanceNotificationSettings = {
  credit?: NotificationConfig;
  incentive?: NotificationConfig;
};

export type BalanceManagementSettings = {
  notifications?: BalanceNotificationSettings;
  autoTopUp?: AutoTopUpSettings;
};

export type Integration = {
  name: TaskType;
  token: string;
};

export type TeamMember = {
  userId: string;
  isActive: boolean;
  roles: Array<TeamRole>;
  createdAt: string;
};

export type UpdateOrganizationDto = {
  trustLevel?: number;
};

export type UpdateNewTeamRequestDto = {
  status: UpdateNewTeamRequestStatus;
};

export const UpdateNewTeamRequestStatus = {
  APPROVED: 'approved',
  DENIED: 'denied',
} as const;

export type UpdateNewTeamRequestStatus =
  (typeof UpdateNewTeamRequestStatus)[keyof typeof UpdateNewTeamRequestStatus];

export type CreateNewTeamRequestDto = {
  teamName: string;
  description: string;
  imageUrl?: string;
};

export type OrganizationSanitizedDto = {
  id: string;
  createdAt: string;
  name: string;
  location?: Location;
  privateTeams: boolean;
  website: string;
  imageUrl: string;
  timezone: string;
  slug: string;
  plan: Plan;
  balance: Balance;
  users: Array<OrganizationMember>;
  phone?: string;
  isDeleted: boolean;
  skipNewPricing: boolean;
  forceNewPricing: boolean;
  scheduler: OrganizationApiCredentials;
  messaging: OrganizationApiCredentials;
  customerId?: string;
  invitations: Array<OrganizationInvitation>;
  features: Features;
  rate?: number;
  trustLevel: number;
  config: OrganizationConfig;
  requests: Array<OrganizationRequest>;
  ssoProviders: Array<SsoProvider>;
  domains: Array<ClaimedDomain>;
  identityProviders: Array<IdentityProvider>;
  participantDataExpiryMonths?: number;
  capabilities: Array<PricingPlanComponentCapability>;
  customerType?: OrganizationCustomerType;
  updatedAt?: string;
  additionalReceiptInfo: {
    [key: string]: unknown;
  };
};

export const OrganizationCustomerType = {
  API_PARTNER: 'api-partner',
  SELF_SERVE: 'self-serve',
} as const;

export type OrganizationCustomerType =
  (typeof OrganizationCustomerType)[keyof typeof OrganizationCustomerType];

export type OrganizationRequest = {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  userId: string;
  status: string;
  createdAt: string;
};

export type OrganizationConfig = {
  recruitmentPauseFactor: number;
  recruitmentMaxFactor: number;
};

export type OrganizationInvitation = {
  token: string;
  userId: string;
  inviterId: string;
  email: string;
  status: string;
  roles: Array<string>;
  createdAt: string;
  teams: Array<string>;
};

export type Organization = {
  id: string;
  createdAt: string;
  name: string;
  location?: Location;
  privateTeams: boolean;
  website: string;
  imageUrl: string;
  timezone: string;
  slug: string;
  plan: Plan;
  balance: Balance;
  users: Array<OrganizationMember>;
  phone?: string;
  isDeleted: boolean;
  skipNewPricing: boolean;
  forceNewPricing: boolean;
  scheduler: OrganizationApiCredentials;
  messaging: OrganizationApiCredentials;
  customerId?: string;
  invitations: Array<OrganizationInvitation>;
  features: Features;
  rate?: number;
  trustLevel: number;
  config: OrganizationConfig;
  requests: Array<OrganizationRequest>;
  ssoProviders: Array<SsoProvider>;
  domains: Array<ClaimedDomain>;
  identityProviders: Array<IdentityProvider>;
  participantDataExpiryMonths?: number;
  newTeamRequests?: Array<NewTeamRequest>;
  capabilities: Array<PricingPlanComponentCapability>;
  customerType?: OrganizationCustomerType;
  additionalReceiptInfo: {
    [key: string]: unknown;
  };
  updatedAt?: string;
};

export type IndustriesPaginatedDto = {
  /**
   * The total number of results possible, which is only provided in the response when `includeCount` is passed as `true` within a request.
   */
  totalResults?: number;
  page: number;
  pageSize: number;
  results: Array<Industry>;
};

export type Industry = {
  id: string;
  createdAt: string;
  name: string;
  updatedAt?: string;
};

export type UpdateProjectCalendlyEventTypesDto = {
  /**
   * List of external scheduling event types to link to the project. Send empty array to clear all event types.
   */
  eventTypes: Array<ProjectCalendlyEventTypeDto>;
};

export type ProjectCalendlyEventTypeDto = {
  /**
   * Canonical reference (unique identifier) for the event type
   */
  uri: string;
  /**
   * Human-readable name of the event type
   */
  name: string;
  /**
   * URL to schedule this event type
   */
  schedulingUrl: string;
  /**
   * Duration of the event in minutes
   */
  duration: number;
  /**
   * Whether the event type is active
   */
  active: boolean;
};

export type GetProjectCalendlyEventTypesDto = {
  /**
   * List of Calendly event types linked to the project
   */
  eventTypes: Array<ProjectCalendlyEventTypeDto>;
};

export type ProjectsPaginatedDto = {
  /**
   * The total number of results possible, which is only provided in the response when `includeCount` is passed as `true` within a request.
   */
  totalResults?: number;
  page: number;
  pageSize: number;
  results: Array<Project>;
};

export type ProjectStatsDto = {
  responsesCount: number;
  qualifiedResponsesCount: number;
  invitedResponsesCount: number;
  scheduledResponsesCount: number;
  attendedResponsesCount: number;
  paidResponsesCount: number;
  notVisibleCount: number;
  completesCount: number;
  viewCount: number;
  clickCount: number;
};

export type AdvancedTargeting = {
  relationshipStatus?: RelationshipStatus;
  languages?: Array<ParticipantLanguage>;
  numberOfChildren?: number;
  numberOfChildrenLiveAtHome?: number;
  childrenBirthYears?: Array<string>;
  livingSituation?: LivingSituation;
  pets?: Array<Pet>;
  homeOwner?: boolean;
  disabilityType?: Array<DisabilityType>;
  nationality?: Array<Nationality>;
  areaType?: AreaType;
  webcam?: boolean;
  mobilePhoneWithInternet?: boolean;
  headsetWithMicrophone?: boolean;
  programmingLanguages?: Array<ProgrammingLanguage>;
  browsers?: Array<Browser>;
  smartphoneOS?: Array<SmartphoneOs>;
  tabletOS?: Array<TabletOs>;
  computerOS?: Array<ComputerOs>;
  assistiveTechnologies?: Array<AssistiveTechnology>;
  cloudPlatforms?: Array<CloudPlatform>;
  codingAITools?: Array<CodingAiTool>;
  designPrototypingTools?: Array<DesignPrototypingTool>;
  foodDeliveryServices?: Array<FoodDeliveryService>;
  healthWellnessApps?: Array<HealthWellnessApp>;
  onlineBankingTools?: Array<OnlineBankingTool>;
  onlineLearningPlatforms?: Array<OnlineLearningPlatform>;
  onlineMarketplacePlatforms?: Array<OnlineMarketplacePlatform>;
  onlineTravelBooking?: Array<OnlineTravelBooking>;
  productivitySoftware?: Array<ProductivitySoftware>;
  smartHomeDevices?: Array<SmartHomeDevice>;
  socialNetworkPlatforms?: Array<SocialNetworkPlatform>;
  streamingServices?: Array<StreamingService>;
  vehicleBrands?: Array<VehicleBrand>;
  militaryServiceStatus?: MilitaryServiceStatus;
  workSetting?: WorkSetting;
  employmentStatuses?: Array<EmploymentStatus>;
  onlineShoppingFrequency?: OnlineShoppingFrequency;
};

export type ProjectTopic = {
  id: string;
  name: string;
};

export type ProjectExternalQuestionOption = {
  /**
   * Partner-stable identifier, unique within the question.
   */
  id: string;
  /**
   * The option as shown to the participant.
   */
  text: string;
};

export const ExternalQuestionType = {
  RADIO: 'RADIO',
  CHECKBOX: 'CHECKBOX',
  TEXT: 'TEXT',
  NUMBER: 'NUMBER',
  OTHER: 'OTHER',
} as const;

export type ExternalQuestionType =
  (typeof ExternalQuestionType)[keyof typeof ExternalQuestionType];

export type ProjectExternalQuestion = {
  /**
   * Partner-stable identifier; bulk answer rows reference it.
   */
  questionId: string;
  questionType: ExternalQuestionType;
  /**
   * The question as shown to the participant.
   */
  text: string;
  /**
   * Choices for RADIO/CHECKBOX questions; answer rows and quota conditions
   * reference them by `id`. Required (non-empty, unique ids) on those types,
   * absent on the others.
   */
  options?: Array<ProjectExternalQuestionOption>;
};

export type ProjectUserDto = {
  id?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
};

export const ProjectRecruitingStatusReason = {
  NO_MARKETPLACE_TARGET: 'noMarketplaceTarget',
} as const;

export type ProjectRecruitingStatusReason =
  (typeof ProjectRecruitingStatusReason)[keyof typeof ProjectRecruitingStatusReason];

export const ProjectReviewStatus = {
  PENDING: 'PENDING',
  APPROVED: 'APPROVED',
  TRUSTED: 'TRUSTED',
  WATCHING: 'WATCHING',
  REJECTED: 'REJECTED',
} as const;

export type ProjectReviewStatus =
  (typeof ProjectReviewStatus)[keyof typeof ProjectReviewStatus];

export type ProjectStatusHistory = {
  id: string;
  createdAt: string;
  oldRecruitingStatus?: ProjectRecruitingStatus;
  newRecruitingStatus?: ProjectRecruitingStatus;
  newStatus?: ProjectStatus;
  oldStatus?: ProjectStatus;
  projectReviewStatus?: ProjectReviewStatus;
  rejectionReason?: ProjectRejectionReason;
  auto?: boolean;
  userId?: string;
  reason?: ProjectRecruitingStatusReason;
  updatedAt?: string;
};

export type ProjectConfig = {
  /**
   * How long after publication the project stays hidden from participants who
   * have no match for it (see the grace-period filter util). Matched
   * participants are unaffected — they see the project immediately.
   */
  unmatchedGracePeriodInMinutes?: number;
  /**
   * How long a newly created profile-project match for this project waits
   * before it counts as a match (PX-395). Copied onto each match as
   * `delayInMinutes` at creation, so it is read once per project per scoring
   * run rather than per match, and so changing it here only affects matches
   * created afterwards. Stamped at publish from the `DelayNewMatches` flag
   * (12 hours while active, otherwise 0) and tunable in the DB afterwards.
   */
  matchedDelayInMinutes?: number;
};

export type GeoJsonPoint = {
  type: string;
  coordinates: Array<number>;
};

export type CalendlyEventType = {
  uri: string;
  name: string;
  schedulingUrl: string;
  duration: number;
  active: boolean;
};

export type ExternalScheduling = {
  calendly?: Array<CalendlyEventType>;
};

export type ProjectEnhancementBodyFragments = {
  /**
   * What the participant will do, completing "We have a study where ...",
   * e.g. "you'll review seasonal chocolate packaging and talk through how you
   * choose what to buy"
   */
  activityClause: string;
};

export type ProjectEnhancementSubjectVariants = {
  /**
   * "$15 for 45 min: Seeking Easter chocolate buyers"
   */
  offerFirst: string;
  /**
   * "do you buy Easter chocolate? $15 for 45 min"
   */
  experienceQuestion: string;
  /**
   * "are you an Easter chocolate buyer? $15 for 45 min"
   */
  audienceQuestion: string;
  /**
   * "45 minutes on how you shop for Easter chocolate ($15)"
   */
  activityLed: string;
  /**
   * "a short study on Easter chocolate shopping"
   */
  noCompensation: string;
  /**
   * "I'm looking for Easter chocolate buyers ($15, 45 min)"
   */
  researcherVoice: string;
};

export type ProjectEnhancementSubjectFragments = {
  /**
   * Who the study is for, e.g. "Easter chocolate buyers"
   */
  audienceNoun: string;
  /**
   * What the study is about, e.g. "Easter chocolate shopping"
   */
  topicNoun: string;
  /**
   * e.g. "do you buy Easter chocolate?"
   */
  experienceQuestion: string;
  /**
   * e.g. "are you an Easter chocolate buyer?"
   */
  audienceQuestion: string;
  /**
   * Completes "N minutes on ...", e.g. "how you shop for Easter chocolate"
   */
  activityPhrase: string;
};

export type ProjectEnhancement = {
  /**
   * LLM-enhanced project description for matching emails
   * Generated when project is published (if feature flag enabled)
   */
  enhancedDescription?: string;
  /**
   * LLM-enhanced target audience description for matching emails
   * Generated when project is published (if feature flag enabled)
   */
  enhancedAudience?: string;
  /**
   * LLM-generated email subject line question for matching emails
   * Format: "Are you [descriptor]?"
   * Generated when project is published (if feature flag enabled)
   */
  enhancedSubject?: string;
  /**
   * Subject-line building blocks, generated alongside the fields above.
   * Absent on projects enhanced before this field existed.
   */
  subjectFragments?: ProjectEnhancementSubjectFragments;
  /**
   * The six subject-line A/B variants rendered from `subjectFragments`.
   * Absent on projects enhanced before this field existed.
   */
  subjectVariants?: ProjectEnhancementSubjectVariants;
  /**
   * Body-copy building blocks for the recruitment-email body A/B test,
   * generated alongside the fields above. Absent on projects enhanced
   * before this field existed.
   */
  bodyFragments?: ProjectEnhancementBodyFragments;
};

export type InterviewConcept = {
  uid: string;
  /**
   * Researcher-only internal label. Never shown to participants.
   */
  internalName: string;
  /**
   * Researcher-authored heading; stamped for probes, not shown to participants.
   */
  title?: string;
  /**
   * Researcher-authored body; stamped for probes, not shown to participants.
   */
  description?: string;
  order: number;
  /**
   * Reuses the question media shape verbatim — same upload endpoint, same S3
   * prefix, same researcher-only `aiContext`. Stamped onto each expanded
   * question as `conceptMedia` so it stays on screen for the whole concept loop.
   */
  media?: InterviewQuestionMedia;
};

export type InterviewQuestionMedia = {
  type: MediaType;
  /**
   * LINK only — absolute https URL shown in a sandboxed iframe.
   */
  url?: string;
  /**
   * LINK only — render the page inside our embed pane. Absent means "embed"
   * (the behavior before the flag existed). Defaulted by the editor from the
   * embed check (`POST .../media/embed-check`), overridable by the researcher.
   * When false the participant only gets the open-in-new-tab control, which
   * is what a page sending `X-Frame-Options` needs anyway — the pane would
   * be blank.
   */
  embedEnabled?: boolean;
  /**
   * IMAGE only — durable S3 bucket keys, in display order; re-signed on
   * every read.
   */
  imageKeys?: Array<string>;
  /**
   * PDF only — durable S3 bucket key for a single PDF; re-signed on every
   * read. Never leaked to participants (they get `pdfUrl`).
   */
  pdfKey?: string;
  /**
   * Participant-facing caption (image) or link label.
   */
  label?: string;
  /**
   * Researcher-only description of the media, fed to the AI moderator.
   */
  aiContext?: string;
  aiContextGeneratedAt?: string;
  /**
   * Hash of the source the `aiContext` was generated from (image content hash
   * or sha256 of the link URL) so the editor can detect a stale context.
   */
  aiContextSourceHash?: string;
};

export type InterviewConceptBlock = {
  /**
   * At least one, at most ten. The cap bounds the read-time expansion
   * (`concepts × questions`) together with the per-section question cap.
   */
  concepts: Array<InterviewConcept>;
  /**
   * How many concepts each participant is shown, drawn per participant from
   * the interview response id (nothing stored). Absent, or not below the
   * concept count, means all.
   */
  sampleSize?: number;
  /**
   * Show each participant the concepts in their own random order. Not
   * defaulted in the schema on purpose: absent means authored order, so
   * existing projects keep their behaviour. The builder turns it on for new
   * blocks.
   */
  randomizeOrder?: boolean;
};

export type InterviewFollowUpConfig = {
  type: FollowUpType;
  guidelines?: string;
};

export type InterviewQuestionOption = {
  uid: string;
  text: string;
  order: number;
};

export type InterviewQuestion = {
  uid: string;
  questionType: InterviewQuestionType;
  text: string;
  order: number;
  options?: Array<InterviewQuestionOption>;
  multiSelect?: boolean;
  showOtherOption?: boolean;
  randomizeOrder?: boolean;
  continueButtonText?: string;
  followUpConfig?: InterviewFollowUpConfig;
  preferredInputType?: string;
  media?: InterviewQuestionMedia;
  /**
   * Capture the participant's screen while they answer this question (and its
   * follow-ups). Only meaningful for audio/video interviews: the screen take
   * is started and stopped by the answer take, so a text question has no
   * recorder to pair it with.
   *
   * A property of the QUESTION, not of its media. The task being observed is
   * often an attachment (a prototype link, a design image), which is why this
   * first lived on `media` — but plenty of usability tasks are not: "open your
   * own inbox and find the last receipt" has a screen worth recording and
   * nothing to attach. Keeping it here also means it survives swapping the
   * attachment for another kind, which the media shape had to hand-carry.
   */
  recordScreen?: boolean;
  /**
   * Hide the section's stimulus while this question is asked. The
   * participant client hides — but does not unmount — the stimulus pane, so an
   * embedded page keeps its state for the next question that shows it again.
   * Meaningless (and ignored) on a section without `stimulus`; absent
   * means "show".
   */
  hideStimulus?: boolean;
  /**
   * TASK only — what the participant is shown (and read) when they press
   * "I'm stuck". Researcher-authored; the only "unblocking" a task offers,
   * since the moderator cannot see the screen. Absent means the stuck button
   * ends the task directly.
   */
  hint?: string;
  /**
   * TASK only — after "done", ask a fixed 1–5 ease rating (1 = very hard, 5 = very easy) (served as a
   * derived multiple-choice sub-question, see `expandTaskQuestions`). Absent
   * means true.
   */
  askRating?: boolean;
  /**
   * TASK only — after the task (and rating), let the moderator ask ONE
   * transcript-aware question about the experience ("why that rating?"
   * "what was hard?"). Stored as a follow-up exchange on the task answer.
   * Absent means true.
   */
  askDebrief?: boolean;
};

export type InterviewSection = {
  uid: string;
  name: string;
  order: number;
  questions?: Array<InterviewQuestion>;
  /**
   * Concept testing: when set, this section's questions are asked once per
   * concept. The loop is unrolled at read time into derived question uids —
   * nothing is stored per (question × concept). Absent on every existing
   * project, and absent means "ask the questions once", so this is purely
   * additive.
   */
  conceptBlock?: InterviewConceptBlock;
  /**
   * Stimulus: one optional attachment (images, a PDF, or an embed URL —
   * same shape and rules as a question's media) shown alongside EVERY question
   * in this section. The participant client keeps it mounted across the whole
   * section so an embedded page keeps its state between questions; a question
   * can opt out of showing it via `hideStimulus`, which hides but does
   * not unmount it.
   *
   * Absent on every existing project and absent means "no stimulus", so
   * this is purely additive. Draft-editable only, frozen at publish, and
   * covered by the same upload / validation / copy / reclamation rails as
   * question media (see `listSectionMediaHolders`).
   */
  stimulus?: InterviewQuestionMedia;
};

export type ClosingMessage = {
  closingMessage?: string;
  redirectOnComplete?: boolean;
  redirectUrl?: string;
};

export type RejectionScreen = {
  closingMessage?: string;
  redirectOnComplete?: boolean;
  redirectUrl?: string;
};

export type WelcomeScreen = {
  title?: string;
  message?: string;
};

export type ProjectIncentiveRecord = {
  id: string;
  createdAt: string;
  newIncentive?: number | null;
  oldIncentive: number;
  updatedAt?: string;
};

export type ProjectFollower = {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  picture?: string;
};

export type ProjectPricingMultiplierRoute = {
  /**
   * 'seniority' | 'function' | 'functionManagement'
   */
  kind: string;
  /**
   * The raw targeting enum value (a seniority or job function code).
   */
  key: string;
  socCode: string;
  occupationName: string;
  W: number;
  multiplier: number;
  tier: string;
};

export type ProjectPricingMultiplierTitle = {
  jobTitleId?: string | null;
  title: string;
  multiplier: number;
  tier: string;
  resolvedSoc?: string | null;
  occupationName?: string | null;
  capApplied?: string | null;
};

export const PricingMultiplierStrategy = {
  TITLES: 'titles',
  NO_TITLES: 'noTitles',
  B2C: 'b2c',
  NOT_PRICED: 'notPriced',
} as const;

export type PricingMultiplierStrategy =
  (typeof PricingMultiplierStrategy)[keyof typeof PricingMultiplierStrategy];

export type ProjectPricingMultiplier = {
  /**
   * The enforced multiplier — the one number billing reads.
   */
  multiplier: number;
  tier: string;
  strategy: PricingMultiplierStrategy;
  /**
   * Wage premium (occupation wage / national median); null when not wage-priced.
   */
  W?: number | null;
  /**
   * Cross-family average before snapping to a tier (titles strategy only).
   */
  studyAverage?: number | null;
  familiesCount?: number | null;
  titles?: Array<ProjectPricingMultiplierTitle>;
  /**
   * Strategy C route derivations, e.g. "finance + management -> 11-3031 ...".
   */
  routes?: Array<string>;
  /**
   * The same routes in display-ready parts (absent on pre-RX-626-fix stamps).
   */
  routeDetails?: Array<ProjectPricingMultiplierRoute>;
  notes?: Array<string>;
  /**
   * What was targeted AT PUBLISH, so a later targeting swap is visible.
   */
  jobTitleIdsPriced?: Array<string>;
  configVersion: number;
  wageTableVersion: string;
  routeTableVersion: number;
  engineVersion: string;
  computedAt: string;
};

export const ProjectCreationFlow = { V3: 'v3' } as const;

export type ProjectCreationFlow =
  (typeof ProjectCreationFlow)[keyof typeof ProjectCreationFlow];

export type ProjectCopied = {
  from?: ObjectId;
  by?: ObjectId;
  at?: string;
};

export type ProjectExternalInvites = {
  enabled: boolean;
  /**
   * The token in the share URL (`/participants/invite/<inviteCode>`). Random,
   * unique across projects, and never equal to `Project.inviteCode`.
   */
  inviteCode: string;
  /**
   * Admit straight to the interview without asking the screener questions.
   */
  skipScreener: boolean;
  /**
   * The researcher pays these participants the incentive themselves, off the
   * platform. They still pay each one out here, which charges the
   * recruitment fee and closes the participant out, but the payout carries
   * no incentive and no money is sent. Copied onto each response when it is
   * filed, so changing it mid-study never changes what an admitted
   * participant was told.
   */
  incentivePaidExternally: boolean;
};

export type Project = {
  id: string;
  createdAt: string;
  /**
   * Character Limit: min 3 and max 255
   */
  name: string;
  status: ProjectStatus;
  closedMessage?: string;
  closedAt?: string;
  closedByUserId?: string;
  archivedAt?: string;
  archivedByUserId?: string;
  paymentType?: string;
  slug: string;
  inviteCode?: string;
  kindOfResearch?: KindOfResearch;
  /**
   * Character Limit: max 255
   */
  internalName: string;
  companyName: string;
  hideCompanyName?: boolean;
  /**
   * Opts this project into the logged-out apply page. Set manually in the
   * database — there is deliberately no UI for it. Absent means off, so
   * every existing project stays gated until someone flips it.
   */
  publicApplicationsEnabled?: boolean;
  /**
   * External participants via a dedicated invite link. Absent means off;
   * when present every field is required. See `ProjectExternalInvites`.
   */
  externalInvites?: ProjectExternalInvites;
  organizationId: string;
  teamId: string;
  /**
   * The program this project belongs to, absent when it belongs to none.
   *
   * **The single record of program membership.** The program document does not
   * hold a `projectIds` array — this scalar is it. That makes
   * one-program-per-project inherent (a field holds one value), the reverse
   * lookup free (it is right here, not a query against every program), and a
   * deleted project leaves no dangling id behind.
   *
   * Membership is one-way by product rule, not by schema:
   * `programs.service.addProject` only sets this when it is unset, and there is
   * no path that clears it. See `../../programs/programs.md`.
   */
  programId?: string;
  userId: string;
  publishedAt?: string;
  expireAt?: string;
  copied?: ProjectCopied;
  creationFlow?: ProjectCreationFlow;
  recruitingStatus?: ProjectRecruitingStatus;
  screenerQuestions?: Array<ScreenerQuestion>;
  /**
   * Optimistic-concurrency version for `screenerQuestions` (ONCALL-2139).
   *
   * Every write path that replaces the screener array increments this field
   * (`$inc`). A client that hydrated the screener may send the version back
   * with its write; the write is rejected with 409 when the stored version has
   * moved on, instead of silently last-write-wins clobbering the array.
   * Absent on documents whose screener has never been written under this
   * scheme — readers treat missing as `0`.
   */
  screenerVersion?: number;
  ageGroups?: Array<AgeGroup>;
  minAge?: number;
  maxAge?: number;
  education?: Array<EducationLevel>;
  ethnicity?: Array<Ethnicities>;
  seniority?: Array<SeniorityLevel>;
  pricingTier?: PricingTier;
  /**
   * Pricing multiplier, stamped once at publish and read
   * by getCreditCostForProject for every quote and charge after that. Absent
   * = 1.00x. NOT related to the legacy pricingTier field above (a dollar
   * display amount from the pre-credits pricing model).
   */
  pricingMultiplier?: ProjectPricingMultiplier;
  exposeParticipantEmails?: boolean;
  talkToAudience: ParticipantAudienceType;
  ndaFileUrl?: string | null;
  ndaLink?: string | null;
  ndaType?: NdaType;
  inviteSurveyLink?: string;
  locationRespondentInPerson?: ProjectLocationInPerson;
  followers?: Array<ProjectFollower>;
  incentiveHistory?: Array<ProjectIncentiveRecord>;
  companySize?: Array<CompanySize>;
  businessOwner?: boolean;
  /**
   * Character Limit: max 800
   */
  description?: string;
  studyGoals?: string;
  welcomeScreen?: WelcomeScreen;
  rejectionScreen?: RejectionScreen;
  closingMessage?: ClosingMessage;
  interviewSections?: Array<InterviewSection>;
  /**
   * The aggregate feature-extraction schema version this project is pinned to.
   * Set once, on the project's first feature extraction, and frozen for life —
   * the feature-extraction pipeline reproduces this version's feature set for
   * every response, and the reader/frontend interpret the project's insights
   * through it. Absent until the first extraction runs.
   */
  featuresSchemaVersion?: number;
  /**
   * Feature-extraction schema versions this project is also extracted at, beside
   * the pin, so a new version's insights can be compared with the pinned ones.
   * Each version keeps its own insight documents; the pin stays what the
   * product shows unless a view asks for another version. Each interview that
   * completes is extracted at every version listed, and an eligibility change
   * rebuilds each of them.
   */
  featuresSchemaVersionExperimental?: Array<number>;
  interviewDeliveryMode?: InterviewDeliveryMode;
  studyLanguage?: string;
  /**
   * Languages participants may choose to respond in, beyond the study
   * language. Absent/empty = participants respond in the study language
   * only. The effective allowed set is always `responseLanguages ∪
   * {studyLanguage}`; writes are normalized (deduped, study language
   * dropped as implicit).
   */
  responseLanguages?: Array<string>;
  /**
   * LLM-enhanced project content
   * Contains enhanced description and audience for matching emails
   * Generated when project is published (if feature flag enabled)
   */
  enhancement?: ProjectEnhancement;
  calendars?: Array<string>;
  externalScheduling?: ExternalScheduling;
  visibility?: ProjectVisibility;
  /**
   * Invitation-driven campaign tags; see `../campaigns.md`. Format mirrors
   * `CAMPAIGN_TAG_PATTERN` in `utils/recruitment-targets.util.ts`.
   */
  campaigns?: Array<string>;
  /**
   * Per-channel participant targets. `marketplace` mirrors
   * `numberOfParticipants`; absent on projects written before the field
   * existed, so read through `getRecruitmentTargets()` rather than directly.
   * See `../recruitment-targets.md`.
   */
  recruitmentTargets?: RecruitmentTargets;
  cities?: Array<ProjectCities>;
  geoPoints?: Array<GeoJsonPoint> | null;
  exportCSVUrl?: string;
  country: ProjectCountryCode;
  countries?: Array<ProjectCountryCode>;
  automations?: ProjectAutomations;
  metrics?: ProjectMetrics;
  metadata?: ProjectMetadata;
  config?: ProjectConfig;
  /**
   * Use professionalIndustries instead.
   * Stores industry name only.
   *
   * @deprecated
   */
  professionalIndustry?: Array<string>;
  income?: Income;
  /**
   * Stores industry id reference
   */
  professionalIndustries?: Array<Industry>;
  professionalJobTitles?: Array<JobTitle>;
  jobTitleFamilyGroupIds?: Array<number> | null;
  jobTitleGroupingVersion?: number | null;
  jobFunctions?: Array<JobFunction>;
  researchConductType?: ResearchConductType;
  /**
   * Limit of 1000 is a one-time limit to test projects, and will not be
   * set for standard projects.
   *
   *
   * Must be changed directly in the DB by engineering, before calling `/publish`
   * in Postman on the DRAFT project.
   *
   * Number Limit: min 1 and max 1000. 0 is allowed when the project has
   * `recruitmentTargets` (invite-only); see `IsMarketplaceTarget`.
   */
  numberOfParticipants: number;
  genders?: Gender;
  history?: Array<ProjectStatusHistory>;
  closed?: boolean;
  user?: ProjectUserDto;
  filtering?: ProjectFiltering;
  remoteCommunicationDetails?: string | null;
  externalScreenerLink?: string | null;
  externalQuestions?: Array<ProjectExternalQuestion>;
  specialInstructions?: string;
  referralBonus?: number;
  /**
   * Admin lever: show the project in the dashboard Top picks strip to every
   * eligible participant, matched or not.
   */
  boostProjectFeed?: boolean;
  /**
   * Order among boosted projects, ascending. Unset sorts after every set index.
   */
  boostProjectFeedIndex?: number;
  saved?: boolean;
  hiddenForRespondent?: boolean;
  desiredSkills?: Array<Skill>;
  /**
   * Topics: max 2. A General Population project needs at least one to publish
   * unless its organization allows optional topics.
   */
  topics?: Array<ProjectTopic>;
  score?: number;
  topicMatched?: string;
  householdIncome?: Array<HouseholdIncome>;
  autopaused?: boolean;
  autopauseWarn?: boolean;
  task?: Task;
  externalResearcher?: ExternalResearcher;
  /**
   * Number Limit: min 5 and max 1000
   */
  respondentRemuneration: number;
  /**
   * Number Limit: min 5 and max 1000
   */
  incentive: number;
  /**
   * Number Limit: min 5
   */
  timeMinutesRequired: number;
  /**
   * Number Limit: min 5
   */
  duration: number;
  /**
   * Flat advanced-targeting attribute map — the source of truth for this
   * project's advanced targeting. Read it via `getProjectAdvancedTargeting()`.
   */
  advancedTargeting?: AdvancedTargeting;
  keyQualifiers?: Array<KeyQualifier>;
  rejectionReason?: ProjectRejectionReason;
  projectSuccessEditPermission?: boolean;
  stats?: ProjectStatsDto;
  participantType?: ParticipantType;
  requiresNda?: boolean;
  totalAmount?: number;
  latestHistoryItem?: ProjectStatusHistory;
  updatedAt?: string;
};

export type UpdateProjectRecruitingStatusDto = {
  recruitingStatus: ProjectRecruitingStatus;
};

export type CloseProjectDto = {
  message?: string;
};

export type PauseProjectDto = {
  paused: boolean;
};

export type PublishAndUpdateTrustLevelDto = {
  reviewStatus?: ProjectReviewStatus;
};

export type RejectProjectDto = {
  rejectionType?: ProjectRejectionType;
  context?: string;
};

export type UpdateProjectAdminDto = {
  /**
   * Character Limit: min 3 and max 255
   */
  name: string;
  recruitingStatus?: ProjectRecruitingStatus;
  /**
   * Character Limit: max 800
   */
  description?: string;
  /**
   * Invitation-driven campaign tags; see `../campaigns.md`. Format mirrors
   * `CAMPAIGN_TAG_PATTERN` in `utils/recruitment-targets.util.ts`.
   */
  campaigns?: Array<string>;
  closed?: boolean;
  remoteCommunicationDetails?: string | null;
  referralBonus?: number;
  /**
   * Admin lever: show the project in the dashboard Top picks strip to every
   * eligible participant, matched or not.
   */
  boostProjectFeed?: boolean;
  /**
   * Order among boosted projects, ascending. Unset sorts after every set index.
   */
  boostProjectFeedIndex?: number;
  /**
   * Number Limit: min 5 and max 1000
   */
  incentive: number;
  /**
   * Number Limit: min 5
   */
  timeMinutesRequired: number;
  incentiveUpdateType: ProjectIncentiveUpdateType;
  /**
   * The marketplace target. 0 is accepted here for an invite-only project;
   * the controller checks it against the project's invited targets
   * (`participantTargetLimitViolation`), so a project without them still
   * needs at least 1.
   */
  numberOfParticipants: number;
  status?: ProjectStatus;
  reopen?: boolean;
};

export type ProcessAllProjectsAutoInviteResultDto = {
  /**
   * Projects matched by the auto-invite sweep filter.
   */
  eligible: number;
  /**
   * Eligible projects with a due applicant whose auto-invite run finished
   * without throwing. Eligible projects with nothing due are not run.
   */
  processed: number;
};

export type ProjectMetricsAndMetadataDto = {
  metrics?: ProjectMetrics;
  metadata?: ProjectMetadata;
};

export type ProjectDiy = {
  id: string;
  createdAt: string;
  /**
   * Character Limit: min 3 and max 255
   */
  name: string;
  status: ProjectStatus;
  closedMessage?: string;
  closedAt?: string;
  closedByUserId?: string;
  archivedAt?: string;
  archivedByUserId?: string;
  paymentType?: string;
  slug: string;
  inviteCode?: string;
  kindOfResearch?: KindOfResearch;
  /**
   * Character Limit: max 255
   */
  internalName: string;
  companyName: string;
  hideCompanyName?: boolean;
  /**
   * Opts this project into the logged-out apply page. Set manually in the
   * database — there is deliberately no UI for it. Absent means off, so
   * every existing project stays gated until someone flips it.
   */
  publicApplicationsEnabled?: boolean;
  /**
   * External participants via a dedicated invite link. Absent means off;
   * when present every field is required. See `ProjectExternalInvites`.
   */
  externalInvites?: ProjectExternalInvites;
  organizationId: string;
  teamId: string;
  /**
   * The program this project belongs to, absent when it belongs to none.
   *
   * **The single record of program membership.** The program document does not
   * hold a `projectIds` array — this scalar is it. That makes
   * one-program-per-project inherent (a field holds one value), the reverse
   * lookup free (it is right here, not a query against every program), and a
   * deleted project leaves no dangling id behind.
   *
   * Membership is one-way by product rule, not by schema:
   * `programs.service.addProject` only sets this when it is unset, and there is
   * no path that clears it. See `../../programs/programs.md`.
   */
  programId?: string;
  userId: string;
  publishedAt?: string;
  expireAt?: string;
  copied?: ProjectCopied;
  creationFlow?: ProjectCreationFlow;
  recruitingStatus?: ProjectRecruitingStatus;
  screenerQuestions?: Array<ScreenerQuestion>;
  /**
   * Optimistic-concurrency version for `screenerQuestions` (ONCALL-2139).
   *
   * Every write path that replaces the screener array increments this field
   * (`$inc`). A client that hydrated the screener may send the version back
   * with its write; the write is rejected with 409 when the stored version has
   * moved on, instead of silently last-write-wins clobbering the array.
   * Absent on documents whose screener has never been written under this
   * scheme — readers treat missing as `0`.
   */
  screenerVersion?: number;
  ageGroups?: Array<AgeGroup>;
  minAge?: number;
  maxAge?: number;
  education?: Array<EducationLevel>;
  ethnicity?: Array<Ethnicities>;
  seniority?: Array<SeniorityLevel>;
  pricingTier?: PricingTier;
  /**
   * Pricing multiplier, stamped once at publish and read
   * by getCreditCostForProject for every quote and charge after that. Absent
   * = 1.00x. NOT related to the legacy pricingTier field above (a dollar
   * display amount from the pre-credits pricing model).
   */
  pricingMultiplier?: ProjectPricingMultiplier;
  exposeParticipantEmails?: boolean;
  talkToAudience: ParticipantAudienceType;
  ndaFileUrl?: string | null;
  ndaLink?: string | null;
  ndaType?: NdaType;
  inviteSurveyLink?: string;
  locationRespondentInPerson?: ProjectLocationInPerson;
  followers?: Array<ProjectFollower>;
  incentiveHistory?: Array<ProjectIncentiveRecord>;
  companySize?: Array<CompanySize>;
  businessOwner?: boolean;
  /**
   * Character Limit: max 800
   */
  description?: string;
  studyGoals?: string;
  welcomeScreen?: WelcomeScreen;
  rejectionScreen?: RejectionScreen;
  closingMessage?: ClosingMessage;
  interviewSections?: Array<InterviewSection>;
  /**
   * The aggregate feature-extraction schema version this project is pinned to.
   * Set once, on the project's first feature extraction, and frozen for life —
   * the feature-extraction pipeline reproduces this version's feature set for
   * every response, and the reader/frontend interpret the project's insights
   * through it. Absent until the first extraction runs.
   */
  featuresSchemaVersion?: number;
  /**
   * Feature-extraction schema versions this project is also extracted at, beside
   * the pin, so a new version's insights can be compared with the pinned ones.
   * Each version keeps its own insight documents; the pin stays what the
   * product shows unless a view asks for another version. Each interview that
   * completes is extracted at every version listed, and an eligibility change
   * rebuilds each of them.
   */
  featuresSchemaVersionExperimental?: Array<number>;
  interviewDeliveryMode?: InterviewDeliveryMode;
  studyLanguage?: string;
  /**
   * Languages participants may choose to respond in, beyond the study
   * language. Absent/empty = participants respond in the study language
   * only. The effective allowed set is always `responseLanguages ∪
   * {studyLanguage}`; writes are normalized (deduped, study language
   * dropped as implicit).
   */
  responseLanguages?: Array<string>;
  /**
   * LLM-enhanced project content
   * Contains enhanced description and audience for matching emails
   * Generated when project is published (if feature flag enabled)
   */
  enhancement?: ProjectEnhancement;
  calendars?: Array<string>;
  externalScheduling?: ExternalScheduling;
  visibility?: ProjectVisibility;
  /**
   * Invitation-driven campaign tags; see `../campaigns.md`. Format mirrors
   * `CAMPAIGN_TAG_PATTERN` in `utils/recruitment-targets.util.ts`.
   */
  campaigns?: Array<string>;
  /**
   * Per-channel participant targets. `marketplace` mirrors
   * `numberOfParticipants`; absent on projects written before the field
   * existed, so read through `getRecruitmentTargets()` rather than directly.
   * See `../recruitment-targets.md`.
   */
  recruitmentTargets?: RecruitmentTargets;
  cities?: Array<ProjectCities>;
  geoPoints?: Array<GeoJsonPoint> | null;
  exportCSVUrl?: string;
  country: ProjectCountryCode;
  countries?: Array<ProjectCountryCode>;
  automations?: ProjectAutomations;
  metrics?: ProjectMetrics;
  metadata?: ProjectMetadata;
  config?: ProjectConfig;
  /**
   * Use professionalIndustries instead.
   * Stores industry name only.
   *
   * @deprecated
   */
  professionalIndustry?: Array<string>;
  income?: Income;
  /**
   * Stores industry id reference
   */
  professionalIndustries?: Array<Industry>;
  professionalJobTitles?: Array<JobTitle>;
  jobTitleFamilyGroupIds?: Array<number> | null;
  jobTitleGroupingVersion?: number | null;
  jobFunctions?: Array<JobFunction>;
  researchConductType?: ResearchConductType;
  /**
   * Limit of 1000 is a one-time limit to test projects, and will not be
   * set for standard projects.
   *
   *
   * Must be changed directly in the DB by engineering, before calling `/publish`
   * in Postman on the DRAFT project.
   *
   * Number Limit: min 1 and max 1000. 0 is allowed when the project has
   * `recruitmentTargets` (invite-only); see `IsMarketplaceTarget`.
   */
  numberOfParticipants: number;
  genders?: Gender;
  history?: Array<ProjectStatusHistory>;
  closed?: boolean;
  user?: ProjectUserDto;
  filtering?: ProjectFiltering;
  remoteCommunicationDetails?: string | null;
  externalScreenerLink?: string | null;
  externalQuestions?: Array<ProjectExternalQuestion>;
  specialInstructions?: string;
  referralBonus?: number;
  /**
   * Admin lever: show the project in the dashboard Top picks strip to every
   * eligible participant, matched or not.
   */
  boostProjectFeed?: boolean;
  /**
   * Order among boosted projects, ascending. Unset sorts after every set index.
   */
  boostProjectFeedIndex?: number;
  saved?: boolean;
  hiddenForRespondent?: boolean;
  desiredSkills?: Array<Skill>;
  /**
   * Topics: max 2. A General Population project needs at least one to publish
   * unless its organization allows optional topics.
   */
  topics?: Array<ProjectTopic>;
  score?: number;
  topicMatched?: string;
  householdIncome?: Array<HouseholdIncome>;
  autopaused?: boolean;
  autopauseWarn?: boolean;
  task?: Task;
  externalResearcher?: ExternalResearcher;
  /**
   * Number Limit: min 5 and max 1000
   */
  respondentRemuneration: number;
  /**
   * Number Limit: min 5 and max 1000
   */
  incentive: number;
  /**
   * Number Limit: min 5
   */
  timeMinutesRequired: number;
  /**
   * Number Limit: min 5
   */
  duration: number;
  /**
   * Flat advanced-targeting attribute map — the source of truth for this
   * project's advanced targeting. Read it via `getProjectAdvancedTargeting()`.
   */
  advancedTargeting?: AdvancedTargeting;
  keyQualifiers?: Array<KeyQualifier>;
  rejectionReason?: ProjectRejectionReason;
  projectSuccessEditPermission?: boolean;
  stats?: ProjectStatsDto;
  participantType?: ParticipantType;
  requiresNda?: boolean;
  totalAmount?: number;
  latestHistoryItem?: ProjectStatusHistory;
  /**
   * alias for number of participants
   */
  target?: number;
  responsesCount?: number;
  surveyLink?: string;
  updatedAt?: string;
};

export type PitchSuggestionResponseDto = {
  /**
   * The name of the project. One of name or description is required.
   */
  name: string;
  /**
   * The description of the project. One of name or description is required.
   */
  description: string;
  /**
   * The tone of this suggestion.
   */
  tone: 'professional' | 'casual' | 'discreet';
};

export type PitchSuggestionDto = {
  /**
   * The name of the project. One of name or description is required.
   */
  name: string;
  /**
   * The description of the project. One of name or description is required.
   */
  description: string;
};

export type CopyProjectDto = {
  /**
   * Program the copy should join at creation. Optional: a copy never inherits
   * the source project's program membership on its own — the researcher asks
   * for it (the "add the duplicate to the same program" checkbox), it is not
   * forced on them. Must be a program owned by the copied project's team and
   * not archived.
   */
  programId?: string;
};

export type CreateProjectDto = {
  /**
   * Character Limit: min 3 and max 255
   */
  name: string;
  kindOfResearch?: KindOfResearch;
  /**
   * Character Limit: max 255
   */
  internalName: string;
  organizationId: string;
  teamId: string;
  talkToAudience: ParticipantAudienceType;
  /**
   * Character Limit: max 800
   */
  description?: string;
  researchConductType?: ResearchConductType;
  /**
   * Limit of 1000 is a one-time limit to test projects, and will not be
   * set for standard projects.
   *
   *
   * Must be changed directly in the DB by engineering, before calling `/publish`
   * in Postman on the DRAFT project.
   *
   * Number Limit: min 1 and max 1000. 0 is allowed when the project has
   * `recruitmentTargets` (invite-only); see `IsMarketplaceTarget`.
   */
  numberOfParticipants: number;
  duration: number;
  companyName?: string;
  hideCompanyName?: boolean;
  copied?: ProjectCopied;
  ageGroups?: Array<AgeGroup>;
  minAge?: number;
  maxAge?: number;
  education?: Array<EducationLevel>;
  ethnicity?: Array<Ethnicities>;
  seniority?: Array<SeniorityLevel>;
  exposeParticipantEmails?: boolean;
  ndaFileUrl?: string | null;
  ndaLink?: string | null;
  ndaType?: NdaType;
  locationRespondentInPerson?: ProjectLocationInPerson;
  companySize?: Array<CompanySize>;
  businessOwner?: boolean;
  visibility?: ProjectVisibility;
  cities?: Array<ProjectCities>;
  country?: ProjectCountryCode;
  countries?: Array<ProjectCountryCode>;
  automations?: ProjectAutomations;
  metrics?: ProjectMetrics;
  income?: Income;
  jobFunctions?: Array<JobFunction>;
  genders?: Gender;
  filtering?: ProjectFiltering;
  remoteCommunicationDetails?: string | null;
  /**
   * External-screener flow: the partner-hosted screener URL. Must be an
   * absolute `https://` URL — participants are redirected to it with their
   * correlation ids in the query string, and `buildExternalScreenerLink`
   * cannot parse a bare hostname (`partner.example.com/...`). Validated here
   * on the write DTOs (create, draft update, published update via PickType)
   * rather than on the entity, so re-validating stored documents on
   * unrelated updates is unaffected.
   */
  externalScreenerLink?: string | null;
  externalQuestions?: Array<ProjectExternalQuestion>;
  specialInstructions?: string;
  householdIncome?: Array<HouseholdIncome>;
  task?: Task;
  externalResearcher?: ExternalResearcher;
  /**
   * Number Limit: min 5 and max 1000
   */
  incentive?: number;
  /**
   * Flat advanced-targeting attribute map — the source of truth for this
   * project's advanced targeting. Read it via `getProjectAdvancedTargeting()`.
   */
  advancedTargeting?: AdvancedTargeting;
  keyQualifiers?: Array<KeyQualifier>;
  projectSuccessEditPermission?: boolean;
  professionalJobTitles?: Array<string>;
  desiredSkills?: Array<string>;
  topics?: Array<string>;
  professionalIndustries?: Array<string>;
  /**
   * @deprecated Use professionalIndustries instead.
   *
   * @deprecated
   */
  professionalIndustry?: Array<string>;
  /**
   * Quotas V2 configuration created with the project, in the same transaction (§4).
   */
  quotas?: UpsertQuotaDto;
  /**
   * internal field to track the project creation flow version
   * in v2, we refactored the project creation flow and added general improvements ie moving topics(key qualifiers) to step 2
   * if not set, default to v1
   */
  flowVersion?: string;
};

export type QuotaV2NoMatchInputDto = {
  target: QuotaV2TargetDto;
};

export const QuotaTargetOperator = {
  MIN: 'min',
  MAX: 'max',
  EXACT: 'exact',
  TRACK: 'track',
} as const;

export type QuotaTargetOperator =
  (typeof QuotaTargetOperator)[keyof typeof QuotaTargetOperator];

export type QuotaV2TargetDto = {
  op: QuotaTargetOperator;
  /**
   * Integer, 0 or more. Required unless `op` is `track`.
   */
  value?: number;
};

export type QuotaV2ExternalScreenerCountConditionDto = {
  source: 'externalScreener';
  questionId: string;
  match: 'count';
  op: QuotaCountOperator;
  /**
   * Number of selected options to compare with.
   */
  value: number;
};

export const QuotaCountOperator = {
  EQ: 'eq',
  NE: 'ne',
  GT: 'gt',
  GTE: 'gte',
  LT: 'lt',
  LTE: 'lte',
} as const;

export type QuotaCountOperator =
  (typeof QuotaCountOperator)[keyof typeof QuotaCountOperator];

export type QuotaV2ExternalScreenerSetConditionDto = {
  source: 'externalScreener';
  questionId: string;
  match: QuotaSetMatch;
  /**
   * `options[].id` values declared on the referenced external question.
   */
  optionIds: Array<string>;
};

export const QuotaSetMatch = {
  ANY: 'any',
  ALL: 'all',
  NONE: 'none',
} as const;

export type QuotaSetMatch = (typeof QuotaSetMatch)[keyof typeof QuotaSetMatch];

export type QuotaV2ScreenerCountConditionDto = {
  source: 'screener';
  questionUid: string;
  match: 'count';
  op: QuotaCountOperator;
  /**
   * Number of selected options to compare with.
   */
  value: number;
};

export type QuotaV2ScreenerSetConditionDto = {
  source: 'screener';
  questionUid: string;
  match: QuotaSetMatch;
  answerUids: Array<string>;
};

export type QuotaV2NumericComparisonConditionDto = {
  source: 'attribute';
  attribute: QuotaNumericAttribute;
  op: 'gt' | 'lt';
  /**
   * Strict bound (whole years for age).
   */
  value: number;
};

export const QuotaNumericAttribute = { AGE: 'age' } as const;

export type QuotaNumericAttribute =
  (typeof QuotaNumericAttribute)[keyof typeof QuotaNumericAttribute];

export type QuotaV2NumericRangeConditionDto = {
  source: 'attribute';
  attribute: QuotaNumericAttribute;
  op: 'range';
  /**
   * Inclusive lower bound (whole years for age).
   */
  min?: number;
  /**
   * Inclusive upper bound (whole years for age).
   */
  max?: number;
};

export type QuotaV2JobTitleConditionDto = {
  source: 'attribute';
  attribute: 'jobTitle';
  op: 'in' | 'notIn';
  /**
   * Job title ids as typed by the researcher.
   */
  values: Array<string>;
  /**
   * Set by the server on every save; ignored on input.
   */
  readonly resolvedFamilyGroupIds: Array<number>;
  /**
   * Set by the server on every save; ignored on input.
   */
  readonly groupingVersion: number;
};

export const QuotaSetAttribute = {
  GENDER: 'gender',
  ETHNICITY: 'ethnicity',
  COUNTRY: 'country',
  EDUCATION: 'education',
  HOUSEHOLD_INCOME: 'householdIncome',
  INDUSTRY: 'industry',
  COMPANY_SIZE: 'companySize',
  SENIORITY: 'seniority',
  JOB_FUNCTIONS: 'jobFunctions',
  MILITARY_SERVICE_STATUS: 'militaryServiceStatus',
  WORK_SETTING: 'workSetting',
  RELATIONSHIP_STATUS: 'relationshipStatus',
  LANGUAGES: 'languages',
  LIVING_SITUATION: 'livingSituation',
  PETS: 'pets',
  HOME_OWNER: 'homeOwner',
  NATIONALITY: 'nationality',
  AREA_TYPE: 'areaType',
  ONLINE_SHOPPING_FREQUENCY: 'onlineShoppingFrequency',
  CLOUD_PLATFORMS: 'cloudPlatforms',
  CODING_AI_TOOLS: 'codingAITools',
  DESIGN_PROTOTYPING_TOOLS: 'designPrototypingTools',
  FOOD_DELIVERY_SERVICES: 'foodDeliveryServices',
  HEALTH_WELLNESS_APPS: 'healthWellnessApps',
  ONLINE_BANKING_TOOLS: 'onlineBankingTools',
  ONLINE_LEARNING_PLATFORMS: 'onlineLearningPlatforms',
  ONLINE_MARKETPLACE_PLATFORMS: 'onlineMarketplacePlatforms',
  ONLINE_TRAVEL_BOOKING: 'onlineTravelBooking',
  PRODUCTIVITY_SOFTWARE: 'productivitySoftware',
  SMART_HOME_DEVICES: 'smartHomeDevices',
  SOCIAL_NETWORK_PLATFORMS: 'socialNetworkPlatforms',
  STREAMING_SERVICES: 'streamingServices',
  VEHICLE_BRANDS: 'vehicleBrands',
  WEBCAM: 'webcam',
  MOBILE_PHONE_WITH_INTERNET: 'mobilePhoneWithInternet',
  HEADSET_WITH_MICROPHONE: 'headsetWithMicrophone',
  PROGRAMMING_LANGUAGES: 'programmingLanguages',
  BROWSERS: 'browsers',
  SMARTPHONE_OS: 'smartphoneOS',
  TABLET_OS: 'tabletOS',
  COMPUTER_OS: 'computerOS',
} as const;

export type QuotaSetAttribute =
  (typeof QuotaSetAttribute)[keyof typeof QuotaSetAttribute];

export type QuotaV2AttributeSetConditionDto = {
  source: 'attribute';
  attribute: QuotaSetAttribute;
  op: 'in' | 'notIn';
  values: Array<string>;
};

export type QuotaV2RuleConditionNodeDto = {
  kind: 'condition';
  condition:
    | QuotaV2AttributeSetConditionDto
    | QuotaV2JobTitleConditionDto
    | QuotaV2NumericRangeConditionDto
    | QuotaV2NumericComparisonConditionDto
    | QuotaV2ScreenerSetConditionDto
    | QuotaV2ScreenerCountConditionDto
    | QuotaV2ExternalScreenerSetConditionDto
    | QuotaV2ExternalScreenerCountConditionDto;
};

export const QuotaRuleConnector = { AND: 'AND', OR: 'OR' } as const;

export type QuotaRuleConnector =
  (typeof QuotaRuleConnector)[keyof typeof QuotaRuleConnector];

export type QuotaV2RuleGroupNodeDto = {
  kind: 'group';
  connector: QuotaRuleConnector;
  children: Array<QuotaV2RuleGroupNodeDto | QuotaV2RuleConditionNodeDto>;
};

export type QuotaV2SegmentInputDto = {
  /**
   * Stable id; minted by the server when omitted. `no-match` is reserved.
   */
  id?: string;
  name: string;
  rule: QuotaV2RuleGroupNodeDto | QuotaV2RuleConditionNodeDto;
  target: QuotaV2TargetDto;
};

export type QuotaV2GroupInputDto = {
  /**
   * Stable id; minted by the server when omitted.
   */
  id?: string;
  name: string;
  /**
   * Ordered: the first matching segment wins (FR-5).
   */
  segments: Array<QuotaV2SegmentInputDto>;
  /**
   * The implicit bucket's target. Tracked-only when omitted.
   */
  noMatch?: QuotaV2NoMatchInputDto;
};

export type UpsertQuotaDto = {
  version: 'v2';
  groups: Array<QuotaV2GroupInputDto>;
};

export type AdminProjectsPaginatedDto = {
  /**
   * The total number of results possible, which is only provided in the response when `includeCount` is passed as `true` within a request.
   */
  totalResults?: number;
  page: number;
  pageSize: number;
  results: Array<AdminProjectListItemDto>;
};

export type AdminProjectListItemDto = {
  id: string;
  name: string | null;
  /**
   * Kept as plain strings rather than the v4 enums: legacy documents can
   * carry historical values the enums no longer list.
   */
  status: string | null;
  recruitingStatus: string | null;
  closed: boolean;
  closedMessage: string | null;
  createdAt: string;
  publishedAt: string | null;
  organizationId: string | null;
  companyName: string | null;
  visibility: string | null;
  paymentType: string | null;
  talkToAudience: number | null;
  researchConductType: number | null;
  timeMinutesRequired: number | null;
  numberOfParticipants: number | null;
  respondentRemuneration: number | null;
  totalAmount: number | null;
  pricingTierCode: string | null;
  pricingTierPrice: number | null;
  userId: string | null;
  userFirstName: string | null;
  userLastName: string | null;
  userEmail: string | null;
  country: string | null;
  /**
   * `cities[].location.formattedAddress`, present entries only.
   */
  cityAddresses: Array<string>;
  inPersonCity: string | null;
  inPersonCountry: string | null;
  qualifiedResponsesCount: number;
  invitedResponsesCount: number;
  scheduledResponsesCount: number;
  attendedResponsesCount: number;
  paidResponsesCount: number;
  /**
   * Legacy's definition: visible completed screeners.
   */
  responsesCount: number;
  viewCount: number;
  clickCount: number;
};

export type ProjectAnalyticsPaginatedDto = {
  /**
   * The total number of results possible, which is only provided in the response when `includeCount` is passed as `true` within a request.
   */
  totalResults?: number;
  page: number;
  pageSize: number;
  results: Array<ProjectAnalyticsRowDto>;
};

export type ProjectAnalyticsRowMetricsDto = {
  screeners?: MetricsScreeners;
};

export type ProjectAnalyticsRowDto = {
  id: string;
  organizationId: string;
  userId?: string;
  kindOfResearch?: KindOfResearch;
  status: ProjectStatus;
  recruitingStatus?: ProjectRecruitingStatus;
  publishedAt?: string;
  createdAt: string;
  talkToAudience?: ParticipantAudienceType;
  numberOfParticipants?: number;
  internalName?: string;
  name?: string;
  companyName?: string;
  metrics?: ProjectAnalyticsRowMetricsDto;
  /**
   * From the aggregation's user lookup; missing when the researcher was deleted.
   */
  user?: ProjectUserDto;
};

export type AugmentedProjectsPaginatedDto = {
  /**
   * The total number of results possible, which is only provided in the response when `includeCount` is passed as `true` within a request.
   */
  totalResults?: number;
  page: number;
  pageSize: number;
  results?: Array<AugmentedProject>;
};

export const JoinRequestStatus = {
  PENDING: 'pending',
  APPROVED: 'approved',
  DENIED: 'denied',
  CANCELLED: 'cancelled',
} as const;

export type JoinRequestStatus =
  (typeof JoinRequestStatus)[keyof typeof JoinRequestStatus];

export type JoinRequest = {
  id: string;
  createdAt: string;
  userId: string;
  userName: string;
  status: JoinRequestStatus;
  updatedBy?: string;
  updatedAt?: string;
};

export type Team = {
  id: string;
  createdAt: string;
  organizationId: string;
  name: string;
  slug: string;
  imageUrl?: string;
  description: string;
  isPrivate: boolean;
  isActive: boolean;
  isDeleted: boolean;
  members: Array<TeamMember>;
  integrations?: Array<Integration>;
  joinRequests?: Array<JoinRequest>;
  customerId?: string;
  newPricing?: boolean;
  hasLegacyPricing?: boolean;
  noCreditReservation?: boolean;
  balanceManagementSettings?: BalanceManagementSettings;
  updatedAt?: string;
};

export type ProjectOrganization = {
  id: string;
  name: string;
  location?: Location;
  privateTeams: boolean;
  website: string;
  imageUrl: string;
  timezone: string;
  slug: string;
  plan: Plan;
  users: Array<OrganizationMember>;
  phone?: string;
  isDeleted: boolean;
  customerId?: string;
  features: Features;
  rate?: number;
  trustLevel: number;
  domains: Array<ClaimedDomain>;
  participantDataExpiryMonths?: number;
  capabilities: Array<PricingPlanComponentCapability>;
  teams?: Array<Team>;
};

export type AugmentedProject = {
  id: string;
  createdAt: string;
  /**
   * Character Limit: min 3 and max 255
   */
  name: string;
  status: ProjectStatus;
  closedMessage?: string;
  closedAt?: string;
  closedByUserId?: string;
  archivedAt?: string;
  archivedByUserId?: string;
  paymentType?: string;
  slug: string;
  inviteCode?: string;
  kindOfResearch?: KindOfResearch;
  /**
   * Character Limit: max 255
   */
  internalName: string;
  companyName: string;
  hideCompanyName?: boolean;
  /**
   * Opts this project into the logged-out apply page. Set manually in the
   * database — there is deliberately no UI for it. Absent means off, so
   * every existing project stays gated until someone flips it.
   */
  publicApplicationsEnabled?: boolean;
  /**
   * External participants via a dedicated invite link. Absent means off;
   * when present every field is required. See `ProjectExternalInvites`.
   */
  externalInvites?: ProjectExternalInvites;
  organizationId: string;
  teamId: string;
  /**
   * The program this project belongs to, absent when it belongs to none.
   *
   * **The single record of program membership.** The program document does not
   * hold a `projectIds` array — this scalar is it. That makes
   * one-program-per-project inherent (a field holds one value), the reverse
   * lookup free (it is right here, not a query against every program), and a
   * deleted project leaves no dangling id behind.
   *
   * Membership is one-way by product rule, not by schema:
   * `programs.service.addProject` only sets this when it is unset, and there is
   * no path that clears it. See `../../programs/programs.md`.
   */
  programId?: string;
  userId: string;
  publishedAt?: string;
  expireAt?: string;
  copied?: ProjectCopied;
  creationFlow?: ProjectCreationFlow;
  recruitingStatus?: ProjectRecruitingStatus;
  screenerQuestions?: Array<ScreenerQuestion>;
  /**
   * Optimistic-concurrency version for `screenerQuestions` (ONCALL-2139).
   *
   * Every write path that replaces the screener array increments this field
   * (`$inc`). A client that hydrated the screener may send the version back
   * with its write; the write is rejected with 409 when the stored version has
   * moved on, instead of silently last-write-wins clobbering the array.
   * Absent on documents whose screener has never been written under this
   * scheme — readers treat missing as `0`.
   */
  screenerVersion?: number;
  ageGroups?: Array<AgeGroup>;
  minAge?: number;
  maxAge?: number;
  education?: Array<EducationLevel>;
  ethnicity?: Array<Ethnicities>;
  seniority?: Array<SeniorityLevel>;
  pricingTier?: PricingTier;
  /**
   * Pricing multiplier, stamped once at publish and read
   * by getCreditCostForProject for every quote and charge after that. Absent
   * = 1.00x. NOT related to the legacy pricingTier field above (a dollar
   * display amount from the pre-credits pricing model).
   */
  pricingMultiplier?: ProjectPricingMultiplier;
  exposeParticipantEmails?: boolean;
  talkToAudience: ParticipantAudienceType;
  ndaFileUrl?: string | null;
  ndaLink?: string | null;
  ndaType?: NdaType;
  inviteSurveyLink?: string;
  locationRespondentInPerson?: ProjectLocationInPerson;
  followers?: Array<ProjectFollower>;
  incentiveHistory?: Array<ProjectIncentiveRecord>;
  companySize?: Array<CompanySize>;
  businessOwner?: boolean;
  /**
   * Character Limit: max 800
   */
  description?: string;
  studyGoals?: string;
  welcomeScreen?: WelcomeScreen;
  rejectionScreen?: RejectionScreen;
  closingMessage?: ClosingMessage;
  interviewSections?: Array<InterviewSection>;
  /**
   * The aggregate feature-extraction schema version this project is pinned to.
   * Set once, on the project's first feature extraction, and frozen for life —
   * the feature-extraction pipeline reproduces this version's feature set for
   * every response, and the reader/frontend interpret the project's insights
   * through it. Absent until the first extraction runs.
   */
  featuresSchemaVersion?: number;
  /**
   * Feature-extraction schema versions this project is also extracted at, beside
   * the pin, so a new version's insights can be compared with the pinned ones.
   * Each version keeps its own insight documents; the pin stays what the
   * product shows unless a view asks for another version. Each interview that
   * completes is extracted at every version listed, and an eligibility change
   * rebuilds each of them.
   */
  featuresSchemaVersionExperimental?: Array<number>;
  interviewDeliveryMode?: InterviewDeliveryMode;
  studyLanguage?: string;
  /**
   * Languages participants may choose to respond in, beyond the study
   * language. Absent/empty = participants respond in the study language
   * only. The effective allowed set is always `responseLanguages ∪
   * {studyLanguage}`; writes are normalized (deduped, study language
   * dropped as implicit).
   */
  responseLanguages?: Array<string>;
  /**
   * LLM-enhanced project content
   * Contains enhanced description and audience for matching emails
   * Generated when project is published (if feature flag enabled)
   */
  enhancement?: ProjectEnhancement;
  calendars?: Array<string>;
  externalScheduling?: ExternalScheduling;
  visibility?: ProjectVisibility;
  /**
   * Invitation-driven campaign tags; see `../campaigns.md`. Format mirrors
   * `CAMPAIGN_TAG_PATTERN` in `utils/recruitment-targets.util.ts`.
   */
  campaigns?: Array<string>;
  /**
   * Per-channel participant targets. `marketplace` mirrors
   * `numberOfParticipants`; absent on projects written before the field
   * existed, so read through `getRecruitmentTargets()` rather than directly.
   * See `../recruitment-targets.md`.
   */
  recruitmentTargets?: RecruitmentTargets;
  cities?: Array<ProjectCities>;
  geoPoints?: Array<GeoJsonPoint> | null;
  exportCSVUrl?: string;
  country: ProjectCountryCode;
  countries?: Array<ProjectCountryCode>;
  automations?: ProjectAutomations;
  metrics?: ProjectMetrics;
  metadata?: ProjectMetadata;
  config?: ProjectConfig;
  /**
   * Use professionalIndustries instead.
   * Stores industry name only.
   *
   * @deprecated
   */
  professionalIndustry?: Array<string>;
  income?: Income;
  /**
   * Stores industry id reference
   */
  professionalIndustries?: Array<Industry>;
  professionalJobTitles?: Array<JobTitle>;
  jobTitleFamilyGroupIds?: Array<number> | null;
  jobTitleGroupingVersion?: number | null;
  jobFunctions?: Array<JobFunction>;
  researchConductType?: ResearchConductType;
  /**
   * Limit of 1000 is a one-time limit to test projects, and will not be
   * set for standard projects.
   *
   *
   * Must be changed directly in the DB by engineering, before calling `/publish`
   * in Postman on the DRAFT project.
   *
   * Number Limit: min 1 and max 1000. 0 is allowed when the project has
   * `recruitmentTargets` (invite-only); see `IsMarketplaceTarget`.
   */
  numberOfParticipants: number;
  genders?: Gender;
  history?: Array<ProjectStatusHistory>;
  closed?: boolean;
  user?: ProjectUserDto;
  filtering?: ProjectFiltering;
  remoteCommunicationDetails?: string | null;
  externalScreenerLink?: string | null;
  externalQuestions?: Array<ProjectExternalQuestion>;
  specialInstructions?: string;
  referralBonus?: number;
  /**
   * Admin lever: show the project in the dashboard Top picks strip to every
   * eligible participant, matched or not.
   */
  boostProjectFeed?: boolean;
  /**
   * Order among boosted projects, ascending. Unset sorts after every set index.
   */
  boostProjectFeedIndex?: number;
  saved?: boolean;
  hiddenForRespondent?: boolean;
  desiredSkills?: Array<Skill>;
  /**
   * Topics: max 2. A General Population project needs at least one to publish
   * unless its organization allows optional topics.
   */
  topics?: Array<ProjectTopic>;
  score?: number;
  topicMatched?: string;
  householdIncome?: Array<HouseholdIncome>;
  autopaused?: boolean;
  autopauseWarn?: boolean;
  task?: Task;
  externalResearcher?: ExternalResearcher;
  /**
   * Number Limit: min 5 and max 1000
   */
  respondentRemuneration: number;
  /**
   * Number Limit: min 5 and max 1000
   */
  incentive: number;
  /**
   * Number Limit: min 5
   */
  timeMinutesRequired: number;
  /**
   * Number Limit: min 5
   */
  duration: number;
  /**
   * Flat advanced-targeting attribute map — the source of truth for this
   * project's advanced targeting. Read it via `getProjectAdvancedTargeting()`.
   */
  advancedTargeting?: AdvancedTargeting;
  keyQualifiers?: Array<KeyQualifier>;
  rejectionReason?: ProjectRejectionReason;
  projectSuccessEditPermission?: boolean;
  stats?: ProjectStatsDto;
  participantType?: ParticipantType;
  requiresNda?: boolean;
  totalAmount?: number;
  latestHistoryItem?: ProjectStatusHistory;
  organization?: ProjectOrganization;
  updatedAt?: string;
};

export type AutoResumeProjectsDto = {
  /**
   * When true the sweep logs the projects it *would* resume without mutating
   * anything — used to observe the back-pressure loop before/while rolling out.
   */
  dryRun?: boolean;
};

export type GetAutoPauseProjectDto = {
  firstRun?: boolean;
  dryRun?: boolean;
};

export type GetRecruitingProjectsData = {
  id: string;
  organizationId: string;
  /**
   * Limit of 1000 is a one-time limit to test projects, and will not be
   * set for standard projects.
   *
   *
   * Must be changed directly in the DB by engineering, before calling `/publish`
   * in Postman on the DRAFT project.
   *
   * Number Limit: min 1 and max 1000. 0 is allowed when the project has
   * `recruitmentTargets` (invite-only); see `IsMarketplaceTarget`.
   */
  numberOfParticipants: number;
  /**
   * Number Limit: min 5 and max 1000
   */
  respondentRemuneration: number;
};

export type UpdatePublishedProjectDto = {
  /**
   * Character Limit: max 800
   */
  description?: string;
  ndaFileUrl?: string | null;
  ndaLink?: string | null;
  ndaType?: NdaType;
  /**
   * External-screener flow: the partner-hosted screener URL. Must be an
   * absolute `https://` URL — participants are redirected to it with their
   * correlation ids in the query string, and `buildExternalScreenerLink`
   * cannot parse a bare hostname (`partner.example.com/...`). Validated here
   * on the write DTOs (create, draft update, published update via PickType)
   * rather than on the entity, so re-validating stored documents on
   * unrelated updates is unaffected.
   */
  externalScreenerLink?: string | null;
  task?: Task;
  /**
   * Number Limit: min 5
   */
  duration?: number;
  /**
   * Character Limit: min 3 and max 255
   */
  name?: string;
  /**
   * Character Limit: max 255
   */
  internalName?: string;
  /**
   * Number Limit: min 1 and max 500
   */
  numberOfParticipants?: number;
  /**
   * Number Limit: min 5 and max 1000
   */
  incentive?: number;
  /**
   * Update-only automations patch. Omitted from the CreateProjectDto inherit
   * set so swagger/TS metadata is this type, not a merge with persisted
   * ProjectAutomations (keys are not nullable on the entity). Each key is
   * independently nullable so a generated client, and the public API (which
   * maps `{ autopay: false }` → `{ pay: null }`), can clear one automation
   * without replacing the whole subdocument. Omitted keys are left untouched;
   * `null` on a key `$unset`s it; `automations: null` `$unset`s the whole
   * subdocument. The service translates this via `projectAutomationsDottedUpdate`.
   */
  automations?: UpdateProjectAutomationsDto | null;
  /**
   * Patch the task completion deadline without replacing the task
   * subdocument (sending `task` replaces it wholesale, which can clobber
   * concurrent edits to fields like `url`). `endsAt` is required and must be
   * in the future; an omitted `timezone` keeps the stored one. The project
   * must have a custom URL task (unmoderated study / survey). Cannot be
   * combined with `task` in the same request.
   */
  taskDeadline?: UpdateTaskDeadlineDto;
  externalResearcher?: UpdateExternalResearcherPublishedDto;
  incentiveUpdateType?: ProjectIncentiveUpdateType;
  reopen?: boolean;
};

export type UpdateExternalResearcherPublishedDto = {
  /**
   * Display name used when messaging participants. Full name is not required —
   * participants only ever see the first token (PX-214), even if a full name
   * is stored here.
   */
  researcherName?: string;
  /**
   * Link to book a meeting with the researcher.
   */
  bookingUrl?: string;
};

export type UpdateTaskDeadlineDto = {
  endsAt: string;
  timezone?: string;
};

export type UpdateProjectAutomationDelayDto = {
  /**
   * Delay in minutes
   */
  delay: number;
};

export type UpdateProjectAutomationsDto = {
  pay?: UpdateProjectAutomationDelayDto | null;
  invite?: UpdateProjectAutomationDelayDto | null;
  complete?: UpdateProjectAutomationDelayDto | null;
};

export type UpdateDraftProjectDto = {
  kindOfResearch?: KindOfResearch;
  talkToAudience?: ParticipantAudienceType;
  /**
   * Character Limit: max 800
   */
  description?: string;
  researchConductType?: ResearchConductType;
  companyName?: string;
  hideCompanyName?: boolean;
  copied?: ProjectCopied;
  ageGroups?: Array<AgeGroup>;
  minAge?: number | null;
  maxAge?: number | null;
  education?: Array<EducationLevel>;
  ethnicity?: Array<Ethnicities>;
  seniority?: Array<SeniorityLevel>;
  exposeParticipantEmails?: boolean;
  ndaFileUrl?: string | null;
  ndaLink?: string | null;
  ndaType?: NdaType;
  locationRespondentInPerson?: ProjectLocationInPerson;
  companySize?: Array<CompanySize>;
  businessOwner?: boolean;
  visibility?: ProjectVisibility;
  /**
   * Cities can be cleared on update by sending `null` or an empty array; an
   * omitted value keeps the existing cities. Declared nullable so generated API
   * clients can express the clear — the service treats `null`/`[]` as "clear"
   * (see `ProjectsService.updateDraftProject`).
   */
  cities?: Array<ProjectCities> | null;
  country?: ProjectCountryCode;
  countries?: Array<ProjectCountryCode>;
  metrics?: ProjectMetrics;
  income?: Income;
  jobFunctions?: Array<JobFunction>;
  genders?: Gender;
  filtering?: ProjectFiltering;
  remoteCommunicationDetails?: string | null;
  /**
   * External-screener flow: the partner-hosted screener URL. Must be an
   * absolute `https://` URL — participants are redirected to it with their
   * correlation ids in the query string, and `buildExternalScreenerLink`
   * cannot parse a bare hostname (`partner.example.com/...`). Validated here
   * on the write DTOs (create, draft update, published update via PickType)
   * rather than on the entity, so re-validating stored documents on
   * unrelated updates is unaffected.
   */
  externalScreenerLink?: string | null;
  externalQuestions?: Array<ProjectExternalQuestion>;
  specialInstructions?: string;
  householdIncome?: Array<HouseholdIncome>;
  task?: Task;
  externalResearcher?: ExternalResearcher;
  /**
   * Override the inherited `advancedTargeting` (from CreateProjectDto, typed as
   * the DB-faithful AdvancedTargeting entity) with the update-only bucket whose
   * scalar attributes are nullable — so a generated client, and the public API
   * (which maps partner requests onto this DTO), can send `null` to clear a
   * scalar attribute on update. The service translates `null` into a `$unset`
   * (see `mergeAdvancedTargeting`).
   */
  advancedTargeting?: UpdateAdvancedTargeting;
  keyQualifiers?: Array<KeyQualifier>;
  projectSuccessEditPermission?: boolean;
  professionalJobTitles?: Array<string>;
  desiredSkills?: Array<string>;
  topics?: Array<string>;
  /**
   * Number Limit: min 5
   */
  duration?: number;
  /**
   * Character Limit: min 3 and max 255
   */
  name?: string;
  /**
   * Character Limit: max 255
   */
  internalName?: string;
  /**
   * Number Limit: min 1 and max 500
   */
  numberOfParticipants?: number;
  /**
   * Number Limit: min 5 and max 1000
   */
  incentive?: number;
  professionalIndustries?: Array<string>;
  /**
   * @deprecated Use professionalIndustries instead.
   *
   * @deprecated
   */
  professionalIndustry?: Array<string>;
  /**
   * Update-only automations patch. Omitted from the CreateProjectDto inherit
   * set so swagger/TS metadata is this type, not a merge with persisted
   * ProjectAutomations (keys are not nullable on the entity). Each key is
   * independently nullable so a generated client, and the public API (which
   * maps `{ autopay: false }` → `{ pay: null }`), can clear one automation
   * without replacing the whole subdocument. Omitted keys are left untouched;
   * `null` on a key `$unset`s it; `automations: null` `$unset`s the whole
   * subdocument. The service translates this via `projectAutomationsDottedUpdate`.
   */
  automations?: UpdateProjectAutomationsDto | null;
  /**
   * Patch the task completion deadline without replacing the task
   * subdocument (sending `task` replaces it wholesale, which can clobber
   * concurrent edits to fields like `url`). `endsAt` is required and must be
   * in the future; an omitted `timezone` keeps the stored one. The project
   * must have a custom URL task (unmoderated study / survey). Cannot be
   * combined with `task` in the same request.
   */
  taskDeadline?: UpdateTaskDeadlineDto;
  /**
   * internal field to track the project creation flow version
   * in v2, we refactored the project creation flow and added general improvements ie moving topics(key qualifiers) to step 2
   * if not set, default to v1
   */
  flowVersion?: string;
};

export type MarkRespondentMessagesReadDto = {
  /**
   * Number of messages that were newly marked as read.
   */
  updatedCount: number;
};

export type RespondentMessageThreadDto = {
  results: Array<RespondentMessageDto>;
  unreadCount: number;
};

export type RespondentMessageDto = {
  id: string;
  userId: string;
  body: string;
  read: boolean;
  sentByUserId?: string;
  createdAt: string;
  updatedAt: string;
};

export type CreateRespondentMessageDto = {
  body: string;
  sendSms?: boolean;
};

export type ProfileLockDto = {
  id: string;
  profileId: string;
  employment?: Lock;
  demographics?: Lock;
  personal?: Lock;
  technical?: Lock;
  facebook?: Lock;
  instagram?: Lock;
  linkedin?: Lock;
  twitter?: Lock;
  github?: Lock;
  location?: Lock;
  name?: Lock;
  skills?: Lock;
  videoIntro?: Lock;
  updatedAt?: string;
};

export type Lock = {
  lastUpdatedAt: string;
  fields: {
    [key: string]: unknown;
  };
};

export type ProfileFieldLockDto = {
  /**
   * Profile ID
   */
  profileId: string;
  /**
   * Array of field locks
   */
  locks: Array<FieldLockEntryDto>;
  /**
   * When the lock document was last updated
   */
  updatedAt: string;
};

export type FieldLockEntryDto = {
  /**
   * Field path (e.g., "employment.email", "firstName")
   */
  path: {
    [key: string]: unknown;
  };
  /**
   * When this field was last updated
   */
  lastUpdatedAt: string;
};

export type TriggerProjectRematchDto = {
  type: RematchTriggerType;
  /**
   * The legacy admin page echoes the path params into the body. Accepted and
   * ignored (the path params are authoritative): the global ValidationPipe
   * runs with `forbidNonWhitelisted`, so removing this member would 400 the
   * legacy caller. Delete it once the legacy admin page is gone.
   *
   * @deprecated
   */
  projectId?: string;
  /**
   * See `projectId` — same legacy-body member, same rule.
   *
   * @deprecated
   */
  organizationId?: string;
};

export const RematchTriggerType = {
  SCORE: 'score',
  RESCORE: 'rescore',
  RETRIGGER_PROJECT: 'retrigger-project',
} as const;

export type RematchTriggerType =
  (typeof RematchTriggerType)[keyof typeof RematchTriggerType];

export type RebuildQuotaPredictionsResultDto = {
  /**
   * Rows re-predicted against the quota's current rules.
   */
  updated: number;
  /**
   * Rows whose prediction was removed: the project has no V2 quota, or its organization is off the flag.
   */
  cleared: number;
  /**
   * Rows that could not be predicted because their profile rollup is gone.
   */
  skipped: number;
};

export type InsertProfileEmailEventDto = {
  projectId: string;
  profileId: string;
  type: MatchEmailEventType;
  source?: MatchEmailEventSource;
  messageId: string;
  eventId: string;
  url?: string;
  variant?: string;
};

export const MatchEmailEventSource = {
  INITIAL: 'initial',
  RECONTACT: 'recontact',
} as const;

export type MatchEmailEventSource =
  (typeof MatchEmailEventSource)[keyof typeof MatchEmailEventSource];

export const MatchEmailEventType = {
  SENT: 'sent',
  DELIVERED: 'delivered',
  OPENED: 'opened',
  CLICKED: 'clicked',
  BOUNCED: 'bounced',
  UNSUBSCRIBED: 'unsubscribed',
  SPAM: 'spam',
  SKIPPED: 'skipped',
} as const;

export type MatchEmailEventType =
  (typeof MatchEmailEventType)[keyof typeof MatchEmailEventType];

export type ProfileRollup = {
  id: string;
  createdAt: string;
  userId: string;
  firstName: string;
  lastName: string;
  status: RespondentStatus;
  linkedinUrl?: string | null;
  mobileType?: string | null;
  phone?: string;
  isPhoneVerified?: boolean;
  verifiedPhone?: string;
  phoneVerificationCode?: string | null;
  twilioPhoneData?: ProfileTwilioPhoneData;
  email?: string;
  emailDomain?: string;
  paypal?: string | null;
  demographics?: ProfileDemographics;
  employment?: ProfileEmployment | null;
  picture?: string | null;
  location?: ProfileLocation;
  social?: ProfileSocial;
  socialLinks?: ProfileSocialLinks | null;
  notificationSettings?: ProfileNotificationSettings;
  cookieSettings?: ProfileCookieSettings;
  score?: number;
  flag?: number;
  surveys?: SurveyResponseStats;
  skills: Array<Skill>;
  videoIntroUrl?: string | null;
  topics?: Array<SimpleTopic>;
  user?: User;
  /**
   * Must stay serialized on ProfileDto: the profile-risk-change-respond-queue
   * lambda reads the profile over GET v4/profiles/:profileId and feeds it to
   * computeOverallProfileRisk (@common/utils), where a null trustedCompany
   * marks the profile risk component 'questioned'. An `@Exclude()` here made
   * the DTO report null for every profile, so trusted-company verification
   * could never clear that flag (profile.dto.unit.spec.ts guards this).
   */
  trustedCompany?: ProfileTrustedCompany | null;
  /**
   * For public api partners only
   */
  externalOrganizationId?: string;
  personal?: PersonalInfo;
  technical?: Technical;
  productsServices?: ProductsServicesInfo;
  shopping?: ShoppingInfo;
  professional?: ProfessionalInfo;
  risk?: ProfileRisk;
  gdprDeletedAt?: string;
  lastLoginAt?: string;
  codeOfConductAcceptedAt?: string;
  lastScreenerSubmittedAt?: string;
  /**
   * First time the profile held gender, date of birth and a city: the rule
   * the participant app uses to leave onboarding. Stamped by
   * `buildOnboardingCompletionUpdate`, never cleared. Absent on participants
   * who completed onboarding before the stamp existed.
   */
  onboardingCompletedAt?: string;
  initialMatching?: ProfileInitialMatching;
  leadId?: string;
  /**
   * Virtual populate of the {@link Lead} referenced by `leadId`. Use
   * `profile.populate('lead')` to materialize the document; otherwise this
   * field is undefined.
   */
  lead?: Lead;
  metaData?: ProfileRollupMetaData;
  updatedAt?: string;
};

export type SchemaObjectId = {
  [key: string]: unknown;
};

export type TeamInteractionDate = {
  teamId: SchemaObjectId;
  date: string;
};

export type EmailStats = {
  sent: number;
  delivered: number;
  opened: number;
  clicked: number;
  bounced: number;
  unsubscribed: number;
  spam: number;
  lastSentEmailDate: string;
  lastOpenedClickedEmailDate: string;
  deliveryRate: number;
  openRate: number;
};

export type OrganizationFlag = {
  flag: number;
  organizationId: string;
};

export type ProfileRollupMetaData = {
  organizationFlag?: Array<OrganizationFlag>;
  emailStats?: EmailStats;
  orgLastPaidAt?: {
    [key: string]: unknown;
  };
  externalResearcherLastPaidAt?: {
    [key: string]: unknown;
  };
  externalTeamLastPaidAt?: {
    [key: string]: unknown;
  };
  externalCompanyLastPaidAt?: {
    [key: string]: unknown;
  };
  teamLastAppliedAt?: Array<TeamInteractionDate>;
  teamLastQualifiedAt?: Array<TeamInteractionDate>;
  teamLastPaidAt?: Array<TeamInteractionDate>;
};

export const ProfileInitialMatchingStatus = {
  PENDING: 'pending',
  DONE: 'done',
  FAILED: 'failed',
} as const;

export type ProfileInitialMatchingStatus =
  (typeof ProfileInitialMatchingStatus)[keyof typeof ProfileInitialMatchingStatus];

export type ProfileInitialMatching = {
  status: ProfileInitialMatchingStatus;
  /**
   * When the pending pass was queued; the sweep takes the oldest first.
   */
  requestedAt: string;
  /**
   * Last time a sweep run claimed this profile. A claim older than the
   * stale window belongs to a run that died and is taken over.
   */
  claimedAt?: string;
  /**
   * Claims since the pass last made progress. Exhausting the limit parks the
   * profile as failed.
   */
  attempts: number;
  /**
   * When the first full check finished; a recheck leaves it as it is.
   */
  firstFullScoreAt?: string;
  /**
   * Recruiting projects the profile was scored against.
   */
  projectsChecked?: number;
  /**
   * Of those, projects with a positive score.
   */
  matched?: number;
  /**
   * Projects an unfinished pass still has to score the profile against; the
   * next run resumes from here. While set, `projectsChecked` and `matched`
   * are the pass's totals so far.
   */
  pendingProjectIds?: Array<string>;
};

export type SurveyResponseStats = {
  organizationLastPaidAt?: string;
  lastPaidAt?: string;
  lastTakenAt?: string;
};

export type InitialMatchingCheckResultDto = {
  /**
   * True when the feature flag is off and nothing was claimed.
   */
  skipped: boolean;
  claimed: number;
  /**
   * Recruiting projects the batch was scored against.
   */
  projectsChecked: number;
  /**
   * Positive scores queued for writing.
   */
  matches: number;
  durationMs: number;
};

export type InitialMatchingCheckDto = {
  /**
   * Pending profiles to claim and score in this run. Defaults to 100.
   */
  batchSize?: number;
};

export type RecruitingStalledProjectDto = {
  projectId: string;
  organizationId: string;
  matched: number;
  /**
   * Length in minutes of the longest uninterrupted RECRUITING window since
   * publish/retrigger.
   */
  recruitingWindowMinutes: number;
  /**
   * True when pause history was unreliable and recruitingWindowMinutes is
   * the wall-clock-since-publish upper bound instead of a real window.
   */
  usedFallbackWallClock: boolean;
  publishedAt: string;
  matchingRetriggeredAt?: string;
};

export type RecruitingStalledSweepDto = {
  /**
   * Minimum length of a single uninterrupted RECRUITING window before a
   * project with no recruitment emails is flagged. Defaults to 60 minutes.
   */
  thresholdMinutes?: number;
};

export type RecruitingMatchingProjectDto = {
  projectId: string;
  matched: number;
};

export type CreateMatchingProjectDto = {
  projectId: string;
  organizationId: string;
};

export type MatchingProject = {
  id: string;
  createdAt: string;
  projectId: string;
  organizationId: string;
  matched: number;
  found?: number;
  processed?: number;
  status?: MatchingProjectStatus;
  v4Status: MatchingProjectStatus;
  lastEmailedAt?: string;
  completedAt?: string;
  v4LastEmailedAt?: string;
  v4LastCheckedAt?: string;
  v4LastRecontactCheckedAt?: string;
  v4CompletedAt?: string;
  matchingRetriggeredAt?: string;
  recruitingStalledAlertedAt?: string;
  events: Array<MatchingProjectEvent>;
  /**
   * One entry per attribute-only Quotas V2 bucket the last whole-project run
   * counted; absent until a run under `quota-dynamic-matching` writes it.
   * See `MatchingProjectQuotaBucketEstimate`.
   */
  quotaBucketEstimates?: Array<MatchingProjectQuotaBucketEstimate>;
  /**
   * The last `quota-refill` request for the project's match pool; absent
   * until a recruitment tick under `quota-dynamic-matching` finds a bucket
   * dry. See `MatchingProjectQuotaRefill`.
   */
  quotaRefill?: MatchingProjectQuotaRefill;
  updatedAt?: string;
};

export type MatchingProjectQuotaRefillExhausted = {
  groupId: string;
  segmentId: string;
  /**
   * The quota revision the bucket's rule was searched under.
   */
  revision: number;
  at: string;
};

export type MatchingProjectQuotaRefillBucket = {
  groupId: string;
  /**
   * `no-match` for the group's implicit bucket.
   */
  segmentId: string;
  /**
   * Participants the bucket was short of when the refill was requested.
   */
  missing: number;
  /**
   * Un-emailed candidates in the allocation window that could land in the bucket.
   */
  supply: number;
  /**
   * Rows the refill added for the bucket; absent until the refill ran.
   */
  newRows?: number;
};

export type MatchingProjectQuotaRefill = {
  requestedAt: string;
  buckets: Array<MatchingProjectQuotaRefillBucket>;
  completedAt?: string;
  exhausted?: Array<MatchingProjectQuotaRefillExhausted>;
};

export type MatchingProjectQuotaBucketEstimate = {
  groupId: string;
  /**
   * `no-match` for the group's implicit bucket.
   */
  segmentId: string;
  /**
   * Profiles the targeting admits that could land in the bucket.
   */
  panel: number;
  /**
   * The quota revision the bucket's rule was read at.
   */
  revision: number;
  estimatedAt: string;
};

export const MatchingProjectEventType = { EMAIL: 'EMAIL' } as const;

export type MatchingProjectEventType =
  (typeof MatchingProjectEventType)[keyof typeof MatchingProjectEventType];

export type MatchingProjectEvent = {
  id: string;
  createdAt: string;
  type: MatchingProjectEventType;
  total: number;
  updatedAt?: string;
};

export type UpdateMatchingProjectDto = {
  total: number;
  type: MatchingProjectEventType;
  v4Status?: MatchingProjectStatus;
};

export type ProfileProjectMatch = {
  id: string;
  createdAt: string;
  projectId: string;
  profileId: string;
  userId: string;
  surveyResponseId?: string | null;
  email?: string;
  isMicrosoftEmailDomain: boolean;
  score?: number;
  v4Score?: number;
  vectorScore?: number;
  projectSimilarityScore?: number;
  sourceProjectId?: string;
  v4ScoreUpdated?: boolean;
  isReferrer?: boolean;
  delayInMinutes?: number;
  source?: MatchSource;
  topicMatched?: string;
  emailEvents: Array<MatchEmailEvent>;
  scoreHistory: Array<MatchScoreHistory>;
  project: MatchProject;
  matchingRetriggeredAt?: string;
  lastLoginAt?: string;
  /**
   * Denormalized copy of profile.notificationSettings.targetedResearchInvitation
   * so the recruitment/recontact crons can exclude opted-out participants
   * in-query. Written at scoring time, kept in sync by the
   * onProfileMatchingDataChange Atlas trigger, and backfilled onto older
   * matches via POST /admin/repair/profile-project-match-targeted-research-invitation.
   * Missing (legacy, not yet backfilled) is treated as opted in.
   */
  targetedResearchInvitation?: boolean;
  quotaPrediction?: ProfileProjectMatchQuotaPrediction;
  updatedAt?: string;
};

export type ProfileProjectMatchQuotaBucketRef = {
  groupId: string;
  /**
   * `no-match` for the group's implicit bucket; `undetermined` when the
   * landing depends on a screener answer the participant has not given yet.
   */
  segmentId: string;
  /**
   * Every bucket of the group the participant could still land in, in
   * display order; `[segmentId]` when the landing is decided. The
   * participant is supply for each, so this is the field a per-bucket
   * read queries (see the ranked index), not `segmentId`.
   */
  possibleSegmentIds: Array<string>;
};

export type ProfileProjectMatchQuotaPrediction = {
  /**
   * One entry per quota group.
   */
  segments: Array<ProfileProjectMatchQuotaBucketRef>;
  /**
   * The quota revision the segments were computed against.
   */
  revision: number;
  /**
   * When the stored segments or revision last changed.
   */
  predictedAt: string;
};

export type MatchProject = {
  id: string;
  createdAt: string;
  name: string;
  kindOfResearch: number;
  talkToAudience: ParticipantAudienceType;
  researchConductType: number;
  duration: number;
  incentive: number;
  publishedAt: string;
  updatedAt?: string;
};

export type MatchScoreHistory = {
  id: string;
  createdAt: string;
  v4Score?: number;
  score?: number;
  vectorScore?: number;
  sourceProjectId?: string;
  projectSimilarityScore?: number;
  updatedAt?: string;
};

export type MatchEmailEvent = {
  id: string;
  createdAt: string;
  type: MatchEmailEventType;
  source?: MatchEmailEventSource;
  messageId: string;
  eventId: string;
  url?: string;
  /**
   * A/B test arm the email was sent under, e.g. `subject-v1:B`. Set by the
   * email lambda on `sent` and echoed back by Mailgun (as the `v:variant`
   * user variable) on every delivery event, so opens, clicks and applies can be
   * grouped by arm. Absent when no experiment was running.
   */
  variant?: string;
  updatedAt?: string;
};

export const MatchSource = {
  OLD_MATCHING: 'old_matching',
  SCREENER_EMBEDDED_MATCHING: 'screener_embedded_matching',
} as const;

export type MatchSource = (typeof MatchSource)[keyof typeof MatchSource];

export type UpsertProfileProjectScoresDto = {
  scores: Array<UpsertProfileProjectScoreDto>;
};

export type UpsertProfileProjectScoreDto = {
  v4Score?: number;
  vectorScore?: number;
  projectSimilarityScore?: number;
  sourceProjectId?: string;
  projectId: string;
  profileId: string;
};

export type UpdateMatchingProjectProfileDto = {
  v4Score?: number;
  vectorScore?: number;
  projectSimilarityScore?: number;
  sourceProjectId?: string;
};

export type UpsertProfileMatchingScoresDto = {
  matchFields?: Array<MatchingProfileField>;
};

export const MatchingProfileField = {
  GENDER: 'gender',
  ETHNICITY: 'ethnicity',
  EDUCATION_LEVEL: 'educationLevel',
  HOUSEHOLD_INCOME: 'householdIncome',
  JOB_TITLE: 'jobTitle',
  COMPANY_SIZE: 'companySize',
  INDUSTRY: 'industry',
  LOCATION: 'location',
  TOPICS: 'topics',
  SKILLS: 'skills',
} as const;

export type MatchingProfileField =
  (typeof MatchingProfileField)[keyof typeof MatchingProfileField];

export type LegacyPrequalifiesForProjectResultsDto = {
  qualifies?: boolean;
  disqualificationReason?: ProjectDisqualificationReason;
};

export type MatchingProjectsPaginatedDto = {
  /**
   * The total number of results possible, which is only provided in the response when `includeCount` is passed as `true` within a request.
   */
  totalResults?: number;
  page: number;
  pageSize: number;
  results?: Array<MatchingProjectDto>;
};

export type MatchingProjectDto = {
  id: string;
  /**
   * Character Limit: min 3 and max 255
   */
  name: string;
  kindOfResearch?: KindOfResearch;
  publishedAt?: string;
  talkToAudience: ParticipantAudienceType;
  /**
   * Character Limit: max 800
   */
  description?: string;
  researchConductType?: ResearchConductType;
  /**
   * Admin lever: show the project in the dashboard Top picks strip to every
   * eligible participant, matched or not.
   */
  boostProjectFeed?: boolean;
  /**
   * Topics: max 2. A General Population project needs at least one to publish
   * unless its organization allows optional topics.
   */
  topics?: Array<ProjectTopic>;
  /**
   * Number Limit: min 5 and max 1000
   */
  respondentRemuneration: number;
  /**
   * Number Limit: min 5
   */
  timeMinutesRequired: number;
  /**
   * Used on the participant dashboard AS WELL AS public project search so v4Score is optional
   */
  v4Score?: number;
  hidden?: boolean;
  referralLink?: string;
};

export type FeasibilityResultsDto = {
  totalResults?: number;
  /**
   * only return the top 20 results
   */
  topResults?: Array<MatchingProfileExpandedDto>;
};

export type MatchingProfileExpandedDto = {
  id: string;
  createdAt: string;
  userId: string;
  firstName: string;
  lastName: string;
  status: RespondentStatus;
  linkedinUrl?: string | null;
  mobileType?: string | null;
  phone?: string;
  isPhoneVerified?: boolean;
  verifiedPhone?: string;
  phoneVerificationCode?: string | null;
  twilioPhoneData?: ProfileTwilioPhoneData;
  email?: string;
  emailDomain?: string;
  paypal?: string | null;
  demographics?: ProfileDemographics;
  employment?: ProfileEmployment | null;
  picture?: string | null;
  location?: ProfileLocation;
  social?: ProfileSocial;
  socialLinks?: ProfileSocialLinks | null;
  notificationSettings?: ProfileNotificationSettings;
  cookieSettings?: ProfileCookieSettings;
  score?: number;
  flag?: number;
  surveys?: SurveyResponseStats;
  skills: Array<Skill>;
  videoIntroUrl?: string | null;
  topics?: Array<SimpleTopic>;
  user?: User;
  /**
   * Must stay serialized on ProfileDto: the profile-risk-change-respond-queue
   * lambda reads the profile over GET v4/profiles/:profileId and feeds it to
   * computeOverallProfileRisk (@common/utils), where a null trustedCompany
   * marks the profile risk component 'questioned'. An `@Exclude()` here made
   * the DTO report null for every profile, so trusted-company verification
   * could never clear that flag (profile.dto.unit.spec.ts guards this).
   */
  trustedCompany?: ProfileTrustedCompany | null;
  /**
   * For public api partners only
   */
  externalOrganizationId?: string;
  personal?: PersonalInfo;
  technical?: Technical;
  productsServices?: ProductsServicesInfo;
  shopping?: ShoppingInfo;
  professional?: ProfessionalInfo;
  risk?: ProfileRisk;
  gdprDeletedAt?: string;
  lastLoginAt?: string;
  codeOfConductAcceptedAt?: string;
  lastScreenerSubmittedAt?: string;
  /**
   * First time the profile held gender, date of birth and a city: the rule
   * the participant app uses to leave onboarding. Stamped by
   * `buildOnboardingCompletionUpdate`, never cleared. Absent on participants
   * who completed onboarding before the stamp existed.
   */
  onboardingCompletedAt?: string;
  initialMatching?: ProfileInitialMatching;
  leadId?: string;
  /**
   * Virtual populate of the {@link Lead} referenced by `leadId`. Use
   * `profile.populate('lead')` to materialize the document; otherwise this
   * field is undefined.
   */
  lead?: Lead;
  metaData?: ProfileRollupMetaData;
  v4Score?: number;
  updatedAt?: string;
};

export type GetFeasibilityScoreDetailsDto = {
  kindOfResearch?: KindOfResearch;
  ageGroups?: Array<AgeGroup>;
  minAge?: number;
  maxAge?: number;
  education?: Array<EducationLevel>;
  ethnicity?: Array<Ethnicities>;
  seniority?: Array<SeniorityLevel>;
  talkToAudience: ParticipantAudienceType;
  locationRespondentInPerson?: ProjectLocationInPerson;
  companySize?: Array<CompanySize>;
  businessOwner?: boolean;
  cities?: Array<ProjectCities>;
  country: ProjectCountryCode;
  countries?: Array<ProjectCountryCode>;
  income?: Income;
  /**
   * Stores industry id reference
   */
  professionalIndustries?: Array<Industry>;
  jobFunctions?: Array<JobFunction>;
  researchConductType?: ResearchConductType;
  /**
   * Limit of 1000 is a one-time limit to test projects, and will not be
   * set for standard projects.
   *
   *
   * Must be changed directly in the DB by engineering, before calling `/publish`
   * in Postman on the DRAFT project.
   *
   * Number Limit: min 1 and max 1000. 0 is allowed when the project has
   * `recruitmentTargets` (invite-only); see `IsMarketplaceTarget`.
   */
  numberOfParticipants: number;
  genders?: Gender;
  filtering?: ProjectFiltering;
  householdIncome?: Array<HouseholdIncome>;
  externalResearcher?: ExternalResearcher;
  /**
   * Flat advanced-targeting attribute map — the source of truth for this
   * project's advanced targeting. Read it via `getProjectAdvancedTargeting()`.
   */
  advancedTargeting?: AdvancedTargeting;
  keyQualifiers?: Array<KeyQualifier>;
  scoringModifers?: ScoringModifiersDto;
  professionalJobTitles?: Array<string>;
  desiredSkills?: Array<string>;
  topics?: Array<string>;
};

export type ScoringModifiersDto = {
  all: {
    profile?: {
      metadata?: {
        emailStats?: {
          maxBounced?: number;
          maxSpam?: number;
          maxUnsubscribed?: number;
          deliveryRatePivot?: number;
          deliveryRateBoost?: number;
        };
        orgLastPaidAt?: {
          anyPaymentBoost?: number;
          recentPaymentBoost?: number;
        };
      };
      notificationSettings?: {
        targetedResearchInvitationEnabledBoost?: number;
      };
      createdAt?: {
        newUsersInDays?: number;
        newUsersBoost?: number;
      };
      lastLoginAt?: {
        b2c?: {
          within7DaysBoost?: number;
          within30DaysBoost?: number;
          within90DaysBoost?: number;
          within180DaysBoost?: number;
        };
        b2b?: {
          within7DaysBoost?: number;
          within30DaysBoost?: number;
          within90DaysBoost?: number;
          within180DaysBoost?: number;
        };
      };
      demographics?: {
        householdIncomeBoost?: number;
      };
      matching?: {
        defaultAttributeMatchBoost?: number;
        presenceTolerantMatchBoost?: number;
      };
      personal?: {
        relationshipStatusBoost?: number;
        languagesBoost?: number;
        numberOfChildrenBoost?: number;
        numberOfChildrenLiveAtHomeBoost?: number;
        childrenBirthYearsBoost?: number;
        livingSituationBoost?: number;
        petsBoost?: number;
        homeOwnerBoost?: number;
      };
      employment?: {
        incomeBoost?: number;
        businessOwnerBoost?: number;
        isEmailVerifiedBoost?: number;
      };
      risk?: {
        plaidApprovalBoost?: number;
      };
    };
  };
  remote: {
    maxLocationRadiusKM?: number;
    b2c?: {
      profile?: {
        metadata?: {
          emailStats?: {
            openRatePivot?: number;
            openRateBoost?: number;
          };
        };
        locationBoost?: number;
        trustedCompanyBoost?: number;
        eachMatchedTopicBoost?: number;
      };
    };
    b2b?: {
      profile?: {
        metadata?: {
          emailStats?: {
            openRatePivot?: number;
            openRateBoost?: number;
          };
        };
        jobTitleBoost?: number;
        eachMatchedSkillBoost?: number;
        industryMatchBoost?: number;
        companySizeBoost?: number;
        eachMatchedTopicBoost?: number;
        seniorityLevelBoost?: number;
      };
    };
  };
  inPerson: {
    maxLocationRadiusKM?: number;
    b2c?: {
      profile?: {
        metadata?: {
          emailStats?: {
            openRatePivot?: number;
            openRateBoost?: number;
          };
        };
        trustedCompanyBoost?: number;
        eachMatchedTopicBoost?: number;
        locationBoost?: number;
      };
    };
    b2b?: {
      profile?: {
        metadata?: {
          emailStats?: {
            openRatePivot?: number;
            openRateBoost?: number;
          };
        };
        jobTitleBoost?: number;
        industryMatchBoost?: number;
        eachMatchedSkillBoost?: number;
        locationBoost?: number;
        eachMatchedTopicBoost?: number;
        companySizeBoost?: number;
        seniorityLevelBoost?: number;
      };
    };
  };
};

export type FeasibilityCountResponseDto = {
  totalCount: number;
};

export type CalculateFeasibilityB2cDto = {
  countries: Array<CountryCode>;
  /**
   * Legacy age buckets. Ignored when minAge or maxAge (inclusive) is provided. Omit, or send N/A, for no age filter.
   */
  ageGroups?: Array<AgeGroup>;
  minAge?: number | null;
  maxAge?: number | null;
  genders: Array<Gender>;
  householdIncomes: Array<HouseholdIncome>;
  educationLevels: Array<EducationLevel>;
  /**
   * Optional city coordinates filter. When provided, only profiles within 100km radius of any coordinate are counted.
   */
  cities?: Array<LocationCoordinate>;
};

export type CalculateFeasibilityB2bDto = {
  /**
   * Organization the study being estimated belongs to. Scopes the per-org job-title-grouping flag so the estimate gates exactly like the matcher will for THAT org (matcher uses project.organizationId). Must be one of the caller's organizations (admins exempt); otherwise ignored and the estimate falls back to legacy fuzzy title matching.
   */
  organizationId?: string;
  countries: Array<CountryCode>;
  industries?: Array<string>;
  jobFunctions?: Array<JobFunction>;
  seniorityLevels?: Array<SeniorityLevel>;
  jobTitles?: Array<string>;
  companySizes?: Array<CompanySize>;
  skills?: Array<SkillDto>;
  /**
   * Optional city coordinates filter. When provided, only profiles within 100km radius of any coordinate are counted.
   */
  cities?: Array<LocationCoordinate>;
};

export type SkillDto = {
  id: string;
  /**
   * Optional skill name for documentation/debugging purposes
   */
  name?: string;
};

export type UpdateInvitationDto = {
  profileId?: string;
  projectId?: string;
  organizationId?: string;
  teamId?: string;
  /**
   * Character Limit: min 1 and max 1000
   */
  message?: string;
  skipScreenerQuestions?: boolean;
  /**
   * Project the invitee is being shared from (follow-up provenance). Must
   * belong to the same team as the target project.
   */
  sourceProjectId?: string;
  eventUid?: string;
  remoteCommunicationDetails?: string;
};

export type ParticipantInvitationDto = {
  invitationId: string;
  projectId: string;
  name: string;
  kindOfResearch: KindOfResearch;
  researchConductType: ResearchConductType;
  respondentRemuneration: number;
  timeMinutesRequired: number;
  publishedAt?: string;
  /**
   * ISO timestamp of the invitation.
   */
  invitedAt: string;
  /**
   * The researcher's note from the invitation, when they wrote one.
   */
  message?: string;
  /**
   * The invitation waives the screener: applying is a single Apply step.
   */
  skipScreenerQuestions: boolean;
  /**
   * The project is paused or has finished recruiting. The apply path would
   * still admit an invitee, but the card withholds its Apply action so an
   * invitee is not steered into a study the researcher has stopped filling.
   *
   * Never true for a merely paused campaign project (`campaign` set): those
   * are paused to stay out of the feed, not to stop invitees. See
   * `projects/campaigns.md`.
   */
  isClosedToNewApplications: boolean;
  /**
   * The campaign the project belongs to, when it is a campaign project (see
   * `projects/campaigns.md`). The dashboard banner keys off this; ordinary
   * researcher invitations do not carry it.
   */
  campaign?: string;
};

export type ProjectInvitationDto = {
  id: string;
  profileId: string;
  firstName: string;
  /**
   * Surname initial only, as on every researcher-facing participant list.
   */
  lastName: string;
  /**
   * From the live profile; absent when the profile has none or was deleted.
   */
  picture?: string;
  invitedAt: string;
  skipScreenerQuestions: boolean;
  /**
   * Follow-up provenance: the project this person was shared *from*, when the
   * invitation came from a program roster or another project's participants
   * tab. Absent for Participant Database invitations.
   */
  sourceProjectId?: string;
  /**
   * Live name of `sourceProjectId`; absent when that project no longer exists.
   */
  sourceProjectName?: string;
};

export type CreateInvitationDto = {
  profileId: string;
  projectId: string;
  organizationId: string;
  teamId: string;
  /**
   * Character Limit: min 1 and max 1000
   */
  message: string;
  skipScreenerQuestions?: boolean;
  /**
   * Project the invitee is being shared from (follow-up provenance). Must
   * belong to the same team as the target project.
   */
  sourceProjectId?: string;
  eventUid?: string;
  remoteCommunicationDetails?: string;
};

export type SimpleJobTitlesPaginatedDto = {
  /**
   * The total number of results possible, which is only provided in the response when `includeCount` is passed as `true` within a request.
   */
  totalResults?: number;
  page: number;
  pageSize: number;
  results: Array<SimpleJobTitle>;
};

export type SimpleJobTitle = {
  id: string;
  name: string;
  seniority?: Array<SeniorityLevel>;
};

export type JobTitleGroupingCoverageDto = {
  /**
   * Grouping artifact version these stats were computed against.
   */
  groupingVersion: number;
  /**
   * Number of `job_title_groups` rows at the current version.
   */
  dictionarySize: number;
  /**
   * B2B profiles in the sample (denominator for the percentages).
   */
  sampleB2b: number;
  /**
   * % of sampled B2B profiles carrying a family id at the current version (gate-effective coverage).
   */
  familyCoveragePct: number;
  /**
   * % of sampled B2B whose normalized title isn't in the dictionary (expected long tail).
   */
  unmappedTitlePct: number;
  /**
   * % of sampled B2B not at the current grouping version.
   */
  staleVersionPct: number;
  /**
   * # of titles with >= the high-usage threshold of profiles that are unmapped (junk excluded).
   */
  highUsageUnmappedCount: number;
  /**
   * Up to N `name (profileCount)` examples of the high-usage unmapped titles.
   */
  topUnmappedTitles: Array<string>;
};

export type UpdateQuotaV1Dto = {
  /**
   * Updated quota criteria details
   */
  criteria: QuotaCriteriaV1InputDto;
  version: 'v1';
};

export type QuotaSegmentInputDto = {
  /**
   * The value for this segment (e.g., "Male" for gender)
   */
  value: string;
  /**
   * The target percentage for this segment (0-100)
   */
  percentage: number;
};

export type QuotaCriteriaV1InputDto = {
  /**
   * The category this quota applies to
   */
  category: 'age' | 'gender' | 'ethnicity' | 'location';
  /**
   * Array of quota segments
   */
  segments: Array<QuotaSegmentInputDto>;
};

export type CreateQuotaV1Dto = {
  version: 'v1';
  /**
   * Quota criteria details
   */
  criteria: QuotaCriteriaV1InputDto;
};

export type QuotaV2ExternalFillsEntryDto = {
  groupId: string;
  segmentId: string;
  count: number;
};

export type QuotaV2TargetEntryDto = {
  groupId: string;
  segmentId: string;
  target: QuotaV2TargetDto;
};

export type QuotaV2UpsertResultDto = {
  /**
   * Absent when the project has no quota document (an empty configuration).
   */
  id?: string;
  projectId: string;
  version: 'v2';
  /**
   * Bumped by every structural upsert. 0 when the project has no quota document. Not an edit guard; see `configVersion`.
   */
  revision: number;
  /**
   * Opaque token for this exact configuration; every rules or targets save replaces it, progress changes do not. Send it back unchanged as `expectedConfigVersion` on PUT, PATCH `…/targets` and DELETE to refuse the write (409, `reason: QUOTA_CONFIG_VERSION_CONFLICT`) when someone else saved in between. `none` when the project has no quota.
   */
  configVersion: string;
  source?: QuotaSource;
  /**
   * Every targeted bucket is filled (FR-18 `quota.filled`).
   */
  filled: boolean;
  /**
   * Some group can admit nobody: the auto-invite halt signal.
   */
  closed: boolean;
  groups: Array<QuotaV2GroupDto>;
  createdAt?: string;
  updatedAt?: string;
  changes: QuotaV2ChangesDto;
  /**
   * Soft findings that did not block the save (§4.2).
   */
  warnings: Array<QuotaV2ValidationIssueDto>;
};

export type QuotaV2ValidationIssueDto = {
  /**
   * Where in the configuration, e.g. `groups[0].segments[2].rule.children[1]`.
   */
  path: string;
  message: string;
};

export type QuotaV2ExternalFillsDroppedDto = {
  groupId: string;
  /**
   * `no-match` for the implicit bucket.
   */
  segmentId: string;
  count: number;
};

export type QuotaV2BucketRefDto = {
  groupId: string;
  /**
   * `no-match` for the implicit bucket.
   */
  segmentId: string;
};

export type QuotaV2ChangesDto = {
  /**
   * Buckets that did not exist before this upsert; they start at zero.
   */
  created: Array<QuotaV2BucketRefDto>;
  /**
   * Buckets whose id existed before; their external fills were carried.
   */
  preserved: Array<QuotaV2BucketRefDto>;
  /**
   * Buckets the new configuration no longer has.
   */
  removed: Array<QuotaV2BucketRefDto>;
  /**
   * Screener responses whose stored bucket was recomputed.
   */
  rebucketed: number;
  /**
   * External fills that sat on removed buckets; nobody exists to re-evaluate them.
   */
  externalFillsDropped: Array<QuotaV2ExternalFillsDroppedDto>;
  /**
   * Buckets the re-bucket pushed above a `max` or `exact` value.
   */
  overTarget: Array<QuotaV2BucketRefDto>;
};

export type QuotaV2BucketDto = {
  target: QuotaV2TargetDto;
  progress: QuotaV2ProgressDto;
  /**
   * Target reached: `min`, `max` or `exact` with the gate count at or above the value. Never for `track`.
   */
  filled: boolean;
  /**
   * Can admit nobody: `max` or `exact` at or above the value. Never for `min` or `track`.
   */
  closed: boolean;
  /**
   * Gate count above a `max` or `exact` value (a re-bucket or an acknowledged refusal put it there).
   */
  overTarget: boolean;
  /**
   * FR-9 lookahead: a landing in this bucket would currently be refused to
   * keep remaining `min`/`exact` slots fillable elsewhere. Computed on read
   * from `minProtection`; the UI never derives it.
   */
  minProtected: boolean;
};

export type QuotaV2ProgressDto = {
  /**
   * Bucketed responses in tier PENDING that are visible and not in a pen (derived on read).
   */
  pending: number;
  /**
   * The `qualified: true` part of `pending` (derived on read).
   */
  qualified: number;
  invited: number;
  attended: number;
  externalFills: number;
};

export type QuotaV2SegmentDto = {
  target: QuotaV2TargetDto;
  progress: QuotaV2ProgressDto;
  /**
   * Target reached: `min`, `max` or `exact` with the gate count at or above the value. Never for `track`.
   */
  filled: boolean;
  /**
   * Can admit nobody: `max` or `exact` at or above the value. Never for `min` or `track`.
   */
  closed: boolean;
  /**
   * Gate count above a `max` or `exact` value (a re-bucket or an acknowledged refusal put it there).
   */
  overTarget: boolean;
  /**
   * FR-9 lookahead: a landing in this bucket would currently be refused to
   * keep remaining `min`/`exact` slots fillable elsewhere. Computed on read
   * from `minProtection`; the UI never derives it.
   */
  minProtected: boolean;
  id: string;
  name: string;
  rule: QuotaV2RuleGroupNodeDto | QuotaV2RuleConditionNodeDto;
};

export type QuotaV2GroupDto = {
  id: string;
  name: string;
  segments: Array<QuotaV2SegmentDto>;
  noMatch: QuotaV2BucketDto;
  /**
   * Every targeted bucket in the group is filled. False when none is targeted.
   */
  filled: boolean;
  /**
   * Every bucket in the group, No match included, is closed.
   */
  closed: boolean;
  /**
   * Some bucket in the group is `minProtected` (FR-9).
   */
  minProtected: boolean;
};

export const QuotaSource = {
  APP: 'app',
  API: 'api',
  AGENT: 'agent',
  MIGRATION: 'migration',
} as const;

export type QuotaSource = (typeof QuotaSource)[keyof typeof QuotaSource];

export type QuotaV1Dto = {
  /**
   * Unique identifier for the quota
   */
  id: string;
  /**
   * Project ID this quota belongs to
   */
  projectId: string;
  /**
   * Version of the quota
   */
  version: 'v1';
  /**
   * Total target number of participants
   */
  totalTarget: number;
  /**
   * Current status of the quota
   */
  status: 'IN_PROGRESS' | 'FILLED';
  /**
   * Criteria for this quota
   */
  criteria: QuotaCriteriaV1Dto;
  /**
   * Progress tracking for each segment
   */
  progress: {
    [key: string]: unknown;
  };
  /**
   * Creation timestamp
   */
  createdAt: string;
  /**
   * Last update timestamp
   */
  updatedAt: string;
};

export type QuotaSegmentDto = {
  /**
   * The value for this segment (e.g., "Male" for gender)
   */
  value: string;
  /**
   * The target percentage for this segment (0-100)
   */
  percentage: number;
  /**
   * Unique identifier for the segment
   */
  id: string;
};

export type QuotaCriteriaV1Dto = {
  /**
   * The category this quota applies to
   */
  category: 'age' | 'gender' | 'ethnicity' | 'location';
  /**
   * Array of quota segments
   */
  segments: Array<QuotaSegmentDto>;
};

export type QuotaV2Dto = {
  /**
   * Absent when the project has no quota document (an empty configuration).
   */
  id?: string;
  projectId: string;
  version: 'v2';
  /**
   * Bumped by every structural upsert. 0 when the project has no quota document. Not an edit guard; see `configVersion`.
   */
  revision: number;
  /**
   * Opaque token for this exact configuration; every rules or targets save replaces it, progress changes do not. Send it back unchanged as `expectedConfigVersion` on PUT, PATCH `…/targets` and DELETE to refuse the write (409, `reason: QUOTA_CONFIG_VERSION_CONFLICT`) when someone else saved in between. `none` when the project has no quota.
   */
  configVersion: string;
  source?: QuotaSource;
  /**
   * Every targeted bucket is filled (FR-18 `quota.filled`).
   */
  filled: boolean;
  /**
   * Some group can admit nobody: the auto-invite halt signal.
   */
  closed: boolean;
  groups: Array<QuotaV2GroupDto>;
  createdAt?: string;
  updatedAt?: string;
};

export type Notification = {
  id: string;
  createdAt: string;
  type: NotificationType;
  status: NotificationStatus;
  name: NotificationName;
  timestamp: number;
  surveyResponseId?: string;
  project: NotificationProject;
  user: NotificationUser;
  invitation?: {
    [key: string]: unknown;
  };
  booking?: {
    [key: string]: unknown;
  };
  updatedAt?: string;
};

export type NotificationUser = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
};

export type NotificationProject = {
  id: string;
  name: string;
};

export const NotificationName = {
  '1_DAY': '1 day',
  '2_DAY': '2 day',
  '3_DAY': '3 day',
  '5_DAY': '5 day',
  '7_DAY': '7 day',
  '30_HOURS': '30 hours',
  '24_HOURS': '24 hours',
  '8_HOURS': '8 hours',
  '4_HOURS': '4 hours',
  '2_HOURS': '2 hours',
  '1_HOURS': '1 hours',
  '1_MONTH_EXPIRED': '1 month Expired',
} as const;

export type NotificationName =
  (typeof NotificationName)[keyof typeof NotificationName];

export const NotificationStatus = {
  PENDING: 'PENDING',
  QUEUED: 'QUEUED',
  COMPLETED: 'COMPLETED',
  SKIPPED: 'SKIPPED',
  ERROR: 'ERROR',
  CANCELLED: 'CANCELLED',
} as const;

export type NotificationStatus =
  (typeof NotificationStatus)[keyof typeof NotificationStatus];

export type NotificationUpdateDto = {
  status: NotificationStatus;
};

export type NotificationEmailPayloadDto = {
  emailType: NotificationType;
  name: NotificationName;
  email: string;
  notificationId: string;
  userId: string;
  projectId: string;
  surveyResponseId?: string;
  isReferrer?: string;
};

export type SurveyResponseDetailsDto = {
  total_time: number;
  ip_address: string;
  recipient_id: string;
  id: string;
  href: string;
  logic_path: {
    [key: string]: unknown;
  };
  metadata: {
    [key: string]: unknown;
  };
  date_modified: string;
  response_status: string;
  custom_variables: {
    [key: string]: unknown;
  };
  custom_value: string;
  edit_url: string;
  analyze_url: string;
  page_path: Array<string>;
  pages: Array<SurveyResponsePageDto>;
  collector_id: string;
  date_created: string;
  survey_id: string;
  collection_mode: string;
};

export type SurveyResponseAnswerTagDataDto = {
  hexcolor?: string;
  label?: string;
  tag_type?: string;
};

export type SurveyResponseAnswerChoiceMetadata = {
  weight: string;
};

export type SurveyResponseAnswerDto = {
  choice_id?: string;
  row_id?: string;
  col_id?: string;
  other_id?: string;
  text?: string;
  choice_metadata?: Array<SurveyResponseAnswerChoiceMetadata>;
  tag_data?: Array<SurveyResponseAnswerTagDataDto>;
};

export type SurveyResponseQuestionDto = {
  id: string;
  variable_id?: string;
  answers: Array<SurveyResponseAnswerDto>;
};

export type SurveyResponsePageDto = {
  id: string;
  questions: Array<SurveyResponseQuestionDto>;
};

export type SurveyListDto = {
  per_page: number;
  page: number;
  total: number;
  data: Array<SurveyListItemDto>;
};

export type SurveyListItemDto = {
  id: string;
  title: string;
  nickname: string;
  href: string;
};

export type CreateIntegrationDto = {
  name: TaskType;
  token: string;
};

export type SurveyResponse = {
  id: ObjectId;
  projectId: ObjectId;
  qualified: boolean;
  qualifiedPendingIdVerification?: boolean;
  qualifiedPendingManualReview?: boolean;
  manualReviewAudit?: {
    [key: string]: unknown;
  };
  percentage: number;
  ndaUrl?: string;
  userId: ObjectId;
  status: SurveyResponseStatus;
  favorite: boolean;
  hidden: boolean;
  isMatch: boolean;
  responses: Array<string>;
  totalTime: number;
  agree: boolean;
  clientOrigin: {
    [key: string]: unknown;
  };
  isNew: boolean;
  matching: {
    [key: string]: unknown;
  };
  emailEvents: {
    [key: string]: unknown;
  };
  visibility: {
    [key: string]: unknown;
  };
  history: Array<string>;
  invitation: SurveyResponseInvitation;
  inviteSentAt: string;
  booking: {
    [key: string]: unknown;
  };
  organizationFlag: boolean;
  inviteId: string;
  inviteCode: string;
  invite: {
    [key: string]: unknown;
  };
  inviter: SurveyResponseInviter;
  createdAt: string;
  updatedAt: string;
  scheduledAt: string;
  statusUpdatedAt: string;
  project: ProjectLegacy;
  profile: ProfileLegacy;
  referrer?: SurveyResponseReferrer;
  review: ReviewV2;
  payouts: Array<string>;
  conversationUid: string;
  rescheduleOverriden: boolean;
  projectStarted: boolean;
  task?: Task;
  customIncentive?: number;
  pay?: SurveyResponsePay;
  complete?: SurveyResponseComplete;
  autoInvite?: SurveyResponseInvite;
  externalProject?: ExternalProject;
};

export type UpdateTaskDto = {
  url?: string;
  parameters?: TaskParameters;
  projectCompletionId?: string;
  deadline?: TaskDeadline;
  status?: TaskStatus;
  context?: {
    [key: string]: unknown;
  };
  type?: TaskType;
};

export type CreateParticipantTaskDto = {
  id?: string;
  type: TaskType;
  url: string;
  status?: TaskStatus;
  projectCompletionId?: string;
  deadline?: TaskDeadline;
};

export type FraudSignal = {
  id: string;
  createdAt: string;
  userId: string;
  profileId?: string;
  signalType: FraudSignalType;
  /**
   * Structured signal payload; its shape is determined by signalType via
   * FRAUD_SIGNAL_VALUE_CLASSES (validated by IsFraudSignalValue).
   */
  value: FraudScoreValue | RiskReasonValue | GeoMismatchValue;
  source: FraudSignalSource;
  /**
   * Where the observation was made, e.g. `{ surface, sessionId?, ip? }`.
   */
  context: {
    [key: string]: unknown;
  };
  observedAt: string;
  /**
   * Optional idempotency key. A unique sparse index guarantees at most one
   * stored signal per key, so producers can enforce invariants like "one
   * ip_proxy signal per screener submission" across retries and repeated
   * checks. Inserts that lose the race are silently skipped by the
   * repository.
   */
  dedupeKey?: string;
  updatedAt?: string;
};

export const FraudSignalSource = {
  CLEARBIT: 'clearbit',
  INTERNAL: 'internal',
  RESEARCHER: 'researcher',
  CX: 'cx',
} as const;

export type FraudSignalSource =
  (typeof FraudSignalSource)[keyof typeof FraudSignalSource];

export type GeoMismatchValue = {
  geoMatch: boolean;
};

export type RiskReasonValue = {
  reason: string;
};

export type FraudScoreValue = {
  score: number;
};

export const FraudSignalType = {
  FRAUD_SCORE: 'fraud_score',
  ADDRESS_GEO_MISMATCH: 'address_geo_mismatch',
  IP_GEO_MISMATCH: 'ip_geo_mismatch',
  IP_COUNTRY_MISMATCH: 'ip_country_mismatch',
  EMAIL_GEO_MISMATCH: 'email_geo_mismatch',
  EMAIL_IP_GEO_MISMATCH: 'email_ip_geo_mismatch',
  IP_PROXY: 'ip_proxy',
  IP_RATELIMITED: 'ip_ratelimited',
  IP_VERY_RATELIMITED: 'ip_very_ratelimited',
  IP_PROXY_NOT_REPUTABLE_COMPANY: 'ip_proxy_not_reputable_company',
  IP_RECENTLY_AT_RISK: 'ip_recently_at_risk',
  INVALID_IP: 'invalid_ip',
  OTHER_IP_OR_GEO_RISK: 'other_ip_or_geo_risk',
} as const;

export type FraudSignalType =
  (typeof FraudSignalType)[keyof typeof FraudSignalType];

export type CreateFraudSignalsDto = {
  signals: Array<CreateFraudSignalDto>;
};

export type CreateFraudSignalDto = {
  userId: string;
  profileId?: string;
  signalType: FraudSignalType;
  /**
   * Structured signal payload; its shape is determined by signalType via
   * FRAUD_SIGNAL_VALUE_CLASSES (validated by IsFraudSignalValue).
   */
  value: FraudScoreValue | RiskReasonValue | GeoMismatchValue;
  source: FraudSignalSource;
  /**
   * Where the observation was made, e.g. `{ surface, sessionId?, ip? }`.
   */
  context: {
    [key: string]: unknown;
  };
  observedAt: string;
  /**
   * Optional idempotency key. A unique sparse index guarantees at most one
   * stored signal per key, so producers can enforce invariants like "one
   * ip_proxy signal per screener submission" across retries and repeated
   * checks. Inserts that lose the race are silently skipped by the
   * repository.
   */
  dedupeKey?: string;
};

export type AbortScreenerFileUploadDto = {
  uploadId: string;
  bucketKey: string;
};

export type CompleteScreenerFileUploadResponseDto = {
  key: string;
  name: string;
  size: number;
  mime: string;
  uploadedAt: string;
};

export type CompleteScreenerFileUploadDto = {
  uploadId: string;
  bucketKey: string;
  parts: Array<CompletedScreenerFilePartDto>;
  fileName: string;
  fileSize: number;
  mimeType:
    | 'image/jpeg'
    | 'image/png'
    | 'image/gif'
    | 'image/webp'
    | 'image/heic'
    | 'application/pdf'
    | 'application/msword'
    | 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    | 'application/vnd.ms-powerpoint'
    | 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
    | 'application/vnd.ms-excel'
    | 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    | 'text/plain'
    | 'video/mp4'
    | 'video/webm'
    | 'video/quicktime'
    | 'video/x-m4v'
    | 'audio/mpeg'
    | 'audio/mp4'
    | 'audio/x-m4a';
};

export type CompletedScreenerFilePartDto = {
  partNumber: number;
  etag: string;
};

export type UploadScreenerFilePartResponseDto = {
  /**
   * 1-based part index echoed from the request
   */
  partNumber: number;
  /**
   * S3 ETag for this part (used in complete())
   */
  etag: string;
};

export type UploadScreenerFilePartDto = {
  uploadId: string;
  bucketKey: string;
  partNumber: number;
  /**
   * Base64-encoded binary data for this part
   */
  data: string;
};

export type InitiateScreenerFileUploadResponseDto = {
  uploadId: string;
  bucketKey: string;
};

export type InitiateScreenerFileUploadDto = {
  projectId: string;
  questionUid: string;
  fileName: string;
  fileSize: number;
  mimeType:
    | 'image/jpeg'
    | 'image/png'
    | 'image/gif'
    | 'image/webp'
    | 'image/heic'
    | 'application/pdf'
    | 'application/msword'
    | 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    | 'application/vnd.ms-powerpoint'
    | 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
    | 'application/vnd.ms-excel'
    | 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    | 'text/plain'
    | 'video/mp4'
    | 'video/webm'
    | 'video/quicktime'
    | 'video/x-m4v'
    | 'audio/mpeg'
    | 'audio/mp4'
    | 'audio/x-m4a';
  inviteCode?: string;
  inviteId?: string;
};

export type InsertTaskReminderEmailEventDto = {
  id: string;
  type: EmailEventType;
  timestamp: string;
  notificationId?: string;
  reminderName?: string;
};

export const EmailEventType = {
  ACCEPTED: 'accepted',
  REJECTED: 'rejected',
  DELIVERED: 'delivered',
  FAILED: 'failed',
  OPENED: 'opened',
  CLICKED: 'clicked',
  UNSUBSCRIBED: 'unsubscribed',
  COMPLAINED: 'complained',
} as const;

export type EmailEventType =
  (typeof EmailEventType)[keyof typeof EmailEventType];

export type InsertEmailEventDto = {
  id: string;
  type: EmailEventType;
  timestamp: string;
};

export type ParticipantCancelBookingDto = {
  actor?: Actor;
  reason?: ParticipantCancellationReason;
  shortNoticeCancel?: boolean;
  message?: string;
};

export const Actor = {
  RESEARCHER: 'researcher',
  RESPONDENT: 'respondent',
  ADMIN: 'admin',
} as const;

export type Actor = (typeof Actor)[keyof typeof Actor];

export type RejectScreenerResponseDto = {
  message?: string;
};

export type AutocompleteDto = {
  autocompleteOn: string;
};

export type CancelBookingReinviteDto = {
  actor?: Actor;
  message?: string;
};

export type ScheduleExternalScreenerResponseDto = {
  /**
   * bookingUrl is optional and can be used if researcher wants to update the booking url during schedule
   * This will also update the invitation remoteCommunicationDetails.
   *
   * @deprecated
   */
  bookingUrl?: string;
  /**
   * bookingLink is optional and can be used if researcher wants to update the booking url during schedule
   */
  bookingLink?: string;
  /**
   * meetingLink is optional and can be used if researcher wants to update the remoteCommunicationDetails that was set during invitation
   */
  meetingLink?: string;
  /**
   * Link to cancel a meeting. If not set, bookingLink will be used.
   */
  meetingCancelLink?: string;
  /**
   * The timezone of the scheduled booking Date.
   * Must be a valid IANA timezone.
   * For a list of valid timezones, see: https://en.wikipedia.org/wiki/List_of_tz_database_time_zones
   */
  timezone: string;
  /**
   * Note: we expect the date to be in the format of YYYY-MM-DDTHH:MM:SS (ISO 8601), in the timezone specified.
   * Offset will be removed from the input date.
   * Date will be stored in UTC.
   */
  bookedDate?: string;
};

export type InviteScreenerResponseDto = {
  /**
   * This will override the special instructions from the project.
   */
  message?: string;
  videoProvider?: InvitationVideoProvider;
  /**
   * If a project is remote, this should be the link to the research study,
   * this will override the remoteCommunicationDetails from the project.
   * If a public api project which has an `externalResearcher.bookingUrl`, and `remoteCommunicationDetails` is passed in,` remoteCommunicationDetails` will be used instead for the bookingUrl on the screener response.
   * If a project has a task, this field is undefined.
   */
  remoteCommunicationDetails?: string;
  event?: InvitationEvent;
  /**
   * External booking URL for Calendly, Cal.com, etc. with prepopulated parameters.
   * This field is used to provide participants with a direct link to schedule their session
   * on an external scheduling platform.
   * Only whitelisted domains are allowed for security purposes.
   * Note: null values are rejected - omit the field if not needed.
   */
  externalBookingUrl?: string;
  /**
   * Only for Public Api. If set, will not be used to overwrite bookingUrl on screener response.
   * If a project is remote, this should be the link to the research study,
   * this will override the remoteCommunicationDetails from the project.
   * If a project has a task, this field is undefined.
   */
  meetingLink?: string;
  /**
   * Only for Public Api. If set, will be used to write bookingUrl on screener response.
   * If a project has a task, this field is undefined.
   */
  bookingLink?: string;
  /**
   * Quotas V2: acknowledges a refusal the invite gate returned as a 409 and
   * asks for the invite to proceed anyway. Only the reasons listed here are
   * lifted, and only when the gate refused for them.
   */
  quotaOverride?: QuotaOverrideDto;
};

export const QuotaRefusalReason = {
  SEGMENT_FULL: 'SEGMENT_FULL',
  MIN_PROTECTION: 'MIN_PROTECTION',
} as const;

export type QuotaRefusalReason =
  (typeof QuotaRefusalReason)[keyof typeof QuotaRefusalReason];

export type QuotaOverrideDto = {
  reasons: Array<QuotaRefusalReason>;
};

export type InvitationEvent = {
  uid: string;
};

export type InvitationVideoProvider = {
  loginProviderId: string;
  providerKey: 'zoom';
  userId: string;
};

export type HideScreenerResponseDto = {
  hidden: boolean;
};

export type QualifyScreenerResponseDto = {
  qualifyStatus: boolean;
  qualifiedOverriden: boolean;
  disqualifyReasons: Array<DisqualifyReasonCode>;
  message: string;
};

export const DisqualifyReasonCode = {
  MISREPRESENTED: 'MISREPRESENTED',
  CONTEXT: 'CONTEXT',
  SCREENER: 'SCREENER',
  BAD_MATCH: 'BAD_MATCH',
  OTHER: 'OTHER',
  PAST_PARTICIPATION: 'PAST_PARTICIPATION',
} as const;

export type DisqualifyReasonCode =
  (typeof DisqualifyReasonCode)[keyof typeof DisqualifyReasonCode];

export type IngestExternalScreenerAnswersDto = {
  answers: Array<ExternalScreenerAnswerRowDto>;
  /**
   * Completion signal. The response leaves EXTERNAL_SCREENER_IN_PROGRESS for
   * PENDING when the accumulated answers cover every declared external
   * question, or earlier when this flag is true (participants may legitimately
   * skip optional questions on the partner's side). Until then rows upsert
   * idempotently and the response stays IN_PROGRESS.
   */
  complete?: boolean;
};

export type ExternalScreenerAnswerRowDto = {
  /**
   * Must reference a declared `Project.externalQuestions[].questionId`.
   * Every row must also carry content: at least one of `answerValues`
   * (non-empty array) or `answerText` (non-empty string).
   */
  questionId: string;
  /**
   * Selected option ids for RADIO/CHECKBOX (each must be a declared
   * `options[].id`; RADIO takes exactly one), or structured values for
   * NUMBER. Required (non-empty) when `answerText` is not provided.
   */
  answerValues?: Array<string>;
  /**
   * Free-text answer for TEXT/OTHER questions.
   * Required (non-empty) when `answerValues` is not provided.
   */
  answerText?: string;
  /**
   * The partner's per-question verdict.
   */
  qualifies: boolean;
};

export type FavoriteScreenerResponseDto = {
  favorite: boolean;
};

export type ReportScreenerResponseDto = {
  userId: string;
  reasons: Array<FlagReason>;
  flaggedMessage: string;
  payParticipant?: boolean;
};

export type ScreenerResponseBookingDto = {
  uid?: string;
  customer?: Customer;
  event: Event;
  timezone?: string;
  paypal?: string | null;
  phone?: string;
  start: string;
  end: string;
  message?: string;
  source?: BookingSource;
  externalBooking?: ExternalBookingDetails;
};

export type ExternalLocation = {
  type: string;
  status?: string;
  joinUrl?: string;
};

export type ExternalBookingDetails = {
  eventUri?: string;
  inviteeUri?: string;
  cancelUrl?: string;
  rescheduleUrl?: string;
  location?: ExternalLocation;
};

export const BookingSource = {
  SCHEDULER: 'scheduler',
  CALENDLY: 'calendly',
} as const;

export type BookingSource = (typeof BookingSource)[keyof typeof BookingSource];

export type Event = {
  uid: string;
  timezone: string;
};

export type Customer = {
  uid: string;
};

export type UpdateScreenerResponseDto = {
  percentage?: number;
  status?: ScreenerResponseStatus;
  isMatch?: boolean;
  responsesCount?: number;
  totalTime?: number;
  agree?: boolean;
  clientOrigin?: ClientOrigin;
  matching?: ScreenerResponseMatching;
  emailEvents?: EmailEvents;
  visibility?: ScreenerResponseVisibility;
  organizationFlag?: boolean;
  inviteId?: string | null;
  inviteCode?: string | null;
  initialUtm?: InitialUtm;
  dashboardPlacement?: DashboardPlacement;
  responses?: Array<
    | SaveResponseAnswerRadio
    | SaveResponseAnswerCheckbox
    | SaveResponseAnswerText
    | SaveResponseAnswerSkipped
    | SaveResponseAnswerFile
    | SaveResponseAnswerRank
    | SaveResponseAnswerMatrix
  >;
  history?: HistoryItem;
  invitation?: ScreenerResponseInvitation;
  statusUpdatedAt?: string | null;
  pay?: ScreenerResponsePay;
};

export type ScreenerResponsePay = {
  autopayOn: string;
};

export type ExternalBooking = {
  url: string;
  baseUrl: string;
};

export type ScreenerResponseInvitation = {
  id: string;
  /**
   * If a project is remote, this should be the link to the research study,
   * this will override the remoteCommunicationDetails from the project.
   * If a public api project which has an `externalResearcher.bookingUrl`, and `remoteCommunicationDetails` is passed in,` remoteCommunicationDetails` will be used instead for the bookingUrl on the screener response.
   * If a project has a task, this field is undefined.
   */
  remoteCommunicationDetails?: string;
  event?: InvitationEvent;
  /**
   * This will override the special instructions from the project.
   */
  message?: string;
  videoProvider?: InvitationVideoProvider;
  /**
   * External booking details for Calendly, Cal.com, etc.
   * Contains both the enriched single-use URL and the original base URL for regeneration.
   */
  externalBooking?: ExternalBooking;
};

export const ScreenerResponseHistoryReason = {
  RISK_CLEARED: 'risk-cleared',
} as const;

export type ScreenerResponseHistoryReason =
  (typeof ScreenerResponseHistoryReason)[keyof typeof ScreenerResponseHistoryReason];

export type HistoryItem = {
  /**
   * The role the change was made in — researcher, respondent or admin. Note
   * this is the role acted *as*, which on impersonation is not the role of the
   * account in `userId`.
   */
  actor?: string;
  /**
   * The account behind the request. Project history has carried this since the
   * beginning (`ProjectStatusHistory.userId`); screener responses never did,
   * which left "an admin reverted this participant" unanswerable (ONCALL-2128).
   *
   * This can disagree with `actor`, and the disagreement is the useful part: an
   * admin acting on a team's behalf is recorded here while `actor` keeps the
   * role they acted as, so impersonation is visible rather than hidden.
   *
   * Deliberately absent rather than null on genuinely automated writes (autopay,
   * batch pay): no account acted, and inventing one would be worse than the gap.
   */
  userId?: string;
  oldStatus: ScreenerResponseStatus;
  newStatus: ScreenerResponseStatus;
  createdAt: string;
  oldVisibilityCode?: ScreenerResponseCode;
  newVisibilityCode?: ScreenerResponseCode;
  reason?: ScreenerResponseHistoryReason;
};

export type EmailEvents = {
  sent: boolean;
  delivered: boolean;
  bounced: boolean;
  opened: boolean;
  clicked: boolean;
  unsubscribed: boolean;
  spam: boolean;
};

export type ScreenerResponseMatching = {
  isGenderMatch: boolean;
  isAgeMatch: boolean;
  isEthnicityMatch: boolean;
  isEducationMatch: boolean;
  questionsQualified: boolean;
  isLocationMatch: boolean;
  isIndustryMatch: boolean;
  isJobTitleMatch: boolean;
  isJobFunctionMatch?: boolean;
  isHouseholdIncomeMatch: boolean;
  isCompanySizeMatch: boolean;
  isTopicsMatch?: boolean;
};

export type ClientOrigin = {
  ip: string;
  userAgent: {
    [key: string]: unknown;
  };
};

export type UpdateScreenerResponseMeetingInformationDto = {
  /**
   * If a project is remote, this should be the link to the research study,
   * this will override the remoteCommunicationDetails from the project.
   * If a public api project which has an `externalResearcher.bookingUrl`, and `remoteCommunicationDetails` is passed in,` remoteCommunicationDetails` will be used instead for the bookingUrl on the screener response.
   * If a project has a task, this field is undefined.
   */
  remoteCommunicationDetails?: string;
  /**
   * This will override the special instructions from the project.
   */
  message?: string;
  screenerResponseIds?: Array<string>;
};

export type UpdateInvitationMessageDto = {
  message: string;
};

export type UpsertScreenerResponseDto = {
  percentage: number;
  status: ScreenerResponseStatus;
  isMatch: boolean;
  responsesCount: number;
  totalTime: number;
  agree: boolean;
  clientOrigin: ClientOrigin;
  matching: ScreenerResponseMatching;
  emailEvents: EmailEvents;
  visibility: ScreenerResponseVisibility;
  organizationFlag: boolean;
  inviteId?: string | null;
  inviteCode?: string | null;
  initialUtm?: InitialUtm;
  dashboardPlacement?: DashboardPlacement;
  responses: Array<
    | SaveResponseAnswerRadio
    | SaveResponseAnswerCheckbox
    | SaveResponseAnswerText
    | SaveResponseAnswerSkipped
    | SaveResponseAnswerFile
    | SaveResponseAnswerRank
    | SaveResponseAnswerMatrix
  >;
};

export type BatchInviteResultDto = {
  invited: Array<ScreenerResponse>;
  /**
   * Responses the quota gate refused. Other failures are logged and appear in neither list.
   */
  refused: Array<BatchInviteRefusedDto>;
};

export type QuotaBlockingBucketDto = {
  groupId: string;
  groupName: string;
  /**
   * `no-match` for the group's implicit bucket.
   */
  segmentId: string;
  segmentName: string | null;
  reason: QuotaRefusalReason;
};

export type QuotaGateRefusalDto = {
  reason: QuotaRefusalReason;
  blocking: Array<QuotaBlockingBucketDto>;
};

export type BatchInviteRefusedDto = {
  id: string;
  /**
   * Why the quota gate refused this invite; acknowledge it on a retry with `quotaOverride`.
   */
  refusal: QuotaGateRefusalDto;
};

export type ScreenerResponseOutstandingSteps = {
  linkedin: boolean;
  nda: boolean;
  phone: boolean;
  plaid: boolean;
};

export type ManualReviewAudit = {
  userId: string;
  reason: string;
  reviewedAt: string;
  status: ManualReviewAuditStatus;
};

export type RespondentTransactionSummary = {
  id: string;
  createdAt: string;
  projectId: string;
  organizationId: string;
  teamId: string;
  surveyResponseId: string;
  masterTransactionId: string;
  incentive?: number;
  fee?: number;
  tip?: number;
  totalIncentive?: number;
  total?: number;
  credit?: number;
  creditCost?: number;
  creditBalance?: number;
  incentiveBalance?: number;
  participantType?: ParticipantType;
  payoutCount?: number;
  updatedAt?: string;
};

export type Rejection = {
  message: string;
  rejectedAt: string;
};

export const DisqualifyReasonDescription = {
  FALSE_OR_MISLEADING_INFORMATION_PROVIDED_BY_PARTICIPANT:
    'False or misleading information provided by Participant',
  NOT_ENOUGH_INFORMATION_ABOUT_PARTICIPANT:
    'Not enough information about participant',
  ANSWERS_TO_SCREENER_QUESTIONS_NOT_A_MATCH:
    'Answers to screener questions not a match',
  NOT_A_GOOD_FIT_FOR_THIS_STUDY: 'Not a good fit for this study',
  OTHER: 'Other',
  PAID_FOR_ANOTHER_STUDY_BY_THIS_ORGANIZATION_WITHIN_THE_PAST_PARTICIPATION_WINDOW:
    'Paid for another study by this organization within the past-participation window',
} as const;

export type DisqualifyReasonDescription =
  (typeof DisqualifyReasonDescription)[keyof typeof DisqualifyReasonDescription];

export type DisqualifyReason = {
  code: DisqualifyReasonCode;
  text: DisqualifyReasonDescription;
};

export type DisqualificationReason = {
  reasons: Array<DisqualifyReason>;
  message: string;
  disqualifiedAt: string;
};

export type ScreenerResponseExternalAnswer = {
  /**
   * References `Project.externalQuestions[].questionId`.
   */
  questionId: string;
  /**
   * Selected option ids for RADIO/CHECKBOX (`Project.externalQuestions[]
   * .options[].id`, validated at ingestion), or the structured value(s) for
   * NUMBER.
   */
  answerValues?: Array<string>;
  /**
   * Free-text answer for TEXT/OTHER questions.
   */
  answerText?: string;
  /**
   * The partner's per-question verdict. The response's overall `qualified`
   * is derived at the IN_PROGRESS → SUBMITTED transition: qualified iff
   * every row qualifies (decision 5).
   */
  qualifies: boolean;
};

export type ScreenerResponseExternalScreener = {
  startedAt?: string;
  submittedAt?: string;
  verdictAt?: string;
  url?: string;
};

export const QuotaResponseTier = {
  PENDING: 'PENDING',
  INVITED: 'INVITED',
  ATTENDED: 'ATTENDED',
  RELEASED: 'RELEASED',
} as const;

export type QuotaResponseTier =
  (typeof QuotaResponseTier)[keyof typeof QuotaResponseTier];

export type ScreenerResponseQuotaBucketRef = {
  groupId: string;
  /**
   * `no-match` for the group's implicit bucket.
   */
  segmentId: string;
};

export type ScreenerResponseQuotaAssignment = {
  /**
   * One entry per quota group.
   */
  segments: Array<ScreenerResponseQuotaBucketRef>;
  tier: QuotaResponseTier;
  /**
   * The quota revision the segments were computed against.
   */
  revision: number;
  /**
   * Last time the segments were (re)computed.
   */
  assignedAt: string;
  /**
   * Set on every tier write, the initial PENDING included.
   */
  tierChangedAt: string;
};

export type ScreenerResponseAutoInvite = {
  autoInviteOn: string;
};

export type ScreenerResponseComplete = {
  autocompleteOn: string;
};

export type RespondentPayoutRespondent = {
  userId: string;
  profileId: string;
  firstName: string;
  lastName: string;
  email: string;
  paypal?: string | null;
};

export type RespondentPayoutProject = {
  id: string;
  name: string;
  userId: string;
};

export type RespondentPayout = {
  id: string;
  createdAt: string;
  projectId: string;
  organizationId: string;
  teamId: string;
  surveyResponseId: string;
  masterTransactionId?: string;
  type: string;
  paymentProcessingSystem?: string;
  status: RespondentPayoutStatus;
  project: RespondentPayoutProject;
  respondent: RespondentPayoutRespondent;
  metadata: PaypalPayoutMetadata;
  paypalMetadata?: PaypalPayoutMetadata;
  tremendousMetadata?: TremendousPayoutMetadata;
  fee: number;
  incentive: number;
  tip: number;
  totalIncentive: number;
  participantType?: ParticipantType;
  methodologyType?: MethodologyType;
  masterTransactions?: Array<MasterTransactionCompact>;
  updatedAt?: string;
};

export type ScreenerResponseReferrer = {
  userId: string;
  projectId?: string | null;
  type?: ReferralType;
  amount: number;
  paypal?: string | null;
  createdAt: string;
};

export type ScreenerResponseProfile = {
  id: string;
  userId: string;
  firstName: string;
  lastName: string;
  status: RespondentStatus;
  phone?: string;
  email: string;
  paypal?: string | null;
  demographics?: ProfileDemographics;
  employment?: ProfileEmployment | null;
  picture?: string | null;
  location?: ProfileLocation;
  socialLinks?: ProfileSocialLinks;
  score: number;
  skills: Array<SimpleSkill>;
  videoIntroUrl?: string | null;
  topics?: Array<SimpleTopic>;
  personal?: PersonalInfo;
  technical?: Technical;
  productsServices?: ProductsServicesInfo;
  shopping?: ShoppingInfo;
  professional?: ProfessionalInfo;
  createdAt?: string;
  externalOrganizationId?: string;
  twilioPhoneData?: ProfileTwilioPhoneData;
  risk?: ProfileRisk;
  trustedCompany?: ProfileTrustedCompany | null;
};

export type ScreenerResponseInviter = {
  createdAt: string;
  userId?: string;
  organizationId?: string;
  projectId?: string;
  inviteCode?: string;
};

export type InvitationObject = {
  createdAt: string;
  profileId: string;
  userId: string;
  organizationId: string;
  teamId: string;
  projectId: string;
  senderId: string;
  message: string;
  eventUid?: string;
  remoteCommunicationDetails?: string;
  skipScreenerQuestions: boolean;
  recruitmentSource?: RecruitmentSource;
  /**
   * Provenance for cross-project (follow-up) invitations: the project this
   * participant was shared FROM, always a project of the same team. Absent on
   * ordinary panel or same-project invitations, which is what makes the
   * "shared with A / new from panel" reporting possible — see
   * `../../programs/programs.md`. Unrelated to
   * `ProfileProjectMatch.sourceProjectId` (matching subsystem).
   */
  sourceProjectId?: string;
  /**
   * The messaging conversation attached to this invitee for this project,
   * mirroring `ScreenerResponse.conversationUid`. Written by the conversations
   * service on create so a person with no screener response yet (asked, not
   * heard back) still resolves to their existing thread — the messaging
   * create endpoint does not dedupe, so without this the program roster's
   * Message tab would fork a parallel conversation on every reopen.
   */
  conversationUid?: string;
  profile: InvitationProfile;
  project: InvitationProject;
  id: string;
  updatedAt?: string;
};

export type ScreenerResponseBooking = {
  uid?: string;
  customer?: Customer;
  event: Event;
  timezone?: string;
  paypal?: string | null;
  phone?: string;
  start: string;
  end: string;
  message?: string;
  source?: BookingSource;
  externalBooking?: ExternalBookingDetails;
};

export type TaskReminderEmailEventHistoryItem = {
  id?: string;
  type: EmailEventType;
  timestamp: string;
  notificationId?: string;
  reminderName?: string;
};

export type EmailEventHistoryItem = {
  id?: string;
  type: EmailEventType;
  timestamp: string;
};

export type EmailEventFlags = {
  accepted: boolean;
  rejected: boolean;
  delivered: boolean;
  failed: boolean;
  opened: boolean;
  clicked: boolean;
  unsubscribed: boolean;
  complained: boolean;
};

export type ScreenerResponseAnswerMatrixSelection = {
  rowUid: string;
  rowText: string;
  answerUid: string;
  answerText: string;
};

export type ScreenerResponseAnswerMatrix = {
  questionId: string;
  /**
   * Stable question uid (mirrors screener question `uid`); optional on legacy rows.
   */
  questionUid?: string;
  questionText: string;
  order?: number;
  type: string;
  questionType: QuestionType;
  qualify: boolean;
  createdAt?: string;
  /**
   * One or more selected columns per answered row. Single-selection-mode
   * questions store exactly one selection per row; multi-selection-mode
   * questions may store several.
   */
  selections: Array<ScreenerResponseAnswerMatrixSelection>;
};

export type ScreenerResponseAnswerRankItem = {
  uid: string;
  text: string;
};

export type ScreenerResponseAnswerRank = {
  questionId: string;
  /**
   * Stable question uid (mirrors screener question `uid`); optional on legacy rows.
   */
  questionUid?: string;
  questionText: string;
  order?: number;
  type: string;
  questionType: QuestionType;
  qualify: boolean;
  createdAt?: string;
  /**
   * Options ordered best→worst. 1-based position = array index + 1.
   */
  ranking: Array<ScreenerResponseAnswerRankItem>;
};

export const UploadScanStatus = {
  PENDING: 'pending',
  CLEAN: 'clean',
  INFECTED: 'infected',
  UNSCANNED: 'unscanned',
} as const;

export type UploadScanStatus =
  (typeof UploadScanStatus)[keyof typeof UploadScanStatus];

export type ScreenerResponseAnswerFileItem = {
  key: string;
  name: string;
  size: number;
  mime: string;
  uploadedAt: string;
  scanStatus?: UploadScanStatus;
  url?: string;
};

export type ScreenerResponseAnswerFile = {
  questionId: string;
  /**
   * Stable question uid (mirrors screener question `uid`); optional on legacy rows.
   */
  questionUid?: string;
  questionText: string;
  order?: number;
  type: string;
  questionType: QuestionType;
  qualify: boolean;
  createdAt?: string;
  files: Array<ScreenerResponseAnswerFileItem>;
  note?: string;
};

export type ScreenerResponseAnswerSkipped = {
  questionId: string;
  /**
   * Stable question uid (mirrors screener question `uid`); optional on legacy rows.
   */
  questionUid?: string;
  questionText: string;
  order?: number;
  type: string;
  questionType: QuestionType;
  qualify: boolean;
  createdAt?: string;
  skipped: boolean;
  skippedBy?: 'user' | 'skipLogic';
};

export type ScreenerResponseAnswerText = {
  questionId: string;
  /**
   * Stable question uid (mirrors screener question `uid`); optional on legacy rows.
   */
  questionUid?: string;
  questionText: string;
  order?: number;
  type: string;
  questionType: QuestionType;
  qualify: boolean;
  createdAt?: string;
  answers: string;
};

export type ScreenerResponseAnswerCheckbox = {
  questionId: string;
  /**
   * Stable question uid (mirrors screener question `uid`); optional on legacy rows.
   */
  questionUid?: string;
  questionText: string;
  order?: number;
  type: string;
  questionType: QuestionType;
  qualify: boolean;
  createdAt?: string;
  /**
   * Checkbox Answers are an array
   */
  answers: Array<ScreenerQuestionAnswerValue>;
};

export type ScreenerQuestionAnswerValue = {
  id: string;
  uid: string;
  text: string;
  answerValue: number;
  goToQuestionUid?: string | null;
  isOther?: boolean;
  other?: string;
  checked?: boolean;
};

export type ScreenerResponseAnswerRadio = {
  questionId: string;
  /**
   * Stable question uid (mirrors screener question `uid`); optional on legacy rows.
   */
  questionUid?: string;
  questionText: string;
  order?: number;
  type: string;
  questionType: QuestionType;
  qualify: boolean;
  createdAt?: string;
  /**
   * Radio Answers are a single object
   */
  answers: ScreenerQuestionAnswerValue;
};

export type ScreenerResponse = {
  id: string;
  createdAt: string;
  projectId: string;
  userId: string;
  qualified: boolean;
  percentage: number;
  /**
   * This response was filed under an invitation that waives the screener
   * (`Invitation.skipScreenerQuestions`), so it carries no answers by design
   * and `percentage` is meaningless for it — the researcher UI labels the
   * score column "Screener bypassed" rather than showing 0%.
   *
   * Persisted rather than re-derived from `invite.skipScreenerQuestions` on
   * each pass. `ScreenerResponse.invite` is typed as `InvitationObject`, which
   * NestJS builds with `OmitType` — a helper that returns a class NOT
   * extending `Invitation` and re-applies only swagger/validator/transformer
   * metadata, never typegoose's `prop()`. The embedded sub-schema therefore
   * has exactly one field (`id`) and mongoose strips the rest on write, so a
   * recalculation reading the stored invitation cannot see the flag. This
   * field is the durable record; it is only ever set, never cleared.
   */
  screenerBypassed?: boolean;
  ndaUrl?: string | null;
  status: ScreenerResponseStatus;
  favorite?: boolean;
  hidden?: boolean;
  isMatch: boolean;
  responses: Array<
    | ScreenerResponseAnswerRadio
    | ScreenerResponseAnswerCheckbox
    | ScreenerResponseAnswerText
    | ScreenerResponseAnswerSkipped
    | ScreenerResponseAnswerFile
    | ScreenerResponseAnswerRank
    | ScreenerResponseAnswerMatrix
  >;
  responsesCount: number;
  totalTime: number;
  agree: boolean;
  clientOrigin: ClientOrigin;
  isNew: boolean;
  matching: ScreenerResponseMatching;
  emailEvents: EmailEvents;
  invitationEmailEvents?: EmailEventFlags;
  invitationEmailEventsHistory?: EmailEventHistoryItem;
  taskReminderEmailEvents?: EmailEventFlags;
  taskReminderEmailEventsHistory?: Array<TaskReminderEmailEventHistoryItem>;
  visibility: ScreenerResponseVisibility;
  history?: HistoryItem;
  invitation?: ScreenerResponseInvitation;
  inviteSentAt?: string;
  invited?: boolean;
  booking?: ScreenerResponseBooking;
  organizationFlag: boolean;
  inviteId?: string | null;
  inviteCode?: string | null;
  invite?: InvitationObject | null;
  inviter?: ScreenerResponseInviter | null;
  recruitmentSource?: RecruitmentSource;
  scheduledAt?: string;
  statusUpdatedAt?: string | null;
  project?: ScreenerResponseProject;
  profile?: ScreenerResponseProfile;
  referrer?: ScreenerResponseReferrer | null;
  review?: ReviewV2;
  payouts?: Array<RespondentPayout>;
  conversationUid?: string;
  rescheduleOverriden?: boolean;
  projectStarted?: boolean;
  /**
   * When the participant first started the task (unmoderated "Start project"
   * or opening an AI-moderated interview). Written with `$min` via
   * `projectStartUpdate`, so returning to the task keeps the first time.
   * Absent on responses started before this field existed.
   */
  projectStartedAt?: string;
  task?: Task;
  customIncentive?: number;
  flagged?: boolean;
  flaggedMessage?: string;
  flaggedAt?: string;
  payParticipant?: boolean;
  pay?: ScreenerResponsePay;
  complete?: ScreenerResponseComplete;
  autoInvite?: ScreenerResponseAutoInvite;
  /**
   * Quotas V2 (read-only): the response's segment per quota group and its consumption tier.
   */
  quotaAssignment?: ScreenerResponseQuotaAssignment;
  externalScreener?: ScreenerResponseExternalScreener;
  externalResponses?: Array<ScreenerResponseExternalAnswer>;
  qualifiedOverriden?: boolean;
  disqualification?: DisqualificationReason;
  rejection?: Rejection;
  cancelledBy?: Actor;
  actor?: Actor;
  reason?: ParticipantCancellationReason;
  shortNoticeCancel?: boolean;
  message?: string;
  confirmedBy?: Actor;
  externalProject?: ExternalProject;
  respondentTransactions?: Array<RespondentTransactionSummary>;
  v4Score?: number;
  paidCount?: number;
  /**
   * Team-scoped count of this participant's prior PAID responses, excluding the
   * current project. Computed at read time by the findAll aggregation (not a
   * stored field). Distinct from `paidCount`, which is platform-wide.
   */
  teamPastParticipant?: number;
  qualifiedPendingIdVerification?: boolean;
  qualifiedPendingManualReview?: boolean;
  /**
   * Server-set marker: the response was filed through the logged-out public
   * application flow (`POST :id/public-application`). Never client-supplied.
   *
   * When this is also the account's first application (no response of
   * theirs predates it — see
   * `CreateScreenerResponsesService.isFirstPublicApplication`), the response
   * skips the phone/Plaid outstanding steps and is held for manual
   * application review instead; the manual review is the terminal identity
   * check for that one application. Both facts are historical, so the
   * special-casing is stable across recalculations. Later applications are
   * new responses without this marker and get the normal verification steps.
   */
  isPublicApplication?: boolean;
  /**
   * Server-set marker: the response was filed for an invite-link participant
   * (`features/external-participants`), a person with a shim account and no
   * Profile. Never client-supplied. The reducer paths that need a profile
   * (targeting match, verification steps, manual review) never run for these
   * responses; the admission service files them directly.
   */
  externalParticipantId?: string;
  /**
   * The `uid` the researcher put on the invite link URL for this person, when
   * the project asks for one. With `projectId` it backs the
   * one-completion-per-(project, uid) rule. Opaque to us; stored as sent.
   */
  externalUid?: string;
  /**
   * The researcher's own query parameters on the invite link this person
   * arrived through, other than `uid`, captured by the landing page from the
   * first URL they landed on and frozen here when the application is filed.
   * Opaque to us; handed back to the researcher on the response and as
   * columns of the export so they can reconcile the answers with their own
   * records. Keys are short identifiers (see
   * `external-participants/external-link-params.ts`), so a key is never a
   * Mongo path or operator.
   */
  externalLinkParams?: {
    [key: string]: string;
  };
  /**
   * Server-set from the project's external invite configuration when an
   * external participant's application is filed: the researcher pays this
   * participant's incentive off the platform. Their payout charges the
   * recruitment fee only, sends no money and no payout email, and takes no
   * tip, custom incentive or repeat payout.
   */
  incentivePaidExternally?: boolean;
  manualReviewAudit?: ManualReviewAudit;
  outstandingSteps?: ScreenerResponseOutstandingSteps;
  initialUtm?: InitialUtm;
  dashboardPlacement?: DashboardPlacement;
  updatedAt?: string;
};

export type BatchInviteScreenerResponseDto = {
  /**
   * This will override the special instructions from the project.
   */
  message?: string;
  videoProvider?: InvitationVideoProvider;
  /**
   * If a project is remote, this should be the link to the research study,
   * this will override the remoteCommunicationDetails from the project.
   * If a public api project which has an `externalResearcher.bookingUrl`, and `remoteCommunicationDetails` is passed in,` remoteCommunicationDetails` will be used instead for the bookingUrl on the screener response.
   * If a project has a task, this field is undefined.
   */
  remoteCommunicationDetails?: string;
  event?: InvitationEvent;
  /**
   * External booking URL for Calendly, Cal.com, etc. with prepopulated parameters.
   * This field is used to provide participants with a direct link to schedule their session
   * on an external scheduling platform.
   * Only whitelisted domains are allowed for security purposes.
   * Note: null values are rejected - omit the field if not needed.
   */
  externalBookingUrl?: string;
  /**
   * Only for Public Api. If set, will not be used to overwrite bookingUrl on screener response.
   * If a project is remote, this should be the link to the research study,
   * this will override the remoteCommunicationDetails from the project.
   * If a project has a task, this field is undefined.
   */
  meetingLink?: string;
  /**
   * Only for Public Api. If set, will be used to write bookingUrl on screener response.
   * If a project has a task, this field is undefined.
   */
  bookingLink?: string;
  /**
   * Quotas V2: acknowledges a refusal the invite gate returned as a 409 and
   * asks for the invite to proceed anyway. Only the reasons listed here are
   * lifted, and only when the gate refused for them.
   */
  quotaOverride?: QuotaOverrideDto;
  screenerResponseIds: Array<string>;
};

export type ScreenerResponsesPaginatedDto = {
  /**
   * The total number of results possible, which is only provided in the response when `includeCount` is passed as `true` within a request.
   */
  totalResults?: number;
  page: number;
  pageSize: number;
  results: Array<ScreenerResponse>;
  /**
   * Distinct country codes present on visible applicants for this project
   * (scoped to the list's visibility/hidden view). Used to build the Location
   * filter options. Independent of gender/age/country/status filters so options
   * stay stable while filtering. Not returned when `includeRosterSummary` is
   * false.
   */
  availableCountries?: Array<CountryCode>;
  /**
   * Size of the project's whole roster: every applicant, ignoring the active
   * view and all filters (hidden rows included). Unlike `totalResults` — which
   * counts the current view — this distinguishes "nobody has applied" from
   * "everyone is in another view", so the dashboard knows whether to show its
   * empty-roster state or keep the filter/view switcher on screen (RX-310).
   * Only returned when `includeCount` is set and `includeRosterSummary` is not
   * false.
   */
  rosterCount?: number;
  /**
   * Whether anyone on that roster came through another channel than the
   * marketplace: an external participant or a participant database invitee
   * (by `externalParticipantId` or the stamped `recruitmentSource`). The
   * dashboard tags each row with its source only when this is true. Only
   * returned when `includeCount` is set and `includeRosterSummary` is not
   * false.
   */
  hasNonMarketplaceParticipants?: boolean;
};

export const ScreenerResponseSortField = {
  SCORE: 'SCORE',
  CREATED_AT: 'CREATED_AT',
} as const;

export type ScreenerResponseSortField =
  (typeof ScreenerResponseSortField)[keyof typeof ScreenerResponseSortField];

export type ParticipantIdentityVerificationDto = {
  hasHistory: boolean;
  provider?: IdentityVerificationProvider;
  template?: IdentityVerificationTemplateType;
  /**
   * The provider's own status, untouched: Plaid `active | success | failed |
   * expired | canceled | pending_review`, Persona `created | pending |
   * completed | approved | declined | failed | expired | needs_review`.
   */
  status?: string;
  /**
   * Why we rejected the verification even though the provider may have
   * passed it (`DOB_MISMATCH`, `DUPLICATE_DOCUMENT`), or the provider's own
   * failure (`PLAID_FAILURE` / `PERSONA_FAILURE`).
   */
  failureReason?: string;
  /**
   * Provider-side identifier: Plaid attempt id or Persona inquiry id (`inq_...`).
   */
  externalId?: string;
  firstName?: string;
  lastName?: string;
  address?: string;
  completedAt?: string;
  verifications?: Array<ParticipantIdentityVerificationStepDto>;
};

export type ParticipantIdentityVerificationStepDto = {
  /**
   * Provider resource type with the `verification/` prefix stripped, e.g. `government-id`.
   */
  type: string;
  /**
   * Provider-side id of this check (Persona `ver_...`).
   */
  verificationId: string;
  status?: string;
  completedAt?: string;
};

export const IdentityVerificationTemplateType = {
  DEFAULT: 'default',
  DOCUMENT: 'document',
  OTHER: 'other',
} as const;

export type IdentityVerificationTemplateType =
  (typeof IdentityVerificationTemplateType)[keyof typeof IdentityVerificationTemplateType];

export const IdentityVerificationProvider = {
  PLAID: 'plaid',
  PERSONA: 'persona',
} as const;

export type IdentityVerificationProvider =
  (typeof IdentityVerificationProvider)[keyof typeof IdentityVerificationProvider];

export type ParticipantFlagHistoryDto = {
  results: Array<ParticipantFlagReport>;
};

export type FlagReportReason = {
  code: string;
  text: string;
};

export type ParticipantFlagReport = {
  organizationId?: string;
  organizationName?: string;
  reasons: Array<FlagReportReason>;
  message?: string;
  createdAt?: string;
};

export type ManualReviewActionBody = {
  reason: string;
};

export type ParticipantIpSummaryDto = {
  results: Array<ParticipantIpEntry>;
};

export type ParticipantIpEntry = {
  ip: string;
  count: number;
  /**
   * ISO-3166-1 alpha-2 country code resolved from the IP, when available.
   */
  country?: string;
  /**
   * Whether the IP is a VPN / anonymizing proxy / Tor exit node, read from the
   * cached MaxMind classification only. `undefined` when the IP is not cached
   * (no live lookup is performed) — surfaced as "check unavailable".
   */
  isVpn?: boolean;
};

export type ManualReviewResponsesPaginatedDto = {
  /**
   * The total number of results possible, which is only provided in the response when `includeCount` is passed as `true` within a request.
   */
  totalResults?: number;
  page: number;
  pageSize: number;
  results: Array<ManualReviewResponseDto>;
};

export type ManualReviewProjectDto = {
  id: string;
  name: string;
  organizationId?: string;
  organizationName?: string;
  isAiModerated: boolean;
};

export type ManualReviewParticipantDto = {
  firstName?: string;
  lastName?: string;
  email?: string;
};

export type ManualReviewResponseDto = {
  id: string;
  userId: string;
  projectId: string;
  status: string;
  percentage: number;
  qualified: boolean;
  qualifiedPendingManualReview?: boolean;
  /**
   * Filed through the logged-out public application flow — held here
   * unconditionally because the phone/Plaid ID verification steps were
   * skipped (see docs/features/public-screener-flow.md). Surfaced so the
   * queue can distinguish these from flag-gated holds.
   */
  isPublicApplication?: boolean;
  createdAt: string;
  profile: ManualReviewParticipantDto;
  project: ManualReviewProjectDto;
};

export type FulfillmentRequestsPaginatedDto = {
  /**
   * The total number of results possible, which is only provided in the response when `includeCount` is passed as `true` within a request.
   */
  totalResults?: number;
  page: number;
  pageSize: number;
  results: Array<FulfillmentRequestRowDto>;
};

export const FulfillmentRequestStatus = {
  OPEN: 'open',
  FULFILLED: 'fulfilled',
  ABOVE_MAX: 'above_max',
  PROJECT_CLOSED: 'project_closed',
  PROJECT_MISSING: 'project_missing',
} as const;

export type FulfillmentRequestStatus =
  (typeof FulfillmentRequestStatus)[keyof typeof FulfillmentRequestStatus];

export type FulfillmentRequestRowDto = {
  id: string;
  createdAt: string;
  /**
   * Time of the latest request; a repeat ask refreshes it.
   */
  updatedAt: string;
  requestedParticipants: number;
  note?: string;
  userId: string;
  userEmail?: string;
  projectId: string;
  projectName?: string;
  kindOfResearch?: number;
  numberOfParticipants?: number;
  projectStatus?: string;
  organizationId?: string;
  organizationName?: string;
  increasedTargetLimit?: boolean;
  /**
   * The project's cap today, from its study type and the organization flag.
   */
  currentLimit?: number;
  status: FulfillmentRequestStatus;
};

export type BlockWorkDomainResponseDto = {
  results: BlockWorkDomainResultDto;
};

export type PaidPendingPayoutDto = {
  /**
   * respondentpayouts _id — pass to admin-cancel-payout.
   */
  payoutId: string;
  surveyResponseId: string | null;
  projectId: string | null;
  projectName: string | null;
  userId: string | null;
  incentive: number | null;
  totalIncentive: number | null;
};

export type BlockWorkDomainResultDto = {
  domain: string;
  matchType: 'exact' | 'self_and_subdomains' | 'tld';
  since: string | null;
  dryRun: boolean;
  rule: {
    alreadyExisted?: boolean;
    created?: boolean;
  };
  /**
   * Verified profiles matching the rule.
   */
  candidateProfiles: number;
  /**
   * Profiles whose verification + trustedCompany were cleared (or would be, in dry run). May be < candidateProfiles if a batch failed — see failedProfileIds.
   */
  clearedProfiles: number;
  clearedTrustedCompany: number;
  /**
   * Company records set trusted:false (or that would be, in dry run).
   */
  companiesUntrusted: number;
  /**
   * Still-visible applications that were (or would be) dismissed. PAID responses are never dismissed.
   */
  dismissedResponses: number;
  /**
   * SCHEDULED/ATTENDED responses set to CANCELLED (or that would be, in dry run). The close-project check counts these statuses regardless of visibility, so dismissal alone would leave the project impossible to close.
   */
  cancelledResponses: number;
  /**
   * Scheduled autopays (pay.autopayOn) cleared, or that would be, on the matched users’ non-PAID responses. The autopay cron ignores visibility, so dismissal alone would not stop these payments.
   */
  clearedAutopays: number;
  /**
   * Still-pending (undisbursed) payouts for the matched users — review and cancel via admin-cancel-payout.
   */
  paidPendingPayouts: Array<PaidPendingPayoutDto>;
  /**
   * Profile IDs whose clear + event could not be committed this run (left verified for a re-run). Empty on success and on dry runs.
   */
  failedProfileIds: Array<string>;
  sampleUserIds: Array<string>;
};

export type BlockWorkDomainDto = {
  /**
   * Domain or TLD to block — e.g. "live" with matchType "tld", or "evilcorp.com" with "exact"/"self_and_subdomains".
   */
  domain: string;
  matchType: 'exact' | 'self_and_subdomains' | 'tld';
  /**
   * Scopes only which screener responses are dismissed and which scheduled autopays are cleared — limited to those created on or after this date. Every verified profile matching the domain/TLD still has its work-email verification and trustedCompany cleared regardless of this date. Defaults to the last 90 days (the work-email re-verification window); pass an explicit earlier date for a deeper historical cleanup.
   */
  since?: string;
  /**
   * When true (default) nothing is written; returns a preview of what would change.
   */
  dryRun?: boolean;
  /**
   * Cap the number of profiles remediated in a single call.
   */
  limit?: number;
  /**
   * self_and_subdomains only: also mark matching company records trusted:false. Exact blocks always untrust the matched company record; tld blocks never do.
   */
  untrustCompanies?: boolean;
};

export type AdminTeamsPaginatedDto = {
  /**
   * The total number of results possible, which is only provided in the response when `includeCount` is passed as `true` within a request.
   */
  totalResults?: number;
  page: number;
  pageSize: number;
  results: Array<AdminTeamRowDto>;
};

export type AdminTeamRowDto = {
  id: string;
  organizationId: string;
  /**
   * Old team documents can lack a name.
   */
  name?: string;
  /**
   * `members.length`, inactive members included (legacy parity).
   */
  usersCount: number;
  isActive: boolean;
  isDeleted: boolean;
  newPricing: boolean;
  /**
   * 1 = legacy pricing, 2 = new pricing, 3 = new pricing with a V3 plan.
   */
  pricingVersion: number;
  /**
   * Raw "active plan is V3" flag, independent of `newPricing`.
   */
  isPricingV3: boolean;
  createdAt?: string;
  updatedAt?: string;
};

export type UpdateResearcherResponseDto = {
  userId: string;
  isActive: boolean;
  trustLevel: number | null;
  pendingReview: boolean;
  approvedBy: string | null;
};

export type UpdateResearcherDto = {
  /**
   * Activate/deactivate the researcher account. Silent in both directions: the user is never emailed (legacy admin-request parity).
   */
  isActive?: boolean;
  /**
   * Researcher trust level (0 flagged / 50 pending / 95 approved / 100 trusted).
   */
  trustLevel?: 0 | 50 | 95 | 100;
  /**
   * Set false to approve a pending researcher: the server stamps approvedBy and defaults trustLevel to 95 (approved) unless one is provided.
   */
  pendingReview?: boolean;
};

export const ResearcherStatusFilter = {
  ACTIVE: 'active',
  DEACTIVATED: 'deactivated',
  ALL: 'all',
} as const;

export type ResearcherStatusFilter =
  (typeof ResearcherStatusFilter)[keyof typeof ResearcherStatusFilter];

export const ResearcherReviewStatusFilter = {
  PENDING: 'pending',
  APPROVED: 'approved',
} as const;

export type ResearcherReviewStatusFilter =
  (typeof ResearcherReviewStatusFilter)[keyof typeof ResearcherReviewStatusFilter];

export const ResearcherTrustLevelStatusFilter = {
  OVER50: 'over50',
  UNDER50: 'under50',
} as const;

export type ResearcherTrustLevelStatusFilter =
  (typeof ResearcherTrustLevelStatusFilter)[keyof typeof ResearcherTrustLevelStatusFilter];

export type AdminResearchersPaginatedDto = {
  /**
   * The total number of results possible, which is only provided in the response when `includeCount` is passed as `true` within a request.
   */
  totalResults?: number;
  page: number;
  pageSize: number;
  results: Array<AdminResearcherRowDto>;
};

export type AdminResearcherOrganizationDto = {
  id: string;
  name?: string;
  userRoles: Array<string>;
  website?: string;
  usersCount?: number;
  trustLevel?: number;
};

export type AdminResearcherRowDto = {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  isEmailVerified: boolean;
  phone?: string;
  trustLevel?: number;
  pendingReview: boolean;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
  lastLoginAt?: string;
  experimentNames: Array<string>;
  organizations: Array<AdminResearcherOrganizationDto>;
};

export type StandardPlanRate = {
  payg_v3: number;
  basic_v3: number;
  advanced_v3: number;
};

export type UpdatePricingPlanRestrictedDto = {
  name?: string;
  description?: string;
  tags?: Array<string>;
  active?: boolean;
};

export type PricingPlansPaginatedDto = {
  /**
   * The total number of results possible, which is only provided in the response when `includeCount` is passed as `true` within a request.
   */
  totalResults?: number;
  page: number;
  pageSize: number;
  results: Array<PricingPlanListItemDto>;
};

export const PricingPlanPeriodUnit = {
  YEARLY: 'yearly',
  MONTHLY: 'monthly',
  INFINITE: 'infinite',
} as const;

export type PricingPlanPeriodUnit =
  (typeof PricingPlanPeriodUnit)[keyof typeof PricingPlanPeriodUnit];

export type PricingPlanPeriod = {
  unit: PricingPlanPeriodUnit;
  amount: number;
};

export type PricingPlanListItemDto = {
  id: string;
  createdAt: string;
  organizationId?: string;
  teamId?: string;
  /**
   * @deprecated
   */
  period: PricingPlanPeriod;
  /**
   * @deprecated
   */
  price: number;
  name: string;
  description: string;
  tags: Array<string>;
  active: boolean;
  components: Array<
    CreditComponent | CapabilityComponent | CreditRateComponent
  >;
  agreement: PricingPlanAgreement;
  autoRenew: boolean;
  pricingVersion?: number;
  teamName?: string;
  organizationName: string;
  updatedAt?: string;
};

export type GetPricingPlansPaginatedDto = {
  page?: number;
  pageSize?: number;
  includeCount?: boolean;
  filters?: PricingPlanFilters;
};

export const PricingPlanType = {
  STANDARD: 'standard',
  CUSTOM: 'custom',
} as const;

export type PricingPlanType =
  (typeof PricingPlanType)[keyof typeof PricingPlanType];

export type PricingPlanFilters = {
  organizationId?: string;
  teamId?: string;
  tags?: Array<string>;
  active?: boolean;
  planType?: PricingPlanType;
  search?: string;
  includePricingV3?: boolean;
};

export type StandardPricingPlanSummaryDto = {
  pricingPlan: PricingPlan;
  rateCard: RateCard;
};

export type PricingPlan = {
  id: string;
  createdAt: string;
  organizationId?: string;
  teamId?: string;
  /**
   * @deprecated
   */
  period: PricingPlanPeriod;
  /**
   * @deprecated
   */
  price: number;
  name: string;
  description: string;
  tags: Array<string>;
  active: boolean;
  components: Array<
    CreditComponent | CapabilityComponent | CreditRateComponent
  >;
  agreement: PricingPlanAgreement;
  autoRenew: boolean;
  pricingVersion?: number;
  updatedAt?: string;
};

export const PricingPlanPeriodTagType = {
  YEARLY: 'yearly',
  MONTHLY: 'monthly',
} as const;

export type PricingPlanPeriodTagType =
  (typeof PricingPlanPeriodTagType)[keyof typeof PricingPlanPeriodTagType];

export const MarketingStandardPricingPlanTypeV3 = {
  PAYG_V3: 'payg_v3',
  BASIC_V3: 'basic_v3',
  ADVANCED_V3: 'advanced_v3',
} as const;

export type MarketingStandardPricingPlanTypeV3 =
  (typeof MarketingStandardPricingPlanTypeV3)[keyof typeof MarketingStandardPricingPlanTypeV3];

export type CreatePricingPlanDto = {
  organizationId?: string;
  teamId?: string;
  /**
   * @deprecated
   */
  period: PricingPlanPeriod;
  /**
   * @deprecated
   */
  price: number;
  name: string;
  description: string;
  tags: Array<string>;
  active: boolean;
  components: Array<
    CreditComponent | CapabilityComponent | CreditRateComponent
  >;
  autoRenew: boolean;
  pricingVersion?: number;
  agreement: CreatePricingPlanAgreementDto;
};

export type CreatePricingPlanAgreementDto = {
  createdAt: string;
  commitment: number;
  expiresAt?: string;
  baseDiscount?: number;
  updatedAt?: string;
};

export type AbortMessageAttachmentUploadDto = {
  uploadId: string;
  bucketKey: string;
};

export type CompleteMessageAttachmentUploadDto = {
  uploadId: string;
  bucketKey: string;
  parts: Array<CompletedMessageAttachmentPartDto>;
};

export type CompletedMessageAttachmentPartDto = {
  partNumber: number;
  etag: string;
};

export type UploadMessageAttachmentPartResponseDto = {
  /**
   * 1-based part index echoed from the request
   */
  partNumber: number;
  /**
   * S3 ETag for this part (sent back on complete)
   */
  etag: string;
};

export type UploadMessageAttachmentPartDto = {
  uploadId: string;
  bucketKey: string;
  partNumber: number;
  /**
   * Base64-encoded binary data for this part
   */
  data: string;
};

export type CreateSignedUrlDto = {
  bucketKey: string;
  /**
   * When set, the URL is signed with a `Content-Disposition: attachment`
   * response override carrying this filename. Lets a browser download the
   * object by plain navigation — no CORS-dependent fetch — with a
   * human-readable name instead of the opaque bucket key.
   */
  downloadFilename?: string;
};

export type AddFeatureFlagOrganizationDto = {
  /**
   * Which list to add the organization to.
   */
  list: 'allow' | 'deny';
  /**
   * The organization id to add to the list.
   */
  organizationId: string;
};

export type SetFeatureFlagActiveDto = {
  /**
   * Whether the feature flag should be globally active.
   */
  isActive: boolean;
};

export type FeatureFlagWithOrganizationsDto = {
  /**
   * The feature flag id.
   */
  id: string;
  /**
   * The unique feature flag name/key.
   */
  name: string;
  /**
   * Human readable description of the flag.
   */
  description: string;
  /**
   * Whether the flag is globally active (true/false).
   */
  isActive: boolean;
  /**
   * When the flag becomes active.
   */
  startDate: string;
  /**
   * When the flag expires, if set.
   */
  endDate?: string | null;
  /**
   * Organizations explicitly allowed (allowlist). When non-empty the flag is active only for these orgs.
   */
  allowOrganizations: Array<FeatureFlagOrganizationDto>;
  /**
   * Organizations explicitly denied (denylist). The flag is inactive for these orgs.
   */
  denyOrganizations: Array<FeatureFlagOrganizationDto>;
};

export type FeatureFlagOrganizationDto = {
  /**
   * The organization id.
   */
  id: string;
  /**
   * The organization name.
   */
  name: string;
};

export type SynchronizeMessagingResponseDto = {
  results: Array<SynchronizeMessagingResultDto>;
  errorMessage?: string;
};

export const SynchronizeMessagingStatus = {
  ADD: 'add',
  REMOVE: 'remove',
  UPDATE: 'update',
  CREATE: 'create',
} as const;

export type SynchronizeMessagingStatus =
  (typeof SynchronizeMessagingStatus)[keyof typeof SynchronizeMessagingStatus];

export const SynchronizeMessagingType = {
  ORGANIZATION: 'organization',
  TEAM: 'team',
  USER: 'user',
} as const;

export type SynchronizeMessagingType =
  (typeof SynchronizeMessagingType)[keyof typeof SynchronizeMessagingType];

export type SynchronizeMessagingResultDto = {
  type: SynchronizeMessagingType;
  status: SynchronizeMessagingStatus;
  objectId: string;
  updatedFields?: Array<string>;
  childObjectId?: string;
};

export type MessagingTeam = {
  uid: string;
  foreignId: string;
  name: string;
  organizationForeignId: string;
  createdAt: string;
  users?: Array<MessagingMember>;
};

export type MessagingMember = {
  uid: string;
  foreignId: string;
  firstName: string;
  lastName: string;
  email: string;
  pictureUrl?: string;
  timezone: string;
  createdAt: string;
  updatedAt?: string;
  addedAt: string;
};

export type MessagingOrganization = {
  uid: string;
  name: string;
  foreignId: string;
  createdAt: string;
  updatedAt?: string;
  users?: Array<MessagingMember>;
};

export type InitiateMessageAttachmentUploadResponseDto = {
  uploadId: string;
  bucketKey: string;
  /**
   * Byte size every part except the last must use
   */
  partSize: number;
};

export type InitiateMessageAttachmentUploadDto = {
  fileName: string;
  fileSize: number;
  mimeType:
    | 'image/jpeg'
    | 'image/png'
    | 'image/gif'
    | 'application/pdf'
    | 'application/msword'
    | 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    | 'application/vnd.ms-excel'
    | 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    | 'application/vnd.ms-powerpoint'
    | 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
    | 'video/mp4'
    | 'video/quicktime';
};

export type UploadedAttachmentDto = {
  url: string;
  key: string;
  contentType: string;
  name: string;
  size: number;
};

export type UpdateConversationDto = {
  /**
   * Name of conversation.
   */
  name?: string;
  /**
   * If conversation is locked.
   */
  locked?: boolean;
};

export type ConversationsPaginatedDto = {
  /**
   * The total number of results possible, which is only provided in the response when `includeCount` is passed as `true` within a request.
   */
  totalResults?: number;
  page: number;
  pageSize: number;
  results: Array<Conversation>;
};

export type LatestMessage = {
  /**
   * Unique identifier of message.
   */
  uid?: string;
  /**
   * Body of message.
   */
  body?: string;
  createdAt?: string;
};

export type AttachmentMetadata = {
  key: string;
  name: string;
  size: number;
  lastModified: string;
};

export type Attachment = {
  messageId: string;
  uid: string;
  url: string;
  contentType: string;
  metadata?: AttachmentMetadata;
  createdAt: string;
};

export type MinimalMessage = {
  /**
   * Unique identifier of message.
   */
  uid: string;
  /**
   * Body of message.
   */
  body: string;
  /**
   * Sender of message.
   * Either a participant or the api team owner/admin.
   * To find the external researcher, use `conversation.metadata.externalResearcherId`.
   */
  sender: MessagingUser;
  attachments: Array<Attachment>;
  updatedAt?: string;
  createdAt: string;
};

export type MessagingUser = {
  uid: string;
  foreignId: string;
  firstName: string;
  lastName: string;
  email: string;
  pictureUrl?: string;
  timezone: string;
  createdAt: string;
  updatedAt?: string;
};

export type ConversationParticipant = {
  uid: string;
  foreignId: string;
  firstName: string;
  lastName: string;
  email: string;
  pictureUrl?: string;
  timezone: string;
  createdAt: string;
  updatedAt?: string;
  /**
   * Whether the participant has read the conversation.
   */
  read: boolean;
  /**
   * Whether the participant has muted the conversation.
   */
  deleted: boolean;
};

export type ConversationMetadata = {
  /**
   * ProjectId of the project the coversation belongs to.
   */
  projectId?: string;
  /**
   * Whether the project the conversation belongs to has been closed. Enriched
   * by core-api (not stored in the messaging service); absent when the
   * conversation has no project or the project no longer exists.
   */
  projectClosed?: boolean;
  researcherUserId?: string;
  /**
   * External researcher identity for this conversation. Participant-facing
   * responses reduce this to a first name only (PX-214).
   */
  researcherName?: string;
  /**
   * ScreenerResponseId for the project, if exists.
   */
  surveyResponseId?: string;
  /**
   * ExternalResearcherId for the project, if exists.
   */
  externalResearcherId?: string;
  /**
   * ExternalTeamId for the project, if exists.
   */
  externalTeamId?: string;
  /**
   * ExternalCompanyId for the project, if exists.
   */
  externalCompanyId?: string;
};

export type Conversation = {
  /**
   * Unique identifier of conversation.
   */
  uid: string;
  /**
   * Name of conversation.
   */
  name: string;
  /**
   * If conversation is marked as deleted.
   */
  deleted: boolean;
  /**
   * If conversation is locked.
   */
  locked: boolean;
  /**
   * Metadata of conversation.
   */
  metadata?: ConversationMetadata;
  /**
   * User who created the conversation.
   * This will be the API owner/admin account for the api team.
   * First name and last name will belong to api team user, not external researcher name.
   * External researcherId will be stored in metadata, which can be used to lookup your external researcher's info internally.
   */
  user: MessagingUser;
  /**
   * All participants of conversation including the researcher.
   * The researcher will be the API owner/admin account for the api team.
   */
  participants: Array<ConversationParticipant>;
  /**
   * All messages in conversation.
   */
  messages: Array<MinimalMessage>;
  /**
   * Latest message in conversation.
   */
  latestMessage: LatestMessage;
  /**
   * Count of messages in conversation.
   */
  messagesCount: number;
  createdAt: string;
  updatedAt?: string;
};

export type CreateConversationDto = {
  /**
   * ProjectId of the project the coversation belongs to.
   */
  projectId: string;
  /**
   * UserId(s) of participants of conversation (not including researcher).
   * Minimum 1 participant required.
   */
  participantUserIds: Array<string>;
};

export type CreateMessageDto = {
  /**
   * Uid of the conversation the message belongs to.
   */
  conversationUid: string;
  /**
   * Body of message.
   */
  body?: string;
  attachments?: Array<CreateAttachmentDto>;
};

export type CreateAttachmentDto = {
  url: string;
  contentType: string;
  metadata?: AttachmentMetadata;
};

export type MessageInboxDto = {
  /**
   * All conversations in the inbox have been read.
   */
  read: boolean;
  /**
   * Count of conversations user is in.
   */
  count: number;
  /**
   * List of unread conversations uids.
   */
  unreadIds: Array<string>;
  /**
   * Count of unread conversations in the inbox.
   */
  unreadCount: number;
};

export type UserInboxDto = {
  /**
   * All conversations in the inbox have been read.
   */
  read: boolean;
  /**
   * Count of conversations the user is in.
   */
  count: number;
  /**
   * Count of unread conversations in the inbox.
   */
  unreadCount: number;
};

export type MessagesPaginatedDto = {
  /**
   * The total number of results possible, which is only provided in the response when `includeCount` is passed as `true` within a request.
   */
  totalResults?: number;
  page: number;
  pageSize: number;
  results: Array<Message>;
};

export type MinimalConversation = {
  /**
   * Unique identifier of conversation.
   */
  uid: string;
  /**
   * Name of conversation.
   */
  name: string;
  /**
   * If conversation is marked as deleted.
   */
  deleted: boolean;
  /**
   * If conversation is locked.
   */
  locked: boolean;
  /**
   * Metadata of conversation.
   */
  metadata?: ConversationMetadata;
  /**
   * All participants of conversation including the researcher.
   * The researcher will be the API owner/admin account for the api team.
   */
  participants: Array<ConversationParticipant>;
  createdAt: string;
  updatedAt?: string;
};

export type Message = {
  /**
   * Unique identifier of message.
   */
  uid: string;
  /**
   * Body of message.
   */
  body: string;
  /**
   * Conversation message belongs to.
   */
  conversation: MinimalConversation;
  /**
   * Sender of message.
   * Either a participant or the api team owner/admin.
   * To find the external researcher, use `conversation.metadata.externalResearcherId`.
   */
  sender: MessagingUser;
  attachments: Array<Attachment>;
  updatedAt?: string;
  createdAt: string;
};

export type CreateConversationMessageDto = {
  /**
   * Body of message.
   */
  body?: string;
  attachments?: Array<CreateAttachmentDto>;
};

export type GetProjectUnreadCountsDto = {
  /**
   * Array of project IDs (hex strings) to get unread counts for
   */
  projectIds: Array<string>;
};

export type InboxSummaryDto = {
  /**
   * Map of projectId to unread conversation count
   */
  projectUnreadCounts: {
    [key: string]: number;
  };
};

export type SystemParam = {
  id?: ObjectId;
  name: string;
  description: string;
  value: {
    [key: string]: unknown;
  };
  startDate: string;
  endDate: string;
};

export type SystemParamDto = {
  name: string;
  value: {
    [key: string]: unknown;
  };
};

export type FindUsersByIdsDto = {
  userIds: Array<string>;
};

export type UsersPaginatedDto = {
  total_results?: number;
  page: number;
  results: Array<User>;
};

export type GetUsersPaginatedDto = {
  page?: number;
  page_size?: number;
  include_count?: boolean;
  filters: UserSearchFilters;
};

export type UserSearchFilters = {
  id?: string;
  ids?: Array<string>;
  firstName?: string;
  lastName?: string;
  phone?: string;
  email?: string;
  isActive?: boolean;
  roles?: Roles;
  pendingReview?: boolean;
  inviterProjectId?: string;
  organizationId?: string;
  trustLevelStatus?: string;
  modifiedWithinYears?: string;
  createdAt?: string;
  active?: boolean;
  sort?: {
    [key: string]: unknown;
  };
};

export const TermsAudience = {
  PARTICIPANT: 'participant',
  RESEARCHER: 'researcher',
} as const;

export type TermsAudience = (typeof TermsAudience)[keyof typeof TermsAudience];

export type AddProviderDto = {
  loginProviderId: string;
  providerKey: {
    [key: string]: unknown;
  };
  accessToken: string;
  refreshToken?: string;
  loginProviderEmail?: string;
  tid?: string;
  expiryDate?: number;
  orgUri?: string;
};

export type CalendlyEventTypesResponseDto = {
  /**
   * List of Calendly event types
   */
  eventTypes: Array<CalendlyEventTypeDto>;
};

export type CalendlyEventTypeProfileDto = {
  /**
   * Profile type
   */
  type: string;
  /**
   * Profile name
   */
  name: string;
  /**
   * Profile owner URI
   */
  owner: string;
};

export type CalendlyEventTypeDto = {
  /**
   * Canonical reference (unique identifier) for the event type
   */
  uri: string;
  /**
   * Human-readable name of the event type
   */
  name: string;
  /**
   * Whether the event type is active
   */
  active: boolean;
  /**
   * URL slug for the event type
   */
  slug: string;
  /**
   * URL to schedule this event type
   */
  schedulingUrl: string;
  /**
   * Duration of the event in minutes
   */
  duration: number;
  /**
   * Event kind (solo or group)
   */
  kind: string;
  /**
   * Pooling type for team events (round robin or collective)
   */
  poolingType?: string | null;
  /**
   * Event type category
   */
  type: string;
  /**
   * Color for the event type
   */
  color: string;
  /**
   * Timestamp when the event type was created
   */
  createdAt: string;
  /**
   * Timestamp when the event type was last updated
   */
  updatedAt: string;
  /**
   * Internal note for the event type
   */
  internalNote?: string;
  /**
   * Plain text description of the event type
   */
  descriptionPlain?: string;
  /**
   * HTML description of the event type
   */
  descriptionHtml?: string;
  /**
   * Profile information for the event type owner
   */
  profile?: CalendlyEventTypeProfileDto;
  /**
   * Whether the event type is secret (not publicly listed)
   */
  secret: boolean;
};

export type UploadedFileResultsDto = {
  filename?: string;
  url?: string;
};

export type UpdateUserDto = {
  isEmailVerified?: boolean;
  email?: string;
  firstName?: string;
  lastName?: string;
  trustLevel?: TrustLevel;
  pendingReview?: boolean;
};

export type CreateResearcherUserDto = {
  email: string;
  emailDomain?: string;
  firstName: string;
  lastName: string;
  referrerUserId?: string | null;
  phone?: string | null;
  gender?: Gender;
  roles: Array<ResearcherRoles>;
  inviter?: UserInviter;
  /**
   * Researcher Only
   */
  signupIntent?: string | null;
  source?: string | null;
  /**
   * Researcher Only
   */
  pendingReview?: boolean;
  /**
   * Researcher Only
   */
  approvedBy?: string | null;
  /**
   * Researcher Only
   */
  trustLevel?: number;
  isEmailVerified: boolean;
  isActive?: boolean;
  timezone?: string;
  picture?: string | null;
  password?: string;
  referrerProjectId?: string;
  initialUtm?: InitialUtm;
};

export const ResearcherRoles = { RESEARCHER: 'researcher' } as const;

export type ResearcherRoles =
  (typeof ResearcherRoles)[keyof typeof ResearcherRoles];

export type CreateRespondentUserDto = {
  email: string;
  emailDomain?: string;
  firstName: string;
  lastName: string;
  referrerUserId?: string | null;
  roles: Array<RespondentRoles>;
  inviter?: UserInviter;
  source?: string | null;
  isEmailVerified: boolean;
  isActive?: boolean;
  timezone?: string;
  picture?: string | null;
  gender?: Gender;
  /**
   * Respondent Only
   */
  isPrivate?: boolean;
  phone?: string;
  password: string;
  referrerProjectId?: string;
  initialUtm?: InitialUtm;
};

export const RespondentRoles = { RESPONDENT: 'respondent' } as const;

export type RespondentRoles =
  (typeof RespondentRoles)[keyof typeof RespondentRoles];

export type ResearcherUserDto = {
  id: string;
  createdAt: string;
  email: string;
  firstName: string;
  lastName: string;
  isActive: boolean;
  referralCode?: string | null;
  referrerUserId?: string | null;
  timezone: string;
  phone?: string | null;
  picture?: string | null;
  roles: Array<Roles>;
  organizations: Array<UserOrganization>;
  teams: Array<UserTeam>;
  notificationSettings?: NotificationSettings;
  inviter?: UserInviter;
  messaging?: UserMessaging | null;
  scheduler?: UserScheduler | null;
  /**
   * Respondent Only
   */
  isPrivate: boolean;
  /**
   * Researcher Only
   */
  signupIntent?: string | null;
  source?: string | null;
  /**
   * Researcher Only
   */
  pendingReview?: boolean;
  /**
   * Researcher Only
   */
  approvedBy?: string | null;
  /**
   * Researcher Only
   */
  trustLevel?: number;
  company?: string | null;
  companySize?: string | null;
  jobTitle?: string | null;
  industry?: string | null;
  rms?: UserRms;
  /**
   * SSO identifier from the IdP, if SSO is enabled
   */
  ssoId?: string;
  isEmailVerified: boolean;
  lastLoginAt: string;
  /**
   * Researcher Only
   */
  ssoEnabled?: boolean;
  uri?: string;
  updatedAt?: string;
  providers: Array<{
    [key: string]: unknown;
  }>;
  experiments?: {
    [key: string]: unknown;
  } | null;
  readNotifications?: {
    [key: string]: unknown;
  };
};

export type RespondentUserDto = {
  id: string;
  createdAt: string;
  email: string;
  emailDomain?: string;
  firstName: string;
  lastName: string;
  isActive: boolean;
  referralCode?: string | null;
  referrerUserId?: string | null;
  referrer?: Referrer;
  timezone: string;
  phone?: string | null;
  picture?: string | null;
  roles: Array<Roles>;
  inviter?: UserInviter;
  messaging?: UserMessaging | null;
  customerScheduler?: UserCustomerScheduler | null;
  /**
   * Respondent Only
   */
  isPrivate: boolean;
  company?: string | null;
  companySize?: string | null;
  jobTitle?: string | null;
  industry?: string | null;
  isEmailVerified: boolean;
  lastLoginAt: string;
  uri?: string;
  updatedAt?: string;
  providers: Array<{
    [key: string]: unknown;
  }>;
  experiments?: {
    [key: string]: unknown;
  } | null;
};

export type ClientAuthAuthenticatedResponseDto = {
  clientId: string;
  organizationId: string;
  teamId: string;
  userId: string;
  organizationName: string;
};

export type ClientAuthUpsertedDto = {
  clientId: string;
  organizationId: string;
  teamId: string;
  userId: string;
  clientSecret: string;
};

export type UpsertTokenDto = {
  teamId: string;
  userId: string;
  /**
   * Optional, if you want to update by clientId
   */
  clientId?: string;
};

export type ClientAuthAuthenticateDto = {
  clientId: string;
  clientSecret: string;
};

export type EnsureApiPartnerAccountResponseDto = {
  dryRun: boolean;
  userId: string | null;
  organizationId: string | null;
  teamId: string | null;
  changes: Array<ProvisionChangeDto>;
};

export type ProvisionChangeDto = {
  target: string;
  description: string;
  type: string;
};

export type EnsureApiPartnerAccountDto = {
  email: string;
  firstName?: string;
  lastName?: string;
  organizationName?: string;
  dryRun?: boolean;
};

export type DisableApiPartnerCredentialResponseDto = {
  clientId: string;
  organizationId: string;
  teamId: string;
  /**
   * When the credential was soft-deleted. On a repeat call this is the
   * original disable timestamp, not the time of the repeat call.
   */
  disabledAt: string;
};

export type ProvisionApiPartnerResponseDto = {
  dryRun: boolean;
  credentials?: ProvisionCredentialsDto | null;
  changes: Array<ProvisionChangeDto>;
};

export type ProvisionCredentialsDto = {
  clientId: string;
  clientSecret: string;
};

export type ProvisionApiPartnerDto = {
  userId: string;
  organizationId: string;
  teamId: string;
  credentialAction: CredentialAction;
  clientIdToRotate?: string;
  addBalances?: AddBalancesDto;
  addTestCreditCard?: boolean;
  enableIncreasedTargetLimit?: boolean;
  dryRun?: boolean;
};

export type AddBalancesDto = {
  credit?: number;
  incentive?: number;
};

export const CredentialAction = {
  CREATE: 'create',
  ROTATE: 'rotate',
  SKIP: 'skip',
} as const;

export type CredentialAction =
  (typeof CredentialAction)[keyof typeof CredentialAction];

export type ApiPartnerLookupResponseDto = {
  user: ApiPartnerLookupUserDto;
  organizations: Array<ApiPartnerLookupOrganizationDto>;
};

export type ApiPartnerLookupExistingCredentialDto = {
  clientId: string;
  teamId: string;
  createdAt: string;
  isActive: boolean;
};

export type ApiPartnerLookupTeamDto = {
  id: string;
  name: string;
};

export type ApiPartnerLookupOrganizationDto = {
  id: string;
  name: string;
  trustLevel: number;
  userRoles: Array<string>;
  teams: Array<ApiPartnerLookupTeamDto>;
  existingCredentials: Array<ApiPartnerLookupExistingCredentialDto>;
};

export type ApiPartnerLookupUserDto = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  isEmailVerified: boolean;
  pendingReview: boolean;
  trustLevel: number;
};

export type ApiPartnerCredentialsResponseDto = {
  credentials: Array<ApiPartnerCredentialDto>;
};

export type ApiPartnerCredentialDto = {
  clientId: string;
  organizationId: string;
  organizationName: string;
  teamId: string;
  teamName: string;
  userId: string;
  userEmail: string;
  createdAt: string;
  isActive: boolean;
};

export type AnalyticsSnapshotsOrgResponseDto = {
  /**
   * The cohort the buckets are summed over.
   */
  organizationIds: Array<string>;
  granularity: AnalyticsSnapshotGranularity;
  from: string;
  to: string;
  buckets: Array<AnalyticsSnapshotOrgBucketDto>;
};

export type AnalyticsSnapshotSegmentTotalsDto = {
  paidParticipants: number;
  paidPayouts: number;
  paidSeatValue: number;
  invitesSent: number;
  invitedParticipants: number;
  visibleApplications: number;
  /**
   * Visible applications that are qualified. Zero for days computed before the field existed, unless backfilled - same caveat for the source subsets below.
   */
  visibleQualifiedApplications: number;
  /**
   * Visible applications where the participant was matched (`v4Score` > 0).
   */
  visibleMatchedApplications: number;
  /**
   * Visible applications with `initialUtm.source` "boost".
   */
  visibleBoostApplications: number;
  /**
   * Visible applications with `initialUtm.source` "screenermatched" (Match.it).
   */
  visibleScreenerMatchedApplications: number;
  /**
   * Qualified visible applications where the participant was matched (`v4Score` > 0).
   */
  visibleQualifiedMatchedApplications: number;
  /**
   * Qualified visible applications with `initialUtm.source` "boost".
   */
  visibleQualifiedBoostApplications: number;
  /**
   * Qualified visible applications with `initialUtm.source` "screenermatched" (Match.it).
   */
  visibleQualifiedScreenerMatchedApplications: number;
  requestedSeats: number;
  filledSeats: number;
  qualifiedVisibleApplications: number;
  projectCount: number;
  /**
   * `filledSeats / requestedSeats` over the bucket; 0 when no seats.
   */
  fillRate: number;
  /**
   * Applications in status PAID across the bucket's fill-rate cohort. Zero for days computed before the paid-rate fields existed.
   */
  paidApplications: number;
  /**
   * Sum of `min(paidApplications, requestedSeats)` per project.
   */
  paidSeats: number;
  /**
   * `paidSeats / requestedSeats` over the bucket; 0 when no seats.
   */
  paidRate: number;
};

export type AnalyticsSnapshotOrgBucketDto = {
  periodStart: string;
  /**
   * Daily platform snapshots contributing to this bucket - the authoritative
   * "the cron ran" count, taken from the platform snapshot collection rather
   * than the per-org one. Short means a gap in the snapshot history or an
   * edge of the range, not zero organization volume.
   */
  days: number;
  b2b: AnalyticsSnapshotSegmentTotalsDto;
  b2c: AnalyticsSnapshotSegmentTotalsDto;
  total: AnalyticsSnapshotSegmentTotalsDto;
};

export const AnalyticsSnapshotGranularity = {
  DAY: 'day',
  WEEK: 'week',
  MONTH: 'month',
} as const;

export type AnalyticsSnapshotGranularity =
  (typeof AnalyticsSnapshotGranularity)[keyof typeof AnalyticsSnapshotGranularity];

export type AnalyticsSnapshotsResponseDto = {
  granularity: AnalyticsSnapshotGranularity;
  from: string;
  to: string;
  buckets: Array<AnalyticsSnapshotBucketDto>;
};

export type AnalyticsSnapshotIdentityVerificationTotalsDto = {
  /**
   * Plaid IDV sessions created, all statuses - each one billed by Plaid.
   */
  sessions: number;
  /**
   * Sessions on the document ("Government ID") template.
   */
  documentSessions: number;
  /**
   * Default-template (database check) sessions for a Group 1 country (US and territories).
   */
  defaultTier1Sessions: number;
  /**
   * Default-template (database check) sessions for a Group 2 country (the agreement's named list).
   */
  defaultTier2Sessions: number;
  /**
   * Default-template (database check) sessions for any other country (Group 3).
   */
  defaultTier3Sessions: number;
  /**
   * Default-template sessions with no country captured; billed at tier 1.
   */
  defaultUnknownCountrySessions: number;
  /**
   * Sessions that reached `success`.
   */
  successfulSessions: number;
  /**
   * Distinct users verified by a successful Plaid session per day, summed over the bucket.
   */
  plaidVerifiedUsers: number;
  /**
   * Plaid spend, USD (`costUsd - personaCostUsd`, so days stored before the split still read as Plaid).
   */
  plaidCostUsd: number;
  /**
   * `plaidCostUsd / plaidVerifiedUsers` over the bucket; 0 when nobody verified via Plaid.
   */
  plaidCostPerVerifiedUserUsd: number;
  /**
   * Sessions priced by their step outcomes; the rest of `sessions` were priced as whole sessions.
   */
  plaidStepPricedSessions: number;
  /**
   * Step-priced sessions the Anti-Fraud Engine ran on.
   */
  plaidAntiFraudChecks: number;
  /**
   * Step-priced sessions with a data source (database) check run.
   */
  plaidDataSourceChecks: number;
  /**
   * Step-priced sessions with a document check run.
   */
  plaidDocumentChecks: number;
  /**
   * Step-priced sessions with a selfie check run.
   */
  plaidSelfieChecks: number;
  /**
   * Plaid's flat platform support fee over the bucket, USD; part of `plaidCostUsd`.
   */
  plaidPlatformFeeUsd: number;
  /**
   * Users whose ID check LinkedIn satisfied, at no cost.
   */
  linkedinVerifiedUsers: number;
  /**
   * Persona inquiries created. Volume, not a charge - Persona bills per verification run.
   */
  personaInquiries: number;
  /**
   * Persona government-id verification runs, every attempt.
   */
  personaGovernmentIdChecks: number;
  /**
   * Persona selfie verification runs, every attempt.
   */
  personaSelfieChecks: number;
  /**
   * Persona database (records) checks for a country with a recorded rate.
   */
  personaDatabaseChecks: number;
  /**
   * Persona database checks for a country without a recorded rate; priced at the dearest known rate.
   */
  personaDatabaseOtherCountryChecks: number;
  /**
   * Distinct users verified by an approved Persona inquiry per day, summed over the bucket.
   */
  personaVerifiedUsers: number;
  /**
   * Persona spend, USD.
   */
  personaCostUsd: number;
  /**
   * `personaCostUsd / personaVerifiedUsers` over the bucket; 0 when nobody verified via Persona.
   */
  personaCostPerVerifiedUserUsd: number;
  /**
   * `plaidVerifiedUsers + linkedinVerifiedUsers + personaVerifiedUsers` - completed IDV journeys.
   */
  verifiedUsers: number;
  /**
   * IDV spend across providers, USD.
   */
  costUsd: number;
  /**
   * `costUsd / verifiedUsers` over the bucket; 0 when nobody verified.
   */
  costPerVerifiedUserUsd: number;
};

export type AnalyticsSnapshotBucketDto = {
  periodStart: string;
  /**
   * Daily snapshots contributing to this bucket. Short means a gap or an edge.
   */
  days: number;
  b2b: AnalyticsSnapshotSegmentTotalsDto;
  b2c: AnalyticsSnapshotSegmentTotalsDto;
  /**
   * Projects created through the public API. Snapshot days computed before
   * the origin split shipped carry no origin fields, so those periods sum to
   * zero until backfilled.
   */
  apiPartner: AnalyticsSnapshotSegmentTotalsDto;
  /**
   * Projects created on the platform itself. Same backfill caveat as `apiPartner`.
   */
  onPlatform: AnalyticsSnapshotSegmentTotalsDto;
  /**
   * B2B slice of API-created projects. Zero for days computed before the cross cells existed.
   */
  apiPartnerB2b: AnalyticsSnapshotSegmentTotalsDto;
  /**
   * B2C slice of API-created projects. Same backfill caveat as `apiPartnerB2b`.
   */
  apiPartnerB2c: AnalyticsSnapshotSegmentTotalsDto;
  /**
   * B2B slice of platform-created projects. Same backfill caveat as `apiPartnerB2b`.
   */
  onPlatformB2b: AnalyticsSnapshotSegmentTotalsDto;
  /**
   * B2C slice of platform-created projects. Same backfill caveat as `apiPartnerB2b`.
   */
  onPlatformB2c: AnalyticsSnapshotSegmentTotalsDto;
  total: AnalyticsSnapshotSegmentTotalsDto;
  newSignupsParticipants: number;
  newSignupsResearchers: number;
  deletionRequestsParticipants: number;
  deletionRequestsResearchers: number;
  /**
   * Plaid IDV sessions, spend and verified users. Participant-only, so no
   * audience split. Zero for days computed before the IDV fields existed,
   * unless backfilled.
   */
  identityVerification: AnalyticsSnapshotIdentityVerificationTotalsDto;
};

export type ActionAuditLog = {
  id?: ObjectId;
  createdBy?: string;
  createdAt: string;
  context: {
    [key: string]: unknown;
  };
  actions: Array<AuditAction>;
  _id?: ObjectId;
};

export type AuditAction = {
  collection: string;
  documentId: ObjectId;
  field: string;
  newValue: {
    [key: string]: unknown;
  };
  origValue: {
    [key: string]: unknown;
  };
};

export type CreateActionAuditLogDto = {
  createdBy?: string;
  context: {
    [key: string]: unknown;
  };
  actions: Array<AuditAction>;
};

export type ObjectIdWritable = {
  [key: string]: unknown;
};

export type ProfileLegacyWritable = {
  [key: string]: unknown;
};

export type SurveyResponseInviteWritable = {
  [key: string]: unknown;
};

export type SurveyResponsePayWritable = {
  [key: string]: unknown;
};

export type SurveyResponseReferrerWritable = {
  [key: string]: unknown;
};

export type ProjectStatusHistoryLegacyWritable = {
  [key: string]: unknown;
};

export type SurveyResponseInviterWritable = {
  [key: string]: unknown;
};

export type SurveyResponseInvitationWritable = {
  [key: string]: unknown;
};

export type CreateProjectDtoWritable = {
  /**
   * Character Limit: min 3 and max 255
   */
  name: string;
  kindOfResearch?: KindOfResearch;
  /**
   * Character Limit: max 255
   */
  internalName: string;
  organizationId: string;
  teamId: string;
  talkToAudience: ParticipantAudienceType;
  /**
   * Character Limit: max 800
   */
  description?: string;
  researchConductType?: ResearchConductType;
  /**
   * Limit of 1000 is a one-time limit to test projects, and will not be
   * set for standard projects.
   *
   *
   * Must be changed directly in the DB by engineering, before calling `/publish`
   * in Postman on the DRAFT project.
   *
   * Number Limit: min 1 and max 1000. 0 is allowed when the project has
   * `recruitmentTargets` (invite-only); see `IsMarketplaceTarget`.
   */
  numberOfParticipants: number;
  duration: number;
  companyName?: string;
  hideCompanyName?: boolean;
  copied?: ProjectCopied;
  ageGroups?: Array<AgeGroup>;
  minAge?: number;
  maxAge?: number;
  education?: Array<EducationLevel>;
  ethnicity?: Array<Ethnicities>;
  seniority?: Array<SeniorityLevel>;
  exposeParticipantEmails?: boolean;
  ndaFileUrl?: string | null;
  ndaLink?: string | null;
  ndaType?: NdaType;
  locationRespondentInPerson?: ProjectLocationInPerson;
  companySize?: Array<CompanySize>;
  businessOwner?: boolean;
  visibility?: ProjectVisibility;
  cities?: Array<ProjectCities>;
  country?: ProjectCountryCode;
  countries?: Array<ProjectCountryCode>;
  automations?: ProjectAutomations;
  metrics?: ProjectMetrics;
  income?: Income;
  jobFunctions?: Array<JobFunction>;
  genders?: Gender;
  filtering?: ProjectFiltering;
  remoteCommunicationDetails?: string | null;
  /**
   * External-screener flow: the partner-hosted screener URL. Must be an
   * absolute `https://` URL — participants are redirected to it with their
   * correlation ids in the query string, and `buildExternalScreenerLink`
   * cannot parse a bare hostname (`partner.example.com/...`). Validated here
   * on the write DTOs (create, draft update, published update via PickType)
   * rather than on the entity, so re-validating stored documents on
   * unrelated updates is unaffected.
   */
  externalScreenerLink?: string | null;
  externalQuestions?: Array<ProjectExternalQuestion>;
  specialInstructions?: string;
  householdIncome?: Array<HouseholdIncome>;
  task?: Task;
  externalResearcher?: ExternalResearcher;
  /**
   * Number Limit: min 5 and max 1000
   */
  incentive?: number;
  /**
   * Flat advanced-targeting attribute map — the source of truth for this
   * project's advanced targeting. Read it via `getProjectAdvancedTargeting()`.
   */
  advancedTargeting?: AdvancedTargeting;
  keyQualifiers?: Array<KeyQualifier>;
  projectSuccessEditPermission?: boolean;
  professionalJobTitles?: Array<string>;
  desiredSkills?: Array<string>;
  topics?: Array<string>;
  professionalIndustries?: Array<string>;
  /**
   * @deprecated Use professionalIndustries instead.
   *
   * @deprecated
   */
  professionalIndustry?: Array<string>;
  /**
   * Quotas V2 configuration created with the project, in the same transaction (§4).
   */
  quotas?: UpsertQuotaDtoWritable;
  /**
   * internal field to track the project creation flow version
   * in v2, we refactored the project creation flow and added general improvements ie moving topics(key qualifiers) to step 2
   * if not set, default to v1
   */
  flowVersion?: string;
};

export type QuotaV2JobTitleConditionDtoWritable = {
  source: 'attribute';
  attribute: 'jobTitle';
  op: 'in' | 'notIn';
  /**
   * Job title ids as typed by the researcher.
   */
  values: Array<string>;
};

export type QuotaV2RuleConditionNodeDtoWritable = {
  kind: 'condition';
  condition:
    | QuotaV2AttributeSetConditionDto
    | QuotaV2JobTitleConditionDtoWritable
    | QuotaV2NumericRangeConditionDto
    | QuotaV2NumericComparisonConditionDto
    | QuotaV2ScreenerSetConditionDto
    | QuotaV2ScreenerCountConditionDto
    | QuotaV2ExternalScreenerSetConditionDto
    | QuotaV2ExternalScreenerCountConditionDto;
};

export type QuotaV2RuleGroupNodeDtoWritable = {
  kind: 'group';
  connector: QuotaRuleConnector;
  children: Array<
    QuotaV2RuleGroupNodeDtoWritable | QuotaV2RuleConditionNodeDtoWritable
  >;
};

export type QuotaV2SegmentInputDtoWritable = {
  /**
   * Stable id; minted by the server when omitted. `no-match` is reserved.
   */
  id?: string;
  name: string;
  rule: QuotaV2RuleGroupNodeDtoWritable | QuotaV2RuleConditionNodeDtoWritable;
  target: QuotaV2TargetDto;
};

export type QuotaV2GroupInputDtoWritable = {
  /**
   * Stable id; minted by the server when omitted.
   */
  id?: string;
  name: string;
  /**
   * Ordered: the first matching segment wins (FR-5).
   */
  segments: Array<QuotaV2SegmentInputDtoWritable>;
  /**
   * The implicit bucket's target. Tracked-only when omitted.
   */
  noMatch?: QuotaV2NoMatchInputDto;
};

export type UpsertQuotaDtoWritable = {
  version: 'v2';
  groups: Array<QuotaV2GroupInputDtoWritable>;
};

export type SchemaObjectIdWritable = {
  [key: string]: unknown;
};

export type QuotaV2UpsertResultDtoWritable = {
  /**
   * Absent when the project has no quota document (an empty configuration).
   */
  id?: string;
  projectId: string;
  version: 'v2';
  /**
   * Bumped by every structural upsert. 0 when the project has no quota document. Not an edit guard; see `configVersion`.
   */
  revision: number;
  /**
   * Opaque token for this exact configuration; every rules or targets save replaces it, progress changes do not. Send it back unchanged as `expectedConfigVersion` on PUT, PATCH `…/targets` and DELETE to refuse the write (409, `reason: QUOTA_CONFIG_VERSION_CONFLICT`) when someone else saved in between. `none` when the project has no quota.
   */
  configVersion: string;
  source?: QuotaSource;
  /**
   * Every targeted bucket is filled (FR-18 `quota.filled`).
   */
  filled: boolean;
  /**
   * Some group can admit nobody: the auto-invite halt signal.
   */
  closed: boolean;
  groups: Array<QuotaV2GroupDtoWritable>;
  createdAt?: string;
  updatedAt?: string;
  changes: QuotaV2ChangesDto;
  /**
   * Soft findings that did not block the save (§4.2).
   */
  warnings: Array<QuotaV2ValidationIssueDto>;
};

export type QuotaV2SegmentDtoWritable = {
  target: QuotaV2TargetDto;
  progress: QuotaV2ProgressDto;
  /**
   * Target reached: `min`, `max` or `exact` with the gate count at or above the value. Never for `track`.
   */
  filled: boolean;
  /**
   * Can admit nobody: `max` or `exact` at or above the value. Never for `min` or `track`.
   */
  closed: boolean;
  /**
   * Gate count above a `max` or `exact` value (a re-bucket or an acknowledged refusal put it there).
   */
  overTarget: boolean;
  /**
   * FR-9 lookahead: a landing in this bucket would currently be refused to
   * keep remaining `min`/`exact` slots fillable elsewhere. Computed on read
   * from `minProtection`; the UI never derives it.
   */
  minProtected: boolean;
  id: string;
  name: string;
  rule: QuotaV2RuleGroupNodeDtoWritable | QuotaV2RuleConditionNodeDtoWritable;
};

export type QuotaV2GroupDtoWritable = {
  id: string;
  name: string;
  segments: Array<QuotaV2SegmentDtoWritable>;
  noMatch: QuotaV2BucketDto;
  /**
   * Every targeted bucket in the group is filled. False when none is targeted.
   */
  filled: boolean;
  /**
   * Every bucket in the group, No match included, is closed.
   */
  closed: boolean;
  /**
   * Some bucket in the group is `minProtected` (FR-9).
   */
  minProtected: boolean;
};

export type QuotaV2DtoWritable = {
  /**
   * Absent when the project has no quota document (an empty configuration).
   */
  id?: string;
  projectId: string;
  version: 'v2';
  /**
   * Bumped by every structural upsert. 0 when the project has no quota document. Not an edit guard; see `configVersion`.
   */
  revision: number;
  /**
   * Opaque token for this exact configuration; every rules or targets save replaces it, progress changes do not. Send it back unchanged as `expectedConfigVersion` on PUT, PATCH `…/targets` and DELETE to refuse the write (409, `reason: QUOTA_CONFIG_VERSION_CONFLICT`) when someone else saved in between. `none` when the project has no quota.
   */
  configVersion: string;
  source?: QuotaSource;
  /**
   * Every targeted bucket is filled (FR-18 `quota.filled`).
   */
  filled: boolean;
  /**
   * Some group can admit nobody: the auto-invite halt signal.
   */
  closed: boolean;
  groups: Array<QuotaV2GroupDtoWritable>;
  createdAt?: string;
  updatedAt?: string;
};

export type ActionAuditLogControllerFindAllData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v4/action-audit-log';
};

export type ActionAuditLogControllerFindAllResponses = {
  200: Array<ActionAuditLog>;
};

export type ActionAuditLogControllerFindAllResponse =
  ActionAuditLogControllerFindAllResponses[keyof ActionAuditLogControllerFindAllResponses];

export type ActionAuditLogControllerCreateData = {
  body: CreateActionAuditLogDto;
  path?: never;
  query?: never;
  url: '/v4/action-audit-log';
};

export type ActionAuditLogControllerCreateResponses = {
  201: ActionAuditLog;
};

export type ActionAuditLogControllerCreateResponse =
  ActionAuditLogControllerCreateResponses[keyof ActionAuditLogControllerCreateResponses];

export type AnalyticsSnapshotsControllerGetSnapshotsData = {
  body?: never;
  path?: never;
  query?: {
    from?: string;
    to?: string;
    granularity?: AnalyticsSnapshotGranularity;
  };
  url: '/v4/admin/analytics-snapshots';
};

export type AnalyticsSnapshotsControllerGetSnapshotsResponses = {
  200: AnalyticsSnapshotsResponseDto;
};

export type AnalyticsSnapshotsControllerGetSnapshotsResponse =
  AnalyticsSnapshotsControllerGetSnapshotsResponses[keyof AnalyticsSnapshotsControllerGetSnapshotsResponses];

export type AnalyticsSnapshotsControllerGetOrganizationSnapshotsData = {
  body?: never;
  path?: never;
  query: {
    from?: string;
    to?: string;
    granularity?: AnalyticsSnapshotGranularity;
    /**
     * Organizations summed into one cohort. A single id is a cohort of one.
     */
    organizationIds: Array<string>;
  };
  url: '/v4/admin/analytics-snapshots/organizations';
};

export type AnalyticsSnapshotsControllerGetOrganizationSnapshotsResponses = {
  200: AnalyticsSnapshotsOrgResponseDto;
};

export type AnalyticsSnapshotsControllerGetOrganizationSnapshotsResponse =
  AnalyticsSnapshotsControllerGetOrganizationSnapshotsResponses[keyof AnalyticsSnapshotsControllerGetOrganizationSnapshotsResponses];

export type ApiPartnerControllerListCredentialsData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * When true, include disabled (soft-deleted) credentials in the response.
     * Defaults to active-only.
     */
    includeDisabled?: boolean;
  };
  url: '/v4/admin/api-partner/credentials';
};

export type ApiPartnerControllerListCredentialsResponses = {
  200: ApiPartnerCredentialsResponseDto;
};

export type ApiPartnerControllerListCredentialsResponse =
  ApiPartnerControllerListCredentialsResponses[keyof ApiPartnerControllerListCredentialsResponses];

export type ApiPartnerControllerLookupData = {
  body?: never;
  path?: never;
  query?: {
    email?: string;
    userId?: string;
  };
  url: '/v4/admin/api-partner/lookup';
};

export type ApiPartnerControllerLookupResponses = {
  200: ApiPartnerLookupResponseDto;
};

export type ApiPartnerControllerLookupResponse =
  ApiPartnerControllerLookupResponses[keyof ApiPartnerControllerLookupResponses];

export type ApiPartnerControllerProvisionData = {
  body: ProvisionApiPartnerDto;
  path?: never;
  query?: never;
  url: '/v4/admin/api-partner/provision';
};

export type ApiPartnerControllerProvisionResponses = {
  200: ProvisionApiPartnerResponseDto;
};

export type ApiPartnerControllerProvisionResponse =
  ApiPartnerControllerProvisionResponses[keyof ApiPartnerControllerProvisionResponses];

export type ApiPartnerControllerDisableCredentialData = {
  body?: never;
  path: {
    clientId: string;
  };
  query?: never;
  url: '/v4/admin/api-partner/credentials/{clientId}';
};

export type ApiPartnerControllerDisableCredentialResponses = {
  200: DisableApiPartnerCredentialResponseDto;
};

export type ApiPartnerControllerDisableCredentialResponse =
  ApiPartnerControllerDisableCredentialResponses[keyof ApiPartnerControllerDisableCredentialResponses];

export type ApiPartnerControllerEnsureAccountData = {
  body: EnsureApiPartnerAccountDto;
  path?: never;
  query?: never;
  url: '/v4/admin/api-partner/ensure-account';
};

export type ApiPartnerControllerEnsureAccountResponses = {
  200: EnsureApiPartnerAccountResponseDto;
};

export type ApiPartnerControllerEnsureAccountResponse =
  ApiPartnerControllerEnsureAccountResponses[keyof ApiPartnerControllerEnsureAccountResponses];

export type ClientAuthControllerUpsertTokenData = {
  body: UpsertTokenDto;
  path: {
    organizationId: string;
  };
  query?: never;
  url: '/v4/organizations/{organizationId}/client-auth';
};

export type ClientAuthControllerUpsertTokenResponses = {
  200: ClientAuthUpsertedDto;
};

export type ClientAuthControllerUpsertTokenResponse =
  ClientAuthControllerUpsertTokenResponses[keyof ClientAuthControllerUpsertTokenResponses];

export type ClientAuthControllerDeleteData = {
  body?: never;
  path: {
    organizationId: string;
    clientId: string;
  };
  query?: never;
  url: '/v4/organizations/{organizationId}/client-auth/{clientId}';
};

export type ClientAuthControllerDeleteResponses = {
  200: unknown;
};

export type ClientAuthControllerAuthenticateData = {
  body: ClientAuthAuthenticateDto;
  path?: never;
  query?: never;
  url: '/v4/client-auth/authenticate';
};

export type ClientAuthControllerAuthenticateResponses = {
  200: ClientAuthAuthenticatedResponseDto;
};

export type ClientAuthControllerAuthenticateResponse =
  ClientAuthControllerAuthenticateResponses[keyof ClientAuthControllerAuthenticateResponses];

export type UsersControllerCreateData = {
  body: CreateResearcherUserDto | CreateRespondentUserDto;
  path?: never;
  query?: never;
  url: '/v4/users';
};

export type UsersControllerCreateResponses = {
  201: RespondentUserDto | ResearcherUserDto;
};

export type UsersControllerCreateResponse =
  UsersControllerCreateResponses[keyof UsersControllerCreateResponses];

export type UsersControllerFindOneData = {
  body?: never;
  path: {
    userId: string;
  };
  query?: never;
  url: '/v4/users/{userId}';
};

export type UsersControllerFindOneResponses = {
  200: User;
};

export type UsersControllerFindOneResponse =
  UsersControllerFindOneResponses[keyof UsersControllerFindOneResponses];

export type UsersControllerUpdateData = {
  body: UpdateUserDto;
  path: {
    userId: string;
  };
  query?: never;
  url: '/v4/users/{userId}';
};

export type UsersControllerUpdateResponses = {
  200: User;
};

export type UsersControllerUpdateResponse =
  UsersControllerUpdateResponses[keyof UsersControllerUpdateResponses];

export type UsersControllerGdprDeleteData = {
  body?: never;
  path: {
    userId: string;
  };
  query?: never;
  url: '/v4/users/gdpr/{userId}';
};

export type UsersControllerGdprDeleteResponses = {
  200: unknown;
};

export type UsersControllerFindByEmailData = {
  body?: never;
  path: {
    email: string;
  };
  query?: never;
  url: '/v4/users/email/{email}';
};

export type UsersControllerFindByEmailResponses = {
  200: User;
};

export type UsersControllerFindByEmailResponse =
  UsersControllerFindByEmailResponses[keyof UsersControllerFindByEmailResponses];

export type UsersControllerUploadFilesFormDataData = {
  body?: never;
  path: {
    userId: string;
  };
  query: {
    type:
      | 'nda'
      | 'picture'
      | 'attachment'
      | 'interview-media'
      | 'screener-response'
      | 'project-media';
  };
  url: '/v4/users/{userId}/files/form-data';
};

export type UsersControllerUploadFilesFormDataResponses = {
  200: UploadedFileResultsDto;
};

export type UsersControllerUploadFilesFormDataResponse =
  UsersControllerUploadFilesFormDataResponses[keyof UsersControllerUploadFilesFormDataResponses];

export type UsersControllerIsZoomTokenValidData = {
  body?: never;
  path: {
    userId: string;
  };
  query?: never;
  url: '/v4/users/{userId}/providers/zoom/valid-token';
};

export type UsersControllerIsZoomTokenValidResponses = {
  200: boolean;
};

export type UsersControllerIsZoomTokenValidResponse =
  UsersControllerIsZoomTokenValidResponses[keyof UsersControllerIsZoomTokenValidResponses];

export type UsersControllerIsCalendlyTokenValidData = {
  body?: never;
  path: {
    userId: string;
  };
  query?: never;
  url: '/v4/users/{userId}/providers/calendly/valid-token';
};

export type UsersControllerIsCalendlyTokenValidResponses = {
  200: boolean;
};

export type UsersControllerIsCalendlyTokenValidResponse =
  UsersControllerIsCalendlyTokenValidResponses[keyof UsersControllerIsCalendlyTokenValidResponses];

export type UsersControllerGetCalendlyEventTypesData = {
  body?: never;
  path: {
    userId: string;
  };
  query?: never;
  url: '/v4/users/{userId}/calendly/event-types';
};

export type UsersControllerGetCalendlyEventTypesResponses = {
  200: CalendlyEventTypesResponseDto;
};

export type UsersControllerGetCalendlyEventTypesResponse =
  UsersControllerGetCalendlyEventTypesResponses[keyof UsersControllerGetCalendlyEventTypesResponses];

export type UsersControllerAddProviderData = {
  body: AddProviderDto;
  path: {
    /**
     * User ID
     */
    userId: string;
  };
  query?: never;
  url: '/v4/users/{userId}/providers';
};

export type UsersControllerAddProviderResponses = {
  201: unknown;
};

export type UsersControllerDeleteProviderData = {
  body?: never;
  path: {
    /**
     * User ID
     */
    userId: string;
    /**
     * OAuth provider key
     */
    providerKey:
      | 'google'
      | 'google_rms'
      | 'outlook'
      | 'outlook_rms'
      | 'linkedin'
      | 'facebook'
      | 'github'
      | 'slack'
      | 'foursquare'
      | 'zoom'
      | 'calendly';
  };
  query?: never;
  url: '/v4/users/{userId}/providers/{providerKey}';
};

export type UsersControllerDeleteProviderResponses = {
  204: void;
};

export type UsersControllerDeleteProviderResponse =
  UsersControllerDeleteProviderResponses[keyof UsersControllerDeleteProviderResponses];

export type UsersControllerResendVerificationEmailData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v4/users/resend-verification-email';
};

export type UsersControllerResendVerificationEmailResponses = {
  204: void;
};

export type UsersControllerResendVerificationEmailResponse =
  UsersControllerResendVerificationEmailResponses[keyof UsersControllerResendVerificationEmailResponses];

export type UsersControllerAcceptTermsData = {
  body?: never;
  path: {
    audience: TermsAudience;
  };
  query?: never;
  url: '/v4/users/terms-acceptance/{audience}';
};

export type UsersControllerAcceptTermsResponses = {
  200: TermsAcceptanceRecord;
};

export type UsersControllerAcceptTermsResponse =
  UsersControllerAcceptTermsResponses[keyof UsersControllerAcceptTermsResponses];

export type UsersControllerSearchData = {
  body: GetUsersPaginatedDto;
  path?: never;
  query?: never;
  url: '/v4/users/list';
};

export type UsersControllerSearchResponses = {
  200: UsersPaginatedDto;
};

export type UsersControllerSearchResponse =
  UsersControllerSearchResponses[keyof UsersControllerSearchResponses];

export type UsersControllerFindManyByIdsData = {
  body: FindUsersByIdsDto;
  path?: never;
  query?: never;
  url: '/v4/users/by-ids';
};

export type UsersControllerFindManyByIdsResponses = {
  200: Array<User>;
};

export type UsersControllerFindManyByIdsResponse =
  UsersControllerFindManyByIdsResponses[keyof UsersControllerFindManyByIdsResponses];

export type UsersControllerDeleteGhostUsersData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v4/users/ghost';
};

export type UsersControllerDeleteGhostUsersErrors = {
  /**
   * If no Ghost Inspector users are found, it will return a HttpStatus.NOT_FOUND error.
   */
  404: unknown;
};

export type UsersControllerDeleteGhostUsersResponses = {
  200: number;
};

export type UsersControllerDeleteGhostUsersResponse =
  UsersControllerDeleteGhostUsersResponses[keyof UsersControllerDeleteGhostUsersResponses];

export type SystemParamsControllerFindAllData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v4/system-params';
};

export type SystemParamsControllerFindAllResponses = {
  200: Array<SystemParamDto>;
};

export type SystemParamsControllerFindAllResponse =
  SystemParamsControllerFindAllResponses[keyof SystemParamsControllerFindAllResponses];

export type SystemParamsControllerGetSystemParamByNameData = {
  body?: never;
  path: {
    /**
     * Find the system params by name.
     */
    name: string;
  };
  query?: never;
  url: '/v4/system-params/{name}';
};

export type SystemParamsControllerGetSystemParamByNameErrors = {
  /**
   * If no system param is found, it will return HttpStatus.NOT_FOUND error
   */
  404: unknown;
  /**
   * if more than one doc are found, throw 500 error
   */
  500: unknown;
};

export type SystemParamsControllerGetSystemParamByNameResponses = {
  200: SystemParam;
};

export type SystemParamsControllerGetSystemParamByNameResponse =
  SystemParamsControllerGetSystemParamByNameResponses[keyof SystemParamsControllerGetSystemParamByNameResponses];

export type MessagesControllerCreateMessageData = {
  body: CreateConversationMessageDto;
  path: {
    conversationUid: string;
  };
  query?: never;
  url: '/v4/messaging/conversations/{conversationUid}/messages';
};

export type MessagesControllerCreateMessageResponses = {
  201: Message;
};

export type MessagesControllerCreateMessageResponse =
  MessagesControllerCreateMessageResponses[keyof MessagesControllerCreateMessageResponses];

export type MessagesControllerFindAllData = {
  body?: never;
  path?: never;
  query?: {
    page?: number;
    pageSize?: number;
    includeCount?: boolean;
    /**
     * Filter messages by conversationUid.
     */
    conversationUid?: string;
    /**
     * Filter messages by externalResearcherId.
     */
    externalResearcherId?: string;
    /**
     * Filter messages by projectId.
     */
    projectId?: string;
    /**
     * Get all messages since timestamp.
     */
    since?: string;
    /**
     * Get all messages until timestamp.
     */
    until?: string;
    /**
     * Sort order by createdAt. Defaults to `asc` (oldest-first, thread order).
     * Use `desc` for newest-first pagination (append older pages on scroll-up).
     */
    direction?: 'asc' | 'desc';
  };
  url: '/v4/messaging/messages';
};

export type MessagesControllerFindAllResponses = {
  200: MessagesPaginatedDto;
};

export type MessagesControllerFindAllResponse =
  MessagesControllerFindAllResponses[keyof MessagesControllerFindAllResponses];

export type MessagesControllerCreateDeprecatedData = {
  body: CreateMessageDto;
  path?: never;
  query?: never;
  url: '/v4/messaging/messages';
};

export type MessagesControllerCreateDeprecatedResponses = {
  201: Message;
};

export type MessagesControllerCreateDeprecatedResponse =
  MessagesControllerCreateDeprecatedResponses[keyof MessagesControllerCreateDeprecatedResponses];

export type MessagesControllerGetUserInboxData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v4/messaging/inbox';
};

export type MessagesControllerGetUserInboxResponses = {
  200: UserInboxDto;
};

export type MessagesControllerGetUserInboxResponse =
  MessagesControllerGetUserInboxResponses[keyof MessagesControllerGetUserInboxResponses];

export type MessagesControllerGetProjectUnreadCountsData = {
  body: GetProjectUnreadCountsDto;
  path: {
    teamId: string;
  };
  query?: never;
  url: '/v4/messaging/teams/{teamId}/messages/unread-counts';
};

export type MessagesControllerGetProjectUnreadCountsResponses = {
  200: InboxSummaryDto;
};

export type MessagesControllerGetProjectUnreadCountsResponse =
  MessagesControllerGetProjectUnreadCountsResponses[keyof MessagesControllerGetProjectUnreadCountsResponses];

export type MessagesControllerGetInboxData = {
  body?: never;
  path: {
    teamId: string;
  };
  query?: {
    /**
     * Filter by external researcher id.
     */
    externalResearcherId?: string;
    /**
     * Filter by projectId.
     */
    projectId?: string;
  };
  url: '/v4/messaging/teams/{teamId}/messages/inbox';
};

export type MessagesControllerGetInboxResponses = {
  200: MessageInboxDto;
};

export type MessagesControllerGetInboxResponse =
  MessagesControllerGetInboxResponses[keyof MessagesControllerGetInboxResponses];

export type MessagesControllerDeleteData = {
  body?: never;
  path: {
    messageUid: string;
  };
  query?: never;
  url: '/v4/messaging/messages/{messageUid}';
};

export type MessagesControllerDeleteResponses = {
  200: unknown;
};

export type MessagesControllerFindOneData = {
  body?: never;
  path: {
    messageUid: string;
  };
  query?: never;
  url: '/v4/messaging/messages/{messageUid}';
};

export type MessagesControllerFindOneResponses = {
  200: Message;
};

export type MessagesControllerFindOneResponse =
  MessagesControllerFindOneResponses[keyof MessagesControllerFindOneResponses];

export type ConversationsControllerFindAllData = {
  body?: never;
  path?: never;
  query?: {
    page?: number;
    pageSize?: number;
    includeCount?: boolean;
    /**
     * Filter conversations by externalResearcherId.
     */
    externalResearcherId?: string;
    /**
     * Filter conversations by projectId.
     */
    projectId?: string;
    /**
     * List every conversation on a team (the researcher team-wide inbox).
     * Researcher-only; ignored when projectId is provided (the project's own
     * team scope wins).
     */
    teamId?: string;
    /**
     * Free-text search across the conversation name and participant names.
     */
    query?: string;
  };
  url: '/v4/messaging/conversations';
};

export type ConversationsControllerFindAllResponses = {
  200: ConversationsPaginatedDto;
};

export type ConversationsControllerFindAllResponse =
  ConversationsControllerFindAllResponses[keyof ConversationsControllerFindAllResponses];

export type ConversationsControllerCreateData = {
  body: CreateConversationDto;
  path?: never;
  query?: never;
  url: '/v4/messaging/conversations';
};

export type ConversationsControllerCreateResponses = {
  201: Conversation;
};

export type ConversationsControllerCreateResponse =
  ConversationsControllerCreateResponses[keyof ConversationsControllerCreateResponses];

export type ConversationsControllerRemoveData = {
  body?: never;
  path: {
    conversationUid: string;
  };
  query?: never;
  url: '/v4/messaging/conversations/{conversationUid}';
};

export type ConversationsControllerRemoveResponses = {
  200: unknown;
};

export type ConversationsControllerFindOneData = {
  body?: never;
  path: {
    conversationUid: string;
  };
  query?: never;
  url: '/v4/messaging/conversations/{conversationUid}';
};

export type ConversationsControllerFindOneResponses = {
  200: Conversation;
};

export type ConversationsControllerFindOneResponse =
  ConversationsControllerFindOneResponses[keyof ConversationsControllerFindOneResponses];

export type ConversationsControllerUpdateData = {
  body: UpdateConversationDto;
  path: {
    conversationUid: string;
  };
  query?: never;
  url: '/v4/messaging/conversations/{conversationUid}';
};

export type ConversationsControllerUpdateResponses = {
  200: Conversation;
};

export type ConversationsControllerUpdateResponse =
  ConversationsControllerUpdateResponses[keyof ConversationsControllerUpdateResponses];

export type ConversationsControllerMarkAsReadData = {
  body?: never;
  path: {
    conversationUid: string;
  };
  query?: never;
  url: '/v4/messaging/conversations/{conversationUid}/read';
};

export type ConversationsControllerMarkAsReadResponses = {
  200: unknown;
};

export type ConversationsControllerUploadAttachmentData = {
  body: {
    files: Blob | File;
  };
  path: {
    conversationUid: string;
  };
  query?: never;
  url: '/v4/messaging/conversations/{conversationUid}/attachment';
};

export type ConversationsControllerUploadAttachmentResponses = {
  200: UploadedAttachmentDto;
};

export type ConversationsControllerUploadAttachmentResponse =
  ConversationsControllerUploadAttachmentResponses[keyof ConversationsControllerUploadAttachmentResponses];

export type ConversationsControllerInitiateAttachmentUploadData = {
  body: InitiateMessageAttachmentUploadDto;
  path: {
    conversationUid: string;
  };
  query?: never;
  url: '/v4/messaging/conversations/{conversationUid}/attachment-uploads/initiate';
};

export type ConversationsControllerInitiateAttachmentUploadResponses = {
  200: InitiateMessageAttachmentUploadResponseDto;
};

export type ConversationsControllerInitiateAttachmentUploadResponse =
  ConversationsControllerInitiateAttachmentUploadResponses[keyof ConversationsControllerInitiateAttachmentUploadResponses];

export type ConversationsControllerRemoveParticipantData = {
  body?: never;
  path: {
    conversationUid: string;
    participantUserId: string;
  };
  query?: never;
  url: '/v4/messaging/conversations/{conversationUid}/participants/{participantUserId}';
};

export type ConversationsControllerRemoveParticipantResponses = {
  200: unknown;
};

export type ConversationsControllerAddParticipantData = {
  body?: never;
  path: {
    conversationUid: string;
    participantUserId: string;
  };
  query?: never;
  url: '/v4/messaging/conversations/{conversationUid}/participants/{participantUserId}';
};

export type ConversationsControllerAddParticipantResponses = {
  201: unknown;
};

export type MessagingOrganizationsControllerFindOneData = {
  body?: never;
  path: {
    organizationId: string;
  };
  query?: never;
  url: '/v4/messaging/organizations/{organizationId}';
};

export type MessagingOrganizationsControllerFindOneResponses = {
  200: MessagingOrganization;
};

export type MessagingOrganizationsControllerFindOneResponse =
  MessagingOrganizationsControllerFindOneResponses[keyof MessagingOrganizationsControllerFindOneResponses];

export type MessagingOrganizationsControllerCreateOrganizationData = {
  body?: never;
  path: {
    organizationId: string;
  };
  query?: never;
  url: '/v4/messaging/organizations/{organizationId}';
};

export type MessagingOrganizationsControllerCreateOrganizationResponses = {
  201: MessagingOrganization;
};

export type MessagingOrganizationsControllerCreateOrganizationResponse =
  MessagingOrganizationsControllerCreateOrganizationResponses[keyof MessagingOrganizationsControllerCreateOrganizationResponses];

export type MessagingOrganizationsControllerUpdateOrganizationData = {
  body?: never;
  path: {
    organizationId: string;
  };
  query?: never;
  url: '/v4/messaging/organizations/{organizationId}';
};

export type MessagingOrganizationsControllerUpdateOrganizationResponses = {
  200: MessagingOrganization;
};

export type MessagingOrganizationsControllerUpdateOrganizationResponse =
  MessagingOrganizationsControllerUpdateOrganizationResponses[keyof MessagingOrganizationsControllerUpdateOrganizationResponses];

export type MessagingOrganizationsControllerCreateTeamData = {
  body?: never;
  path: {
    organizationId: string;
    teamForeignId: string;
  };
  query?: never;
  url: '/v4/messaging/organizations/{organizationId}/teams/{teamForeignId}';
};

export type MessagingOrganizationsControllerCreateTeamResponses = {
  201: MessagingTeam;
};

export type MessagingOrganizationsControllerCreateTeamResponse =
  MessagingOrganizationsControllerCreateTeamResponses[keyof MessagingOrganizationsControllerCreateTeamResponses];

export type MessagingOrganizationsControllerUpdateTeamData = {
  body?: never;
  path: {
    organizationId: string;
    teamForeignId: string;
  };
  query?: never;
  url: '/v4/messaging/organizations/{organizationId}/teams/{teamForeignId}';
};

export type MessagingOrganizationsControllerUpdateTeamResponses = {
  200: MessagingTeam;
};

export type MessagingOrganizationsControllerUpdateTeamResponse =
  MessagingOrganizationsControllerUpdateTeamResponses[keyof MessagingOrganizationsControllerUpdateTeamResponses];

export type MessagingOrganizationsControllerFindTeamData = {
  body?: never;
  path: {
    organizationUid: string;
    teamForeignId: string;
  };
  query?: never;
  url: '/v4/messaging/organizations/{organizationUid}/teams/{teamForeignId}';
};

export type MessagingOrganizationsControllerFindTeamResponses = {
  200: MessagingTeam;
};

export type MessagingOrganizationsControllerFindTeamResponse =
  MessagingOrganizationsControllerFindTeamResponses[keyof MessagingOrganizationsControllerFindTeamResponses];

export type MessagingUsersControllerCreateData = {
  body?: never;
  path: {
    userId: string;
  };
  query?: never;
  url: '/v4/messaging/users/{userId}';
};

export type MessagingUsersControllerCreateResponses = {
  201: MessagingUser;
};

export type MessagingUsersControllerCreateResponse =
  MessagingUsersControllerCreateResponses[keyof MessagingUsersControllerCreateResponses];

export type MessagingUsersControllerUpdateData = {
  body?: never;
  path: {
    userId: string;
  };
  query?: never;
  url: '/v4/messaging/users/{userId}';
};

export type MessagingUsersControllerUpdateResponses = {
  200: MessagingUser;
};

export type MessagingUsersControllerUpdateResponse =
  MessagingUsersControllerUpdateResponses[keyof MessagingUsersControllerUpdateResponses];

export type MessagingUsersControllerFindOneData = {
  body?: never;
  path: {
    userUid: string;
  };
  query?: never;
  url: '/v4/messaging/users/{userUid}';
};

export type MessagingUsersControllerFindOneResponses = {
  200: MessagingUser;
};

export type MessagingUsersControllerFindOneResponse =
  MessagingUsersControllerFindOneResponses[keyof MessagingUsersControllerFindOneResponses];

export type MessagingUsersControllerFindOneByEmailData = {
  body?: never;
  path: {
    email: string;
  };
  query?: never;
  url: '/v4/messaging/users/find/{email}';
};

export type MessagingUsersControllerFindOneByEmailResponses = {
  200: MessagingUser;
};

export type MessagingUsersControllerFindOneByEmailResponse =
  MessagingUsersControllerFindOneByEmailResponses[keyof MessagingUsersControllerFindOneByEmailResponses];

export type SynchronizeControllerSyncUserData = {
  body?: never;
  path: {
    userId: string;
  };
  query: {
    updateMembership: boolean;
  };
  url: '/v4/messaging/synchronize/users/{userId}';
};

export type SynchronizeControllerSyncUserResponses = {
  201: SynchronizeMessagingResponseDto;
};

export type SynchronizeControllerSyncUserResponse =
  SynchronizeControllerSyncUserResponses[keyof SynchronizeControllerSyncUserResponses];

export type SynchronizeControllerSyncOrganizationData = {
  body?: never;
  path: {
    organizationId: string;
  };
  query?: never;
  url: '/v4/messaging/synchronize/organizations/{organizationId}';
};

export type SynchronizeControllerSyncOrganizationResponses = {
  201: SynchronizeMessagingResponseDto;
};

export type SynchronizeControllerSyncOrganizationResponse =
  SynchronizeControllerSyncOrganizationResponses[keyof SynchronizeControllerSyncOrganizationResponses];

export type SynchronizeControllerSyncTeamData = {
  body?: never;
  path: {
    teamId: string;
  };
  query?: never;
  url: '/v4/messaging/synchronize/teams/{teamId}';
};

export type SynchronizeControllerSyncTeamResponses = {
  201: SynchronizeMessagingResponseDto;
};

export type SynchronizeControllerSyncTeamResponse =
  SynchronizeControllerSyncTeamResponses[keyof SynchronizeControllerSyncTeamResponses];

export type SynchronizeControllerRemoveTestOrgUserData = {
  body?: never;
  path: {
    organizationId: string;
    userId: string;
  };
  query?: never;
  url: '/v4/messaging/synchronize/testOrgUser/organizations/{organizationId}/users/{userId}';
};

export type SynchronizeControllerRemoveTestOrgUserResponses = {
  200: unknown;
};

export type SynchronizeControllerAddTestOrgUserData = {
  body?: never;
  path: {
    organizationId: string;
    userId: string;
  };
  query?: never;
  url: '/v4/messaging/synchronize/testOrgUser/organizations/{organizationId}/users/{userId}';
};

export type SynchronizeControllerAddTestOrgUserResponses = {
  200: unknown;
};

export type FeatureFlagsControllerSetActiveData = {
  body: SetFeatureFlagActiveDto;
  path: {
    /**
     * The feature flag id.
     */
    id: string;
  };
  query?: never;
  url: '/v4/feature-flags/{id}/active';
};

export type FeatureFlagsControllerSetActiveErrors = {
  /**
   * The current user does not have the developer role.
   */
  403: unknown;
  /**
   * No feature flag exists for the given id.
   */
  404: unknown;
};

export type FeatureFlagsControllerSetActiveResponses = {
  /**
   * Returns the updated feature flag.
   */
  200: FeatureFlagWithOrganizationsDto;
};

export type FeatureFlagsControllerSetActiveResponse =
  FeatureFlagsControllerSetActiveResponses[keyof FeatureFlagsControllerSetActiveResponses];

export type FeatureFlagsControllerAddOrganizationData = {
  body: AddFeatureFlagOrganizationDto;
  path: {
    /**
     * The feature flag id.
     */
    id: string;
  };
  query?: never;
  url: '/v4/feature-flags/{id}/organizations';
};

export type FeatureFlagsControllerAddOrganizationErrors = {
  /**
   * The current user does not have the developer role.
   */
  403: unknown;
  /**
   * No feature flag exists for the given id, or no organization exists for the given organizationId.
   */
  404: unknown;
};

export type FeatureFlagsControllerAddOrganizationResponses = {
  /**
   * Returns the updated feature flag.
   */
  200: FeatureFlagWithOrganizationsDto;
};

export type FeatureFlagsControllerAddOrganizationResponse =
  FeatureFlagsControllerAddOrganizationResponses[keyof FeatureFlagsControllerAddOrganizationResponses];

export type FeatureFlagsControllerRemoveOrganizationData = {
  body?: never;
  path: {
    /**
     * The feature flag id.
     */
    id: string;
    /**
     * The organization id to remove.
     */
    organizationId: string;
  };
  query: {
    /**
     * Which list to remove the organization from.
     */
    list: 'allow' | 'deny';
  };
  url: '/v4/feature-flags/{id}/organizations/{organizationId}';
};

export type FeatureFlagsControllerRemoveOrganizationErrors = {
  /**
   * Removing the organization would empty the allow list of an active feature flag and enable it for every organization.
   */
  400: unknown;
  /**
   * The current user does not have the developer role.
   */
  403: unknown;
  /**
   * No feature flag exists for the given id.
   */
  404: unknown;
};

export type FeatureFlagsControllerRemoveOrganizationResponses = {
  /**
   * Returns the updated feature flag.
   */
  200: FeatureFlagWithOrganizationsDto;
};

export type FeatureFlagsControllerRemoveOrganizationResponse =
  FeatureFlagsControllerRemoveOrganizationResponses[keyof FeatureFlagsControllerRemoveOrganizationResponses];

export type FeatureFlagsControllerListAllData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v4/feature-flags/all';
};

export type FeatureFlagsControllerListAllResponses = {
  /**
   * Returns all feature flags with their global active state and allow/deny organizations (resolved to id + name).
   */
  200: Array<FeatureFlagWithOrganizationsDto>;
};

export type FeatureFlagsControllerListAllResponse =
  FeatureFlagsControllerListAllResponses[keyof FeatureFlagsControllerListAllResponses];

export type FeatureFlagsControllerFindAllData = {
  body?: never;
  path?: never;
  query?: {
    organizationId?: string;
  };
  url: '/v4/feature-flags';
};

export type FeatureFlagsControllerFindAllResponses = {
  /**
   * This description defines when a HttpStatus.OK response is returned. It should always present a list of names with all active feature flags.
   */
  200: Array<unknown>;
};

export type FeatureFlagsControllerFindAllResponse =
  FeatureFlagsControllerFindAllResponses[keyof FeatureFlagsControllerFindAllResponses];

export type FeatureFlagsControllerGetFeatureFlagActiveData = {
  body?: never;
  path: {
    /**
     * The endpoint will find the feature flag by its name.
     */
    name: string;
  };
  query?: {
    organizationId?: string;
  };
  url: '/v4/feature-flags/{name}/active';
};

export type FeatureFlagsControllerGetFeatureFlagActiveErrors = {
  /**
   * If no feature flag is found, it will return HttpStatus.NOT_FOUND error.
   */
  404: unknown;
};

export type FeatureFlagsControllerGetFeatureFlagActiveResponses = {
  /**
   * The return is based on the active flag, the start and end date. If the feature flag is active, it will return true. If it is inactive, it will return false.
   */
  200: unknown;
};

export type SecureFilesControllerCreateSignedUrlData = {
  body: CreateSignedUrlDto;
  path?: never;
  query?: never;
  url: '/v4/secure-files/signed-url';
};

export type SecureFilesControllerCreateSignedUrlResponses = {
  201: string;
};

export type SecureFilesControllerCreateSignedUrlResponse =
  SecureFilesControllerCreateSignedUrlResponses[keyof SecureFilesControllerCreateSignedUrlResponses];

export type MessageAttachmentUploadsControllerUploadPartData = {
  body: UploadMessageAttachmentPartDto;
  path?: never;
  query?: never;
  url: '/v4/messaging/attachment-uploads/upload-part';
};

export type MessageAttachmentUploadsControllerUploadPartResponses = {
  200: UploadMessageAttachmentPartResponseDto;
};

export type MessageAttachmentUploadsControllerUploadPartResponse =
  MessageAttachmentUploadsControllerUploadPartResponses[keyof MessageAttachmentUploadsControllerUploadPartResponses];

export type MessageAttachmentUploadsControllerCompleteData = {
  body: CompleteMessageAttachmentUploadDto;
  path?: never;
  query?: never;
  url: '/v4/messaging/attachment-uploads/complete';
};

export type MessageAttachmentUploadsControllerCompleteResponses = {
  200: UploadedAttachmentDto;
};

export type MessageAttachmentUploadsControllerCompleteResponse =
  MessageAttachmentUploadsControllerCompleteResponses[keyof MessageAttachmentUploadsControllerCompleteResponses];

export type MessageAttachmentUploadsControllerAbortData = {
  body: AbortMessageAttachmentUploadDto;
  path?: never;
  query?: never;
  url: '/v4/messaging/attachment-uploads/abort';
};

export type MessageAttachmentUploadsControllerAbortResponses = {
  204: void;
};

export type MessageAttachmentUploadsControllerAbortResponse =
  MessageAttachmentUploadsControllerAbortResponses[keyof MessageAttachmentUploadsControllerAbortResponses];

export type PricingPlansControllerCreateData = {
  body: CreatePricingPlanDto;
  path?: never;
  query?: never;
  url: '/v4/pricing/plans';
};

export type PricingPlansControllerCreateResponses = {
  201: PricingPlan;
};

export type PricingPlansControllerCreateResponse =
  PricingPlansControllerCreateResponses[keyof PricingPlansControllerCreateResponses];

export type PricingPlansControllerGetStandardPlansSummaryData = {
  body?: never;
  path?: never;
  query?: {
    planTypes?: Array<MarketingStandardPricingPlanTypeV3>;
    planPeriods?: Array<PricingPlanPeriodTagType>;
  };
  url: '/v4/pricing/plans/standard';
};

export type PricingPlansControllerGetStandardPlansSummaryResponses = {
  200: Array<StandardPricingPlanSummaryDto>;
};

export type PricingPlansControllerGetStandardPlansSummaryResponse =
  PricingPlansControllerGetStandardPlansSummaryResponses[keyof PricingPlansControllerGetStandardPlansSummaryResponses];

export type PricingPlansControllerFindAllData = {
  body: GetPricingPlansPaginatedDto;
  path?: never;
  query?: never;
  url: '/v4/pricing/plans/list';
};

export type PricingPlansControllerFindAllResponses = {
  200: PricingPlansPaginatedDto;
};

export type PricingPlansControllerFindAllResponse =
  PricingPlansControllerFindAllResponses[keyof PricingPlansControllerFindAllResponses];

export type PricingPlansControllerRemoveData = {
  body?: never;
  path: {
    id: string;
  };
  query?: never;
  url: '/v4/pricing/plans/{id}';
};

export type PricingPlansControllerRemoveResponses = {
  200: PricingPlan;
};

export type PricingPlansControllerRemoveResponse =
  PricingPlansControllerRemoveResponses[keyof PricingPlansControllerRemoveResponses];

export type PricingPlansControllerFindOneData = {
  body?: never;
  path: {
    id: string;
  };
  query?: never;
  url: '/v4/pricing/plans/{id}';
};

export type PricingPlansControllerFindOneResponses = {
  200: PricingPlan;
};

export type PricingPlansControllerFindOneResponse =
  PricingPlansControllerFindOneResponses[keyof PricingPlansControllerFindOneResponses];

export type PricingPlansControllerUpdateData = {
  body: UpdatePricingPlanRestrictedDto;
  path: {
    id: string;
  };
  query?: never;
  url: '/v4/pricing/plans/{id}';
};

export type PricingPlansControllerUpdateResponses = {
  200: PricingPlan;
};

export type PricingPlansControllerUpdateResponse =
  PricingPlansControllerUpdateResponses[keyof PricingPlansControllerUpdateResponses];

export type RateCardsControllerGetStandardPlanRatesData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v4/pricing/rate-cards/rates/standard';
};

export type RateCardsControllerGetStandardPlanRatesResponses = {
  200: StandardPlanRate;
};

export type RateCardsControllerGetStandardPlanRatesResponse =
  RateCardsControllerGetStandardPlanRatesResponses[keyof RateCardsControllerGetStandardPlanRatesResponses];

export type RateCardsControllerGetDefaultRateCardData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v4/pricing/rate-cards/default';
};

export type RateCardsControllerGetDefaultRateCardResponses = {
  200: RateCard;
};

export type RateCardsControllerGetDefaultRateCardResponse =
  RateCardsControllerGetDefaultRateCardResponses[keyof RateCardsControllerGetDefaultRateCardResponses];

export type RateCardsControllerGetStandardPlanRateCardsData = {
  body?: never;
  path?: never;
  query?: {
    planTypes?: Array<MarketingStandardPricingPlanTypeV3>;
    planPeriods?: Array<PricingPlanPeriodTagType>;
  };
  url: '/v4/pricing/rate-cards/standard';
};

export type RateCardsControllerGetStandardPlanRateCardsResponses = {
  200: Array<RateCard>;
};

export type RateCardsControllerGetStandardPlanRateCardsResponse =
  RateCardsControllerGetStandardPlanRateCardsResponses[keyof RateCardsControllerGetStandardPlanRateCardsResponses];

export type AdminResearchersControllerListData = {
  body?: never;
  path?: never;
  query?: {
    page?: number;
    pageSize?: number;
    includeCount?: boolean;
    /**
     * When set, every other filter (including status) is ignored — admins can
     * always inspect a researcher by id, deactivated or not.
     */
    userId?: string;
    organizationId?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    /**
     * UTC day (the whole day is matched, legacy parity).
     */
    createdAt?: string;
    /**
     * Honest N-years updatedAt window (the legacy v4 users/list filter
     * hardwired 1 year regardless of the selected value). Absent = all time.
     */
    modifiedWithinYears?: number;
    trustLevelStatus?: ResearcherTrustLevelStatusFilter;
    reviewStatus?: ResearcherReviewStatusFilter;
    status?: ResearcherStatusFilter;
    /**
     * Case-insensitive substring over the experiments record's keys.
     */
    experimentName?: string;
  };
  url: '/v4/admin/researchers/list';
};

export type AdminResearchersControllerListResponses = {
  200: AdminResearchersPaginatedDto;
};

export type AdminResearchersControllerListResponse =
  AdminResearchersControllerListResponses[keyof AdminResearchersControllerListResponses];

export type AdminResearchersControllerUpdateResearcherData = {
  body: UpdateResearcherDto;
  path: {
    userId: string;
  };
  query?: never;
  url: '/v4/admin/researchers/{userId}';
};

export type AdminResearchersControllerUpdateResearcherResponses = {
  200: UpdateResearcherResponseDto;
};

export type AdminResearchersControllerUpdateResearcherResponse =
  AdminResearchersControllerUpdateResearcherResponses[keyof AdminResearchersControllerUpdateResearcherResponses];

export type AdminTeamsControllerListData = {
  body?: never;
  path?: never;
  query?: {
    page?: number;
    pageSize?: number;
    includeCount?: boolean;
    teamId?: string;
    organizationId?: string;
    /**
     * Include soft-deleted teams in an organization listing (default false).
     */
    includeDeleted?: boolean;
  };
  url: '/v4/admin/teams/list';
};

export type AdminTeamsControllerListResponses = {
  200: AdminTeamsPaginatedDto;
};

export type AdminTeamsControllerListResponse =
  AdminTeamsControllerListResponses[keyof AdminTeamsControllerListResponses];

export type FraudWorkDomainsControllerBlockWorkDomainData = {
  body: BlockWorkDomainDto;
  path?: never;
  query?: never;
  url: '/v4/admin/fraud-work-domains';
};

export type FraudWorkDomainsControllerBlockWorkDomainResponses = {
  200: BlockWorkDomainResponseDto;
};

export type FraudWorkDomainsControllerBlockWorkDomainResponse =
  FraudWorkDomainsControllerBlockWorkDomainResponses[keyof FraudWorkDomainsControllerBlockWorkDomainResponses];

export type AdminFulfillmentRequestsControllerListData = {
  body?: never;
  path?: never;
  query?: {
    page?: number;
    pageSize?: number;
    includeCount?: boolean;
  };
  url: '/v4/admin/fulfillment-requests/list';
};

export type AdminFulfillmentRequestsControllerListResponses = {
  200: FulfillmentRequestsPaginatedDto;
};

export type AdminFulfillmentRequestsControllerListResponse =
  AdminFulfillmentRequestsControllerListResponses[keyof AdminFulfillmentRequestsControllerListResponses];

export type ManualReviewControllerFindManualReviewResponsesData = {
  body?: never;
  path?: never;
  query?: {
    page?: number;
    pageSize?: number;
    includeCount?: boolean;
    /**
     * Optionally scope the queue to a single organization.
     */
    organizationId?: string;
    /**
     * Free-text filter matched (case-insensitively) against the project title,
     * project id, or the participant's name.
     */
    search?: string;
  };
  url: '/v4/survey-responses/manual-review';
};

export type ManualReviewControllerFindManualReviewResponsesResponses = {
  200: ManualReviewResponsesPaginatedDto;
};

export type ManualReviewControllerFindManualReviewResponsesResponse =
  ManualReviewControllerFindManualReviewResponsesResponses[keyof ManualReviewControllerFindManualReviewResponsesResponses];

export type ManualReviewControllerParticipantIpSummaryData = {
  body?: never;
  path: {
    userId: string;
  };
  query?: never;
  url: '/v4/survey-responses/users/{userId}/ip-summary';
};

export type ManualReviewControllerParticipantIpSummaryResponses = {
  200: ParticipantIpSummaryDto;
};

export type ManualReviewControllerParticipantIpSummaryResponse =
  ManualReviewControllerParticipantIpSummaryResponses[keyof ManualReviewControllerParticipantIpSummaryResponses];

export type ManualReviewControllerApproveManualReviewResponseData = {
  body: ManualReviewActionBody;
  path: {
    responseId: string;
  };
  query?: never;
  url: '/v4/survey-responses/manual-review/{responseId}/approve';
};

export type ManualReviewControllerApproveManualReviewResponseResponses = {
  201: unknown;
};

export type ManualReviewControllerRejectManualReviewResponseData = {
  body: ManualReviewActionBody;
  path: {
    responseId: string;
  };
  query?: never;
  url: '/v4/survey-responses/manual-review/{responseId}/reject';
};

export type ManualReviewControllerRejectManualReviewResponseResponses = {
  201: unknown;
};

export type ManualReviewControllerReportManualReviewResponseData = {
  body: ManualReviewActionBody;
  path: {
    responseId: string;
  };
  query?: never;
  url: '/v4/survey-responses/manual-review/{responseId}/report';
};

export type ManualReviewControllerReportManualReviewResponseResponses = {
  201: unknown;
};

export type ManualReviewControllerParticipantFlagHistoryData = {
  body?: never;
  path: {
    userId: string;
  };
  query?: never;
  url: '/v4/survey-responses/users/{userId}/flag-history';
};

export type ManualReviewControllerParticipantFlagHistoryResponses = {
  200: ParticipantFlagHistoryDto;
};

export type ManualReviewControllerParticipantFlagHistoryResponse =
  ManualReviewControllerParticipantFlagHistoryResponses[keyof ManualReviewControllerParticipantFlagHistoryResponses];

export type ManualReviewControllerParticipantIdentityVerificationData = {
  body?: never;
  path: {
    userId: string;
  };
  query?: never;
  url: '/v4/survey-responses/users/{userId}/identity-verification';
};

export type ManualReviewControllerParticipantIdentityVerificationResponses = {
  200: ParticipantIdentityVerificationDto;
};

export type ManualReviewControllerParticipantIdentityVerificationResponse =
  ManualReviewControllerParticipantIdentityVerificationResponses[keyof ManualReviewControllerParticipantIdentityVerificationResponses];

export type ScreenerResponsesControllerBatchInviteData = {
  body: BatchInviteScreenerResponseDto;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/projects/{projectId}/screener-responses/batch-invite';
};

export type ScreenerResponsesControllerBatchInviteResponses = {
  200: BatchInviteResultDto;
};

export type ScreenerResponsesControllerBatchInviteResponse =
  ScreenerResponsesControllerBatchInviteResponses[keyof ScreenerResponsesControllerBatchInviteResponses];

export type ScreenerResponsesControllerNextQuestionData = {
  body?: never;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/projects/{projectId}/screener-responses/next-question';
};

export type ScreenerResponsesControllerNextQuestionResponses = {
  200: string;
};

export type ScreenerResponsesControllerNextQuestionResponse =
  ScreenerResponsesControllerNextQuestionResponses[keyof ScreenerResponsesControllerNextQuestionResponses];

export type ScreenerResponsesControllerFindAllData = {
  body?: never;
  path: {
    projectId: string;
  };
  query?: {
    page?: number;
    pageSize?: number;
    includeCount?: boolean;
    hidden?: boolean;
    status?: ScreenerResponseStatus;
    statuses?: Array<ScreenerResponseStatus>;
    qualified?: boolean;
    favorite?: boolean;
    /**
     * When true, only the unified Rejected view (`REJECTED_MATCH_FILTER`):
     * responses the researcher manually rejected via the "Not a fit" flow
     * (rows with a `disqualification` record — requalifying unsets it) plus
     * the rejection lifecycle statuses (REJECTEDQUALITY, INVALIDRESPONSE).
     * When false, the complement.
     */
    rejected?: boolean;
    autoInvite?: boolean;
    visibilityStatus?: ScreenerResponseVisibilityStatus;
    /**
     * the project's `exposeParticipantEmails` setting, read server-side and passed
     * to the repository directly, so a request cannot ask for the address.
     *
     * The field stays declared only because the global ValidationPipe runs with
     * `forbidNonWhitelisted`, so removing it would 400 any client still sending
     * it, including browsers holding a stale bundle. Delete it once no traffic
     * carries it.
     *
     * @deprecated
     */
    exposeParticipantEmails?: boolean;
    screenerResponseIds?: Array<string>;
    userId?: string;
    taskStatus?: TaskStatus;
    taskType?: TaskType;
    /**
     * Opt-in sort. Omit to preserve natural insertion ordering.
     */
    sortBy?: ScreenerResponseSortField;
    /**
     * Free-text search across participant name and job title; empty/whitespace is treated as no filter.
     */
    q?: string;
    genders?: Array<Gender>;
    ageGroups?: Array<AgeGroup>;
    countries?: Array<CountryCode>;
    /**
     * Quotas V2: only responses bucketed into these segments. Each value is
     * `encodeURIComponent(groupId) + ':' + encodeURIComponent(segmentId)`
     * (`no-match` for a group's implicit bucket). Segments of one group are
     * alternatives; different groups must all match.
     */
    quotaSegments?: Array<string>;
    /**
     * Whether to return the roster summary: `availableCountries`, plus
     * `rosterCount` and `hasNonMarketplaceParticipants` when `includeCount` is
     * set. Each one reads the project's roster regardless of the status and
     * demographic filters, so a caller that never shows them turns this off.
     */
    includeRosterSummary?: boolean;
  };
  url: '/v4/projects/{projectId}/screener-responses';
};

export type ScreenerResponsesControllerFindAllResponses = {
  200: ScreenerResponsesPaginatedDto;
};

export type ScreenerResponsesControllerFindAllResponse =
  ScreenerResponsesControllerFindAllResponses[keyof ScreenerResponsesControllerFindAllResponses];

export type ScreenerResponsesControllerUpsertData = {
  body: UpsertScreenerResponseDto;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/projects/{projectId}/screener-responses';
};

export type ScreenerResponsesControllerUpsertResponses = {
  200: ScreenerResponse;
};

export type ScreenerResponsesControllerUpsertResponse =
  ScreenerResponsesControllerUpsertResponses[keyof ScreenerResponsesControllerUpsertResponses];

export type ScreenerResponsesControllerGetResponseCountByProjectIdData = {
  body?: never;
  path: {
    projectId: string;
  };
  query: {
    qualified: boolean;
  };
  url: '/v4/projects/{projectId}/screener-responses/count';
};

export type ScreenerResponsesControllerGetResponseCountByProjectIdResponses = {
  200: number;
};

export type ScreenerResponsesControllerGetResponseCountByProjectIdResponse =
  ScreenerResponsesControllerGetResponseCountByProjectIdResponses[keyof ScreenerResponsesControllerGetResponseCountByProjectIdResponses];

export type ScreenerResponsesControllerUpdateInvitationMessageData = {
  body: UpdateInvitationMessageDto;
  path: {
    projectId: string;
    screenerResponseId: string;
  };
  query?: never;
  url: '/v4/projects/{projectId}/screener-responses/{screenerResponseId}/invitation-message';
};

export type ScreenerResponsesControllerUpdateInvitationMessageResponses = {
  200: unknown;
};

export type ScreenerResponsesControllerBatchUpdateMeetingInformationData = {
  body: UpdateScreenerResponseMeetingInformationDto;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/projects/{projectId}/screener-responses/batch-update-meeting-information';
};

export type ScreenerResponsesControllerBatchUpdateMeetingInformationResponses =
  {
    200: Array<ScreenerResponse>;
  };

export type ScreenerResponsesControllerBatchUpdateMeetingInformationResponse =
  ScreenerResponsesControllerBatchUpdateMeetingInformationResponses[keyof ScreenerResponsesControllerBatchUpdateMeetingInformationResponses];

export type ScreenerResponsesControllerFindOneData = {
  body?: never;
  path: {
    projectId: string;
    screenerResponseId: string;
  };
  query?: never;
  url: '/v4/projects/{projectId}/screener-responses/{screenerResponseId}';
};

export type ScreenerResponsesControllerFindOneResponses = {
  200: ScreenerResponse;
};

export type ScreenerResponsesControllerFindOneResponse =
  ScreenerResponsesControllerFindOneResponses[keyof ScreenerResponsesControllerFindOneResponses];

export type ScreenerResponsesControllerUpdateData = {
  body: UpdateScreenerResponseDto;
  path: {
    projectId: string;
    screenerResponseId: string;
  };
  query?: never;
  url: '/v4/projects/{projectId}/screener-responses/{screenerResponseId}';
};

export type ScreenerResponsesControllerUpdateResponses = {
  200: ScreenerResponse;
};

export type ScreenerResponsesControllerUpdateResponse =
  ScreenerResponsesControllerUpdateResponses[keyof ScreenerResponsesControllerUpdateResponses];

export type ScreenerResponsesControllerProcessAutoInviteByScreenerResponseIdData =
  {
    body?: never;
    path: {
      projectId: string;
      screenerResponseId: string;
    };
    query?: never;
    url: '/v4/projects/{projectId}/screener-responses/{screenerResponseId}/process-auto-invite';
  };

export type ScreenerResponsesControllerProcessAutoInviteByScreenerResponseIdResponses =
  {
    200: ScreenerResponse;
  };

export type ScreenerResponsesControllerProcessAutoInviteByScreenerResponseIdResponse =
  ScreenerResponsesControllerProcessAutoInviteByScreenerResponseIdResponses[keyof ScreenerResponsesControllerProcessAutoInviteByScreenerResponseIdResponses];

export type ScreenerResponsesControllerSaveBookingData = {
  body: ScreenerResponseBookingDto;
  path: {
    projectId: string;
    screenerResponseId: string;
  };
  query?: never;
  url: '/v4/projects/{projectId}/screener-responses/{screenerResponseId}/save-booking';
};

export type ScreenerResponsesControllerSaveBookingResponses = {
  200: ScreenerResponse;
};

export type ScreenerResponsesControllerSaveBookingResponse =
  ScreenerResponsesControllerSaveBookingResponses[keyof ScreenerResponsesControllerSaveBookingResponses];

export type ScreenerResponsesControllerReportData = {
  body: ReportScreenerResponseDto;
  path: {
    projectId: string;
    screenerResponseId: string;
  };
  query?: never;
  url: '/v4/projects/{projectId}/screener-responses/{screenerResponseId}/report';
};

export type ScreenerResponsesControllerReportResponses = {
  200: ScreenerResponse;
};

export type ScreenerResponsesControllerReportResponse =
  ScreenerResponsesControllerReportResponses[keyof ScreenerResponsesControllerReportResponses];

export type ScreenerResponsesControllerFavoriteData = {
  body: FavoriteScreenerResponseDto;
  path: {
    projectId: string;
    screenerResponseId: string;
  };
  query?: never;
  url: '/v4/projects/{projectId}/screener-responses/{screenerResponseId}/favorite';
};

export type ScreenerResponsesControllerFavoriteResponses = {
  200: ScreenerResponse;
};

export type ScreenerResponsesControllerFavoriteResponse =
  ScreenerResponsesControllerFavoriteResponses[keyof ScreenerResponsesControllerFavoriteResponses];

export type ScreenerResponsesControllerIngestExternalScreenerAnswersData = {
  body: IngestExternalScreenerAnswersDto;
  path: {
    projectId: string;
    screenerResponseId: string;
  };
  query?: never;
  url: '/v4/projects/{projectId}/screener-responses/{screenerResponseId}/external-screener-answers';
};

export type ScreenerResponsesControllerIngestExternalScreenerAnswersResponses =
  {
    201: unknown;
  };

export type ScreenerResponsesControllerQualifyData = {
  body: QualifyScreenerResponseDto;
  path: {
    projectId: string;
    screenerResponseId: string;
  };
  query?: never;
  url: '/v4/projects/{projectId}/screener-responses/{screenerResponseId}/qualify';
};

export type ScreenerResponsesControllerQualifyResponses = {
  200: ScreenerResponse;
};

export type ScreenerResponsesControllerQualifyResponse =
  ScreenerResponsesControllerQualifyResponses[keyof ScreenerResponsesControllerQualifyResponses];

export type ScreenerResponsesControllerHideData = {
  body: HideScreenerResponseDto;
  path: {
    projectId: string;
    screenerResponseId: string;
  };
  query?: never;
  url: '/v4/projects/{projectId}/screener-responses/{screenerResponseId}/hide';
};

export type ScreenerResponsesControllerHideResponses = {
  200: ScreenerResponse;
};

export type ScreenerResponsesControllerHideResponse =
  ScreenerResponsesControllerHideResponses[keyof ScreenerResponsesControllerHideResponses];

export type ScreenerResponsesControllerInviteData = {
  body: InviteScreenerResponseDto;
  path: {
    projectId: string;
    screenerResponseId: string;
  };
  query?: never;
  url: '/v4/projects/{projectId}/screener-responses/{screenerResponseId}/invite';
};

export type ScreenerResponsesControllerInviteResponses = {
  200: ScreenerResponse;
};

export type ScreenerResponsesControllerInviteResponse =
  ScreenerResponsesControllerInviteResponses[keyof ScreenerResponsesControllerInviteResponses];

export type ScreenerResponsesControllerScheduleExternalData = {
  body: ScheduleExternalScreenerResponseDto;
  path: {
    projectId: string;
    screenerResponseId: string;
  };
  query?: never;
  url: '/v4/projects/{projectId}/screener-responses/{screenerResponseId}/schedule-external';
};

export type ScreenerResponsesControllerScheduleExternalResponses = {
  200: ScreenerResponse;
};

export type ScreenerResponsesControllerScheduleExternalResponse =
  ScreenerResponsesControllerScheduleExternalResponses[keyof ScreenerResponsesControllerScheduleExternalResponses];

export type ScreenerResponsesControllerMarkAttendedData = {
  body?: never;
  path: {
    projectId: string;
    screenerResponseId: string;
  };
  query?: never;
  url: '/v4/projects/{projectId}/screener-responses/{screenerResponseId}/attended';
};

export type ScreenerResponsesControllerMarkAttendedResponses = {
  200: ScreenerResponse;
};

export type ScreenerResponsesControllerMarkAttendedResponse =
  ScreenerResponsesControllerMarkAttendedResponses[keyof ScreenerResponsesControllerMarkAttendedResponses];

export type ScreenerResponsesControllerCancelBookingAndReinviteData = {
  body: CancelBookingReinviteDto;
  path: {
    projectId: string;
    screenerResponseId: string;
  };
  query?: never;
  url: '/v4/projects/{projectId}/screener-responses/{screenerResponseId}/cancel-booking-reinvite';
};

export type ScreenerResponsesControllerCancelBookingAndReinviteResponses = {
  200: ScreenerResponse;
};

export type ScreenerResponsesControllerCancelBookingAndReinviteResponse =
  ScreenerResponsesControllerCancelBookingAndReinviteResponses[keyof ScreenerResponsesControllerCancelBookingAndReinviteResponses];

export type ScreenerResponsesControllerAutocompleteData = {
  body: AutocompleteDto;
  path: {
    projectId: string;
    screenerResponseId: string;
  };
  query?: never;
  url: '/v4/projects/{projectId}/screener-responses/{screenerResponseId}/autocomplete';
};

export type ScreenerResponsesControllerAutocompleteResponses = {
  200: unknown;
};

export type ScreenerResponsesControllerMarkNoShowData = {
  body?: never;
  path: {
    projectId: string;
    screenerResponseId: string;
  };
  query?: never;
  url: '/v4/projects/{projectId}/screener-responses/{screenerResponseId}/no-show';
};

export type ScreenerResponsesControllerMarkNoShowResponses = {
  200: ScreenerResponse;
};

export type ScreenerResponsesControllerMarkNoShowResponse =
  ScreenerResponsesControllerMarkNoShowResponses[keyof ScreenerResponsesControllerMarkNoShowResponses];

export type ScreenerResponsesControllerEnableRescheduleData = {
  body?: never;
  path: {
    projectId: string;
    screenerResponseId: string;
  };
  query?: never;
  url: '/v4/projects/{projectId}/screener-responses/{screenerResponseId}/enable-reschedule';
};

export type ScreenerResponsesControllerEnableRescheduleResponses = {
  200: ScreenerResponse;
};

export type ScreenerResponsesControllerEnableRescheduleResponse =
  ScreenerResponsesControllerEnableRescheduleResponses[keyof ScreenerResponsesControllerEnableRescheduleResponses];

export type ScreenerResponsesControllerRejectedData = {
  body: RejectScreenerResponseDto;
  path: {
    projectId: string;
    screenerResponseId: string;
  };
  query?: never;
  url: '/v4/projects/{projectId}/screener-responses/{screenerResponseId}/reject';
};

export type ScreenerResponsesControllerRejectedResponses = {
  200: ScreenerResponse;
};

export type ScreenerResponsesControllerRejectedResponse =
  ScreenerResponsesControllerRejectedResponses[keyof ScreenerResponsesControllerRejectedResponses];

export type ScreenerResponsesControllerCancelInviteData = {
  body?: never;
  path: {
    projectId: string;
    screenerResponseId: string;
  };
  query?: never;
  url: '/v4/projects/{projectId}/screener-responses/{screenerResponseId}/cancel-invite';
};

export type ScreenerResponsesControllerCancelInviteResponses = {
  200: ScreenerResponse;
};

export type ScreenerResponsesControllerCancelInviteResponse =
  ScreenerResponsesControllerCancelInviteResponses[keyof ScreenerResponsesControllerCancelInviteResponses];

export type ScreenerResponsesControllerCancelBookingData = {
  body?: never;
  path: {
    projectId: string;
    screenerResponseId: string;
  };
  query?: never;
  url: '/v4/projects/{projectId}/screener-responses/{screenerResponseId}/cancel-booking';
};

export type ScreenerResponsesControllerCancelBookingResponses = {
  200: ScreenerResponse;
};

export type ScreenerResponsesControllerCancelBookingResponse =
  ScreenerResponsesControllerCancelBookingResponses[keyof ScreenerResponsesControllerCancelBookingResponses];

export type ScreenerResponsesControllerParticipantCancelBookingData = {
  body: ParticipantCancelBookingDto;
  path: {
    projectId: string;
    screenerResponseId: string;
  };
  query?: never;
  url: '/v4/projects/{projectId}/screener-responses/{screenerResponseId}/participant-cancel-booking';
};

export type ScreenerResponsesControllerParticipantCancelBookingResponses = {
  200: ScreenerResponse;
};

export type ScreenerResponsesControllerParticipantCancelBookingResponse =
  ScreenerResponsesControllerParticipantCancelBookingResponses[keyof ScreenerResponsesControllerParticipantCancelBookingResponses];

export type ScreenerResponsesControllerParticipantCancelBookingExternalData = {
  body: ParticipantCancelBookingDto;
  path: {
    projectId: string;
    screenerResponseId: string;
  };
  query?: never;
  url: '/v4/projects/{projectId}/screener-responses/{screenerResponseId}/participant-cancel-booking/external';
};

export type ScreenerResponsesControllerParticipantCancelBookingExternalResponses =
  {
    200: ScreenerResponse;
  };

export type ScreenerResponsesControllerParticipantCancelBookingExternalResponse =
  ScreenerResponsesControllerParticipantCancelBookingExternalResponses[keyof ScreenerResponsesControllerParticipantCancelBookingExternalResponses];

export type ScreenerResponsesControllerInsertEmailEventData = {
  body: InsertEmailEventDto;
  path: {
    projectId: string;
    screenerResponseId: string;
  };
  query?: never;
  url: '/v4/projects/{projectId}/screener-responses/{screenerResponseId}/email-events';
};

export type ScreenerResponsesControllerInsertEmailEventResponses = {
  201: ScreenerResponse;
};

export type ScreenerResponsesControllerInsertEmailEventResponse =
  ScreenerResponsesControllerInsertEmailEventResponses[keyof ScreenerResponsesControllerInsertEmailEventResponses];

export type ScreenerResponsesControllerInsertTaskReminderEmailEventData = {
  body: InsertTaskReminderEmailEventDto;
  path: {
    projectId: string;
    screenerResponseId: string;
  };
  query?: never;
  url: '/v4/projects/{projectId}/screener-responses/{screenerResponseId}/task-reminder-email-events';
};

export type ScreenerResponsesControllerInsertTaskReminderEmailEventResponses = {
  201: ScreenerResponse;
};

export type ScreenerResponsesControllerInsertTaskReminderEmailEventResponse =
  ScreenerResponsesControllerInsertTaskReminderEmailEventResponses[keyof ScreenerResponsesControllerInsertTaskReminderEmailEventResponses];

export type ScreenerFileUploadsControllerInitiateData = {
  body: InitiateScreenerFileUploadDto;
  path?: never;
  query?: never;
  url: '/v4/participant/screener-file-uploads/initiate';
};

export type ScreenerFileUploadsControllerInitiateErrors = {
  401: unknown;
};

export type ScreenerFileUploadsControllerInitiateResponses = {
  200: InitiateScreenerFileUploadResponseDto;
};

export type ScreenerFileUploadsControllerInitiateResponse =
  ScreenerFileUploadsControllerInitiateResponses[keyof ScreenerFileUploadsControllerInitiateResponses];

export type ScreenerFileUploadsControllerUploadPartData = {
  body: UploadScreenerFilePartDto;
  path?: never;
  query?: never;
  url: '/v4/participant/screener-file-uploads/upload-part';
};

export type ScreenerFileUploadsControllerUploadPartErrors = {
  401: unknown;
};

export type ScreenerFileUploadsControllerUploadPartResponses = {
  200: UploadScreenerFilePartResponseDto;
};

export type ScreenerFileUploadsControllerUploadPartResponse =
  ScreenerFileUploadsControllerUploadPartResponses[keyof ScreenerFileUploadsControllerUploadPartResponses];

export type ScreenerFileUploadsControllerCompleteData = {
  body: CompleteScreenerFileUploadDto;
  path?: never;
  query?: never;
  url: '/v4/participant/screener-file-uploads/complete';
};

export type ScreenerFileUploadsControllerCompleteErrors = {
  401: unknown;
};

export type ScreenerFileUploadsControllerCompleteResponses = {
  200: CompleteScreenerFileUploadResponseDto;
};

export type ScreenerFileUploadsControllerCompleteResponse =
  ScreenerFileUploadsControllerCompleteResponses[keyof ScreenerFileUploadsControllerCompleteResponses];

export type ScreenerFileUploadsControllerAbortData = {
  body: AbortScreenerFileUploadDto;
  path?: never;
  query?: never;
  url: '/v4/participant/screener-file-uploads/abort';
};

export type ScreenerFileUploadsControllerAbortErrors = {
  401: unknown;
};

export type ScreenerFileUploadsControllerAbortResponses = {
  204: void;
};

export type ScreenerFileUploadsControllerAbortResponse =
  ScreenerFileUploadsControllerAbortResponses[keyof ScreenerFileUploadsControllerAbortResponses];

export type FraudSignalsControllerCreateData = {
  body: CreateFraudSignalsDto;
  path?: never;
  query?: never;
  url: '/v4/fraud-signals';
};

export type FraudSignalsControllerCreateResponses = {
  201: Array<FraudSignal>;
};

export type FraudSignalsControllerCreateResponse =
  FraudSignalsControllerCreateResponses[keyof FraudSignalsControllerCreateResponses];

export type IntegrationsControllerCreateTaskForParticipantData = {
  body: CreateParticipantTaskDto;
  path: {
    id: string;
  };
  query?: never;
  url: '/v4/integrations/survey-responses/{id}/task';
};

export type IntegrationsControllerCreateTaskForParticipantResponses = {
  201: Task;
};

export type IntegrationsControllerCreateTaskForParticipantResponse =
  IntegrationsControllerCreateTaskForParticipantResponses[keyof IntegrationsControllerCreateTaskForParticipantResponses];

export type IntegrationsControllerUpdateTaskOnProjectData = {
  body: UpdateTaskDto;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/integrations/projects/{projectId}/task';
};

export type IntegrationsControllerUpdateTaskOnProjectResponses = {
  200: ProjectLegacy;
};

export type IntegrationsControllerUpdateTaskOnProjectResponse =
  IntegrationsControllerUpdateTaskOnProjectResponses[keyof IntegrationsControllerUpdateTaskOnProjectResponses];

export type IntegrationsControllerUpdateTaskOnSurveyResponseData = {
  body: UpdateTaskDto;
  path: {
    id: string;
  };
  query?: never;
  url: '/v4/integrations/survey-responses/tasks/{id}';
};

export type IntegrationsControllerUpdateTaskOnSurveyResponseErrors = {
  /**
   * Task was not modified due to a conflict - most likely the task has already been updated
   */
  409: unknown;
};

export type IntegrationsControllerUpdateTaskOnSurveyResponseResponses = {
  200: SurveyResponse;
};

export type IntegrationsControllerUpdateTaskOnSurveyResponseResponse =
  IntegrationsControllerUpdateTaskOnSurveyResponseResponses[keyof IntegrationsControllerUpdateTaskOnSurveyResponseResponses];

export type IntegrationsControllerFindAllSurveyResponsesWithTasksForProjectData =
  {
    body?: never;
    path: {
      id: string;
    };
    query: {
      type: TaskType;
      status: TaskStatus;
    };
    url: '/v4/integrations/projects/{id}/survey-responses';
  };

export type IntegrationsControllerFindAllSurveyResponsesWithTasksForProjectResponses =
  {
    200: Array<SurveyResponse>;
  };

export type IntegrationsControllerFindAllSurveyResponsesWithTasksForProjectResponse =
  IntegrationsControllerFindAllSurveyResponsesWithTasksForProjectResponses[keyof IntegrationsControllerFindAllSurveyResponsesWithTasksForProjectResponses];

export type IntegrationsControllerDownloadCompletesData = {
  body?: never;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/integrations/project/{projectId}/survey-responses/completes/download';
};

export type IntegrationsControllerDownloadCompletesResponses = {
  200: unknown;
};

export type IntegrationsControllerCreateIntegrationData = {
  body: CreateIntegrationDto;
  path: {
    id: string;
  };
  query?: never;
  url: '/v4/integrations/teams/{id}';
};

export type IntegrationsControllerCreateIntegrationResponses = {
  201: Integration;
};

export type IntegrationsControllerCreateIntegrationResponse =
  IntegrationsControllerCreateIntegrationResponses[keyof IntegrationsControllerCreateIntegrationResponses];

export type IntegrationsControllerDeleteIntegrationData = {
  body?: never;
  path: {
    id: string;
    type: string;
  };
  query?: never;
  url: '/v4/integrations/teams/{id}/{type}';
};

export type IntegrationsControllerDeleteIntegrationResponses = {
  200: Integration;
};

export type IntegrationsControllerDeleteIntegrationResponse =
  IntegrationsControllerDeleteIntegrationResponses[keyof IntegrationsControllerDeleteIntegrationResponses];

export type IntegrationsControllerMarkTaskAsCompleteByProjectCompletionIdData =
  {
    body?: never;
    path: {
      projectCompletionId: string;
    };
    query?: never;
    url: '/v4/integrations/survey-responses/tasks/{projectCompletionId}/complete';
  };

export type IntegrationsControllerMarkTaskAsCompleteByProjectCompletionIdResponses =
  {
    200: ScreenerResponse;
  };

export type IntegrationsControllerMarkTaskAsCompleteByProjectCompletionIdResponse =
  IntegrationsControllerMarkTaskAsCompleteByProjectCompletionIdResponses[keyof IntegrationsControllerMarkTaskAsCompleteByProjectCompletionIdResponses];

export type IntegrationsControllerFindAllSurveysForIntegrationData = {
  body?: never;
  path: {
    id: string;
  };
  query: {
    type: string;
  };
  url: '/v4/integrations/teams/{id}/surveys';
};

export type IntegrationsControllerFindAllSurveysForIntegrationResponses = {
  200: SurveyListDto;
};

export type IntegrationsControllerFindAllSurveysForIntegrationResponse =
  IntegrationsControllerFindAllSurveysForIntegrationResponses[keyof IntegrationsControllerFindAllSurveysForIntegrationResponses];

export type IntegrationsControllerGetSurveyResponseDetailsData = {
  body?: never;
  path: {
    taskType: string;
    projectId: string;
    collectorId: string;
    responseId: string;
  };
  query?: never;
  url: '/v4/integrations/{taskType}/projects/{projectId}/collectors/{collectorId}/responses/{responseId}';
};

export type IntegrationsControllerGetSurveyResponseDetailsResponses = {
  200: SurveyResponseDetailsDto;
};

export type IntegrationsControllerGetSurveyResponseDetailsResponse =
  IntegrationsControllerGetSurveyResponseDetailsResponses[keyof IntegrationsControllerGetSurveyResponseDetailsResponses];

export type NotificationsControllerGetEmailPayloadByStatusData = {
  body?: never;
  path: {
    status: string;
  };
  query: {
    limit: number;
  };
  url: '/v4/notifications/email-payload/{status}';
};

export type NotificationsControllerGetEmailPayloadByStatusResponses = {
  200: Array<NotificationEmailPayloadDto>;
};

export type NotificationsControllerGetEmailPayloadByStatusResponse =
  NotificationsControllerGetEmailPayloadByStatusResponses[keyof NotificationsControllerGetEmailPayloadByStatusResponses];

export type NotificationsControllerUpdateStatusByIdData = {
  body: NotificationUpdateDto;
  path: {
    notificationId: string;
  };
  query?: never;
  url: '/v4/notifications/{notificationId}';
};

export type NotificationsControllerUpdateStatusByIdResponses = {
  200: Notification;
};

export type NotificationsControllerUpdateStatusByIdResponse =
  NotificationsControllerUpdateStatusByIdResponses[keyof NotificationsControllerUpdateStatusByIdResponses];

export type NotificationsControllerCancelPendingTaskCompletionRemindersData = {
  body?: never;
  path: {
    projectId: string;
    screenerResponseId: string;
  };
  query?: never;
  url: '/v4/notifications/projects/{projectId}/screener-reponses/{screenerResponseId}';
};

export type NotificationsControllerCancelPendingTaskCompletionRemindersResponses =
  {
    200: Array<{
      [key: string]: unknown;
    }>;
  };

export type NotificationsControllerCancelPendingTaskCompletionRemindersResponse =
  NotificationsControllerCancelPendingTaskCompletionRemindersResponses[keyof NotificationsControllerCancelPendingTaskCompletionRemindersResponses];

export type QuotasControllerDeleteData = {
  body?: never;
  path: {
    projectId: string;
  };
  query?: {
    /**
     * The quota `configVersion` the client loaded, unchanged (`none` for no quota). When the stored one differs, the write is refused with a 409 whose `reason` is `QUOTA_CONFIG_VERSION_CONFLICT`. Omit for last-write-wins.
     */
    expectedConfigVersion?: string;
  };
  url: '/v4/projects/{projectId}/quotas';
};

export type QuotasControllerDeleteErrors = {
  /**
   * The project still has a V1 quota, or `expectedConfigVersion` no longer matches (`reason: QUOTA_CONFIG_VERSION_CONFLICT`)
   */
  409: unknown;
};

export type QuotasControllerDeleteResponses = {
  204: void;
};

export type QuotasControllerDeleteResponse =
  QuotasControllerDeleteResponses[keyof QuotasControllerDeleteResponses];

export type QuotasControllerGetData = {
  body?: never;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/projects/{projectId}/quotas';
};

export type QuotasControllerGetErrors = {
  /**
   * Project not found
   */
  404: unknown;
};

export type QuotasControllerGetResponses = {
  /**
   * The V2 configuration with progress, the empty configuration when the project has none, or the V1 quota while one still exists
   */
  200: QuotaV2Dto | QuotaV1Dto;
};

export type QuotasControllerGetResponse =
  QuotasControllerGetResponses[keyof QuotasControllerGetResponses];

export type QuotasControllerUpsertData = {
  body: UpsertQuotaDtoWritable;
  path: {
    projectId: string;
  };
  query?: {
    /**
     * The quota `configVersion` the client loaded, unchanged (`none` for no quota). When the stored one differs, the write is refused with a 409 whose `reason` is `QUOTA_CONFIG_VERSION_CONFLICT`. Omit for last-write-wins.
     */
    expectedConfigVersion?: string;
  };
  url: '/v4/projects/{projectId}/quotas';
};

export type QuotasControllerUpsertErrors = {
  /**
   * Invalid configuration; the body lists every error
   */
  400: unknown;
  /**
   * Quotas V2 is not enabled for the organization
   */
  403: unknown;
  /**
   * Project not found
   */
  404: unknown;
  /**
   * The project still has a V1 quota, or `expectedConfigVersion` no longer matches (`reason: QUOTA_CONFIG_VERSION_CONFLICT`)
   */
  409: unknown;
};

export type QuotasControllerUpsertResponses = {
  200: QuotaV2UpsertResultDto;
};

export type QuotasControllerUpsertResponse =
  QuotasControllerUpsertResponses[keyof QuotasControllerUpsertResponses];

export type QuotasControllerDeleteQuotaData = {
  body?: never;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/projects/{projectId}/quota';
};

export type QuotasControllerDeleteQuotaErrors = {
  /**
   * V1 quota on a project that is not a draft, or the caller is not on the project team
   */
  400: unknown;
};

export type QuotasControllerDeleteQuotaResponses = {
  /**
   * The stored quota was removed, or the project had no quota. A V2 quota is removed with no flag check, including after publish. A V1 quota is removed only while the project is a draft.
   */
  204: void;
};

export type QuotasControllerDeleteQuotaResponse =
  QuotasControllerDeleteQuotaResponses[keyof QuotasControllerDeleteQuotaResponses];

export type QuotaControllerGetQuotaByProjectIdData = {
  body?: never;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/projects/{projectId}/quota';
};

export type QuotaControllerGetQuotaByProjectIdResponses = {
  /**
   * Quota retrieved successfully
   */
  200: QuotaV1Dto;
  /**
   * No quota exists for this project
   */
  204: void;
};

export type QuotaControllerGetQuotaByProjectIdResponse =
  QuotaControllerGetQuotaByProjectIdResponses[keyof QuotaControllerGetQuotaByProjectIdResponses];

export type QuotaControllerUpdateQuotaData = {
  body: UpdateQuotaV1Dto;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/projects/{projectId}/quota';
};

export type QuotaControllerUpdateQuotaErrors = {
  /**
   * Invalid quota criteria or segments, Project not in a valid state
   */
  400: unknown;
  /**
   * Quota not found
   */
  404: unknown;
  /**
   * Internal server error
   */
  500: unknown;
};

export type QuotaControllerUpdateQuotaResponses = {
  /**
   * Quota updated successfully
   */
  200: QuotaV1Dto;
};

export type QuotaControllerUpdateQuotaResponse =
  QuotaControllerUpdateQuotaResponses[keyof QuotaControllerUpdateQuotaResponses];

export type QuotaControllerCreateQuotaData = {
  body: CreateQuotaV1Dto;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/projects/{projectId}/quota';
};

export type QuotaControllerCreateQuotaErrors = {
  /**
   * Invalid quota criteria or segments, Project not in a valid state
   */
  400: unknown;
  /**
   * Project not found
   */
  404: unknown;
  /**
   * Internal server error
   */
  500: unknown;
};

export type QuotaControllerCreateQuotaResponses = {
  /**
   * Quota created successfully
   */
  201: QuotaV1Dto;
};

export type QuotaControllerCreateQuotaResponse =
  QuotaControllerCreateQuotaResponses[keyof QuotaControllerCreateQuotaResponses];

export type QuotasControllerUpsert2Data = {
  body: UpsertQuotaDtoWritable;
  path: {
    projectId: string;
  };
  query?: {
    /**
     * The quota `configVersion` the client loaded, unchanged (`none` for no quota). When the stored one differs, the write is refused with a 409 whose `reason` is `QUOTA_CONFIG_VERSION_CONFLICT`. Omit for last-write-wins.
     */
    expectedConfigVersion?: string;
  };
  url: '/v4/projects/{projectId}/quota';
};

export type QuotasControllerUpsert2Errors = {
  /**
   * Invalid configuration; the body lists every error
   */
  400: unknown;
  /**
   * Quotas V2 is not enabled for the organization
   */
  403: unknown;
  /**
   * Project not found
   */
  404: unknown;
  /**
   * The project still has a V1 quota, or `expectedConfigVersion` no longer matches (`reason: QUOTA_CONFIG_VERSION_CONFLICT`)
   */
  409: unknown;
};

export type QuotasControllerUpsert2Responses = {
  200: QuotaV2UpsertResultDto;
};

export type QuotasControllerUpsert2Response =
  QuotasControllerUpsert2Responses[keyof QuotasControllerUpsert2Responses];

export type QuotasControllerPatchTargetsData = {
  body: Array<QuotaV2TargetEntryDto>;
  path: {
    projectId: string;
  };
  query?: {
    /**
     * The quota `configVersion` the client loaded, unchanged (`none` for no quota). When the stored one differs, the write is refused with a 409 whose `reason` is `QUOTA_CONFIG_VERSION_CONFLICT`. Omit for last-write-wins.
     */
    expectedConfigVersion?: string;
  };
  url: '/v4/projects/{projectId}/quotas/targets';
};

export type QuotasControllerPatchTargetsErrors = {
  /**
   * A duplicate or unknown bucket, or a target the configuration cannot take
   */
  400: unknown;
  /**
   * The project still has a V1 quota, or `expectedConfigVersion` no longer matches (`reason: QUOTA_CONFIG_VERSION_CONFLICT`)
   */
  409: unknown;
};

export type QuotasControllerPatchTargetsResponses = {
  200: QuotaV2Dto;
};

export type QuotasControllerPatchTargetsResponse =
  QuotasControllerPatchTargetsResponses[keyof QuotasControllerPatchTargetsResponses];

export type QuotasControllerPatchTargets2Data = {
  body: Array<QuotaV2TargetEntryDto>;
  path: {
    projectId: string;
  };
  query?: {
    /**
     * The quota `configVersion` the client loaded, unchanged (`none` for no quota). When the stored one differs, the write is refused with a 409 whose `reason` is `QUOTA_CONFIG_VERSION_CONFLICT`. Omit for last-write-wins.
     */
    expectedConfigVersion?: string;
  };
  url: '/v4/projects/{projectId}/quota/targets';
};

export type QuotasControllerPatchTargets2Errors = {
  /**
   * A duplicate or unknown bucket, or a target the configuration cannot take
   */
  400: unknown;
  /**
   * The project still has a V1 quota, or `expectedConfigVersion` no longer matches (`reason: QUOTA_CONFIG_VERSION_CONFLICT`)
   */
  409: unknown;
};

export type QuotasControllerPatchTargets2Responses = {
  200: QuotaV2Dto;
};

export type QuotasControllerPatchTargets2Response =
  QuotasControllerPatchTargets2Responses[keyof QuotasControllerPatchTargets2Responses];

export type QuotasControllerSetExternalFillsData = {
  body: Array<QuotaV2ExternalFillsEntryDto>;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/projects/{projectId}/quotas/external-fills';
};

export type QuotasControllerSetExternalFillsErrors = {
  /**
   * A duplicate or unknown bucket
   */
  400: unknown;
  /**
   * The project still has a V1 quota
   */
  409: unknown;
};

export type QuotasControllerSetExternalFillsResponses = {
  200: QuotaV2Dto;
};

export type QuotasControllerSetExternalFillsResponse =
  QuotasControllerSetExternalFillsResponses[keyof QuotasControllerSetExternalFillsResponses];

export type QuotasControllerSetExternalFills2Data = {
  body: Array<QuotaV2ExternalFillsEntryDto>;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/projects/{projectId}/quota/external-fills';
};

export type QuotasControllerSetExternalFills2Errors = {
  /**
   * A duplicate or unknown bucket
   */
  400: unknown;
  /**
   * The project still has a V1 quota
   */
  409: unknown;
};

export type QuotasControllerSetExternalFills2Responses = {
  200: QuotaV2Dto;
};

export type QuotasControllerSetExternalFills2Response =
  QuotasControllerSetExternalFills2Responses[keyof QuotasControllerSetExternalFills2Responses];

export type JobTitlesControllerGroupingCoverageData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v4/job-titles/grouping/coverage';
};

export type JobTitlesControllerGroupingCoverageResponses = {
  200: JobTitleGroupingCoverageDto;
};

export type JobTitlesControllerGroupingCoverageResponse =
  JobTitlesControllerGroupingCoverageResponses[keyof JobTitlesControllerGroupingCoverageResponses];

export type JobTitlesControllerFindAllData = {
  body?: never;
  path?: never;
  query?: {
    page?: number;
    pageSize?: number;
    includeCount?: boolean;
    /**
     * Character Limit: min 3 and max 100
     */
    query?: string;
    modifiedSince?: string;
    /**
     * Filter job titles by seniority levels.
     * Returns job titles that have at least one of the specified seniority levels.
     *
     * Behavior depends on `includeHealthcare`:
     * - `includeHealthcare=undefined`: Seniority filter applies to ALL job titles (including healthcare)
     * - `includeHealthcare=true`: Healthcare providers bypass seniority filter (always included)
     * - `includeHealthcare=false`: Healthcare providers excluded entirely, seniority applies to rest
     */
    seniority?: Array<SeniorityLevel>;
  };
  url: '/v4/job-titles';
};

export type JobTitlesControllerFindAllResponses = {
  200: SimpleJobTitlesPaginatedDto;
};

export type JobTitlesControllerFindAllResponse =
  JobTitlesControllerFindAllResponses[keyof JobTitlesControllerFindAllResponses];

export type JobTitlesControllerFindOneData = {
  body?: never;
  path: {
    id: string;
  };
  query?: never;
  url: '/v4/job-titles/{id}';
};

export type JobTitlesControllerFindOneResponses = {
  200: JobTitle;
};

export type JobTitlesControllerFindOneResponse =
  JobTitlesControllerFindOneResponses[keyof JobTitlesControllerFindOneResponses];

export type InvitationsControllerFindAllData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v4/invitations';
};

export type InvitationsControllerFindAllResponses = {
  200: unknown;
};

export type InvitationsControllerCreateData = {
  body: CreateInvitationDto;
  path?: never;
  query?: never;
  url: '/v4/invitations';
};

export type InvitationsControllerCreateResponses = {
  201: Invitation;
};

export type InvitationsControllerCreateResponse =
  InvitationsControllerCreateResponses[keyof InvitationsControllerCreateResponses];

export type InvitationsControllerFindPendingForProjectData = {
  body?: never;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/projects/{projectId}/invitations';
};

export type InvitationsControllerFindPendingForProjectResponses = {
  200: Array<ProjectInvitationDto>;
};

export type InvitationsControllerFindPendingForProjectResponse =
  InvitationsControllerFindPendingForProjectResponses[keyof InvitationsControllerFindPendingForProjectResponses];

export type InvitationsControllerRemoveData = {
  body?: never;
  path: {
    id: string;
  };
  query?: never;
  url: '/v4/invitations/{id}';
};

export type InvitationsControllerRemoveResponses = {
  200: unknown;
};

export type InvitationsControllerFindOneData = {
  body?: never;
  path: {
    id: string;
  };
  query?: never;
  url: '/v4/invitations/{id}';
};

export type InvitationsControllerFindOneResponses = {
  200: Invitation;
};

export type InvitationsControllerFindOneResponse =
  InvitationsControllerFindOneResponses[keyof InvitationsControllerFindOneResponses];

export type InvitationsControllerUpdateData = {
  body: UpdateInvitationDto;
  path: {
    id: string;
  };
  query?: never;
  url: '/v4/invitations/{id}';
};

export type InvitationsControllerUpdateResponses = {
  200: unknown;
};

export type InvitationsControllerFindPendingForParticipantData = {
  body?: never;
  path: {
    userId: string;
  };
  query?: never;
  url: '/v4/participant/projects/{userId}/invitations';
};

export type InvitationsControllerFindPendingForParticipantResponses = {
  200: Array<ParticipantInvitationDto>;
};

export type InvitationsControllerFindPendingForParticipantResponse =
  InvitationsControllerFindPendingForParticipantResponses[keyof InvitationsControllerFindPendingForParticipantResponses];

export type MatchingControllerProjectFeasibilityData = {
  body: GetFeasibilityScoreDetailsDto;
  path?: never;
  query?: never;
  url: '/v4/matching/projects/feasibility';
};

export type MatchingControllerProjectFeasibilityResponses = {
  200: FeasibilityResultsDto;
};

export type MatchingControllerProjectFeasibilityResponse =
  MatchingControllerProjectFeasibilityResponses[keyof MatchingControllerProjectFeasibilityResponses];

export type MatchingControllerCalculateB2bFeasibilityByAttributesData = {
  body: CalculateFeasibilityB2bDto;
  path?: never;
  query?: never;
  url: '/v4/matching/projects/feasibility-attributes/b2b';
};

export type MatchingControllerCalculateB2bFeasibilityByAttributesErrors = {
  /**
   * Invalid request parameters
   */
  400: unknown;
  /**
   * Unauthorized
   */
  401: unknown;
  /**
   * Internal server error
   */
  500: unknown;
};

export type MatchingControllerCalculateB2bFeasibilityByAttributesResponses = {
  /**
   * Feasibility count calculated successfully
   */
  200: FeasibilityCountResponseDto;
};

export type MatchingControllerCalculateB2bFeasibilityByAttributesResponse =
  MatchingControllerCalculateB2bFeasibilityByAttributesResponses[keyof MatchingControllerCalculateB2bFeasibilityByAttributesResponses];

export type MatchingControllerCalculateB2cFeasibilityByAttributesData = {
  body: CalculateFeasibilityB2cDto;
  path?: never;
  query?: never;
  url: '/v4/matching/projects/feasibility-attributes/b2c';
};

export type MatchingControllerCalculateB2cFeasibilityByAttributesErrors = {
  /**
   * Invalid request parameters
   */
  400: unknown;
  /**
   * Unauthorized
   */
  401: unknown;
  /**
   * Internal server error
   */
  500: unknown;
};

export type MatchingControllerCalculateB2cFeasibilityByAttributesResponses = {
  /**
   * Feasibility count calculated successfully
   */
  200: FeasibilityCountResponseDto;
};

export type MatchingControllerCalculateB2cFeasibilityByAttributesResponse =
  MatchingControllerCalculateB2cFeasibilityByAttributesResponses[keyof MatchingControllerCalculateB2cFeasibilityByAttributesResponses];

export type MatchingControllerGetExistingScoringModifiersData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v4/matching/scoring-modifiers';
};

export type MatchingControllerGetExistingScoringModifiersResponses = {
  200: ScoringModifiersDto;
};

export type MatchingControllerGetExistingScoringModifiersResponse =
  MatchingControllerGetExistingScoringModifiersResponses[keyof MatchingControllerGetExistingScoringModifiersResponses];

export type MatchingControllerFindMatchedProjectsNoAuthData = {
  body?: never;
  path?: never;
  query?: {
    page?: number;
    pageSize?: number;
    includeCount?: boolean;
    /**
     * [
     *
     * ONE_ON_ONE: 1
     *
     * FOCUS_GROUP: 2
     *
     * MULTI_DAY_STUDY: 3
     *
     * UNMODERATED_STUDY: 4
     *
     * PHONE_INTERVIEW: 5
     *
     * USABILITY_TESTING: 6
     *
     * IN_RESPONDENT_HOME: 8
     *
     * SURVEY: 9
     *
     * DIARY_STUDY: 10
     *
     * IN_RESPONDENT_OFFICE: 11
     *
     * AI_MODERATED_INTERVIEW: 12
     *
     * ]
     */
    kindsOfResearch?: Array<KindOfResearch>;
    /**
     * [
     *
     * B2B: 1
     *
     * B2C: 2
     *
     * ]
     */
    talkToAudience?: ParticipantAudienceType;
    /**
     * [
     *
     * IN_PERSON: 1
     *
     * REMOTE: 2
     *
     * ]
     */
    researchConductType?: ResearchConductType;
    minIncentive?: number;
    maxIncentive?: number;
    minTimeMinutesRequired?: number;
    maxTimeMinutesRequired?: number;
    sort?: RespondentDashboardSort;
    onlyShowMatched?: boolean;
    showHiddenProjects?: boolean;
    queryText?: string;
    /**
     * if showEligible is true, then dataOfBirth, gender, educationLevel, ethnicity, country are required
     */
    showEligible?: boolean;
    dateOfBirth?: string;
    gender?: Gender;
    educationLevel?: EducationLevel;
    ethnicity?: Ethnicities;
    country?: ProjectCountryCode;
  };
  url: '/v4/matching/projects/search';
};

export type MatchingControllerFindMatchedProjectsNoAuthResponses = {
  200: MatchingProjectsPaginatedDto;
};

export type MatchingControllerFindMatchedProjectsNoAuthResponse =
  MatchingControllerFindMatchedProjectsNoAuthResponses[keyof MatchingControllerFindMatchedProjectsNoAuthResponses];

export type MatchingControllerPrequalifiesForProjectData = {
  body?: never;
  path: {
    projectId: string;
    profileId: string;
  };
  query?: never;
  url: '/v4/matching/projects/{projectId}/qualify/profiles/{profileId}';
};

export type MatchingControllerPrequalifiesForProjectResponses = {
  200: LegacyPrequalifiesForProjectResultsDto;
};

export type MatchingControllerPrequalifiesForProjectResponse =
  MatchingControllerPrequalifiesForProjectResponses[keyof MatchingControllerPrequalifiesForProjectResponses];

export type MatchingControllerProjectAudienceSizeEstimateData = {
  body?: never;
  path: {
    projectId: string;
  };
  query?: {
    /**
     * If true, the audience size estimate will be updated on the project.
     */
    ifUpdate?: boolean;
    /**
     * If true, `minStrict` will be calculated.
     */
    getMinStrict?: boolean;
    /**
     * If true, `maxStrict` will be calculated.
     */
    getMaxStrict?: boolean;
    /**
     * If true, `maxMatching` will be calculated.
     */
    getMaxMatching?: boolean;
    /**
     * If true, `minMatching` will be calculated.
     */
    getMinMatching?: boolean;
  };
  url: '/v4/matching/projects/{projectId}/feasibility/audience-size-estimate';
};

export type MatchingControllerProjectAudienceSizeEstimateResponses = {
  200: AudienceSizeEstimate;
};

export type MatchingControllerProjectAudienceSizeEstimateResponse =
  MatchingControllerProjectAudienceSizeEstimateResponses[keyof MatchingControllerProjectAudienceSizeEstimateResponses];

export type MatchingControllerUpsertProjectScoreData = {
  body?: never;
  path: {
    projectId: string;
  };
  query: {
    type: string;
  };
  url: '/v4/matching/projects/{projectId}';
};

export type MatchingControllerUpsertProjectScoreResponses = {
  200: unknown;
};

export type MatchingControllerUpsertProjectScoresFromProfileIdData = {
  body: UpsertProfileMatchingScoresDto;
  path: {
    profileId: string;
  };
  query?: never;
  url: '/v4/matching/profiles/{profileId}';
};

export type MatchingControllerUpsertProjectScoresFromProfileIdResponses = {
  200: unknown;
};

export type MatchingControllerHandleProfileProjectScoreAndUpsertData = {
  body?: never;
  path: {
    projectId: string;
    profileId: string;
  };
  query?: never;
  url: '/v4/matching/profiles/{profileId}/projects/{projectId}';
};

export type MatchingControllerHandleProfileProjectScoreAndUpsertResponses = {
  200: unknown;
};

export type MatchingControllerUpsertProjectScoreByProfileData = {
  body: UpdateMatchingProjectProfileDto;
  path: {
    projectId: string;
    profileId: string;
  };
  query?: never;
  url: '/v4/matching/projects/{projectId}/profiles/{profileId}';
};

export type MatchingControllerUpsertProjectScoreByProfileResponses = {
  200: unknown;
};

export type MatchingControllerUpsertProfileProjectScoresData = {
  body: UpsertProfileProjectScoresDto;
  path?: never;
  query?: never;
  url: '/v4/matching/profile-project-matches/scores';
};

export type MatchingControllerUpsertProfileProjectScoresResponses = {
  200: unknown;
};

export type MatchingControllerGetTopMatchedProfilesData = {
  body?: never;
  path: {
    projectId: string;
  };
  query: {
    limit: number;
    /**
     * Cursor to resume a ranked fetch (sorted v4Score desc, _id asc).
     * Both cursor fields must be provided together.
     */
    afterV4Score?: number;
    afterProfileMatchId?: string;
  };
  url: '/v4/matching/projects/{projectId}/top-matched';
};

export type MatchingControllerGetTopMatchedProfilesResponses = {
  200: Array<ProfileProjectMatch>;
};

export type MatchingControllerGetTopMatchedProfilesResponse =
  MatchingControllerGetTopMatchedProfilesResponses[keyof MatchingControllerGetTopMatchedProfilesResponses];

export type MatchingControllerGetRecontactProfilesData = {
  body?: never;
  path: {
    projectId: string;
  };
  query: {
    recontactHours: number;
    limit: number;
    /**
     * Cursor to resume a ranked fetch (sorted v4Score desc, _id asc).
     * Both cursor fields must be provided together.
     */
    afterV4Score?: number;
    afterProfileMatchId?: string;
  };
  url: '/v4/matching/projects/{projectId}/recontact-profiles';
};

export type MatchingControllerGetRecontactProfilesResponses = {
  200: Array<ProfileProjectMatch>;
};

export type MatchingControllerGetRecontactProfilesResponse =
  MatchingControllerGetRecontactProfilesResponses[keyof MatchingControllerGetRecontactProfilesResponses];

export type MatchingControllerFindMatchedProjectsData = {
  body?: never;
  path: {
    profileId: string;
  };
  query?: {
    page?: number;
    pageSize?: number;
    includeCount?: boolean;
    /**
     * [
     *
     * ONE_ON_ONE: 1
     *
     * FOCUS_GROUP: 2
     *
     * MULTI_DAY_STUDY: 3
     *
     * UNMODERATED_STUDY: 4
     *
     * PHONE_INTERVIEW: 5
     *
     * USABILITY_TESTING: 6
     *
     * IN_RESPONDENT_HOME: 8
     *
     * SURVEY: 9
     *
     * DIARY_STUDY: 10
     *
     * IN_RESPONDENT_OFFICE: 11
     *
     * AI_MODERATED_INTERVIEW: 12
     *
     * ]
     */
    kindsOfResearch?: Array<KindOfResearch>;
    /**
     * [
     *
     * B2B: 1
     *
     * B2C: 2
     *
     * ]
     */
    talkToAudience?: ParticipantAudienceType;
    /**
     * [
     *
     * IN_PERSON: 1
     *
     * REMOTE: 2
     *
     * ]
     */
    researchConductType?: ResearchConductType;
    minIncentive?: number;
    maxIncentive?: number;
    minTimeMinutesRequired?: number;
    maxTimeMinutesRequired?: number;
    sort?: RespondentDashboardSort;
    onlyShowMatched?: boolean;
    showHiddenProjects?: boolean;
    queryText?: string;
    /**
     * if showEligible is true, then dataOfBirth, gender, educationLevel, ethnicity, country are required
     */
    showEligible?: boolean;
    dateOfBirth?: string;
    gender?: Gender;
    educationLevel?: EducationLevel;
    ethnicity?: Ethnicities;
    country?: ProjectCountryCode;
  };
  url: '/v4/matching/projects/search/profiles/{profileId}';
};

export type MatchingControllerFindMatchedProjectsResponses = {
  200: MatchingProjectsPaginatedDto;
};

export type MatchingControllerFindMatchedProjectsResponse =
  MatchingControllerFindMatchedProjectsResponses[keyof MatchingControllerFindMatchedProjectsResponses];

export type MatchingControllerUpdateMatchingProjectData = {
  body: UpdateMatchingProjectDto;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/matching/projects/matching-projects/{projectId}';
};

export type MatchingControllerUpdateMatchingProjectResponses = {
  200: MatchingProject;
};

export type MatchingControllerUpdateMatchingProjectResponse =
  MatchingControllerUpdateMatchingProjectResponses[keyof MatchingControllerUpdateMatchingProjectResponses];

export type MatchingControllerCreateMatchingProjectData = {
  body: CreateMatchingProjectDto;
  path?: never;
  query?: never;
  url: '/v4/matching/projects/matching-projects';
};

export type MatchingControllerCreateMatchingProjectResponses = {
  201: MatchingProject;
};

export type MatchingControllerCreateMatchingProjectResponse =
  MatchingControllerCreateMatchingProjectResponses[keyof MatchingControllerCreateMatchingProjectResponses];

export type MatchingControllerCheckIfStopEmailsData = {
  body?: never;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/matching/projects/is-stop-emails/{projectId}';
};

export type MatchingControllerCheckIfStopEmailsResponses = {
  200: boolean;
};

export type MatchingControllerCheckIfStopEmailsResponse =
  MatchingControllerCheckIfStopEmailsResponses[keyof MatchingControllerCheckIfStopEmailsResponses];

export type MatchingControllerMarkMatchingProjectCheckedData = {
  body?: never;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/matching/matching-projects/{projectId}/checked';
};

export type MatchingControllerMarkMatchingProjectCheckedResponses = {
  200: unknown;
};

export type MatchingControllerGetProcessingMatchingProjectsData = {
  body?: never;
  path?: never;
  query: {
    followingUpEmailsScheduleHours: number;
  };
  url: '/v4/matching/matching-projects/processing';
};

export type MatchingControllerGetProcessingMatchingProjectsResponses = {
  200: Array<MatchingProject>;
};

export type MatchingControllerGetProcessingMatchingProjectsResponse =
  MatchingControllerGetProcessingMatchingProjectsResponses[keyof MatchingControllerGetProcessingMatchingProjectsResponses];

export type MatchingControllerMarkMatchingProjectRecontactCheckedData = {
  body?: never;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/matching/matching-projects/{projectId}/recontact-checked';
};

export type MatchingControllerMarkMatchingProjectRecontactCheckedResponses = {
  200: unknown;
};

export type MatchingControllerGetRecruitingMatchingProjectsData = {
  body?: never;
  path?: never;
  query?: {
    hoursSincePublish?: number;
    /**
     * Cap on the batch returned to the recontact cron, least-recently-checked
     * first. Omitted means the whole work list.
     */
    limit?: number;
  };
  url: '/v4/matching/matching-projects/recruiting';
};

export type MatchingControllerGetRecruitingMatchingProjectsResponses = {
  200: Array<RecruitingMatchingProjectDto>;
};

export type MatchingControllerGetRecruitingMatchingProjectsResponse =
  MatchingControllerGetRecruitingMatchingProjectsResponses[keyof MatchingControllerGetRecruitingMatchingProjectsResponses];

export type MatchingControllerSweepRecruitingStalledProjectsData = {
  body: RecruitingStalledSweepDto;
  path?: never;
  query?: never;
  url: '/v4/matching/matching-projects/recruiting-stalled-sweep';
};

export type MatchingControllerSweepRecruitingStalledProjectsResponses = {
  201: Array<RecruitingStalledProjectDto>;
};

export type MatchingControllerSweepRecruitingStalledProjectsResponse =
  MatchingControllerSweepRecruitingStalledProjectsResponses[keyof MatchingControllerSweepRecruitingStalledProjectsResponses];

export type MatchingControllerRunInitialMatchingCheckData = {
  body: InitialMatchingCheckDto;
  path?: never;
  query?: never;
  url: '/v4/matching/profiles/initial-check';
};

export type MatchingControllerRunInitialMatchingCheckResponses = {
  201: InitialMatchingCheckResultDto;
};

export type MatchingControllerRunInitialMatchingCheckResponse =
  MatchingControllerRunInitialMatchingCheckResponses[keyof MatchingControllerRunInitialMatchingCheckResponses];

export type MatchingControllerMonitorRecruitingData = {
  body?: never;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/matching/matching-projects/monitoring/{projectId}';
};

export type MatchingControllerMonitorRecruitingResponses = {
  200: boolean;
};

export type MatchingControllerMonitorRecruitingResponse =
  MatchingControllerMonitorRecruitingResponses[keyof MatchingControllerMonitorRecruitingResponses];

export type MatchingControllerUpdateData = {
  body?: never;
  path: {
    profileId: string;
    type: string;
  };
  query?: never;
  url: '/v4/matching/profiles-rollup/{profileId}/{type}';
};

export type MatchingControllerUpdateResponses = {
  200: ProfileRollup;
};

export type MatchingControllerUpdateResponse =
  MatchingControllerUpdateResponses[keyof MatchingControllerUpdateResponses];

export type MatchingControllerInsertProfileProjectMatchesEmailEventData = {
  body: InsertProfileEmailEventDto;
  path?: never;
  query?: never;
  url: '/v4/matching/profile-project-matches/email-event';
};

export type MatchingControllerInsertProfileProjectMatchesEmailEventResponses = {
  201: unknown;
};

export type MatchingControllerGetUpdatedProfileProjectMatchesData = {
  body?: never;
  path?: never;
  query: {
    minutesLimit: number;
  };
  url: '/v4/matching/profile-project-matches';
};

export type MatchingControllerGetUpdatedProfileProjectMatchesResponses = {
  200: Array<string>;
};

export type MatchingControllerGetUpdatedProfileProjectMatchesResponse =
  MatchingControllerGetUpdatedProfileProjectMatchesResponses[keyof MatchingControllerGetUpdatedProfileProjectMatchesResponses];

export type MatchingControllerGetUpdatedFlaggedRespondentsData = {
  body?: never;
  path?: never;
  query: {
    minutesLimit: number;
  };
  url: '/v4/matching/flagged-respondents';
};

export type MatchingControllerGetUpdatedFlaggedRespondentsResponses = {
  200: Array<string>;
};

export type MatchingControllerGetUpdatedFlaggedRespondentsResponse =
  MatchingControllerGetUpdatedFlaggedRespondentsResponses[keyof MatchingControllerGetUpdatedFlaggedRespondentsResponses];

export type MatchingControllerGetUpdatedExternalResearcherRespondentTransactionsData =
  {
    body?: never;
    path?: never;
    query: {
      minutesLimit: number;
    };
    url: '/v4/matching/respondent-transactions/external-researcher';
  };

export type MatchingControllerGetUpdatedExternalResearcherRespondentTransactionsResponses =
  {
    200: Array<string>;
  };

export type MatchingControllerGetUpdatedExternalResearcherRespondentTransactionsResponse =
  MatchingControllerGetUpdatedExternalResearcherRespondentTransactionsResponses[keyof MatchingControllerGetUpdatedExternalResearcherRespondentTransactionsResponses];

export type MatchingControllerGetUpdatedRespondentTransactionsData = {
  body?: never;
  path?: never;
  query: {
    minutesLimit: number;
  };
  url: '/v4/matching/respondent-transactions';
};

export type MatchingControllerGetUpdatedRespondentTransactionsResponses = {
  200: Array<string>;
};

export type MatchingControllerGetUpdatedRespondentTransactionsResponse =
  MatchingControllerGetUpdatedRespondentTransactionsResponses[keyof MatchingControllerGetUpdatedRespondentTransactionsResponses];

export type MatchingControllerGetProfileRollupMetadataData = {
  body?: never;
  path: {
    profileId: string;
  };
  query?: never;
  url: '/v4/matching/profiles-rollup/{profileId}/metadata';
};

export type MatchingControllerGetProfileRollupMetadataResponses = {
  200: ProfileRollupMetaData;
};

export type MatchingControllerGetProfileRollupMetadataResponse =
  MatchingControllerGetProfileRollupMetadataResponses[keyof MatchingControllerGetProfileRollupMetadataResponses];

export type MatchingControllerRebuildQuotaPredictionsData = {
  body?: never;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/matching/projects/{projectId}/quota-predictions/rebuild';
};

export type MatchingControllerRebuildQuotaPredictionsResponses = {
  201: RebuildQuotaPredictionsResultDto;
};

export type MatchingControllerRebuildQuotaPredictionsResponse =
  MatchingControllerRebuildQuotaPredictionsResponses[keyof MatchingControllerRebuildQuotaPredictionsResponses];

export type MatchingControllerTriggerRematchForExistingProjectData = {
  body: TriggerProjectRematchDto;
  path: {
    projectId: string;
    organizationId: string;
  };
  query?: never;
  url: '/v4/matching/projects/{projectId}/organization/{organizationId}/rematch';
};

export type MatchingControllerTriggerRematchForExistingProjectResponses = {
  204: void;
};

export type MatchingControllerTriggerRematchForExistingProjectResponse =
  MatchingControllerTriggerRematchForExistingProjectResponses[keyof MatchingControllerTriggerRematchForExistingProjectResponses];

export type ProfileLocksControllerGetLockedFieldsByProfileIdData = {
  body?: never;
  path: {
    profileId: string;
  };
  query?: never;
  url: '/v4/profile-locks/profiles/{profileId}/locked-fields';
};

export type ProfileLocksControllerGetLockedFieldsByProfileIdResponses = {
  /**
   * Profile field locks with timestamps
   */
  200: ProfileFieldLockDto;
};

export type ProfileLocksControllerGetLockedFieldsByProfileIdResponse =
  ProfileLocksControllerGetLockedFieldsByProfileIdResponses[keyof ProfileLocksControllerGetLockedFieldsByProfileIdResponses];

export type ProfileLocksControllerGetByProfileIdData = {
  body?: never;
  path: {
    profileId: string;
  };
  query?: never;
  url: '/v4/profile-locks/profiles/{profileId}';
};

export type ProfileLocksControllerGetByProfileIdResponses = {
  200: ProfileLockDto;
};

export type ProfileLocksControllerGetByProfileIdResponse =
  ProfileLocksControllerGetByProfileIdResponses[keyof ProfileLocksControllerGetByProfileIdResponses];

export type RespondentMessagesControllerCreateData = {
  body: CreateRespondentMessageDto;
  path: {
    userId: string;
  };
  query?: never;
  url: '/v4/admin/users/{userId}/respondent-messages';
};

export type RespondentMessagesControllerCreateResponses = {
  201: RespondentMessageDto;
};

export type RespondentMessagesControllerCreateResponse =
  RespondentMessagesControllerCreateResponses[keyof RespondentMessagesControllerCreateResponses];

export type RespondentMessagesControllerGetThreadData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v4/respondent-messages';
};

export type RespondentMessagesControllerGetThreadResponses = {
  200: RespondentMessageThreadDto;
};

export type RespondentMessagesControllerGetThreadResponse =
  RespondentMessagesControllerGetThreadResponses[keyof RespondentMessagesControllerGetThreadResponses];

export type RespondentMessagesControllerMarkAllReadData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v4/respondent-messages/read';
};

export type RespondentMessagesControllerMarkAllReadResponses = {
  200: MarkRespondentMessagesReadDto;
};

export type RespondentMessagesControllerMarkAllReadResponse =
  RespondentMessagesControllerMarkAllReadResponses[keyof RespondentMessagesControllerMarkAllReadResponses];

export type ProjectsControllerGetRecruitingProjectsDataData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v4/projects/status/recruiting/data';
};

export type ProjectsControllerGetRecruitingProjectsDataResponses = {
  200: Array<GetRecruitingProjectsData>;
};

export type ProjectsControllerGetRecruitingProjectsDataResponse =
  ProjectsControllerGetRecruitingProjectsDataResponses[keyof ProjectsControllerGetRecruitingProjectsDataResponses];

export type ProjectsControllerFindAllRecruitingProjectsData = {
  body: UpsertProfileMatchingScoresDto;
  path?: never;
  query?: never;
  url: '/v4/projects/status/recruiting';
};

export type ProjectsControllerFindAllRecruitingProjectsResponses = {
  200: Array<string>;
};

export type ProjectsControllerFindAllRecruitingProjectsResponse =
  ProjectsControllerFindAllRecruitingProjectsResponses[keyof ProjectsControllerFindAllRecruitingProjectsResponses];

export type ProjectsControllerAutoPauseProjectData = {
  body: GetAutoPauseProjectDto;
  path?: never;
  query?: never;
  url: '/v4/projects/autopause';
};

export type ProjectsControllerAutoPauseProjectResponses = {
  200: unknown;
};

export type ProjectsControllerAutoResumeProjectsData = {
  body?: AutoResumeProjectsDto;
  path?: never;
  query?: never;
  url: '/v4/projects/autoresume';
};

export type ProjectsControllerAutoResumeProjectsResponses = {
  200: unknown;
};

export type ProjectsControllerFindAllForProjectApprovalData = {
  body?: never;
  path?: never;
  query?: {
    page?: number;
    pageSize?: number;
    includeCount?: boolean;
    projectId?: string;
    externalResearcherId?: string;
    status?: ProjectStatus;
    statuses?: Array<ProjectStatus>;
    excludeStatuses?: Array<ProjectStatus>;
    closed?: boolean;
    organizationId?: string;
    publishedAt?: string;
    kindOfResearch?: number;
    /**
     * Character Limit: min 3 and max 255
     */
    name?: string;
    userEmail?: string;
    researcherTrustLevel?: number;
    organizationTrustLevel?: number;
    isRejected?: boolean;
    userId?: string;
    createdAt?: string;
  };
  url: '/v4/projects';
};

export type ProjectsControllerFindAllForProjectApprovalResponses = {
  200: AugmentedProjectsPaginatedDto;
};

export type ProjectsControllerFindAllForProjectApprovalResponse =
  ProjectsControllerFindAllForProjectApprovalResponses[keyof ProjectsControllerFindAllForProjectApprovalResponses];

export type ProjectsControllerCreateData = {
  body: CreateProjectDtoWritable;
  path?: never;
  query?: never;
  url: '/v4/projects';
};

export type ProjectsControllerCreateResponses = {
  201: Project;
};

export type ProjectsControllerCreateResponse =
  ProjectsControllerCreateResponses[keyof ProjectsControllerCreateResponses];

export type ProjectsControllerFindAllData = {
  body?: never;
  path?: never;
  query?: {
    page?: number;
    pageSize?: number;
    includeCount?: boolean;
    statuses?: Array<ProjectStatus>;
    kindOfResearch?: number;
    /**
     * Inclusive floor on `createdAt` — only projects created on or after this
     * date (floored to start of day) are returned.
     */
    createdAtGte?: string;
  };
  url: '/v4/projects/all';
};

export type ProjectsControllerFindAllResponses = {
  200: ProjectAnalyticsPaginatedDto;
};

export type ProjectsControllerFindAllResponse =
  ProjectsControllerFindAllResponses[keyof ProjectsControllerFindAllResponses];

export type ProjectsControllerAdminListData = {
  body?: never;
  path?: never;
  query?: {
    page?: number;
    pageSize?: number;
    includeCount?: boolean;
    /**
     * Regex-matched against name, internalName, companyName, follower
     * name/email and city — the same seven fields the legacy search targets.
     */
    query?: string;
    status?: ProjectStatus;
    recruitingStatus?: ProjectRecruitingStatus;
    closed?: boolean;
    /**
     * Legacy AudienceTypes: 1 = Industry Professionals, 2 = General Population.
     */
    talkToAudience?: number;
    /**
     * Inclusive lower bound on `createdAt`.
     */
    createdAtFrom?: string;
    /**
     * Inclusive upper bound on `createdAt`.
     */
    createdAtTo?: string;
  };
  url: '/v4/projects/admin/list';
};

export type ProjectsControllerAdminListResponses = {
  200: AdminProjectsPaginatedDto;
};

export type ProjectsControllerAdminListResponse =
  ProjectsControllerAdminListResponses[keyof ProjectsControllerAdminListResponses];

export type ProjectsControllerCopyData = {
  body: CopyProjectDto;
  path: {
    projectId: string;
  };
  query: {
    populate: boolean;
  };
  url: '/v4/projects/{projectId}/copy';
};

export type ProjectsControllerCopyResponses = {
  201: Project;
};

export type ProjectsControllerCopyResponse =
  ProjectsControllerCopyResponses[keyof ProjectsControllerCopyResponses];

export type ProjectsControllerGetPitchSuggestionsData = {
  body: PitchSuggestionDto;
  path?: never;
  query?: never;
  url: '/v4/projects/suggestions/pitch';
};

export type ProjectsControllerGetPitchSuggestionsErrors = {
  /**
   * Invalid request values or type.
   */
  400: unknown;
  /**
   * Internal server error or invalid/unexpected response from generative service.
   */
  500: unknown;
};

export type ProjectsControllerGetPitchSuggestionsResponses = {
  /**
   * Pitch suggestions
   */
  200: Array<PitchSuggestionResponseDto>;
};

export type ProjectsControllerGetPitchSuggestionsResponse =
  ProjectsControllerGetPitchSuggestionsResponses[keyof ProjectsControllerGetPitchSuggestionsResponses];

export type ProjectsControllerSendAutocompleteEmailsData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v4/projects/emails/autocomplete';
};

export type ProjectsControllerSendAutocompleteEmailsResponses = {
  201: unknown;
};

export type ProjectsControllerDeleteProjectData = {
  body?: never;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/projects/{projectId}';
};

export type ProjectsControllerDeleteProjectResponses = {
  200: unknown;
};

export type ProjectsControllerFindOneData = {
  body?: never;
  path: {
    projectId: string;
  };
  query: {
    populate: boolean;
    stats: boolean;
  };
  url: '/v4/projects/{projectId}';
};

export type ProjectsControllerFindOneResponses = {
  200: Project;
};

export type ProjectsControllerFindOneResponse =
  ProjectsControllerFindOneResponses[keyof ProjectsControllerFindOneResponses];

export type ProjectsControllerUpdateData = {
  body: UpdateDraftProjectDto | UpdatePublishedProjectDto;
  path: {
    projectId: string;
  };
  query: {
    populate: boolean;
  };
  url: '/v4/projects/{projectId}';
};

export type ProjectsControllerUpdateResponses = {
  200: Project;
};

export type ProjectsControllerUpdateResponse =
  ProjectsControllerUpdateResponses[keyof ProjectsControllerUpdateResponses];

export type ProjectsControllerFindOneDiyData = {
  body?: never;
  path: {
    projectId: string;
  };
  query: {
    populate: boolean;
    stats: boolean;
  };
  url: '/v4/projects/{projectId}/diy';
};

export type ProjectsControllerFindOneDiyResponses = {
  200: ProjectDiy;
};

export type ProjectsControllerFindOneDiyResponse =
  ProjectsControllerFindOneDiyResponses[keyof ProjectsControllerFindOneDiyResponses];

export type ProjectsControllerGetComputedProjectMetricsData = {
  body?: never;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/projects/{projectId}/metrics';
};

export type ProjectsControllerGetComputedProjectMetricsResponses = {
  200: ProjectMetricsAndMetadataDto;
};

export type ProjectsControllerGetComputedProjectMetricsResponse =
  ProjectsControllerGetComputedProjectMetricsResponses[keyof ProjectsControllerGetComputedProjectMetricsResponses];

export type ProjectsControllerUpdateProjectMetricsData = {
  body?: never;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/projects/{projectId}/metrics';
};

export type ProjectsControllerUpdateProjectMetricsResponses = {
  200: ProjectMetricsAndMetadataDto;
};

export type ProjectsControllerUpdateProjectMetricsResponse =
  ProjectsControllerUpdateProjectMetricsResponses[keyof ProjectsControllerUpdateProjectMetricsResponses];

export type ProjectsControllerGetPlaidVerificationData = {
  body?: never;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/projects/{projectId}/verifications';
};

export type ProjectsControllerGetPlaidVerificationResponses = {
  200: Array<string>;
};

export type ProjectsControllerGetPlaidVerificationResponse =
  ProjectsControllerGetPlaidVerificationResponses[keyof ProjectsControllerGetPlaidVerificationResponses];

export type ProjectsControllerProcessAllProjectsAutoInviteData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v4/projects/process-auto-invite';
};

export type ProjectsControllerProcessAllProjectsAutoInviteResponses = {
  /**
   * How many projects the sweep found and processed
   */
  200: ProcessAllProjectsAutoInviteResultDto;
};

export type ProjectsControllerProcessAllProjectsAutoInviteResponse =
  ProjectsControllerProcessAllProjectsAutoInviteResponses[keyof ProjectsControllerProcessAllProjectsAutoInviteResponses];

export type ProjectsControllerProcessAutoInviteData = {
  body?: never;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/projects/{projectId}/process-auto-invite';
};

export type ProjectsControllerProcessAutoInviteResponses = {
  200: Array<ScreenerResponse>;
};

export type ProjectsControllerProcessAutoInviteResponse =
  ProjectsControllerProcessAutoInviteResponses[keyof ProjectsControllerProcessAutoInviteResponses];

export type ProjectsControllerAdminUpdateData = {
  body: UpdateProjectAdminDto;
  path: {
    projectId: string;
  };
  query: {
    populate: boolean;
  };
  url: '/v4/projects/{projectId}/admin';
};

export type ProjectsControllerAdminUpdateResponses = {
  200: Project;
};

export type ProjectsControllerAdminUpdateResponse =
  ProjectsControllerAdminUpdateResponses[keyof ProjectsControllerAdminUpdateResponses];

export type ProjectsControllerUploadFilesFormDataData = {
  body?: never;
  path: {
    projectId: string;
  };
  query: {
    type:
      | 'nda'
      | 'picture'
      | 'attachment'
      | 'interview-media'
      | 'screener-response'
      | 'project-media';
  };
  url: '/v4/projects/{projectId}/files/form-data';
};

export type ProjectsControllerUploadFilesFormDataResponses = {
  200: UploadedFileResultsDto;
};

export type ProjectsControllerUploadFilesFormDataResponse =
  ProjectsControllerUploadFilesFormDataResponses[keyof ProjectsControllerUploadFilesFormDataResponses];

export type ProjectsControllerRejectProjectData = {
  body: RejectProjectDto;
  path: {
    projectId: string;
  };
  query: {
    populate: boolean;
  };
  url: '/v4/projects/{projectId}/reject';
};

export type ProjectsControllerRejectProjectResponses = {
  200: Project;
};

export type ProjectsControllerRejectProjectResponse =
  ProjectsControllerRejectProjectResponses[keyof ProjectsControllerRejectProjectResponses];

export type ProjectsControllerBulkUpsertExternalQuestionsData = {
  body: Array<ProjectExternalQuestion>;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/projects/{projectId}/external-questions/bulk';
};

export type ProjectsControllerBulkUpsertExternalQuestionsResponses = {
  200: Array<ProjectExternalQuestion>;
};

export type ProjectsControllerBulkUpsertExternalQuestionsResponse =
  ProjectsControllerBulkUpsertExternalQuestionsResponses[keyof ProjectsControllerBulkUpsertExternalQuestionsResponses];

export type ProjectsControllerValidatePublishData = {
  body?: never;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/projects/{projectId}/validate-publish';
};

export type ProjectsControllerValidatePublishResponses = {
  200: unknown;
};

export type ProjectsControllerPublishData = {
  body?: never;
  path: {
    projectId: string;
  };
  query: {
    populate: boolean;
    publishPaused: boolean;
  };
  url: '/v4/projects/{projectId}/publish';
};

export type ProjectsControllerPublishResponses = {
  200: Project;
};

export type ProjectsControllerPublishResponse =
  ProjectsControllerPublishResponses[keyof ProjectsControllerPublishResponses];

export type ProjectsControllerPublishAndUpdateTrustLevelData = {
  body: PublishAndUpdateTrustLevelDto;
  path: {
    projectId: string;
  };
  query: {
    populate: boolean;
  };
  url: '/v4/projects/{projectId}/publish-and-update-trust-level';
};

export type ProjectsControllerPublishAndUpdateTrustLevelResponses = {
  200: Project;
};

export type ProjectsControllerPublishAndUpdateTrustLevelResponse =
  ProjectsControllerPublishAndUpdateTrustLevelResponses[keyof ProjectsControllerPublishAndUpdateTrustLevelResponses];

export type ProjectsControllerPauseData = {
  body: PauseProjectDto;
  path: {
    projectId: string;
  };
  query: {
    populate: boolean;
  };
  url: '/v4/projects/{projectId}/pause';
};

export type ProjectsControllerPauseResponses = {
  200: Project;
};

export type ProjectsControllerPauseResponse =
  ProjectsControllerPauseResponses[keyof ProjectsControllerPauseResponses];

export type ProjectsControllerCloseData = {
  body: CloseProjectDto;
  path: {
    projectId: string;
  };
  query: {
    populate: boolean;
  };
  url: '/v4/projects/{projectId}/close';
};

export type ProjectsControllerCloseResponses = {
  200: Project;
};

export type ProjectsControllerCloseResponse =
  ProjectsControllerCloseResponses[keyof ProjectsControllerCloseResponses];

export type ProjectsControllerArchiveData = {
  body?: never;
  path: {
    projectId: string;
  };
  query: {
    populate: boolean;
  };
  url: '/v4/projects/{projectId}/archive';
};

export type ProjectsControllerArchiveResponses = {
  200: Project;
};

export type ProjectsControllerArchiveResponse =
  ProjectsControllerArchiveResponses[keyof ProjectsControllerArchiveResponses];

export type ProjectsControllerUpdateRecruitingStatusData = {
  body: UpdateProjectRecruitingStatusDto;
  path: {
    projectId: string;
  };
  query: {
    populate: boolean;
  };
  url: '/v4/projects/{projectId}/recruiting-status';
};

export type ProjectsControllerUpdateRecruitingStatusResponses = {
  200: Project;
};

export type ProjectsControllerUpdateRecruitingStatusResponse =
  ProjectsControllerUpdateRecruitingStatusResponses[keyof ProjectsControllerUpdateRecruitingStatusResponses];

export type ProjectsControllerFindAllProjectsForTeamData = {
  body?: never;
  path: {
    teamId: string;
  };
  query?: {
    page?: number;
    pageSize?: number;
    includeCount?: boolean;
    externalResearcherId?: string;
    status?: ProjectStatus;
    closed?: boolean;
    populate?: boolean;
  };
  url: '/v4/teams/{teamId}/projects';
};

export type ProjectsControllerFindAllProjectsForTeamResponses = {
  200: ProjectsPaginatedDto;
};

export type ProjectsControllerFindAllProjectsForTeamResponse =
  ProjectsControllerFindAllProjectsForTeamResponses[keyof ProjectsControllerFindAllProjectsForTeamResponses];

export type ProjectsControllerGetExternalSchedulingEventTypesData = {
  body?: never;
  path: {
    projectId: string;
    /**
     * External scheduling provider
     */
    provider: 'calendly';
  };
  query?: never;
  url: '/v4/projects/{projectId}/external-scheduling/{provider}';
};

export type ProjectsControllerGetExternalSchedulingEventTypesResponses = {
  /**
   * Returns the linked external scheduling event types for the project
   */
  200: GetProjectCalendlyEventTypesDto;
};

export type ProjectsControllerGetExternalSchedulingEventTypesResponse =
  ProjectsControllerGetExternalSchedulingEventTypesResponses[keyof ProjectsControllerGetExternalSchedulingEventTypesResponses];

export type ProjectsControllerUpdateExternalSchedulingEventTypesData = {
  body: UpdateProjectCalendlyEventTypesDto;
  path: {
    projectId: string;
    /**
     * External scheduling provider
     */
    provider: 'calendly';
  };
  query?: never;
  url: '/v4/projects/{projectId}/external-scheduling/{provider}';
};

export type ProjectsControllerUpdateExternalSchedulingEventTypesResponses = {
  /**
   * Updates the linked external scheduling event types for the project
   */
  200: Project;
};

export type ProjectsControllerUpdateExternalSchedulingEventTypesResponse =
  ProjectsControllerUpdateExternalSchedulingEventTypesResponses[keyof ProjectsControllerUpdateExternalSchedulingEventTypesResponses];

export type IndustriesControllerFindAllData = {
  body?: never;
  path?: never;
  query?: {
    page?: number;
    pageSize?: number;
    includeCount?: boolean;
    modifiedSince?: string;
  };
  url: '/v4/industries';
};

export type IndustriesControllerFindAllResponses = {
  200: IndustriesPaginatedDto;
};

export type IndustriesControllerFindAllResponse =
  IndustriesControllerFindAllResponses[keyof IndustriesControllerFindAllResponses];

export type IndustriesControllerFindOneData = {
  body?: never;
  path: {
    id: string;
  };
  query?: never;
  url: '/v4/industries/{id}';
};

export type IndustriesControllerFindOneResponses = {
  200: Industry;
};

export type IndustriesControllerFindOneResponse =
  IndustriesControllerFindOneResponses[keyof IndustriesControllerFindOneResponses];

export type OrganizationsControllerCreateNewTeamRequestData = {
  body: CreateNewTeamRequestDto;
  path: {
    organizationId: string;
  };
  query?: never;
  url: '/v4/organizations/{organizationId}/new-team-requests';
};

export type OrganizationsControllerCreateNewTeamRequestResponses = {
  201: unknown;
};

export type OrganizationsControllerUpdateNewTeamRequestData = {
  body: UpdateNewTeamRequestDto;
  path: {
    newTeamRequestId: string;
    organizationId: string;
  };
  query?: never;
  url: '/v4/organizations/{organizationId}/new-team-requests/{newTeamRequestId}';
};

export type OrganizationsControllerUpdateNewTeamRequestResponses = {
  200: unknown;
};

export type OrganizationsControllerGetOrganizationsForUserData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v4/organizations';
};

export type OrganizationsControllerGetOrganizationsForUserResponses = {
  200: OrganizationSanitizedDto | Organization;
};

export type OrganizationsControllerGetOrganizationsForUserResponse =
  OrganizationsControllerGetOrganizationsForUserResponses[keyof OrganizationsControllerGetOrganizationsForUserResponses];

export type OrganizationsControllerRemoveData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v4/organizations/{organizationId}';
};

export type OrganizationsControllerRemoveResponses = {
  200: unknown;
};

export type OrganizationsControllerFindOneData = {
  body?: never;
  path: {
    organizationId: string;
  };
  query?: never;
  url: '/v4/organizations/{organizationId}';
};

export type OrganizationsControllerFindOneResponses = {
  200: Organization;
};

export type OrganizationsControllerFindOneResponse =
  OrganizationsControllerFindOneResponses[keyof OrganizationsControllerFindOneResponses];

export type OrganizationsControllerUpdateData = {
  body: UpdateOrganizationDto;
  path: {
    organizationId: string;
  };
  query?: never;
  url: '/v4/organizations/{organizationId}';
};

export type OrganizationsControllerUpdateResponses = {
  200: Organization;
};

export type OrganizationsControllerUpdateResponse =
  OrganizationsControllerUpdateResponses[keyof OrganizationsControllerUpdateResponses];

export type OrganizationsControllerMergeData = {
  body?: never;
  path: {
    mergeId: string;
  };
  query?: never;
  url: '/v4/organizations/merge/{mergeId}';
};

export type OrganizationsControllerMergeResponses = {
  200: unknown;
};

export type OrganizationsControllerUploadFilesFormDataData = {
  body?: never;
  path: {
    organizationId: string;
  };
  query: {
    type:
      | 'nda'
      | 'picture'
      | 'attachment'
      | 'interview-media'
      | 'screener-response'
      | 'project-media';
  };
  url: '/v4/organizations/{organizationId}/files/form-data';
};

export type OrganizationsControllerUploadFilesFormDataResponses = {
  200: UploadedFileResultsDto;
};

export type OrganizationsControllerUploadFilesFormDataResponse =
  OrganizationsControllerUploadFilesFormDataResponses[keyof OrganizationsControllerUploadFilesFormDataResponses];

export type TeamsControllerFindAllForOrganizationData = {
  body?: never;
  path: {
    organizationId: string;
  };
  query?: {
    name?: string;
  };
  url: '/v4/organizations/{organizationId}/teams';
};

export type TeamsControllerFindAllForOrganizationResponses = {
  200: Team | TeamSanitizedDto;
};

export type TeamsControllerFindAllForOrganizationResponse =
  TeamsControllerFindAllForOrganizationResponses[keyof TeamsControllerFindAllForOrganizationResponses];

export type TeamsControllerGetAllTeamsWithUserJoinRequestData = {
  body?: never;
  path: {
    organizationId: string;
  };
  query: {
    status: string;
  };
  url: '/v4/organizations/{organizationId}/teams/join-requests';
};

export type TeamsControllerGetAllTeamsWithUserJoinRequestResponses = {
  200: Array<Team>;
};

export type TeamsControllerGetAllTeamsWithUserJoinRequestResponse =
  TeamsControllerGetAllTeamsWithUserJoinRequestResponses[keyof TeamsControllerGetAllTeamsWithUserJoinRequestResponses];

export type TeamsControllerFindOneData = {
  body?: never;
  path: {
    teamId: string;
    organizationId: string;
  };
  query?: never;
  url: '/v4/organizations/{organizationId}/teams/{teamId}';
};

export type TeamsControllerFindOneResponses = {
  200: Team;
};

export type TeamsControllerFindOneResponse =
  TeamsControllerFindOneResponses[keyof TeamsControllerFindOneResponses];

export type TeamsControllerCreateJoinRequestData = {
  body?: never;
  path: {
    organizationId: string;
    teamId: string;
  };
  query?: never;
  url: '/v4/organizations/{organizationId}/teams/{teamId}/join-requests';
};

export type TeamsControllerCreateJoinRequestResponses = {
  201: unknown;
};

export type TeamsControllerUpdateJoinRequestData = {
  body: UpdateJoinRequestDto;
  path: {
    teamId: string;
    joinRequestId: string;
    organizationId: string;
  };
  query?: never;
  url: '/v4/organizations/{organizationId}/teams/{teamId}/join-requests/{joinRequestId}';
};

export type TeamsControllerUpdateJoinRequestResponses = {
  200: unknown;
};

export type TeamsControllerUploadFilesFormDataData = {
  body?: never;
  path: {
    teamId: string;
    organizationId: string;
  };
  query: {
    type:
      | 'nda'
      | 'picture'
      | 'attachment'
      | 'interview-media'
      | 'screener-response'
      | 'project-media';
  };
  url: '/v4/organizations/{organizationId}/teams/{teamId}/files/form-data';
};

export type TeamsControllerUploadFilesFormDataResponses = {
  200: UploadedFileResultsDto;
};

export type TeamsControllerUploadFilesFormDataResponse =
  TeamsControllerUploadFilesFormDataResponses[keyof TeamsControllerUploadFilesFormDataResponses];

export type TeamsControllerFindTeamMembersData = {
  body?: never;
  path: {
    teamId: string;
    organizationId: string;
  };
  query: {
    query: string;
  };
  url: '/v4/organizations/{organizationId}/teams/{teamId}/members';
};

export type TeamsControllerFindTeamMembersResponses = {
  200: Array<TeamMembersDto>;
};

export type TeamsControllerFindTeamMembersResponse =
  TeamsControllerFindTeamMembersResponses[keyof TeamsControllerFindTeamMembersResponses];

export type TeamsControllerGetBalanceManagementSettingsData = {
  body?: never;
  path: {
    teamId: string;
    organizationId: string;
  };
  query?: never;
  url: '/v4/organizations/{organizationId}/teams/{teamId}/balance-management-settings';
};

export type TeamsControllerGetBalanceManagementSettingsResponses = {
  200: BalanceManagementSettingsResponseDto;
};

export type TeamsControllerGetBalanceManagementSettingsResponse =
  TeamsControllerGetBalanceManagementSettingsResponses[keyof TeamsControllerGetBalanceManagementSettingsResponses];

export type TeamsControllerUpdateBalanceManagementSettingsData = {
  body: UpdateBalanceManagementSettingsDto;
  path: {
    teamId: string;
    organizationId: string;
  };
  query?: never;
  url: '/v4/organizations/{organizationId}/teams/{teamId}/balance-management-settings';
};

export type TeamsControllerUpdateBalanceManagementSettingsResponses = {
  200: BalanceManagementSettingsResponseDto;
};

export type TeamsControllerUpdateBalanceManagementSettingsResponse =
  TeamsControllerUpdateBalanceManagementSettingsResponses[keyof TeamsControllerUpdateBalanceManagementSettingsResponses];

export type OrganizationRateCardsControllerGetForOrganizationData = {
  body?: never;
  path: {
    organizationId: string;
  };
  query?: never;
  url: '/v4/pricing/organizations/{organizationId}/rate-card';
};

export type OrganizationRateCardsControllerGetForOrganizationResponses = {
  200: OrganizationRateCardStateDto;
};

export type OrganizationRateCardsControllerGetForOrganizationResponse =
  OrganizationRateCardsControllerGetForOrganizationResponses[keyof OrganizationRateCardsControllerGetForOrganizationResponses];

export type OrganizationRateCardsControllerUpsertForOrganizationData = {
  body: UpsertOrganizationRateCardDto;
  path: {
    organizationId: string;
  };
  query?: never;
  url: '/v4/pricing/organizations/{organizationId}/rate-card';
};

export type OrganizationRateCardsControllerUpsertForOrganizationResponses = {
  200: OrganizationRateCard;
};

export type OrganizationRateCardsControllerUpsertForOrganizationResponse =
  OrganizationRateCardsControllerUpsertForOrganizationResponses[keyof OrganizationRateCardsControllerUpsertForOrganizationResponses];

export type PricingCreditsControllerFindAllData = {
  body: GetPricingCreditsPaginatedDto;
  path?: never;
  query?: never;
  url: '/v4/pricing/credits/list';
};

export type PricingCreditsControllerFindAllResponses = {
  200: PricingCreditsPaginatedDto;
};

export type PricingCreditsControllerFindAllResponse =
  PricingCreditsControllerFindAllResponses[keyof PricingCreditsControllerFindAllResponses];

export type PricingCreditsControllerFindAllForOrganizationData = {
  body: GetPricingCreditsPaginatedDto;
  path: {
    organizationId: string;
  };
  query?: never;
  url: '/v4/pricing/organizations/{organizationId}/credits/list';
};

export type PricingCreditsControllerFindAllForOrganizationResponses = {
  200: PricingCreditsPaginatedDto;
};

export type PricingCreditsControllerFindAllForOrganizationResponse =
  PricingCreditsControllerFindAllForOrganizationResponses[keyof PricingCreditsControllerFindAllForOrganizationResponses];

export type PricingCreditsControllerFindAllForTeamData = {
  body: GetPricingCreditsPaginatedDto;
  path: {
    teamId: string;
  };
  query?: never;
  url: '/v4/pricing/teams/{teamId}/credits/list';
};

export type PricingCreditsControllerFindAllForTeamResponses = {
  200: PricingCreditsPaginatedDto;
};

export type PricingCreditsControllerFindAllForTeamResponse =
  PricingCreditsControllerFindAllForTeamResponses[keyof PricingCreditsControllerFindAllForTeamResponses];

export type PricingCreditsControllerFindAllForTeamPlanData = {
  body: GetPricingCreditsPaginatedDto;
  path: {
    teamId: string;
    teamPlanId: string;
  };
  query?: never;
  url: '/v4/pricing/teams/{teamId}/team-plans/{teamPlanId}/credits/list';
};

export type PricingCreditsControllerFindAllForTeamPlanResponses = {
  200: PricingCreditsPaginatedDto;
};

export type PricingCreditsControllerFindAllForTeamPlanResponse =
  PricingCreditsControllerFindAllForTeamPlanResponses[keyof PricingCreditsControllerFindAllForTeamPlanResponses];

export type PricingCreditsControllerFindAllExpiredData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v4/pricing/credits/expired';
};

export type PricingCreditsControllerFindAllExpiredResponses = {
  200: Array<PricingCredit>;
};

export type PricingCreditsControllerFindAllExpiredResponse =
  PricingCreditsControllerFindAllExpiredResponses[keyof PricingCreditsControllerFindAllExpiredResponses];

export type PricingCreditsControllerFindAllWithOneWeekExpiryData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v4/pricing/credits/week-expiry';
};

export type PricingCreditsControllerFindAllWithOneWeekExpiryResponses = {
  200: Array<PricingCredit>;
};

export type PricingCreditsControllerFindAllWithOneWeekExpiryResponse =
  PricingCreditsControllerFindAllWithOneWeekExpiryResponses[keyof PricingCreditsControllerFindAllWithOneWeekExpiryResponses];

export type PricingCreditsControllerFindOneData = {
  body?: never;
  path: {
    id: string;
  };
  query?: never;
  url: '/v4/pricing/credits/{id}';
};

export type PricingCreditsControllerFindOneResponses = {
  200: PricingCredit;
};

export type PricingCreditsControllerFindOneResponse =
  PricingCreditsControllerFindOneResponses[keyof PricingCreditsControllerFindOneResponses];

export type PricingCreditsControllerManageCreditExpiryData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v4/pricing/credits/manage-expiry';
};

export type PricingCreditsControllerManageCreditExpiryResponses = {
  201: unknown;
};

export type PricingCreditsControllerExpireCreditData = {
  body?: never;
  path: {
    creditId: string;
  };
  query?: never;
  url: '/v4/pricing/credits/{creditId}/expire';
};

export type PricingCreditsControllerExpireCreditResponses = {
  200: PricingCredit;
};

export type PricingCreditsControllerExpireCreditResponse =
  PricingCreditsControllerExpireCreditResponses[keyof PricingCreditsControllerExpireCreditResponses];

export type PricingCreditsControllerTransferData = {
  body: TransferPricingCreditDto;
  path: {
    teamId: string;
    teamPlanId: string;
  };
  query?: never;
  url: '/v4/pricing/teams/{teamId}/team-plans/{teamPlanId}/credits/transfer';
};

export type PricingCreditsControllerTransferResponses = {
  201: unknown;
};

export type PricingCreditsControllerReserveData = {
  body: ReservePricingCreditDto;
  path: {
    teamId: string;
  };
  query?: never;
  url: '/v4/pricing/teams/{teamId}/credits/reserve';
};

export type PricingCreditsControllerReserveResponses = {
  200: Array<PricingCredit>;
};

export type PricingCreditsControllerReserveResponse =
  PricingCreditsControllerReserveResponses[keyof PricingCreditsControllerReserveResponses];

export type PricingCreditsControllerGetRequirementsForProjectData = {
  body?: never;
  path: {
    teamId: string;
    projectId: string;
  };
  query?: never;
  url: '/v4/pricing/teams/{teamId}/projects/{projectId}/credits/requirements';
};

export type PricingCreditsControllerGetRequirementsForProjectResponses = {
  200: ProjectRequirementsDto;
};

export type PricingCreditsControllerGetRequirementsForProjectResponse =
  PricingCreditsControllerGetRequirementsForProjectResponses[keyof PricingCreditsControllerGetRequirementsForProjectResponses];

export type PricingCreditsControllerGetRequirementsToEditProjectData = {
  body?: never;
  path: {
    teamId: string;
    projectId: string;
  };
  query?: never;
  url: '/v4/pricing/teams/{teamId}/projects/{projectId}/edit/credits/requirements';
};

export type PricingCreditsControllerGetRequirementsToEditProjectResponses = {
  200: ProjectRequirementsDto;
};

export type PricingCreditsControllerGetRequirementsToEditProjectResponse =
  PricingCreditsControllerGetRequirementsToEditProjectResponses[keyof PricingCreditsControllerGetRequirementsToEditProjectResponses];

export type PricingCreditsControllerReservationsData = {
  body?: never;
  path: {
    teamId: string;
    projectId: string;
  };
  query?: never;
  url: '/v4/pricing/teams/{teamId}/projects/{projectId}/credits/reservations/summary';
};

export type PricingCreditsControllerReservationsResponses = {
  200: ProjectReservationsSummaryDto;
};

export type PricingCreditsControllerReservationsResponse =
  PricingCreditsControllerReservationsResponses[keyof PricingCreditsControllerReservationsResponses];

export type PricingCreditsControllerReleaseData = {
  body: ReleasePricingCreditDto;
  path: {
    teamId: string;
    creditId: string;
  };
  query?: never;
  url: '/v4/pricing/teams/{teamId}/credits/{creditId}/release';
};

export type PricingCreditsControllerReleaseResponses = {
  200: PricingCredit;
};

export type PricingCreditsControllerReleaseResponse =
  PricingCreditsControllerReleaseResponses[keyof PricingCreditsControllerReleaseResponses];

export type PricingCreditsControllerReleaseAllData = {
  body?: never;
  path: {
    teamId: string;
  };
  query?: never;
  url: '/v4/pricing/teams/{teamId}/credits/release';
};

export type PricingCreditsControllerReleaseAllResponses = {
  200: Array<PricingCredit>;
};

export type PricingCreditsControllerReleaseAllResponse =
  PricingCreditsControllerReleaseAllResponses[keyof PricingCreditsControllerReleaseAllResponses];

export type PricingCreditsControllerSendNotificationData = {
  body?: never;
  path: {
    teamId: string;
    creditId: string;
  };
  query?: never;
  url: '/v4/pricing/teams/{teamId}/credits/{creditId}/notification';
};

export type PricingCreditsControllerSendNotificationResponses = {
  201: PricingCredit;
};

export type PricingCreditsControllerSendNotificationResponse =
  PricingCreditsControllerSendNotificationResponses[keyof PricingCreditsControllerSendNotificationResponses];

export type PricingTeamPlansControllerFindAllData = {
  body: GetPricingTeamPlansPaginatedDto;
  path?: never;
  query?: never;
  url: '/v4/pricing/team-plans/list';
};

export type PricingTeamPlansControllerFindAllResponses = {
  200: PricingTeamPlansPaginatedDto;
};

export type PricingTeamPlansControllerFindAllResponse =
  PricingTeamPlansControllerFindAllResponses[keyof PricingTeamPlansControllerFindAllResponses];

export type PricingTeamPlansControllerCreateData = {
  body: CreatePricingTeamPlanDto;
  path: {
    teamId: string;
  };
  query?: never;
  url: '/v4/pricing/teams/{teamId}/team-plans';
};

export type PricingTeamPlansControllerCreateResponses = {
  201: PricingTeamPlan;
};

export type PricingTeamPlansControllerCreateResponse =
  PricingTeamPlansControllerCreateResponses[keyof PricingTeamPlansControllerCreateResponses];

export type PricingTeamPlansControllerFindAllForTeamData = {
  body: GetPricingTeamPlansPaginatedDto;
  path: {
    teamId: string;
  };
  query?: never;
  url: '/v4/pricing/teams/{teamId}/team-plans/list';
};

export type PricingTeamPlansControllerFindAllForTeamResponses = {
  200: PricingTeamPlansPaginatedDto;
};

export type PricingTeamPlansControllerFindAllForTeamResponse =
  PricingTeamPlansControllerFindAllForTeamResponses[keyof PricingTeamPlansControllerFindAllForTeamResponses];

export type PricingTeamPlansControllerGetActiveTeamPlanData = {
  body?: never;
  path: {
    teamId: string;
  };
  query?: never;
  url: '/v4/pricing/teams/{teamId}/team-plans/active';
};

export type PricingTeamPlansControllerGetActiveTeamPlanResponses = {
  200: PricingTeamPlan;
};

export type PricingTeamPlansControllerGetActiveTeamPlanResponse =
  PricingTeamPlansControllerGetActiveTeamPlanResponses[keyof PricingTeamPlansControllerGetActiveTeamPlanResponses];

export type PricingTeamPlansControllerFindOneData = {
  body?: never;
  path: {
    teamId: string;
    id: string;
  };
  query?: never;
  url: '/v4/pricing/teams/{teamId}/team-plans/{id}';
};

export type PricingTeamPlansControllerFindOneResponses = {
  200: PricingTeamPlan;
};

export type PricingTeamPlansControllerFindOneResponse =
  PricingTeamPlansControllerFindOneResponses[keyof PricingTeamPlansControllerFindOneResponses];

export type PricingTeamPlansControllerUpdateData = {
  body: UpdatePricingTeamPlanDto;
  path: {
    teamId: string;
    id: string;
  };
  query?: never;
  url: '/v4/pricing/teams/{teamId}/team-plans/{id}';
};

export type PricingTeamPlansControllerUpdateResponses = {
  200: PricingTeamPlan;
};

export type PricingTeamPlansControllerUpdateResponse =
  PricingTeamPlansControllerUpdateResponses[keyof PricingTeamPlansControllerUpdateResponses];

export type PricingTeamPlansControllerUpdateCapabilitiesData = {
  body: UpdatePricingTeamPlanCapabilitiesDto;
  path: {
    teamId: string;
    teamPlanId: string;
  };
  query?: never;
  url: '/v4/pricing/teams/{teamId}/team-plans/{teamPlanId}/capabilities';
};

export type PricingTeamPlansControllerUpdateCapabilitiesResponses = {
  200: PricingTeamPlan;
};

export type PricingTeamPlansControllerUpdateCapabilitiesResponse =
  PricingTeamPlansControllerUpdateCapabilitiesResponses[keyof PricingTeamPlansControllerUpdateCapabilitiesResponses];

export type PricingTeamPlansControllerUnpauseData = {
  body?: never;
  path: {
    teamId: string;
    id: string;
  };
  query?: never;
  url: '/v4/pricing/teams/{teamId}/team-plans/{id}/unpause';
};

export type PricingTeamPlansControllerUnpauseResponses = {
  200: PricingTeamPlan;
};

export type PricingTeamPlansControllerUnpauseResponse =
  PricingTeamPlansControllerUnpauseResponses[keyof PricingTeamPlansControllerUnpauseResponses];

export type PricingTeamPlansControllerPauseData = {
  body: PausePricingTeamPlanDto;
  path: {
    teamId: string;
    id: string;
  };
  query?: never;
  url: '/v4/pricing/teams/{teamId}/team-plans/{id}/pause';
};

export type PricingTeamPlansControllerPauseResponses = {
  200: PricingTeamPlan;
};

export type PricingTeamPlansControllerPauseResponse =
  PricingTeamPlansControllerPauseResponses[keyof PricingTeamPlansControllerPauseResponses];

export type PricingTeamPlansControllerGetActiveRateCardData = {
  body?: never;
  path: {
    teamId: string;
  };
  query?: never;
  url: '/v4/pricing/teams/{teamId}/rate-cards/active';
};

export type PricingTeamPlansControllerGetActiveRateCardResponses = {
  200: RateCard;
};

export type PricingTeamPlansControllerGetActiveRateCardResponse =
  PricingTeamPlansControllerGetActiveRateCardResponses[keyof PricingTeamPlansControllerGetActiveRateCardResponses];

export type PricingTeamPlansControllerGetSummaryForTeamData = {
  body?: never;
  path: {
    teamId: string;
  };
  query?: never;
  url: '/v4/pricing/teams/{teamId}/summary';
};

export type PricingTeamPlansControllerGetSummaryForTeamResponses = {
  200: PricingTeamPlanSummaryDto;
};

export type PricingTeamPlansControllerGetSummaryForTeamResponse =
  PricingTeamPlansControllerGetSummaryForTeamResponses[keyof PricingTeamPlansControllerGetSummaryForTeamResponses];

export type PricingTeamPlansControllerTransferTeamToNewCustomPlanData = {
  body: PricingTeamTransferToCustomPlanDto;
  path: {
    teamId: string;
  };
  query?: never;
  url: '/v4/pricing/teams/{teamId}/plan';
};

export type PricingTeamPlansControllerTransferTeamToNewCustomPlanResponses = {
  200: unknown;
};

export type PricingTeamPlansControllerTeamUpgradeData = {
  body: PricingTeamUpgradeDto;
  path: {
    teamId: string;
  };
  query?: never;
  url: '/v4/pricing/teams/{teamId}/upgrade';
};

export type PricingTeamPlansControllerTeamUpgradeResponses = {
  200: unknown;
};

export type PricingTeamPlansControllerBulkUpgradeData = {
  body: PricingTeamsUpgradeDto;
  path?: never;
  query?: never;
  url: '/v4/pricing/teams/upgrade';
};

export type PricingTeamPlansControllerBulkUpgradeResponses = {
  200: PricingTeamsUpgradeResponseDto;
};

export type PricingTeamPlansControllerBulkUpgradeResponse =
  PricingTeamPlansControllerBulkUpgradeResponses[keyof PricingTeamPlansControllerBulkUpgradeResponses];

export type PricingTeamPlansControllerHandleCreditBalanceChangeData = {
  body: number;
  path: {
    teamId: string;
  };
  query?: never;
  url: '/v4/pricing/teams/{teamId}/handle-credit-balance-change';
};

export type PricingTeamPlansControllerHandleCreditBalanceChangeResponses = {
  201: unknown;
};

export type PricingTeamPlansControllerMigratePricingV2ToV3Data = {
  body: MigratePricingV2ToV3Dto;
  path?: never;
  query?: never;
  url: '/v4/pricing/teams/migrate-v2-to-v3';
};

export type PricingTeamPlansControllerMigratePricingV2ToV3Responses = {
  200: MigratePricingV2ToV3ResultDto;
};

export type PricingTeamPlansControllerMigratePricingV2ToV3Response =
  PricingTeamPlansControllerMigratePricingV2ToV3Responses[keyof PricingTeamPlansControllerMigratePricingV2ToV3Responses];

export type PricingTeamPlansControllerCheckV2TeamDataForMigrationToV3Data = {
  body?: never;
  path: {
    teamId: string;
  };
  query?: never;
  url: '/v4/pricing/teams/{teamId}/check-v2-pricing-data';
};

export type PricingTeamPlansControllerCheckV2TeamDataForMigrationToV3Responses =
  {
    200: CheckV2PricingDataResultDto;
  };

export type PricingTeamPlansControllerCheckV2TeamDataForMigrationToV3Response =
  PricingTeamPlansControllerCheckV2TeamDataForMigrationToV3Responses[keyof PricingTeamPlansControllerCheckV2TeamDataForMigrationToV3Responses];

export type PricingChargesControllerFindAllData = {
  body: GetPricingChargesPaginatedDto;
  path?: never;
  query?: never;
  url: '/v4/pricing/charges/list';
};

export type PricingChargesControllerFindAllResponses = {
  200: PricingChargesPaginatedDto;
};

export type PricingChargesControllerFindAllResponse =
  PricingChargesControllerFindAllResponses[keyof PricingChargesControllerFindAllResponses];

export type PricingChargesControllerAddDisputeData = {
  body: BrainTreeWebhookDto;
  path?: never;
  query?: never;
  url: '/v4/pricing/charges/disputes';
};

export type PricingChargesControllerAddDisputeResponses = {
  200: unknown;
};

export type PricingChargesControllerSendReceiptEmailData = {
  body?: never;
  path: {
    id: string;
  };
  query?: never;
  url: '/v4/pricing/charges/{id}/send-receipt-email';
};

export type PricingChargesControllerSendReceiptEmailResponses = {
  200: unknown;
};

export type PricingChargesControllerFindOneData = {
  body?: never;
  path: {
    id: string;
  };
  query?: never;
  url: '/v4/pricing/charges/{id}';
};

export type PricingChargesControllerFindOneResponses = {
  200: PricingCharge;
};

export type PricingChargesControllerFindOneResponse =
  PricingChargesControllerFindOneResponses[keyof PricingChargesControllerFindOneResponses];

export type PricingChargesControllerFindAllForTeamData = {
  body: GetPricingChargesPaginatedDto;
  path: {
    teamId: string;
  };
  query?: never;
  url: '/v4/pricing/teams/{teamId}/charges/list';
};

export type PricingChargesControllerFindAllForTeamResponses = {
  200: PricingChargesPaginatedDto;
};

export type PricingChargesControllerFindAllForTeamResponse =
  PricingChargesControllerFindAllForTeamResponses[keyof PricingChargesControllerFindAllForTeamResponses];

export type PricingChargesControllerCreateChargesPdfData = {
  body?: never;
  path: {
    teamId: string;
    chargeId: string;
  };
  query?: never;
  url: '/v4/pricing/teams/{teamId}/charges/{chargeId}/receipt';
};

export type PricingChargesControllerCreateChargesPdfResponses = {
  /**
   * PDF file
   */
  200: unknown;
};

export type PricingChargesControllerCreateProjectChargesPdfData = {
  body?: never;
  path: {
    teamId: string;
    projectId: string;
  };
  query?: never;
  url: '/v4/pricing/teams/{teamId}/projects/{projectId}/receipt';
};

export type PricingChargesControllerCreateProjectChargesPdfResponses = {
  /**
   * PDF file
   */
  200: unknown;
};

export type PricingChargesControllerFindAllForOrganizationData = {
  body: GetPricingChargesPaginatedDto;
  path: {
    organizationId: string;
  };
  query?: never;
  url: '/v4/pricing/organizations/{organizationId}/charges/list';
};

export type PricingChargesControllerFindAllForOrganizationResponses = {
  200: PricingChargesPaginatedDto;
};

export type PricingChargesControllerFindAllForOrganizationResponse =
  PricingChargesControllerFindAllForOrganizationResponses[keyof PricingChargesControllerFindAllForOrganizationResponses];

export type RespondentTransactionsControllerFindAllData = {
  body: GetRespondentTransactionsPaginatedDto;
  path?: never;
  query?: never;
  url: '/v4/pricing/respondent-transactions/list';
};

export type RespondentTransactionsControllerFindAllResponses = {
  200: RespondentTransactionsSearchResultDto;
};

export type RespondentTransactionsControllerFindAllResponse =
  RespondentTransactionsControllerFindAllResponses[keyof RespondentTransactionsControllerFindAllResponses];

export type RespondentTransactionsControllerFindAllByTeamData = {
  body?: never;
  path: {
    teamId: string;
  };
  query?: {
    page?: number;
    pageSize?: number;
    includeCount?: boolean;
    fromDate?: string;
    toDate?: string;
    projectId?: string;
    userId?: string;
  };
  url: '/v4/pricing/teams/{teamId}/respondent-transactions';
};

export type RespondentTransactionsControllerFindAllByTeamResponses = {
  200: RespondentTransactionsSearchResultDto;
};

export type RespondentTransactionsControllerFindAllByTeamResponse =
  RespondentTransactionsControllerFindAllByTeamResponses[keyof RespondentTransactionsControllerFindAllByTeamResponses];

export type RespondentTransactionsControllerGetParticipantPaidCountData = {
  body?: never;
  path: {
    userId: string;
  };
  query?: never;
  url: '/v4/pricing/respondent-transactions/participants/{userId}/paid-count';
};

export type RespondentTransactionsControllerGetParticipantPaidCountResponses = {
  200: {
    paidCount: number;
    totalPaidInMinor: number;
  };
};

export type RespondentTransactionsControllerGetParticipantPaidCountResponse =
  RespondentTransactionsControllerGetParticipantPaidCountResponses[keyof RespondentTransactionsControllerGetParticipantPaidCountResponses];

export type RespondentTransactionsControllerDownloadDeprecatedData = {
  body: RespondentTransactionsFilters;
  path?: never;
  query?: never;
  url: '/v4/pricing/respondent-transactions/download_deprecated';
};

export type RespondentTransactionsControllerDownloadDeprecatedResponses = {
  200: Blob | File;
  201: unknown;
};

export type RespondentTransactionsControllerDownloadDeprecatedResponse =
  RespondentTransactionsControllerDownloadDeprecatedResponses[keyof RespondentTransactionsControllerDownloadDeprecatedResponses];

export type RespondentTransactionsControllerDownloadData = {
  body?: never;
  path?: never;
  query: {
    organizationId?: string;
    teamId: string;
    projectId?: string;
    userId?: string;
    search?: string;
    fromDate?: string;
    toDate?: string;
    /**
     * Only contain legacy transactions, otherwise will
     * contain both new and legacy pricing transactions.
     */
    legacy?: boolean;
  };
  url: '/v4/pricing/respondent-transactions/download';
};

export type RespondentTransactionsControllerDownloadResponses = {
  /**
   * CSV file
   */
  200: Blob | File;
};

export type RespondentTransactionsControllerDownloadResponse =
  RespondentTransactionsControllerDownloadResponses[keyof RespondentTransactionsControllerDownloadResponses];

export type SurveyResponsesControllerProfileParticipationStatsData = {
  body?: never;
  path: {
    id: string;
  };
  query?: never;
  url: '/v4/survey-responses/{id}/stats';
};

export type SurveyResponsesControllerProfileParticipationStatsResponses = {
  200: SurveyResponseProfileStats;
};

export type SurveyResponsesControllerProfileParticipationStatsResponse =
  SurveyResponsesControllerProfileParticipationStatsResponses[keyof SurveyResponsesControllerProfileParticipationStatsResponses];

export type SurveyResponsesControllerFindAllProjectResponsesData = {
  body: SurveyResponseFilters;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/survey-responses/projects/{projectId}/list';
};

export type SurveyResponsesControllerFindAllProjectResponsesResponses = {
  200: ProjectResponsesPaginatedDto;
  201: ProjectResponsesPaginatedDto;
};

export type SurveyResponsesControllerFindAllProjectResponsesResponse =
  SurveyResponsesControllerFindAllProjectResponsesResponses[keyof SurveyResponsesControllerFindAllProjectResponsesResponses];

export type SurveyResponsesControllerDownloadData = {
  body?: never;
  path: {
    projectId: string;
  };
  query: {
    limit: number;
    sort: string;
    reviewed?: boolean;
    /**
     * TODO update frontend to use the new endpoint
     *
     * @deprecated
     */
    projectName?: string;
    hidden?: boolean;
    projectId?: string;
    query?: string;
    currentPage?: number;
    skip?: number;
    minPercentage?: number;
    maxPercentage?: number;
    status?: string;
    qualified?: boolean;
    socialLinks?: string;
    skillsFilter?: string;
    lastPaidAt?: string;
    favorite?: boolean;
    /**
     * request (meaning "include rejected rows", the default). Ignored.
     *
     * @deprecated
     */
    rejected?: boolean;
    /**
     * When true, only responses the researcher manually rejected (via the
     * "Not a fit" flow) — i.e. rows with a `disqualification` record. Mirrors
     * the paginated list's `rejected` param so CSV exports match the on-screen
     * Rejected view.
     */
    rejectedOnly?: boolean;
    visibilityStatus?: string;
    includeTeamProfile?: boolean;
    includeStats?: boolean;
  };
  url: '/v4/survey-responses/project/{projectId}/download';
};

export type SurveyResponsesControllerDownloadResponses = {
  /**
   * CSV file
   */
  200: Blob | File;
};

export type SurveyResponsesControllerDownloadResponse =
  SurveyResponsesControllerDownloadResponses[keyof SurveyResponsesControllerDownloadResponses];

export type SurveyResponsesControllerDownloadDeprecatedData = {
  body: SurveyResponseFilters;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/survey-responses/project/{projectId}/download';
};

export type SurveyResponsesControllerDownloadDeprecatedResponses = {
  /**
   * CSV file
   */
  200: Blob | File;
  201: unknown;
};

export type SurveyResponsesControllerDownloadDeprecatedResponse =
  SurveyResponsesControllerDownloadDeprecatedResponses[keyof SurveyResponsesControllerDownloadDeprecatedResponses];

export type SurveyResponsesControllerGetSurveyResponseData = {
  body?: never;
  path: {
    id: string;
  };
  query?: never;
  url: '/v4/survey-responses/{id}';
};

export type SurveyResponsesControllerGetSurveyResponseResponses = {
  200: SurveyResponse;
};

export type SurveyResponsesControllerGetSurveyResponseResponse =
  SurveyResponsesControllerGetSurveyResponseResponses[keyof SurveyResponsesControllerGetSurveyResponseResponses];

export type SurveyResponsesControllerUpdateVisibilityByUserIdData = {
  body: UpdateVisibilityDto;
  path: {
    userId: string;
  };
  query?: never;
  url: '/v4/survey-responses/users/{userId}/visibility';
};

export type SurveyResponsesControllerUpdateVisibilityByUserIdResponses = {
  200: unknown;
};

export type SurveyResponsesControllerGetSurveyResponseByUserAndProjectData = {
  body?: never;
  path: {
    userId: string;
    projectId: string;
  };
  query?: never;
  url: '/v4/survey-responses/users/{userId}/projects/{projectId}';
};

export type SurveyResponsesControllerGetSurveyResponseByUserAndProjectResponses =
  {
    200: SurveyResponse;
  };

export type SurveyResponsesControllerGetSurveyResponseByUserAndProjectResponse =
  SurveyResponsesControllerGetSurveyResponseByUserAndProjectResponses[keyof SurveyResponsesControllerGetSurveyResponseByUserAndProjectResponses];

export type ProgramsControllerFindAllData = {
  body?: never;
  path: {
    teamId: string;
  };
  query?: never;
  url: '/v4/teams/{teamId}/programs';
};

export type ProgramsControllerFindAllResponses = {
  200: Array<ProgramDto>;
};

export type ProgramsControllerFindAllResponse =
  ProgramsControllerFindAllResponses[keyof ProgramsControllerFindAllResponses];

export type ProgramsControllerCreateData = {
  body: CreateProgramDto;
  path: {
    teamId: string;
  };
  query?: never;
  url: '/v4/teams/{teamId}/programs';
};

export type ProgramsControllerCreateResponses = {
  201: ProgramDto;
};

export type ProgramsControllerCreateResponse =
  ProgramsControllerCreateResponses[keyof ProgramsControllerCreateResponses];

export type ProgramsControllerFindByProjectData = {
  body?: never;
  path: {
    teamId: string;
    projectId: string;
  };
  query?: never;
  url: '/v4/teams/{teamId}/programs/by-project/{projectId}';
};

export type ProgramsControllerFindByProjectResponses = {
  200: ProgramDto;
};

export type ProgramsControllerFindByProjectResponse =
  ProgramsControllerFindByProjectResponses[keyof ProgramsControllerFindByProjectResponses];

export type ProgramsControllerFindParticipantsData = {
  body?: never;
  path: {
    teamId: string;
    programId: string;
  };
  query?: never;
  url: '/v4/teams/{teamId}/programs/{programId}/participants';
};

export type ProgramsControllerFindParticipantsResponses = {
  200: ProgramParticipantsDto;
};

export type ProgramsControllerFindParticipantsResponse =
  ProgramsControllerFindParticipantsResponses[keyof ProgramsControllerFindParticipantsResponses];

export type ProgramsControllerFindOneData = {
  body?: never;
  path: {
    teamId: string;
    programId: string;
  };
  query?: never;
  url: '/v4/teams/{teamId}/programs/{programId}';
};

export type ProgramsControllerFindOneResponses = {
  200: ProgramDto;
};

export type ProgramsControllerFindOneResponse =
  ProgramsControllerFindOneResponses[keyof ProgramsControllerFindOneResponses];

export type ProgramsControllerUpdateData = {
  body: UpdateProgramDto;
  path: {
    teamId: string;
    programId: string;
  };
  query?: never;
  url: '/v4/teams/{teamId}/programs/{programId}';
};

export type ProgramsControllerUpdateResponses = {
  200: ProgramDto;
};

export type ProgramsControllerUpdateResponse =
  ProgramsControllerUpdateResponses[keyof ProgramsControllerUpdateResponses];

export type ProgramsControllerArchiveData = {
  body?: never;
  path: {
    teamId: string;
    programId: string;
  };
  query?: never;
  url: '/v4/teams/{teamId}/programs/{programId}/archive';
};

export type ProgramsControllerArchiveResponses = {
  200: ProgramDto;
};

export type ProgramsControllerArchiveResponse =
  ProgramsControllerArchiveResponses[keyof ProgramsControllerArchiveResponses];

export type ProgramsControllerUnarchiveData = {
  body?: never;
  path: {
    teamId: string;
    programId: string;
  };
  query?: never;
  url: '/v4/teams/{teamId}/programs/{programId}/unarchive';
};

export type ProgramsControllerUnarchiveResponses = {
  200: ProgramDto;
};

export type ProgramsControllerUnarchiveResponse =
  ProgramsControllerUnarchiveResponses[keyof ProgramsControllerUnarchiveResponses];

export type ProgramsControllerAddProjectData = {
  body: AddProgramProjectDto;
  path: {
    teamId: string;
    programId: string;
  };
  query?: never;
  url: '/v4/teams/{teamId}/programs/{programId}/projects';
};

export type ProgramsControllerAddProjectResponses = {
  200: ProgramDto;
};

export type ProgramsControllerAddProjectResponse =
  ProgramsControllerAddProjectResponses[keyof ProgramsControllerAddProjectResponses];

export type ScreenerQuestionsControllerFindAllData = {
  body?: never;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/projects/{projectId}/screener-questions';
};

export type ScreenerQuestionsControllerFindAllResponses = {
  200: Array<ScreenerQuestion>;
};

export type ScreenerQuestionsControllerFindAllResponse =
  ScreenerQuestionsControllerFindAllResponses[keyof ScreenerQuestionsControllerFindAllResponses];

export type ScreenerQuestionsControllerCreateData = {
  body:
    | CreateRadioScreenerQuestionDto
    | CreateCheckboxScreenerQuestionDto
    | CreateTextboxScreenerQuestionDto
    | CreateSliderScaleScreenerQuestionDto
    | CreateFileUploadScreenerQuestionDto
    | CreateRankScreenerQuestionDto
    | CreateMatrixScreenerQuestionDto;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/projects/{projectId}/screener-questions';
};

export type ScreenerQuestionsControllerCreateResponses = {
  201: ScreenerQuestion;
};

export type ScreenerQuestionsControllerCreateResponse =
  ScreenerQuestionsControllerCreateResponses[keyof ScreenerQuestionsControllerCreateResponses];

export type ScreenerQuestionsControllerBulkUpsertData = {
  body: Array<
    | UpsertRadioScreenerQuestionDto
    | UpsertCheckboxScreenerQuestionDto
    | UpsertTextboxScreenerQuestionDto
    | UpsertSliderScaleScreenerQuestionDto
    | UpsertFileUploadScreenerQuestionDto
    | UpsertRankScreenerQuestionDto
    | UpsertMatrixScreenerQuestionDto
  >;
  path: {
    projectId: string;
  };
  query?: {
    expectedScreenerVersion?: number;
  };
  url: '/v4/projects/{projectId}/screener-questions/bulk';
};

export type ScreenerQuestionsControllerBulkUpsertResponses = {
  200: Array<ScreenerQuestion>;
};

export type ScreenerQuestionsControllerBulkUpsertResponse =
  ScreenerQuestionsControllerBulkUpsertResponses[keyof ScreenerQuestionsControllerBulkUpsertResponses];

export type ScreenerQuestionsControllerSearchPreviouslyUsedData = {
  body?: never;
  path: {
    projectId: string;
  };
  query?: {
    /**
     * Deprecated and ignored. Team scope is derived from the route project.
     *
     * @deprecated
     */
    teamId?: string;
    /**
     * Deprecated and ignored. Org scope is derived from the route project.
     *
     * @deprecated
     */
    organizationId?: string;
    /**
     * Presence-only toggle for the "My Projects" view: when provided, results
     * are restricted to the *authenticated* user's projects. The supplied value
     * is ignored — the server always uses the current user's id — so it cannot
     * scope the search to an arbitrary user. Omit for the "Team Projects" view.
     */
    userId?: string;
    searchText?: string;
    /**
     * Lower bound on project `createdAt`. Derived on the client from the
     * timeframe selector (e.g. now - 30 days).
     */
    createdAtFrom?: string;
    limit?: number;
  };
  url: '/v4/projects/{projectId}/screener-questions/search';
};

export type ScreenerQuestionsControllerSearchPreviouslyUsedResponses = {
  200: Array<ScreenerQuestion>;
};

export type ScreenerQuestionsControllerSearchPreviouslyUsedResponse =
  ScreenerQuestionsControllerSearchPreviouslyUsedResponses[keyof ScreenerQuestionsControllerSearchPreviouslyUsedResponses];

export type ScreenerQuestionsControllerSearchPreviouslyUsedProjectsData = {
  body?: never;
  path: {
    projectId: string;
  };
  query?: {
    /**
     * Deprecated and ignored. Team scope is derived from the route project.
     *
     * @deprecated
     */
    teamId?: string;
    /**
     * Deprecated and ignored. Org scope is derived from the route project.
     *
     * @deprecated
     */
    organizationId?: string;
    /**
     * Presence-only toggle for the "My Projects" view: when provided, results
     * are restricted to the *authenticated* user's projects. The supplied value
     * is ignored — the server always uses the current user's id — so it cannot
     * scope the search to an arbitrary user. Omit for the "Team Projects" view.
     */
    userId?: string;
    searchText?: string;
    /**
     * Lower bound on project `createdAt`. Derived on the client from the
     * timeframe selector (e.g. now - 30 days).
     */
    createdAtFrom?: string;
    limit?: number;
  };
  url: '/v4/projects/{projectId}/screener-questions/search-projects';
};

export type ScreenerQuestionsControllerSearchPreviouslyUsedProjectsResponses = {
  200: Array<ScreenerQuestionProjectGroup>;
};

export type ScreenerQuestionsControllerSearchPreviouslyUsedProjectsResponse =
  ScreenerQuestionsControllerSearchPreviouslyUsedProjectsResponses[keyof ScreenerQuestionsControllerSearchPreviouslyUsedProjectsResponses];

export type ScreenerQuestionsControllerRemoveData = {
  body?: never;
  path: {
    projectId: string;
    screenerQuestionId: string;
  };
  query?: never;
  url: '/v4/projects/{projectId}/screener-questions/{screenerQuestionId}';
};

export type ScreenerQuestionsControllerRemoveResponses = {
  200: ScreenerQuestion;
};

export type ScreenerQuestionsControllerRemoveResponse =
  ScreenerQuestionsControllerRemoveResponses[keyof ScreenerQuestionsControllerRemoveResponses];

export type ScreenerQuestionsControllerFindOneData = {
  body?: never;
  path: {
    projectId: string;
    screenerQuestionId: string;
  };
  query?: never;
  url: '/v4/projects/{projectId}/screener-questions/{screenerQuestionId}';
};

export type ScreenerQuestionsControllerFindOneResponses = {
  200: ScreenerQuestion;
};

export type ScreenerQuestionsControllerFindOneResponse =
  ScreenerQuestionsControllerFindOneResponses[keyof ScreenerQuestionsControllerFindOneResponses];

export type ScreenerQuestionsControllerUpdateData = {
  body:
    | UpdateRadioScreenerQuestionDto
    | UpdateCheckboxScreenerQuestionDto
    | UpdateTextboxScreenerQuestionDto
    | UpdateSliderScaleScreenerQuestionDto
    | UpdateFileUploadScreenerQuestionDto
    | UpdateRankScreenerQuestionDto
    | UpdateMatrixScreenerQuestionDto;
  path: {
    projectId: string;
    screenerQuestionId: string;
  };
  query?: never;
  url: '/v4/projects/{projectId}/screener-questions/{screenerQuestionId}';
};

export type ScreenerQuestionsControllerUpdateResponses = {
  200: ScreenerQuestion;
};

export type ScreenerQuestionsControllerUpdateResponse =
  ScreenerQuestionsControllerUpdateResponses[keyof ScreenerQuestionsControllerUpdateResponses];

export type ScreenerQuestionsControllerOrderData = {
  body: Array<string>;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/projects/{projectId}/screener-questions/order';
};

export type ScreenerQuestionsControllerOrderResponses = {
  200: Array<ScreenerQuestion>;
};

export type ScreenerQuestionsControllerOrderResponse =
  ScreenerQuestionsControllerOrderResponses[keyof ScreenerQuestionsControllerOrderResponses];

export type ScreenerQuestionsControllerValidateQuestionsData = {
  body: Array<string>;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/projects/{projectId}/screener-questions/validate';
};

export type ScreenerQuestionsControllerValidateQuestionsResponses = {
  200: Array<ScreenerQuestion>;
};

export type ScreenerQuestionsControllerValidateQuestionsResponse =
  ScreenerQuestionsControllerValidateQuestionsResponses[keyof ScreenerQuestionsControllerValidateQuestionsResponses];

export type LookupsControllerFindAllData = {
  body?: never;
  path?: never;
  query: {
    pick: Array<LookupGroup>;
    externalOnly?: boolean;
    contexts?: Array<Context>;
    lang?: Language;
  };
  url: '/v4/lookups';
};

export type LookupsControllerFindAllResponses = {
  200: LookupsMinimalDto;
};

export type LookupsControllerFindAllResponse =
  LookupsControllerFindAllResponses[keyof LookupsControllerFindAllResponses];

export type LookupsControllerFindAllExpandedData = {
  body?: never;
  path?: never;
  query: {
    pick: Array<LookupGroup>;
    externalOnly?: boolean;
    contexts?: Array<Context>;
    lang?: Language;
  };
  url: '/v4/lookups/expanded';
};

export type LookupsControllerFindAllExpandedResponses = {
  200: LookupsExpandedDto;
};

export type LookupsControllerFindAllExpandedResponse =
  LookupsControllerFindAllExpandedResponses[keyof LookupsControllerFindAllExpandedResponses];

export type LookupsControllerIndustriesLookupData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Character Limit: min 2 and max 15
     */
    query?: string;
    /**
     * Number Limit: min 1 and max 50
     */
    top?: number;
    /**
     * Number Limit: min 1 and max 50
     */
    limit?: number;
    lang?: Language;
  };
  url: '/v4/lookups/industries';
};

export type LookupsControllerIndustriesLookupResponses = {
  200: Array<LookupMinimalDto>;
};

export type LookupsControllerIndustriesLookupResponse =
  LookupsControllerIndustriesLookupResponses[keyof LookupsControllerIndustriesLookupResponses];

export type LookupsControllerJobTitlesLookupData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Character Limit: min 2 and max 15
     */
    query?: string;
    /**
     * Number Limit: min 1 and max 50
     */
    top?: number;
    /**
     * Number Limit: min 1 and max 50
     */
    limit?: number;
    lang?: Language;
  };
  url: '/v4/lookups/job-titles';
};

export type LookupsControllerJobTitlesLookupResponses = {
  200: Array<LookupMinimalDto>;
};

export type LookupsControllerJobTitlesLookupResponse =
  LookupsControllerJobTitlesLookupResponses[keyof LookupsControllerJobTitlesLookupResponses];

export type LookupsControllerSkillsLookupData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Character Limit: min 2 and max 15
     */
    query?: string;
    /**
     * Number Limit: min 1 and max 50
     */
    top?: number;
    /**
     * Number Limit: min 1 and max 50
     */
    limit?: number;
    lang?: Language;
  };
  url: '/v4/lookups/skills';
};

export type LookupsControllerSkillsLookupResponses = {
  200: Array<LookupMinimalDto>;
};

export type LookupsControllerSkillsLookupResponse =
  LookupsControllerSkillsLookupResponses[keyof LookupsControllerSkillsLookupResponses];

export type LookupsControllerTopicsLookupData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Character Limit: min 2 and max 15
     */
    query?: string;
    /**
     * Number Limit: min 1 and max 50
     */
    top?: number;
    /**
     * Number Limit: min 1 and max 50
     */
    limit?: number;
    lang?: Language;
  };
  url: '/v4/lookups/topics';
};

export type LookupsControllerTopicsLookupResponses = {
  200: Array<LookupMinimalDto>;
};

export type LookupsControllerTopicsLookupResponse =
  LookupsControllerTopicsLookupResponses[keyof LookupsControllerTopicsLookupResponses];

export type SkillsControllerFindAllData = {
  body?: never;
  path?: never;
  query?: {
    page?: number;
    pageSize?: number;
    includeCount?: boolean;
    /**
     * Character Limit: min 3 and max 100
     */
    query?: string;
    modifiedSince?: string;
    validated?: boolean;
  };
  url: '/v4/skills';
};

export type SkillsControllerFindAllResponses = {
  200: SimpleSkillsPaginatedDto;
};

export type SkillsControllerFindAllResponse =
  SkillsControllerFindAllResponses[keyof SkillsControllerFindAllResponses];

export type SkillsControllerFindOneData = {
  body?: never;
  path: {
    id: string;
  };
  query?: never;
  url: '/v4/skills/{id}';
};

export type SkillsControllerFindOneResponses = {
  200: Skill;
};

export type SkillsControllerFindOneResponse =
  SkillsControllerFindOneResponses[keyof SkillsControllerFindOneResponses];

export type TopicsControllerFindAllData = {
  body?: never;
  path?: never;
  query?: {
    page?: number;
    pageSize?: number;
    includeCount?: boolean;
    /**
     * Character Limit: min 3 and max 100
     */
    query?: string;
    modifiedSince?: string;
  };
  url: '/v4/topics';
};

export type TopicsControllerFindAllResponses = {
  200: SimpleTopicsPaginatedDto;
};

export type TopicsControllerFindAllResponse =
  TopicsControllerFindAllResponses[keyof TopicsControllerFindAllResponses];

export type TopicsControllerFindOneData = {
  body?: never;
  path: {
    id: string;
  };
  query?: never;
  url: '/v4/topics/{id}';
};

export type TopicsControllerFindOneResponses = {
  200: Topic;
};

export type TopicsControllerFindOneResponse =
  TopicsControllerFindOneResponses[keyof TopicsControllerFindOneResponses];

export type ExternalInvitesControllerValidateData = {
  body?: never;
  path: {
    inviteCode: string;
  };
  query?: {
    /**
     * The per-person id on the link, if any
     */
    uid?: string;
  };
  url: '/v4/participant/external-invites/{inviteCode}';
};

export type ExternalInvitesControllerValidateResponses = {
  200: ExternalInviteValidationDto;
};

export type ExternalInvitesControllerValidateResponse =
  ExternalInvitesControllerValidateResponses[keyof ExternalInvitesControllerValidateResponses];

export type ExternalInvitesControllerSendVerificationData = {
  body: SendExternalInviteVerificationDto;
  path: {
    inviteCode: string;
  };
  query?: never;
  url: '/v4/participant/external-invites/{inviteCode}/verification';
};

export type ExternalInvitesControllerSendVerificationResponses = {
  204: void;
};

export type ExternalInvitesControllerSendVerificationResponse =
  ExternalInvitesControllerSendVerificationResponses[keyof ExternalInvitesControllerSendVerificationResponses];

export type ExternalInvitesControllerSubmitApplicationData = {
  body: ExternalInviteApplicationDto;
  path: {
    inviteCode: string;
  };
  query?: never;
  url: '/v4/participant/external-invites/{inviteCode}/application';
};

export type ExternalInvitesControllerSubmitApplicationResponses = {
  201: ExternalInviteApplicationResponseDto;
};

export type ExternalInvitesControllerSubmitApplicationResponse =
  ExternalInvitesControllerSubmitApplicationResponses[keyof ExternalInvitesControllerSubmitApplicationResponses];

export type ExternalScreenerControllerGetScreenerData = {
  body?: never;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/participant/external-projects/{projectId}/screener';
};

export type ExternalScreenerControllerGetScreenerResponses = {
  200: GetScreenerResponseDto;
};

export type ExternalScreenerControllerGetScreenerResponse =
  ExternalScreenerControllerGetScreenerResponses[keyof ExternalScreenerControllerGetScreenerResponses];

export type ExternalScreenerControllerSaveScreenerResponseData = {
  body: SaveScreenerResponseDto;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/participant/external-projects/{projectId}/screener-responses';
};

export type ExternalScreenerControllerSaveScreenerResponseErrors = {
  /**
   * Invalid answers, or already filed
   */
  400: unknown;
};

export type ExternalScreenerControllerSaveScreenerResponseResponses = {
  200: SaveScreenerResponseResponseDto;
  201: SaveScreenerResponseResponseDto;
};

export type ExternalScreenerControllerSaveScreenerResponseResponse =
  ExternalScreenerControllerSaveScreenerResponseResponses[keyof ExternalScreenerControllerSaveScreenerResponseResponses];

export type ProjectMetricsControllerUpsertDailyMetricsData = {
  body: UpsertDailyMetricsDto;
  path?: never;
  query?: never;
  url: '/v4/project-metrics/projects/{projectId}';
};

export type ProjectMetricsControllerUpsertDailyMetricsResponses = {
  201: Array<ProjectMetric>;
};

export type ProjectMetricsControllerUpsertDailyMetricsResponse =
  ProjectMetricsControllerUpsertDailyMetricsResponses[keyof ProjectMetricsControllerUpsertDailyMetricsResponses];

export type SuggestionsControllerGetIncentiveSuggestionData = {
  body: IncentiveRequestB2bDto | IncentiveRequestB2cDto;
  path?: never;
  query?: never;
  url: '/v4/suggestions/incentive';
};

export type SuggestionsControllerGetIncentiveSuggestionErrors = {
  /**
   * Invalid request values or type.
   */
  400: unknown;
  /**
   * Internal server error or invalid/unexpected response from generative service.
   */
  500: unknown;
};

export type SuggestionsControllerGetIncentiveSuggestionResponses = {
  /**
   * Incentive suggestion
   */
  200: IncentiveResponseDto;
};

export type SuggestionsControllerGetIncentiveSuggestionResponse =
  SuggestionsControllerGetIncentiveSuggestionResponses[keyof SuggestionsControllerGetIncentiveSuggestionResponses];

export type AdminOrganizationsControllerListData = {
  body?: never;
  path?: never;
  query?: {
    page?: number;
    pageSize?: number;
    includeCount?: boolean;
    organizationId?: string;
    query?: string;
    /**
     * When true, duplicate organizations are merged into one row per name
     * (compared trimmed and case-insensitively) carrying every matching id in
     * `organizationIds`. Ignored when `organizationId` is set. Pagination and
     * `totalResults` then count names, not organizations.
     */
    groupByName?: boolean;
  };
  url: '/v4/admin/organizations/list';
};

export type AdminOrganizationsControllerListResponses = {
  200: AdminOrganizationsPaginatedDto;
};

export type AdminOrganizationsControllerListResponse =
  AdminOrganizationsControllerListResponses[keyof AdminOrganizationsControllerListResponses];

export type AdminOrganizationsControllerListActiveUsersData = {
  body?: never;
  path: {
    organizationId: string;
  };
  query: {
    from: string;
    to: string;
  };
  url: '/v4/admin/organizations/{organizationId}/active-users';
};

export type AdminOrganizationsControllerListActiveUsersResponses = {
  200: AdminOrganizationActiveUsersDto;
};

export type AdminOrganizationsControllerListActiveUsersResponse =
  AdminOrganizationsControllerListActiveUsersResponses[keyof AdminOrganizationsControllerListActiveUsersResponses];

export type AdminOrganizationsControllerUpdateData = {
  body: UpdateAdminOrganizationDto;
  path: {
    organizationId: string;
  };
  query?: never;
  url: '/v4/admin/organizations/{organizationId}';
};

export type AdminOrganizationsControllerUpdateResponses = {
  204: void;
};

export type AdminOrganizationsControllerUpdateResponse =
  AdminOrganizationsControllerUpdateResponses[keyof AdminOrganizationsControllerUpdateResponses];

export type AdminOrganizationsControllerCreateMergeData = {
  body: CreateOrganizationMergeDto;
  path: {
    organizationId: string;
    childOrganizationId: string;
  };
  query?: never;
  url: '/v4/admin/organizations/{organizationId}/merge/{childOrganizationId}';
};

export type AdminOrganizationsControllerCreateMergeResponses = {
  201: OrganizationMergeResponseDto;
};

export type AdminOrganizationsControllerCreateMergeResponse =
  AdminOrganizationsControllerCreateMergeResponses[keyof AdminOrganizationsControllerCreateMergeResponses];

export type AdminParticipantPaymentsControllerListRespondentPayoutsData = {
  body?: never;
  path?: never;
  query?: {
    page?: number;
    pageSize?: number;
    includeCount?: boolean;
    projectId?: string;
    userId?: string;
    /**
     * Case-insensitive starts-with match. Trimmed; blank counts as absent.
     */
    firstName?: string;
    surveyResponseId?: string;
  };
  url: '/v4/admin/participant-payments/respondent-payouts/list';
};

export type AdminParticipantPaymentsControllerListRespondentPayoutsResponses = {
  200: AdminRespondentPayoutsListDto;
};

export type AdminParticipantPaymentsControllerListRespondentPayoutsResponse =
  AdminParticipantPaymentsControllerListRespondentPayoutsResponses[keyof AdminParticipantPaymentsControllerListRespondentPayoutsResponses];

export type AdminParticipantsControllerListData = {
  body?: never;
  path?: never;
  query?: {
    page?: number;
    pageSize?: number;
    includeCount?: boolean;
    /**
     * Short-circuits every other filter and reads the primary by id. Falls back
     * to a `userId` lookup when it misses, because the page's quick-search box
     * cannot tell the two id kinds apart (today's BFF does the same).
     */
    profileId?: string;
    /**
     * Short-circuits every other filter and reads the primary by `userId`.
     */
    userId?: string;
    firstName?: string;
    lastName?: string;
    /**
     * Matched against `email` and `employment.email`. Both are `keyword`-analyzed
     * in the Atlas index, so this is an exact, full-string match, not a prefix.
     */
    email?: string;
    status?: RespondentStatus;
    /**
     * OR-matched: a profile matches when any of its `employment.industry` entries
     * matches any entry here. Legacy additionally rejected names outside a
     * hardcoded list; dropped, so a typo returns an empty page instead of a 400.
     */
    industry?: Array<string>;
    jobTitle?: string;
    companySize?: CompanySize;
    /**
     * AND-matched, each entry across the four indexed city paths. The page merges
     * its free-text location and its country select into this one array, exactly
     * as the legacy admin page does.
     */
    location?: Array<string>;
    /**
     * Both bounds or neither (400 otherwise). A reversed range is swapped.
     */
    ageMin?: number;
    ageMax?: number;
    ethnicity?: Ethnicities;
    gender?: Gender;
    educationLevel?: EducationLevel;
    /**
     * AND-matched: a profile must carry every skill listed.
     */
    skills?: Array<string>;
  };
  url: '/v4/admin/participants/list';
};

export type AdminParticipantsControllerListResponses = {
  200: AdminParticipantsPaginatedDto;
};

export type AdminParticipantsControllerListResponse =
  AdminParticipantsControllerListResponses[keyof AdminParticipantsControllerListResponses];

export type AdminParticipantsControllerExportData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Short-circuits every other filter and reads the primary by id. Falls back
     * to a `userId` lookup when it misses, because the page's quick-search box
     * cannot tell the two id kinds apart (today's BFF does the same).
     */
    profileId?: string;
    /**
     * Short-circuits every other filter and reads the primary by `userId`.
     */
    userId?: string;
    firstName?: string;
    lastName?: string;
    /**
     * Matched against `email` and `employment.email`. Both are `keyword`-analyzed
     * in the Atlas index, so this is an exact, full-string match, not a prefix.
     */
    email?: string;
    status?: RespondentStatus;
    /**
     * OR-matched: a profile matches when any of its `employment.industry` entries
     * matches any entry here. Legacy additionally rejected names outside a
     * hardcoded list; dropped, so a typo returns an empty page instead of a 400.
     */
    industry?: Array<string>;
    jobTitle?: string;
    companySize?: CompanySize;
    /**
     * AND-matched, each entry across the four indexed city paths. The page merges
     * its free-text location and its country select into this one array, exactly
     * as the legacy admin page does.
     */
    location?: Array<string>;
    /**
     * Both bounds or neither (400 otherwise). A reversed range is swapped.
     */
    ageMin?: number;
    ageMax?: number;
    ethnicity?: Ethnicities;
    gender?: Gender;
    educationLevel?: EducationLevel;
    /**
     * AND-matched: a profile must carry every skill listed.
     */
    skills?: Array<string>;
    /**
     * Rows written to the file, in search order. Above the cap the export is
     * truncated, not split into multiple files (the legacy page did split); the
     * admin UI says so when the result count exceeds it.
     */
    limit?: number;
  };
  url: '/v4/admin/participants/export';
};

export type AdminParticipantsControllerExportResponses = {
  /**
   * CSV file
   */
  200: Blob | File;
};

export type AdminParticipantsControllerExportResponse =
  AdminParticipantsControllerExportResponses[keyof AdminParticipantsControllerExportResponses];

export type AdminParticipantsControllerUpdateParticipantData = {
  body: UpdateAdminParticipantDto;
  path: {
    profileId: string;
  };
  query?: never;
  url: '/v4/admin/participants/{profileId}';
};

export type AdminParticipantsControllerUpdateParticipantResponses = {
  200: UpdateAdminParticipantResponseDto;
};

export type AdminParticipantsControllerUpdateParticipantResponse =
  AdminParticipantsControllerUpdateParticipantResponses[keyof AdminParticipantsControllerUpdateParticipantResponses];

export type AdminParticipantsControllerGetParticipantHistoryData = {
  body?: never;
  path: {
    userId: string;
  };
  query?: never;
  url: '/v4/admin/participants/{userId}/history';
};

export type AdminParticipantsControllerGetParticipantHistoryResponses = {
  200: AdminParticipantHistoryDto;
};

export type AdminParticipantsControllerGetParticipantHistoryResponse =
  AdminParticipantsControllerGetParticipantHistoryResponses[keyof AdminParticipantsControllerGetParticipantHistoryResponses];

export type AdminProjectsControllerUpdateRecruitingStatusData = {
  body: UpdateProjectRecruitingStatusDto;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/admin/projects/{projectId}/recruiting-status';
};

export type AdminProjectsControllerUpdateRecruitingStatusResponses = {
  204: void;
};

export type AdminProjectsControllerUpdateRecruitingStatusResponse =
  AdminProjectsControllerUpdateRecruitingStatusResponses[keyof AdminProjectsControllerUpdateRecruitingStatusResponses];

export type AdminProjectsControllerGetAiModProjectStatsData = {
  body: GetAiModProjectStatsDto;
  path?: never;
  query?: never;
  url: '/v4/admin/projects/ai-mod-stats';
};

export type AdminProjectsControllerGetAiModProjectStatsResponses = {
  200: Array<AiModProjectStatsDto>;
};

export type AdminProjectsControllerGetAiModProjectStatsResponse =
  AdminProjectsControllerGetAiModProjectStatsResponses[keyof AdminProjectsControllerGetAiModProjectStatsResponses];

export type AdminProjectsControllerGetProjectInsightsStatusData = {
  body?: never;
  path: {
    projectId: string;
  };
  query: {
    featuresSchemaVersion: number;
  };
  url: '/v4/admin/projects/{projectId}/insights-status';
};

export type AdminProjectsControllerGetProjectInsightsStatusResponses = {
  200: ProjectInsightsStatusDto;
};

export type AdminProjectsControllerGetProjectInsightsStatusResponse =
  AdminProjectsControllerGetProjectInsightsStatusResponses[keyof AdminProjectsControllerGetProjectInsightsStatusResponses];

export type AdminProjectsControllerPinFeaturesSchemaVersionData = {
  body: PinFeaturesSchemaVersionDto;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/admin/projects/{projectId}/features-schema-version';
};

export type AdminProjectsControllerPinFeaturesSchemaVersionResponses = {
  204: void;
};

export type AdminProjectsControllerPinFeaturesSchemaVersionResponse =
  AdminProjectsControllerPinFeaturesSchemaVersionResponses[keyof AdminProjectsControllerPinFeaturesSchemaVersionResponses];

export type AdminUsersControllerConvertParticipantToResearcherData = {
  body: ConvertParticipantToResearcherDto;
  path?: never;
  query?: never;
  url: '/v4/admin/users/convert-to-researcher';
};

export type AdminUsersControllerConvertParticipantToResearcherResponses = {
  200: ConvertParticipantToResearcherResponseDto;
};

export type AdminUsersControllerConvertParticipantToResearcherResponse =
  AdminUsersControllerConvertParticipantToResearcherResponses[keyof AdminUsersControllerConvertParticipantToResearcherResponses];

export type AdminUsersControllerUpdateData = {
  body: UpdateAdminUserDto;
  path: {
    userId: string;
  };
  query?: never;
  url: '/v4/admin/users/{userId}';
};

export type AdminUsersControllerUpdateResponses = {
  204: void;
};

export type AdminUsersControllerUpdateResponse =
  AdminUsersControllerUpdateResponses[keyof AdminUsersControllerUpdateResponses];

export type AdminUsersControllerRemoveUserFromOrganizationData = {
  body?: never;
  path: {
    userId: string;
    organizationId: string;
  };
  query?: never;
  url: '/v4/admin/users/{userId}/organizations/{organizationId}';
};

export type AdminUsersControllerRemoveUserFromOrganizationResponses = {
  204: void;
};

export type AdminUsersControllerRemoveUserFromOrganizationResponse =
  AdminUsersControllerRemoveUserFromOrganizationResponses[keyof AdminUsersControllerRemoveUserFromOrganizationResponses];

export type AdminIncentiveAdjustmentsControllerGetProjectIncentiveAdjustmentsData =
  {
    body?: never;
    path: {
      projectId: string;
    };
    query?: never;
    url: '/v4/admin/projects/{projectId}/incentive-adjustments';
  };

export type AdminIncentiveAdjustmentsControllerGetProjectIncentiveAdjustmentsResponses =
  {
    200: ProjectIncentiveAdjustmentsDto;
  };

export type AdminIncentiveAdjustmentsControllerGetProjectIncentiveAdjustmentsResponse =
  AdminIncentiveAdjustmentsControllerGetProjectIncentiveAdjustmentsResponses[keyof AdminIncentiveAdjustmentsControllerGetProjectIncentiveAdjustmentsResponses];

export type AdminIncentiveAdjustmentsControllerAdjustProjectIncentivesData = {
  body: AdjustProjectIncentivesDto;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/admin/projects/{projectId}/incentives';
};

export type AdminIncentiveAdjustmentsControllerAdjustProjectIncentivesResponses =
  {
    204: void;
  };

export type AdminIncentiveAdjustmentsControllerAdjustProjectIncentivesResponse =
  AdminIncentiveAdjustmentsControllerAdjustProjectIncentivesResponses[keyof AdminIncentiveAdjustmentsControllerAdjustProjectIncentivesResponses];

export type FraudMetricsControllerGetReportedUsersOverTimeData = {
  body?: never;
  path?: never;
  query?: {
    from?: string;
    to?: string;
    granularity?: FraudMetricsGranularity;
  };
  url: '/v4/admin/fraud-metrics/reported-users';
};

export type FraudMetricsControllerGetReportedUsersOverTimeResponses = {
  200: ReportedUsersMetricsResponseDto;
};

export type FraudMetricsControllerGetReportedUsersOverTimeResponse =
  FraudMetricsControllerGetReportedUsersOverTimeResponses[keyof FraudMetricsControllerGetReportedUsersOverTimeResponses];

export type FraudMetricsControllerGetRejectedVpnApplicationsOverTimeData = {
  body?: never;
  path?: never;
  query?: {
    from?: string;
    to?: string;
    granularity?: FraudMetricsGranularity;
  };
  url: '/v4/admin/fraud-metrics/rejected-vpn-applications';
};

export type FraudMetricsControllerGetRejectedVpnApplicationsOverTimeResponses =
  {
    200: FraudMetricsTimeSeriesResponseDto;
  };

export type FraudMetricsControllerGetRejectedVpnApplicationsOverTimeResponse =
  FraudMetricsControllerGetRejectedVpnApplicationsOverTimeResponses[keyof FraudMetricsControllerGetRejectedVpnApplicationsOverTimeResponses];

export type FraudMetricsControllerGetRejectedIpApplicationsOverTimeData = {
  body?: never;
  path?: never;
  query?: {
    from?: string;
    to?: string;
    granularity?: FraudMetricsGranularity;
  };
  url: '/v4/admin/fraud-metrics/rejected-ip-applications';
};

export type FraudMetricsControllerGetRejectedIpApplicationsOverTimeResponses = {
  200: FraudMetricsTimeSeriesResponseDto;
};

export type FraudMetricsControllerGetRejectedIpApplicationsOverTimeResponse =
  FraudMetricsControllerGetRejectedIpApplicationsOverTimeResponses[keyof FraudMetricsControllerGetRejectedIpApplicationsOverTimeResponses];

export type FraudMetricsControllerGetPlaidRiskVerificationsOverTimeData = {
  body?: never;
  path?: never;
  query?: {
    from?: string;
    to?: string;
    granularity?: FraudMetricsGranularity;
  };
  url: '/v4/admin/fraud-metrics/plaid-risk-verifications';
};

export type FraudMetricsControllerGetPlaidRiskVerificationsOverTimeResponses = {
  200: PlaidRiskVerificationsMetricsResponseDto;
};

export type FraudMetricsControllerGetPlaidRiskVerificationsOverTimeResponse =
  FraudMetricsControllerGetPlaidRiskVerificationsOverTimeResponses[keyof FraudMetricsControllerGetPlaidRiskVerificationsOverTimeResponses];

export type AiChatControllerFindByProjectData = {
  body?: never;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/ai-chat/{projectId}';
};

export type AiChatControllerFindByProjectResponses = {
  200: FindChatThreadDto;
};

export type AiChatControllerFindByProjectResponse =
  AiChatControllerFindByProjectResponses[keyof AiChatControllerFindByProjectResponses];

export type AiChatControllerUpsertData = {
  body: SaveChatThreadDto;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/ai-chat/{projectId}';
};

export type AiChatControllerUpsertResponses = {
  200: AiChatThread;
};

export type AiChatControllerUpsertResponse =
  AiChatControllerUpsertResponses[keyof AiChatControllerUpsertResponses];

export type InsightsChatControllerListThreadsData = {
  body?: never;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/researcher/projects/{projectId}/insights-chat/threads';
};

export type InsightsChatControllerListThreadsResponses = {
  200: Array<InsightsChatThreadSummaryDto>;
};

export type InsightsChatControllerListThreadsResponse =
  InsightsChatControllerListThreadsResponses[keyof InsightsChatControllerListThreadsResponses];

export type InsightsChatControllerDeleteThreadData = {
  body?: never;
  path: {
    projectId: string;
    threadId: string;
  };
  query?: never;
  url: '/v4/researcher/projects/{projectId}/insights-chat/threads/{threadId}';
};

export type InsightsChatControllerDeleteThreadResponses = {
  204: void;
};

export type InsightsChatControllerDeleteThreadResponse =
  InsightsChatControllerDeleteThreadResponses[keyof InsightsChatControllerDeleteThreadResponses];

export type InsightsChatControllerGetThreadData = {
  body?: never;
  path: {
    projectId: string;
    threadId: string;
  };
  query?: {
    /**
     * The feature schema version to read the project's insights at: its pin,
     * which is the default, or one of the experimental versions it is also
     * extracted at.
     */
    featuresSchemaVersion?: number;
  };
  url: '/v4/researcher/projects/{projectId}/insights-chat/threads/{threadId}';
};

export type InsightsChatControllerGetThreadResponses = {
  200: InsightsChatThreadDto;
};

export type InsightsChatControllerGetThreadResponse =
  InsightsChatControllerGetThreadResponses[keyof InsightsChatControllerGetThreadResponses];

export type InsightsChatControllerRenameThreadData = {
  body: RenameInsightsChatThreadDto;
  path: {
    projectId: string;
    threadId: string;
  };
  query?: never;
  url: '/v4/researcher/projects/{projectId}/insights-chat/threads/{threadId}';
};

export type InsightsChatControllerRenameThreadResponses = {
  200: InsightsChatThreadSummaryDto;
};

export type InsightsChatControllerRenameThreadResponse =
  InsightsChatControllerRenameThreadResponses[keyof InsightsChatControllerRenameThreadResponses];

export type InsightsChatControllerEvaluateChartData = {
  body: EvaluateInsightsChatChartDto;
  path: {
    projectId: string;
    threadId: string;
  };
  query?: never;
  url: '/v4/researcher/projects/{projectId}/insights-chat/threads/{threadId}/charts/evaluate';
};

export type InsightsChatControllerEvaluateChartResponses = {
  200: InsightsChatChartEvaluationDto;
};

export type InsightsChatControllerEvaluateChartResponse =
  InsightsChatControllerEvaluateChartResponses[keyof InsightsChatControllerEvaluateChartResponses];

export type InsightsChatControllerSendMessageData = {
  body: SendInsightsChatMessageDto;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/researcher/projects/{projectId}/insights-chat/messages';
};

export type InsightsChatControllerSendMessageResponses = {
  201: unknown;
};

export type AuthControllerParticipantSignupData = {
  body: ParticipantSignupDto;
  path?: never;
  query?: never;
  url: '/v4/auth/participant-signup';
};

export type AuthControllerParticipantSignupResponses = {
  201: ParticipantSignupResponseDto;
};

export type AuthControllerParticipantSignupResponse =
  AuthControllerParticipantSignupResponses[keyof AuthControllerParticipantSignupResponses];

export type AuthControllerSendPublicApplyVerificationData = {
  body: SendPublicApplyVerificationDto;
  path?: never;
  query?: never;
  url: '/v4/auth/public-apply-verification';
};

export type AuthControllerSendPublicApplyVerificationResponses = {
  204: void;
};

export type AuthControllerSendPublicApplyVerificationResponse =
  AuthControllerSendPublicApplyVerificationResponses[keyof AuthControllerSendPublicApplyVerificationResponses];

export type AuthControllerResearcherSignupData = {
  body: ResearcherSignupDto;
  path?: never;
  query?: never;
  url: '/v4/auth/researcher-signup';
};

export type AuthControllerResearcherSignupResponses = {
  201: ResearcherSignupResponseDto;
};

export type AuthControllerResearcherSignupResponse =
  AuthControllerResearcherSignupResponses[keyof AuthControllerResearcherSignupResponses];

export type AuthControllerInitiatePasswordResetData = {
  body: InitiatePasswordResetDto;
  path?: never;
  query?: never;
  url: '/v4/auth/reset-password-initiate';
};

export type AuthControllerInitiatePasswordResetResponses = {
  204: void;
};

export type AuthControllerInitiatePasswordResetResponse =
  AuthControllerInitiatePasswordResetResponses[keyof AuthControllerInitiatePasswordResetResponses];

export type AuthControllerVerifyEmailData = {
  body: VerifyEmailDto;
  path?: never;
  query?: never;
  url: '/v4/auth/verify-email';
};

export type AuthControllerVerifyEmailResponses = {
  201: VerifyEmailResponseDto;
};

export type AuthControllerVerifyEmailResponse =
  AuthControllerVerifyEmailResponses[keyof AuthControllerVerifyEmailResponses];

export type ProfilesControllerCreateData = {
  body: CreateProfileDto;
  path?: never;
  query?: never;
  url: '/v4/profiles';
};

export type ProfilesControllerCreateResponses = {
  201: ProfileDto;
};

export type ProfilesControllerCreateResponse =
  ProfilesControllerCreateResponses[keyof ProfilesControllerCreateResponses];

export type ProfilesControllerVerifyPaypalData = {
  body?: never;
  path?: never;
  query: {
    email: string;
  };
  url: '/v4/profiles/verify-paypal';
};

export type ProfilesControllerVerifyPaypalResponses = {
  200: boolean;
};

export type ProfilesControllerVerifyPaypalResponse =
  ProfilesControllerVerifyPaypalResponses[keyof ProfilesControllerVerifyPaypalResponses];

export type ProfilesControllerGetIdByPhoneNumberData = {
  body?: never;
  path?: never;
  query: {
    phone: string;
    countryCode: string;
  };
  url: '/v4/profiles/get-id-by-phone-number';
};

export type ProfilesControllerGetIdByPhoneNumberResponses = {
  200: unknown;
};

export type ProfilesControllerStartLinkedinVolData = {
  body?: never;
  path: {
    profileId: string;
  };
  query?: {
    /**
     * Journey intent: identity = launched from the ID-verification step (unverified members are forwarded to LinkedIn verification); identity_recheck = returning from LinkedIn verification
     */
    intent?: 'connect' | 'identity' | 'identity_recheck';
  };
  url: '/v4/profiles/{profileId}/linkedin-vol/authorize';
};

export type ProfilesControllerStartLinkedinVolResponses = {
  200: unknown;
};

export type ProfilesControllerLinkedinVolOAuthCallbackData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Authorization code when LinkedIn redirects after success (use with state)
     */
    code?: string;
    /**
     * Opaque state from the authorize request (success callback)
     */
    state?: string;
    /**
     * OAuth error code when LinkedIn redirects after denial/failure
     */
    error?: string;
    /**
     * Human-readable error details (error callback)
     */
    error_description?: string;
  };
  url: '/v4/profiles/linkedin-vol/oauth-callback';
};

export type ProfilesControllerLinkedinVolOAuthCallbackResponses = {
  200: unknown;
};

export type ProfilesControllerUpdateTrustedCompanyData = {
  body: UpdateTrustedCompanyDto;
  path?: never;
  query?: never;
  url: '/v4/profiles/trusted-company';
};

export type ProfilesControllerUpdateTrustedCompanyResponses = {
  200: unknown;
};

export type ProfilesControllerUpdateProfileRiskData = {
  body: UpdateRiskDto;
  path: {
    profileId: string;
  };
  query?: never;
  url: '/v4/profiles/{profileId}/risk';
};

export type ProfilesControllerUpdateProfileRiskResponses = {
  200: unknown;
};

export type ProfilesControllerFindOneData = {
  body?: never;
  path: {
    profileId: string;
  };
  query?: never;
  url: '/v4/profiles/{profileId}';
};

export type ProfilesControllerFindOneResponses = {
  200: ProfileDto;
};

export type ProfilesControllerFindOneResponse =
  ProfilesControllerFindOneResponses[keyof ProfilesControllerFindOneResponses];

export type ProfilesControllerUpdateData = {
  body: UpdateProfileDto;
  path: {
    profileId: string;
  };
  query?: never;
  url: '/v4/profiles/{profileId}';
};

export type ProfilesControllerUpdateResponses = {
  200: ProfileDto;
};

export type ProfilesControllerUpdateResponse =
  ProfilesControllerUpdateResponses[keyof ProfilesControllerUpdateResponses];

export type ProfilesControllerGetScreenerCycleData = {
  body?: never;
  path: {
    profileId: string;
  };
  query?: never;
  url: '/v4/profiles/{profileId}/screener-cycle';
};

export type ProfilesControllerGetScreenerCycleResponses = {
  200: ScreenerCycleDto;
};

export type ProfilesControllerGetScreenerCycleResponse =
  ProfilesControllerGetScreenerCycleResponses[keyof ProfilesControllerGetScreenerCycleResponses];

export type ProfilesControllerVerifyWorkEmailData = {
  body?: never;
  path: {
    profileId: string;
    token: string;
  };
  query?: never;
  url: '/v4/profiles/{profileId}/verify/work-email/{token}';
};

export type ProfilesControllerVerifyWorkEmailResponses = {
  200: unknown;
};

export type ProfilesControllerFindByUserIdData = {
  body?: never;
  path: {
    userId: string;
  };
  query?: never;
  url: '/v4/profiles/user/{userId}';
};

export type ProfilesControllerFindByUserIdResponses = {
  200: ProfileDto;
};

export type ProfilesControllerFindByUserIdResponse =
  ProfilesControllerFindByUserIdResponses[keyof ProfilesControllerFindByUserIdResponses];

export type ProfilesControllerFindByEmailData = {
  body?: never;
  path: {
    email: string;
  };
  query?: never;
  url: '/v4/profiles/email/{email}';
};

export type ProfilesControllerFindByEmailResponses = {
  200: ProfileDto;
};

export type ProfilesControllerFindByEmailResponse =
  ProfilesControllerFindByEmailResponses[keyof ProfilesControllerFindByEmailResponses];

export type ProfilesControllerProfileParticipationStatsForProfileData = {
  body?: never;
  path: {
    profileId: string;
  };
  query: {
    organizationId: string;
    teamId: string;
  };
  url: '/v4/profiles/{profileId}/stats';
};

export type ProfilesControllerProfileParticipationStatsForProfileResponses = {
  200: SurveyResponseProfileStats;
};

export type ProfilesControllerProfileParticipationStatsForProfileResponse =
  ProfilesControllerProfileParticipationStatsForProfileResponses[keyof ProfilesControllerProfileParticipationStatsForProfileResponses];

export type ProfilesControllerHandleRejectedProfileData = {
  body?: never;
  path: {
    profileId: string;
  };
  query?: never;
  url: '/v4/profiles/{profileId}/status/rejected';
};

export type ProfilesControllerHandleRejectedProfileResponses = {
  200: unknown;
};

export type ProfilesControllerRecheckRiskHiddenResponsesData = {
  body: RecheckRiskHiddenResponsesDto;
  path?: never;
  query?: never;
  url: '/v4/profiles/risk-hidden-responses/recheck';
};

export type ProfilesControllerRecheckRiskHiddenResponsesResponses = {
  200: RecheckRiskHiddenResponsesResponseDto;
  201: RecheckRiskHiddenResponsesResponseDto;
};

export type ProfilesControllerRecheckRiskHiddenResponsesResponse =
  ProfilesControllerRecheckRiskHiddenResponsesResponses[keyof ProfilesControllerRecheckRiskHiddenResponsesResponses];

export type ProfilesControllerRejectProfilesData = {
  body: RejectProfilesDto;
  path?: never;
  query?: never;
  url: '/v4/profiles/reject';
};

export type ProfilesControllerRejectProfilesResponses = {
  200: RejectProfilesResponseDto;
  201: RejectProfilesResponseDto;
};

export type ProfilesControllerRejectProfilesResponse =
  ProfilesControllerRejectProfilesResponses[keyof ProfilesControllerRejectProfilesResponses];

export type ProfilesControllerFindExternalResearcherWithoutProfilePictureData =
  {
    body?: never;
    path?: never;
    query?: {
      limit?: number;
    };
    url: '/v4/profiles/externalresearchers/missing-picture';
  };

export type ProfilesControllerFindExternalResearcherWithoutProfilePictureResponses =
  {
    200: Array<ProfileExternalResearcherUserIdDto>;
  };

export type ProfilesControllerFindExternalResearcherWithoutProfilePictureResponse =
  ProfilesControllerFindExternalResearcherWithoutProfilePictureResponses[keyof ProfilesControllerFindExternalResearcherWithoutProfilePictureResponses];

export type MissingCompanyDomainsControllerGetMissingCompanyDomainsData = {
  body?: never;
  path?: never;
  query: {
    page?: number;
    pageSize?: number;
    includeCount?: boolean;
    status: MissingCompanyDomainsStatus;
  };
  url: '/v4/missing-domains';
};

export type MissingCompanyDomainsControllerGetMissingCompanyDomainsResponses = {
  200: Array<MissingDomainToProcessDto>;
};

export type MissingCompanyDomainsControllerGetMissingCompanyDomainsResponse =
  MissingCompanyDomainsControllerGetMissingCompanyDomainsResponses[keyof MissingCompanyDomainsControllerGetMissingCompanyDomainsResponses];

export type MissingCompanyDomainsControllerListMissingCompanyDomainsData = {
  body?: never;
  path?: never;
  query?: {
    page?: number;
    pageSize?: number;
    includeCount?: boolean;
    status?: MissingCompanyDomainsStatus;
    statuses?: Array<MissingCompanyDomainsStatus>;
    search?: string;
    /**
     * Only return records with _id at or after this ObjectId. Since ObjectIds
     * embed their creation time, this cuts the list to records created on or
     * after a chosen moment (e.g. when website-check data started flowing).
     */
    cutoffId?: string;
  };
  url: '/v4/missing-domains/list';
};

export type MissingCompanyDomainsControllerListMissingCompanyDomainsResponses =
  {
    200: MissingCompanyDomainsPaginatedDto;
  };

export type MissingCompanyDomainsControllerListMissingCompanyDomainsResponse =
  MissingCompanyDomainsControllerListMissingCompanyDomainsResponses[keyof MissingCompanyDomainsControllerListMissingCompanyDomainsResponses];

export type MissingCompanyDomainsControllerUpDateDomainData = {
  body?: never;
  path: {
    domainName: string;
  };
  query?: {
    isClearbitUpdate?: boolean;
    /**
     * Set by the missing-domain cron's security checks (domain age / MX) when
     * finalizing a domain's status without consulting Clearbit — updates the
     * status but leaves lastClearbitCheckedAt untouched.
     */
    isSecurityUpdate?: boolean;
    status?: MissingCompanyDomainsStatus;
    reason?: string;
  };
  url: '/v4/missing-domains/name/{domainName}';
};

export type MissingCompanyDomainsControllerUpDateDomainResponses = {
  200: unknown;
};

export type MissingCompanyDomainsControllerGetMissingDomainReviewData = {
  body?: never;
  path: {
    domainName: string;
  };
  query?: never;
  url: '/v4/missing-domains/name/{domainName}/review';
};

export type MissingCompanyDomainsControllerGetMissingDomainReviewResponses = {
  200: MissingDomainReviewDto;
};

export type MissingCompanyDomainsControllerGetMissingDomainReviewResponse =
  MissingCompanyDomainsControllerGetMissingDomainReviewResponses[keyof MissingCompanyDomainsControllerGetMissingDomainReviewResponses];

export type MissingCompanyDomainsControllerUpdateWebsiteCheckData = {
  body: UpdateWebsiteCheckDto;
  path: {
    domainName: string;
  };
  query?: never;
  url: '/v4/missing-domains/name/{domainName}/website-check';
};

export type MissingCompanyDomainsControllerUpdateWebsiteCheckResponses = {
  200: unknown;
};

export type MissingCompanyDomainsControllerApplyVerificationData = {
  body: ApplyVerificationDto;
  path: {
    domainName: string;
  };
  query?: never;
  url: '/v4/missing-domains/name/{domainName}/verification';
};

export type MissingCompanyDomainsControllerApplyVerificationResponses = {
  200: VerificationResultDto;
};

export type MissingCompanyDomainsControllerApplyVerificationResponse =
  MissingCompanyDomainsControllerApplyVerificationResponses[keyof MissingCompanyDomainsControllerApplyVerificationResponses];

export type MissingCompanyDomainsControllerRecordSecurityCheckAttemptData = {
  body?: never;
  path: {
    domainName: string;
  };
  query?: never;
  url: '/v4/missing-domains/name/{domainName}/security-check-attempt';
};

export type MissingCompanyDomainsControllerRecordSecurityCheckAttemptResponses =
  {
    200: SecurityCheckAttemptResponseDto;
  };

export type MissingCompanyDomainsControllerRecordSecurityCheckAttemptResponse =
  MissingCompanyDomainsControllerRecordSecurityCheckAttemptResponses[keyof MissingCompanyDomainsControllerRecordSecurityCheckAttemptResponses];

export type MissingCompanyDomainsControllerRejectDomainProfilesData = {
  body?: never;
  path: {
    domainName: string;
  };
  query: {
    reason: string;
  };
  url: '/v4/missing-domains/name/{domainName}/reject-profiles';
};

export type MissingCompanyDomainsControllerRejectDomainProfilesResponses = {
  200: RejectDomainProfilesResponseDto;
};

export type MissingCompanyDomainsControllerRejectDomainProfilesResponse =
  MissingCompanyDomainsControllerRejectDomainProfilesResponses[keyof MissingCompanyDomainsControllerRejectDomainProfilesResponses];

export type MissingCompanyDomainsControllerBulkUpdateMissingDomainStatusData = {
  body: BulkUpdateMissingDomainStatusDto;
  path?: never;
  query?: never;
  url: '/v4/missing-domains/status';
};

export type MissingCompanyDomainsControllerBulkUpdateMissingDomainStatusResponses =
  {
    200: BulkUpdateMissingDomainStatusResponseDto;
  };

export type MissingCompanyDomainsControllerBulkUpdateMissingDomainStatusResponse =
  MissingCompanyDomainsControllerBulkUpdateMissingDomainStatusResponses[keyof MissingCompanyDomainsControllerBulkUpdateMissingDomainStatusResponses];

export type MissingCompanyDomainsControllerUpdateMissingDomainStatusData = {
  body?: never;
  path: {
    domainName: string;
  };
  query: {
    status: MissingCompanyDomainsStatus;
    reason: string;
  };
  url: '/v4/missing-domains/name/{domainName}/status';
};

export type MissingCompanyDomainsControllerUpdateMissingDomainStatusResponses =
  {
    200: MissingCompanyDomain;
  };

export type MissingCompanyDomainsControllerUpdateMissingDomainStatusResponse =
  MissingCompanyDomainsControllerUpdateMissingDomainStatusResponses[keyof MissingCompanyDomainsControllerUpdateMissingDomainStatusResponses];

export type CompaniesControllerUpsertCompanyByDomainData = {
  body?: never;
  path: {
    domainName: string;
  };
  query?: never;
  url: '/v4/companies/domain/{domainName}';
};

export type CompaniesControllerUpsertCompanyByDomainResponses = {
  200: unknown;
};

export type LeadsControllerFindAllData = {
  body?: never;
  path?: never;
  query?: {
    page?: number;
    pageSize?: number;
    includeCount?: boolean;
    modifiedSince?: string;
  };
  url: '/v4/leads';
};

export type LeadsControllerFindAllResponses = {
  200: LeadsPaginatedDto;
};

export type LeadsControllerFindAllResponse =
  LeadsControllerFindAllResponses[keyof LeadsControllerFindAllResponses];

export type LeadsControllerCreateData = {
  body: CreateLeadDto;
  path?: never;
  query?: never;
  url: '/v4/leads';
};

export type LeadsControllerCreateResponses = {
  201: Lead;
};

export type LeadsControllerCreateResponse =
  LeadsControllerCreateResponses[keyof LeadsControllerCreateResponses];

export type LeadsControllerCreateManyData = {
  body: CreateLeadsBulkDto;
  path?: never;
  query?: never;
  url: '/v4/leads/bulk';
};

export type LeadsControllerCreateManyResponses = {
  201: Array<Lead>;
};

export type LeadsControllerCreateManyResponse =
  LeadsControllerCreateManyResponses[keyof LeadsControllerCreateManyResponses];

export type LeadsControllerFindUtmTrackedUsersData = {
  body?: never;
  path?: never;
  query: {
    createdSince: string;
    initialUtmUtmId?: string;
    initialUtmAdvertId?: string;
    page?: number;
    pageSize?: number;
    includeCount?: boolean;
  };
  url: '/v4/leads/utm-tracked';
};

export type LeadsControllerFindUtmTrackedUsersResponses = {
  200: UtmTrackedUsersPaginatedDto;
};

export type LeadsControllerFindUtmTrackedUsersResponse =
  LeadsControllerFindUtmTrackedUsersResponses[keyof LeadsControllerFindUtmTrackedUsersResponses];

export type LeadsControllerFindUtmTrackedResponsesData = {
  body?: never;
  path?: never;
  query: {
    createdSince: string;
    initialUtmUtmId?: string;
    initialUtmAdvertId?: string;
    status?: ScreenerResponseStatus;
    page?: number;
    pageSize?: number;
    includeCount?: boolean;
  };
  url: '/v4/leads/utm-tracked-responses';
};

export type LeadsControllerFindUtmTrackedResponsesResponses = {
  200: UtmTrackedResponsesPaginatedDto;
};

export type LeadsControllerFindUtmTrackedResponsesResponse =
  LeadsControllerFindUtmTrackedResponsesResponses[keyof LeadsControllerFindUtmTrackedResponsesResponses];

export type LeadsControllerDeleteData = {
  body?: never;
  path: {
    id: string;
  };
  query?: never;
  url: '/v4/leads/{id}';
};

export type LeadsControllerDeleteResponses = {
  204: void;
};

export type LeadsControllerDeleteResponse =
  LeadsControllerDeleteResponses[keyof LeadsControllerDeleteResponses];

export type LeadsControllerFindOneData = {
  body?: never;
  path: {
    id: string;
  };
  query?: never;
  url: '/v4/leads/{id}';
};

export type LeadsControllerFindOneResponses = {
  200: Lead;
};

export type LeadsControllerFindOneResponse =
  LeadsControllerFindOneResponses[keyof LeadsControllerFindOneResponses];

export type LeadsControllerUpdateData = {
  body: UpdateLeadDto;
  path: {
    id: string;
  };
  query?: never;
  url: '/v4/leads/{id}';
};

export type LeadsControllerUpdateResponses = {
  200: Lead;
};

export type LeadsControllerUpdateResponse =
  LeadsControllerUpdateResponses[keyof LeadsControllerUpdateResponses];

export type LeadsControllerValidateLeadData = {
  body: ValidateLeadDto;
  path?: never;
  query?: never;
  url: '/v4/leads/validate';
};

export type LeadsControllerValidateLeadResponses = {
  201: ValidatedLeadDto;
};

export type LeadsControllerValidateLeadResponse =
  LeadsControllerValidateLeadResponses[keyof LeadsControllerValidateLeadResponses];

export type LeadsControllerConsumeLeadData = {
  body: ConsumeLeadDto;
  path?: never;
  query?: never;
  url: '/v4/leads/consume';
};

export type LeadsControllerConsumeLeadResponses = {
  201: unknown;
};

export type LeadsControllerRejectLeadData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v4/leads/reject';
};

export type LeadsControllerRejectLeadResponses = {
  201: unknown;
};

export type AtlasAppAdminControllerSyncData = {
  body: SyncAtlasAppDto;
  path?: never;
  query?: never;
  url: '/v4/atlas-app-admin/sync';
};

export type AtlasAppAdminControllerSyncResponses = {
  201: AtlasAppSyncResult;
};

export type AtlasAppAdminControllerSyncResponse =
  AtlasAppAdminControllerSyncResponses[keyof AtlasAppAdminControllerSyncResponses];

export type CspControllerReportCspViolationData = {
  body: CspReportDto;
  path?: never;
  query?: never;
  url: '/v4/csp/report';
};

export type CspControllerReportCspViolationResponses = {
  201: unknown;
};

export type EmailQueueControllerQueueEmailData = {
  body: QueueEmailForSendingDto;
  path?: never;
  query?: never;
  url: '/v4/email-queue';
};

export type EmailQueueControllerQueueEmailResponses = {
  201: unknown;
};

export type EmailQueueControllerQueueEmailNewMessageAvailableData = {
  body: EmailQueueNewMessageAvailableDto;
  path: {
    conversationUid: string;
  };
  query?: never;
  url: '/v4/email-queue/new-message-available/{conversationUid}';
};

export type EmailQueueControllerQueueEmailNewMessageAvailableResponses = {
  201: unknown;
};

export type FeedbackControllerFindData = {
  body?: never;
  path?: never;
  query: {
    context: FeedbackContext;
    /**
     * Interview-scoped lookup id. Provide exactly one of interviewResponseId or projectId.
     */
    interviewResponseId?: string;
    /**
     * Project-scoped lookup id. Provide exactly one of projectId or interviewResponseId.
     */
    projectId?: string;
  };
  url: '/v4/feedback';
};

export type FeedbackControllerFindResponses = {
  200: FeedbackDto | null;
};

export type FeedbackControllerFindResponse =
  FeedbackControllerFindResponses[keyof FeedbackControllerFindResponses];

export type FeedbackControllerCreateData = {
  body: CreateFeedbackDto;
  path?: never;
  query?: never;
  url: '/v4/feedback';
};

export type FeedbackControllerCreateResponses = {
  201: FeedbackDto;
};

export type FeedbackControllerCreateResponse =
  FeedbackControllerCreateResponses[keyof FeedbackControllerCreateResponses];

export type FeedbackControllerUpdateData = {
  body: UpdateFeedbackDto;
  path: {
    id: string;
  };
  query?: never;
  url: '/v4/feedback/{id}';
};

export type FeedbackControllerUpdateResponses = {
  200: FeedbackDto;
};

export type FeedbackControllerUpdateResponse =
  FeedbackControllerUpdateResponses[keyof FeedbackControllerUpdateResponses];

export type FeedbackControllerListData = {
  body?: never;
  path?: never;
  query?: {
    page?: number;
    pageSize?: number;
    includeCount?: boolean;
    context?: FeedbackContext;
  };
  url: '/v4/feedback/list';
};

export type FeedbackControllerListResponses = {
  200: FeedbacksPaginatedDto;
};

export type FeedbackControllerListResponse =
  FeedbackControllerListResponses[keyof FeedbackControllerListResponses];

export type FlaggedRespondentsControllerFindAllByOrganizationAndProfileData = {
  body?: never;
  path: {
    profileId: string;
    organizationId: string;
  };
  query?: never;
  url: '/v4/flagged-respondents/organizations/{organizationId}/profiles/{profileId}';
};

export type FlaggedRespondentsControllerFindAllByOrganizationAndProfileResponses =
  {
    200: Array<FlaggedRespondent>;
  };

export type FlaggedRespondentsControllerFindAllByOrganizationAndProfileResponse =
  FlaggedRespondentsControllerFindAllByOrganizationAndProfileResponses[keyof FlaggedRespondentsControllerFindAllByOrganizationAndProfileResponses];

export type FlaggedRespondentsControllerFindAllByOrganizationAndProfileQueryData =
  {
    body?: never;
    path?: never;
    query: {
      profileId: string;
      organizationId: string;
    };
    url: '/v4/flagged-respondents';
  };

export type FlaggedRespondentsControllerFindAllByOrganizationAndProfileQueryResponses =
  {
    200: Array<FlaggedRespondent>;
  };

export type FlaggedRespondentsControllerFindAllByOrganizationAndProfileQueryResponse =
  FlaggedRespondentsControllerFindAllByOrganizationAndProfileQueryResponses[keyof FlaggedRespondentsControllerFindAllByOrganizationAndProfileQueryResponses];

export type FlaggedRespondentsControllerCreateData = {
  body: CreateFlaggedRespondentDto;
  path?: never;
  query?: never;
  url: '/v4/flagged-respondents';
};

export type FlaggedRespondentsControllerCreateResponses = {
  201: FlaggedRespondent;
};

export type FlaggedRespondentsControllerCreateResponse =
  FlaggedRespondentsControllerCreateResponses[keyof FlaggedRespondentsControllerCreateResponses];

export type FlaggedRespondentsControllerRemoveData = {
  body?: never;
  path: {
    id: string;
  };
  query?: never;
  url: '/v4/flagged-respondents/{id}';
};

export type FlaggedRespondentsControllerRemoveResponses = {
  200: FlaggedRespondent;
};

export type FlaggedRespondentsControllerRemoveResponse =
  FlaggedRespondentsControllerRemoveResponses[keyof FlaggedRespondentsControllerRemoveResponses];

export type FlaggedRespondentsControllerFindOneData = {
  body?: never;
  path: {
    id: string;
  };
  query?: never;
  url: '/v4/flagged-respondents/{id}';
};

export type FlaggedRespondentsControllerFindOneResponses = {
  200: FlaggedRespondent;
};

export type FlaggedRespondentsControllerFindOneResponse =
  FlaggedRespondentsControllerFindOneResponses[keyof FlaggedRespondentsControllerFindOneResponses];

export type FlaggedRespondentsControllerUpdateData = {
  body: UpdateFlaggedRespondentDto;
  path: {
    id: string;
  };
  query?: never;
  url: '/v4/flagged-respondents/{id}';
};

export type FlaggedRespondentsControllerUpdateResponses = {
  200: FlaggedRespondent;
};

export type FlaggedRespondentsControllerUpdateResponse =
  FlaggedRespondentsControllerUpdateResponses[keyof FlaggedRespondentsControllerUpdateResponses];

export type ForbiddenWorkDomainsControllerAddForbiddenWorkDomainData = {
  body: AddForbiddenWorkDomainDto;
  path?: never;
  query?: never;
  url: '/v4/forbidden-work-domains';
};

export type ForbiddenWorkDomainsControllerAddForbiddenWorkDomainResponses = {
  201: unknown;
};

export type GdprDeletionsControllerFindAllData = {
  body?: never;
  path?: never;
  query?: {
    page?: number;
    pageSize?: number;
    includeCount?: boolean;
    status?: GdprDeletionStatus;
    id?: string;
    userId?: string;
    createdAt?: string;
    deletedAt?: string;
  };
  url: '/v4/gdpr-deletions/list';
};

export type GdprDeletionsControllerFindAllResponses = {
  200: GdprDeletionsPaginatedDto;
};

export type GdprDeletionsControllerFindAllResponse =
  GdprDeletionsControllerFindAllResponses[keyof GdprDeletionsControllerFindAllResponses];

export type GdprDeletionsControllerCreateData = {
  body: CreateGdprDeletionDto;
  path?: never;
  query?: never;
  url: '/v4/gdpr-deletions';
};

export type GdprDeletionsControllerCreateResponses = {
  201: GdprDeletion;
};

export type GdprDeletionsControllerCreateResponse =
  GdprDeletionsControllerCreateResponses[keyof GdprDeletionsControllerCreateResponses];

export type GdprDeletionsControllerUpdateData = {
  body: UpdateGdprDeletionDto;
  path: {
    gdprDeletionId: string;
  };
  query?: never;
  url: '/v4/gdpr-deletions/{gdprDeletionId}';
};

export type GdprDeletionsControllerUpdateResponses = {
  200: GdprDeletion;
};

export type GdprDeletionsControllerUpdateResponse =
  GdprDeletionsControllerUpdateResponses[keyof GdprDeletionsControllerUpdateResponses];

export type GhostInspectorControllerDeleteOrganizationData = {
  body?: never;
  path: {
    orgId: string;
  };
  query: {
    commit: boolean;
  };
  url: '/v4/ghost-inspector/{orgId}';
};

export type GhostInspectorControllerDeleteOrganizationErrors = {
  /**
   * If no ghost inspector organization is found, it will return a HttpStatus.NOT_FOUND error.
   */
  404: unknown;
};

export type GhostInspectorControllerDeleteOrganizationResponses = {
  200: unknown;
};

export type ParticipantPaymentsControllerGetPaymentBatchesPendingPaymentData = {
  body?: never;
  path?: never;
  query?: {
    limit?: number;
    oldestFirst?: boolean;
    origin?: PaymentBatchOrigin;
  };
  url: '/v4/participant-payments/payment-batches/pending-payment';
};

export type ParticipantPaymentsControllerGetPaymentBatchesPendingPaymentResponses =
  {
    200: Array<PaymentBatch>;
  };

export type ParticipantPaymentsControllerGetPaymentBatchesPendingPaymentResponse =
  ParticipantPaymentsControllerGetPaymentBatchesPendingPaymentResponses[keyof ParticipantPaymentsControllerGetPaymentBatchesPendingPaymentResponses];

export type ParticipantPaymentsControllerGetPaymentBatchesPendingConfirmationData =
  {
    body?: never;
    path?: never;
    query?: {
      limit?: number;
      oldestFirst?: boolean;
      delayMinutesAfterCompleted?: number;
      maxRetryPayPalCount?: number;
    };
    url: '/v4/participant-payments/payment-batches/pending-confirmation';
  };

export type ParticipantPaymentsControllerGetPaymentBatchesPendingConfirmationResponses =
  {
    200: Array<PaymentBatch>;
  };

export type ParticipantPaymentsControllerGetPaymentBatchesPendingConfirmationResponse =
  ParticipantPaymentsControllerGetPaymentBatchesPendingConfirmationResponses[keyof ParticipantPaymentsControllerGetPaymentBatchesPendingConfirmationResponses];

export type ParticipantPaymentsControllerUpdateManyPaymentBatchData = {
  body: UpdateManyPaymentBatchDto;
  path?: never;
  query?: never;
  url: '/v4/participant-payments/payment-batches';
};

export type ParticipantPaymentsControllerUpdateManyPaymentBatchResponses = {
  200: Array<PaymentBatch>;
};

export type ParticipantPaymentsControllerUpdateManyPaymentBatchResponse =
  ParticipantPaymentsControllerUpdateManyPaymentBatchResponses[keyof ParticipantPaymentsControllerUpdateManyPaymentBatchResponses];

export type ParticipantPaymentsControllerCreatePaymentBatchesData = {
  body: CreateBulkPaymentBatchesDto;
  path?: never;
  query?: never;
  url: '/v4/participant-payments/payment-batches';
};

export type ParticipantPaymentsControllerCreatePaymentBatchesResponses = {
  201: Array<PaymentBatch>;
};

export type ParticipantPaymentsControllerCreatePaymentBatchesResponse =
  ParticipantPaymentsControllerCreatePaymentBatchesResponses[keyof ParticipantPaymentsControllerCreatePaymentBatchesResponses];

export type ParticipantPaymentsControllerGetPendingRespondentPayoutsBeforeDateData =
  {
    body: PendingRespondentPayoutsDto;
    path?: never;
    query?: never;
    url: '/v4/participant-payments/respondent-payouts/pending-payments';
  };

export type ParticipantPaymentsControllerGetPendingRespondentPayoutsBeforeDateResponses =
  {
    200: Array<RespondentPayout>;
  };

export type ParticipantPaymentsControllerGetPendingRespondentPayoutsBeforeDateResponse =
  ParticipantPaymentsControllerGetPendingRespondentPayoutsBeforeDateResponses[keyof ParticipantPaymentsControllerGetPendingRespondentPayoutsBeforeDateResponses];

export type ParticipantPaymentsControllerUpdatePaymentBatchData = {
  body: UpdatePaymentBatchDto;
  path: {
    paymentBatchId: string;
  };
  query?: never;
  url: '/v4/participant-payments/payment-batches/{paymentBatchId}';
};

export type ParticipantPaymentsControllerUpdatePaymentBatchResponses = {
  200: PaymentBatch;
};

export type ParticipantPaymentsControllerUpdatePaymentBatchResponse =
  ParticipantPaymentsControllerUpdatePaymentBatchResponses[keyof ParticipantPaymentsControllerUpdatePaymentBatchResponses];

export type ParticipantPaymentsControllerUpdateRespondentPayoutsData = {
  body: UpdateRespondentPayoutsDto;
  path: {
    respondentPayoutId: string;
  };
  query?: never;
  url: '/v4/participant-payments/respondent-payouts/{respondentPayoutId}';
};

export type ParticipantPaymentsControllerUpdateRespondentPayoutsResponses = {
  200: RespondentPayout;
};

export type ParticipantPaymentsControllerUpdateRespondentPayoutsResponse =
  ParticipantPaymentsControllerUpdateRespondentPayoutsResponses[keyof ParticipantPaymentsControllerUpdateRespondentPayoutsResponses];

export type ParticipantPaymentsControllerUpdateManyRespondentPayoutsData = {
  body: UpdateManyRespondentPayoutsDto;
  path?: never;
  query?: never;
  url: '/v4/participant-payments/respondent-payouts';
};

export type ParticipantPaymentsControllerUpdateManyRespondentPayoutsResponses =
  {
    200: Array<RespondentPayout>;
  };

export type ParticipantPaymentsControllerUpdateManyRespondentPayoutsResponse =
  ParticipantPaymentsControllerUpdateManyRespondentPayoutsResponses[keyof ParticipantPaymentsControllerUpdateManyRespondentPayoutsResponses];

export type ParticipantMatchingControllerFindMatchedProjectsForParticipantData =
  {
    body?: never;
    path: {
      profileId: string;
    };
    query?: {
      page?: number;
      pageSize?: number;
      includeCount?: boolean;
      kindsOfResearchStudy?: Array<KindOfResearchStudy>;
      minIncentive?: number;
      maxIncentive?: number;
      minTimeMinutesRequired?: number;
      maxTimeMinutesRequired?: number;
      sort?: RespondentDashboardSort;
      onlyShowMatched?: boolean;
      /**
       * With onlyShowMatched: also return projects flagged boostProjectFeed.
       */
      includeBoosted?: boolean;
      showHiddenProjects?: boolean;
      queryText?: string;
      /**
       * if showEligible is true, then dataOfBirth, gender, educationLevel, ethnicity, country are required
       */
      showEligible?: boolean;
      dateOfBirth?: string;
      gender?: Gender;
      educationLevel?: EducationLevel;
      ethnicity?: Ethnicities;
      country?: ProjectCountryCode;
    };
    url: '/v4/participant/matching/projects/search/profiles/{profileId}';
  };

export type ParticipantMatchingControllerFindMatchedProjectsForParticipantResponses =
  {
    200: MatchingProjectsPaginatedDto;
  };

export type ParticipantMatchingControllerFindMatchedProjectsForParticipantResponse =
  ParticipantMatchingControllerFindMatchedProjectsForParticipantResponses[keyof ParticipantMatchingControllerFindMatchedProjectsForParticipantResponses];

export type ParticipantMatchingControllerCheckQualificationForProjectData = {
  body?: never;
  path: {
    projectId: string;
    profileId: string;
  };
  query?: never;
  url: '/v4/participant/matching/projects/{projectId}/qualify/profiles/{profileId}';
};

export type ParticipantMatchingControllerCheckQualificationForProjectResponses =
  {
    200: PrequalifiesForProjectResultsDto;
  };

export type ParticipantMatchingControllerCheckQualificationForProjectResponse =
  ParticipantMatchingControllerCheckQualificationForProjectResponses[keyof ParticipantMatchingControllerCheckQualificationForProjectResponses];

export type ParticipantMatchingControllerUpdateProfileAndCheckQualificationData =
  {
    body: UpdateProfileDto;
    path: {
      projectId: string;
      profileId: string;
    };
    query?: never;
    url: '/v4/participant/matching/projects/{projectId}/qualify/profiles/{profileId}';
  };

export type ParticipantMatchingControllerUpdateProfileAndCheckQualificationResponses =
  {
    200: PrequalifiesForProjectResultsDto;
  };

export type ParticipantMatchingControllerUpdateProfileAndCheckQualificationResponse =
  ParticipantMatchingControllerUpdateProfileAndCheckQualificationResponses[keyof ParticipantMatchingControllerUpdateProfileAndCheckQualificationResponses];

export type AdminRepairControllerAdminRepairData = {
  body: RunRepairActiontDto;
  path?: never;
  query?: never;
  url: '/v4/admin/repair';
};

export type AdminRepairControllerAdminRepairResponses = {
  200: RunRepairActionResponseDto;
};

export type AdminRepairControllerAdminRepairResponse =
  AdminRepairControllerAdminRepairResponses[keyof AdminRepairControllerAdminRepairResponses];

export type AdminRepairControllerAdminRepairMethodologyPricingData = {
  body: RunRepairActiontDto;
  path?: never;
  query?: never;
  url: '/v4/admin/repair/methodology-pricing';
};

export type AdminRepairControllerAdminRepairMethodologyPricingResponses = {
  200: RunRepairActionResponseDto;
};

export type AdminRepairControllerAdminRepairMethodologyPricingResponse =
  AdminRepairControllerAdminRepairMethodologyPricingResponses[keyof AdminRepairControllerAdminRepairMethodologyPricingResponses];

export type AdminRepairControllerAdminRepairPricingPlanCapabilitiesData = {
  body: RunRepairActiontDto;
  path?: never;
  query?: never;
  url: '/v4/admin/repair/pricing-plan-capabilities';
};

export type AdminRepairControllerAdminRepairPricingPlanCapabilitiesResponses = {
  200: RunRepairActionResponseDto;
};

export type AdminRepairControllerAdminRepairPricingPlanCapabilitiesResponse =
  AdminRepairControllerAdminRepairPricingPlanCapabilitiesResponses[keyof AdminRepairControllerAdminRepairPricingPlanCapabilitiesResponses];

export type AdminRepairControllerAdminRepairJobTitleGroupingData = {
  body: RunRepairActiontDto;
  path?: never;
  query?: never;
  url: '/v4/admin/repair/job-title-grouping';
};

export type AdminRepairControllerAdminRepairJobTitleGroupingResponses = {
  200: RunRepairActionResponseDto;
};

export type AdminRepairControllerAdminRepairJobTitleGroupingResponse =
  AdminRepairControllerAdminRepairJobTitleGroupingResponses[keyof AdminRepairControllerAdminRepairJobTitleGroupingResponses];

export type AdminRepairControllerAdminRepairProjectTitleFamiliesData = {
  body: RunRepairActiontDto;
  path?: never;
  query?: never;
  url: '/v4/admin/repair/project-title-families-backfill';
};

export type AdminRepairControllerAdminRepairProjectTitleFamiliesResponses = {
  200: RunRepairActionResponseDto;
};

export type AdminRepairControllerAdminRepairProjectTitleFamiliesResponse =
  AdminRepairControllerAdminRepairProjectTitleFamiliesResponses[keyof AdminRepairControllerAdminRepairProjectTitleFamiliesResponses];

export type AdminRepairControllerAdminRepairProfilesRollupData = {
  body: RunRepairActiontDto;
  path?: never;
  query?: never;
  url: '/v4/admin/repair/profiles-rollup-backfill';
};

export type AdminRepairControllerAdminRepairProfilesRollupResponses = {
  200: RunRepairActionResponseDto;
};

export type AdminRepairControllerAdminRepairProfilesRollupResponse =
  AdminRepairControllerAdminRepairProfilesRollupResponses[keyof AdminRepairControllerAdminRepairProfilesRollupResponses];

export type AdminRepairControllerAdminRepairProfileRiskManualReviewAuditData = {
  body: RunRepairActiontDto;
  path?: never;
  query?: never;
  url: '/v4/admin/repair/profile-risk-manual-review-audit';
};

export type AdminRepairControllerAdminRepairProfileRiskManualReviewAuditResponses =
  {
    200: RunRepairActionResponseDto;
  };

export type AdminRepairControllerAdminRepairProfileRiskManualReviewAuditResponse =
  AdminRepairControllerAdminRepairProfileRiskManualReviewAuditResponses[keyof AdminRepairControllerAdminRepairProfileRiskManualReviewAuditResponses];

export type AdminRepairControllerAdminRepairProfileRiskPlaidTemplateIdData = {
  body: RunRepairActiontDto;
  path?: never;
  query?: never;
  url: '/v4/admin/repair/profile-risk-plaid-template-id';
};

export type AdminRepairControllerAdminRepairProfileRiskPlaidTemplateIdResponses =
  {
    200: RunRepairActionResponseDto;
  };

export type AdminRepairControllerAdminRepairProfileRiskPlaidTemplateIdResponse =
  AdminRepairControllerAdminRepairProfileRiskPlaidTemplateIdResponses[keyof AdminRepairControllerAdminRepairProfileRiskPlaidTemplateIdResponses];

export type AdminRepairControllerAdminRepairProfileProjectMatchEmailEligibilityData =
  {
    body: RunRepairActiontDto;
    path?: never;
    query?: never;
    url: '/v4/admin/repair/profile-project-match-email-eligibility';
  };

export type AdminRepairControllerAdminRepairProfileProjectMatchEmailEligibilityResponses =
  {
    200: RunRepairActionResponseDto;
  };

export type AdminRepairControllerAdminRepairProfileProjectMatchEmailEligibilityResponse =
  AdminRepairControllerAdminRepairProfileProjectMatchEmailEligibilityResponses[keyof AdminRepairControllerAdminRepairProfileProjectMatchEmailEligibilityResponses];

export type AdminRepairControllerAdminRepairProfileProjectMatchTargetedResearchInvitationData =
  {
    body: RunRepairActiontDto;
    path?: never;
    query?: never;
    url: '/v4/admin/repair/profile-project-match-targeted-research-invitation';
  };

export type AdminRepairControllerAdminRepairProfileProjectMatchTargetedResearchInvitationResponses =
  {
    200: RunRepairActionResponseDto;
  };

export type AdminRepairControllerAdminRepairProfileProjectMatchTargetedResearchInvitationResponse =
  AdminRepairControllerAdminRepairProfileProjectMatchTargetedResearchInvitationResponses[keyof AdminRepairControllerAdminRepairProfileProjectMatchTargetedResearchInvitationResponses];

export type AdminRepairControllerAdminRepairAtlasDisableTriggersData = {
  body: AtlasListOfTriggersOptionalDto;
  path?: never;
  query?: never;
  url: '/v4/admin/repair/atlas/disable-triggers';
};

export type AdminRepairControllerAdminRepairAtlasDisableTriggersResponses = {
  200: Array<string>;
};

export type AdminRepairControllerAdminRepairAtlasDisableTriggersResponse =
  AdminRepairControllerAdminRepairAtlasDisableTriggersResponses[keyof AdminRepairControllerAdminRepairAtlasDisableTriggersResponses];

export type AdminRepairControllerAdminRepairAtlasDisableTriggersCatchupData = {
  body: AtlasListOfTriggersDto;
  path?: never;
  query?: never;
  url: '/v4/admin/repair/atlas/disable-triggers-catchup';
};

export type AdminRepairControllerAdminRepairAtlasDisableTriggersCatchupResponses =
  {
    200: unknown;
  };

export type AdminRepairControllerAdminRepairAtlasEnableTriggersCatchupData = {
  body: AtlasListOfTriggersDto;
  path?: never;
  query?: never;
  url: '/v4/admin/repair/atlas/enable-triggers-catchup';
};

export type AdminRepairControllerAdminRepairAtlasEnableTriggersCatchupResponses =
  {
    200: unknown;
  };

export type AdminRepairControllerAdminRepairAtlasEnableTriggersData = {
  body: AtlasListOfTriggersDto;
  path?: never;
  query?: never;
  url: '/v4/admin/repair/atlas/enable-triggers';
};

export type AdminRepairControllerAdminRepairAtlasEnableTriggersResponses = {
  200: unknown;
};

export type CreditsManagementControllerCancelData = {
  body: CancelPricingCreditDto;
  path: {
    creditId: string;
  };
  query?: never;
  url: '/v4/pricing/credits/{creditId}/cancel';
};

export type CreditsManagementControllerCancelResponses = {
  200: PricingCredit;
};

export type CreditsManagementControllerCancelResponse =
  CreditsManagementControllerCancelResponses[keyof CreditsManagementControllerCancelResponses];

export type CreditsManagementControllerExtendCreditData = {
  body: ExtendPricingCreditDto;
  path: {
    creditId: string;
  };
  query?: never;
  url: '/v4/pricing/credits/{creditId}/extend';
};

export type CreditsManagementControllerExtendCreditResponses = {
  200: PricingCredit;
};

export type CreditsManagementControllerExtendCreditResponse =
  CreditsManagementControllerExtendCreditResponses[keyof CreditsManagementControllerExtendCreditResponses];

export type CreditsManagementControllerRestoreData = {
  body: RestorePricingCreditDto;
  path: {
    creditId: string;
  };
  query?: never;
  url: '/v4/pricing/credits/{creditId}/restore';
};

export type CreditsManagementControllerRestoreResponses = {
  200: PricingCredit;
};

export type CreditsManagementControllerRestoreResponse =
  CreditsManagementControllerRestoreResponses[keyof CreditsManagementControllerRestoreResponses];

export type CreditsManagementControllerGetCreditExpirySummaryForTeamData = {
  body?: never;
  path: {
    teamId: string;
  };
  query?: {
    page?: number;
    pageSize?: number;
    includeCount?: boolean;
    paused?: boolean;
    onlyExpired?: boolean;
    excludeExpired?: boolean;
  };
  url: '/v4/pricing/teams/{teamId}/credits/expiry/summary';
};

export type CreditsManagementControllerGetCreditExpirySummaryForTeamResponses =
  {
    200: PricingCreditsExpirySummaryPaginatedDto;
  };

export type CreditsManagementControllerGetCreditExpirySummaryForTeamResponse =
  CreditsManagementControllerGetCreditExpirySummaryForTeamResponses[keyof CreditsManagementControllerGetCreditExpirySummaryForTeamResponses];

export type BalanceManagementControllerProcessBalanceManagementData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v4/pricing/balance-management/process';
};

export type BalanceManagementControllerProcessBalanceManagementResponses = {
  201: unknown;
};

export type BalancesControllerFindAllData = {
  body?: never;
  path: {
    teamId: string;
  };
  query?: {
    page?: number;
    pageSize?: number;
    includeCount?: boolean;
    /**
     * Character Limit: max 100
     */
    search?: string;
    type?: Array<string>;
    creditType?: string;
    fromDate?: string;
    toDate?: string;
  };
  url: '/v4/pricing/teams/{teamId}/balances';
};

export type BalancesControllerFindAllResponses = {
  200: BalancesPaginatedDto;
};

export type BalancesControllerFindAllResponse =
  BalancesControllerFindAllResponses[keyof BalancesControllerFindAllResponses];

export type BalancesControllerDownloadData = {
  body?: never;
  path: {
    teamId: string;
  };
  query?: {
    page?: number;
    pageSize?: number;
    includeCount?: boolean;
    /**
     * Character Limit: max 100
     */
    search?: string;
    type?: Array<string>;
    creditType?: string;
    fromDate?: string;
    toDate?: string;
  };
  url: '/v4/pricing/teams/{teamId}/balances/download';
};

export type BalancesControllerDownloadResponses = {
  /**
   * CSV file
   */
  200: Blob | File;
};

export type BalancesControllerDownloadResponse =
  BalancesControllerDownloadResponses[keyof BalancesControllerDownloadResponses];

export type BillingHistoryControllerFindAllForTeamData = {
  body?: never;
  path: {
    teamId: string;
  };
  query?: {
    page?: number;
    pageSize?: number;
    includeCount?: boolean;
    fromDate?: string;
    toDate?: string;
  };
  url: '/v4/pricing/teams/{teamId}/billing-history';
};

export type BillingHistoryControllerFindAllForTeamResponses = {
  200: BillingHistoryPaginatedDto;
};

export type BillingHistoryControllerFindAllForTeamResponse =
  BillingHistoryControllerFindAllForTeamResponses[keyof BillingHistoryControllerFindAllForTeamResponses];

export type ManageTeamPlansControllerManagePricingTeamCreateCustomPlanData = {
  body: ManagePricingTeamCreateCustomPlanDto;
  path: {
    teamId: string;
  };
  query?: never;
  url: '/v4/pricing/v3/teams/{teamId}/plan';
};

export type ManageTeamPlansControllerManagePricingTeamCreateCustomPlanResponses =
  {
    200: PricingTeamPlan;
  };

export type ManageTeamPlansControllerManagePricingTeamCreateCustomPlanResponse =
  ManageTeamPlansControllerManagePricingTeamCreateCustomPlanResponses[keyof ManageTeamPlansControllerManagePricingTeamCreateCustomPlanResponses];

export type ManageTeamPlansControllerManagePricingTeamBalanceData = {
  body: ManagePricingTeamBalanceDto;
  path: {
    teamId: string;
  };
  query?: never;
  url: '/v4/pricing/v3/teams/{teamId}/balance';
};

export type ManageTeamPlansControllerManagePricingTeamBalanceResponses = {
  200: unknown;
};

export type ManageTeamPlansControllerManagePlanRenewalsData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v4/pricing/team-plans/renewals/plan';
};

export type ManageTeamPlansControllerManagePlanRenewalsResponses = {
  201: ManageTeamPlanRenewalsSummaryDto;
};

export type ManageTeamPlansControllerManagePlanRenewalsResponse =
  ManageTeamPlansControllerManagePlanRenewalsResponses[keyof ManageTeamPlansControllerManagePlanRenewalsResponses];

export type ManageTeamPlansControllerManagePausedPlansData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v4/pricing/team-plans/renewals/pause';
};

export type ManageTeamPlansControllerManagePausedPlansResponses = {
  201: ManagePausedPlanSummaryDto;
};

export type ManageTeamPlansControllerManagePausedPlansResponse =
  ManageTeamPlansControllerManagePausedPlansResponses[keyof ManageTeamPlansControllerManagePausedPlansResponses];

export type PaymentProcessingControllerGetOverviewData = {
  body?: never;
  path: {
    teamId: string;
    projectId: string;
  };
  query?: never;
  url: '/v4/pricing/teams/{teamId}/projects/{projectId}/payments/overview';
};

export type PaymentProcessingControllerGetOverviewResponses = {
  200: ProjectPaymentsOverviewDto;
};

export type PaymentProcessingControllerGetOverviewResponse =
  PaymentProcessingControllerGetOverviewResponses[keyof PaymentProcessingControllerGetOverviewResponses];

export type PaymentProcessingControllerListParticipantsToPayData = {
  body?: never;
  path: {
    teamId: string;
    projectId: string;
  };
  query?: {
    page?: number;
    pageSize?: number;
    includeCount?: boolean;
    /**
     * Free-text search over participant name (case-insensitive substring match).
     */
    search?: string;
    /**
     * Filter rows by previous payout count.
     * "all" or undefined → no filter (default behaviour)
     * "none" → only participants who have never been paid
     * "1".."5" → exact previous-payout-count
     */
    payoutCountFilter?: string;
  };
  url: '/v4/pricing/teams/{teamId}/projects/{projectId}/payments/participants-to-pay';
};

export type PaymentProcessingControllerListParticipantsToPayResponses = {
  200: ParticipantsToPayPaginatedDto;
};

export type PaymentProcessingControllerListParticipantsToPayResponse =
  PaymentProcessingControllerListParticipantsToPayResponses[keyof PaymentProcessingControllerListParticipantsToPayResponses];

export type PaymentProcessingControllerListPastPaymentsData = {
  body?: never;
  path: {
    teamId: string;
    projectId: string;
  };
  query?: {
    page?: number;
    pageSize?: number;
    includeCount?: boolean;
    /**
     * Free-text search over participant name (case-insensitive substring match).
     */
    search?: string;
  };
  url: '/v4/pricing/teams/{teamId}/projects/{projectId}/payments/past-payments';
};

export type PaymentProcessingControllerListPastPaymentsResponses = {
  200: PastPaymentsPaginatedDto;
};

export type PaymentProcessingControllerListPastPaymentsResponse =
  PaymentProcessingControllerListPastPaymentsResponses[keyof PaymentProcessingControllerListPastPaymentsResponses];

export type PaymentProcessingControllerDownloadPastPaymentsData = {
  body?: never;
  path: {
    teamId: string;
    projectId: string;
  };
  query?: never;
  url: '/v4/pricing/teams/{teamId}/projects/{projectId}/payments/past-payments/download';
};

export type PaymentProcessingControllerDownloadPastPaymentsResponses = {
  /**
   * CSV file
   */
  200: string;
};

export type PaymentProcessingControllerDownloadPastPaymentsResponse =
  PaymentProcessingControllerDownloadPastPaymentsResponses[keyof PaymentProcessingControllerDownloadPastPaymentsResponses];

export type PaymentProcessingControllerListLegacyProjectPaymentsData = {
  body?: never;
  path: {
    teamId: string;
    projectId: string;
  };
  query?: {
    page?: number;
    pageSize?: number;
    includeCount?: boolean;
  };
  url: '/v4/pricing/teams/{teamId}/projects/{projectId}/payments/legacy';
};

export type PaymentProcessingControllerListLegacyProjectPaymentsResponses = {
  200: LegacyProjectPaymentsPaginatedDto;
};

export type PaymentProcessingControllerListLegacyProjectPaymentsResponse =
  PaymentProcessingControllerListLegacyProjectPaymentsResponses[keyof PaymentProcessingControllerListLegacyProjectPaymentsResponses];

export type PaymentProcessingControllerPayoutSummaryData = {
  body: GetPayoutSummaryDto;
  path: {
    projectId: string;
    teamId: string;
  };
  query?: never;
  url: '/v4/pricing/teams/{teamId}/projects/{projectId}/credits/payout/summary';
};

export type PaymentProcessingControllerPayoutSummaryResponses = {
  200: PayoutSummaryDto;
};

export type PaymentProcessingControllerPayoutSummaryResponse =
  PaymentProcessingControllerPayoutSummaryResponses[keyof PaymentProcessingControllerPayoutSummaryResponses];

export type PaymentProcessingControllerCreatePaygData = {
  body: CreatePaygPricingCreditDto;
  path: {
    teamId: string;
    teamPlanId: string;
  };
  query?: never;
  url: '/v4/pricing/teams/{teamId}/team-plans/{teamPlanId}/credits';
};

export type PaymentProcessingControllerCreatePaygResponses = {
  201: PricingCredit;
};

export type PaymentProcessingControllerCreatePaygResponse =
  PaymentProcessingControllerCreatePaygResponses[keyof PaymentProcessingControllerCreatePaygResponses];

export type PaymentProcessingControllerPayoutProjectData = {
  body: PayoutProjectDto;
  path: {
    teamId: string;
    projectId: string;
  };
  query?: never;
  url: '/v4/pricing/teams/{teamId}/projects/{projectId}/credits/payout';
};

export type PaymentProcessingControllerPayoutProjectResponses = {
  201: Array<PricingCredit>;
};

export type PaymentProcessingControllerPayoutProjectResponse =
  PaymentProcessingControllerPayoutProjectResponses[keyof PaymentProcessingControllerPayoutProjectResponses];

export type PaymentProcessingControllerProcessAutoPayData = {
  body: ProcessAutopayDto;
  path: {
    teamId: string;
  };
  query?: never;
  url: '/v4/pricing/payments/teams/{teamId}/process-auto-pay';
};

export type PaymentProcessingControllerProcessAutoPayResponses = {
  200: ProcessAutopayResponseDto;
};

export type PaymentProcessingControllerProcessAutoPayResponse =
  PaymentProcessingControllerProcessAutoPayResponses[keyof PaymentProcessingControllerProcessAutoPayResponses];

export type PaymentProcessingControllerPayoutCancelData = {
  body: PayoutCancelDto;
  path: {
    respondentPayoutId: string;
  };
  query?: never;
  url: '/v4/pricing/payments/respondentPayouts/{respondentPayoutId}/cancel';
};

export type PaymentProcessingControllerPayoutCancelResponses = {
  200: unknown;
};

export type PrepayControllerPrepayProjectData = {
  body: PrepayProjectDto;
  path: {
    projectId: string;
    teamId: string;
  };
  query?: never;
  url: '/v4/pricing/teams/{teamId}/projects/{projectId}/prepay';
};

export type PrepayControllerPrepayProjectResponses = {
  200: PrepayResponseDto;
};

export type PrepayControllerPrepayProjectResponse =
  PrepayControllerPrepayProjectResponses[keyof PrepayControllerPrepayProjectResponses];

export type PricingCreditsAuditControllerFindAllData = {
  body: GetPricingCreditsAuditPaginatedDto;
  path?: never;
  query?: never;
  url: '/v4/pricing/credits-audit/list';
};

export type PricingCreditsAuditControllerFindAllResponses = {
  200: PricingCreditsAuditPaginatedDto;
};

export type PricingCreditsAuditControllerFindAllResponse =
  PricingCreditsAuditControllerFindAllResponses[keyof PricingCreditsAuditControllerFindAllResponses];

export type PricingPromotionsControllerCreateData = {
  body: CreatePricingPromotionDto;
  path?: never;
  query?: never;
  url: '/v4/pricing/promotions';
};

export type PricingPromotionsControllerCreateResponses = {
  201: PricingPromotion;
};

export type PricingPromotionsControllerCreateResponse =
  PricingPromotionsControllerCreateResponses[keyof PricingPromotionsControllerCreateResponses];

export type PricingPromotionsControllerFindAllData = {
  body: GetPricingPromotionsPaginatedDto;
  path?: never;
  query?: never;
  url: '/v4/pricing/promotions/list';
};

export type PricingPromotionsControllerFindAllResponses = {
  200: PricingPromotionsPaginatedDto;
};

export type PricingPromotionsControllerFindAllResponse =
  PricingPromotionsControllerFindAllResponses[keyof PricingPromotionsControllerFindAllResponses];

export type PricingPromotionsControllerFindOneData = {
  body?: never;
  path: {
    id: string;
  };
  query?: never;
  url: '/v4/pricing/promotions/{id}';
};

export type PricingPromotionsControllerFindOneResponses = {
  200: PricingPromotion;
};

export type PricingPromotionsControllerFindOneResponse =
  PricingPromotionsControllerFindOneResponses[keyof PricingPromotionsControllerFindOneResponses];

export type PricingPromotionsControllerUpdateData = {
  body: UpdatePricingPromotionDto;
  path: {
    id: string;
  };
  query?: never;
  url: '/v4/pricing/promotions/{id}';
};

export type PricingPromotionsControllerUpdateErrors = {
  404: unknown;
};

export type PricingPromotionsControllerUpdateResponses = {
  200: PricingPromotion;
};

export type PricingPromotionsControllerUpdateResponse =
  PricingPromotionsControllerUpdateResponses[keyof PricingPromotionsControllerUpdateResponses];

export type PricingPromotionsControllerRedeemData = {
  body: RedeemPricingPromotionDto;
  path: {
    teamId: string;
  };
  query?: never;
  url: '/v4/pricing/teams/{teamId}/promotions/redeem';
};

export type PricingPromotionsControllerRedeemResponses = {
  200: RedeemPricingPromotionResultDto;
};

export type PricingPromotionsControllerRedeemResponse =
  PricingPromotionsControllerRedeemResponses[keyof PricingPromotionsControllerRedeemResponses];

export type PricingPurchaseOrdersControllerCreateData = {
  body: CreatePricingPurchaseOrderDto;
  path?: never;
  query?: never;
  url: '/v4/pricing/purchase-orders';
};

export type PricingPurchaseOrdersControllerCreateResponses = {
  201: PricingPurchaseOrder;
};

export type PricingPurchaseOrdersControllerCreateResponse =
  PricingPurchaseOrdersControllerCreateResponses[keyof PricingPurchaseOrdersControllerCreateResponses];

export type PricingPurchaseOrdersControllerFindAllData = {
  body: GetPricingPurchaseOrdersPaginatedDto;
  path?: never;
  query?: never;
  url: '/v4/pricing/purchase-orders/list';
};

export type PricingPurchaseOrdersControllerFindAllResponses = {
  200: PricingPurchaseOrdersPaginatedDto;
};

export type PricingPurchaseOrdersControllerFindAllResponse =
  PricingPurchaseOrdersControllerFindAllResponses[keyof PricingPurchaseOrdersControllerFindAllResponses];

export type PricingPurchaseOrdersControllerFindOneData = {
  body?: never;
  path: {
    id: string;
  };
  query?: never;
  url: '/v4/pricing/purchase-orders/{id}';
};

export type PricingPurchaseOrdersControllerFindOneResponses = {
  200: PricingPurchaseOrder;
};

export type PricingPurchaseOrdersControllerFindOneResponse =
  PricingPurchaseOrdersControllerFindOneResponses[keyof PricingPurchaseOrdersControllerFindOneResponses];

export type PricingPurchaseOrdersControllerUpdateData = {
  body: UpdatePricingPurchaseOrderDto;
  path: {
    id: string;
  };
  query?: never;
  url: '/v4/pricing/purchase-orders/{id}';
};

export type PricingPurchaseOrdersControllerUpdateErrors = {
  404: unknown;
};

export type PricingPurchaseOrdersControllerUpdateResponses = {
  200: PricingPurchaseOrder;
};

export type PricingPurchaseOrdersControllerUpdateResponse =
  PricingPurchaseOrdersControllerUpdateResponses[keyof PricingPurchaseOrdersControllerUpdateResponses];

export type ParticipantProfilesControllerFindByUserIdData = {
  body?: never;
  path: {
    userId: string;
  };
  query?: never;
  url: '/v4/participant/profiles/user/{userId}';
};

export type ParticipantProfilesControllerFindByUserIdResponses = {
  200: ParticipantProfileWithLocksDto;
};

export type ParticipantProfilesControllerFindByUserIdResponse =
  ParticipantProfilesControllerFindByUserIdResponses[keyof ParticipantProfilesControllerFindByUserIdResponses];

export type ParticipantProfilesControllerAcceptCodeOfConductData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v4/participant/profiles/user/accept-code-of-conduct';
};

export type ParticipantProfilesControllerAcceptCodeOfConductResponses = {
  201: unknown;
};

export type ParticipantProfilesControllerSendEmploymentEmailVerificationData = {
  body?: never;
  path: {
    id: string;
  };
  query?: never;
  url: '/v4/participant/profiles/{id}/send-employment-email-verification';
};

export type ParticipantProfilesControllerSendEmploymentEmailVerificationResponses =
  {
    201: unknown;
  };

export type ParticipantProfilesControllerVerifyEmploymentEmailCodeData = {
  body: VerifyEmploymentEmailCodeDto;
  path: {
    id: string;
  };
  query?: never;
  url: '/v4/participant/profiles/{id}/verify-employment-email-code';
};

export type ParticipantProfilesControllerVerifyEmploymentEmailCodeResponses = {
  201: unknown;
};

export type ParticipantProfilesControllerChangePasswordData = {
  body: ChangePasswordDto;
  path?: never;
  query?: never;
  url: '/v4/participant/profiles/change-password';
};

export type ParticipantProfilesControllerChangePasswordResponses = {
  204: void;
};

export type ParticipantProfilesControllerChangePasswordResponse =
  ParticipantProfilesControllerChangePasswordResponses[keyof ParticipantProfilesControllerChangePasswordResponses];

export type ParticipantProfilesControllerDeactivateData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v4/participant/profiles/deactivate';
};

export type ParticipantProfilesControllerDeactivateResponses = {
  204: void;
};

export type ParticipantProfilesControllerDeactivateResponse =
  ParticipantProfilesControllerDeactivateResponses[keyof ParticipantProfilesControllerDeactivateResponses];

export type ParticipantProjectsControllerViewProjectData = {
  body?: never;
  path: {
    id: string;
  };
  query?: {
    referralCode?: string;
    inviteCode?: string;
    inviteId?: string;
  };
  url: '/v4/participant/projects/view/{id}';
};

export type ParticipantProjectsControllerViewProjectResponses = {
  /**
   * Project details retrieved successfully
   */
  200: ViewProjectResponseDto;
};

export type ParticipantProjectsControllerViewProjectResponse =
  ParticipantProjectsControllerViewProjectResponses[keyof ParticipantProjectsControllerViewProjectResponses];

export type ParticipantProjectsControllerGetProjectDetailsData = {
  body?: never;
  path: {
    id: string;
  };
  query?: {
    referralCode?: string;
    inviteCode?: string;
    inviteId?: string;
  };
  url: '/v4/participant/projects/{id}/details';
};

export type ParticipantProjectsControllerGetProjectDetailsResponses = {
  200: ProjectDetailsResponseDto;
};

export type ParticipantProjectsControllerGetProjectDetailsResponse =
  ParticipantProjectsControllerGetProjectDetailsResponses[keyof ParticipantProjectsControllerGetProjectDetailsResponses];

export type ParticipantProjectsControllerGetScreenerQuestionsData = {
  body?: never;
  path: {
    id: string;
  };
  query?: {
    inviteCode?: string;
    inviteId?: string;
  };
  url: '/v4/participant/projects/{id}/screener-questions';
};

export type ParticipantProjectsControllerGetScreenerQuestionsResponses = {
  200: ScreenerQuestionsResponseDto;
};

export type ParticipantProjectsControllerGetScreenerQuestionsResponse =
  ParticipantProjectsControllerGetScreenerQuestionsResponses[keyof ParticipantProjectsControllerGetScreenerQuestionsResponses];

export type ParticipantProjectsControllerGetNextScreenerQuestionData = {
  body: ScreenerNavigationDto;
  path: {
    id: string;
  };
  query?: {
    inviteCode?: string;
    inviteId?: string;
  };
  url: '/v4/participant/projects/{id}/screener-questions/next';
};

export type ParticipantProjectsControllerGetNextScreenerQuestionErrors = {
  /**
   * Project is not currently recruiting, or responses are invalid
   */
  400: unknown;
  /**
   * Project not found or not published
   */
  404: unknown;
};

export type ParticipantProjectsControllerGetNextScreenerQuestionResponses = {
  /**
   * Navigation state resolved successfully
   */
  200: ScreenerNavigationResponseDto;
  201: ScreenerNavigationResponseDto;
};

export type ParticipantProjectsControllerGetNextScreenerQuestionResponse =
  ParticipantProjectsControllerGetNextScreenerQuestionResponses[keyof ParticipantProjectsControllerGetNextScreenerQuestionResponses];

export type ParticipantProjectsControllerGetProjectProfileQuestionsData = {
  body?: never;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/participant/projects/{projectId}/profile-questions';
};

export type ParticipantProjectsControllerGetProjectProfileQuestionsErrors = {
  /**
   * Project not found or not published
   */
  404: unknown;
};

export type ParticipantProjectsControllerGetProjectProfileQuestionsResponses = {
  /**
   * Profile questions retrieved successfully
   */
  200: ProfileCompletionResponseDto;
};

export type ParticipantProjectsControllerGetProjectProfileQuestionsResponse =
  ParticipantProjectsControllerGetProjectProfileQuestionsResponses[keyof ParticipantProjectsControllerGetProjectProfileQuestionsResponses];

export type ParticipantProjectsControllerGetProjectB2bQuestionsData = {
  body?: never;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/participant/projects/{projectId}/b2b-questions';
};

export type ParticipantProjectsControllerGetProjectB2bQuestionsErrors = {
  /**
   * Project not found or not published
   */
  404: unknown;
};

export type ParticipantProjectsControllerGetProjectB2bQuestionsResponses = {
  /**
   * B2B questions retrieved successfully
   */
  200: B2bCompletionResponseDto;
};

export type ParticipantProjectsControllerGetProjectB2bQuestionsResponse =
  ParticipantProjectsControllerGetProjectB2bQuestionsResponses[keyof ParticipantProjectsControllerGetProjectB2bQuestionsResponses];

export type ParticipantProjectsControllerGetScreenerData = {
  body?: never;
  path: {
    id: string;
  };
  query?: {
    inviteCode?: string;
    inviteId?: string;
  };
  url: '/v4/participant/projects/{id}/screener';
};

export type ParticipantProjectsControllerGetScreenerResponses = {
  /**
   * Screener questions retrieved successfully
   */
  200: GetScreenerResponseDto;
};

export type ParticipantProjectsControllerGetScreenerResponse =
  ParticipantProjectsControllerGetScreenerResponses[keyof ParticipantProjectsControllerGetScreenerResponses];

export type ParticipantProjectsControllerRecalculateScreenerStatusData = {
  body?: never;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/participant/projects/{projectId}/recalculate-screener-status';
};

export type ParticipantProjectsControllerRecalculateScreenerStatusResponses = {
  /**
   * Screener status recalculated successfully
   */
  200: RecalculateScreenerStatusResponseDto;
  201: RecalculateScreenerStatusResponseDto;
};

export type ParticipantProjectsControllerRecalculateScreenerStatusResponse =
  ParticipantProjectsControllerRecalculateScreenerStatusResponses[keyof ParticipantProjectsControllerRecalculateScreenerStatusResponses];

export type ParticipantProjectsControllerMarkPlaidCompleteData = {
  body?: never;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/participant/projects/{projectId}/mark-plaid-complete';
};

export type ParticipantProjectsControllerMarkPlaidCompleteResponses = {
  /**
   * Screener response updated successfully
   */
  200: MarkPlaidCompleteResponseDto;
  201: MarkPlaidCompleteResponseDto;
};

export type ParticipantProjectsControllerMarkPlaidCompleteResponse =
  ParticipantProjectsControllerMarkPlaidCompleteResponses[keyof ParticipantProjectsControllerMarkPlaidCompleteResponses];

export type ParticipantProjectsControllerSubmitPublicApplicationData = {
  body: PublicApplicationDto;
  path: {
    id: string;
  };
  query?: never;
  url: '/v4/participant/projects/{id}/public-application';
};

export type ParticipantProjectsControllerSubmitPublicApplicationErrors = {
  /**
   * Invalid or expired verification code, or invalid answers
   */
  400: unknown;
};

export type ParticipantProjectsControllerSubmitPublicApplicationResponses = {
  /**
   * Account created and application filed
   */
  201: PublicApplicationResponseDto;
};

export type ParticipantProjectsControllerSubmitPublicApplicationResponse =
  ParticipantProjectsControllerSubmitPublicApplicationResponses[keyof ParticipantProjectsControllerSubmitPublicApplicationResponses];

export type ParticipantProjectsControllerCheckPublicApplyWorkEmailData = {
  body: CheckWorkEmailAvailabilityDto;
  path: {
    id: string;
  };
  query?: never;
  url: '/v4/participant/projects/{id}/public-apply-work-email-check';
};

export type ParticipantProjectsControllerCheckPublicApplyWorkEmailResponses = {
  /**
   * Availability verdict
   */
  200: WorkEmailAvailabilityDto;
  201: WorkEmailAvailabilityDto;
};

export type ParticipantProjectsControllerCheckPublicApplyWorkEmailResponse =
  ParticipantProjectsControllerCheckPublicApplyWorkEmailResponses[keyof ParticipantProjectsControllerCheckPublicApplyWorkEmailResponses];

export type ParticipantProjectsControllerSaveScreenerResponseData = {
  body: SaveScreenerResponseDto;
  path: {
    id: string;
  };
  query?: never;
  url: '/v4/participant/projects/{id}/screener-responses';
};

export type ParticipantProjectsControllerSaveScreenerResponseErrors = {
  /**
   * Invalid request or validation error
   */
  400: unknown;
};

export type ParticipantProjectsControllerSaveScreenerResponseResponses = {
  /**
   * Screener response saved successfully
   */
  200: SaveScreenerResponseResponseDto;
  201: SaveScreenerResponseResponseDto;
};

export type ParticipantProjectsControllerSaveScreenerResponseResponse =
  ParticipantProjectsControllerSaveScreenerResponseResponses[keyof ParticipantProjectsControllerSaveScreenerResponseResponses];

export type ParticipantProjectsControllerStartUnmoderatedProjectData = {
  body?: never;
  path: {
    id: string;
  };
  query?: never;
  url: '/v4/participant/projects/{id}/start';
};

export type ParticipantProjectsControllerStartUnmoderatedProjectResponses = {
  /**
   * Survey URL generated successfully
   */
  200: StartUnmoderatedProjectResponseDto;
  201: StartUnmoderatedProjectResponseDto;
};

export type ParticipantProjectsControllerStartUnmoderatedProjectResponse =
  ParticipantProjectsControllerStartUnmoderatedProjectResponses[keyof ParticipantProjectsControllerStartUnmoderatedProjectResponses];

export type ParticipantProjectsControllerStartExternalScreenerData = {
  body: StartExternalScreenerDto;
  path: {
    id: string;
  };
  query?: never;
  url: '/v4/participant/projects/{id}/external-screener';
};

export type ParticipantProjectsControllerStartExternalScreenerResponses = {
  201: StartExternalScreenerResponseDto;
};

export type ParticipantProjectsControllerStartExternalScreenerResponse =
  ParticipantProjectsControllerStartExternalScreenerResponses[keyof ParticipantProjectsControllerStartExternalScreenerResponses];

export type ParticipantProjectsControllerHideProjectData = {
  body?: never;
  path: {
    id: string;
  };
  query?: never;
  url: '/v4/participant/projects/hide/{id}';
};

export type ParticipantProjectsControllerHideProjectResponses = {
  /**
   * Project hidden successfully
   */
  200: HideProjectResponseDto;
  201: HideProjectResponseDto;
};

export type ParticipantProjectsControllerHideProjectResponse =
  ParticipantProjectsControllerHideProjectResponses[keyof ParticipantProjectsControllerHideProjectResponses];

export type ParticipantProjectsControllerResendPayoutEmailData = {
  body?: never;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/participant/projects/{projectId}/resend-payout-email';
};

export type ParticipantProjectsControllerResendPayoutEmailResponses = {
  /**
   * Payout email sent successfully
   */
  200: unknown;
  201: unknown;
};

export type ParticipantProjectsControllerGetProfileCompletionQuestionsData = {
  body?: never;
  path: {
    /**
     * The project ID
     */
    projectId: string;
  };
  query?: never;
  url: '/v4/participant/projects/{projectId}/profile-completion';
};

export type ParticipantProjectsControllerGetProfileCompletionQuestionsErrors = {
  /**
   * Invalid request parameters
   */
  400: unknown;
  /**
   * User must be authenticated
   */
  401: unknown;
  /**
   * Project or profile not found
   */
  404: unknown;
};

export type ParticipantProjectsControllerGetProfileCompletionQuestionsResponses =
  {
    /**
     * Profile completion questions retrieved successfully
     */
    200: ProfileCompletionResponseDto;
  };

export type ParticipantProjectsControllerGetProfileCompletionQuestionsResponse =
  ParticipantProjectsControllerGetProfileCompletionQuestionsResponses[keyof ParticipantProjectsControllerGetProfileCompletionQuestionsResponses];

export type ParticipantProjectsControllerGetParticipantPaidCountData = {
  body?: never;
  path: {
    userId: string;
  };
  query?: never;
  url: '/v4/participant/projects/participants/{userId}/paid-count';
};

export type ParticipantProjectsControllerGetParticipantPaidCountResponses = {
  200: {
    paidCount: number;
    totalPaid: number;
  };
};

export type ParticipantProjectsControllerGetParticipantPaidCountResponse =
  ParticipantProjectsControllerGetParticipantPaidCountResponses[keyof ParticipantProjectsControllerGetParticipantPaidCountResponses];

export type ParticipantProjectsControllerGetApplicationSignalsData = {
  body?: never;
  path: {
    userId: string;
  };
  query?: never;
  url: '/v4/participant/projects/participants/{userId}/application-signals';
};

export type ParticipantProjectsControllerGetApplicationSignalsResponses = {
  200: ApplicationSignalsDto;
};

export type ParticipantProjectsControllerGetApplicationSignalsResponse =
  ParticipantProjectsControllerGetApplicationSignalsResponses[keyof ParticipantProjectsControllerGetApplicationSignalsResponses];

export type ParticipantProjectsControllerCancelApplicationData = {
  body?: never;
  path: {
    projectId: string;
    userId: string;
  };
  query?: never;
  url: '/v4/participant/projects/participants/{userId}/projects/{projectId}/cancel-application';
};

export type ParticipantProjectsControllerCancelApplicationResponses = {
  /**
   * Application cancelled successfully
   */
  200: unknown;
  201: unknown;
};

export type ParticipantProjectsControllerGetInterviewData = {
  body?: never;
  path: {
    id: string;
  };
  query?: never;
  url: '/v4/participant/projects/{id}/interview';
};

export type ParticipantProjectsControllerGetInterviewErrors = {
  /**
   * Project or screener response not found
   */
  404: unknown;
};

export type ParticipantProjectsControllerGetInterviewResponses = {
  /**
   * Interview data retrieved successfully
   */
  200: GetInterviewResponseDto;
};

export type ParticipantProjectsControllerGetInterviewResponse =
  ParticipantProjectsControllerGetInterviewResponses[keyof ParticipantProjectsControllerGetInterviewResponses];

export type ParticipantProjectsControllerGetInterviewQuestionMediaData = {
  body?: never;
  path: {
    id: string;
    questionUid: string;
  };
  query?: never;
  url: '/v4/participant/projects/{id}/interview/questions/{questionUid}/media';
};

export type ParticipantProjectsControllerGetInterviewQuestionMediaErrors = {
  /**
   * Project, screener response, or question not found
   */
  404: unknown;
};

export type ParticipantProjectsControllerGetInterviewQuestionMediaResponses = {
  /**
   * Freshly-signed media URLs
   */
  200: InterviewQuestionMediaUrlsDto;
};

export type ParticipantProjectsControllerGetInterviewQuestionMediaResponse =
  ParticipantProjectsControllerGetInterviewQuestionMediaResponses[keyof ParticipantProjectsControllerGetInterviewQuestionMediaResponses];

export type ParticipantProjectsControllerSetInterviewResponseLanguageData = {
  body: SetResponseLanguageDto;
  path: {
    id: string;
  };
  query?: never;
  url: '/v4/participant/projects/{id}/interview/response-language';
};

export type ParticipantProjectsControllerSetInterviewResponseLanguageErrors = {
  /**
   * Language is not allowed for this project
   */
  400: unknown;
};

export type ParticipantProjectsControllerSetInterviewResponseLanguageResponses =
  {
    /**
     * Response language set; returns the refreshed interview data
     */
    200: GetInterviewResponseDto;
  };

export type ParticipantProjectsControllerSetInterviewResponseLanguageResponse =
  ParticipantProjectsControllerSetInterviewResponseLanguageResponses[keyof ParticipantProjectsControllerSetInterviewResponseLanguageResponses];

export type ParticipantProjectsControllerSaveInterviewResponsesData = {
  body: SaveInterviewResponseDto;
  path: {
    id: string;
  };
  query?: never;
  url: '/v4/participant/projects/{id}/interview/responses';
};

export type ParticipantProjectsControllerSaveInterviewResponsesErrors = {
  /**
   * Invalid response or validation error
   */
  400: unknown;
};

export type ParticipantProjectsControllerSaveInterviewResponsesResponses = {
  /**
   * Responses saved successfully
   */
  200: SaveInterviewResponseResponseDto;
  201: SaveInterviewResponseResponseDto;
};

export type ParticipantProjectsControllerSaveInterviewResponsesResponse =
  ParticipantProjectsControllerSaveInterviewResponsesResponses[keyof ParticipantProjectsControllerSaveInterviewResponsesResponses];

export type ParticipantProjectsControllerCompleteInterviewData = {
  body: CompleteInterviewRequestDto;
  path: {
    id: string;
  };
  query?: never;
  url: '/v4/participant/projects/{id}/interview/complete';
};

export type ParticipantProjectsControllerCompleteInterviewErrors = {
  /**
   * Not all questions answered or validation error
   */
  400: unknown;
};

export type ParticipantProjectsControllerCompleteInterviewResponses = {
  /**
   * Interview completed successfully
   */
  200: CompleteInterviewResponseDto;
  201: CompleteInterviewResponseDto;
};

export type ParticipantProjectsControllerCompleteInterviewResponse =
  ParticipantProjectsControllerCompleteInterviewResponses[keyof ParticipantProjectsControllerCompleteInterviewResponses];

export type ParticipantProjectsControllerGenerateFollowUpData = {
  body: GenerateFollowUpDto;
  path: {
    id: string;
  };
  query?: never;
  url: '/v4/participant/projects/{id}/interview/follow-up';
};

export type ParticipantProjectsControllerGenerateFollowUpResponses = {
  /**
   * Follow-up generation result
   */
  200: GenerateFollowUpResponseDto;
  201: GenerateFollowUpResponseDto;
};

export type ParticipantProjectsControllerGenerateFollowUpResponse =
  ParticipantProjectsControllerGenerateFollowUpResponses[keyof ParticipantProjectsControllerGenerateFollowUpResponses];

export type ParticipantProjectsControllerGetMyProjectsData = {
  body?: never;
  path: {
    userId: string;
  };
  query?: {
    page?: number;
    pageSize?: number;
    includeCount?: boolean;
    statuses?: Array<ScreenerResponseStatus>;
    search?: string;
  };
  url: '/v4/participant/projects/{userId}/my-projects';
};

export type ParticipantProjectsControllerGetMyProjectsResponses = {
  200: ActiveSurveyResponsesPaginatedDto;
};

export type ParticipantProjectsControllerGetMyProjectsResponse =
  ParticipantProjectsControllerGetMyProjectsResponses[keyof ParticipantProjectsControllerGetMyProjectsResponses];

export type ParticipantProjectsControllerGetMessagingProjectsData = {
  body?: never;
  path: {
    userId: string;
  };
  query?: never;
  url: '/v4/participant/projects/{userId}/messaging-projects';
};

export type ParticipantProjectsControllerGetMessagingProjectsResponses = {
  200: Array<MessagingProjectDto>;
};

export type ParticipantProjectsControllerGetMessagingProjectsResponse =
  ParticipantProjectsControllerGetMessagingProjectsResponses[keyof ParticipantProjectsControllerGetMessagingProjectsResponses];

export type ParticipantProjectsControllerGetPastProjectsData = {
  body?: never;
  path: {
    userId: string;
  };
  query?: {
    page?: number;
    pageSize?: number;
    includeCount?: boolean;
    statuses?: Array<PastScreenerResponseStatus>;
    /**
     * Case-insensitive match on public project name only
     */
    search?: string;
  };
  url: '/v4/participant/projects/{userId}/past-projects';
};

export type ParticipantProjectsControllerGetPastProjectsResponses = {
  200: ActiveSurveyResponsesPaginatedDto;
};

export type ParticipantProjectsControllerGetPastProjectsResponse =
  ParticipantProjectsControllerGetPastProjectsResponses[keyof ParticipantProjectsControllerGetPastProjectsResponses];

export type ParticipantBookingControllerGetBookingData = {
  body?: never;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/participant/projects/{projectId}/booking';
};

export type ParticipantBookingControllerGetBookingResponses = {
  200: ParticipantBookingDto;
};

export type ParticipantBookingControllerGetBookingResponse =
  ParticipantBookingControllerGetBookingResponses[keyof ParticipantBookingControllerGetBookingResponses];

export type ParticipantBookingControllerBookData = {
  body: BookSessionDto;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/participant/projects/{projectId}/booking';
};

export type ParticipantBookingControllerBookResponses = {
  200: BookSessionResponseDto;
  201: BookSessionResponseDto;
};

export type ParticipantBookingControllerBookResponse =
  ParticipantBookingControllerBookResponses[keyof ParticipantBookingControllerBookResponses];

export type ParticipantBookingControllerFindTimesData = {
  body: FindBookingTimesDto;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/participant/projects/{projectId}/booking/find-times';
};

export type ParticipantBookingControllerFindTimesResponses = {
  200: FindBookingTimesResponseDto;
  201: FindBookingTimesResponseDto;
};

export type ParticipantBookingControllerFindTimesResponse =
  ParticipantBookingControllerFindTimesResponses[keyof ParticipantBookingControllerFindTimesResponses];

export type ParticipantBookingControllerRescheduleData = {
  body: RescheduleSessionDto;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/participant/projects/{projectId}/booking/reschedule';
};

export type ParticipantBookingControllerRescheduleResponses = {
  200: BookSessionResponseDto;
  201: BookSessionResponseDto;
};

export type ParticipantBookingControllerRescheduleResponse =
  ParticipantBookingControllerRescheduleResponses[keyof ParticipantBookingControllerRescheduleResponses];

export type ParticipantBookingControllerCancelData = {
  body: CancelBookingDto;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/participant/projects/{projectId}/booking/cancel';
};

export type ParticipantBookingControllerCancelErrors = {
  /**
   * No active booking
   */
  409: unknown;
};

export type ParticipantBookingControllerCancelResponses = {
  204: void;
};

export type ParticipantBookingControllerCancelResponse =
  ParticipantBookingControllerCancelResponses[keyof ParticipantBookingControllerCancelResponses];

export type ParticipantBookingControllerNotifyResearcherData = {
  body: NotifyResearcherDto;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/participant/projects/{projectId}/booking/notify-researcher';
};

export type ParticipantBookingControllerNotifyResearcherErrors = {
  /**
   * The participant cannot book
   */
  409: unknown;
};

export type ParticipantBookingControllerNotifyResearcherResponses = {
  204: void;
};

export type ParticipantBookingControllerNotifyResearcherResponse =
  ParticipantBookingControllerNotifyResearcherResponses[keyof ParticipantBookingControllerNotifyResearcherResponses];

export type InterviewMediaControllerInitiateUploadData = {
  body: InitiateUploadDto;
  path?: never;
  query?: never;
  url: '/v4/participant/interview-media/initiate';
};

export type InterviewMediaControllerInitiateUploadErrors = {
  401: unknown;
};

export type InterviewMediaControllerInitiateUploadResponses = {
  200: InitiateUploadResponseDto;
  201: InitiateUploadResponseDto;
};

export type InterviewMediaControllerInitiateUploadResponse =
  InterviewMediaControllerInitiateUploadResponses[keyof InterviewMediaControllerInitiateUploadResponses];

export type InterviewMediaControllerUploadPartData = {
  body: UploadPartDto;
  path?: never;
  query?: never;
  url: '/v4/participant/interview-media/upload-part';
};

export type InterviewMediaControllerUploadPartErrors = {
  401: unknown;
};

export type InterviewMediaControllerUploadPartResponses = {
  200: unknown;
  201: unknown;
};

export type InterviewMediaControllerCompleteUploadData = {
  body: CompleteUploadDto;
  path?: never;
  query?: never;
  url: '/v4/participant/interview-media/complete';
};

export type InterviewMediaControllerCompleteUploadErrors = {
  401: unknown;
};

export type InterviewMediaControllerCompleteUploadResponses = {
  200: CompleteUploadResponseDto;
  201: CompleteUploadResponseDto;
};

export type InterviewMediaControllerCompleteUploadResponse =
  InterviewMediaControllerCompleteUploadResponses[keyof InterviewMediaControllerCompleteUploadResponses];

export type InterviewMediaControllerAbortUploadData = {
  body: AbortUploadDto;
  path?: never;
  query?: never;
  url: '/v4/participant/interview-media/abort';
};

export type InterviewMediaControllerAbortUploadErrors = {
  401: unknown;
};

export type InterviewMediaControllerAbortUploadResponses = {
  200: unknown;
  201: unknown;
};

export type InterviewMediaControllerUploadTranscriptWordsData = {
  body: UploadTranscriptWordsDto;
  path?: never;
  query?: never;
  url: '/v4/participant/interview-media/transcript-words';
};

export type InterviewMediaControllerUploadTranscriptWordsErrors = {
  401: unknown;
};

export type InterviewMediaControllerUploadTranscriptWordsResponses = {
  200: unknown;
  201: unknown;
};

export type InterviewMediaControllerGetTranscriptionTokenData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v4/participant/interview-media/transcription-token';
};

export type InterviewMediaControllerGetTranscriptionTokenErrors = {
  401: unknown;
};

export type InterviewMediaControllerGetTranscriptionTokenResponses = {
  200: TranscriptionTokenResponseDto;
};

export type InterviewMediaControllerGetTranscriptionTokenResponse =
  InterviewMediaControllerGetTranscriptionTokenResponses[keyof InterviewMediaControllerGetTranscriptionTokenResponses];

export type GetInterviewCaptionsData = {
  body?: never;
  path?: never;
  query: {
    /**
     * The recording key: interview-media/{projectId}/{userId}/{questionUid}[.ext]
     */
    bucketKey: string;
    /**
     * A language code, or 'original' for the recording's language.
     */
    language: string;
  };
  url: '/v4/researcher/interview-media/captions';
};

export type GetInterviewCaptionsErrors = {
  401: unknown;
  403: unknown;
};

export type GetInterviewCaptionsResponses = {
  200: InterviewCaptionsResponseDto;
};

export type GetInterviewCaptionsResponse =
  GetInterviewCaptionsResponses[keyof GetInterviewCaptionsResponses];

export type ResearcherProjectsControllerChatData = {
  body: ChatRequestDto;
  path?: never;
  query?: never;
  url: '/v4/researcher/projects/chat';
};

export type ResearcherProjectsControllerChatResponses = {
  201: unknown;
};

export type ResearcherProjectsControllerFindAllData = {
  body?: never;
  path?: never;
  query: {
    page?: number;
    pageSize?: number;
    includeCount?: boolean;
    teamId: string;
    /**
     * When neither `statuses` nor `recruitingStatuses` is given, ARCHIVED and
     * DELETED projects are excluded; they must be requested explicitly.
     */
    statuses?: Array<ProjectStatus>;
    recruitingStatuses?: Array<ProjectRecruitingStatus>;
    /**
     * [
     *
     * ONE_ON_ONE: 1
     *
     * FOCUS_GROUP: 2
     *
     * MULTI_DAY_STUDY: 3
     *
     * UNMODERATED_STUDY: 4
     *
     * PHONE_INTERVIEW: 5
     *
     * USABILITY_TESTING: 6
     *
     * IN_RESPONDENT_HOME: 8
     *
     * SURVEY: 9
     *
     * DIARY_STUDY: 10
     *
     * IN_RESPONDENT_OFFICE: 11
     *
     * AI_MODERATED_INTERVIEW: 12
     *
     * ]
     */
    kindsOfResearch?: Array<KindOfResearch>;
    name?: string;
    userId?: string;
    /**
     * Restrict the result to projects belonging to these programs.
     *
     * Added for programs: membership is a `programId` on the project, so the
     * program pages resolve their projects by asking for exactly the projects in a
     * program (detail page: one id) or across several (list page: one id per card,
     * grouped client-side by `programId`). Replaces an earlier `projectIds` filter
     * and the client-side id-union it required.
     *
     * Still subject to `pageSize` (max {@link RESEARCHER_PROJECTS_MAX_PAGE_SIZE}),
     * so it narrows the query rather than bypassing pagination. Combines with the
     * other filters (passing a program and a status returns the intersection).
     *
     * **Callers must serialize this as `programIds[]=a&programIds[]=b`.** The
     * generated client's default serializer emits a bare repeated key, which the
     * API's `qs` parser reads as an array only when there are two or more values;
     * a single value would arrive as a string and be rejected by the transform.
     */
    programIds?: Array<string>;
    closed?: boolean;
    populate?: boolean;
    sortBy?: ProjectSortField;
    sortDir?: SortDirection;
  };
  url: '/v4/researcher/projects';
};

export type ResearcherProjectsControllerFindAllResponses = {
  200: ProjectsPaginatedDto;
};

export type ResearcherProjectsControllerFindAllResponse =
  ResearcherProjectsControllerFindAllResponses[keyof ResearcherProjectsControllerFindAllResponses];

export type ResearcherProjectsControllerCreateData = {
  body: CreateResearcherProjectDto;
  path?: never;
  query?: never;
  url: '/v4/researcher/projects';
};

export type ResearcherProjectsControllerCreateResponses = {
  201: Project;
};

export type ResearcherProjectsControllerCreateResponse =
  ResearcherProjectsControllerCreateResponses[keyof ResearcherProjectsControllerCreateResponses];

export type ResearcherProjectsControllerFindOneData = {
  body?: never;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/researcher/projects/{projectId}';
};

export type ResearcherProjectsControllerFindOneResponses = {
  200: Project;
};

export type ResearcherProjectsControllerFindOneResponse =
  ResearcherProjectsControllerFindOneResponses[keyof ResearcherProjectsControllerFindOneResponses];

export type ResearcherProjectsControllerUpdateDraftData = {
  body: UpdateResearcherDraftDto;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/researcher/projects/{projectId}';
};

export type ResearcherProjectsControllerUpdateDraftResponses = {
  200: Project;
};

export type ResearcherProjectsControllerUpdateDraftResponse =
  ResearcherProjectsControllerUpdateDraftResponses[keyof ResearcherProjectsControllerUpdateDraftResponses];

export type ResearcherProjectsControllerGetPricingMultiplierData = {
  body?: never;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/researcher/projects/{projectId}/pricing-multiplier';
};

export type ResearcherProjectsControllerGetPricingMultiplierResponses = {
  200: ProjectPricingMultiplierPreviewDto;
};

export type ResearcherProjectsControllerGetPricingMultiplierResponse =
  ResearcherProjectsControllerGetPricingMultiplierResponses[keyof ResearcherProjectsControllerGetPricingMultiplierResponses];

export type ResearcherProjectsControllerGetIncentiveRecommendationData = {
  body?: never;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/researcher/projects/{projectId}/incentive-recommendation';
};

export type ResearcherProjectsControllerGetIncentiveRecommendationResponses = {
  200: ProjectIncentiveRecommendationDto;
};

export type ResearcherProjectsControllerGetIncentiveRecommendationResponse =
  ResearcherProjectsControllerGetIncentiveRecommendationResponses[keyof ResearcherProjectsControllerGetIncentiveRecommendationResponses];

export type ResearcherProjectsControllerGetRecruitmentStatsData = {
  body?: never;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/researcher/projects/{projectId}/recruitment-stats';
};

export type ResearcherProjectsControllerGetRecruitmentStatsResponses = {
  200: RecruitmentStatsDto;
};

export type ResearcherProjectsControllerGetRecruitmentStatsResponse =
  ResearcherProjectsControllerGetRecruitmentStatsResponses[keyof ResearcherProjectsControllerGetRecruitmentStatsResponses];

export type ResearcherProjectsControllerUpdatePublishedDetailsData = {
  body: UpdatePublishedProjectDetailsDto;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/researcher/projects/{projectId}/published-details';
};

export type ResearcherProjectsControllerUpdatePublishedDetailsResponses = {
  200: Project;
};

export type ResearcherProjectsControllerUpdatePublishedDetailsResponse =
  ResearcherProjectsControllerUpdatePublishedDetailsResponses[keyof ResearcherProjectsControllerUpdatePublishedDetailsResponses];

export type ResearcherProjectsControllerLaunchData = {
  body?: never;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/researcher/projects/{projectId}/launch';
};

export type ResearcherProjectsControllerLaunchResponses = {
  201: Project;
};

export type ResearcherProjectsControllerLaunchResponse =
  ResearcherProjectsControllerLaunchResponses[keyof ResearcherProjectsControllerLaunchResponses];

export type ResearcherProjectsControllerUploadNdaFileData = {
  body?: never;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/researcher/projects/{projectId}/nda-file';
};

export type ResearcherProjectsControllerUploadNdaFileResponses = {
  200: UploadedFileResultsDto;
};

export type ResearcherProjectsControllerUploadNdaFileResponse =
  ResearcherProjectsControllerUploadNdaFileResponses[keyof ResearcherProjectsControllerUploadNdaFileResponses];

export type ResearcherProjectsControllerUploadMessageAttachmentData = {
  body: {
    files: Blob | File;
  };
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/researcher/projects/{projectId}/message-attachment';
};

export type ResearcherProjectsControllerUploadMessageAttachmentResponses = {
  200: UploadedAttachmentDto;
};

export type ResearcherProjectsControllerUploadMessageAttachmentResponse =
  ResearcherProjectsControllerUploadMessageAttachmentResponses[keyof ResearcherProjectsControllerUploadMessageAttachmentResponses];

export type ResearcherProjectsControllerInitiateMessageAttachmentUploadData = {
  body: InitiateMessageAttachmentUploadDto;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/researcher/projects/{projectId}/message-attachment-uploads/initiate';
};

export type ResearcherProjectsControllerInitiateMessageAttachmentUploadResponses =
  {
    200: InitiateMessageAttachmentUploadResponseDto;
  };

export type ResearcherProjectsControllerInitiateMessageAttachmentUploadResponse =
  ResearcherProjectsControllerInitiateMessageAttachmentUploadResponses[keyof ResearcherProjectsControllerInitiateMessageAttachmentUploadResponses];

export type ResearcherProjectsControllerUploadProjectMediaData = {
  body: {
    files: Blob | File;
  };
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/researcher/projects/{projectId}/project-media/upload';
};

export type ResearcherProjectsControllerUploadProjectMediaResponses = {
  201: UploadedProjectMediaDto;
};

export type ResearcherProjectsControllerUploadProjectMediaResponse =
  ResearcherProjectsControllerUploadProjectMediaResponses[keyof ResearcherProjectsControllerUploadProjectMediaResponses];

export type ResearcherProjectsControllerImportProjectMediaFromUrlData = {
  body: ImportProjectMediaUrlDto;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/researcher/projects/{projectId}/project-media/import-url';
};

export type ResearcherProjectsControllerImportProjectMediaFromUrlResponses = {
  201: UploadedProjectMediaDto;
};

export type ResearcherProjectsControllerImportProjectMediaFromUrlResponse =
  ResearcherProjectsControllerImportProjectMediaFromUrlResponses[keyof ResearcherProjectsControllerImportProjectMediaFromUrlResponses];

export type ResearcherProjectsControllerCheckProjectMediaEmbedData = {
  body: CheckProjectMediaEmbedDto;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/researcher/projects/{projectId}/project-media/embed-check';
};

export type ResearcherProjectsControllerCheckProjectMediaEmbedResponses = {
  201: ProjectMediaEmbedCheckDto;
};

export type ResearcherProjectsControllerCheckProjectMediaEmbedResponse =
  ResearcherProjectsControllerCheckProjectMediaEmbedResponses[keyof ResearcherProjectsControllerCheckProjectMediaEmbedResponses];

export type ResearcherProjectsControllerGenerateProjectMediaContextData = {
  body: GenerateProjectMediaContextRequestDto;
  path: {
    projectId: string;
    questionUid: string;
  };
  query?: never;
  url: '/v4/researcher/projects/{projectId}/interview-questions/{questionUid}/media-context';
};

export type ResearcherProjectsControllerGenerateProjectMediaContextResponses = {
  201: GeneratedProjectMediaContextDto;
};

export type ResearcherProjectsControllerGenerateProjectMediaContextResponse =
  ResearcherProjectsControllerGenerateProjectMediaContextResponses[keyof ResearcherProjectsControllerGenerateProjectMediaContextResponses];

export type ResearcherProjectsControllerGetAllInterviewResponsesData = {
  body?: never;
  path: {
    projectId: string;
  };
  query?: {
    /**
     * The feature schema version to read the project's insights at: its pin,
     * which is the default, or one of the experimental versions it is also
     * extracted at.
     */
    featuresSchemaVersion?: number;
    /**
     * 1-based page number. When omitted (along with `pageSize`), every
     * response is returned in a single page — used by "Download all
     * transcripts" which needs the full set.
     */
    page?: number;
    pageSize?: number;
    /**
     * When true, only responses whose feature extraction is COMPLETED are
     * included in `results`/`totalResults` (the Results table needs tagline +
     * summary). Metrics always cover every completed interview.
     */
    extractedOnly?: boolean;
  };
  url: '/v4/researcher/projects/{projectId}/interview-responses';
};

export type ResearcherProjectsControllerGetAllInterviewResponsesResponses = {
  200: GetInterviewResponsesPageForResearcherDto;
};

export type ResearcherProjectsControllerGetAllInterviewResponsesResponse =
  ResearcherProjectsControllerGetAllInterviewResponsesResponses[keyof ResearcherProjectsControllerGetAllInterviewResponsesResponses];

export type ResearcherProjectsControllerGetQuestionSummariesData = {
  body?: never;
  path: {
    projectId: string;
  };
  query?: {
    /**
     * The feature schema version to read the project's insights at: its pin,
     * which is the default, or one of the experimental versions it is also
     * extracted at.
     */
    featuresSchemaVersion?: number;
  };
  url: '/v4/researcher/projects/{projectId}/question-summaries';
};

export type ResearcherProjectsControllerGetQuestionSummariesResponses = {
  200: QuestionSummariesDto;
};

export type ResearcherProjectsControllerGetQuestionSummariesResponse =
  ResearcherProjectsControllerGetQuestionSummariesResponses[keyof ResearcherProjectsControllerGetQuestionSummariesResponses];

export type ResearcherProjectsControllerGetConceptReportData = {
  body?: never;
  path: {
    projectId: string;
  };
  query?: {
    /**
     * The feature schema version to read the project's insights at: its pin,
     * which is the default, or one of the experimental versions it is also
     * extracted at.
     */
    featuresSchemaVersion?: number;
  };
  url: '/v4/researcher/projects/{projectId}/concept-report';
};

export type ResearcherProjectsControllerGetConceptReportResponses = {
  200: ConceptReportDto;
};

export type ResearcherProjectsControllerGetConceptReportResponse =
  ResearcherProjectsControllerGetConceptReportResponses[keyof ResearcherProjectsControllerGetConceptReportResponses];

export type ResearcherProjectsControllerGetQuoteClipsData = {
  body?: never;
  path: {
    projectId: string;
  };
  query?: {
    /**
     * The feature schema version to read the project's insights at: its pin,
     * which is the default, or one of the experimental versions it is also
     * extracted at.
     */
    featuresSchemaVersion?: number;
  };
  url: '/v4/researcher/projects/{projectId}/quote-clips';
};

export type ResearcherProjectsControllerGetQuoteClipsResponses = {
  200: QuoteClipsDto;
};

export type ResearcherProjectsControllerGetQuoteClipsResponse =
  ResearcherProjectsControllerGetQuoteClipsResponses[keyof ResearcherProjectsControllerGetQuoteClipsResponses];

export type ResearcherProjectsControllerGetFeaturesSchemaVersionsData = {
  body?: never;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/researcher/projects/{projectId}/features-schema-versions';
};

export type ResearcherProjectsControllerGetFeaturesSchemaVersionsResponses = {
  200: FeaturesSchemaVersionsDto;
};

export type ResearcherProjectsControllerGetFeaturesSchemaVersionsResponse =
  ResearcherProjectsControllerGetFeaturesSchemaVersionsResponses[keyof ResearcherProjectsControllerGetFeaturesSchemaVersionsResponses];

export type ResearcherProjectsControllerGetInsightsReportData = {
  body?: never;
  path: {
    projectId: string;
  };
  query?: {
    /**
     * The feature schema version to read the project's insights at: its pin,
     * which is the default, or one of the experimental versions it is also
     * extracted at.
     */
    featuresSchemaVersion?: number;
  };
  url: '/v4/researcher/projects/{projectId}/insights-report';
};

export type ResearcherProjectsControllerGetInsightsReportResponses = {
  200: InsightsReportDto;
};

export type ResearcherProjectsControllerGetInsightsReportResponse =
  ResearcherProjectsControllerGetInsightsReportResponses[keyof ResearcherProjectsControllerGetInsightsReportResponses];

export type ResearcherProjectsControllerEvaluateInsightsReportChartData = {
  body: EvaluateInsightsReportChartDto;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/researcher/projects/{projectId}/insights-report/charts/evaluate';
};

export type ResearcherProjectsControllerEvaluateInsightsReportChartResponses = {
  200: InsightsReportChartEvaluationDto;
};

export type ResearcherProjectsControllerEvaluateInsightsReportChartResponse =
  ResearcherProjectsControllerEvaluateInsightsReportChartResponses[keyof ResearcherProjectsControllerEvaluateInsightsReportChartResponses];

export type ResearcherProjectsControllerGetInsightsReportStatementsData = {
  body?: never;
  path: {
    projectId: string;
  };
  query: {
    /**
     * The feature schema version to read the project's insights at: its pin,
     * which is the default, or one of the experimental versions it is also
     * extracted at.
     */
    featuresSchemaVersion?: number;
    /**
     * The statements to answer for, by the id the report prints them under. An id
     * a merge absorbed is answered by the statement that absorbed it, which is
     * where that statement's supporters are counted.
     */
    statementIds: Array<number>;
  };
  url: '/v4/researcher/projects/{projectId}/insights-report/statements';
};

export type ResearcherProjectsControllerGetInsightsReportStatementsResponses = {
  200: InsightsReportStatementsDto;
};

export type ResearcherProjectsControllerGetInsightsReportStatementsResponse =
  ResearcherProjectsControllerGetInsightsReportStatementsResponses[keyof ResearcherProjectsControllerGetInsightsReportStatementsResponses];

export type ResearcherProjectsControllerSearchInsightsReportStatementsData = {
  body?: never;
  path: {
    projectId: string;
  };
  query: {
    /**
     * The feature schema version to read the project's insights at: its pin,
     * which is the default, or one of the experimental versions it is also
     * extracted at.
     */
    featuresSchemaVersion?: number;
    q: string;
  };
  url: '/v4/researcher/projects/{projectId}/insights-report/statements/search';
};

export type ResearcherProjectsControllerSearchInsightsReportStatementsResponses =
  {
    200: InsightsReportStatementSearchDto;
  };

export type ResearcherProjectsControllerSearchInsightsReportStatementsResponse =
  ResearcherProjectsControllerSearchInsightsReportStatementsResponses[keyof ResearcherProjectsControllerSearchInsightsReportStatementsResponses];

export type ResearcherProjectsControllerDownloadLegacyInsightsReportPdfData = {
  body?: never;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/researcher/projects/{projectId}/insights-report/pdf';
};

export type ResearcherProjectsControllerDownloadLegacyInsightsReportPdfResponses =
  {
    200: unknown;
  };

export type ResearcherProjectsControllerRegenerateInsightsReportData = {
  body: GenerateInsightsReportDto;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/researcher/projects/{projectId}/insights-report/regenerate';
};

export type ResearcherProjectsControllerRegenerateInsightsReportResponses = {
  201: GenerateInsightsReportResponseDto;
};

export type ResearcherProjectsControllerRegenerateInsightsReportResponse =
  ResearcherProjectsControllerRegenerateInsightsReportResponses[keyof ResearcherProjectsControllerRegenerateInsightsReportResponses];

export type ResearcherProjectsControllerNotifyInsightsUpdatedData = {
  body?: never;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/researcher/projects/{projectId}/realtime/insights-updated';
};

export type ResearcherProjectsControllerNotifyInsightsUpdatedResponses = {
  204: void;
};

export type ResearcherProjectsControllerNotifyInsightsUpdatedResponse =
  ResearcherProjectsControllerNotifyInsightsUpdatedResponses[keyof ResearcherProjectsControllerNotifyInsightsUpdatedResponses];

export type ResearcherProjectsControllerGeneratePreviewFollowUpData = {
  body: GeneratePreviewFollowUpDto;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/researcher/projects/{projectId}/interview/preview-follow-up';
};

export type ResearcherProjectsControllerGeneratePreviewFollowUpResponses = {
  201: GeneratePreviewFollowUpResponseDto;
};

export type ResearcherProjectsControllerGeneratePreviewFollowUpResponse =
  ResearcherProjectsControllerGeneratePreviewFollowUpResponses[keyof ResearcherProjectsControllerGeneratePreviewFollowUpResponses];

export type ResearcherProjectsControllerGetInterviewContentTranslationData = {
  body?: never;
  path: {
    projectId: string;
    language: string;
  };
  query?: never;
  url: '/v4/researcher/projects/{projectId}/interview-content-translations/{language}';
};

export type ResearcherProjectsControllerGetInterviewContentTranslationResponses =
  {
    200: InterviewContentTranslationForResearcherDto;
  };

export type ResearcherProjectsControllerGetInterviewContentTranslationResponse =
  ResearcherProjectsControllerGetInterviewContentTranslationResponses[keyof ResearcherProjectsControllerGetInterviewContentTranslationResponses];

export type ResearcherProjectsControllerUpdateInterviewContentTranslationData =
  {
    body: UpdateInterviewContentTranslationDto;
    path: {
      projectId: string;
      language: string;
    };
    query?: never;
    url: '/v4/researcher/projects/{projectId}/interview-content-translations/{language}';
  };

export type ResearcherProjectsControllerUpdateInterviewContentTranslationResponses =
  {
    200: InterviewContentTranslationForResearcherDto;
  };

export type ResearcherProjectsControllerUpdateInterviewContentTranslationResponse =
  ResearcherProjectsControllerUpdateInterviewContentTranslationResponses[keyof ResearcherProjectsControllerUpdateInterviewContentTranslationResponses];

export type ResearcherProjectsControllerGetInterviewResponsesData = {
  body?: never;
  path: {
    projectId: string;
    participantUserId: string;
  };
  query?: never;
  url: '/v4/researcher/projects/{projectId}/interview-responses/{participantUserId}';
};

export type ResearcherProjectsControllerGetInterviewResponsesResponses = {
  200: GetInterviewResponsesForResearcherDto;
};

export type ResearcherProjectsControllerGetInterviewResponsesResponse =
  ResearcherProjectsControllerGetInterviewResponsesResponses[keyof ResearcherProjectsControllerGetInterviewResponsesResponses];

export type ResearcherProjectsControllerUpdateInsightsInclusionData = {
  body: UpdateInsightsInclusionDto;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/researcher/projects/{projectId}/interview-responses/insights-inclusion';
};

export type ResearcherProjectsControllerUpdateInsightsInclusionResponses = {
  200: UpdateInsightsInclusionResponseDto;
};

export type ResearcherProjectsControllerUpdateInsightsInclusionResponse =
  ResearcherProjectsControllerUpdateInsightsInclusionResponses[keyof ResearcherProjectsControllerUpdateInsightsInclusionResponses];

export type ResearcherProjectsControllerReExtractInterviewResponseData = {
  body: ReExtractInterviewResponseDto;
  path: {
    projectId: string;
    participantUserId: string;
  };
  query?: never;
  url: '/v4/researcher/projects/{projectId}/interview-responses/{participantUserId}/re-extract';
};

export type ResearcherProjectsControllerReExtractInterviewResponseResponses = {
  201: ReExtractInterviewResponseResponseDto;
};

export type ResearcherProjectsControllerReExtractInterviewResponseResponse =
  ResearcherProjectsControllerReExtractInterviewResponseResponses[keyof ResearcherProjectsControllerReExtractInterviewResponseResponses];

export type ResearcherProjectsControllerListFollowersData = {
  body?: never;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/researcher/projects/{projectId}/followers';
};

export type ResearcherProjectsControllerListFollowersResponses = {
  200: Array<ProjectFollower>;
};

export type ResearcherProjectsControllerListFollowersResponse =
  ResearcherProjectsControllerListFollowersResponses[keyof ResearcherProjectsControllerListFollowersResponses];

export type ResearcherProjectsControllerAddFollowerData = {
  body: AddFollowerDto;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/researcher/projects/{projectId}/followers';
};

export type ResearcherProjectsControllerAddFollowerResponses = {
  201: Array<ProjectFollower>;
};

export type ResearcherProjectsControllerAddFollowerResponse =
  ResearcherProjectsControllerAddFollowerResponses[keyof ResearcherProjectsControllerAddFollowerResponses];

export type ResearcherProjectsControllerRemoveFollowerData = {
  body?: never;
  path: {
    projectId: string;
    userId: string;
  };
  query?: never;
  url: '/v4/researcher/projects/{projectId}/followers/{userId}';
};

export type ResearcherProjectsControllerRemoveFollowerResponses = {
  200: Array<ProjectFollower>;
};

export type ResearcherProjectsControllerRemoveFollowerResponse =
  ResearcherProjectsControllerRemoveFollowerResponses[keyof ResearcherProjectsControllerRemoveFollowerResponses];

export type StudyChecksControllerCheckData = {
  body?: never;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/researcher/projects/{projectId}/study-checks';
};

export type StudyChecksControllerCheckResponses = {
  200: StudyChecksResultDto;
};

export type StudyChecksControllerCheckResponse =
  StudyChecksControllerCheckResponses[keyof StudyChecksControllerCheckResponses];

export type StudyChecksControllerFeedbackData = {
  body: StudyCheckFeedbackDto;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/researcher/projects/{projectId}/study-checks/feedback';
};

export type StudyChecksControllerFeedbackResponses = {
  204: void;
};

export type StudyChecksControllerFeedbackResponse =
  StudyChecksControllerFeedbackResponses[keyof StudyChecksControllerFeedbackResponses];

export type StudyChecksControllerRecordEventsData = {
  body: StudyCheckInteractionsDto;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/researcher/projects/{projectId}/study-checks/events';
};

export type StudyChecksControllerRecordEventsResponses = {
  204: void;
};

export type StudyChecksControllerRecordEventsResponse =
  StudyChecksControllerRecordEventsResponses[keyof StudyChecksControllerRecordEventsResponses];

export type ParticipantReferralsControllerFindByReferrerUserIdData = {
  body?: never;
  path: {
    userId: string;
  };
  query?: {
    page?: number;
    pageSize?: number;
    includeCount?: boolean;
    query?: string;
    includeLifetimeBonusReceived?: boolean;
    hideCompleted?: boolean;
  };
  url: '/v4/participant-referrals/referrer/{userId}';
};

export type ParticipantReferralsControllerFindByReferrerUserIdResponses = {
  200: ReferralsByReferrerPaginatedDto;
};

export type ParticipantReferralsControllerFindByReferrerUserIdResponse =
  ParticipantReferralsControllerFindByReferrerUserIdResponses[keyof ParticipantReferralsControllerFindByReferrerUserIdResponses];

export type SchedulerEventsControllerCreateData = {
  body: CreateSchedulerDto;
  path?: never;
  query?: never;
  url: '/v4/scheduler/events';
};

export type SchedulerEventsControllerCreateResponses = {
  201: SchedulerEventDto;
};

export type SchedulerEventsControllerCreateResponse =
  SchedulerEventsControllerCreateResponses[keyof SchedulerEventsControllerCreateResponses];

export type SchedulerEventsControllerListForProjectData = {
  body: FindEventsDto;
  path?: never;
  query?: never;
  url: '/v4/scheduler/events/find';
};

export type SchedulerEventsControllerListForProjectResponses = {
  200: SchedulerEventListDto;
};

export type SchedulerEventsControllerListForProjectResponse =
  SchedulerEventsControllerListForProjectResponses[keyof SchedulerEventsControllerListForProjectResponses];

export type SchedulerEventsControllerRemoveData = {
  body?: never;
  path: {
    uid: string;
  };
  query?: never;
  url: '/v4/scheduler/events/{uid}';
};

export type SchedulerEventsControllerRemoveResponses = {
  204: void;
};

export type SchedulerEventsControllerRemoveResponse =
  SchedulerEventsControllerRemoveResponses[keyof SchedulerEventsControllerRemoveResponses];

export type SchedulerEventsControllerGetByUidData = {
  body?: never;
  path: {
    uid: string;
  };
  query?: never;
  url: '/v4/scheduler/events/{uid}';
};

export type SchedulerEventsControllerGetByUidResponses = {
  200: SchedulerEventDto;
};

export type SchedulerEventsControllerGetByUidResponse =
  SchedulerEventsControllerGetByUidResponses[keyof SchedulerEventsControllerGetByUidResponses];

export type SchedulerEventsControllerUpdateData = {
  body: UpdateSchedulerDto;
  path: {
    uid: string;
  };
  query?: never;
  url: '/v4/scheduler/events/{uid}';
};

export type SchedulerEventsControllerUpdateResponses = {
  200: SchedulerEventDto;
};

export type SchedulerEventsControllerUpdateResponse =
  SchedulerEventsControllerUpdateResponses[keyof SchedulerEventsControllerUpdateResponses];

export type SchedulerEventsControllerDuplicateData = {
  body?: never;
  path: {
    uid: string;
  };
  query?: never;
  url: '/v4/scheduler/events/{uid}/duplicate';
};

export type SchedulerEventsControllerDuplicateResponses = {
  201: SchedulerEventDto;
};

export type SchedulerEventsControllerDuplicateResponse =
  SchedulerEventsControllerDuplicateResponses[keyof SchedulerEventsControllerDuplicateResponses];

export type CalendarConnectionControllerDisconnectData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v4/scheduler/calendar-connection';
};

export type CalendarConnectionControllerDisconnectResponses = {
  204: void;
};

export type CalendarConnectionControllerDisconnectResponse =
  CalendarConnectionControllerDisconnectResponses[keyof CalendarConnectionControllerDisconnectResponses];

export type CalendarConnectionControllerGetData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v4/scheduler/calendar-connection';
};

export type CalendarConnectionControllerGetResponses = {
  200: CalendarConnectionDto;
};

export type CalendarConnectionControllerGetResponse =
  CalendarConnectionControllerGetResponses[keyof CalendarConnectionControllerGetResponses];

export type CalendarConnectionControllerUpdateData = {
  body: UpdateCalendarConnectionDto;
  path?: never;
  query?: never;
  url: '/v4/scheduler/calendar-connection';
};

export type CalendarConnectionControllerUpdateResponses = {
  200: CalendarConnectionDto;
};

export type CalendarConnectionControllerUpdateResponse =
  CalendarConnectionControllerUpdateResponses[keyof CalendarConnectionControllerUpdateResponses];

export type TeamRespondentsControllerFindOneData = {
  body?: never;
  path: {
    teamId: string;
    profileId: string;
  };
  query?: never;
  url: '/v4/team-respondents/teams/{teamId}/profiles/{profileId}';
};

export type TeamRespondentsControllerFindOneResponses = {
  200: TeamRespondentDto;
};

export type TeamRespondentsControllerFindOneResponse =
  TeamRespondentsControllerFindOneResponses[keyof TeamRespondentsControllerFindOneResponses];

export type TeamRespondentsControllerFindAllData = {
  body?: never;
  path: {
    teamId: string;
  };
  query?: {
    page?: number;
    pageSize?: number;
    includeCount?: boolean;
    name?: string;
    jobTitle?: string;
    lastPaidAt?: string;
    projectIds?: Array<string>;
    tags?: Array<string>;
    source?: ParticipantSource;
    externalResearcherId?: string;
    externalTeamId?: string;
    externalCompanyId?: string;
    sortBy?: SortBy;
  };
  url: '/v4/team-respondents/teams/{teamId}';
};

export type TeamRespondentsControllerFindAllResponses = {
  200: TeamRespondentsPaginatedDto;
};

export type TeamRespondentsControllerFindAllResponse =
  TeamRespondentsControllerFindAllResponses[keyof TeamRespondentsControllerFindAllResponses];

export type TeamRespondentsControllerBatchInviteData = {
  body: BatchInviteTeamRespondentsDto;
  path: {
    teamId: string;
  };
  query?: never;
  url: '/v4/team-respondents/teams/{teamId}/batch-invite';
};

export type TeamRespondentsControllerBatchInviteResponses = {
  200: BatchInviteTeamRespondentsResponseDto;
};

export type TeamRespondentsControllerBatchInviteResponse =
  TeamRespondentsControllerBatchInviteResponses[keyof TeamRespondentsControllerBatchInviteResponses];

export type TestSetupControllerDeleteUserDataData = {
  body?: never;
  path: {
    userId: string;
  };
  query?: never;
  url: '/v4/test-setup/users/{userId}';
};

export type TestSetupControllerDeleteUserDataResponses = {
  200: unknown;
};

export type TestSetupControllerUpdateTestUserData = {
  body: UpdateTestUserDto;
  path: {
    userId: string;
  };
  query?: never;
  url: '/v4/test-setup/users/{userId}';
};

export type TestSetupControllerUpdateTestUserResponses = {
  200: unknown;
};

export type TestSetupControllerDeleteSurveyResponsesData = {
  body?: never;
  path: {
    userId: string;
  };
  query?: never;
  url: '/v4/test-setup/users/{userId}/survey-responses';
};

export type TestSetupControllerDeleteSurveyResponsesResponses = {
  200: unknown;
};

export type TestSetupControllerGetUserVerificationTokenData = {
  body?: never;
  path: {
    email: string;
  };
  query?: never;
  url: '/v4/test-setup/email/{email}/verification-token';
};

export type TestSetupControllerGetUserVerificationTokenResponses = {
  200: unknown;
};

export type TestSetupControllerGetWorkEmailVerificationLinkData = {
  body?: never;
  path: {
    userId: string;
  };
  query?: never;
  url: '/v4/test-setup/users/{userId}/work-email-verification-link';
};

export type TestSetupControllerGetWorkEmailVerificationLinkResponses = {
  200: unknown;
};

export type TestSetupControllerGetWorkEmailVerificationCodeData = {
  body?: never;
  path: {
    userId: string;
  };
  query?: never;
  url: '/v4/test-setup/users/{userId}/work-email-verification-code';
};

export type TestSetupControllerGetWorkEmailVerificationCodeResponses = {
  200: unknown;
};

export type TestSetupControllerAddFeatureFlagAllowOrganizationData = {
  body: AddFeatureFlagAllowOrganizationDto;
  path?: never;
  query?: never;
  url: '/v4/test-setup/feature-flags/allow-organizations';
};

export type TestSetupControllerAddFeatureFlagAllowOrganizationResponses = {
  201: unknown;
};

export type TestSetupControllerRemoveFeatureFlagAllowOrganizationData = {
  body?: never;
  path: {
    name: string;
    organizationId: string;
  };
  query?: never;
  url: '/v4/test-setup/feature-flags/{name}/allow-organizations/{organizationId}';
};

export type TestSetupControllerRemoveFeatureFlagAllowOrganizationResponses = {
  200: unknown;
};

export type TestSetupControllerAddFeatureFlagDenyOrganizationData = {
  body: AddFeatureFlagDenyOrganizationDto;
  path?: never;
  query?: never;
  url: '/v4/test-setup/feature-flags/deny-organizations';
};

export type TestSetupControllerAddFeatureFlagDenyOrganizationResponses = {
  201: unknown;
};

export type TestSetupControllerRemoveFeatureFlagDenyOrganizationData = {
  body?: never;
  path: {
    name: string;
    organizationId: string;
  };
  query?: never;
  url: '/v4/test-setup/feature-flags/{name}/deny-organizations/{organizationId}';
};

export type TestSetupControllerRemoveFeatureFlagDenyOrganizationResponses = {
  200: unknown;
};

export type TestSetupControllerCreateProjectData = {
  body: CreateProjectDtoWritable;
  path: {
    userId: string;
  };
  query?: never;
  url: '/v4/test-setup/users/{userId}/projects';
};

export type TestSetupControllerCreateProjectResponses = {
  201: {
    [key: string]: unknown;
  };
};

export type TestSetupControllerCreateProjectResponse =
  TestSetupControllerCreateProjectResponses[keyof TestSetupControllerCreateProjectResponses];

export type TestSetupControllerPublishProjectData = {
  body?: never;
  path: {
    projectId: string;
  };
  query: {
    actorId: string;
  };
  url: '/v4/test-setup/projects/{projectId}/publish';
};

export type TestSetupControllerPublishProjectResponses = {
  201: Project;
};

export type TestSetupControllerPublishProjectResponse =
  TestSetupControllerPublishProjectResponses[keyof TestSetupControllerPublishProjectResponses];

export type TestSetupControllerBulkUpsertScreenerQuestionsData = {
  body: Array<string>;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/test-setup/projects/{projectId}/screener-questions/bulk';
};

export type TestSetupControllerBulkUpsertScreenerQuestionsResponses = {
  200: Array<ScreenerQuestion>;
};

export type TestSetupControllerBulkUpsertScreenerQuestionsResponse =
  TestSetupControllerBulkUpsertScreenerQuestionsResponses[keyof TestSetupControllerBulkUpsertScreenerQuestionsResponses];

export type TestSetupControllerDeleteProjectDataData = {
  body?: never;
  path: {
    projectId: string;
  };
  query?: never;
  url: '/v4/test-setup/projects/{projectId}';
};

export type TestSetupControllerDeleteProjectDataResponses = {
  200: unknown;
};

export type TestSetupControllerFindUserByEmailData = {
  body?: never;
  path: {
    email: string;
  };
  query?: never;
  url: '/v4/test-setup/users/email/{email}';
};

export type TestSetupControllerFindUserByEmailResponses = {
  200: User;
};

export type TestSetupControllerFindUserByEmailResponse =
  TestSetupControllerFindUserByEmailResponses[keyof TestSetupControllerFindUserByEmailResponses];

export type TestSetupControllerInviteScreenerResponseData = {
  body?: never;
  path: {
    projectId: string;
    userId: string;
  };
  query?: never;
  url: '/v4/test-setup/survey-responses/projects/{projectId}/users/{userId}/invite';
};

export type TestSetupControllerInviteScreenerResponseResponses = {
  200: ScreenerResponse;
};

export type TestSetupControllerInviteScreenerResponseResponse =
  TestSetupControllerInviteScreenerResponseResponses[keyof TestSetupControllerInviteScreenerResponseResponses];

export type TestSetupControllerSetProfileRiskSignalsData = {
  body: SetTestProfileRiskSignalsDto;
  path: {
    profileId: string;
  };
  query?: never;
  url: '/v4/test-setup/profiles/{profileId}/risk-signals';
};

export type TestSetupControllerSetProfileRiskSignalsResponses = {
  200: unknown;
};

export type TestSetupControllerRecomputeProfileRiskData = {
  body?: never;
  path: {
    profileId: string;
  };
  query?: never;
  url: '/v4/test-setup/profiles/{profileId}/risk/recompute';
};

export type TestSetupControllerRecomputeProfileRiskResponses = {
  201: unknown;
};

export type VerificationControllerCreateData = {
  body: CreateVerificationDto;
  path?: never;
  query?: never;
  url: '/v4/verification';
};

export type VerificationControllerCreateResponses = {
  204: void;
};

export type VerificationControllerCreateResponse =
  VerificationControllerCreateResponses[keyof VerificationControllerCreateResponses];

export type VerificationControllerVerifyCodeData = {
  body: VerifyCodeDto;
  path: {
    code: string;
  };
  query?: never;
  url: '/v4/verification/{code}';
};

export type VerificationControllerVerifyCodeResponses = {
  200: unknown;
};

export type VerificationsControllerTwilioCreateData = {
  body: TwilioCreateVerificationDto;
  path?: never;
  query?: never;
  url: '/v4/verifications/twilio';
};

export type VerificationsControllerTwilioCreateResponses = {
  204: void;
};

export type VerificationsControllerTwilioCreateResponse =
  VerificationsControllerTwilioCreateResponses[keyof VerificationsControllerTwilioCreateResponses];

export type VerificationsControllerTwilioVerifyCodeData = {
  body: TwilioVerifyCodeDto;
  path: {
    code: string;
  };
  query?: never;
  url: '/v4/verifications/twilio/{code}';
};

export type VerificationsControllerTwilioVerifyCodeResponses = {
  200: unknown;
};

export type VerificationsControllerGetPlaidVerificationLinkTokenData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Project the respondent is being verified for. Used to resolve the owning organization when selecting the Plaid verification template.
     */
    projectId?: string;
  };
  url: '/v4/verifications/plaid/link-token';
};

export type VerificationsControllerGetPlaidVerificationLinkTokenResponses = {
  /**
   * Plaid Link token for identity verification, or a linkedin_identity_verification indicator when the participant must verify via the LinkedIn identity journey instead
   */
  200: PlaidLinkTokenResponseDto;
};

export type VerificationsControllerGetPlaidVerificationLinkTokenResponse =
  VerificationsControllerGetPlaidVerificationLinkTokenResponses[keyof VerificationsControllerGetPlaidVerificationLinkTokenResponses];

export type VerificationsControllerPatchPlaidSessionData = {
  body?: never;
  path: {
    plaidSessionId: string;
  };
  query?: never;
  url: '/v4/verifications/plaid/{plaidSessionId}';
};

export type VerificationsControllerPatchPlaidSessionResponses = {
  200: unknown;
};

export type VerificationsControllerPatchPersonaInquiryData = {
  body?: never;
  path: {
    inquiryId: string;
  };
  query?: never;
  url: '/v4/verifications/persona/{inquiryId}';
};

export type VerificationsControllerPatchPersonaInquiryResponses = {
  200: unknown;
};

export type WebhooksOrganizationsControllerFindAllData = {
  body?: never;
  path: {
    organizationId: string;
  };
  query?: never;
  url: '/v4/organizations/{organizationId}/webhooks';
};

export type WebhooksOrganizationsControllerFindAllResponses = {
  200: WebhookDto;
};

export type WebhooksOrganizationsControllerFindAllResponse =
  WebhooksOrganizationsControllerFindAllResponses[keyof WebhooksOrganizationsControllerFindAllResponses];

export type WebhooksOrganizationsControllerCreateData = {
  body: CreateWebhookDto;
  path: {
    organizationId: string;
  };
  query?: never;
  url: '/v4/organizations/{organizationId}/webhooks';
};

export type WebhooksOrganizationsControllerCreateResponses = {
  201: WebhookDto;
};

export type WebhooksOrganizationsControllerCreateResponse =
  WebhooksOrganizationsControllerCreateResponses[keyof WebhooksOrganizationsControllerCreateResponses];

export type WebhooksOrganizationsControllerDeactivateData = {
  body?: never;
  path: {
    organizationId: string;
    webhookId: string;
  };
  query?: never;
  url: '/v4/organizations/{organizationId}/webhooks/{webhookId}';
};

export type WebhooksOrganizationsControllerDeactivateResponses = {
  200: unknown;
};

export type WebhooksOrganizationsControllerFindOneData = {
  body?: never;
  path: {
    organizationId: string;
    webhookId: string;
  };
  query?: never;
  url: '/v4/organizations/{organizationId}/webhooks/{webhookId}';
};

export type WebhooksOrganizationsControllerFindOneResponses = {
  200: WebhookDto;
};

export type WebhooksOrganizationsControllerFindOneResponse =
  WebhooksOrganizationsControllerFindOneResponses[keyof WebhooksOrganizationsControllerFindOneResponses];

export type WebhooksOrganizationsControllerFindEventTypesData = {
  body?: never;
  path: {
    organizationId: string;
    webhookId: string;
  };
  query?: never;
  url: '/v4/organizations/{organizationId}/webhooks/{webhookId}/event-types';
};

export type WebhooksOrganizationsControllerFindEventTypesResponses = {
  200: WebhookEventTypesDto;
};

export type WebhooksOrganizationsControllerFindEventTypesResponse =
  WebhooksOrganizationsControllerFindEventTypesResponses[keyof WebhooksOrganizationsControllerFindEventTypesResponses];

export type WebhooksOrganizationsControllerSimulateData = {
  body: SimulateWebhookDto;
  path: {
    organizationId: string;
    webhookId: string;
  };
  query?: never;
  url: '/v4/organizations/{organizationId}/webhooks/{webhookId}/simulate';
};

export type WebhooksOrganizationsControllerSimulateResponses = {
  201: unknown;
};

export type WebhooksControllerFindAllData = {
  body?: never;
  path: {
    teamId: string;
  };
  query?: never;
  url: '/v4/teams/{teamId}/webhooks';
};

export type WebhooksControllerFindAllResponses = {
  200: WebhookDto;
};

export type WebhooksControllerFindAllResponse =
  WebhooksControllerFindAllResponses[keyof WebhooksControllerFindAllResponses];

export type WebhooksControllerCreateData = {
  body: CreateWebhookDto;
  path: {
    teamId: string;
  };
  query?: never;
  url: '/v4/teams/{teamId}/webhooks';
};

export type WebhooksControllerCreateResponses = {
  201: WebhookDto;
};

export type WebhooksControllerCreateResponse =
  WebhooksControllerCreateResponses[keyof WebhooksControllerCreateResponses];

export type WebhooksControllerDeactivateData = {
  body?: never;
  path: {
    teamId: string;
    webhookId: string;
  };
  query?: never;
  url: '/v4/teams/{teamId}/webhooks/{webhookId}';
};

export type WebhooksControllerDeactivateResponses = {
  200: unknown;
};

export type WebhooksControllerFindOneData = {
  body?: never;
  path: {
    teamId: string;
    webhookId: string;
  };
  query?: never;
  url: '/v4/teams/{teamId}/webhooks/{webhookId}';
};

export type WebhooksControllerFindOneResponses = {
  200: WebhookDto;
};

export type WebhooksControllerFindOneResponse =
  WebhooksControllerFindOneResponses[keyof WebhooksControllerFindOneResponses];

export type WebhooksControllerFindEventTypesData = {
  body?: never;
  path: {
    teamId: string;
    webhookId: string;
  };
  query?: never;
  url: '/v4/teams/{teamId}/webhooks/{webhookId}/event-types';
};

export type WebhooksControllerFindEventTypesResponses = {
  200: WebhookEventTypesDto;
};

export type WebhooksControllerFindEventTypesResponse =
  WebhooksControllerFindEventTypesResponses[keyof WebhooksControllerFindEventTypesResponses];

export type WebhooksControllerSimulateData = {
  body: SimulateWebhookDto;
  path: {
    teamId: string;
    webhookId: string;
  };
  query?: never;
  url: '/v4/teams/{teamId}/webhooks/{webhookId}/simulate';
};

export type WebhooksControllerSimulateResponses = {
  201: unknown;
};

export type WebhooksReceivedControllerFindAllData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * The organizationId to which the webhook belongs.
     */
    organizationId?: string;
    /**
     * The teamId to which the webhook belongs.
     */
    teamId?: string;
    webhookId?: string;
  };
  url: '/v4/webhooks-received';
};

export type WebhooksReceivedControllerFindAllResponses = {
  200: Array<WebhookReceieved>;
};

export type WebhooksReceivedControllerFindAllResponse =
  WebhooksReceivedControllerFindAllResponses[keyof WebhooksReceivedControllerFindAllResponses];

export type WebhooksReceivedControllerCreateData = {
  body: CreateWebhookReceivedDto;
  path?: never;
  query?: never;
  url: '/v4/webhooks-received';
};

export type WebhooksReceivedControllerCreateResponses = {
  201: WebhookReceieved;
};

export type WebhooksReceivedControllerCreateResponse =
  WebhooksReceivedControllerCreateResponses[keyof WebhooksReceivedControllerCreateResponses];

export type WebhooksReceivedControllerDeleteData = {
  body?: never;
  path: {
    webhookId: string;
  };
  query?: never;
  url: '/v4/webhooks-received/{webhookId}';
};

export type WebhooksReceivedControllerDeleteResponses = {
  200: unknown;
};

export type WebhooksReceivedControllerTestData = {
  body?: never;
  path: {
    webhookId: string;
  };
  query?: never;
  url: '/v4/webhooks-received/{webhookId}/test';
};

export type WebhooksReceivedControllerTestResponses = {
  200: unknown;
};

export type EmailEventsControllerCreateEmailEventData = {
  body: CreateEmailEventDto;
  path?: never;
  query?: never;
  url: '/v4/email-events';
};

export type EmailEventsControllerCreateEmailEventResponses = {
  201: EmailEvent;
};

export type EmailEventsControllerCreateEmailEventResponse =
  EmailEventsControllerCreateEmailEventResponses[keyof EmailEventsControllerCreateEmailEventResponses];

export type UsersSessionsControllerCreateData = {
  body: CreateUserSessionDto;
  path?: never;
  query?: never;
  url: '/v4/users-sessions';
};

export type UsersSessionsControllerCreateResponses = {
  201: UsersSession;
};

export type UsersSessionsControllerCreateResponse =
  UsersSessionsControllerCreateResponses[keyof UsersSessionsControllerCreateResponses];

export type UsersSessionsControllerGetOneByUserIdData = {
  body?: never;
  path: {
    userId: string;
  };
  query: {
    actionType: string;
  };
  url: '/v4/users-sessions/{userId}';
};

export type UsersSessionsControllerGetOneByUserIdResponses = {
  200: UsersSession;
};

export type UsersSessionsControllerGetOneByUserIdResponse =
  UsersSessionsControllerGetOneByUserIdResponses[keyof UsersSessionsControllerGetOneByUserIdResponses];

export type ClearbitRiskHistoryControllerCreateData = {
  body: CreateClearbitRiskHistoryDto;
  path?: never;
  query?: never;
  url: '/v4/clearbit-risk-history';
};

export type ClearbitRiskHistoryControllerCreateResponses = {
  201: ClearbitRiskHistory;
};

export type ClearbitRiskHistoryControllerCreateResponse =
  ClearbitRiskHistoryControllerCreateResponses[keyof ClearbitRiskHistoryControllerCreateResponses];

export type RoundtableVerificationsControllerFindAllData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v4/roundtable-verifications';
};

export type RoundtableVerificationsControllerFindAllResponses = {
  200: unknown;
};

export type RoundtableVerificationsControllerTriggerRoundtableVerificationData =
  {
    body: CreateRoundtableVerificationDto;
    path?: never;
    query?: never;
    url: '/v4/roundtable-verifications';
  };

export type RoundtableVerificationsControllerTriggerRoundtableVerificationResponses =
  {
    201: RoundtableVerification;
  };

export type RoundtableVerificationsControllerTriggerRoundtableVerificationResponse =
  RoundtableVerificationsControllerTriggerRoundtableVerificationResponses[keyof RoundtableVerificationsControllerTriggerRoundtableVerificationResponses];

export type RoundtableVerificationsControllerRemoveData = {
  body?: never;
  path: {
    id: string;
  };
  query?: never;
  url: '/v4/roundtable-verifications/{id}';
};

export type RoundtableVerificationsControllerRemoveResponses = {
  200: unknown;
};

export type RoundtableVerificationsControllerFindOneData = {
  body?: never;
  path: {
    id: string;
  };
  query?: never;
  url: '/v4/roundtable-verifications/{id}';
};

export type RoundtableVerificationsControllerFindOneResponses = {
  200: unknown;
};

export type RoundtableVerificationsControllerUpdateData = {
  body: UpdateRoundtableVerificationDto;
  path: {
    id: string;
  };
  query?: never;
  url: '/v4/roundtable-verifications/{id}';
};

export type RoundtableVerificationsControllerUpdateResponses = {
  200: unknown;
};

export type CreditCardsControllerGetClientTokenData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/v4/braintree/client-token';
};

export type CreditCardsControllerGetClientTokenResponses = {
  200: ClientTokenDto;
};

export type CreditCardsControllerGetClientTokenResponse =
  CreditCardsControllerGetClientTokenResponses[keyof CreditCardsControllerGetClientTokenResponses];

export type CreditCardsControllerGetAllData = {
  body?: never;
  path: {
    teamId: string;
  };
  query?: never;
  url: '/v4/teams/{teamId}/credit-cards';
};

export type CreditCardsControllerGetAllResponses = {
  200: Array<CreditCardDto>;
};

export type CreditCardsControllerGetAllResponse =
  CreditCardsControllerGetAllResponses[keyof CreditCardsControllerGetAllResponses];

export type CreditCardsControllerUpdateData = {
  body: UpdateCreditCardDto;
  path: {
    teamId: string;
  };
  query?: never;
  url: '/v4/teams/{teamId}/credit-cards';
};

export type CreditCardsControllerUpdateResponses = {
  204: void;
};

export type CreditCardsControllerUpdateResponse =
  CreditCardsControllerUpdateResponses[keyof CreditCardsControllerUpdateResponses];

export type CreditCardsControllerCreateData = {
  body: CreateCreditCardDto;
  path: {
    teamId: string;
  };
  query?: never;
  url: '/v4/teams/{teamId}/credit-cards';
};

export type CreditCardsControllerCreateResponses = {
  201: CreditCardDto;
};

export type CreditCardsControllerCreateResponse =
  CreditCardsControllerCreateResponses[keyof CreditCardsControllerCreateResponses];

export type CreditCardsControllerUpdateDefaultCardData = {
  body: UpdateDefaultCreditCardDto;
  path: {
    teamId: string;
  };
  query?: never;
  url: '/v4/teams/{teamId}/credit-cards/default';
};

export type CreditCardsControllerUpdateDefaultCardResponses = {
  204: void;
};

export type CreditCardsControllerUpdateDefaultCardResponse =
  CreditCardsControllerUpdateDefaultCardResponses[keyof CreditCardsControllerUpdateDefaultCardResponses];

export type CreditCardsControllerRemoveData = {
  body?: never;
  path: {
    teamId: string;
    token: string;
  };
  query?: never;
  url: '/v4/teams/{teamId}/credit-cards/{token}';
};

export type CreditCardsControllerRemoveResponses = {
  204: void;
};

export type CreditCardsControllerRemoveResponse =
  CreditCardsControllerRemoveResponses[keyof CreditCardsControllerRemoveResponses];
